// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { caModuleTips, caForseti } from './ca-extras.js';
// Sprint 62.57.0 Slice E — R-57: the English, for structural parity.
import { enServiceCertificates, enVendorServiceCertificate } from './en-extras.js';
export const ca = {
    ai_governance_pages: {
        nav: {
            inventory: 'AI inventory reviews',
            payment: 'Payment authorities',
            interventions: 'Interventions',
            complaints: 'AI complaints',
            framework: 'Framework record and three lines',
            board: 'Board report: AI figures',
        },
        common: {
            loading: 'Loading…',
            loadFailed: 'This could not be read.',
            retry: 'Try again',
            retrySr: 'read it again',
            notRecorded: 'Not recorded',
            dueNotInForce: 'Not counted: the cadence is not yet in force',
            none: 'None',
            choose: 'Choose…',
            cancel: 'Cancel',
            close: 'Close',
            saving: 'Saving…',
            saveFailed: 'The record was not saved.',
            requiredMark: '(required)',
            missingHeading: 'The record cannot be saved yet. These are missing:',
            directoryFailed: 'The list of people could not be read.',
            directoryPartial: 'Showing {shown} people. The list is not complete.',
            yes: 'Yes',
            no: 'No',
            readOnly: 'Read only',
            noAnswer: 'The server did not answer.',
            listTruncated: 'Showing {shown} of {total} rows. The list is not complete.',
            awaitingOtherApprover: 'Awaiting approval by another person',
            obligationsRetrySr: 'read the dated obligations again',
            systemDatesRetrySr: "read this system's dated obligations again",
            actions: 'Actions',
        },
        states: {
            incomplete: 'Incomplete',
            complete: 'Complete',
            not_recorded: 'Not recorded',
            not_evidenced: 'Not evidenced',
            pending_approval: 'Awaiting a second approval',
            expired: 'Expired',
            matches: 'Matches',
            mismatch: 'Mismatch',
            requested: 'Requested',
            confirmed: 'Confirmed',
            recorded: 'Recorded',
            performed: 'Performed',
            not_mandatory: 'Not mandatory',
            not_applicable: 'Not applicable',
            open: 'Open',
            closed: 'Closed',
            routed: 'Routed',
            stale: 'Stale',
            signed: 'Signed',
            current: 'Current',
            overdue: 'Overdue',
            due: 'Due',
            cadence_not_declared: 'No cadence declared',
            not_yet_in_force: 'Not yet in force',
            not_judged: 'Not judged',
            not_required: 'Not required',
            not_trained: 'Not trained',
            completed: 'Completed',
            unsettled: 'Unsettled',
            present: 'Present',
            absent: 'Absent',
            pending: 'Pending',
            ready: 'Ready',
            active: 'Active',
            suspended: 'Suspended',
            revoked: 'Revoked',
            approved: 'Approved',
            reopened: 'Reopened',
            basis_not_retained: 'Basis not retained',
            reason_notified_basis_recorded: 'Reason notified; basis held',
            needs_evidence: 'Needs evidence',
            evidenced: 'Evidenced',
            not_started: 'Not started',
            in_progress: 'In progress',
            evidence_captured: 'Evidence captured',
            answered: 'Answered',
        },
        missing: {
            risk_tier: 'An approved risk level from a reviewed classification is needed.',
            impact_assessment: 'An approved impact assessment is needed for a high-risk system.',
            validation: 'A validation signed for this version is needed.',
            validation_findings: 'Every finding on this version’s validation must be closed on a re-test.',
            security_tests: 'Every attack category needs a record, and none of poisoning, evasion or extraction may be open for a high-risk version.',
            rollback_plan: 'A rollback plan naming an earlier version of this system is needed.',
            senior_management_approval: 'A high-risk go-live needs the senior management body that approved it, and the date.',
            documents_retained_for_audit: 'The documents kept for audit',
            data_disposition: 'How the data was disposed of or isolated',
            model_disposition: 'How the models were disposed of or isolated',
            configuration_disposition: 'How the configuration was disposed of or isolated',
        },
        revalidation: {
            revalidation_required: 'Re-validation required',
            revalidation_not_required: 'Re-validation not required',
        },
        separation: {
            required: 'Required: a different person re-tests',
            not_required: 'Not required: basis stated',
        },
        securityStates: {
            not_mandatory: 'Not mandatory',
            performed: 'Performed',
            not_applicable: 'Not applicable',
            open: 'Open',
        },
        attackCategories: {
            privacy_breach: 'Privacy breach',
            data_poisoning: 'Data poisoning',
            model_poisoning: 'Model poisoning',
            evasion: 'Evasion',
            model_extraction: 'Model extraction',
            prompt_injection: 'Prompt injection',
            hallucination: 'Hallucination',
            bias_in_generated_output: 'Bias in generated output',
        },
        validationAreas: {
            data_fit: 'Fit of training and test data',
            algorithm_soundness: 'Soundness of algorithm and architecture',
            performance_accuracy: 'Performance and accuracy',
            explainability: 'Explainability',
            fairness_bias_control: 'Fairness and bias control',
            stability: 'Stability',
            third_party_purpose_scope_risk: 'Third party: purpose, scope and risk',
            third_party_black_box_on_own_data: 'Third party: black-box tests on the bank data',
            third_party_output_under_operating_conditions: 'Third party: output under operating conditions',
            third_party_provider_documentation: 'Third party: provider documentation',
            third_party_human_controls_and_monitoring: 'Third party: human controls and monitoring',
        },
        versions: {
            heading: 'Versions, change control and rollback',
            description: 'Each change writes a version row; the prior value is kept. A change after release needs a re-validation decision by a named person.',
            add: 'Add a version',
            addTitle: 'Add a version',
            addSubmit: 'Save version',
            empty: 'No version is recorded yet.',
            colSequence: 'No.',
            colKind: 'Kind',
            colModel: 'Model version',
            colApplied: 'Applied',
            colChanged: 'Changed or stopped',
            colConfig: 'Configuration',
            colTests: 'Test result',
            colRollbackPlan: 'Rollback plan',
            colRestored: 'Version restored',
            colRestoredAt: 'Restored at',
            colReason: 'Reason',
            kind: { baseline: 'Baseline', change: 'Change', rollback: 'Rollback' },
            planRecorded: 'Recorded',
            planNotRecorded: 'Not recorded',
            stillInUse: 'In use',
            recordChange: 'Record a change',
            setPlan: 'Set rollback plan',
            fModel: 'Model version',
            fScope: 'Scope affected',
            fPrompt: 'Prompt or instruction set reference',
            fReferenceData: 'Reference-data reference',
            fIntegration: 'Integration reference',
            fConfig: 'Configuration reference',
            fTests: 'Test result reference',
            fAutonomy: 'Autonomy setting',
            fListedDevelopers: 'Listed developers of this version',
            fRevalidation: 'Re-validation decision',
            fRevalidationReason: 'Reason for the decision',
            revalidationRequiredHint: 'Required when the system is in production.',
            revalidationOptionalHint: 'Needed only for a change in production.',
            changeDecisionHint: 'A change needs a re-validation decision and a reason.',
            changeTitle: 'Record a change',
            changeSubmit: 'Save change',
            fChangeSummary: 'What changed',
            fChangedFields: 'Fields changed',
            changedField: {
                model_version: 'Model version',
                prompt_reference: 'Prompt reference',
                reference_data_reference: 'Reference-data reference',
                integration_reference: 'Integration reference',
                autonomy_level: 'Autonomy setting',
                scope_affected: 'Scope affected',
                configuration_reference: 'Configuration reference',
                test_result_reference: 'Test result reference',
                listed_developer_user_ids: 'Listed developers',
            },
            planTitle: 'Rollback plan',
            planSubmit: 'Save plan',
            fLastGoodVersion: 'Last known-good version',
            fSteps: 'Steps to restore',
            fPlanOwner: 'Owner of the plan',
            fLastRehearsal: 'Date of the last rehearsal',
            rollbackHeading: 'Rollback events',
            rollbackEmpty: 'No rollback is recorded.',
            recordRollback: 'Record a rollback',
            rollbackTitle: 'Record a rollback',
            rollbackSubmit: 'Save rollback',
            fRestoredVersion: 'Version restored',
            fRollbackReason: 'Reason',
        },
        security: {
            fNotMandatory: 'Reason not mandatory',
            notMandatoryHint: 'Required when the state is not mandatory. Only poisoning, evasion and extraction may be recorded so, and never for a high-risk system.',
            findingsOpen: 'Findings',
            findingsTitle: 'Security findings',
            findingsDescription: 'A finding closes only when it has a severity, a finding owner, a due date and a change record, and a re-test by someone other than the person who fixed it.',
            findingsEmpty: 'No finding is recorded for this category.',
            addFinding: 'Open a finding',
            fDescription: 'What the finding is',
            fSeverity: 'Severity',
            fFindingOwner: 'Finding owner',
            fDueDate: 'Due date',
            fChangeRecord: 'Change record',
            noChangeRecord: 'No change record yet',
            recordFix: 'Record the fix',
            closeOnRetest: 'Close on re-test',
            setDetails: 'Set severity, owner, due date and change record',
            fFixedBy: 'Fixed by',
            fRetestDate: 'Date of the re-test',
            fRetester: 'Re-tested by',
            fRetestResult: 'Re-test result',
            findingSubmit: 'Save finding',
            findingNotClosed: 'Not closed',
            findingStatus: {
                open: 'Open',
                fixed: 'Fixed',
                closed: 'Closed',
            },
            severities: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            periodic: {
                heading: 'Periodic security testing',
                description: "The period and the last test date are the bank's own and are entered here. The platform derives the next test date and reads it. It runs no test.",
                nextDate: 'Next test date: {date}',
                noNextDate: 'No schedule is set for this version.',
                set: 'Set the schedule',
                formTitle: 'Periodic security testing schedule',
                fPeriod: 'Period in months',
                periodHint: 'A whole number of months, from 1 to 120.',
                fLastTest: 'Date of the last test',
                submit: 'Save schedule',
                states: {
                    not_scheduled: 'Not scheduled',
                    scheduled: 'Scheduled',
                    due: 'Due',
                    overdue: 'Overdue',
                },
            },
            heading: 'Security testing by attack category',
            description: 'One record per attack category for the current version. A blank category is shown as not recorded. The platform runs no test.',
            empty: 'No security test is recorded.',
            blank: 'Not recorded',
            colCategory: 'Attack category',
            colState: 'State',
            colOwner: 'Owner',
            colTest: 'Test',
            colFindings: 'Findings',
            findingCount: '{open} open of {total}',
            record: 'Record',
            update: 'Update',
            formTitle: 'Security test',
            submit: 'Save test record',
            fState: 'State',
            fNotApplicable: 'Reason not applicable',
            naHint: 'Required when the state is not applicable.',
            fMethod: 'Method',
            fTester: 'Tester',
            fTestDate: 'Date of the test',
            fResult: 'Result',
            fArtefact: 'Artefact reference',
            fPersonalData: 'Does the artefact hold personal data?',
            fOwner: 'Owner of this category',
            fSeparation: 'Separation of duties on a re-test',
            fSeparationBasis: 'Basis for not separating',
            separationBasisHint: 'Required when separation is not required.',
        },
        validation: {
            heading: 'Independent validation',
            description: 'A validator independent of the development, deployment, operation and supplier signs the validation. The platform records the attestation and does not judge it.',
            add: 'Open a validation',
            empty: 'No validation is signed.',
            colScope: 'Scope',
            colValidator: 'Validator',
            colUnit: 'Validating unit',
            colSigned: 'Signed',
            colTests: 'Tests',
            colFindings: 'Findings',
            closeFinding: 'Close finding',
            colStatus: 'Status',
            signNow: 'Sign off',
            signNowTitle: 'Sign off this validation',
            signNowMessage: 'Signing records the sign-off against the version under test and cannot be undone. The validator’s independence statement is recorded as stated.',
            signNowSubmit: 'Sign off',
            respond: 'Respond',
            respondTitle: 'Owner response',
            fResponse: 'Response',
            respondSubmit: 'Save response',
            responseLabel: 'Owner response',
            addFinding: 'Open a finding',
            signTitle: 'Open a validation',
            signSubmit: 'Open validation',
            fScope: 'Scope of the validation',
            fValidator: 'Validator',
            fUnit: 'Validating unit',
            fIndependence: 'Independence statement',
            independenceHint: 'The validator states how the unit is independent of development, deployment, operation and the supplier.',
            testsLegend: 'Test',
            fTestArea: 'Test area',
            fTestName: 'Test name',
            fTestMethod: 'Method',
            fTestData: 'Data',
            fTestResult: 'Result',
            confirmTitle: 'Confirm the validation',
            confirmMessage: 'Opening records this validation against the version under test. It is not signed until the validator signs it, and signing needs at least one test on the record.',
            findingTitle: 'Finding',
            openSubmit: 'Open finding',
            closeSubmit: 'Close finding',
            fFindingDescription: 'Description',
            fFindingOwner: 'Owner of the finding',
            fDueDate: 'Due date',
            fRetestDate: 'Date of the re-test',
            fRetestResult: 'Result of the re-test',
            retestHint: 'A finding closes only on a re-test dated after the fix.',
        },
        release: {
            heading: 'Release approval',
            description: 'A release is refused until each precondition is recorded. The approver cannot be the owner, and a listed developer cannot approve the release of their own version.',
            add: 'Approve a release',
            empty: 'No release is approved.',
            colStatus: 'Status',
            colGoLive: 'Go-live date',
            colLabel: 'Approval timing',
            colApprover: 'Approved by',
            colApproved: 'Approved on',
            colStale: 'Stale because',
            beforeGoLive: 'Before go-live',
            afterTheFact: 'After the fact',
            staleBecause: 'Changed:',
            formTitle: 'Approve a release',
            formDescription: 'The seven preconditions are checked when you save. A refused release names each one that is missing.',
            checklistLegend: 'Preconditions',
            checklistUnmet: 'Missing',
            checklistNotChecked: 'Checked on save',
            preconditions: {
                risk_tier: 'A risk level from an approved classification run',
                impact_assessment: 'An approved impact assessment, for a high-risk system',
                validation: 'A validation signed for this version',
                validation_findings: 'Every finding on the signed validation is closed on a re-test',
                security_tests: 'A security-test record for every category, with no open poisoning, evasion or extraction category',
                rollback_plan: 'A rollback plan naming an earlier version',
                senior_management_approval: 'For a high-risk go-live, the senior management body and the decision date',
            },
            rehearsalLabel: 'Rollback rehearsal',
            rehearsalNotDone: 'Not rehearsed',
            rehearsalDone: 'Rehearsed on',
            openFindingsLabel: 'Open validation findings',
            gateUnreadable: 'The gate for this version could not be read. Choose the version again to retry.',
            fVersion: 'Version to release',
            fGoLive: 'Go-live date',
            fSeniorBody: 'Senior management body',
            fSeniorDate: 'Date of the senior management decision',
            seniorHint: 'Required for a high-risk go-live.',
            submit: 'Approve release',
            confirmTitle: 'Approve this release',
            confirmMessage: 'This records the release approval for the chosen version. Approving does not deploy the system; the release is made in the bank’s own systems.',
        },
        impact: {
            heading: 'Impact assessment',
            description: 'The five contents of the assessment, the fairness section and the approval. A change of function, data or operating conditions reopens an approved assessment.',
            add: 'Record an impact assessment',
            empty: 'No impact assessment is recorded.',
            colStatus: 'Status',
            colPurpose: 'Purpose',
            colDpia: 'DPIA or reason',
            colFairness: 'Fairness section',
            colApproval: 'Approval',
            approve: 'Approve',
            fairnessAction: 'Fairness section',
            fairnessRecorded: 'Recorded',
            fairnessNotRecorded: 'Not recorded',
            addTitle: 'Record an impact assessment',
            addSubmit: 'Save assessment',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fMechanism: 'How the system works',
            fOversight: 'How a person oversees and intervenes',
            oversightHint: 'Describe the human oversight and the means of intervention.',
            fFairness: 'Fairness risk',
            fTransparency: 'Transparency risk',
            fExplainability: 'Explainability risk',
            fDiscrimination: 'Risk of discrimination against customers',
            fDpia: 'DPIA record identifier',
            dpiaHint: 'The identifier of the DPIA record. The DPIA text is not copied.',
            fPersonalDataNotApplicable: 'Reason the personal-data assessment does not apply',
            personalDataHint: 'Give a DPIA identifier, or a reason it does not apply.',
            fControls: 'Controls',
            fMonitoring: 'Monitoring plan',
            fIncident: 'Incident response',
            fairnessTitle: 'Fairness section',
            fairnessSubmit: 'Save fairness section',
            fFairnessAssessment: 'Fairness assessment',
            fBiasControls: 'Bias controls',
            fVulnerable: 'Vulnerable groups',
        },
        inventory: {
            heading: 'Annual inventory review',
            description: 'The annual review of this system for the year shown. A profile not confirmed by its due date reads overdue.',
            confirm: 'Confirm review',
            empty: 'This system has no review for this year.',
            colPeriod: 'Period',
            colAnchor: 'Anchor',
            colDue: 'Due',
            colReading: 'Reading',
            anchorKind: {
                first_recorded: 'First recorded',
                last_confirmation: 'Last confirmation',
                review_duty_effective: 'Review duty took effect',
            },
            formTitle: 'Confirm the annual review',
            submit: 'Confirm review',
            fOutcome: 'Outcome',
            outcome: { confirmed: 'Confirmed without change', changed: 'Changed' },
            fChangeSummary: 'What changed',
        },
        components: {
            heading: 'AI component register',
            description: 'Libraries, models, datasets and dependencies, each row with its integrity check. A row missing a field reads incomplete.',
            empty: 'No component register is recorded.',
            noRows: 'The register lists no component yet.',
            record: 'Record the register',
            integrity: 'Record an integrity check',
            advisory: 'Record an advisory',
            supplyReview: 'Record a supply-chain review',
            incompleteCount: '{count} rows are incomplete.',
            supplyReviewReading: 'Supply-chain review',
            colKind: 'Kind',
            colName: 'Name',
            colSupplier: 'Supplier or developer',
            colVersion: 'Version',
            colIntegrity: 'Integrity check',
            colReading: 'Reading',
            missingFields: 'Missing',
            integrityHeading: 'Integrity checks',
            integrityEmpty: 'No integrity check is recorded.',
            advisoryHeading: 'Advisories',
            advisoryEmpty: 'No advisory is recorded.',
            kind: { library: 'Library', model: 'Model', dataset: 'Dataset', dependency: 'Dependency' },
            formTitle: 'Record the component register',
            formDescription: 'Saving appends a new snapshot. The earlier register stays readable.',
            submit: 'Save',
            rowLegend: 'Component {n}',
            fKind: 'Kind',
            fName: 'Name',
            fSupplier: 'Supplier or developer',
            fVersion: 'Version',
            fLicence: 'Licence',
            fCheck: 'Integrity check value',
            fOrigin: 'Origin',
            fAffected: 'Attack categories this component affects',
            addRow: 'Add a component',
            removeRow: 'Remove',
            integrityFormTitle: 'Record an integrity check',
            fComponent: 'Component',
            fMethod: 'Method of check',
            fCheckValue: 'Check value',
            fCheckedOn: 'Date checked',
            advisoryFormTitle: 'Record an advisory',
            fAdvisoryRef: 'Advisory reference',
            fReceivedOn: 'Date received',
            fRiskDecision: 'Risk decision',
            reviewFormTitle: 'Record a supply-chain review',
            fReviewSummary: 'Summary of the review',
        },
        provenance: {
            heading: 'Data provenance and operational logs',
            description: 'The sources of training, test and operating data, the quality and leakage references, and the log retention with its citation.',
            record: 'Record provenance',
            empty: 'No provenance is recorded.',
            sources: 'Sources',
            thirdParty: 'Third party',
            synthetic: 'Synthetic data',
            qualityPolicy: 'Data-quality policy',
            leakageControl: 'Leakage control',
            retention: 'Log retention',
            citation: 'Citation',
            citationMissing: 'No citation recorded',
            revision: 'Revision',
            dataUse: { training: 'Training', test: 'Test', operating: 'Operating' },
            formTitle: 'Record data provenance',
            submit: 'Save provenance',
            fDataUse: 'Data use',
            fSource: 'Source of the data',
            fLawfulBasis: 'Lawful-basis statement',
            fThirdParty: 'Supplied by a third party',
            fSupplier: 'Supplier name',
            fWarranty: 'Warranty of source',
            fSharing: 'Right to share',
            fSynthetic: 'Uses synthetic data',
            fSyntheticAssessment: 'Synthetic-data assessment',
            fInputLog: 'Input-processing log reference',
            fQualityPolicy: 'Data-quality policy reference',
            fLeakage: 'Leakage control reference',
            fRetention: 'Log retention period',
            retentionHint: 'The period the bank sets for operational logs.',
            fCitation: 'Citation of the rule that sets the period',
            citationHint: 'Required with a retention period.',
        },
        decisions: {
            heading: 'Decision records',
            description: 'For each automated decision that affects an obligation, a financial position or access to a product: the basis and key factors, and the explanation given.',
            add: 'Record a decision',
            empty: 'No decision is recorded.',
            colDate: 'Decision date',
            colRef: 'Reference',
            colEffect: 'Effect',
            colBasis: 'Basis',
            colExplanation: 'Explanation given',
            colVersion: 'Version on the decision date',
            versionLegacy: 'Version current when recorded',
            versionNone: 'No version was in force on the decision date',
            basisRuleHeading: "The platform's reading",
            basisRule: "ComplianceOne's reading of Điều 20 khoản 5: for every automated decision that directly affects a customer's legal liability, financial obligations, or access to or use of a product or service, the basis and the main factors are kept, whatever the decision's purpose. For a decision made to prevent fraud, scams, money laundering or terrorism financing, or to ensure cyber security, your organisation need only tell the customer a reason suited to its risk review and to keeping the customer's assets safe; it may tell the customer more. No explanation may disclose source code, detailed algorithms, parameter sets, or business or technology secrets. This is the platform's reading of the text, not a legal review.",
            effect: {
                legal_or_financial_obligation: 'Legal liability or financial obligation',
                customer_access: 'Access to a product or service',
            },
            category: {
                standard: 'Standard',
                fraud: 'Fraud',
                scam: 'Scams',
                money_laundering: 'Money laundering',
                terrorism_financing: 'Terrorism financing',
                cyber_security: 'Cyber-security',
            },
            formTitle: 'Record a decision',
            submit: 'Save decision',
            fDate: 'Decision date',
            fRef: 'Decision reference',
            fEffect: 'Effect',
            fCategory: 'Decision category',
            categoryHint: 'For fraud, scams, money laundering, terrorism financing or cyber security, the institution need only tell the customer a reason; the basis and key factors are still stored.',
            fBasis: 'Basis for the decision',
            basisHint: 'Leave empty only if the basis was not kept; the record then shows basis not retained.',
            fKeyFactors: 'Key factors',
            factorsHint: 'One factor per line.',
            fExplanation: 'Explanation given to the customer',
            fDelivered: 'Date delivered',
            fChannel: 'Channel',
            fStatedReason: 'Stated reason',
            restrictedHint: 'What the customer is told. For this category the institution need only tell the customer a reason; if it told the customer more, record that explanation too.',
            restrictedBasisHint: 'Stored, not told to the customer. Leave empty only if the basis was not kept; the record then shows basis not retained.',
        },
        disclosures: {
            heading: 'AI disclosures in use',
            description: 'The text in use on the date shown, per kind, product and channel. A new text is recorded beside the old one; it never replaces it.',
            add: 'Record a disclosure',
            notRecorded: 'No disclosure is recorded for this date.',
            colKind: 'Kind',
            colProduct: 'Product',
            colChannel: 'Channel',
            colVersion: 'Version',
            colGoLive: 'Go-live date',
            colText: 'Text',
            kind: {
                pre_service_information: 'Information before the service',
                ai_interaction_statement: 'Statement that the customer is dealing with AI',
                internal_user_information: 'Information for internal users',
            },
            formTitle: 'Record a disclosure',
            submit: 'Save disclosure',
            fOn: 'Date in use',
            fKind: 'Kind',
            fProduct: 'Product',
            fChannel: 'Channel',
            fGoLive: 'Go-live date',
            fText: 'Text in use',
            textHint: 'Enter or paste the text. The platform does not write it.',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fLimits: 'Limits',
            fGuidance: 'Interpretation guidance',
            fRoute: 'Route to ask for human intervention',
            fFeedback: 'Feedback channel',
            fSameDayReason: 'Reason for a second version on the same day',
            sameDayHint: 'Required only when another version of this text goes live on the same day.',
            noChannels: 'Record a channel before recording a disclosure.',
            pendingHeading: 'Versions awaiting a second person',
            pendingDescription: 'A version takes effect only when a second person approves it. The person who recorded it cannot approve it.',
            pendingEmpty: 'No version is awaiting approval.',
            inUseHeading: 'In use on the date shown',
            approve: 'Approve version',
        },
        payment: {
            heading: 'Automated payment authority',
            description: 'An authority per customer agreement: the eleven elements, the limits against the ceiling the installed framework declares, and suspension or revocation. The platform does not execute or stop a payment.',
            add: 'Record an authority',
            empty: 'No payment authority is recorded for this system.',
            colState: 'State',
            colAgreement: 'Agreement',
            colAutonomy: 'Autonomy on the system',
            colPerDay: 'Per-day limit',
            colCeiling: 'Ceiling source',
            colMissing: 'Missing elements',
            colRelief: 'Confirmation relief',
            relief: {
                heading: 'Transaction confirmation relief',
                open: 'Confirmation relief',
                openSr: 'for agreement {agreement}',
                close: 'Close the relief',
                intro: 'What the installed framework declares about relief from transaction confirmation for payments the AI system executes within this agreement, and which of its conditions are recorded on this authority.',
                notApplied: 'The platform records what a person states. It does not apply the relief, confirm, count or classify a transaction, or compute any total the referenced instrument defines.',
                statedBy: 'Stated by {framework}, {provision}.',
                refersTo: 'Refers to',
                amendedBy: 'as amended by {list}',
                lifts: 'Confirmation it lifts',
                quantities: 'Totals the value is left out of',
                annexVersion: 'Annex versions',
                conditionsHeading: 'Conditions',
                colCondition: 'Condition',
                colBasis: 'Basis',
                colRecorded: 'On this authority',
                colMissing: 'Still to record',
                recorded: 'Recorded',
                notRecorded: 'Not recorded',
                effectHeading: 'Effect',
                notLiftedHeading: 'What it does not lift',
                reachHeading: 'Reach',
                namedSection: '{section}: {framework}, {provision}',
                reading: {
                    conditions_not_recorded: 'Conditions not recorded',
                    conditions_recorded: 'Conditions recorded',
                    relied_on: 'Relied on',
                    relied_on_conditions_no_longer_recorded: 'Relied on; conditions no longer recorded',
                },
                reliance: 'Relied on by {who} on {when}. Reason: {reason}',
                rely: 'Record that the unit relies on this relief',
                relyDisabled: 'Reliance can be recorded once every condition is recorded on this authority.',
                relyTitle: 'Record reliance on the relief',
                relyDescription: 'Records who relies on the relief for this agreement, when and why. It cannot be recorded while no approved terms are in force, by the person who wrote the terms in force, or by anyone who wrote an approved version that set or changed either of the two statements. The platform does not apply the relief.',
                relySubmit: 'Record reliance',
                fReason: 'Why the unit relies on the relief',
                relied: 'Reliance on the relief is recorded.',
                statements: 'Record the statements',
                statementsTitle: 'Record the statements the relief rests on',
                statementsDescription: 'Saved as a change to the authority. It takes effect when a second person approves it.',
                statementsSubmit: 'Save as a change',
                statementsSaved: 'The change is saved. A second person must approve it.',
                statementsPending: 'The statements can be recorded once a second person approves the change pending on this authority.',
                fAiExecution: 'How the unit tells a transaction the system executed',
                aiExecutionHint: 'The reference of the marker, field or log that shows a transaction was executed by the system and not by a person.',
                fAutomationConditions: 'Statement that the conditions for automation hold',
                automationConditionsHint: 'A person states that each condition the instrument sets for automating this system holds.',
                evidence: {
                    ai_execution_reference: 'Statement of how AI-executed transactions are identified',
                    automation_conditions_statement: 'Statement that the conditions for automation hold',
                    terms_approved: 'Terms approved by a second person',
                    ceiling_checked: 'Per-day limit checked against the declared ceiling',
                    authority_active: 'Authority active (not suspended, revoked or expired)',
                },
                noneDeclared: 'No installed framework declares a relief from transaction confirmation for payment authorities.',
                unreadable: 'A confirmation relief an installed framework declares could not be read.',
                notDeclared: 'Not declared',
                listReferenceOnly: 'Declared by a framework installed for reference only',
                listNotOperational: 'Declared by a framework that is not operational',
                listUnreadable: 'Declared, but could not be read',
                activationUpcoming: 'The framework that declares this relief is not yet in force. The relief applies from the date it comes into force; reliance can be recorded before then.',
            },
            scopeHeading: 'Scope flagged for legal review',
            scopeIntro: 'An organisation the installed framework names in one provision and leaves out of its scope provision. The platform treats it as neither covered nor excluded.',
            scopeNamed: 'Named in {named_in}; not named in {absent_from}.',
            scopeStatus: { under_legal_review: 'Flagged for legal review' },
            scopeSource: 'Stated by {framework}',
            scopeUnreadable: 'A scope qualification an installed framework declares could not be read.',
            scopeLoadFailed: 'The scope notice could not be read.',
            scopeRetrySr: 'read the scope notice again',
            recordEvent: 'Record an event',
            approve: 'Approve this change',
            exportHistory: 'Export authority history',
            customerClass: { individual: 'Individual', organisation: 'Organisation' },
            element: {
                customer_class: 'Customer class',
                agreement_reference: 'Agreement reference',
                automated_transaction_scope: 'Scope of automated transactions',
                per_transaction_limit_vnd: 'Per-transaction limit',
                per_day_limit_vnd: 'Per-day limit',
                valid_from: 'Valid from',
                valid_until: 'Valid until',
                suspension_conditions: 'Suspension conditions',
                customer_termination_clause_reference: 'Customer termination clause',
                identity_verification_statement: 'Identity-verification statement',
                pause_or_cancel_tool_reference: 'Customer pause or cancel tool',
            },
            formTitle: 'Record a payment authority',
            formDescription: 'A per-day limit above the ceiling the installed framework declares is refused, and the refusal names that framework and its clause.',
            submit: 'Save authority',
            fCustomerClass: 'Customer class',
            fAgreement: 'Agreement reference (a file or a reference)',
            agreementHint: 'The agreement is recorded by reference.',
            fScope: 'Scope of automated transactions',
            fScopeItem: 'Transaction type',
            scopeHint: 'Add each type, one at a time.',
            addScope: 'Add to the scope',
            removeScope: 'Remove',
            fPerTransaction: 'Per-transaction limit (VND)',
            fPerDay: 'Per-day limit (VND)',
            perDayHint: 'Checked against the ceiling the installed framework declares.',
            fValidFrom: 'Valid from',
            fValidUntil: 'Valid until',
            fSuspension: 'Suspension conditions',
            fTermination: 'Customer termination clause reference',
            fIdentity: 'Identity-verification statement',
            identityHint: 'Entered by the person who checked the verification. The platform does not check biometrics.',
            fPauseTool: 'Customer pause or cancel tool reference',
            fLead: 'Payments lead',
            eventTitle: 'Record an event',
            eventSubmit: 'Save event',
            eventKind: {
                activate: 'Activate',
                suspend: 'Suspend',
                revoke: 'Revoke',
                execution_summary: 'Execution summary',
            },
            fEventKind: 'Event',
            fReportedAt: 'Time reported',
            reportedHint: 'The time the stop was reported.',
            fImplementedAt: 'Time implemented',
            implementedHint: 'Leave empty until the stop is implemented; the state then reads not evidenced.',
            fReason: 'Reason',
            fCustomerRef: 'Customer reference',
            fExecutedOn: 'Date executed',
            fTxCount: 'Number of transactions',
            fLargest: 'Largest transaction (VND)',
            fLargestRef: 'Reference of the largest transaction',
            fDailyTotal: 'Daily total (VND)',
        },
        interventions: {
            heading: 'Human oversight and interventions',
            description: 'Each intervention is recorded with its reason. A pause or stop reads requested until a person enters the confirmed time.',
            add: 'Record an intervention',
            empty: 'No intervention is recorded.',
            colKind: 'Kind',
            colContent: 'Content changed',
            colReason: 'Reason',
            colReading: 'State',
            colRequested: 'Requested at',
            colConfirmed: 'Confirmed at',
            kind: {
                content_change: 'Content change',
                override: 'Override',
                pause: 'Pause',
                stop: 'Stop',
                emergency_stop: 'Emergency stop',
                manual_operation: 'Manual operation',
                resume: 'Resume',
            },
            confirm: 'Enter confirmed time',
            formTitle: 'Record an intervention',
            submit: 'Save intervention',
            fKind: 'Kind',
            fContent: 'Content changed',
            fReason: 'Reason',
            fRequestedAt: 'Time requested',
            requestedHint: 'Required for a pause, stop, emergency stop or manual operation.',
            confirmTitle: 'Enter the confirmed time',
            confirmSubmit: 'Save confirmed time',
            fConfirmedAt: 'Time confirmed in the bank’s system',
            confirmedHint: 'Taken from the bank’s own system; the platform does not read it.',
            alreadyRetired: 'This system is retired.',
            retireHeading: 'Retirement',
            retireSectionDescription: 'Retire this system when it is taken out of service; retiring it needs a decommissioning record.',
            retire: 'Retire this system',
            retireTitle: 'Retire this system',
            retireDescription: 'A retired system needs a decommissioning record: the documents kept for audit, and how the data, models and configuration were disposed of or isolated.',
            retireSubmit: 'Retire system',
            decom: {
                documents_retained_for_audit: 'Documents retained for audit',
                data_disposition: 'Disposition of the data',
                model_disposition: 'Disposition of the models',
                configuration_disposition: 'Disposition of the configuration',
            },
            colChannel: 'Channel',
            colDecision: 'Decision',
            fChannel: 'Channel',
            fDecision: 'Decision identifier',
            decisionHint: 'Required for a content change, an override or a manual operation. Leave blank for a pause, stop, emergency stop or resume.',
            fResumes: 'Intervention resumed',
            resumeHint: 'The pause, stop or emergency stop this resume follows.',
            requestedNote: 'The time of the request is recorded when you save.',
            approve: 'Approve',
            awaitingApproval: 'Awaiting a second person',
            colApproval: 'Second person',
            approved: 'Approved',
            notRequired: 'Not required',
        },
        channels: {
            heading: 'Channels and supervisor lines',
            description: 'Each AI-assisted channel of this system, with the supervisor’s line over it. Interventions and disclosures name a channel.',
            add: 'Record a channel',
            empty: 'No channel is recorded for this system.',
            colName: 'Channel',
            colLine: 'Supervisor’s line',
            colRecorded: 'Recorded on',
            export: 'Export log and disclosures',
            formTitle: 'Record a channel',
            submit: 'Save channel',
            fName: 'Channel name',
            fLine: 'Supervisor’s line',
            lineHint: 'The unit or role that supervises this channel.',
            exportTitle: 'Export the channel log',
            exportSubmit: 'Export',
            exportFrom: 'From',
            exportTo: 'To',
            colDualControl: 'Dual control',
            fDualControl: 'A second person approves each pause, stop and resume',
            dualControlHint: 'When set, a pause, a stop and a resume take effect only after a second person approves it. An emergency stop is never held.',
            registerExport: 'Export the pause and stop register',
            registerTitle: 'Export the pause and stop register',
        },
        channelStatus: {
            heading: 'Channel status',
            description: 'The state of each channel today, read from its record: the disclosures in use, the versions awaiting a second person, and the interventions still open. Read only.',
            empty: 'No channel is recorded for this system.',
            colChannel: 'Channel',
            colState: 'State',
            colDisclosures: 'Disclosures in use',
            colAwaiting: 'Awaiting a second person',
            colOpen: 'Open interventions',
            noneOpen: 'None',
            openAwaiting: 'awaiting a second person',
            openUnanswered: 'escalation unanswered',
            openRequested: 'awaiting the bank’s confirmation',
            state: {
                running: 'Running',
                paused: 'Paused',
                stopped: 'Stopped',
                emergency_stopped: 'Emergency stopped',
            },
        },
        escalations: {
            heading: 'Escalations and responses',
            description: 'Every intervention is escalated to the system owner for cause analysis and to the second line for the board report. Each escalation stays open until its addressee records a response.',
            empty: 'No escalation is raised.',
            colAddressee: 'Addressed to',
            colIntervention: 'Intervention',
            colRaised: 'Raised on',
            colResponse: 'Response',
            none: 'No one is named; the task went to the person who recorded it.',
            respond: 'Record a response',
            respondTitle: 'Record a response',
            respondSubmit: 'Save response',
            fResponse: 'Response',
            responseHint: 'Recorded once, with the addressee’s name and the time.',
            addressee: {
                owner: 'System owner',
                second_line: 'Second line',
            },
            colAnsweredAs: 'Answered as',
            answeredAs: {
                owner: 'System owner',
                attest_fallback: 'Attest holder, in place of the owner',
            },
        },
        briefings: {
            heading: 'Staff briefings on the system’s limits',
            description: 'Who was briefed, when, and on which version of the procedure. Completion of training is held in the training register.',
            add: 'Record a briefing',
            empty: 'No briefing is recorded.',
            colDate: 'Briefed on',
            colProcedure: 'Procedure version',
            colAttendees: 'Briefed',
            formTitle: 'Record a briefing',
            submit: 'Save briefing',
            fDate: 'Day of the briefing',
            fLimits: 'Limits covered',
            fProcedure: 'Procedure version',
            fAttendees: 'Staff briefed (user IDs, separated by commas)',
            fTraining: 'Training record reference (optional)',
            addAttendee: 'Add this person',
            removeAttendee: 'Remove',
        },
        monitoring: {
            heading: 'Performance and behaviour monitoring',
            description: 'Comparisons of predicted and actual outcomes entered or imported, the owner’s thresholds, and events that stay open until a person closes them.',
            addComparison: 'Record a comparison',
            addEvent: 'Record an event',
            setThresholds: 'Set thresholds',
            eventsHeading: 'Events',
            eventsEmpty: 'No event is recorded.',
            comparisonsHeading: 'Comparisons',
            comparisonsEmpty: 'No comparison is recorded.',
            colReading: 'State',
            colKind: 'Kind',
            colDetected: 'Detected on',
            colDescription: 'Description',
            colResponse: 'Response',
            colMetric: 'Metric',
            colMeasured: 'Measured on',
            colPredicted: 'Predicted',
            colActual: 'Actual',
            colDeviation: 'Deviation',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            close: 'Close with a response',
            kind: {
                output_deviation: 'Output deviation',
                quality_decline: 'Quality decline',
                bias: 'Bias',
                model_drift: 'Model drift',
                data_drift: 'Data drift',
                threshold_crossing: 'Threshold crossed',
            },
            source: { entered: 'Entered by a person', imported: 'Imported' },
            eventTitle: 'Record an event',
            submitEvent: 'Save event',
            fKind: 'Kind',
            fDescription: 'Description',
            fMetric: 'Metric',
            comparisonTitle: 'Record a comparison',
            submitComparison: 'Save comparison',
            fMeasuredOn: 'Measured on',
            fPredicted: 'Predicted value',
            fActual: 'Actual value',
            fSource: 'Source',
            closeTitle: 'Close the event',
            submitClose: 'Close event',
            fResponse: 'Response',
            thresholdsTitle: 'Thresholds',
            submitThresholds: 'Save thresholds',
            thresholdLegend: 'Threshold {n}',
            fMaxDeviation: 'Largest permitted deviation',
            addThreshold: 'Add a threshold',
        },
        duties: {
            heading: 'Duty holders and competence',
            description: 'The people named against a duty to monitor, detect deviation or intervene. Training is read against a completed assignment; the review falls due yearly.',
            add: 'Name a duty holder',
            capacity: 'Record capacity',
            empty: 'No duty holder is named.',
            colPerson: 'Person',
            colDuty: 'Duty',
            colTraining: 'Training',
            colCompetence: 'Competence review',
            colDue: 'Review due',
            duty: { monitor: 'Monitor', detect_deviation: 'Detect deviation', intervene: 'Intervene' },
            review: 'Record a competence review',
            addTitle: 'Name a duty holder',
            submit: 'Save duty holder',
            fPerson: 'Person',
            fDuty: 'Duty',
            fTraining: 'Training assignment reference (optional)',
            trainingHint: 'A training completion counts only against a completed assignment.',
            reviewTitle: 'Competence review',
            submitReview: 'Save review',
            fReviewedOn: 'Date reviewed',
            fSummary: 'Summary of the review',
            capacityTitle: 'Capacity record',
            submitCapacity: 'Save capacity',
            fBasis: 'Basis',
            basis: {
                unit_builds_or_runs: 'The unit builds or runs the system',
                outsourced: 'Outsourced',
            },
            fCapacity: 'Capacity statement',
            fSupplierName: 'Supplier name',
            supplierHint: 'Required for an outsourced system.',
            fSupplierCapacity: 'Supplier capacity statement',
            fAssessedOn: 'Date the supplier was assessed',
        },
        bankingTriggers: {
            heading: 'Banking triggers',
            intro: 'The trigger facts recorded for this system, compared with the thresholds the installed framework declares. Where the triggers reach the system and a fact exceeds a threshold, the platform raises a classification proposal, shown here and in the classification review. A person classifies; the platform sets no tier.',
            listLabel: 'Within a high-risk entry of the official list (conditions other than value)',
            listStatus: { listed: 'Within an entry', not_listed: 'Not within an entry' },
            listNotRecorded: 'Not recorded',
            listRecorded: 'Recorded by {user} on {date}',
            listHint: 'Recorded on the system form: whether the system meets the conditions of a high-risk entry of the official list other than the transaction or loan value, which the thresholds supply. You record the answer; the platform does not decide it.',
            preconditionLabel: 'Precondition',
            precondition: "The triggers reach only a system within an entry of {list}, on that entry's conditions other than value ({provision}).",
            preconditionNone: 'No installed framework declares a precondition: the triggers reach every system.',
            scopeLabel: 'Reach',
            scope: {
                applies: 'The triggers reach this system',
                not_applicable: 'Not within an entry of the list: no banking trigger applies',
                not_recorded: 'Not recorded: whether the triggers reach this system is not known',
                precondition_unreadable: 'The precondition an installed framework declares could not be read, so the platform cannot show whether the triggers reach this system, and raises no proposal',
            },
            factsNotRecorded: 'No trigger facts are recorded for this system.',
            noThresholds: 'No installed framework declares a banking threshold.',
            unreadable: '{count} declaration(s) an installed framework made could not be read.',
            exceedancesHeading: 'Thresholds exceeded',
            noExceedance: 'No recorded fact exceeds a declared threshold.',
            colThreshold: 'Trigger',
            colValue: 'Recorded value (VND)',
            colAmount: 'Threshold amount (VND)',
            colProvision: 'Provision',
            measure: {
                per_transaction_payment: 'One payment',
                daily_total_per_customer: "One customer's daily total",
                credit_amount: 'Credit amount',
            },
            customerClass: {
                individual: 'individual customer',
                organisation: 'organisation customer',
                any: 'any customer',
            },
            comparison: { greater_than: 'over', greater_or_equal: 'at least' },
            thresholdLabel: '{measure}, {class}: {comparison} the amount',
            proposalHeading: 'Classification proposal raised',
            proposalText: 'Run a classification screening to review them: the classification review shows them beside the tier control. The tier is the one a reviewer records there.',
            noProposal: 'No classification proposal is raised.',
            reviewProposalHeading: 'Banking-trigger proposal for this review',
            reviewProposalIntro: 'This system is recorded as within a high-risk entry of the official list the installed framework names, and a recorded fact exceeds the thresholds below. Weigh them in this review; the tier is the one you record.',
            reviewProposalIntroNoPrecondition: 'A recorded fact on this system exceeds the thresholds below, which the installed framework applies to every system. Weigh them in this review; the tier is the one you record.',
            reviewProposalItem: '{threshold}: {value} VND against {amount} VND ({provision})',
        },
        systemDates: {
            heading: 'Dated obligations for this system',
            intro: 'The commencement dates the installed framework declares, as they fall on this system: whether it was in operation before the framework took effect, and whether your organisation has a system of the tier a carve-out names.',
            inOperationSince: 'In official operation since',
            inOperationNotRecorded: 'Not recorded. Set it on the system form; until then a date that depends on it is not shown.',
            empty: 'No installed framework declares a dated obligation for AI systems.',
            colReference: 'Commencement provision',
            colDate: 'Date for this system',
            colReason: 'Why',
            colSource: 'From',
            noDate: 'No date',
            notStated: 'a date not stated',
            partNotNamed: 'the provisions the transitional stage does not move',
            decidedBy: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this.',
            decidedByRow: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this — see that row.',
            reason: {
                every_system: 'The same date for every system',
                unit_condition_not_held: 'No system of your organisation is recorded as {tier}, the tier the carve-out names, so the stated date applies to every system',
                unit_condition_undetermined: 'Depends on classification: {count} system of your organisation not yet classified. If it is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                unit_condition_undetermined_plural: 'Depends on classification: {count} systems of your organisation not yet classified. If any is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                new_systems_only: 'Applies only to a system put into operation on or after {cutoff}',
                new_system_in_stated_period: 'Put into operation on or after {cutoff}: this date applies if that was within the period the provision states',
                existing_system: 'In operation before {cutoff}',
                existing_system_moved: 'In operation before {cutoff}: the transitional date applies',
                existing_system_partly_moved: 'In operation before {cutoff}: this date applies to the provisions the transitional stage does not move; see that stage for the rest',
                new_system: 'Put into operation on or after {cutoff}',
                unit_holds_tier: 'Your organisation has a system classified {tier}, the tier the carve-out names',
                existing_systems_only: 'Applies only to a system in operation before {cutoff}',
                operation_start_not_recorded: 'Depends on when the system went into operation, which is not recorded',
                every_system_in_part: 'Applies to {part} for every system; for the rest it depends on when the system went into operation, which is not recorded',
                declaration_unreadable: 'The commencement the installed framework declares for this could not be read',
                applies_through_condition_met: 'In operation before {cutoff}, and its recorded autonomy level automates the execution of decisions: the provision applies to it through another from this date',
                applies_through_condition_not_met: 'In operation before {cutoff}, and its recorded autonomy level does not automate the execution of decisions: the date for systems already in operation applies',
                applies_through_condition_not_recorded: 'Depends on the autonomy level, which is not recorded: {dateMet} if the system automates the execution of decisions, {dateNotMet} if not. Nothing reads overdue.',
            },
            // fix-sprint-20261031-1200 F39(e) — a provision brought in through another, with its reading.
            throughNote: '{provision} through {via}: {reading}',
        },
        inventoryRegister: {
            heading: 'Inventory completeness',
            statement: 'This register holds the AI systems recorded in ComplianceOne and the legacy entries imported from your organisation’s existing inventory. It can show only what has been recorded: a system nobody recorded is not in it.',
            recordedSystems: 'Systems recorded',
            legacyEntries: 'Imported legacy entries',
            noImport: 'No legacy import has been made.',
            lastImport: 'Last import',
            received: 'Received',
            imported: 'Imported',
            notImported: 'Not imported',
            refusalHeading: 'Rows not imported',
            colRow: 'Row',
            colName: 'Name',
            colReason: 'Reason',
            refusal: {
                name_missing: 'No name',
                already_registered: 'Already registered',
                duplicate_in_batch: 'Repeated in this file',
            },
            importHeading: 'Legacy import',
            importHint: 'One system per line: name | legacy reference | owner as recorded | status as recorded. A name already in the register, or repeated in the file, is not imported.',
            importField: 'Rows from the existing inventory',
            importSource: 'Source label',
            importSubmit: 'Import rows',
            importDone: 'Import recorded.',
            importFailed: 'The import was not recorded.',
            importEmpty: 'Enter at least one row.',
            legacyHeading: 'Imported legacy entries',
            legacyEmpty: 'No legacy entry is imported.',
            colLegacyRef: 'Legacy reference',
            colOwnerRecorded: 'Owner as recorded',
            colStatusRecorded: 'Status as recorded',
            colImportedOn: 'Imported on',
            colTier: 'Risk tier',
            notClassified: 'Not classified',
            importedMarker: 'Imported',
            obligationsHeading: 'Dated obligations',
            obligationsHint: 'The dates the installed AI instruments state, each with the status and the source it is read from. What each date covers, including any transitional rule or carve-out, is in its own column, worded as the installed framework words it.',
            obligationsEmpty: 'No installed AI instrument states a dated obligation.',
            colReference: 'Article',
            colFramework: 'Instrument',
            colStatus: 'Status',
            colDate: 'Date or period',
            colReasonNotEnforced: 'Why not enforced',
            colCarveOut: 'What the date covers',
            colSource: 'Pack source',
            noPeriod: 'The framework states no date or period.',
            reasonNotApplicable: 'Not applicable',
            annualRecurrence: 'Each year, by {day}/{month}',
            unitUnknown: '{amount} ({unit})',
            unit: {
                hours: 'hours',
                days: 'days',
                working_days: 'working days',
                calendar_days: 'calendar days',
                weeks: 'weeks',
                months: 'months',
                years: 'years',
                annual: 'years',
                working_days_before_event: 'working days before the event',
                calendar_days_before_event: 'calendar days before the event',
                calendar_days_after_event: 'calendar days after the event',
                before_use: 'Before the system is put into use',
            },
            unitOne: {
                hours: 'hour',
                days: 'day',
                working_days: 'working day',
                calendar_days: 'calendar day',
                weeks: 'week',
                months: 'month',
                years: 'year',
                annual: 'year',
                working_days_before_event: 'working day before the event',
                calendar_days_before_event: 'calendar day before the event',
                calendar_days_after_event: 'calendar day after the event',
            },
            periodRecurring: 'Every {period}',
            noCarveOut: 'The installed framework states no separate scope for this date.',
            unitHoldsTier: 'Your organisation has a system classified at the tier the carve-out names, so the carve-out date applies. Without one, the date is {stated}.',
            status: {
                in_force: 'In force',
                upcoming: 'Upcoming',
                not_recorded: 'Not recorded',
                not_enforced: 'Not enforced',
            },
            reason: {
                reference_install: 'Installed for reference only',
                not_operational: 'Not operational for this organisation',
            },
            exportHeading: 'Export the inventory',
            exportIntro: 'The organisation-wide register as a file: every system and imported entry, with the completeness limit in its header. The platform assigns no tier.',
            exportButton: 'Export inventory',
            exportFailed: 'The inventory could not be exported.',
        },
        evidenceIndex: {
            heading: 'Regulator evidence index',
            intro: 'The regulator-facing evidence this system holds, by area, with where each area sits.',
            colArea: 'Area',
            colCount: 'Records',
            colLatest: 'Latest',
            colWhere: 'Where it sits',
            none: 'None recorded',
            area: {
                classification: 'Classification runs',
                validation: 'Validation',
                security_tests: 'Security tests',
                interventions: 'Interventions',
                complaints: 'Complaints',
                supplier_arrangements: 'Supplier arrangements',
            },
            where: {
                classification: 'This page, classification section',
                validation: 'This page, validation section',
                security_tests: 'This page, security section',
                interventions: 'This page, interventions section, and the interventions register',
                complaints: 'Complaints register',
                supplier_arrangements: 'This page, supplier section',
            },
        },
        suppliers: {
            heading: 'Supplier arrangements',
            description: 'The arrangement with a vendor, read on the vendor record. A term with no state reads not recorded. The register counts the terms recorded present, out of the eight Điều 18 khoản 3 names; it does not judge the contract.',
            add: 'Record an arrangement',
            empty: 'No arrangement is recorded with this vendor.',
            vendorHint: 'Choose the vendor to read its arrangements.',
            colPeriod: 'Period',
            colClauses: 'Clauses',
            colCompliant: 'Terms recorded',
            colAssurance: 'Assurance report',
            colTraining: 'Training data',
            compliantYes: 'All eight terms recorded present',
            compliantNo: 'Not all eight recorded',
            expires: 'expires',
            clause: {
                assurance_evidence: 'Assurance evidence',
                version_reporting: 'Version reporting',
                monitoring_access: 'Monitoring access',
                continuity_and_transition: 'Continuity and transition',
                regulatory_cooperation: 'Regulatory cooperation',
                training_data_commitment: 'Training-data commitment',
                it_service_contract_rules: 'IT-service contract rules',
                technical_information_and_limits: 'Technical information, model limits and security measures',
            },
            clauseState: { present: 'Present', absent: 'Absent', pending: 'Pending' },
            clauseHint: 'Choose a state; leave empty for not recorded.',
            training: {
                commitment: 'Commitment',
                consent: 'Written consent',
                not_recorded: 'Not recorded',
            },
            // Fix-sprint-20261031-1200 F32 — the supplier page's boundary statement.
            boundary: "ComplianceOne records what the contract says and what the supplier reported. It does not connect to the supplier's systems, does not verify a model's contents and does not judge the contract: monitoring access is evidenced by your organisation's record.",
            // F4 — the assurance warning period, and where its figure comes from.
            fWarnDays: 'Warning period (days before expiry)',
            warnDaysDefault: '30 days unless your organisation sets another figure. This is a ComplianceOne default: the installed framework sets no warning period.',
            warnDaysReading: 'Warning period: {days} days ({source})',
            warnDaysSource: {
                organisation: 'set by your organisation',
                platform_default: 'ComplianceOne default',
            },
            // F35 — the training-data term read with its recorded position.
            trainingBasis: "Điều 18 khoản 3 điểm h: the third party commits not to use your organisation's data to train, fine-tune or improve its model without your organisation's written consent.",
            trainingTerm: {
                recorded: 'Recorded with its position',
                position_not_recorded: 'Position not recorded: the eight terms do not read as recorded',
                clause_not_present: 'The clause is not recorded present',
            },
            fConsentReference: 'Written consent reference',
            fConsentBasis: "Your organisation's basis for consenting",
            consentBasisHint: 'Where personal data is involved, the DPO reviews this basis.',
            // F7 — the supplier record export per period.
            exportRecord: 'Export the supplier record',
            exportTitle: 'Export the supplier record',
            exportDescription: "The arrangements overlapping the period, their terms, the events dated in it, and each system's component register at the end of the period.",
            fExportPeriod: 'Period',
            exportPeriodHint: 'A year (YYYY) or a month (YYYY-MM).',
            fExportFormat: 'File format',
            exportFormat: { csv: 'CSV', xlsx: 'Excel (XLSX)', pdf: 'PDF' },
            exportSubmit: 'Export',
            exportFailed: 'The supplier record could not be exported.',
            formTitle: 'Record a supplier arrangement',
            submit: 'Save arrangement',
            fVendor: 'Vendor',
            fPeriodStart: 'Period starts',
            fPeriodEnd: 'Period ends',
            fTrainingPosition: 'Training-data position',
            fTrainingScope: 'Scope of the consent',
            colVersions: 'Version reports',
            validationSigned: 'validated',
            validationUnknown: 'validation unknown',
            recordedOn: 'Recorded',
            fReviewCycle: 'Review cycle (months)',
            fReviewCycleHint: 'The bank’s review cycle for this arrangement. Leave empty for no cycle.',
            fAssuranceRef: 'Assurance report reference',
            fAssuranceStart: 'Report period starts',
            fAssuranceEnd: 'Report period ends',
            fAssuranceExpires: 'Report expires on',
            colReadings: 'Readings',
            readingReview: 'Review',
            reviewNotSet: 'no cycle set',
            reviewCurrent: 'on cycle',
            reviewOverdue: 'overdue',
            reviewDueOn: 'due',
            assurance: {
                current: 'current',
                expiring: 'expiring',
                expired: 'expired',
            },
            terminatedOn: 'Terminated on',
            chainFlagged: 'Sub-processor change not yet reviewed',
            continuityTested: 'Continuity last tested',
            monitoringReviewed: 'Monitoring access last reviewed',
            regulatorContact: 'Regulator contact',
            regulatorRequests: 'Regulator requests logged',
            addEvent: 'Record a supplier event',
            eventFormTitle: 'Record a supplier event',
            eventSubmit: 'Save event',
            fEventArrangement: 'Arrangement',
            fEventKind: 'Event',
            eventKind: {
                model_version: 'Model version reported',
                sub_processor_change: 'Sub-processor change',
                termination: 'Termination',
                regulator_request: 'Regulator request',
                regulator_contact: 'Regulator contact named',
                continuity_test: 'Continuity test',
                monitoring_access_review: 'Monitoring access review',
                review: 'Arrangement review',
            },
            fModelVersion: 'Model version',
            fSupplierReportedOn: 'Supplier reported on',
            fSubProcessor: 'Sub-processor',
            fComponentRef: 'Component changed',
            fChangedOn: 'Changed on',
            fSummary: 'Summary',
            fEffectiveOn: 'Effective on',
            fReason: 'Reason',
            fRequestReference: 'Request reference',
            fReceivedOn: 'Received on',
            fContactUserId: 'Named contact (a person of this organisation)',
            fRoute: 'Route a regulator request takes',
            fTestedOn: 'Tested on',
            fReviewedOn: 'Reviewed on',
            fOutcome: 'Outcome',
        },
        panels: {
            heading: 'Duties by obligation kind',
            description: 'Each kind lists its duties with the status and the evidence state. A duty marked complete without evidence needs evidence.',
            kind: {
                human_oversight: 'Oversight',
                transparency_labeling: 'Transparency and labelling',
                monitoring: 'Monitoring',
                incident: 'Incident',
            },
            count: '{count} duties',
            empty: 'No duty of this kind is attached.',
            colDuty: 'Duty',
            colStatus: 'Status',
            colEvidence: 'Evidence',
            evidence: {
                needs_evidence: 'Needs evidence',
                evidenced: 'Evidence recorded',
                not_recorded: 'Not recorded',
            },
            needsEvidenceWhy: 'The status cannot stand as complete without an evidence record.',
            edit: 'Edit',
        },
        registers: {
            backToRegister: 'Back to the register',
            inventoryGuide: 'Use this page once a year: choose the period, then confirm each system or record what changed. The full guide explains each outcome and when a review reads overdue.',
            paymentGuide: "Read the payment authorities an AI system holds, with their limits and what each one still lacks. Authorities are recorded and approved on the system's own page.",
            interventionsGuide: "Read the pauses, stops and other human interventions recorded for an AI system. Interventions are recorded on the system's own page; the full guide explains when one reads confirmed.",
            complaintsGuide: 'Record a complaint about an AI-influenced decision, route it to an independent reviewer, note any item withheld from the customer, then record the re-review and the close. The counts below show when a product reaches the threshold your organisation sets.',
            frameworkGuide: 'Record the approved risk-management framework for AI and the map of the three lines of defence. The full guide explains what makes a line complete.',
            boardGuide: 'These are the AI figures the board report carries, read from the registers now. The table shows the time they were read. To change a figure, change the register it is read from.',
            systemsLoading: 'Loading the AI systems…',
            systemsFailed: 'The list of AI systems could not be read.',
            systemsRetrySr: 'read the list of AI systems again',
            systemsEmpty: 'No AI system is recorded in the register yet.',
            thresholdCurrent: 'Current threshold',
            thresholdComplaints: '{count} complaints',
            thresholdLoading: 'Loading the threshold…',
            thresholdFailed: 'The threshold setting could not be read.',
            thresholdRetrySr: 'read the threshold setting again',
            fSystem: 'AI system',
            inventoryTitle: 'AI inventory reviews',
            inventorySubtitle: 'Annual review of every AI system in the register',
            inventorySummary: 'Each system is confirmed or changed by a named person. A system not confirmed by its due date reads overdue.',
            inventoryHeading: 'Reviews for the period',
            inventoryEmpty: 'No system has a review for this period.',
            fPeriod: 'Period',
            colSystem: 'System',
            colPeriod: 'Period',
            colDue: 'Due',
            colReading: 'Reading',
            confirm: 'Confirm',
            confirmTitle: 'Confirm the annual review',
            submit: 'Save',
            fOutcome: 'Outcome',
            paymentTitle: 'Payment authorities',
            paymentSubtitle: 'Automated payment authorities, by system',
            paymentSummary: 'Choose a system to read its payment authorities. The platform records each authority; it does not execute a payment.',
            paymentHeading: 'Authorities for the system',
            paymentEmpty: 'No payment authority is recorded for this system.',
            openSystem: 'Open the system record',
            interventionsTitle: 'Interventions',
            interventionsSubtitle: 'Human oversight records, by system',
            interventionsSummary: 'Choose a system to read its interventions. A pause or stop reads requested until a person enters the confirmed time.',
            interventionsHeading: 'Interventions for the system',
            interventionsEmpty: 'No intervention is recorded for this system.',
            complaintsTitle: 'AI complaints',
            complaintsSubtitle: 'Complaints about AI-influenced decisions, and their human re-review',
            complaintsSummary: 'A complaint is routed to a reviewer who is neither the system owner nor a listed developer. It closes only with a recorded re-review.',
            complaintsHeading: 'Complaints',
            complaintAdd: 'Record a complaint',
            complaintsEmpty: 'No complaint is recorded.',
            colReceived: 'Received',
            colProduct: 'Product',
            colCategory: 'Decision category',
            colStatus: 'Status',
            colReviewer: 'Reviewer',
            colWithheld: 'Withheld items',
            colReReview: 'Re-review',
            reReviewRecorded: 'Recorded',
            route: 'Route',
            withhold: 'Withhold an item',
            reReview: 'Re-review',
            close: 'Close',
            countsHeading: 'Complaints by system and product',
            countsEmpty: 'No complaint is counted yet.',
            colCount: 'Count',
            colThreshold: 'Threshold',
            thresholdNotSet: 'Not set',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            setThreshold: 'Set the threshold',
            createTitle: 'Record a complaint',
            fCategory: 'Decision category',
            fReceivedOn: 'Date received',
            fSummary: 'Summary of the complaint',
            fProduct: 'Product',
            fStatedReason: 'Stated reason',
            fExplanation: 'Explanation given',
            colBasis: 'Decision basis',
            basisReading: {
                recorded: 'Recorded',
                basis_not_retained: 'Basis not retained',
                reason_notified_basis_recorded: 'Reason notified; basis held',
            },
            noDecision: 'No decision linked',
            fDecision: 'Decision complained of',
            fDecisionHint: 'Optional. The decision as recorded in the AI decision register.',
            fExplanationDay: 'Date the explanation was delivered',
            fExplanationChannel: 'Channel the explanation was delivered by',
            fNotifiedMethod: 'How the customer was notified',
            fNotifiedOn: 'Date the customer was notified',
            exportFile: 'Export the complaint file',
            routeTitle: 'Route the complaint',
            fReviewer: 'Reviewer',
            reviewerHint: 'Not the system owner or a listed developer.',
            submitRoute: 'Route complaint',
            withholdTitle: 'Withhold an item',
            fWithheldItem: 'Item withheld',
            fWithheldReason: 'Reason for withholding',
            fWithheldCategory: 'Category',
            withheldCategory: {
                source_code_algorithm_or_parameters: 'Source code, detailed algorithm or parameter set',
                business_or_technology_secret: 'Business or technology secret',
                third_party_information: 'Third-party information',
                security_sensitive: 'Security-sensitive',
                restricted_decision: 'Restricted decision',
                other: 'Other',
            },
            submitWithhold: 'Save item',
            reviewTitle: 'Re-review the complaint',
            fOutcomeSummary: 'Outcome of the re-review',
            reviewHint: 'The re-review is the final basis for the reply. Its outcome is recorded for every category.',
            reviewReasonHint: 'What the customer is told. For this category the institution need only tell the customer a reason; the outcome above is held internally.',
            submitReview: 'Save re-review',
            thresholdTitle: 'Complaint threshold',
            fThresholdCount: 'Complaints that raise a task',
            thresholdHint: 'The organisation sets this figure; the regulation states none.',
            fSecondLine: 'Second-line holder',
            submitThreshold: 'Save threshold',
            closeTitle: 'Close the complaint',
            closeHint: 'The server refuses a close with no re-review recorded.',
            submitClose: 'Close complaint',
            complaintsBoundary: 'ComplianceOne holds the complaint, the decision basis and the re-review. It does not decide a complaint and does not write the explanation to the customer.',
            closeNeedsReReview: 'No re-review is recorded for this complaint, so it cannot be closed yet. Record the re-review first.',
            fComplaintReason: 'Reason for the complaint',
            complaintReasonHint: "ComplianceOne's own categories; the Circular names none.",
            colComplaintReason: 'Reason',
            complaintReason: {
                decision_outcome: 'Disagrees with the outcome',
                explanation: 'Asks for an explanation, or finds it inadequate',
                unfair_treatment: 'Unfair treatment or discrimination',
                data_error: 'Wrong or outdated data',
                ai_interaction: 'Dealing with an AI system',
                service_failure: 'System error or interruption',
                other: 'Other',
                not_recorded: 'Not recorded',
            },
            colAiSystem: 'AI system',
            colCountInPeriod: 'Complaints in the period',
            colByReason: 'By reason',
            thresholdWithWindow: '{count} in {months} month(s)',
            thresholdNoWindow: 'counted over every complaint recorded; no window set',
            periodFrom: 'From month',
            periodTo: 'To month',
            periodHint: 'The default is the six months ending this month.',
            exportTrend: 'Export the trend by month',
            fThresholdWindow: 'Window, in months',
            thresholdWindowHint: 'Optional, from 1 to 12 months ending with the month counted. Left empty, every complaint recorded is counted.',
            patternHeading: 'Responses to a pattern of complaints',
            patternLabel: "Your organisation's response to a pattern of complaints. The Circular sets no threshold and no response for a pattern; ComplianceOne decides neither.",
            patternEmpty: 'No response to a pattern of complaints is recorded.',
            patternRespond: 'Record the response',
            patternTitle: 'Record the response to a pattern of complaints',
            patternSubmit: 'Save response',
            fPatternKind: 'What your organisation did',
            patternKind: {
                no_change_with_reason: 'No change, with the reason',
                revalidation_requested: 'Re-validation requested',
                intervention_recorded: 'Intervention recorded',
                incident_recorded: 'Incident recorded',
                change_recorded: 'Change recorded',
                other: 'Other',
            },
            fPatternLinked: 'The record it refers to',
            patternLinkedEmpty: 'This system has no record of this kind to link.',
            patternRespondSr: 'for {system}, {product}',
            patternRespondSrCrossed: 'for {system}, {product}, crossed on {date}',
            patternCrossedOn: 'Crossed on {date}.',
            patternCrossingNote: 'The response answers the latest complaint that brought this count to the threshold. ComplianceOne finds that complaint and records its day with the response.',
            patternWho: "Only the system's owner, the second-line holder the threshold names, or a person on the second line of the current three-lines map whose role in the register is P-VN-42 can record the response.",
            patternLinkedLoading: 'Reading the records of this system…',
            patternLinkedFailed: 'The records of this system could not be read.',
            patternLinkedRetrySr: 'read the records to link again',
            patternAlreadyAnswered: 'This crossing of the threshold already has its response.',
            periodInvalid: 'A period covers at most 60 months.',
            lineAddedInVersion: 'Attested by someone added to this line in the version they attested.',
            fPatternSummary: 'What was done, and why',
            fRespondedOn: 'Day of the response',
            colCrossedOn: 'Threshold crossed on',
            colResponseKind: 'Response',
            colRespondedBy: 'Responded by',
            colRespondedOn: 'Responded on',
            colExplanations: 'Explanations',
            explanationAdd: 'Record an explanation',
            explanationTitle: 'Record an explanation after the re-review',
            explanationHint: 'Record each explanation delivered to the customer after the re-review, as it was delivered. None is required to close the complaint.',
            fExplanationText: 'Explanation as delivered',
            explanationSubmit: 'Save explanation',
            explanationStage: {
                at_intake: 'At intake',
                after_re_review: 'After the re-review',
            },
            frameworkTitle: 'Framework record and three lines',
            frameworkSubtitle: 'The risk-management framework and the three lines of defence',
            frameworkSummary: 'The framework is approved by a named body and person. The three-lines map shows each line’s unit, people and attestation.',
            frameworkHeading: 'Risk-management framework',
            frameworkAdd: 'Record a framework',
            frameworkEmpty: 'No framework is recorded.',
            frameworkFormTitle: 'Record the framework',
            fFrameworkTitle: 'Framework title',
            fApproverBody: 'Approving body',
            fApprover: 'Approver',
            fApprovedOn: 'Date approved',
            fRiskCode: 'Risk type code',
            fDefinition: 'Definition',
            fThresholds: 'Thresholds',
            fControls: 'Controls',
            colTitle: 'Title',
            colApprover: 'Approver',
            colApproved: 'Approved on',
            colRiskTypes: 'Risk types',
            linesHeading: 'Three lines of defence',
            linesAdd: 'Record the map',
            linesEmpty: 'No three-lines map is recorded.',
            linesFormTitle: 'Record the three-lines map',
            linesFormDescription: 'Each line names its unit and its holders. A line is attested by one of its own holders; a line not attested, or attested by someone not on it, reads incomplete.',
            colVersion: 'Version',
            colIncomplete: 'Incomplete lines',
            colLines: 'Lines',
            attestedYes: 'attested',
            attestedNo: 'not attested',
            line: { first: 'First line', second: 'Second line', third: 'Third line' },
            fUnit: 'Unit',
            fHolder: 'Person',
            fRole: 'Role',
            fPersona: 'Role in the register',
            fAttestation: 'Attestation statement',
            attestationHint: 'Entered by the signatory.',
            fAttested: 'Attested',
            // Fix-sprint-20261031-1200 F32 — the framework and three-lines page's boundary statement.
            frameworkBoundary: "ComplianceOne holds the framework record, the three-lines map and each line's attestation, as the people named entered them. It approves no framework, judges no line's independence and attests nothing on anyone's behalf.",
            // F6 — one line per attestation, by one of that line's own holders.
            attestLine: 'Attest this line',
            attestTitle: 'Attest the {line}',
            attestDescription: 'You are named on this line. Your attestation is recorded in your name and dated today.',
            submitAttest: 'Save attestation',
            lineAttestedOn: 'Attested on {date}',
            lineCarried: 'Attested on {date} (carried from version {n})',
            lineNotAttested: 'Not attested',
            lineNonHolder: 'Attested by someone not named on this line: re-attest.',
            sharedHolder: '{name} is named on the {lineA} and the {lineB}. Điều 14 khoản 2 asks for independent lines.',
            lineLower: { first: 'first line', second: 'second line', third: 'third line' },
            lineJoin: ', the ',
            boardTitle: 'Board report: AI figures',
            boardSubtitle: 'The AI figures in the board report, read from the AI registers',
            boardSummary: 'Overdue inventory reviews count the reviews of the current calendar year. A pause, stop, emergency stop, manual operation or resume counts as an open intervention from when it is recorded until a person enters its confirmed time.',
            boardHeading: 'Current figures',
            boardEmpty: 'The register returned no AI figures.',
            colFigure: 'Figure',
            colValue: 'Value',
            figSystems: 'AI systems',
            figLow: 'Low risk',
            figMedium: 'Medium risk',
            figHigh: 'High risk',
            figUnclassified: 'Unclassified',
            figOverdue: 'Overdue inventory reviews',
            figFindings: 'Open validation findings',
            figInterventions: 'Open interventions',
            figDisclosures: 'Disclosures in use',
            figAsOf: 'As of',
        },
        // fix-sprint-20261031-1200 F1 and F34 — the periodic report contents, retirement and a change's significance.
        boardReport: {
            heading: 'Periodic report contents',
            intro: 'The contents the installed framework names for the report to the senior management body, read from the registers for the window below. ComplianceOne counts what the registers hold; it does not write or present the report.',
            windowFrom: 'Window from',
            windowTo: 'Window to',
            applyWindow: 'Read for this window',
            windowInvalid: 'Enter a window that opens on or before the day it closes.',
            statusComplete: 'Reads the five contents Điều 12 khoản 6 names — operation, risk, serious incidents, complaints and drift trends — from the registers, for {from} to {to}. ComplianceOne does not present the report to the senior management body or the parent bank.',
            statusPartial: 'Partial against Điều 12 khoản 6: {gaps}. The figures below are what the registers hold for {from} to {to}.',
            gapUnclassified: "{n} systems have no recorded tier, so they cannot be placed in or out of the report's scope",
            gapSignificance: '{n} changes to high-risk systems have no significance answer',
            gapOperationDate: '{n} systems have no operation date, so the day the report is first owed is not known for them',
            gapUnreadable: '{content} could not be read',
            windowLonger: 'This window is longer than the six months the text sets as the minimum frequency.',
            nextDue: 'Next report due by {date} (from {anchor})',
            anchorLastExport: 'the last board report export on {date}',
            anchorFirstApplies: 'the earliest day the report applies to a system in scope, {date}',
            nextDueNotDeclared: 'No next due date: no installed framework in force sets the report interval.',
            nextDueNoScope: "No next due date: no system of your organisation is in the report's scope.",
            nextDueUnknown: 'The next due date is not known: no system in scope has an operation date.',
            autonomyNotRecorded: '{n} systems in scope have no recorded autonomy level; the earlier of the two possible days is counted.',
            colContent: 'Content',
            opProduction: 'Systems in scope in production',
            opInterventionsKind: 'Interventions recorded in the window: {kind}',
            opChanges: 'Version changes recorded in the window',
            riskTier: 'Systems in scope: {tier}',
            incSerious: 'Incidents determined serious in the window',
            incUndetermined: 'Incidents opened in the window whose seriousness is not determined',
            incCompletion: 'Serious incidents with a completion report',
            cReceived: 'Complaints received in the window',
            cClosed: 'Complaints closed in the window',
            cOpen: 'Complaints open now',
            cCrossings: 'Complaint threshold crossings in the window',
            driftRow: '{kind}: {now} in the window; {before} from {from} to {to}',
            driftUnreadable: 'Monitoring events by kind, in the window and the window before it',
            nextDueUndated: 'No next due date: the installed framework states no date from which this applies.',
            retrySr: 'read the periodic report contents again',
            gapRecordNotInstalled: 'No installed framework declares the report to the senior management body, so its scope, contents and interval are not known',
            changesHeading: 'Change records',
            changesEmpty: 'No change is recorded.',
            colChangeRecorded: 'Recorded on',
            significanceRecord: 'Record significance',
            significanceRecordSr: 'for the change recorded on {date}',
            significanceBasisShort: 'Basis',
            significanceChoose: 'Choose yes or no.',
            significanceAnsweredBy: 'Answered by {user} on {date}',
            significanceAlreadyRecorded: 'This change already carries a significance answer.',
            significanceBasisRequired: 'Record the basis with the answer.',
            sigHeading: 'Significant changes to high-risk systems',
            sigSince: 'Recorded after the last report export on {date}.',
            sigSinceNone: 'No report export has carried these contents yet, so every change is listed.',
            sigEmpty: 'No change is recorded as significant.',
            sigRow: '{system}: {change} ({date})',
            sigNotRecorded: 'Changes with no significance answer',
            retiredFigure: 'Retired systems',
            retiredOn: 'Retired on {date}: no annual review is due after retirement.',
            retiredNoDay: 'Retired; retirement day not recorded.',
            retiredReading: 'Retired',
            significanceLabel: 'Significant change',
            significanceHint: 'Whether this is a significant change within Điều 2 khoản 9, which refers to the re-assessment cases of the decree that implements the AI law. A person records the answer and its basis; ComplianceOne does not decide it.',
            significanceYes: 'Yes, significant',
            significanceNo: 'No, not significant',
            significanceBasis: 'Basis for the answer',
            significanceBasisHint: 'Required with a yes or no answer.',
        },
        incidentAi: {
            heading: 'AI system in this incident',
            description: 'Link the incident to the AI system it concerns, and record the serious-incident duties of the AI record. The platform submits no report to a State authority.',
            fLink: 'AI system',
            linkHint: 'Choose the AI system whose operation this incident concerns.',
            noProfiles: 'No AI system is recorded for your organisation.',
            saveLink: 'Save link',
            linkSaved: 'Linked to this incident.',
            recordHeading: 'Serious-incident record',
            fSerious: 'Serious incident',
            seriousYes: 'Serious',
            seriousNo: 'Not serious',
            notDetermined: 'Not determined',
            fSeriousBasis: 'Basis for the determination',
            basisHint: 'A serious determination raises one reassessment task for the system owner.',
            reassessment: 'Reassessment task raised',
            reassessmentYes: 'Yes, to the system owner',
            reassessmentNo: 'No',
            deadline: 'Report deadline',
            deadlineStatedOne: 'Not computed. {framework} states no period for this report.',
            deadlineStatedMany: 'Not computed. These frameworks state no period for this report: {frameworks}.',
            deadlineNoneDeclared: 'Not computed. The platform sets no report date for this incident.',
            deadlineSeparateOne: '{framework} states reporting periods for serious AI incidents. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineSeparateMany: 'Reporting periods for serious AI incidents are stated in {frameworks}. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineRecordBelow: 'Record the report you made below.',
            notInForce: 'not yet in force',
            stateReports: 'Reports to the authorities',
            authority: {
                competent_state_authority: 'Competent State authority',
                state_bank_it_department: 'State Bank IT Department',
            },
            fRemediation: 'Remediation',
            fAuthority: 'Authority the report was made to',
            authorityHint: 'Record the reference of a report made by a person outside the platform.',
            fReportedOn: 'Date reported',
            fReference: 'Reference of the report',
            referenceHint: 'The reference the authority gave, or the reference in your own file.',
            completion: 'Completion report',
            fCause: 'Cause',
            fDamage: 'Damage',
            fCompletedOn: 'Date completed',
            emergency: 'Emergency stop or manual operation',
            emergencyNote: 'Recorded as an intervention of the AI system, with its time and person.',
            notification: 'Customer notification',
            notificationNote: 'Read from the notification statuses of this incident, not copied here.',
            status: 'Serious-incident state',
            recordAction: 'Record the serious-incident duties',
            recordTitle: 'Serious-incident record',
            saveRecord: 'Save record',
        },
    },
    // Sprint 63.24.0 Slice A — controlled-testing case pages. English copy until the R-57 deferral (sprint 57.7.0).
    controlled_testing: {
        // Sprint 63.36.0: authored English fallback for this module; locale translation remains separate.
        dossierChecklist: {
            heading: 'Dossier documentary reference',
            reference: 'Current amended source reference',
            regime: 'Whether this source regime governed this case is not determined.',
            unassigned: 'No documentary route is assigned. Source routes below are unselected references only.',
            selected: 'Selected documentary route',
            unselected: 'Unselected source reference — no case coverage is assigned',
            observations: 'Current documentary observations may include facts recorded after the filing or role reference date. These counts do not describe the dossier as it stood at filing or assess legal completeness.',
            recorded: 'Recorded fact items',
            partial: 'Partial items',
            pending: 'Pending items',
            historical: 'Historical items',
            source: 'Source',
            pages: 'PDF pages',
            referenceOn: 'Reference date',
            filing: 'Filing date used for route and role context',
            observedOn: 'Current observations as of',
            as_of: 'Current date used for route and role context',
            context: 'Documentary route context',
            form: 'Application form',
            kind: 'Applicant kind',
            unknown: 'Not recorded or not determinable',
            noProjection: 'No matching documentary projection is available; review required.',
            person: 'Personnel record',
            factDate: 'Actual fact reference date',
            status: {
                available: 'Source reference available',
                undeclared: 'No dossier checklist is declared.',
                unavailable: 'Dossier checklist is unavailable; review required.',
                not_applicable: 'This is not an admission-form documentary route.',
            },
            codes: {
                case_anchor: 'Immutable case form anchor',
                filed_snapshot: 'Immutable filed snapshot',
                draft_record: 'Current draft record',
                reported_registry_information: 'Reported registry information',
                none: 'No supported fact source',
                required: 'Source-listed component',
                conditional_if_any: 'Conditional source component',
                one_of: 'Documentary alternatives',
                historical_repealed: 'Historical separate attachment — repealed',
                documentary_route: 'Documentary route',
                unknown: 'Applicability not determined',
                historical: 'Historical reference',
                recorded_facts: 'Recorded bounded facts',
                recorded_fact: 'Recorded bounded fact',
                partial: 'Partial documentary observation',
                not_recorded: 'Not recorded',
                not_determinable: 'Not determinable',
                recorded: 'Recorded',
                pending: 'Pending',
                conflict: 'Conflicting facts',
                withheld: 'Withheld',
                not_held: 'Not held',
                field_only: 'Descriptive field only',
                filing_metadata_only: 'Filing metadata only; not a completed signed application',
                document_presence_only: 'Held document presence only; contents not assessed',
                classification_reference_only: 'Classification reference only; no approval or compliance assessment',
                statement_date_only: 'Statement date only; no held document inferred',
                reported_information_only: 'Reported information only',
                not_declared: 'No source declaration',
                invalid_declaration: 'Invalid source declaration',
                invalid_declaration_context: 'Invalid source context',
                ambiguous_declaration: 'Ambiguous source declarations',
                applicant_kind_required: 'Explicit applicant context is missing',
                track_not_declared: 'No matching documentary route is declared',
                ambiguous_track: 'Multiple matching documentary routes',
                not_admission_form: 'Not an admission form; no parent route is substituted',
                coverage_limit_exceeded: 'Documentary projection exceeds its technical bound',
                unsupported_binding: 'No supported documentary binding',
                missing_record: 'Required fact is not recorded',
                missing_person: 'Relevant person is not recorded',
                unknown_applicability: 'Conditional applicability is not determined',
                missing_actual_date: 'Actual date is not recorded',
                information_pending: 'Information remains pending; a request is not the information',
                legacy_unconverted: 'Legacy facts have not been converted',
            },
        },
        annexGuidance: {
            heading: 'Source guidance',
            undeclared: 'Guidance is not declared by the installed framework.',
            unavailable: 'Declared guidance is unavailable; review its source declaration.',
            internalSummary: 'Internal source summary. This guidance does not assess a submitted document, signature or filing.',
            sourceDocument: 'Source document',
            sourcePages: 'Source PDF pages',
            legacy: 'Historical record: a provisional report-format absence marker was stored. Current guidance is shown separately.',
            reasons: {
                not_declared: 'No guidance declaration is installed.',
                invalid_declaration: 'The guidance declaration is invalid.',
                invalid_declaration_context: 'The guidance source context is invalid.',
                ambiguous_declaration: 'Multiple guidance declarations require review.',
            },
        },
        parentCopy: {
            operationCapturedAt: 'Creation request captured at',
            acceptedAt: 'Creation accepted at',
            initiatedBy: 'Creation initiated by',
            operationPage: 'Recorded creations page',
            previous: 'Previous page',
            next: 'Next page',
            listFailed: 'Recorded child creations could not be read. Retry the status check; do not submit another creation request.',
            reviewOperations: 'Review recorded child creations',
            knownOperation: 'Recorded operation reference',
            noResume: 'The selected recorded creation has no resume action available.',
            heading: 'Frozen parent solution evidence',
            capturedVersion: 'Captured source solution version',
            capturedAt: 'Source captured at',
            captureHint: 'This snapshot preserves the source version and capture time shown here.',
            copy_absent: 'No parent solution copy recorded.',
            not_recorded: 'The copied solution has no recorded technical-statement file.',
            available: 'The original copied file is available.',
            withheld: 'Parent solution content is withheld. Independent parent read access is required.',
            legacy_lineage_unverified: 'The earlier copy has no verified source lineage. Its file cannot be downloaded through this snapshot.',
            source_unavailable: 'The source solution is unavailable. The copied reference is preserved.',
            lineage_invalid: 'The source lineage needs review. No file is available through this snapshot.',
            not_held: 'The copied file is not currently held.',
            deleted: 'The copied file is deleted. Its original reference remains recorded.',
            released: 'The copied file was released under retention rules. Its original reference remains recorded.',
            release_in_progress: 'Release or destruction is in progress. The copied file is unavailable.',
            loading: 'Checking copied evidence availability…',
            loadFailed: 'Copied evidence availability could not be checked.',
            refresh: 'Reload copied evidence',
            download: 'Download original copied statement',
            downloadFailed: 'The copied file could not be downloaded. Reload its availability and retry.',
            downloadUnavailable: 'Download is unavailable with the current access.',
            opening: 'Creating the child case…',
            creationPending: 'Child creation is awaiting completion. This form keeps the original request; retrying does not create another child.',
            creationUnknown: 'The result of this request is uncertain. Check its status before creating another child. Your original form is preserved.',
            checkCreation: 'Check child creation status',
            resume: 'Complete the recorded child creation',
            operationChoice: 'Recorded child creation',
            chooseOperation: 'Choose the recorded operation to review',
            preparing: 'Creation is being prepared',
            committed: 'Creation accepted; completion pending',
            published: 'Child case is available',
            cancelled: 'Creation was cancelled',
            integrity_conflict: 'Recorded creation needs integrity review',
            resumeFailed: 'The recorded child creation could not be completed.',
            originalRequest: 'Original child request',
            newRequest: 'Return to a new request after review',
            recoveryHint: 'Review the recorded operations explicitly. No operation is selected from its date or name.',
            noOperations: 'No recoverable operation is listed. Review the child list before starting a new request.',
        },
        stopReasonMessages: {
            framework_not_operational: 'The framework is not operational for this case. No transitional determination is made.',
            transitional_rule_ambiguous: 'Multiple transitional rule declarations apply. Review their source declarations.',
            transitional_rule_invalid: 'The declared receipt or confirmation references need source review.',
            transitional_documentary_sources_conflict: 'The actual receipt or confirmation sources disagree or are ambiguous. Compare the separate documentary records.',
            confirmation_mapping_not_declared: 'No source-declared confirmation reference is available. A receipt cannot replace it.',
            legacy_confirmation_claim_not_verified: 'A prior confirmation-date claim exists, but its documentary evidence is not verified. Review that claim separately from receipt.',
            confirmation_scope_conflict: 'The confirmation evidence and its applicable recording scope conflict. Review the preserved sources.',
            historical_recording_scope_review_required: 'The historical processing regime still needs source review. Verified receipt does not resolve that applicability question.',
            authored_documentary_counterpart_not_verified: 'A declared receipt or confirmation counterpart still lacks verified evidence. Review the separate source records.',
            confirmation_regime_not_verified: 'The actual confirmation is recorded, but its processing regime or commencement boundary is not verified.',
            main_and_nested_proceedings: 'Main and nested STOP facts both exist. Review the proceeding identity.',
            stop_reference_not_declared: 'The installed framework does not identify this STOP proceeding.',
            stop_reference_invalid: 'The declared STOP procedure or receipt reference needs review.',
            stored_stop_procedure_changed: 'The installed target differs from the preserved proceeding. Recorded facts have not been moved.',
            stop_lifecycle_not_declared: 'The source-declared proceeding steps are unavailable. Review the declaration.',
            receipt_occurrences_conflict: 'Multiple or conflicting receipt facts need documentary review.',
            receipt_not_verified: 'The actual receipt evidence still needs verification.',
            receipt_not_recorded: 'No actual receipt fact is recorded. Record it from its evidence when known.',
            multiple_decision_occurrences: 'Multiple lifecycle decision facts need documentary review.',
            decision_dates_disagree: 'The actual decision dates differ across documentary sources.',
            decision_references_disagree: 'The decision references differ across documentary sources.',
            decision_evidence_conflict: 'Decision evidence contains a conflict. Preserve the actual sources for review.',
            completion_and_termination_recorded: 'A completion certificate and termination facts are both recorded. Review their documentary conflict.',
            stop_context_unresolved: 'The proceeding context is unresolved. New requests need review; actual facts remain preserved.',
            grant_not_declared: 'No grant declaration is available for a new voluntary request.',
            grant_not_recorded: 'The declared grant has not been recorded. No grant date is inferred.',
            completion_certified: 'An actual completion certificate is recorded. A new voluntary stop request is unavailable.',
            termination_recorded_or_unresolved: 'Termination facts are recorded or need review. New initiation is unavailable.',
            stop_already_filed: 'A voluntary request is already recorded.',
            voluntary_ground_not_declared_or_ambiguous: 'The voluntary-request ground is missing or ambiguous in the installed source.',
            completion_already_filed: 'A completion request is already recorded.',
        },
        stopProceeding: {
            documentaryEnding: 'Documentary ending for this clock',
            verified: 'Evidence verified',
            pending_anchor: 'Required scope anchor not recorded',
            requestFiled: 'Applicant request recorded',
            heading: 'STOP proceeding and documentary decisions',
            intro: 'Record actual documentary acts in their declared proceeding. Applicant requests, receipt, authority decisions and post-stop duties remain separate records.',
            context: 'Proceeding context',
            main: 'Main case proceeding',
            nested: 'Separate proceeding within this case',
            not_declared: 'Not declared by the installed framework',
            unresolved: 'Proceeding declaration needs review',
            ambiguous: 'More than one proceeding context needs review',
            request: 'Voluntary applicant request',
            notFiled: 'No applicant request recorded',
            eligibility: 'Eligibility for a new voluntary request',
            allowed: 'New request available',
            refused: 'New request unavailable',
            review: 'Review the recorded facts and source declarations before this action.',
            receipt: 'Actual stop-dossier receipt',
            receiptHint: 'Enter the authority receipt date and its evidence. Filing, complete-valid confirmation and recording dates remain distinct.',
            pending: 'Pending evidence or applicability',
            recorded: 'Recorded',
            pending_evidence: 'Evidence needs verification',
            conflict: 'Documentary facts disagree or are ambiguous',
            none: 'No documentary decision recorded',
            recordReceipt: 'Record actual receipt',
            recordEvent: 'Record proceeding fact',
            recordTransition: 'Record actual proceeding step',
            recordAuthority: 'Record actual authority decision',
            authority: 'Authority termination record',
            legacy: 'Preserved earlier revocation record',
            raw: 'Decision in the proceeding history',
            decisions: 'Documentary decision comparison',
            decisionHint: 'Each source remains independent. A recorded decision does not fill a closure anchor or erase continuing duties.',
            protocol: 'Saving held evidence',
            ready: 'Held evidence can be linked',
            not_ready: 'Held evidence cannot be linked yet',
            protocolHint: 'Keep this draft and retry after evidence binding becomes available. Uploading holds a file; it does not file a request or record a decision.',
            noInference: 'Receipt does not confirm a complete-valid dossier. A pending phase does not replace an actual receipt date.',
            phase: 'Recorded proceeding state',
            history: 'Immutable proceeding history',
            events: 'Actual proceeding facts',
            clocks: 'Source-declared proceeding clocks',
            noFacts: 'No fact recorded',
            noStates: 'No declared next step available',
            event: 'Declared event',
            state: 'Declared next step',
            date: 'Actual documentary date',
            receivedOn: 'Actual receipt date',
            evidence: 'Evidence references',
            evidenceHint: 'One actual reference per line. A local file reference is subject to its held-evidence checks.',
            ground: 'Declared ground',
            groundNotRecorded: 'Ground not recorded',
            source: 'Source citation',
            actor: 'Documentary actor',
            recordedBy: 'Recorded by',
            recordedAt: 'Recorded at',
            fileHeld: 'Held',
            fileUnavailable: 'File unavailable',
            submit: 'Record documentary fact',
            saved: 'Documentary fact recorded',
            failed: 'The documentary fact could not be recorded.',
            refresh: 'Reload current proceeding',
            stale: 'The case changed. Your draft is retained. Reload before reviewing and submitting again.',
            draftChanged: 'The proceeding or selected declaration changed. Your retained draft has not been reinterpreted.',
            reset: 'Discard unavailable selection',
            close: 'Cancel',
            recipients: 'Actual notice recipients',
            recipient: 'Recipient description',
            addRecipient: 'Add recipient',
            removeRecipient: 'Remove recipient',
            channel: 'Actual notification channel',
            recipientsHint: 'Record actual recipients and channel. This does not establish complete customer coverage.',
            notRecorded: 'Not recorded',
            advisory: 'Source notice advisory',
            invalid_declaration: 'Advisory declaration needs review',
            not_determinable: 'Applicable rule is not determined',
            received_not_confirmed: 'Receipt recorded; complete-valid confirmation not recorded',
            review_required: 'Historical source applicability requires review',
            confirmation: 'Actual complete-valid confirmation',
            transitionalReceipt: 'Actual dossier receipt evidence',
            authorityHint: 'Record an actual authority act independently of applicant filing. Conflicting existing documentary sources remain visible.',
            decisionRef: 'Authority decision reference',
            decidedOn: 'Actual decision date',
            decisionText: 'Authority decision text',
            file: 'Held decision evidence',
            filingHint: 'A voluntary filing records the applicant request only; receipt and authority decision are recorded separately.',
            groundRequired: 'Choose the declared voluntary ground',
            noClock: 'No numeric clock output available',
            reason: 'Review reason',
            confirm: 'Confirm voluntary filing',
            confirmationHint: 'Confirm this applicant request with the entered date and held files. This does not record authority receipt or a decision.',
        },
        registryFacts: {
            correction: 'Outcome data correction',
            correctionHint: 'An evidence-backed correction identifies an erroneous previous fact. It is not a waiver or a successful retrieval.',
            correctionIntent: 'This change corrects an erroneous previous fact',
            correctionReason: 'Correction reason',
            outcomeHistory: 'Previous registry outcome facts',
            person: 'Exact linked personnel record',
            choosePerson: 'Choose a personnel record explicitly',
            personHint: 'Names do not identify a record. Choose the exact record ID; no person is inferred. An unassigned legacy row can be linked once.',
            personUnavailable: 'Personnel choices could not be read or no declared eligible role is available.',
            linkedOutcome: 'Outcome held only on the linked personnel record',
            outcomeHint: 'Record or correct this outcome in the personnel section. This registry row does not write a second outcome.',
            legacy: 'Preserved nonauthoritative registry outcome',
            retrievedOn: 'Actual source-retrieval date',
            suppliedOn: 'Actual component supplied date',
            requestHeading: 'Documentary follow-up requests',
            requestHint: 'Record an existing written request. A request is not the information supplied and does not waive missing information. Requests are append-only.',
            requestOn: 'Actual written-request issue date',
            reference: 'Written-document reference',
            request: 'Append documentary request',
            requestMissing: 'No declared source-specific request is available.',
            information: 'Information availability',
            available_as_reported: 'Available as reported at the recorded date; no eligibility conclusion',
            pending: 'Information remains pending',
            undetermined: 'Information availability is undetermined',
            requestRecorded: 'Documentary request recorded; this does not establish information receipt',
            evidenceNoLongerHeld: 'Request attachment is no longer held',
            noRequest: 'No documentary request is recorded',
            copy: 'Applicant-copy file',
            copyHint: 'Upload holds a file only. Bind it explicitly to this row. A held copy does not override the linked personnel outcome.',
            upload: 'Upload a registry documentary file',
            uploaded: 'Uploaded but not bound: {name}',
            bind: 'Bind uploaded applicant copy',
            unbind: 'Unbind applicant copy',
            held: 'Bound file is held for this exact row',
            notHeld: 'Bound file is not held for this row',
            notBound: 'No applicant-copy file is bound',
            useRequestFile: 'Use this upload as the request attachment',
            noRequestFile: 'No request attachment selected',
            decisionHint: 'A separately recorded decision is preserved as a fact; it does not waive request or information requirements.',
            coverage: 'Requirement coverage',
            count: 'Recorded rows: {count}; unassigned rows: {unassigned}',
            people: 'Eligible people: {eligible}; linked people: {linked}',
            scopeUnavailable: 'Requirement scope is not declared; person linkage remains undetermined',
            refresh: 'Read current registry row',
            refreshed: 'Current row read. Your entries are retained; review current scope, person and source declarations before retrying.',
            stale: 'The row changed. Read it again before retrying; your entries are retained.',
            unavailableDraft: 'Your unsaved person choice is no longer available or differs from a now-fixed link. Clear the draft choice to retain the current link.',
            keepLink: 'Use the current recorded link',
            failed: 'The documentary change could not be recorded.',
            history: 'Immutable request history',
            date: 'Actual date',
            actor: 'Recorded by',
            recordedAt: 'Recorded at',
            classificationOn: 'Outcome classification uses the recorded outcome date',
            outcomeUnresolved: 'The linked outcome or personnel scope is unresolved; the stored code is not treated as available information.',
            requestDeclarationChanged: 'The source-specific request declaration changed. Your draft is preserved and cannot be silently recorded as another kind of request.',
            resetRequestDraft: 'Discard this request draft and use the current declaration',
        },
        applicantSolution: {
            kind: 'Recorded applicant kind',
            kindHint: 'Choose the applicant kind explicitly. The shared application form does not determine it. Leaving it unrecorded keeps the track undetermined and does not create a filing requirement.',
            choose: 'Not recorded',
            kindLocked: 'Applicant kind is locked after filing or authority facts. The recorded value is preserved.',
            keepRecorded: 'Use the current recorded kind and discard the unsaved kind change',
            filedKind: 'Applicant kind in the immutable filed snapshot',
            absentAtFiling: 'Not captured in this legacy filed snapshot',
            track: 'Declared dossier track',
            recorded: 'Recorded using the current declaration',
            not_recorded: 'Applicant kind is not recorded',
            recorded_not_declared: 'Stored value is not resolved by the current declaration',
            not_declared: 'No usable declaration is available',
            invalid_declaration: 'The installed declaration requires review',
            resolved: 'Track resolved from the recorded form and applicant kind',
            applicant_kind_required: 'Record applicant kind to determine the track',
            applicant_kind_not_declared: 'The stored applicant kind cannot resolve a current track',
            track_not_declared: 'No matching declared track is available',
            ambiguous: 'Several conflicting declarations match; no choice is made',
            conditional: 'The cited dossier component includes an investment registration certificate if any. This does not establish that this applicant has or must obtain one.',
            notListed: 'An investment registration certificate is not listed in this cited dossier provision. This is not a general exemption from investment-registration obligations.',
            scheme: 'Referenced information-system security scheme',
            declared: 'The exact referenced scheme is installed',
            named_not_installed: 'The named scheme is declared but not installed. Install the referenced framework; no alternative scheme is substituted.',
            schemeNotDeclared: 'No usable scheme reference is declared. No other installed scheme is substituted.',
            reference: 'Declared target',
            source: 'Source',
            legacyLevel: 'This original security-level link is preserved. It has not been converted or assigned missing source provenance.',
            levelHint: 'New selections record the declaring framework, scheme and tier explicitly. Changing the live solution does not refresh existing frozen child copies or certify system compliance.',
            loadFailed: 'Applicant-kind declarations could not be read. The kind remains unrecorded; no choice is inferred.',
        },
        filing: {
            heading: 'Organisation filing record',
            record: 'Record a filing already made',
            recordHint: 'Record the filing the organisation already made. This action does not send documents or record authority receipt.',
            notDeclared: 'The installed procedure declares no filing requirements. A reviewed filing declaration is needed before a filing can be recorded.',
            immutable: 'The filed snapshot is preserved. This page cannot change a filing already recorded.',
            frozenGroup: 'Draft customer group in the frozen parent-solution copy',
            filedGroup: 'Customer group actually filed',
            filedReport: 'Results-report file reference at filing',
            notRecorded: 'Not recorded',
            notRecordedAtFiling: 'Not recorded in the filed snapshot',
            reference: 'Organisation’s filing reference',
            submittedOn: 'Actual filing date',
            childGroupHint: 'Prefilled from the child’s frozen parent-solution copy. Enter what was actually filed, including a missing draft group. This does not change that copy or the parent’s draft solution.',
            parentGroupHint: 'Enter the customer group actually filed. This records the filed snapshot and does not change the draft solution.',
            approvalReady: 'Internal sign-off is recorded as complete for this child.',
            approvalRequired: 'The child’s internal sign-off must be recorded as complete before its filing can be recorded.',
            draftReport: 'Draft results report',
            reportHint: 'Uploading holds a file. Attach it to this draft as a separate step. Neither action records a filing or verifies the results-report format.',
            reportNotBound: 'No results report is attached to this draft.',
            reportBound: 'Draft report attached and held (file reference: {reference}).',
            reportNotHeld: 'The draft report is no longer held or does not belong to this child. Detach or replace it before recording a filing.',
            reportUnknown: 'The draft report’s held status could not be determined. Read the case again before recording a filing.',
            reportChangeFailed: 'The draft report attachment could not be changed.',
            uploadReport: 'Upload a draft results report',
            uploadHeld: 'Upload completed: {name}. It is not yet attached to this draft.',
            bindReport: 'Attach uploaded report to draft',
            unbindReport: 'Detach draft report',
            leaveUnbound: 'Leave this upload unattached',
            saved: 'The filing record was saved.',
            failed: 'The filing record could not be saved.',
            refreshFailed: 'The current case could not be read. Reload the case before retrying.',
            stale: 'The case changed. Its current version has been read and your entries are retained. Review the current report and sign-off before retrying.',
        },
        lifecycle: {
            heading: 'Documentary facts and their actual dates',
            intro: 'Record what the evidence shows happened, including facts received after an authority outcome. The occurrence date is distinct from when this entry is recorded. The platform makes no authority decision.',
            record: 'Record a documentary fact',
            event: 'Declared fact',
            occurredOn: 'Actual occurrence date',
            evidence: 'Evidence references',
            verification: 'Verification against declared prerequisites',
            recordedAt: 'Entry recorded at',
            actor: 'Documentary actor: {actor}',
            actorAuthority: 'Authority',
            actorOrganisation: 'Organisation',
            actorRelevantMinistries: 'Relevant ministries',
            source: 'Source: {source}',
            recordedBy: 'Entry recorded by: {actor}',
            scopeVerified: 'This application’s recorded anchor is within a reviewed recording period.',
            scopePending: 'Recording period pending: record the declared anchor fact with its actual date and evidence.',
            scopeReview: 'This application’s recording period requires review. No historical regime is inferred.',
            scopeConflict: 'Recorded application anchors conflict. Review their dates and evidence.',
            scopeNotDeclared: 'No reviewed recording period is declared by the installed procedure.',
            scopeAnchor: 'Recorded application anchor: {date}',
            notDeclared: 'No documentary fact declared by the installed procedure',
            empty: 'No documentary facts recorded.',
            verified: 'Prerequisites verified from the recorded facts',
            pending: 'Pending prerequisite facts or a countable declared window',
            conflict: 'Dates or prerequisite facts conflict',
            verificationHint: 'Review the actual dates, supporting evidence and prerequisites in the source. A pending or conflicting fact cannot satisfy a transition prerequisite.',
            choose: 'Choose a declared fact',
            saved: 'The documentary fact was recorded.',
            failed: 'The documentary fact could not be recorded.',
            transitionRemedy: 'Only declared next steps are offered. If a prerequisite is missing, record the documentary fact and its actual date, then retry this step.',
            noNextStep: 'No next step is declared from the recorded state. Independent documentary facts remain recordable.',
            clockEnded: 'Clock ended',
            clockEndedOn: 'Ended on the source-specific actual date: {date}',
            clockRecordedStart: 'Recorded start date: {date}',
            clockHistoricalDue: 'Historical due date: {date}',
            clockConflict: 'Clock dates conflict',
            clockConflictHint: 'The recorded end precedes the start. Review the source evidence; no active due date is shown.',
            clockReview: 'Clock requires review',
            clockReviewHint: 'The installed declaration requires an anchor reading before this clock can be dated.',
            notRecorded: 'Not recorded',
        },
        title: 'Controlled-testing cases',
        subtitle: 'The organisation’s own record of each application to the controlled-testing mechanism. The authority records each step; the platform keeps it with its evidence and derives the clocks the installed record declares. The platform decides nothing.',
        moduleGuideSummary: 'Hold each controlled-testing application: the procedure it follows, the steps the authority has recorded and the clocks the installed record declares. The platform does not assess eligibility or decide the outcome.',
        restricted: 'You need permission to read controlled-testing cases to see them.',
        loading: 'Loading',
        reload: 'Reload',
        notYetRecorded: 'Not yet recorded by the authority',
        notDeclared: 'Not declared by the installed record',
        noChannel: 'No filing channel recorded',
        yes: 'Yes',
        no: 'No',
        list: {
            filtersLabel: 'Filter cases',
            chooseFrameworkFirst: 'Choose a framework first',
            chooseProcedureFirst: 'Choose a procedure first',
            noMatch: 'No match',
            heading: 'Cases',
            openCase: 'Open a case',
            filterFramework: 'Framework',
            filterProcedure: 'Procedure',
            filterStep: 'Recorded step',
            anyFramework: 'Any framework',
            anyProcedure: 'Any procedure',
            anyStep: 'Any step',
            clearFilters: 'Clear filters',
            colApplicant: 'Applicant',
            colProcedure: 'Procedure',
            colStep: 'Recorded step',
            colUpdated: 'Last updated',
            total: '{count} case(s) in total',
            empty: 'No case is held yet. Open one on an installed procedure to start.',
            emptyFiltered: 'No case matches these filters.',
            loadError: 'The cases could not be loaded.',
            retrySubject: 'the case list',
            pagination: 'Pages of cases',
            procedureLabelError: 'The procedure could not be read from the installed record.',
            stepLabelError: 'The step name could not be read from the installed record.',
        },
        pick: {
            frameworkHint: 'Only frameworks that declare a controlled-testing procedure are listed.',
            noCaseFrameworks: 'No installed framework declares a procedure that can carry a case.',
            noFormsOption: '{label} (no application form declared)',
            noFormsInFramework: 'No procedure in this framework declares an application form, so no case can be opened on it. A framework that amends it may declare the forms.',
            framework: 'Installed framework',
            procedure: 'Procedure',
            chooseFramework: 'Choose a framework',
            chooseProcedure: 'Choose a procedure',
            noProcedures: 'This framework declares no procedure that can carry a case.',
            noOpenableFrameworks: 'No installed framework can take a case: a framework installed for reference only does not.',
            hiddenUndeclared: '{count} procedure(s) in this framework declare no lifecycle and are not offered.',
            loadFrameworksError: 'The installed frameworks could not be read.',
            loadProceduresError: 'The procedures of this framework could not be read.',
            needFrameworkRead: 'Reading a framework’s procedures needs the framework read permission.',
        },
        open: {
            noForms: 'This procedure declares no application form, so a case cannot be opened on it. Choose another procedure.',
            title: 'Open a case',
            hint: 'Choose the installed procedure this application follows. The case opens with no authority step recorded.',
            form: 'Application form',
            formHint: 'The forms this procedure declares.',
            applicant: 'Applicant name',
            notes: 'Notes',
            submit: 'Open case',
            cancel: 'Cancel',
            saving: 'Saving…',
            blocked: 'Choose a framework, a procedure and a form, and enter the applicant name.',
            chooseForm: 'Choose a form',
            required: 'Required',
            opened: 'The case was opened.',
            failed: 'The case could not be opened.',
        },
        detail: {
            back: 'Back to cases',
            heading: 'Case',
            applicant: 'Applicant',
            procedure: 'Procedure',
            form: 'Application form',
            framework: 'Framework',
            channel: 'Filing channel',
            usb: 'USB copy',
            usbRecorded: 'USB copy recorded',
            usbNotRecorded: 'USB copy not yet recorded',
            receivedOn: 'Received on',
            notRecorded: 'Not recorded',
            state: 'Recorded step',
            stateUndeclared: 'The recorded step is no longer declared by the installed procedure.',
            history: 'Recorded steps',
            historyEmpty: 'No step has been recorded.',
            colStep: 'Step',
            colEnteredOn: 'Date on the authority’s document',
            colEvidence: 'Evidence',
            colRecordedAt: 'Recorded on',
            clocks: 'Clocks',
            clocksNone: 'The installed record declares no clock for this case.',
            clockRunning: 'Running',
            clockNotStarted: 'Not started',
            clockNoNumeric: 'No numeric period',
            clockNotStartedText: 'The step the clock starts from has no recorded entry, so nothing is counted yet.',
            clockNoNumericText: 'The installed record states no numeric period for this clock.',
            clockPeriod: '{period} {unit}',
            clockStartsOn: 'Counted from {date}',
            clockDue: 'Due {date}',
            clockPaused: 'Paused for {days} day(s)',
            clockSource: 'Source: computed from the recorded anchor',
            clockOwner: 'Obligation owner: {owner}',
            clockName: 'Clock',
            clockStatusHeading: 'Status',
            clockDueHeading: 'Due',
            clockSourceHeading: 'Source',
            clockUnitCalendarDays: 'calendar days',
            clockUnitWorkingDays: 'working days',
            clockUnitMonths: 'months',
            suppressedFrameworkNotOperational: 'Clocks are not computed: the framework is installed for reference only.',
            suppressedProcedureNotDeclared: 'Clocks are not computed: the installed procedure declares no lifecycle.',
            suppressedNotInForce: 'Clocks are not computed: the framework is not in force on {date}.',
            suppressedActivationUnknown: 'Clocks are not computed: the date they bind from is not stated by the installed record.',
            transitionalTitle: 'Transitional status',
            transitionalNotApplicable: 'This procedure carries no transitional rule.',
            transitionalNotDeterminable: 'The installed record does not let the platform determine the transitional status.',
            transitionalAnchorNotRecorded: 'The transitional status waits for the anchor date to be recorded.',
            transitionalYes: 'Transitional: the anchor date is before {cutoff}. No numeric clock is shown for it.',
            transitionalNo: 'Not transitional: the anchor date is on or after {cutoff}.',
            completeness: 'Open items',
            completenessOther: 'An open item applies to this case. The installed record states it.',
            completenessUsb: 'The filing channel requires a USB copy of the dossier set. Record it once it is received. An open item does not block a step.',
            channelUndeclared: 'The recorded channel is not declared by the installed framework.',
            asOf: 'Clocks computed on {date}',
            edit: 'Edit notes and channel',
            editNotes: 'Edit notes',
            recordStep: 'Record a step',
            loadError: 'The case could not be loaded.',
            frameworkUnreadable: 'The framework’s procedure and channel names could not be read, so steps and channels are shown by their identifiers.',
        },
        transition: {
            title: 'Record an authority step',
            hint: 'Enter the step as the authority’s own document states it, with the date on that document and the evidence that shows it. The platform records the step; it does not decide one.',
            step: 'Step',
            chooseStep: 'Choose a step',
            enteredOn: 'Date on the authority’s document',
            evidence: 'Evidence, one reference per line',
            evidenceHint: 'Name the document or attachment that shows this step. At least one is required.',
            submit: 'Record step',
            cancel: 'Cancel',
            saving: 'Saving…',
            recorded: 'The step was recorded.',
            failed: 'The step could not be recorded.',
            blocked: 'Choose a step, enter its date and at least one evidence reference.',
            required: 'Required',
        },
        edit: {
            title: 'Notes and channel',
            titleNotes: 'Notes',
            applicant: 'Applicant name',
            notes: 'Notes',
            channel: 'Filing channel',
            noChannel: 'No channel chosen',
            usbRecorded: 'A USB copy came with the filing',
            usbNotRecorded: 'No USB copy recorded',
            receivedOn: 'Date received',
            submit: 'Save changes',
            saving: 'Saving…',
            saved: 'The case was saved.',
            failed: 'The case could not be saved.',
        },
        stale: 'The case changed since it was opened. It has been reloaded and what you entered is still here. Check it, then submit again.',
        refusedUndeclared: 'The installed procedure does not declare that step. Nothing was recorded.',
        solution: {
            heading: 'Solution evidence',
            intro: 'The one solution this application proposes to test: its type, the customer group it reaches, the technical statement it rests on and the security level it is held to. The platform records what the solution states. It does not decide whether the solution is suitable.',
            loading: 'Loading the solution record…',
            loadError: 'The solution record could not be loaded.',
            retrySubject: 'the solution record',
            readOnly: 'You can read this record. Recording it, changing it and attaching the technical statement need the manage permission.',
            add: 'Record the solution',
            edit: 'Change the solution',
            notRecorded: 'Not yet recorded',
            typeLabel: 'Solution type',
            typeChosen: 'Chosen: {label}',
            typeNotUsed: 'Not used: {label}',
            typeNoneDeclared: 'The installed record declares no solution type, so none can be recorded yet.',
            typeSingleDeclared: 'The installed record declares one solution type only: {label}. The solution description states a choice between two.',
            typeUndeclared: 'Not declared by the installed record ({code})',
            customerGroupLabel: 'Prospective customer group',
            statementLabel: 'Technical statement',
            statementNotAttached: 'Not yet attached',
            statementAttached: 'Attached: {name}',
            statementAttachedNoName: 'Attached. Its name needs the permission to download attachments.',
            statementUpload: 'Attach the technical statement',
            statementRejected: 'The technical statement could not be attached.',
            statementNotBound: 'The file is held for this solution but is not yet attached to it. {message}',
            levelLabel: 'Security level',
            levelRecorded: '{label} ({scheme}; basis: {basis})',
            levelUndeclaredTier: 'Not declared by the installed record ({code})',
            levelNotDeclared: 'Not declared by an installed framework',
            criteriaLabel: 'Technical criteria',
            criteriaTextNotInCorpus: 'Text not in corpus. Source: {source}. No criterion is shown here as a verified requirement.',
            criteriaNotDeclared: 'Not declared by an installed framework.',
            dossierLabel: 'Solution record',
            dossierComplete: 'All four items are recorded. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_solution_type: 'the solution type',
            item_customer_group: 'the customer group',
            item_technical_statement: 'the technical statement',
            item_security_level: 'the security level',
            editor_title: 'Record the solution',
            editor_titleEdit: 'Change the solution',
            editor_hint: 'Choose the solution type the installed record declares, enter the customer group as the solution description states it, and choose the security level an installed framework declares. Attach the technical statement after the solution is saved.',
            editor_typeChoose: 'Choose a solution type',
            editor_typeNone: 'The installed record declares no solution type, so none can be chosen.',
            editor_customerGroup: 'Prospective customer group',
            editor_levelChoose: 'Choose a security level',
            editor_levelNone: 'No installed framework declares a security level, so none can be chosen.',
            editor_levelOption: '{label} ({scheme})',
            editor_submit: 'Save the solution',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The solution was saved.',
            editor_failed: 'The solution could not be saved.',
            editor_stale: 'The solution changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
        },
        registry: {
            heading: 'Authoritative-source records',
            intro: 'Documentary source records for this application. Person-scoped rows link an exact personnel record; its criminal outcome remains in the personnel section. Requests, source retrieval and actual component supply are separate dated facts. A request or decision does not replace missing information.',
            loading: 'Loading the registry records…',
            loadError: 'The registry records could not be loaded.',
            retrySubject: 'the registry records',
            readOnly: 'You can read these records. Recording a document, changing it and attaching a supplement file need the manage permission.',
            add: 'Record a document',
            edit: 'Change',
            notRecorded: 'Not yet recorded',
            stateBankRetrieves: 'The State Bank retrieves this record. The platform does not call the registry, and the outcome shown is the one recorded from the State Bank’s answer.',
            requirementLabel: 'Requirement',
            requirementNotDeclared: 'Not declared by an installed framework',
            sourceLabel: 'Source',
            sourceRegistry: 'Retrieved from the registry by the State Bank',
            sourceApplicant: 'A copy the organisation supplies',
            outcomeLabel: 'State Bank outcome',
            outcomeClass_retrieved: 'Counts as retrieved',
            outcomeClass_not_retrieved: 'Does not count as retrieved',
            outcomeClass_incomplete_or_inaccurate: 'Does not count as retrieved: incomplete or inaccurate',
            outcomeSeen: 'Date seen: {date}',
            outcomeNotDeclared: 'Not declared by an installed framework, so no outcome can be recorded.',
            outcomeUndeclared: 'Not declared by the installed record ({code})',
            outcomeNotApplicable: 'Not applicable: the document is a copy the organisation supplies.',
            recorderLabel: 'Recorded by',
            supplementLabel: 'Supplement or decision',
            supplementNotNeeded: 'Not needed: the outcome counts as retrieved.',
            supplementRecorded: 'Supplement dated {date}: {reference}',
            supplementFileOnly: 'Supplement dated {date}, with its file attached.',
            supplementFileAttached: 'File: {name}',
            supplementFileNoName: 'File attached. Its name needs the permission to download attachments.',
            decisionRecorded: 'Decision dated {date}: {text}',
            supplementMissing: 'Needed. Record a dated supplement with its reference or file, or a dated decision that none is required.',
            limbLabel: 'Investment registration limb',
            limbApplies: 'Applies on this track. Source: {source}.',
            item_scope_declaration: 'declared requirement scope',
            item_personnel_link: 'exact personnel link',
            item_retrieved_on: 'actual source-retrieval date',
            item_failure_follow_up_declaration: 'source-specific follow-up declaration',
            item_failure_follow_up_request: 'documentary follow-up request',
            item_supplement: 'actual supplied component',
            item_applicant_copy_file: 'held applicant-copy file',
            item_information_pending: 'information remains pending',
            limbConditional: 'This cited dossier component includes an investment registration certificate if any. It does not establish that this applicant has or must obtain one. Source: {source}.',
            limbNotListed: 'This cited dossier component does not list an investment registration certificate. This is not a general investment-registration exemption. Source: {source}.',
            limbNotOnTrack: 'Does not apply on this track.',
            limbTrackNotDeclared: 'The track or source-component declaration is undetermined. No document obligation or exemption is inferred.',
            dossierLabel: 'Record',
            dossierComplete: 'Complete: the row holds what its source and outcome require. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_source: 'the source',
            item_outcome: 'the State Bank outcome and the date it was seen',
            item_supplement_or_decision: 'a dated supplement or a recorded decision',
            editor_title: 'Record a document',
            editor_titleEdit: 'Change the document',
            editor_hint: 'Choose the requirement, the document’s source, and the outcome the State Bank answered with the date it was seen. Where the State Bank did not retrieve a document in full, it needs a dated supplement, or a dated decision that none is required.',
            editor_requirement: 'Requirement',
            editor_requirementChoose: 'Choose a requirement',
            editor_requirementNone: 'Every requirement the installed framework declares is already recorded, or none is declared.',
            editor_source: 'Source',
            editor_sourceChoose: 'Choose a source',
            editor_outcome: 'State Bank outcome',
            editor_outcomeChoose: 'Choose an outcome',
            editor_outcomeNone: 'The installed record declares no outcome for this requirement, so none can be chosen.',
            editor_outcomeOn: 'Date the outcome was seen',
            editor_supplementOn: 'Actual component supplied date',
            editor_supplementReference: 'Supplement reference',
            editor_supplementReferenceHint: 'Up to 500 characters: the letter or file reference.',
            editor_decisionOn: 'Decision date',
            editor_decisionText: 'Decision text',
            editor_decisionTextHint: 'Up to 1000 characters: preserve the actual documentary decision and explanation. It does not waive source-specific requests or missing information.',
            editor_submit: 'Save the document',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The document was saved.',
            editor_failed: 'The document could not be saved.',
            editor_stale: 'The document changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            supplementUpload: 'Attach the supplement file',
            supplementRejected: 'The supplement file could not be attached.',
            supplementNotBound: 'The file is held for this document but is not yet attached to it. {message}',
        },
        personnel: {
            generic: {
                heading: 'Declared documentary conditions',
                hint: 'Record each declared answer with its own evidence reference. These are documentary records, not eligibility decisions. Changing a choice requires its evidence to be entered again.',
                evidence: 'Evidence reference for this condition',
                required: 'required when an answer is chosen',
                source: 'Source',
                notDeclared: 'No usable condition declaration is available. No eligibility conclusion is made.',
                not_recorded: 'Not recorded',
                recorded_with_evidence: 'Answer and its evidence reference recorded',
                evidence_missing: 'Recorded answer has no evidence reference',
                option_not_declared: 'The recorded option is no longer declared',
                does_not_apply: 'Does not apply to this record’s declared form and roles',
                not_declared: 'Condition declaration requires review',
                clear: 'Clear this condition answer explicitly',
                legacyHeading: 'Preserved earlier condition content',
                legacyHint: 'Earlier content is preserved separately and has not been converted into current condition answers.',
                legacyBasis: 'Preserved earlier role qualification',
                clearBasis: 'Clear earlier role qualification',
                history: 'Immutable earlier role-qualification history',
                historyNone: 'No earlier role snapshot is recorded.',
                role: 'Role',
                qualification: 'Earlier qualification code',
                dates: 'Recorded role dates',
                evidenceIds: 'Recorded evidence references; current held status is not asserted',
                actor: 'Recorded by',
                time: 'Recorded at',
                versions: 'Record versions',
                reload: 'Read current personnel record',
                stale: 'The record changed. Read the current record, then review your retained entries before retrying.',
                reloaded: 'Current record read. Your entries remain unchanged; review the declared choices and current version before retrying.',
                retiredAnswer: 'Preserved answer not declared by the installed record',
                retainedDraft: 'Retained draft change needs review against the current declaration. It has not been saved.',
                discardDraft: 'Discard this unsaved draft change',
            },
            heading: 'Personnel eligibility evidence',
            intro: 'The people this application names, the dated roles each one holds and the evidence each role rests on. The platform records what the evidence shows. It does not decide whether anyone is eligible.',
            loading: 'Loading the personnel records…',
            loadError: 'The personnel records could not be loaded.',
            retrySubject: 'the personnel records',
            vocabError: 'The roles and outcomes declared by the installed record could not be read.',
            notDeclaredRoles: 'The installed record declares no role for this framework, so no role can be recorded yet.',
            notDeclaredBases: 'The installed record declares no qualification alternative for this framework.',
            notDeclaredOutcomes: 'The installed record declares no criminal-record outcome for this framework, so none can be recorded yet.',
            empty: 'No person is recorded for this case yet.',
            total: '{count} personnel record(s) in total',
            pagination: 'Pages of personnel records',
            readOnly: 'You can read these records. Recording, changing them and attaching evidence need the manage permission.',
            add: 'Add a person',
            personName: 'Name of the person',
            personHint: 'Type the name as it appears on the personal record statement.',
            statementSignedOn: 'Date the personal record statement was signed',
            statementNotRecorded: 'Statement date not recorded',
            edit: 'Edit this record',
            colRole: 'Role',
            colBasis: 'Qualification alternative the evidence shows',
            colDates: 'Dates held',
            colApplicability: 'Applies to the application form',
            colEvidence: 'Evidence',
            noRoles: 'No role is recorded for this person yet.',
            basisNotRecorded: 'Not yet recorded',
            undeclaredRole: 'Role not declared by the installed record ({code})',
            undeclaredBasis: 'Alternative not declared by the installed record ({code})',
            undeclaredOutcome: 'Outcome not declared by the installed record ({code})',
            heldFrom: 'From {from}',
            heldUntil: 'to {to}',
            heldOpen: 'still held',
            applies: 'Applies to this application form',
            doesNotApply: 'Does not apply to this application form',
            applicabilityNotDeclared: 'Not declared by the installed record',
            evidenceNone: 'No evidence is attached to this role.',
            evidenceCount: '{count} file(s) attached. Their names need the permission to download attachments.',
            evidenceUpload: 'Attach evidence to {role}',
            evidenceRejected: 'The evidence could not be attached.',
            evidenceNotBound: 'The file is held for this record but is not yet attached to the role. {message}',
            currency: {
                heading: 'Currency of the statement',
                current: 'Within the declared limit until {date}.',
                older: 'Evidence is older than the declared limit.',
                notDeterminable: 'Not determinable: {reason}.',
                reasonNoStatementDate: 'no statement date is recorded',
                reasonNoLimit: 'the installed record declares no currency limit',
                reasonNoLimitInForce: 'the declared limit is not in force on this date',
                reasonActivationUnknown: 'the date the declared limit takes effect is not stated',
                reasonNotOperational: 'the framework is installed for reference only',
                basisReceipt: 'Judged on the receipt date, {date}.',
                basisAsOf: 'Judged on the date of this view, {date}.',
            },
            outcome: {
                heading: 'Criminal-record check outcome',
                recordOutcome: 'Record the authority’s outcome',
                changeOutcome: 'Change the recorded outcome',
                recordedOn: '{outcome}, reported on {date}',
                title: 'Record the criminal-record outcome the authority gave',
                hint: 'Choose the outcome the State Bank reported and enter the date the authority gave it. The platform keeps that outcome only. The content of the check stays with the authority that holds it, and none of it can be entered here.',
                code: 'Outcome',
                chooseCode: 'Choose an outcome',
                on: 'Date the authority gave the outcome',
                submit: 'Record outcome',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The outcome was recorded.',
                failed: 'The outcome could not be recorded.',
                blocked: 'Choose an outcome and enter the date the authority gave it.',
            },
            record: {
                addTitle: 'Add a person',
                editTitle: 'Edit this personnel record',
                hint: 'Enter the person’s name and each role with the dates it was held, as the evidence states them. For each role, record the qualification alternative the evidence shows. The platform records that choice; it does not decide whether the person qualifies.',
                roleRow: 'Role {n}',
                role: 'Role',
                chooseRole: 'Choose a role',
                basis: 'Qualification alternative the evidence shows',
                basisNotRecorded: 'Not yet recorded',
                from: 'Held from',
                to: 'Held until',
                toOpen: 'Still held (leave empty)',
                removeRole: 'Remove this role',
                addRole: 'Add a role',
                submitAdd: 'Record person',
                submitEdit: 'Save record',
                saving: 'Saving…',
                cancel: 'Cancel',
                required: 'Required',
                blocked: 'Enter the person’s name and, for each role, the role and the date it was held from.',
                added: 'The person was recorded.',
                saved: 'The record was saved.',
                failed: 'The record could not be saved.',
                stale: 'The record changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            },
            conditions: {
                heading: 'Conditions of the legal representative and general director',
                recordConditions: 'Record the conditions',
                changeConditions: 'Change the recorded conditions',
                notRecorded: 'Not recorded',
                nationality: 'Nationality',
                conviction: 'Criminal record',
                sanction: 'Administrative sanction in finance, banking or cybersecurity',
                notDeclared: 'The installed record declares no condition for this application form.',
                title: 'Record what the evidence shows about each condition',
                hint: 'For each condition, choose what the evidence shows. Leave a condition empty when it is not recorded. The platform keeps the choice the evidence shows. It does not decide on the condition.',
                chooseOption: 'Not recorded',
                undeclared: 'Not an option the installed record declares ({code})',
                submit: 'Record conditions',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The conditions were recorded.',
                failed: 'The conditions could not be recorded.',
                clearRetired: 'Clear the retired criminal-record value',
                clearRetiredHint: 'The criminal-record code was retired, and the installed record no longer declares it. Clearing removes it from this record. The two conditions above are not changed.',
                clearedRetired: 'The retired value was cleared.',
            },
        },
        copy: {
            recordedBy: 'Recorded by',
            notice: 'Platform notice',
        },
        outcome: {
            heading: 'Stop, completion and closure',
            intro: 'A stop is the only way to end a case early. The platform records what the organisation files and what the State Bank decides. The State Bank decides every stop, revocation and completion, and issues every decision and certificate. The platform holds the copy the authority gives and says where that copy came from.',
            readOnly: 'You can read these records. Filing or recording needs the manage permission.',
            // Sprint 62.51.0 (CL-3) — a procedure that declares no organisation outcome filing.
            stopNotOffered: 'The installed procedure of this case offers no stop filing by the organisation, so none is recorded here.',
            completionNotOffered: 'The installed procedure of this case offers no completion filing by the organisation, so none is recorded here.',
            recordedOnIntro: 'Its outcome is recorded from the authority’s and the body’s own documents in:',
            recordedOnNone: 'The installed procedure names no register where its outcome is recorded.',
            loadError: 'The stop, completion, closure and requests could not be read.',
            retry: 'Try again',
            notDeclared: 'Not declared by an installed framework.',
            stop: {
                heading: 'Stop',
                none: 'No stop has been filed for this case.',
                filed: 'Stop filed. The State Bank’s decision is awaited. The platform records no decision of its own.',
                decided: 'The State Bank’s decision is recorded, as the authority gave it.',
                stopDate: 'Stop date, as a person entered it: {date}.',
                groundsHeading: 'Grounds',
                viewGrounds: 'View the grounds ({count})',
                groundsNone: 'Not declared by an installed framework. The platform records no ground.',
                filesHeading: 'Files the stop is filed with',
                role_application: 'Application',
                role_results_report: 'Results report',
                role_stop_plan: 'Stop plan',
                held: 'Held: {name}.',
                notHeld: 'Not held under this case.',
                notFiled: 'Not filed.',
                missing: 'Still missing: {roles}.',
                complete: 'Every file the stop needs is held.',
                endedByCertificate: 'The authority’s certificate of completion is recorded, so no stop is filed on this case.',
                decisionEndedByCertificate: 'The authority’s certificate of completion is recorded, so no decision to stop and revoke the test is recorded on this case.',
                filedEndedByCertificate: 'Stop filed. The authority’s certificate of completion is recorded, so no decision on this stop is awaited.',
                decisionHeading: 'The State Bank’s decision',
                decisionNone: 'No decision is recorded. The platform records one only from the authority’s document.',
                decisionRef: 'Decision reference: {ref}',
                decidedOn: 'Date of the decision: {date}',
                decisionText: 'Text of the decision, as the authority gave it:',
                evidence: 'Evidence: {names}',
            },
            completion: {
                heading: 'Completion',
                none: 'No completion request has been filed.',
                filed: 'Completion request filed. The State Bank evaluates it. The platform decides nothing.',
                role_evaluation_report: 'Evaluation report',
                role_results_report: 'Results report',
                endedByRevocation: 'The State Bank’s decision to stop and revoke the test is recorded, so no completion request or copy of a certificate is recorded on this case.',
                certificateNone: 'No copy of the certificate is recorded. The platform issues no certificate.',
                certificateRecorded: 'The authority’s copy is recorded.',
                copySource: 'Source: the authority’s copy.',
                copyDate: 'Date on the authority’s copy: {date}.',
            },
            closure: {
                heading: 'Closure',
                unavailable: 'Closure is recorded after the authority’s decision is recorded.',
                noticesHeading: 'Stop notices',
                noticesNone: 'No stop notice is recorded.',
                notice: 'Notice sent {date}: {name}.',
                liquidationHeading: 'Liquidation record',
                liquidationNone: 'No liquidation record is recorded.',
                liquidation: 'Liquidation record of {date}: {name}.',
                dutiesHeading: 'Post-stop duties',
                dutiesIntro: 'These duties come from the base instrument. The platform records them and does not create them. Each runs from its own anchor date, which a person enters on the closure record. A duty whose anchor date is not entered shows no date.',
                duty_entitlements_settled: 'Customer rights and obligations are settled',
                duty_settlement_report: 'The settlement is reported in writing',
                citation: 'Source: {citation}',
                dutyNotDeclared: 'Not declared by an installed framework. No period is shown and no date is computed.',
                dutyAmbiguous: 'More than one installed record declares this duty. The platform does not choose between them.',
                dutyNotCountable: 'The installed period is not one the platform counts. No date is computed.',
                dutyDueFromAnchor: 'Period: {period} {unit} from {anchor}. Due {due}.',
                dutyAnchorNotRecorded: 'Anchor not recorded ({anchor}). No date is shown.',
                dutyNotInForce: 'The installed clock for this duty is not in force yet, or the record states no start date from which it can be read. No date is shown.',
                dutyContinuingNoPeriod: 'Continuing duty. The text states no period for a solution outside the scope of this duty, so no period and no date are shown.',
                dutySolutionPointNotRecorded: 'Solution point not recorded. The case’s application form does not show which solution point it is, so no date is computed.',
                anchor_stop_decision: 'the State Bank stop decision date',
                anchor_settlement_completion: 'the settlement completion date',
                anchorLabel_stop_decision: 'State Bank stop decision date',
                anchorLabel_settlement_completion: 'Settlement completion date',
                anchorsHeading: 'Anchor dates',
                settlementReferencePending: 'Settlement reference: pending until the State Bank stop decision date is recorded.',
                settlementReferenceRecorded: 'Settlement reference: {date}, the recorded State Bank stop decision date.',
                anchorRecorded: '{label}: {date}. Evidence: {evidence}.',
                anchorNotRecorded: '{label}: not recorded.',
                unit_months: 'months',
                unit_calendar_days: 'calendar days',
                unit_working_days: 'working days',
            },
            directions: {
                heading: 'The authority’s requests and on-site notices',
                intro: 'Each is recorded in the direction register. Its response period is read from the installed record that declares its kind.',
                none: 'None recorded.',
                kindUndeclared: 'Recorded as “{kind}”, a kind no installed framework of this case declares.',
                registerWithheld: 'The reference, authority, receipt and response period are held in the direction register, which needs its own read permission.',
                manageNeeded: 'Recording a request or notice also needs the direction register’s manage permission.',
                ref: 'Reference: {ref}',
                authority: 'Authority: {name}',
                received: 'Received {date}',
                clockRunning: 'Response due {due}, from the installed record.',
                clockNotDeclared: 'Response period: not declared by an installed framework.',
                clockOther: 'Response period not computed: {reason}.',
                clockNotInForceFrom: 'Response period not computed: the framework that declares it applies only from {date}.',
            },
            actions: {
                fileStop: 'File a stop',
                recordDecision: 'Record the authority’s decision',
                fileCompletion: 'File a completion request',
                recordCertificate: 'Record the authority’s copy',
                recordClosure: 'Record closure',
                recordDirection: 'Record a request or notice',
                chooseFile: 'Choose a file',
                uploading: 'Holding the file…',
                uploaded: 'Held under this case.',
                uploadFailed: 'The file could not be held under this case.',
                cancel: 'Cancel',
                saving: 'Saving…',
                continue: 'Continue to confirmation',
            },
            stopForm: {
                title: 'File a stop',
                hint: 'The stop needs the application, the results report and the stop plan, each held under this case, and the stop date as a person knows it.',
                incomplete: 'Hold the application, the results report and the stop plan, and enter the stop date, before continuing.',
                stopDate: 'Stop date',
            },
            stopConfirm: {
                title: 'Confirm the stop',
                text: 'Filing records the organisation’s stop. The State Bank decides whether the test is stopped and whether the certificate is revoked. Nothing on this page decides for it.',
                confirm: 'File the stop',
                done: 'The stop is filed.',
                failed: 'The stop could not be filed.',
            },
            revoke: {
                title: 'Record the State Bank’s decision',
                hint: 'Record only the authority’s own decision, from its document. The reference and the text are kept as the authority gave them.',
                incomplete: 'Enter the decision reference, the date, the text and the decision file. Each is kept as the authority gave it.',
                ref: 'Decision reference',
                decidedOn: 'Date of the decision',
                text: 'Text of the decision',
                evidence: 'Decision file',
                submit: 'Record the decision',
                done: 'The decision is recorded.',
                failed: 'The decision could not be recorded.',
            },
            completionForm: {
                title: 'File a completion request',
                hint: 'The request needs the evaluation report and the results report, each held under this case.',
                incomplete: 'Hold both reports before filing the request.',
                submit: 'File the request',
                done: 'The completion request is filed.',
                failed: 'The completion request could not be filed.',
            },
            certificate: {
                title: 'Record the authority’s copy',
                hint: 'Record only a copy the authority gave. The platform issues no certificate.',
                incomplete: 'Hold the authority’s copy and enter its date.',
                copyDate: 'Date on the authority’s copy',
                file: 'The authority’s copy',
                submit: 'Record the copy',
                done: 'The authority’s copy is recorded.',
                failed: 'The authority’s copy could not be recorded.',
            },
            anchorCorrection: {
                heading: 'Anchor and documentary-event comparison',
                limitation: 'This compares declared events on this case only. Correcting an anchor does not change a lifecycle event or revocation record. Wrong lifecycle facts require a separate correction path.',
                not_declared: 'No same-case event comparison is declared.',
                pending_anchor: 'Documentary event recorded; an explicit anchor is still required.',
                anchor_only: 'Explicit anchor recorded; no counterpart event is recorded.',
                pending_fact: 'Counterpart evidence is pending verification. No affected due date is shown.',
                agreed: 'The explicit anchor and verified documentary event agree.',
                conflict: 'The records conflict. Both dates are preserved; no affected due date is shown.',
                mappingInvalid: 'The installed comparison declaration needs review.',
                multipleEvents: 'Several counterpart occurrences are recorded. None is selected as authoritative.',
                datesDisagree: 'The recorded actual dates differ.',
                evidenceReview: 'Review the counterpart event’s evidence and declared prerequisites.',
                anchorDate: 'Explicit anchor date',
                eventDate: 'Documentary event occurrence date',
                event: 'Declared event',
                source: 'Source',
                notRecorded: 'Not recorded',
                anchorNotDeclared: 'The installed duty declares no anchor. No due date is computed.',
                dutyConflict: 'The anchor conflicts with its documentary event. No due date is computed.',
                dutyPending: 'The anchor’s counterpart evidence is pending verification. No due date is computed.',
                declaredPeriod: 'Declared period: {period} {unit}.',
                settlementConflict: 'Settlement reference: conflicting documentary dates. No reference date is selected.',
                correct: 'Correct anchor',
                title: 'Correct an explicitly recorded anchor',
                hint: 'Enter the actual corrected date, evidence and justification. The previous record is preserved in immutable history. This action does not record or correct an authority decision, lifecycle fact or revocation.',
                date: 'Corrected actual date',
                evidence: 'Evidence reference for the correction',
                reason: 'Reason for correction',
                required: 'required',
                submit: 'Record anchor correction',
                saved: 'The anchor correction was recorded.',
                failed: 'The anchor correction could not be recorded.',
                stale: 'The case changed. Reload its closure record before trying again. Your entries are retained.',
                reload: 'Reload closure record',
                reloaded: 'Current closure read. Review the current anchor and retained entries before retrying.',
                history: 'Immutable anchor correction history',
                historyNone: 'No anchor correction is recorded.',
                previous: 'Previous anchor record',
                corrected: 'Corrected anchor record',
                recordedBy: 'Recorded by',
                recordedAt: 'Recorded at',
                versions: 'Case versions',
            },
            closureForm: {
                title: 'Record closure',
                hint: 'Record a stop notice, the liquidation record, or both, as each was made.',
                incomplete: 'Hold a stop notice with its date, the liquidation record with its date, or both.',
                noticeFile: 'Stop notice file',
                noticeDate: 'Date the notice was sent',
                liquidationFile: 'Liquidation record file',
                liquidationDate: 'Date of the liquidation record',
                anchorsHint: 'Enter each date as the record states it, with the evidence it rests on. No date is computed here.',
                stopDecisionDate: 'State Bank stop decision date',
                stopDecisionEvidence: 'Evidence of the stop decision',
                settlementDate: 'Settlement completion date',
                settlementEvidence: 'Evidence of the settlement',
                noticeIncomplete: 'A stop notice needs its file and the date it was sent. Enter both, or leave the notice empty.',
                liquidationIncomplete: 'The liquidation record needs its file and its date. Enter both, or leave it empty.',
                anchorIncomplete: 'Each anchor date needs its date and its evidence. Enter both, or leave that anchor empty.',
                anchorRecordedNote: '{label}: already recorded. It is not entered again.',
                refusal: {
                    STOP_DECISION_IN_FUTURE: 'The stop decision date cannot be after today.',
                    SETTLEMENT_BEFORE_STOP: 'The settlement completion date cannot be before the stop it follows.',
                    POST_STOP_ANCHOR_ALREADY_RECORDED: 'This date is already recorded. It is not entered again.',
                },
                submit: 'Record',
                done: 'The closure is recorded.',
                failed: 'The closure could not be recorded.',
            },
            directionForm: {
                kindsLoading: 'Reading the kinds the installed record declares…',
                kindsNone: 'No installed framework of this case declares a kind of request or notice, so none can be recorded here.',
                kindsLoadError: 'The kinds of request or notice could not be read.',
                kindChoose: 'Choose a kind',
                title: 'Record a request or notice',
                hint: 'Recorded in the direction register. Its clock is read from the installed record, not set here.',
                incomplete: 'Complete each field, and give at least one receipt reference.',
                kind: 'Kind',
                ref: 'Reference',
                authority: 'Authority',
                scope: 'Scope',
                received: 'Date and time received',
                receivedDay: 'Date received',
                receivedTime: 'Time received',
                receipt: 'Receipt references, one per line',
                submit: 'Record',
                done: 'The request or notice is recorded.',
                failed: 'The request or notice could not be recorded.',
            },
            fieldLabels: {
                application_file_id: 'Application',
                results_report_file_id: 'Results report',
                stop_plan_file_id: 'Stop plan',
                stop_date: 'Stop date',
                decision_ref: 'Decision reference',
                decided_on: 'Date of the decision',
                decision_text: 'Text of the decision',
                evidence_file_ids: 'Decision file',
                evaluation_report_file_id: 'Evaluation report',
                file_id: 'The authority’s copy',
                copy_dated_on: 'Date on the authority’s copy',
                notice: 'Stop notice',
                liquidation: 'Liquidation record',
                kind: 'Kind',
                direction_ref: 'Reference',
                authority_name: 'Authority',
                scope_description: 'Scope',
                received_at: 'Date and time received',
                receipt_evidence_refs: 'Receipt references',
                'anchors.stop_decision.date': 'State Bank stop decision date',
                'anchors.stop_decision.evidence_ref': 'Evidence of the stop decision',
                'anchors.settlement_completion.date': 'Settlement completion date',
                'anchors.settlement_completion.evidence_ref': 'Evidence of the settlement',
                'anchors.stop_decision': 'State Bank stop decision date',
                'anchors.settlement_completion': 'Settlement completion date',
            },
        },
        children: {
            heading: 'Adjustments and extensions',
            intro: 'A child case changes or continues an application this case already holds. The platform records the steps the State Bank reports and computes the periods the installed record declares. The State Bank decides each request.',
            kindAdjustment: 'Adjustment',
            kindExtension: 'Extension',
            openAdjustment: 'Open an adjustment',
            openExtension: 'Open an extension',
            openTitleAdjustment: 'Open an adjustment',
            openTitleExtension: 'Open an extension',
            openHint: 'The platform copies this case’s solution record as it stands now. The copy does not change when the solution is edited later.',
            formLabel: 'Application form',
            formNone: 'The installed record declares no application form for this kind.',
            notesLabel: 'Notes (optional)',
            submit: 'Open the case',
            saving: 'Opening…',
            cancel: 'Cancel',
            opened: 'The child case is open.',
            openFailed: 'The child case could not be opened.',
            empty: 'No adjustment or extension is recorded on this case.',
            loadError: 'The adjustments and extensions could not be read.',
            unavailable: 'No child case can be opened on this case. {reason}',
            refusedReason: {
                grant_not_declared: 'The parent case has no grant recorded by an installed declaration.',
                grant_not_recorded: 'The parent case has no grant recorded yet.',
                parent_procedure_not_declared: 'The parent case’s procedure declares no lifecycle, so no child case can open on it.',
                framework_not_operational: 'The framework is installed for reference, so no child case can open on it.',
                parent_framework_not_installed: 'The framework is not installed for this organisation.',
                kind_not_declared: 'No installed record declares this kind of child case.',
                kind_ambiguous: 'More than one installed record declares this kind of child case.',
                parent_stopped_and_revoked: 'The State Bank’s decision to stop and revoke the parent test is recorded, so no child case can open on it.',
            },
            readOnly: 'You can read these cases. Opening a child case needs the manage permission.',
            clocksHeading: 'Clocks',
            noClocks: 'No clock applies to this request under the installed record.',
            clockRunning: '{name}: due {due}. Counted {period} {unit} from {start}.',
            clockPaused: '{name}: due {due}. Counted {period} {unit} from {start}, with {paused} {unit} paused.',
            clockNotStarted: '{name}: not started. No entry is recorded for its start step.',
            clockNoNumeric: '{name}: the installed record declares no numeric period.',
            clockSource: 'Computed from the recorded step date. It is not a date set by the authority.',
            clockOwnerAuthority: 'Owed by the State Bank.',
            clockOwnerOrganisation: 'Owed by the organisation.',
            unitWorkingDays: 'working days',
            unitCalendarDays: 'calendar days',
            unitMonths: 'months',
            resultsHeading: 'Results report',
            resultsProvisional: 'Format provisional. The results-report format is in Phụ lục V of Decree 94, which is not in the corpus. No format is shown as verified.',
            filingHeading: 'Filing',
            filingNotRecorded: 'Not recorded. A filing is recorded only against an internal sign-off that is recorded as complete.',
            filingRecorded: 'Recorded by the organisation: reference {reference}, filed {date}.',
            signoffNone: 'No internal sign-off has been requested for this case.',
            signoffPending: 'Internal sign-off: pending. A filing cannot be recorded until the sign-off is recorded as complete.',
            signoffComplete: 'Internal sign-off: recorded as complete.',
            signoffNotComplete: 'Internal sign-off: recorded as not complete. A filing cannot be recorded on it.',
            solutionHeading: 'Solution copied from the parent',
            solutionCopied: 'Copied from the parent solution record at version {version}.',
            solutionNone: 'The parent holds no solution record, so nothing was copied.',
            recordStep: 'Record a step',
            retry: 'Try again',
            currentStep: 'Current step: {step}',
            noStep: 'No step is recorded yet.',
            chooseForm: 'Choose a form',
        },
    },
    // Sprint 63.23.0 — the OAuth consent screen at /oauth/consent.
    oauth_consent: {
        error_request_not_found: 'This request has expired or was already answered. Return to the app and start connecting it again.',
        error_request_already_decided: 'This request was already answered. Return to the app; if it is not connected, start connecting it again.',
        error_client_revoked: 'This app has been revoked, so it cannot connect. Ask an administrator of your organisation if you need it.',
        page_title: 'Approve an app',
        title: 'Allow {client} to use ComplianceOne?',
        title_pending: 'Approve an app',
        loading: 'Loading the request…',
        intro: '{client} is asking to use ComplianceOne on your behalf.',
        client_label: 'App',
        origin_label: 'Registered by',
        origin_admin: 'An administrator of your organisation',
        origin_dynamic: 'The app itself — no administrator has reviewed it',
        redirect_label: 'Sends you back to',
        organisation_label: 'Organisation',
        scopes_heading: 'It will be able to',
        scope_read: 'Read your organisation’s records (mcp:read)',
        scope_write: 'Propose changes for approval (mcp:write)',
        scope_write_note: 'A proposed change is not made until an administrator approves it in the approval queue.',
        scope_other: 'Another permission: {scope}',
        tools_heading: 'Tools it can use',
        tools_none: 'No tools.',
        acts_as_you: 'It acts as you, with no more access than your own role gives you. You can revoke it at any time under Connected apps on your profile.',
        approve: 'Allow',
        deny: 'Deny',
        submitting: 'Sending your decision…',
        missing_request: 'This page was opened without a request. Return to the app and start connecting it again.',
        error_load: 'The request could not be read. It may have expired or already been answered; return to the app and start connecting it again, or reload the request.',
        reload: 'Reload the request',
        error_decide: 'Your decision could not be sent.',
        redirecting: 'Returning you to the app…',
        // Gate E12.5 pass 1 (P2-8) — a redirect whose "host" says nothing about
        // who receives the approval.
        redirect_warning_title: 'Check where this approval goes',
        redirect_warning_loopback: 'This app receives the approval at an address on your own computer ({host}). Any program running on this computer could be listening there. Allow it only if you started this connection yourself, on this computer.',
        redirect_warning_private_use_scheme: 'This app receives the approval through a link that an app installed on your device opens ({host}). Any installed app can claim such a link. Allow it only if you started this connection yourself, from the app you expect.',
    },
    login: {
        title: 'AesirX ComplianceOne',
        subtitle: 'Inicieu sessió al vostre compte',
        email: 'Correu electrònic',
        password: 'Contrasenya',
        sign_in: 'Iniciar sessió',
        signing_in: 'Iniciant sessió...',
        mfa_code: 'Codi MFA',
        mfa_placeholder: 'Introduïu el codi de 6 dígits',
        mfa_instruction: "Introduïu el codi de 6 dígits de la vostra aplicació d'autenticació.",
        // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
        password_change_required_title: 'Change your password',
        password_change_required_intro: 'Your administrator requires you to choose a new password before you continue. Nothing else is available until you do.',
        password_change_current: 'Current password',
        password_change_current_hint: 'The password you signed in with.',
        password_change_new: 'New password',
        password_change_new_hint: 'At least 12 characters, with an upper-case letter, a lower-case letter, a digit and a special character. It must differ from your last 12 passwords.',
        password_change_confirm: 'Confirm new password',
        password_change_mismatch: 'The two new passwords do not match.',
        password_change_field_required: 'This field is required.',
        password_change_submit: 'Change password',
        password_change_failed: 'Your password could not be changed.',
        password_change_done: 'Your password has been changed and you have been signed out everywhere. Sign in with your new password.',
        password_change_sign_out: 'Sign out',
        mfa_enrolment_required_title: 'Set up two-factor authentication',
        mfa_enrolment_required_intro: 'Your organisation requires a second factor for your account. Set one up to continue — it takes a minute and you can finish it here.',
        mfa_enrolment_required_sign_out: 'Sign out instead',
        mfa_enrolment_required_continue: 'Continue to the platform',
        mfa_enrolment_required_blocked: 'Until this is set up, the rest of the platform is unavailable to you.',
        // ── Sprint 63.6.3, gate E12.5 pass 2: the TOTP step-up page for a LIVE session held at MFA_REQUIRED ──
        mfa_step_up_title: 'Enter your verification code',
        mfa_step_up_intro: 'Your organisation asks for a code from your authenticator app before this session can continue. Enter it below to carry on where you were.',
        mfa_step_up_instruction: 'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
        mfa_step_up_locked: 'Too many incorrect codes were entered. Wait a few minutes, then try again.',
        mfa_step_up_not_enrolled: 'This account has no authenticator set up, so there is no code to enter. Sign out and sign in again.',
        mfa_step_up_accepted: 'Code accepted. Continuing.',
        mfa_step_up_code_label: 'Verification code',
        mfa_step_up_placeholder: 'Authenticator code or backup code',
        mfa_step_up_refresh_failed: 'Your code was accepted, but the page could not confirm it with the server. Try again to continue.',
        verify: 'Verificar',
        verifying: 'Verificant...',
        forgot_password: 'Heu oblidat la contrasenya?',
        account_locked: 'El compte està bloquejat. Torneu-ho a provar més tard.',
        account_deactivated: 'El compte ha estat desactivat. Contacteu amb el vostre administrador.',
        invalid_mfa: 'Codi MFA no vàlid. Torneu-ho a provar.',
        invalid_credentials: 'Correu electrònic o contrasenya no vàlids.',
        login_failed: "Error d'inici de sessió",
        session_limit_reached: 'El compte ha arribat al límit de sessions actives. Tanqueu la sessió en un altre dispositiu o demaneu a un administrador que revoqui una sessió existent i torneu-ho a provar.',
        session_expired: 'La vostra sessió ha caducat. Torneu a carregar la pàgina i torneu-ho a provar.',
        sso_divider: 'or',
        sso_sign_in_with: 'Sign in with',
        sso_checking: 'Checking sign-in options…',
        sso_completing: 'Completing sign-in…',
        sso_failed: 'Single sign-on did not complete. Contact your administrator.',
        sso_required: 'This organisation requires single sign-on. Use the link your administrator gave you.',
        sso_options_label: 'Single sign-on options',
        sso_provider_session_not_ended: 'You are signed out of ComplianceOne, but your identity provider session was not ended. If you share this device, sign out at your identity provider as well.',
    },
    auth: {
        forgot_password: {
            title: 'Contrasenya oblidada',
            subtitle: 'Introduïu la vostra adreça electrònica i us enviarem un enllaç per restablir la contrasenya.',
            send: 'Enviar enllaç de restabliment',
            sending: 'Enviant...',
            success: "Si el correu electrònic existeix, s'ha enviat un enllaç per restablir la contrasenya.",
            back_to_login: "Tornar a l'inici de sessió",
        },
        reset_password: {
            title: 'Restablir contrasenya',
            subtitle: 'Introduïu la vostra nova contrasenya a continuació.',
            new_password: 'Nova contrasenya',
            confirm_password: 'Confirmar contrasenya',
            reset: 'Restablir contrasenya',
            resetting: 'Restablint...',
            success: "La vostra contrasenya s'ha restablert correctament.",
            go_to_login: "Anar a l'inici de sessió",
            password_mismatch: 'Les contrasenyes no coincideixen.',
            invalid_token: 'Token de restabliment no vàlid o absent. Sol·liciteu un nou enllaç de restabliment de contrasenya.',
            requirements_title: 'Requisits de la contrasenya:',
            password_requirements: 'La contrasenya ha de complir els requisits següents:',
            rules: {
                min_length: 'Com a mínim 12 caràcters',
                uppercase: 'Com a mínim una lletra majúscula',
                lowercase: 'Com a mínim una lletra minúscula',
                digit: 'Com a mínim un dígit',
                special: 'Com a mínim un caràcter especial',
            },
        },
        verify_email: {
            title: 'Verificació del correu electrònic',
            verifying: 'Verificant la vostra adreça electrònica...',
            success: "El vostre correu electrònic s'ha verificat correctament.",
            invalid_token: 'Token de verificació no vàlid o absent.',
            failed: 'La verificació del correu electrònic ha fallat. El token pot no ser vàlid o haver caducat.',
            go_to_login: "Anar a l'inici de sessió",
        },
        offline: {
            title: 'Sense connexió',
            message: 'Sembla que heu perdut la connexió a Internet. Comproveu la vostra xarxa i torneu-ho a provar.',
        },
    },
    intake: {
        title: 'Sol·licitud de drets de dades',
        subtitle: 'Presenteu una sol·licitud de drets de dades a la nostra organització',
        fields: {
            full_name: 'Nom complet',
            email: 'Adreça electrònica',
            phone: 'Número de telèfon (opcional)',
            request_type: 'Tipus de sol·licitud',
            description: 'Descriviu la vostra sol·licitud',
            preferred_language: 'Idioma preferit',
            consent: "Dono el meu consentiment per al tractament d'aquesta sol·licitud",
        },
        submit: 'Enviar sol·licitud',
        validation: {
            name_required: 'El nom complet és obligatori',
            email_required: 'Cal una adreça electrònica vàlida',
            type_required: 'Seleccioneu un tipus de sol·licitud',
            description_required: 'Descriviu la vostra sol·licitud',
            consent_required: 'Heu de donar el consentiment per al tractament per continuar',
        },
        error_title: 'Error',
        config_load_error: "No s'ha pogut carregar la configuració. Si us plau, verifiqueu l'URL.",
        organization_label: 'Organització',
        customer_reference: 'Número de referència del client',
        country: 'País / Jurisdicció',
        preferred_channel: 'Canal de resposta preferit',
        channels: {
            email: 'Correu electrònic',
            postal: 'Correu postal',
            phone: 'Telèfon',
            portal: 'Portal',
        },
        confirmation: {
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            next_steps_legal_matter: 'Your request will be handled as a legal matter: the organisation will review it and contact you about it. Please keep your reference number.',
            title: 'Sol·licitud rebuda',
            message: "La vostra sol·licitud s'ha registrat correctament. El vostre número de referència és:",
            next_steps: "Tramitarem la vostra sol·licitud tan aviat com sigui possible d'acord amb la legislació aplicable. El termini de tractament previst és de 30 dies hàbils.",
        },
        // English until Phase 25 (R-57).
        // fix-sprint #431 (owner ruling 2026-10-03) — the acknowledgement emailed
        // to the requester when Settings › Notifications "Send confirmation email to
        // requester" is on. {name}, {organization} and {reference} are filled in.
        acknowledgement_email: {
            subject: 'We received your request (reference {reference})',
            greeting: 'Hello,',
            body: '{organization} has received your request concerning your personal data.',
            reference: 'Your reference is {reference}. Please quote it in any message about this request.',
            closing: 'We will contact you about the next steps. This message was sent automatically.',
        },
        request_type_not_offered: "This kind of request is not one this organisation's installed frameworks provide for. Please choose another request type.",
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        legal_matter_right: 'What do you wish to raise?',
        legal_matter_right_hint: 'This request is received as a legal matter. Choose which of these you wish to raise.',
        legal_matter_right_not_sure: 'Not sure',
        error_summary: 'Your request was not sent. Please correct the following:',
        request_type_not_enabled: 'This form does not accept that request type. Please choose another request type.',
        submit_failed: 'Your request could not be submitted: {reason}',
    },
    request_types: {
        access: 'Accés (DSAR)',
        deletion: 'Supressió',
        correction: 'Rectificació / Actualització',
        objection: 'Oposició',
        portability: 'Portabilitat de dades',
        restriction: 'Limitació del tractament',
        consent_withdrawal: 'Retirada del consentiment',
        protective_measures: 'Sol·licitar mesures i solucions de protecció de dades personals',
        processing_information: 'Be informed about the processing of personal data',
        consent_decision: 'Consent or refuse consent to processing',
        complaint_or_compensation: 'Complaint, denunciation, lawsuit or claim for damages',
    },
    statuses: {
        new: 'Nou',
        in_review: 'En revisió',
        waiting_on_requester: 'Esperant el sol·licitant',
        waiting_on_internal_data: 'Esperant dades internes',
        approved: 'Aprovat',
        closed: 'Tancat',
        rejected: 'Rebutjat',
    },
    admin: {
        nav: {
            rights_requests: 'Sol·licituds de drets',
            templates: 'Plantilles',
            response_templates: 'Plantilles de Resposta',
            data_mapping: "Registre d'activitats de tractament",
            governance: 'Governança',
            consent: 'Consentiment',
            assessments: 'Avaluacions',
            classification: 'Classificació',
            monitoring: 'Supervisió',
            incidents: 'Incidències',
            onboarding: 'Incorporació',
            connectors: 'Connectors',
            vendor_risk: 'Risc de proveïdors',
            deletion_orchestration: 'Orquestració de supressió',
            discovery: 'Descobriment de dades',
            incident_ops: "Operacions d'incidents",
            program_governance: 'Governança del programa',
            access_accountability: "Responsabilitat d'accés",
            consent_properties: 'Propietats de consentiment',
            localization: 'Localització',
            regulatory: 'Regulatòria',
            partner_workspace: 'Espai de col·laboradors',
            audit_trail: "Pista d'auditoria",
            forseti: 'Forseti',
            settings: 'Configuració',
            help_center: "Centre d'ajuda",
            dashboard: 'Tauler de control',
            platform: 'Plataforma',
            integrations: 'Integracions',
            cmp_dsar_sync: 'Sincronització CMP DSAR',
            cmp_integration: 'Integració CMP',
            purpose_mapping: 'Assignació de finalitats',
            webhook_config: 'Configuració de webhooks',
            bulk_sync: 'Sincronització massiva',
            privacy_scanner: 'Escàner de privacitat',
            privacy_monitoring: 'Supervisió de privacitat',
            real_time_scanner: 'Escàner en temps real',
            compliance_forms: 'Formularis de compliment',
            forms_dashboard: 'Tauler de formularis',
            form_templates: 'Plantilles',
            form_catalog: 'Catàleg de formularis',
            pack_catalog: 'Catàleg de paquets',
            org_groups: 'Grups organitzatius',
            consolidation: 'Consolidació',
            organizations: 'Organitzacions',
            org_list: 'Totes les organitzacions',
            intake_configs: "Configuracions d'entrada",
            task_management: 'Gestió de tasques',
            unified_audit: 'Auditoria Unificada',
            evidence_packs: "Paquets d'Evidència",
            framework_evidence_packs: 'Framework evidence packs',
            audit_periods: "Períodes d'Auditoria",
            regulatory_updates: 'Actualitzacions Regulatòries',
            departments: 'Departaments',
        },
        sidebar_category_data_privacy_rights: 'Privacitat de Dades & Drets',
        sidebar_category_vendor_third_party: 'Gestió de Proveïdors & Tercers',
        sidebar_category_risk_assessment: 'Risc & Avaluació',
        sidebar_category_operations_automation: 'Operacions & Automatització',
        sidebar_category_sector_overlays: 'Sector Overlays',
        sidebar_category_task_workflow: 'Gestió de Tasques & Flux de Treball',
        sidebar_groups: {
            consent_cmp_webhooks: 'CMP i Webhooks',
            // Sprint 62.28.0 — marketing-communications registers.
            marketing_communications: 'Marketing Communications',
            data_mapping_ropa: 'Registre RoPA',
            data_mapping_classification: 'Classificació',
            data_mapping_governance: 'Governança',
            access_live_monitoring: 'Seguiment en directe',
            access_reviews_investigations: 'Revisions i investigacions',
            access_evidence_policies: 'Evidències i polítiques',
            classification_taxonomies: 'Taxonomies i etiquetes',
            classification_workflow: 'Flux de treball',
            classification_insights: 'Insights',
            connectors_runtime: "Temps d'execució",
            connectors_due_diligence: 'Due Diligence',
            connectors_coverage: 'Cobertura',
            cp_definitions: 'Definicions',
            cp_lifecycle: 'Cicle de vida',
            cp_quality: 'Qualitat i evidència',
            deletion_execution: 'Execució',
            deletion_vendors: 'Venedors',
            deletion_evidence: 'Evidència i integració',
            discovery_rules: 'Regles i plantilles',
            discovery_scanning: 'Escaneig',
            discovery_review: 'Revisió del flux de treball',
            discovery_insights: 'Insights',
            governance_oversight: 'Supervisió',
            governance_reporting: 'Informes',
            governance_vendors: 'Venedors i transfronterers',
            governance_lifecycle: 'Cicle de vida',
            incident_ops_dashboards: 'Taulers de control',
            incident_ops_sla: 'SLA Config',
            incident_ops_playbooks: 'Llibres de jugades',
            incident_ops_vendor_regulator: 'Venedor i regulador',
            incident_ops_evidence: 'Evidència i revisió',
            incidents_workload: 'Càrrega de treball',
            incidents_regulatory: 'Regulatòria',
            incidents_reporting: 'Informes',
            localization_content: 'Contingut',
            regulatory_frameworks: 'Frameworks',
            localization_regulatory_registers: 'Regulatory Registers',
            localization_penalties_enforcement: 'Penalties & Enforcement',
            localization_sector_overlays: 'Sector Overlays',
            // Sprint 63.43.0 — Sector Overlays (Module 30): management and sector groups.
            sector_overlays_management: 'Overlay Management',
            sector_overlays_banking_payments: 'Banking & Payments',
            sector_overlays_crypto_assets: 'Crypto Assets',
            sector_overlays_manufacturing: 'Manufacturing',
            sector_overlays_food_traceability: 'Food Traceability',
            sector_overlays_transport_logistics: 'Transport & Logistics',
            sector_overlays_public_administration: 'Public Administration',
            sector_overlays_digital_technology: 'Digital Technology',
            sector_overlays_publishing: 'Publishing',
            sector_overlays_education: 'Education',
            sector_overlays_cross_sector: 'Cross-Sector',
            localization_data_exploitation: 'Data Exploitation',
            localization_cybersecurity_assurance_index: 'Cybersecurity Assurance Index',
            localization_food_traceability: 'Food Traceability',
            localization_observability: 'Observabilitat',
            monitoring_metrics: 'Mètriques',
            monitoring_programs: 'Programes',
            monitoring_tasks: 'Tasques',
            monitoring_controls: 'Controls',
            org_entities: 'Entitats',
            org_config: 'Configuració',
            org_frameworks: 'Frameworks i Rollup',
            partner_overview: 'Visió general',
            partner_partners: 'Socis',
            partner_delivery: 'Lliurament',
            partner_support: 'Suport',
            platform_identity: 'Identitat i rols',
            platform_access_control: "Control d'accés",
            platform_auth_session: 'Autenticació i sessió',
            platform_access_reviews: 'Accedir a les ressenyes',
            platform_files: 'Fitxers',
            platform_notifications: 'Notificacions',
            platform_module_config: 'Configuració del mòdul',
            platform_ops: 'Operacions i registres',
            pg_health: 'Programa Salut',
            pg_maturity: 'Maduresa',
            pg_audit: 'Auditoria i evidència',
            pg_training: 'Formació',
            pg_privacy_office: 'Privacy Office',
            pg_obligations: 'Obligacions',
            pg_documents: 'Documents',
            vr_overview: 'Visió general',
            vr_onboarding: 'Incorporació',
            vr_assessment: 'Avaluació',
            vr_oversight: 'Supervisió',
        },
        tickets: {
            title: 'Sol·licituds de drets',
            create: 'Afegir sol·licitud',
            filters: 'Filtres',
            search_placeholder: 'Search tickets...',
            export_csv: 'Exporta CSV',
            exporting: "S'està exportant...",
            bulk_export_failed: "L'exportació massiva ha fallat",
            bulk_export: {
                label: 'Exporta',
                format: {
                    pdf: 'PDF Resum',
                    zip: 'ZIP (paquets de proves per cas)',
                    csv: 'CSV',
                },
                warning_over_50: 'Això generarà aproximadament {count} paquets de casos; pot trigar uns quants minuts. Continuar?',
                blocked_over_500: "Restringeix el filtre a 500 casos o menys abans d'exportar ZIP (actual: {count}).",
                success: 'Exportació massiva desada al navegador de fitxers ({count} casos)',
                failed: "L'exportació massiva ha fallat",
                continue: 'Continua',
                cancel: 'Cancel·la',
            },
            no_results: "No s'han trobat requests",
            ticket_id: 'ID del tiquet',
            request_type: 'Sol·licitud Tipus',
            requester: 'Sol·licitant',
            source: 'Font',
            all_sources: 'Totes les fonts',
            assigned_to: 'Assignat A',
            created: 'Creat',
            load_error: 'Error en load cases. Si us plau, torneu-ho a intentar.',
            case_creation_failed: 'Error en crear el cas',
            business_unit: 'Unitat de negoci',
            reference: 'Referència',
            sla: {
                title: 'Estat SLA',
                on_track: '{days} dies restants',
                approaching: 'Termini proper',
                overdue: '{days} dies de retard',
                ack_overdue: "{hours}h passat el termini d'acusament de recepció",
                ack_warning: 'Acusament de recepció properament',
                closed: 'Tancat',
                completed_in: 'Completat en {days} dies',
                source_statutory: 'Statutory period',
                source_tenant_target: 'Your SLA target',
                source_platform_default: 'Platform default, not a legal period',
                awaiting_activation: 'Awaiting activation from {date}',
                no_installed_clock: 'No installed clock applies',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                handled_in_legal_matters: 'Handled in Legal Matters',
            },
        },
        templates: {
            title: 'Resposta Plantilles',
            new: 'Nou plantilla',
            edit: 'Editar Plantilla',
            all_types: 'Tots els tipus',
            all_locales: 'Tots els idiomes',
            type_access: 'Accés/DSAR',
            type_deletion: 'Eliminació',
            type_correction: 'Correcció',
            type_objection: 'Oposició',
            type_portability: 'Portabilitat',
            type_other: 'Altre',
            type_restriction: 'Restricció',
            type_all: 'All',
            locale_en: 'Anglès',
            locale_vi: 'Vietnamita',
            locale_all: 'Ambdós',
            name: 'Nom',
            request_type: 'Sol·licitud Tipus',
            locale: 'Idioma',
            subject: 'Titular',
            body: 'Cos',
            name_placeholder: 'Nom de la plantilla...',
            subject_placeholder: 'Assumpte del correu/carta...',
            body_placeholder: 'Cos de la plantilla. Utilitzeu {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} com a marcadors de posició.',
            available_placeholders: 'Marcadors de posició disponibles:',
            create_template: 'Afegir plantilla',
            preview: 'Previsualització',
            close_preview: 'Tancar previsualització',
            default_badge: 'Predeterminat',
            no_templates: "No s'han trobat templates",
            confirm_delete: 'Eliminar aquest template?',
            load_error: 'Error en load templates. Si us plau, torneu-ho a intentar.',
            create_error: 'Error en create template. Si us plau, torneu-ho a intentar.',
            update_error: 'Error en update template. Si us plau, torneu-ho a intentar.',
            delete_error: 'Error en delete template. Si us plau, torneu-ho a intentar.',
            preview_error: 'Error en preview template. Si us plau, torneu-ho a intentar.',
            response_templates_badge: 'Plantilles de Resposta',
            browse_form_templates: 'Explorar Plantilles de Formularis de Compliment',
            templates_loaded: 'Plantilles carregades',
            sample_organization: 'Organització de mostra',
        },
        detail: {
            requester_info: 'Informació del sol·licitant',
            status: 'Estat',
            assignment: 'Assignació',
            notes: 'Notes',
            audit_trail: "Pista d'auditoria",
            add_note: 'Afegir Note',
            case_not_found: 'Cas no trobat',
            load_error: 'Error en load case. Si us plau, torneu-ho a intentar.',
            partial_load_failed: 'Some information for this case could not be loaded, so parts of this page may be incomplete.',
            partial_load_parts: 'Not loaded: {parts}.',
            note_error: 'Error en add note. Si us plau, torneu-ho a intentar.',
            submitted_from: 'Enviat des de',
            via_cmp: 'mitjançant AesirX CMP',
            cmp_ticket: 'Tiquet CMP:',
            select_template: 'Seleccionar una plantilla',
            direction_inbound: 'CMP → GRC',
            direction_outbound: 'GRC → CMP',
            tabs: {
                details: 'Detalls',
                evidence: 'Evidència',
                communications: 'Comunicacions',
                escalations: 'Escalaments',
                audit_trail: "Pista d'auditoria",
                fulfillment: 'Compliment',
                response: 'Resposta',
                extension: 'Extensió',
            },
            requester: {
                name: 'Nom',
                email: 'Correu electrònic',
                phone: 'Telèfon',
                language: 'Idioma',
                source: 'Font',
                customer_ref: 'Ref. del client',
                country: 'País',
                // fix-sprint #431 — the country as received, where it was stored as a code.
                country_as_received: '(received as “{value}”)',
                preferred_channel: 'Canal preferit',
            },
            // fix-sprint #447 W9 (owner ruling OR3) — processor or third party must act
            third_party: {
                label: 'Processor or third party must act',
                hint: 'The installed deadline records may state a different period when a processor or third party must act. Changing this dates the case again from the day it was received, by the record that matches; an approved extension and closed pauses are carried onto the new date.',
                closed: 'The case is closed, so whether a processor or third party must act can no longer be changed.',
                confirm_title: 'Date the case again?',
                confirm_body: 'The case will be dated again from the day it was received, by the installed deadline record that matches. An approved extension and closed pauses are carried onto the new date. The old and the new due date are recorded.',
                confirm_label: 'Change and date again',
                saved: 'Saved. The case shows its old and new due date.',
                redated: 'Changed on {date}: dated again from receipt. Due date was {old}, now {new}.',
                not_redated_no_installed_record: 'Changed on {date}. No installed framework dates this case, so its due date ({due}) is unchanged.',
                not_redated_no_due_date: 'Changed on {date}. The case holds no due date, so nothing was dated again.',
                carried_extension: '{days} approved extension day(s) carried onto the new date.',
                carried_paused: '{amount} paused {unit} carried onto the new date.',
                units: {
                    calendar_days: 'calendar day(s)',
                    working_days: 'working day(s)',
                    hours: 'hour(s)',
                },
                mapping_invalid: 'An installed deadline record for this kind of request cannot be read as a period, so the case could not be dated again and the change was not saved.',
            },
            sla: {
                title: 'SLA',
                on_track: 'A Rastrejar',
                due_soon: 'Proper a vèncer',
                overdue: 'Vençut',
                days_remaining: 'd restants',
                due_date: 'Data de venciment',
                breached: 'Violat',
                paused: 'En pausa',
                total_paused_days: 'Total de dies en pausa',
                pause_intervals: 'Pause Intervals',
                pause_intervals_help: 'Each period the clock was stopped, so waiting-on-requester time can be told apart from processing time.',
                pause_intervals_none: 'No pauses recorded on this case.',
                no_due_date: 'No due date recorded',
                stopped_case_closed: 'SLA stopped — case closed',
                pause_interval_open: 'Paused since {from} — still waiting',
                pause_interval_closed: '{from} to {to} — {days} day(s)',
                pause_interval_by: 'Paused by {who}',
                pause_interval_actor_unknown: 'Paused by: not recorded',
                pause_interval_from_status: 'Paused from {status}',
                pause_interval_from_status_unknown: 'Paused from: not recorded',
                pause_interval_reason_unknown: 'Reason: not recorded',
                pause_interval_resumed_by: 'Resumed by {who}',
                processing_days: 'Processing time',
                waiting_days: 'Waiting on requester',
                days_elapsed: 'Dies transcorreguts',
                progress: 'Progrés',
                prov_heading_ack: 'Acknowledgement deadline',
                prov_heading_fulfil: 'Fulfilment deadline',
                prov_source_statutory: 'Statutory period: {reference}',
                prov_source_statutory_from_activation: 'Statutory period: {reference}, running from {date}, the day it came into force',
                prov_source_tenant_target: 'Your SLA target: {days} days from receipt',
                prov_source_platform_default: 'Platform default, not a legal period: {days} days from receipt',
                prov_source_none: 'Not dated: no installed clock, organisation target or platform default applies',
                prov_due: 'Due {date}',
                prov_awaiting_activation: 'Awaiting activation from {date}: the installed clock {reference} applies from that date, and the case will be dated by it then.',
                prov_no_installed_clock: 'No installed clock applies to this kind of request.',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                prov_handled_in_legal_matters: 'Handled in Legal Matters: this request is received as a legal matter, which is the record that is worked. No deadline applies to this intake record.',
                prov_open_legal_matter: 'Open the legal matter',
                prov_framework_unnamed: 'an installed framework',
                prov_legal_matter_no_access: 'The legal matter is visible to people who hold the permission to read legal matters.',
                prov_excluded: 'Installed clocks that could not be dated and took no part: {reasons}',
                prov_excluded_activation_date_not_stated: '{reference}: no effective date stated',
                prov_excluded_framework_record_unreadable: '{reference}: framework record could not be read',
                prov_excluded_phase_reference_unresolved: '{reference}: phase start not dated',
            },
            identity: {
                title: 'Identitat Verificació',
                verified: 'Verificat',
                not_verified: 'No verificat',
                method: 'Mètode',
                notes: 'Notes',
                verified_by: 'Verificat Per',
                verified_at: 'Verificat A',
                warning: 'Identitat no yet verificat.',
                verify_button: 'Verificar Identitat',
                select_method: '-- Seleccionar --',
                methods: {
                    email_confirmation: 'Confirmació per correu electrònic',
                    id_document: "Document d'identitat",
                    phone_call: 'Trucada telefònica',
                    in_person: 'En persona',
                    other: 'Altre',
                },
            },
            closure: {
                reason: 'Motiu de tancament',
                summary: 'Resum de tancament',
                summary_placeholder: 'Resum de com es va resoldre la sol·licitud...',
                reason_required: 'El resum i el motiu de tancament són obligatoris',
                response_not_sent: "Cal enviar una resposta formal a l'interessat abans de tancar aquest cas",
                reasons: {
                    fulfilled: 'Complert',
                    partially_fulfilled: 'Parcialment complert',
                    unfounded: 'Infundat',
                    excessive: 'Excessiu',
                    withdrawn: 'Retirat',
                    duplicate: 'Duplicar',
                    identity_unverified: 'Identitat no verificada',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — moving a case into a waiting status stops its clock,
            // and the server requires the reason (`pause_reason`).
            pause: {
                reason_label: 'Why is the case waiting?',
                reason_placeholder: 'Select a reason',
                reason_required: 'Choose why the case is waiting before moving it to a waiting status.',
                reasons: {
                    awaiting_subject: 'Waiting for the data subject',
                    awaiting_verification: 'Waiting for identity verification',
                    third_party_dependency: 'Waiting for a third party',
                    other: 'Other',
                },
            },
            // fix-sprint #431 — rejecting a case requires a reason code
            // (`rejection_reason_code`).
            rejection: {
                code_label: 'Rejection reason',
                code_placeholder: 'Select a reason',
                code_required: 'Choose a rejection reason before rejecting the case.',
                description_label: 'Rejection details (optional)',
                codes: {
                    identity_not_verified: 'Identity not verified',
                    manifestly_unfounded: 'Manifestly unfounded',
                    repetitive: 'Repeated request',
                    exempt: 'Exempt',
                    out_of_scope: 'Out of scope',
                    duplicate: 'Duplicate request',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — what the cross-border detector found when the case
            // was created (`cross_border_finding`). A record; it decides nothing.
            cross_border_finding: {
                title: 'Cross-border check at creation',
                result: 'Finding',
                evaluated_at: 'Checked at',
                triggered: 'The rule matched: the data subject’s country ({subject}) differs from the organisation’s ({org}).',
                reasons: {
                    request_type_not_disclosing: 'Not checked further: this request type discloses no data.',
                    subject_country_unknown: 'Not matched: the data subject’s country is not recorded.',
                    same_country: 'Not matched: the data subject is in the organisation’s country ({org}).',
                    // fix-sprint #431 — the organisation records no country: never assumed.
                    org_country_not_recorded: 'Not evaluated: the organisation’s country is not recorded.',
                    // fix-sprint #431 — a recorded country that reads as no code is not compared.
                    subject_country_not_recognised: 'Not evaluated: the data subject’s country, as recorded, is not a country code or name the platform recognises.',
                },
                other_reason: 'Not matched ({reason}).',
                org_country_pointer: 'The cross-border check compares the data subject’s country with the organisation’s. The organisation’s country is recorded on its record: Organizations › All Organizations, then the organisation’s Country field.',
                org_country_link: 'Record the organisation’s country',
                not_evaluated: 'Could not be evaluated when the case was created.',
                not_recorded: 'Not recorded: this case was created before the check ran on creation.',
                decides_nothing: 'This records what the rule found. It changes no status, deadline or assignment, and opens no cross-border dossier.',
                // fix-sprint #431 (owner ruling 2026-10-03) — viewing a case opens
                // nothing; a person opens the cross-border dossier with the button.
                current: 'Assessment now',
                dossier: 'Cross-border dossier',
                dossier_reading: 'Reading…',
                dossier_unreadable: 'Whether a dossier is open for this case could not be read.',
                no_dossier: 'No cross-border dossier has been opened for this case.',
                dossier_opened: 'Dossier {dossier}, opened {at}.',
                filing: 'Statutory filing',
                filing_no_form: 'None opened: no installed framework declares a filing form for this transfer.',
                filing_not_opened: 'None opened: the filing could not be created when the dossier was opened.',
                filing_not_recorded: 'None recorded.',
                open_button: 'Open cross-border dossier',
                open_hint: 'Opening the dossier is your action, recorded in the case audit log under your name. Where an installed framework declares a filing form for this transfer, the statutory filing is opened with it.',
                opening: 'Opening…',
                confirm_title: 'Open cross-border dossier?',
                confirm_message: 'This opens a cross-border dossier for case {case} and, where an installed framework declares a filing form for this transfer, a statutory filing. It is recorded under your name.',
                confirm_label: 'Open dossier',
                opened_toast: 'Cross-border dossier {dossier} opened.',
                already_open_toast: 'A cross-border dossier was already open for this case: {dossier}.',
                open_error: 'The cross-border dossier could not be opened.',
                // fix-sprint #431 (gate E12.5 remediation 1) — why opening the dossier was refused.
                not_detected: 'No cross-border transfer is detected for this case now, so no dossier was opened.',
                case_not_found: 'This case could not be found.',
                // gate E12.5 remediation 1 — retrying a filing that could not be opened; the subject country compared and its source.
                filing_opening: 'Being opened now.',
                retry_filing_button: 'Retry the statutory filing',
                retry_filing_hint: 'The statutory filing could not be opened with this dossier. Retrying it is your action, recorded in the case audit log under your name.',
                retry_filing_failed: 'The statutory filing still could not be opened.',
                filing_retried_toast: 'Statutory filing {filing} opened.',
                // Gate E12.5 remediation 2 — a stopped opening, and what a retry answered.
                filing_opening_stalled: 'None opened: the request that was opening it stopped before it finished.',
                retry_filing_in_progress: 'Another request is opening the statutory filing now, so this retry started nothing.',
                retry_filing_no_form: 'No statutory filing was opened: no installed framework declares a filing form for this transfer now.',
                // Gate E12.5 pass 3 — a filing retry that could not be attempted, and why.
                retry_filing_unavailable_service: 'This retry started nothing: statutory filings cannot be opened on this installation.',
                retry_filing_unavailable_org_country: 'This retry started nothing: the dossier records no organisation country, which the statutory filing needs.',
                confirm_retry_title: 'Retry the statutory filing?',
                confirm_retry_message: 'This tries again to open the statutory filing for dossier {dossier} of case {case}. It is recorded under your name.',
                confirm_retry_label: 'Retry filing',
                subject_compared: 'Data subject’s country compared',
                subject_compared_value: '{country}, from {source}',
                subject_sources: {
                    residence_country: 'the country of residence recorded with the request',
                    case_country: 'the case’s Country field',
                    address_country: 'the country in the data subject’s address',
                },
            },
            export_title: 'Exportar',
            export_pdf: 'Exportar PDF',
            export_zip: 'Exportar ZIP',
            case_file_pdf: 'Exporta PDF (Fitxer de cas)',
            evidence_pack_zip: 'Exporta ZIP (paquet de proves)',
            cross_border_warning: 'This request may involve a cross-border transfer. Review whether your transfer impact assessment covers it.',
            breach: {
                flag_btn: "Marca com a causant d'infraccions",
                modal: {
                    title: "Marca el cas com a causant d'incompliments",
                    type_label: "Tipus d'incompliment",
                    type: {
                        misdirected_response: 'Resposta mal dirigida',
                        unauthorised_disclosure: 'Divulgació no autoritzada',
                        identity_mismatch: "Desajust d'identitat",
                        other: 'Altres',
                    },
                    description: 'Descripció (opcional)',
                    description_placeholder: 'Què va passar? (màxim 1.000 caràcters)',
                    confirm: 'Marcar i iniciar el rellotge de notificació',
                    cancel: 'Cancel·la',
                    flagging: "S'està marcant...",
                },
                banner: {
                    title: 'Incompliment marcat: {type}',
                    detected_at: 'Detectat a les {timestamp}',
                    filing_deadline: "Termini de notificació: d'aquí a {hours} hores",
                    overdue: 'Termini de notificació: VENÇUT',
                },
                toasts: {
                    flagged: "Cas marcat com a causant d'incompliments: s'ha iniciat el rellotge de notificació",
                    flag_failed: "No s'ha pogut marcar el cas com a causant d'incompliments",
                },
            },
            export: {
                generating: "S'està generant... si us plau, espereu",
                success: 'Exportació desada al navegador de fitxers',
                failed: "Ha fallat l'exportació: comproveu el registre d'auditoria",
            },
            no_notes: 'Sense notes encara',
            assign_placeholder: "ID de l'usuari a assignar...",
            reassign: 'Reassignar',
            evidence: {
                checklist_title: "Llista de verificació d'evidències",
                complete_pct: '% completat',
                no_checklist: 'Sense elements de llista',
                attachments: 'Adjunts',
                upload_attachment: 'Pujar fitxer adjunt',
                upload_button: 'Pujar',
                uploading: 'Pujant...',
                attachment_uploaded: 'Fitxer adjunt pujat correctament',
                upload_error: 'Error en pujar el fitxer adjunt',
                category: 'Categoria',
                categories: {
                    identity_proof: "Prova d'identitat",
                    correspondence: 'Correspondència',
                    internal_data: 'Dades internes',
                    legal: 'Legal',
                    other: 'Altre',
                },
                no_attachments: 'Sense adjunts',
                references_title: "Referències d'evidències",
                no_references: 'Sense referències',
                add_reference: 'Afegir Reference',
                ref_type: 'Tipus',
                ref_value: 'Valor',
                ref_desc: 'Descripció',
                ref_value_placeholder: 'URL, ID de correu electrònic o referència...',
                ref_desc_placeholder: 'Descripció...',
                ref_types: {
                    url: 'URL',
                    email_id: 'Correu electrònic ID',
                    crm_record: 'Registre de CRM',
                    ticket_ref: 'Referència del ticket',
                    other: 'Altre',
                },
            },
            communications: {
                title: 'Comunicacions',
                add: 'Afegir Message',
                no_records: 'Sense comunicacions registrades',
                direction: 'Direcció',
                channel: 'Canal',
                visibility: 'Visibilitat',
                subject: 'Titular',
                body: 'Cos',
                send: 'Enviar',
                inbound: 'Entrada',
                outbound: 'Sortint',
                internal: 'Intern',
                external: 'Extern',
                tab_all: 'All',
                tab_internal: 'Interna',
                tab_external: 'Extern',
                subject_placeholder: 'Titular (optional)',
                body_placeholder: 'Cos del missatge...',
                channels: {
                    email: 'Correu electrònic',
                    portal: 'Portal',
                    phone: 'Telèfon',
                    postal: 'Correu postal',
                    in_person: 'En persona',
                },
                apply_template: 'Aplicar Plantilla',
                select_template: 'Seleccionar plantilla...',
                no_templates: 'No hi ha plantilles disponibles',
                templates_load_error: 'Templates could not be loaded',
                template_applied: 'Plantilla aplicada a la resposta.',
                template_apply_error: "No s'ha pogut aplicar la plantilla.",
            },
            escalations: {
                title: 'Escalament Historial',
                no_events: "No hi ha esdeveniments d'escalat",
                tier: 'Nivell',
                threshold: 'Llindar',
                action: 'Acció',
                target: 'Destinació',
                triggered_at: 'Activat el',
                // fix-sprint #431 (owner ruling 2026-10-03) — what a tier did. English until
                // Phase 25 (R-57).
                outcome: 'Outcome',
                outcome_acted: 'Acted',
                outcome_baselined: 'Passed before automatic escalation was on',
                // fix-sprint #431 — a tier recorded because a ladder edit added, switched on or lowered it.
                outcome_baselined_ladder_edit: 'Passed before this tier was added, switched on or lowered',
                outcome_baselined_detail: 'No one was notified and the case was not reassigned.',
            },
            cmp_sync: {
                title: 'Sincronització AesirX CMP',
                sync_status: 'Sincronitzar Estat',
                last_synced: 'Últim Sincronitzat',
                not_synced: 'No Sincronitzat',
                resync: 'Resincronitzar ara',
                syncing: 'Sincronitzant...',
                sync_history: 'Sincronitzar Historial (Last 10)',
                direction: 'Direcció',
                time: 'Hora',
                retries: 'Reintents',
                error_col: 'Error',
            },
            audit: {
                filter_event_placeholder: "Filtrar per tipus d'esdeveniment...",
                filter_actor_placeholder: 'Filtra per actor...',
            },
            fulfillment: {
                title: 'Compliment departamental',
                assign_departments: 'Assignar departaments',
                department: 'Departament',
                assignee: 'Responsable',
                deadline: 'Data límit',
                evidence_count: 'Evidència',
                signoff: 'Aprovació',
                signed_off: 'Aprovat',
                pending_signoff: 'Pendent',
                start_button: 'Iniciar',
                complete_button: 'Marcar com a completat',
                signoff_button: 'Tanqueu la sessió',
                no_tasks: "Encara no s'han assignat tasques de compliment.",
                tasks_table: 'Tasques de compliment',
                progress_label: 'Progrés de compliment',
                overdue: 'vençut',
                assigned_success: 'Tasca de compliment departamental assignada correctament.',
                assign_error: 'Error en assignar la tasca de compliment.',
                signoff_success: 'Aprovació departamental registrada.',
                signoff_error: "Error en registrar l'aprovació.",
                identity_required: 'Identity verification required before fulfillment actions.',
                remove_task: 'Remove Task',
                remove_success: 'Fulfillment task removed successfully.',
                add_evidence_btn: 'Afegir evidència',
                evidence_uploaded: 'Evidència pujada i vinculada',
                evidence_upload_error: "Error en pujar l'evidència",
                suggest_data_stores: 'Suggerir magatzems de dades',
                suggest_error: 'Error en carregar suggeriments de magatzem.',
                suggest_stores_title: 'Magatzems de dades suggerits',
                suggest_stores_none: 'No hi ha cap magatzem de dades que coincideixi amb aquest cas. Configureu sistemes a Data Mapping per millorar els suggeriments.',
                suggest_stores_add: 'Afegeix al cas',
                suggest_stores_added: 'Magatzem de dades afegit al cas',
                no_departments_title: "No s'ha configurat cap departament",
                no_departments_body: "Els departaments us permeten dirigir les tasques de compliment de DSR a una funció (privadesa, legal, recursos humans, operacions de TI). Instal·leu els departaments de mostra o demaneu al vostre administrador que configureu els departaments a la configuració de l'organització.",
                no_departments_cta: 'Instal·leu dades de mostra',
                department_placeholder: 'Nom del departament...',
                assignee_placeholder: "ID d'usuari a assignar...",
                assign_button: 'Assignar',
                status_pending: 'Pendent',
                status_in_progress: 'En curs',
                status_completed: 'Completat',
                status_verified: 'Verificat',
                confirm_approve: 'Esteu segur que voleu aprovar aquesta pròrroga?',
                confirm_deny: 'Esteu segur que voleu denegar aquesta pròrroga?',
            },
            response: {
                title: 'Generació de resposta',
                select_template: 'Plantilla de resposta',
                choose_template: 'Seleccioneu una plantilla...',
                generate: 'Generar resposta',
                preview: 'Previsualització de resposta',
                send: 'Enviar resposta',
                delivery_method: {
                    label: "Mètode d'entrega",
                    email: 'Correu electrònic',
                    portal: 'Portal',
                    postal: 'Correu postal',
                    in_person: 'En persona',
                },
                recipient: {
                    email: 'Correu electrònic del destinatari',
                    portal: "Identificador d'usuari del portal",
                    address: 'Adreça postal',
                },
                auto_generate_btn: 'Generar i enviar resposta',
                auto_generate_success: 'Resposta generada i lliurada',
                auto_generate_missing_recipient: "Proporcioneu un destinatari abans d'enviar.",
                auto_fields: 'Camps emplenats automàticament',
                case_id: 'ID del cas',
                requester_name: 'Nom del sol·licitant',
                rights_type: 'Tipus de dret',
                history: 'Respostes enviades',
                generated_at: 'Generat',
                confirmation: 'Confirmació',
                sent_label: 'Enviat',
                draft_label: 'Esborrany',
                generated_success: 'Resposta generada correctament.',
                generate_error: 'Error en generar la resposta.',
                sent_success: 'Resposta enviada correctament.',
                send_error: 'Error en enviar la resposta.',
                load_error: 'Error en carregar les plantilles de resposta.',
                pdf_title: "Carta de resposta a l'interessat",
                download_pdf: 'Descarregar PDF',
                download_all: 'Descarregar tot',
                download_error: 'Error en descarregar la resposta',
            },
            extension: {
                title: 'Extensions de termini',
                acknowledgment_deadline: "Termini d'acusament de recepció",
                fulfillment_deadline: 'Termini de compliment',
                request_extension: 'Sol·licitar extensió',
                reason: 'Motiu',
                reason_placeholder: 'Motiu per sol·licitar temps addicional...',
                additional_days: 'Dies addicionals',
                submit_request: "Enviar sol·licitud d'extensió",
                history: "Historial d'extensions",
                requested_at: 'Sol·licitat',
                approve: 'Aprovar',
                deny: 'Denegar',
                request_success: "Sol·licitud d'extensió enviada.",
                request_error: "Error en enviar la sol·licitud d'extensió.",
                approved_success: 'Extensió aprovada.',
                denied_success: 'Extensió denegada.',
                decision_error: "Error en processar la decisió d'extensió.",
                status_pending: 'Pendent',
                status_approved: 'Aprovat',
                status_denied: 'Denegat',
                confirm_approve: 'Esteu segur que voleu aprovar aquesta sol·licitud de pròrroga?',
                confirm_deny: 'Esteu segur que voleu denegar aquesta sol·licitud de pròrroga?',
                decided_by: 'Decidit per',
            },
            // fix-sprint #431 (gate E12.5 remediation 1) — the banner’s link to the check; why a status change was refused.
            cross_border_warning_link: 'See the cross-border check',
            status_errors: {
                invalid_transition: 'The case cannot move to this status from its current status.',
                invalid_sub_status: 'The chosen sub-status does not apply to this status.',
                assignment_required: 'Assign the case before moving it to In Review.',
                identity_verification_required: 'Verify the requester’s identity before fulfilling the request.',
                closure_delivery_required: 'Record how and when the response was delivered before closing the case.',
                invalid_value: 'The change was not saved: a value is missing or not accepted.',
            },
            evidence_unlink_read_failed: 'The attachment was deleted, but the fulfilment tasks could not be read, so it may still be listed as evidence on a task.',
            partial_load_part_names: {
                advisor_summary: 'advisor summary',
                sla_status: 'SLA status',
                attachments: 'attachments',
                fulfilment_tasks: 'fulfilment tasks',
                checklist: 'evidence checklist',
                references: 'references',
                escalations: 'escalations',
                cmp_sync_history: 'CMP sync history',
                flagged_breaches: 'flagged breaches',
                audit_trail: 'audit trail',
                communications: 'communications',
            },
            partial_load_reload: 'Reload the missing parts',
            request_type_not_granted: 'This kind of request is not a right under the frameworks this organisation has installed. The request is kept and can still be worked.',
        },
        audit: {
            export_csv: 'Exportar CSV',
            exporting: 'Exportant...',
            search_placeholder: "Cerca detalls d'auditoria...",
            filter_by_actor: 'Filtra per actor...',
            all_event_types: 'Tots Esdeveniment Types',
            seq: '#',
            timestamp: 'Marca de temps',
            event: 'Esdeveniment',
            case_col: 'Cas',
            actor: 'Actor',
            summary: 'Resum',
            no_events: "No s'han trobat audit events",
            export_success: "La pista d'auditoria s'ha exportat correctament.",
            export_error: 'Error en export audit csv. Si us plau, torneu-ho a intentar.',
            load_error: 'Error en load audit trail. Si us plau, torneu-ho a intentar.',
            announce_loaded: "Registre d'auditoria carregat",
            start_date: "Data d'inici",
            end_date: 'Data de fi',
            per_page: '{count} / pàgina',
            unknown: 'Desconegut',
            source_filter: {
                label: 'Font',
                all: 'Tots',
                module: 'Mòdul',
                forseti: 'Forseti',
                aria_label: "Filtreu els esdeveniments d'auditoria per font",
            },
            event_types: {
                case_created: 'Cas creat',
                status_changed: 'Estat canviat',
                case_assigned: 'Cas assignat',
                note_added: 'Nota afegida',
                attachment_added: 'Adjunt afegit',
                attachment_deleted: 'Adjunt Eliminat',
                reference_added: 'Referència afegida',
                checklist_updated: 'Llista de verificació actualitzada',
                identity_verified: 'Identitat Verificat',
                closure_summary_added: 'Resum de tancament',
                approval_granted: 'Aprovació concedida',
                template_applied: 'Plantilla aplicada',
                export_generated: 'Exportació generada',
                sla_warning: 'SLA Advertència',
                sla_overdue: 'SLA Vençut',
                sla_flag_revisited: 'SLA flag revisited',
                third_party_involvement_changed: 'Processor or third-party involvement changed',
                aml_diligence_linked: 'AML due diligence record linked',
                aml_diligence_confirmed: 'AML due diligence record confirmed',
                aml_scoping_recorded: 'AML reporting-entity scoping recorded',
                aml_scoping_deactivation_refused: 'AML programme-inactive role refused',
                aml_two_person_policy_changed: 'AML two-person rule changed',
                aml_retention_scope_recorded: 'AML records retention scope recorded',
                aml_retention_scope_updated: 'AML records retention scope updated',
                aml_indicator_row_updated: 'AML indicator coverage row updated',
                aml_indicator_entity_row_recorded: 'AML organisation-defined indicator recorded',
                aml_indicator_config_approved: 'AML indicator configuration approved',
                aml_case_opened: 'Suspicious-activity case opened',
                aml_case_updated: 'Suspicious-activity case edited',
                aml_case_viewed: 'Suspicious-activity case read',
                aml_case_listed: 'Suspicious-activity case list read',
                aml_case_read_denied: 'Suspicious-activity case read refused',
                aml_case_determined: 'Suspicious-activity case determination recorded',
                aml_case_approved: 'Suspicious-activity case determination approved',
                aml_case_self_approval_refused: 'Suspicious-activity case approval by the determiner refused',
                aml_case_approval_reverted: 'Suspicious-activity case approval taken back',
                aml_case_filing_recorded: 'Suspicious-activity case report reference recorded',
                aml_case_closed: 'Suspicious-activity case closed',
                aml_case_cdd_refresh_requested: 'Due diligence refresh asked for from a suspicious-activity case',
                aml_diligence_refresh_requested: 'AML due diligence refresh asked for',
                aml_beneficial_owner_recorded: 'Beneficial owners recorded',
                aml_beneficial_owner_updated: 'Beneficial owners updated',
                aml_launch_recorded: 'AML launch record created',
                aml_launch_scope_determined: 'AML launch scope determination recorded',
                aml_launch_assessment_recorded: 'AML pre-launch risk assessment recorded',
                aml_launch_assessment_decided: 'AML pre-launch risk assessment decision recorded',
                aml_launch_approved: 'AML launch approved',
                aml_launch_approval_refused: 'AML launch approval refused',
                aml_inspection_pack_exported: 'AML inspection pack exported',
                aml_indicator_config_self_approval_refused: 'AML indicator configuration approval by an editor of the draft refused',
                aml_indicator_review_task_raised: 'AML indicator review task raised from a launch assessment',
                aml_indicator_review_task_completed: 'AML indicator review recorded',
                aml_retention_scope_approved: 'AML records retention scope signed',
                aml_retention_scope_self_approval_refused: 'Second signature of an AML records retention scope entry by the same person refused',
                aml_case_related_action_recorded: 'Suspicious-activity case related action recorded',
                aml_case_retention_start_recorded: 'Suspicious-activity case retention start recorded',
                aml_launch_self_approval_refused: 'AML launch approval by the assessment decider refused',
                aml_launch_post_launch_review_set: 'AML launch post-launch review date recorded',
                aml_case_reads_retention_swept: 'Suspicious-activity case read log retention sweep',
                org_profile_statement_recorded: 'Organization profile statement recorded',
                dossier_authority_communication_recorded: 'Authority communication recorded on a filing',
                dossier_authority_communication_exported: 'Authority communications of a filing exported',
                framework_summary_exported: 'Framework summary exported',
                framework_briefing_recorded: 'Briefing or consultation comment recorded on a framework',
                framework_briefing_approved: 'Briefing on a framework approved',
                dossier_receipt_evidence_recorded: 'Receipt of a dossier recorded on a filing',
                dossier_handling_statement_recorded: 'Handling authority recorded on a filing',
                dossier_result_recorded: 'Authority’s result recorded on a filing',
                dossier_result_supplement_request_recorded: 'Supplement request after a result recorded on a filing',
                dossier_failure_follow_up_recorded: 'Follow-up to a result recorded on a filing',
                dossier_authority_stated_result_date_recorded: 'Result date stated by the authority recorded on a filing',
                dossier_completion_request_recorded: 'Completion request recorded on a filing',
                dossier_completion_submitted: 'Completion submitted on a filing',
                dossier_update_filing_created: 'Update filing created for a filing',
                dossier_update_addressee_recorded: 'Recipient and competent authority recorded on an update filing',
                regulatory_update_comparison_reviewed: 'Comparison review recorded on a regulatory update',
                access_statement_recorded: 'Access statement recorded on a processing activity',
            },
            unified_audit: {
                title: "Pista d'Auditoria Unificada",
                module_filter: 'Mòdul',
                framework_filter: 'Marc normatiu',
                select_framework: 'Seleccioneu un marc...',
                entity_type_filter: "Tipus d'entitat",
                action_filter: 'Acció',
                actor_filter: 'Actor',
                select_actor: 'Seleccioneu un actor...',
                search_filter: 'Cerca',
                search_placeholder: "Cercar esdeveniments d'auditoria...",
                filters_heading: 'Filtres',
                date_range: 'Rang de dates',
                no_events_found: "No s'han trobat esdeveniments d'auditoria.",
                event_count: "Nombre d'esdeveniments",
                action: 'Acció',
                actor: 'Actor',
                date_range_start: 'Inici del rang de dates',
                date_range_end: 'Fi del rang de dates',
                filtering_events: "Filtrant esdeveniments d'auditoria",
                framework_codes: 'Codis de marc',
                entity_type_col: "Tipus d'entitat",
                aria_table: "Esdeveniments d'auditoria unificats",
                page_of: 'Pàgina {page} de {total}',
                entity_types: {
                    user: 'Usuari',
                    case: 'Cas',
                    incident: 'Incident',
                    assessment: 'Avaluació',
                    vendor: 'Venedor',
                    consent: 'Consentiment',
                    policy: 'Política',
                    data_map: 'Mapa de dades',
                    approval: 'Approval',
                    consent_record: 'Consent record',
                    data_flow: 'Data flow',
                    org_profile_statement: 'Organization profile statement',
                    dpia: 'DPIA',
                    dpia_mitigation: 'DPIA mitigation',
                    evidence_item: 'Evidence item',
                    processing_activity: 'Processing activity',
                    transfer_mechanism: 'Transfer mechanism',
                    consent_policy: 'Consent policy',
                    data_category: 'Data category',
                    drift_event: 'Drift event',
                    maturity_model: 'Maturity model',
                    program: 'Monitoring program',
                    purpose: 'Processing purpose',
                    review_record: 'Review record',
                    risk_score: 'Risk score',
                    system: 'System',
                    taxonomy: 'Taxonomy',
                },
                actions: {
                    // Sprint 62.57.0 Slice E — the processing-service certificate register's audit events.
                    service_certificate_certificate_recorded: 'Processing service certificate recorded',
                    service_certificate_certificate_corrected: 'Processing service certificate corrected',
                    service_certificate_certificate_document_added: 'Document added to a processing service certificate',
                    service_certificate_certificate_revocation_recorded: 'Certificate revocation recorded',
                    service_certificate_certificate_return_recorded: 'Return of the certificate recorded',
                    service_certificate_certificate_publication_recorded: 'Publication of a revocation recorded',
                    service_certificate_certificate_communication_recorded: 'Authority communication recorded on a certificate',
                    service_certificate_application_created: 'Certificate application opened',
                    service_certificate_application_updated: 'Certificate application updated',
                    service_certificate_application_part_recorded: 'Dossier part recorded',
                    service_certificate_application_condition_links_recorded: 'Impact-assessment filings linked',
                    service_certificate_application_filed: 'Certificate application filing recorded',
                    service_certificate_application_receipt_recorded: 'Receipt of the application recorded',
                    service_certificate_application_communication_recorded: 'Authority communication recorded on an application',
                    service_certificate_application_supplement_request_recorded: 'Deficiency notice recorded',
                    service_certificate_application_supplement_sent: 'Supplement sent recorded',
                    service_certificate_application_evaluation_recorded: 'Evaluation of the application recorded',
                    service_certificate_application_decision_recorded: 'Decision on the application recorded',
                    service_certificate_application_withdrawn: 'Certificate application withdrawn',
                    service_certificate_register_exported: 'Certificate register exported',
                    form_dossier_statement_changed: 'Dossier statement changed',
                    create: 'Crear',
                    update: 'Actualització',
                    delete: 'Suprimeix',
                    view: 'Veure',
                    export: 'Exporta',
                    status_change: "Canvi d'estat",
                    // Sprint 63.25.0, gate E12.5 remediation 1 (P1-1) — the verbs producers write.
                    created: 'Created',
                    updated: 'Updated',
                    deleted: 'Deleted',
                    viewed: 'Viewed',
                    exported: 'Exported',
                    approved: 'Approved',
                    rejected: 'Rejected',
                    assigned: 'Assigned',
                    completed: 'Completed',
                    closed: 'Closed',
                    status_changed: 'Status changed',
                    submitted_for_review: 'Submitted for review',
                    risk_added: 'Risk added',
                    mitigation_added: 'Mitigation added',
                    evidence_linked: 'Evidence linked',
                    scored: 'Scored',
                    reviewed: 'Reviewed',
                    activated: 'Activated',
                    period_started: 'Period started',
                    version_created: 'Version created',
                    evaluated: 'Evaluated',
                    triage_completed: 'Triage completed',
                    impact_assessed: 'Impact assessed',
                    breach_assessed: 'Breach assessed',
                    case_created: 'Case created',
                    assignment_changed: 'Assignment changed',
                    case_closed: 'Case closed',
                    identity_verified: 'Identity verified',
                    maturity_model_created: 'Maturity model created',
                    maturity_model_activated: 'Maturity model activated',
                    maturity_model_evaluated: 'Maturity model evaluated',
                    maturity_model_approved: 'Maturity model approved',
                    response_generated: 'Response generated',
                    response_delivered: 'Response delivered',
                    assembled: 'Assembled',
                    reassembled: 'Reassembled',
                    verified: 'Verified',
                    returned: 'Returned',
                    recorded_as_submitted: 'Recorded as submitted',
                    audit_evidence_pack_assembled: 'Evidence pack assembled',
                    audit_evidence_pack_reassembled: 'Evidence pack reassembled',
                    audit_evidence_pack_partial: 'Evidence pack partial',
                    audit_evidence_pack_drift_checked: 'Evidence pack sources checked for changes',
                    audit_evidence_pack_verified: 'Evidence pack verified',
                    audit_evidence_pack_returned: 'Evidence pack returned',
                    audit_evidence_pack_recorded_as_submitted: 'Evidence pack recorded as submitted',
                    audit_evidence_pack_transition_refused: 'Evidence pack status change refused',
                    audit_evidence_pack_exported: 'Evidence pack exported',
                    // Sprint 63.37.0 — the form-instance events the sample packages write.
                    instance_created: 'Form created',
                    instance_updated: 'Form updated',
                    consent_captured: 'Consent captured',
                    revoked: 'Revoked',
                    mitigation_status_changed: 'Mitigation status changed',
                    ai_advisor_record_summary_fetched: 'Forseti read the record summary',
                    regulatory_guidance_installed: 'Regulatory guidance installed',
                    regulatory_guidance_version_bumped: 'Regulatory guidance moved to a new version',
                    unknown: 'Action not recorded',
                },
                additional_filters: 'Additional audit filters',
                sample_marker: 'Sample',
                sample_marker_label: 'Sample data',
                sample_package_label: 'Sample data from the industry package {package}',
                actor_system: 'The platform',
                entity_none: 'No record type',
                summary_composed: '{action} · {entity}',
                // Sprint 63.29.0 (#585, AC3/AC6) — the server export and the integrity check.
                export_title: 'Unified Audit Trail export',
                export_generated: 'Generated: {date}',
                export_filters: 'Filters: {filters}',
                export_no_filters: 'Filters: none',
                export_count: 'Events: {count}',
                export_source_unreadable: 'Source {source} ({collection}): could not be read; its events are not in this file.',
                list_sources_unreadable: 'These audit sources could not be read, so their events are neither listed nor counted: {sources}.',
                list_page_beyond_window: 'Pages beyond the first {max} events cannot be listed. Narrow the filters, or export the filtered set.',
                list_page_beyond_window_no_export: 'Pages beyond the first {max} events cannot be listed. Narrow the filters.',
                list_window_note: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters or export the filtered set.',
                list_window_note_no_export: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters.',
                export_col_entity_id: 'Record ID',
                export_col_actor_id: 'Actor ID',
                export_menu_label: 'Export',
                integrity: {
                    column: 'Integrity',
                    check: 'Check integrity',
                    check_aria: 'Check the integrity of the audit events of record {id}',
                    checking: 'Checking…',
                    heading: 'Integrity check: {type}',
                    heading_untyped: 'Integrity check',
                    record_ref: 'Record ID: {id}',
                    case_ref: 'Case ID: {case}',
                    chain_verified: 'Hash chain verified, {verified} of {events} events.',
                    chain_broken: '{verified} of {events} events verified before the break.',
                    chain_legacy_unverifiable: '{count} events were written before the current link rule; their content matches the stored checksum, but their order in the chain cannot be verified.',
                    no_chain: 'No hash chain names this record.',
                    break_checksum: 'Break found in case {case} at sequence {sequence}: the stored checksum does not match the one recomputed from the event before it.',
                    break_gap: 'Break found in case {case}: sequence {sequence} is missing from the chain.',
                    break_withheld: 'A break was found in the chain of case {case} at an event you are not permitted to read, so its position is not shown. The chain cannot be relied on; a holder of the restricted grant can see where it breaks.',
                    no_break: 'No break found.',
                    not_chained: '{source}: {events} events. Not chained; consistency checked only.',
                    not_chained_inconsistent: '{source}: {events} events. Not chained; consistency checked only. {inconsistent} carry an unreadable or future date (first: {at}).',
                    proofs_link: 'Proofs and anchors are verified in the Integrity Center.',
                    failed: 'The integrity check could not be run.',
                    close: 'Close the integrity result',
                    // Gate E12.5 pass 1 (P2-3) — a source the check could not read.
                    unreadable: 'The check could not read {sources}; the result does not cover the events held there.',
                },
            },
            evidence_packs: {
                title: "Paquets d'Evidència d'Auditoria",
                assemble_pack: "Muntar Paquet d'Evidència",
                framework: 'Marc normatiu',
                select_framework: 'Seleccioneu un marc...',
                framework_code_label: 'Codi del marc',
                entity_types: "Tipus d'entitats",
                select_entity_types: "Seleccioneu tipus d'entitats...",
                period: 'Període',
                period_start: 'Inici del període',
                period_end: 'Fi del període',
                completeness: 'Completesa',
                no_packs_found: "No s'han trobat paquets d'evidència.",
                verify: 'Verificar',
                export_pack: 'Exportar',
                exporting: 'Exportant...',
                pack_assembled: 'Paquet de proves reunit',
                pack_exported: 'Paquet de proves exportat',
                aria_table: "Paquets de proves d'auditoria",
                aria_export_pack: 'Exportar paquet',
                export_format: "Format d'exportació",
                export_in_progress: 'Exportació en curs...',
                status_draft: 'Esborrany',
                status_assembled: 'Muntat',
                status_verified: 'Verificat',
                status_submitted: 'Enviat',
                entity_type_labels: {
                    user: 'Usuari',
                    case: 'Cas',
                    incident: 'Incident',
                    assessment: 'Avaluació',
                    vendor: 'Venedor',
                    consent: 'Consentiment',
                    policy: 'Política',
                    data_map: 'Mapa de dades',
                },
                template_id: 'ID de plantilla',
                csv_headers: {
                    // Sprint 63.29.0 (#542) — the actor's name from the user directory, beside the id.
                    actor_name: 'Actor Name',
                    module: 'Mòdul',
                    action: 'Acció',
                    actor_id: 'Actor',
                    actor_role: 'Actor Role',
                    entity_type: "Tipus d'entitat",
                    entity_id: "ID de l'entitat",
                    summary: 'Resum',
                    timestamp: 'Marca de temps',
                    record_type: 'Record Type',
                    framework_code: 'Marc',
                },
                // Sprint 63.25.0 — versioned packs: statuses, record types and the export header (spec G1, G2).
                status_partial: 'Partial',
                status_returned: 'Returned',
                status_recorded_as_submitted: 'Recorded as submitted',
                status_legacy_unversioned: 'Assembled before versioning',
                record_type_labels: {
                    consent: 'Consent',
                    data_map: 'Data Map',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    incident: 'Incident',
                    case: 'Case',
                    policy: 'Policy',
                    deletion: 'Deletion',
                    monitoring: 'Monitoring',
                    forms: 'Forms',
                    task: 'Task',
                    access: 'Access',
                    user: 'User',
                    platform: 'Platform',
                    evidence_pack_lifecycle: 'Evidence pack lifecycle',
                },
                framework_none: 'no framework filter',
                export_header_pack: 'Pack {pack_id} — version {version}',
                export_header_name: 'Name: {name}',
                export_header_manifest: 'Manifest SHA-256: {sha}',
                export_header_period_tz: 'Period: {start} to {end} ({timezone})',
                export_header_untagged_included: 'Events not tagged to a framework: included',
                export_header_untagged_excluded: 'Events not tagged to a framework: not included',
                export_header_record_types: 'Record types: {types}',
                export_header_modules: 'Modules: {names}',
                export_header_all_modules: 'Modules: all',
                export_header_counts: 'Events: {total} ({tagged} tagged to a framework, {untagged} untagged)',
                export_header_coverage: 'Record-type coverage: {covered} of {denominator} selected record types have at least one event',
                export_header_coverage_missing: 'Selected record types with no event: {types}',
                export_header_unresolved: 'References not resolved: {count}',
                export_header_unresolved_unchecked: 'References not resolved: not checked',
                export_header_review: 'Review: {decision} by {reviewer} on {date}',
                export_header_review_note: 'Review note: {note}',
                export_header_not_reviewed: 'Review: not reviewed',
                export_header_submission: 'Recorded as submitted: to {recipient} on {sent_on} via {channel}, reference {reference}',
                export_header_submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                export_header_no_submission: 'Submission: none recorded',
                export_header_source_collected: 'Source {source} ({collection}): {count} events collected',
                export_header_source_failed: 'Source {source} ({collection}): could not be read ({error})',
                export_header_sources_not_read: 'Sources not read: {count} (not selected)',
                export_header_excluded_by_rule: 'Excluded by rule: record-summary read events ({count})',
                export_header_excluded_by_rule_uncounted: 'Excluded by rule: record-summary read events (not counted)',
                export_header_excluded_by_rule_not_applied: 'Excluded by rule: record-summary read events (rule not applied: this version was assembled before the rule existed)',
                value_none: 'none',
                // Gate E12.5 remediation 1 (P1-2): an event whose producer wrote no sentence.
                summary_composed: '{action} · {record_type}',
                export_doc_title: 'Evidence Pack {pack_id}',
                export_header_framework: "Paquet d'evidència — Marc: {framework}",
                export_header_period: 'Període: {start} a {end}',
                export_header_completeness: 'Completesa: {score}%',
                export_header_status: 'Estat: {status}',
                col_entity_type: 'Entity Type',
                events_collected: 'events collected',
                missing_label: 'Missing',
                no_events_in_pack: 'No audit events found for this evidence pack.',
                // Sprint 63.25.0 (Slices C3, C4, F, H) — the versioned audit evidence pack page.
                page_description: 'Collect the audit events recorded in a period into a versioned dossier. Each version is a snapshot: its count, its event list, its coverage and every export read the same events.',
                cross_ref_audit: "Audit evidence packs collect the audit events recorded in a period; framework evidence packs gather one framework's evidence items for a regulator.",
                cross_ref_open_framework: 'Go to framework evidence packs',
                cross_ref_framework: "Framework evidence packs gather one framework's evidence items for a regulator; audit evidence packs collect the audit events recorded in a period.",
                cross_ref_open_audit: 'Go to audit evidence packs',
                filter_status: 'Status',
                filter_all_statuses: 'All statuses',
                col_name: 'Name',
                col_events: 'Events',
                col_version: 'Version',
                col_coverage: 'Record-type coverage',
                col_assembled: 'Assembled',
                coverage_short: '{covered} of {denominator}',
                not_a_member_count: 'Not a list of events',
                show_details: 'Show details for {name}',
                list_load_failed: 'The evidence pack list could not be read.',
                list_retry_subject: 'the evidence pack list',
                list_pagination: 'Pages of evidence packs',
                field_name: 'Name',
                name_hint: 'A name people will recognise, for example "Q3 inspection".',
                framework_hint: 'Optional. Only frameworks installed for your organisation are listed.',
                no_framework_option: 'No framework filter',
                include_untagged: 'Include events not tagged to any framework',
                include_untagged_hint: 'Most audit events carry no framework tag. Leave this ticked unless you want only the events tagged to the chosen framework. The pack shows how many of each it holds either way.',
                include_untagged_no_effect: 'With no framework chosen, every event in the period is included and this box has no effect.',
                period_hint: "Dates are read in your organisation's timezone, and the end date is included in full.",
                record_types_legend: 'Record types',
                record_types_hint: 'Choose at least one.',
                modules_legend: 'Modules',
                modules_hint: 'Optional. Leave every box unticked to include all modules.',
                error_name_required: 'Enter a name.',
                error_period_start_required: 'Choose a start date.',
                error_period_end_required: 'Choose an end date.',
                error_period_order: 'The end date must be on or after the start date.',
                error_record_types_required: 'Choose at least one record type.',
                assembling: 'Assembling…',
                detail_loading: 'Loading the pack…',
                detail_load_failed: 'This pack could not be read.',
                detail_retry_subject: 'this evidence pack',
                scope_heading: 'Scope and counts',
                meta_period: 'Period',
                meta_untagged: 'Events not tagged to a framework',
                meta_record_types: 'Record types',
                meta_modules: 'Modules',
                meta_counts: 'Events',
                meta_manifest: 'Manifest SHA-256',
                period_value: '{start} to {end} ({timezone})',
                untagged_included: 'Included',
                untagged_excluded: 'Not included',
                all_modules: 'All modules',
                counts_value: '{total} ({tagged} tagged to a framework, {untagged} untagged)',
                not_checked: 'Not checked',
                legacy_notice: 'This pack was assembled before versioning. Its stored count is not a list of events, so it cannot be reviewed or exported. Re-assemble it to create a reviewable version.',
                version_select: 'Version shown',
                version_option: 'Version {version}',
                reassemble: 'Re-assemble',
                reassembling: 'Re-assembling…',
                reassembled: 'Version {version} assembled.',
                drift_check: 'Check for changes since assembly',
                drift_checking: 'Checking…',
                drift_none: 'Nothing has changed since version {version} was assembled.',
                drift_new: '{count} events recorded in this period since assembly. Re-assemble to include them.',
                drift_gone: '{count} events in this version no longer exist at their source.',
                drift_changed: '{count} events in this version have changed at their source.',
                drift_failed_sources: 'Sources that could not be read during the check: {sources}.',
                drift_failed: 'The check for changes could not be run.',
                coverage_heading: 'Record-type coverage',
                coverage_sentence: '{covered} of {denominator} selected record types have at least one event.',
                coverage_missing: 'No event for: {types}.',
                coverage_not: 'This shows which selected record types have events. It does not measure whether the evidence is sufficient, or whether the organisation complies with any law.',
                history_heading: 'Version history',
                col_assembled_by: 'Assembled by',
                col_assembled_at: 'Assembled on',
                col_reviewer: 'Reviewer',
                col_decision: 'Decision',
                col_note: 'Note',
                col_reviewed_at: 'Reviewed on',
                col_submission: 'Recorded as submitted',
                decision_verified: 'Verified',
                decision_returned: 'Returned',
                not_reviewed: 'Not reviewed',
                no_submission: 'None recorded',
                submission_line: 'To {recipient} on {sent_on} via {channel}',
                items_heading: 'Events in version {version}',
                items_loading: 'Loading the events of this version…',
                items_empty_scope: 'It was assembled for {period}; framework: {framework}; record types: {types}; modules: {module_list}.',
                items_denied: 'You do not have permission to read the events of this pack. Reading them needs the "Read audit events" permission (audit:events:read).',
                items_failed: 'The events of this version could not be read.',
                items_retry_subject: 'the events of this version',
                items_populated: '{count} events in this version.',
                partial_banner: 'Some sources could not be read when this version was assembled, so it is partial and cannot be verified. Sources not read: {sources}.',
                partial_reassemble_hint: 'A person with permission to export audit events can re-assemble it.',
                partial_no_members: 'No events were collected from the sources that answered.',
                source_item: '{source} ({collection})',
                col_record_type: 'Record type',
                col_actor: 'Actor',
                col_timestamp: 'Time',
                col_module: 'Module',
                col_action: 'Action',
                col_summary: 'Summary',
                items_pagination: 'Pages of events',
                review: 'Review',
                review_title: 'Review version {version}',
                review_decision: 'Decision',
                review_option_verified: 'Verify this version',
                review_option_returned: 'Return it to the person who assembled it',
                review_note: 'Note',
                review_note_hint: 'Required when you return a version: say what needs to change.',
                error_decision_required: 'Choose a decision.',
                error_note_required: 'Enter a note saying why the version is returned.',
                review_submit: 'Record decision',
                review_recorded: 'Your decision on version {version} was recorded.',
                review_own_version: 'You assembled this version, so another person must review it.',
                record_submission: 'Record as submitted',
                submission_title: 'Record version {version} as submitted',
                submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                submission_recipient: 'Recipient',
                submission_sent_on: 'Sent on',
                submission_channel: 'Channel',
                submission_channel_hint: 'How it was sent, for example by email or by hand.',
                submission_reference: 'Reference',
                submission_reference_hint: 'Optional. For example a letter or ticket number.',
                submission_proof: 'Proof of sending',
                submission_proof_hint: 'Optional. A file showing the pack was sent, such as a dispatch receipt.',
                error_recipient_required: 'Enter the recipient.',
                error_sent_on_required: 'Choose the date it was sent.',
                error_channel_required: 'Enter the channel.',
                submission_recorded: 'Version {version} was recorded as submitted.',
                export_version: 'Export version {version}',
                // Sprint 63.25.0 (spec E3) — references that did not resolve at assembly.
                reference_not_found: 'Reference not found',
                reference_not_found_record: 'The source event names no record.',
                reference_not_found_actor: 'The person who acted is not in the user directory.',
                unresolved_line: 'References not resolved: {count}',
                unresolved_not_checked: 'References not resolved: not checked',
                unresolved_breakdown: '{records} with no record named; {actors} whose actor is not in the user directory.',
                unresolved_breakdown_actors_unchecked: '{records} with no record named; actors not checked.',
                server_error_reviewer_is_assembler: 'You assembled this version, so you cannot review it. Another person with the review permission must record the decision.',
                server_error_evidence_pack_version_not_found: 'This version of the pack was not found. The pack may have been re-assembled or removed.',
                server_error_submission_proof_not_owned: 'The proof file is not held under this version. Upload it to this version first.',
                server_error_evidence_pack_legacy: 'This pack was assembled before versioning. Re-assemble it to create a version that can be checked.',
                server_error_evidence_pack_too_large: 'More events match this scope than one pack can hold. Choose a shorter period or a narrower filter.',
                server_error_evidence_pack_definition_invalid: "This pack's stored definition cannot be assembled again. Create a new pack.",
            },
            audit_periods: {
                title: "Períodes d'Auditoria",
                create_period: "Crear Període d'Auditoria",
                framework_code_label: 'Codi del marc',
                select_framework: 'Seleccioneu un marc...',
                period_label: 'Etiqueta de període',
                period_start: 'Inici del període',
                period_end: 'Fi del període',
                start: 'Comença',
                end: 'Final',
                framework: 'Marc',
                retention_days: 'Dies de retenció',
                section_period_dates: 'Dates del període',
                no_periods_found: "No s'han trobat períodes d'auditoria.",
                period_created: "Període d'auditoria creat",
                aria_table: "Períodes d'auditoria",
                suggested_boundaries: 'Límits suggerits',
                suggested_boundaries_info: "Els límits de període suggerits es deriven del cicle d'informes reguladors de cada marc. Ajusteu segons les necessitats de la vostra organització.",
                reporting_cycle: "Cicle d'informes",
                suggested_start: 'Inici suggerit',
                suggested_end: 'Final suggerit',
                suggested_next: 'Següent període suggerit',
                cycle_monthly: 'Mensualment',
                cycle_quarterly: 'Trimestral',
                cycle_semi_annual: 'Semestral',
                cycle_annual: 'Anual',
                cycle_custom: 'Personalitzat',
                period_deleted: "S'ha suprimit el període d'auditoria",
                list_truncated: 'Only {shown} of {total} audit periods could be read, so this list is incomplete.',
            },
        },
        create_case: {
            customer_ref: 'Referència del client',
            country: 'País / Jurisdicció',
            preferred_channel: 'Canal preferit',
            channels: {
                email: 'Correu electrònic',
                postal: 'Correu postal',
                phone: 'Telèfon',
                portal: 'Portal',
            },
            source: 'Font',
            sources: {
                admin_manual: "Manual d'administració",
                public_form: 'Forma pública',
                cmp_gateway: 'CMP Gateway',
                aesirx_cmp: 'AesirX CMP',
                phone_received: 'Telèfon rebut',
                postal_received: 'Correu rebut',
                email_received: 'Correu electrònic rebut',
                in_person: 'En persona',
                legacy_import: 'Importació heretada',
            },
            required_fields: 'El tipus de sol·licitud, el nom i el correu electrònic són obligatoris',
            create_error: 'Error en create case. Si us plau, torneu-ho a intentar.',
            // fix-sprint #447 W9 (owner ruling OR3)
            third_party_involved: 'A processor or third party must act on this request',
            third_party_involved_hint: 'The installed deadline records may state a different period when a processor or third party must act. The case is dated by the record that matches. It can be changed on the case later, which dates the case again from receipt.',
            request_type_not_offered: 'This kind of request is not a right under the frameworks your organisation has installed, so it cannot be recorded as a new request.',
            request_type_no_installed_clock: 'No installed framework states a period for this kind of request. The case will be dated from your SLA target for it or, failing that, the platform default, which is not a legal period.',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            request_type_handled_in_legal_matters: 'This kind of request is received as a legal matter in Legal Matters. No deadline is set for it.',
            legal_matter_right: 'Which right it raises',
            legal_matter_right_optional_hint: 'Optional. If it is not yet known, leave it empty: a person records it on the legal matter.',
            legal_matter_right_not_known: 'Not yet known',
        },
        settings: {
            title: 'Configuració',
            request_types: 'Tipus de sol·licitud',
            request_types_desc: "Configureu quins tipus de sol·licitud estan habilitats per al formulari públic d'admissió.",
            notifications: 'Notificacions',
            ticket_numbering: 'Numeració de tiquets',
            ticket_format: 'Format actual',
            ticket_prefix: 'Prefix',
            ticket_prefix_note: 'no configurable',
            sla_deadlines: 'Terminis SLA',
            sla_desc: "Establiu el temps màxim de resposta (en dies naturals) per a cada tipus de sol·licitud. Els casos que s'apropin al llindar mostraran avisos.",
            days: 'dies',
            warning_threshold: "Llindar d'avís",
            escalation_label: "Habilitar l'escalat automàtic en cas d'incompliment de l'SLA",
            // fix-sprint #431 F1 — English until Phase 25 (R-57).
            escalation_hint: 'Runs the escalation ladder below. When off, no tier acts; warnings and breaches are still flagged.',
            warning_threshold_hint: 'A case no installed framework dates shows a warning at this share of its period and is flagged critical at 90%, or at this share if it is higher. A case an installed framework dates is warned at 75% and flagged critical at 90%.',
            // Gate E12.5 remediation 1 (fix-sprint #431) — escalation settings need sla:config:write.
            escalation_settings_need_write: 'Changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            // Gate E12.5 remediation 2 — neither sla:config:write nor case:update: nothing here can be saved.
            sla_settings_read_only: 'You can view these settings but not change them: saving them needs the SLA configuration permission, or the case update permission for the deadlines alone.',
            sla_save_forbidden: 'The SLA settings were not saved: changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            sla_save_invalid: 'The SLA settings were not saved: a deadline is set for something that is not a request type, or a value is out of range.',
            save_sla: 'Desar configuració SLA',
            saving: 'Desant...',
            saved: 'Desat correctament',
            announce_loaded: 'Configuració carregada',
            email_on_creation: 'Notificació per correu electrònic en crear un tiquet',
            send_confirmation: 'Enviar correu de confirmació al sol·licitant',
            users: 'Usuaris',
            roles: 'Rols',
            api_keys: 'API Claus',
            mfa: 'MFA',
            sessions: 'Sessions',
            webhooks: 'Webhooks',
            modules: 'Mòduls',
            // Sprint 62.45.0 (AC17) — the organisation calendar section.
            calendar: {
                heading: 'Working days and holidays',
                effect_notice: 'Every working-day deadline in the platform counts only the working days below, skips these holidays and counts these make-up working days — rights requests and their pauses, incidents, obligations, enforcement limitation periods, dossier filings and supervisory reports among them. A deadline worked out after you save uses the new calendar; a due date already recorded on a record keeps its date.',
                no_list_notice: "The platform supplies no public-holiday list: enter your organisation's own days off, public holidays included.",
                working_days_legend: 'Working days of the week',
                working_days_hint: 'Working-day deadlines count only these days.',
                working_days_required: 'Select at least one working day.',
                day_0: 'Sunday',
                day_1: 'Monday',
                day_2: 'Tuesday',
                day_3: 'Wednesday',
                day_4: 'Thursday',
                day_5: 'Friday',
                day_6: 'Saturday',
                holidays_heading: 'Holidays',
                holidays_caption: 'Holidays entered: {count}',
                empty: 'No holidays entered: working-day deadlines skip only the days outside the working week.',
                add_legend: 'Add a holiday',
                date_label: 'Date',
                label_label: 'Label (optional)',
                label_hint: 'A short name for the day, for example why the office is closed.',
                add: 'Add date',
                remove: 'Remove',
                remove_aria: 'Remove {date}',
                col_date: 'Date',
                col_label: 'Label',
                col_actions: 'Actions',
                no_label: 'No label',
                date_required: 'Enter a date.',
                date_out_of_range: 'Enter a date between {min} and {max}.',
                duplicate: '{date} is already in the list.',
                limit_reached: 'At most {max} holidays can be entered. Remove one before adding another.',
                save: 'Save calendar',
                saved: 'Calendar saved. Working-day deadlines worked out from now on count these working days, skip these holidays and count these make-up working days.',
                save_error: 'The calendar was not saved. Your entries are still here.',
                conflict: 'Someone else saved the calendar after you opened it, so your changes were not saved. Reload it to see their version, then enter your changes again.',
                reload: 'Reload calendar',
                load_error: 'The calendar could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                last_saved: 'Last saved {date}.',
                never_saved: 'This calendar has not been saved here yet.',
                read_only: 'You can view this calendar but not change it.',
                unsaved: 'Unsaved changes.',
                holiday_is_extra: '{date} is entered as a make-up working day. A date is either a holiday or a make-up working day.',
                extra_heading: 'Make-up working days',
                extra_intro: 'A day your organisation works although it falls outside the working week, for example a Saturday worked in exchange for a day off beside a public holiday. Every working-day deadline counts it as a working day.',
                extra_caption: 'Make-up working days entered: {count}',
                extra_empty: 'No make-up working days entered.',
                extra_add_legend: 'Add a make-up working day',
                extra_date_label: 'Date worked',
                extra_label_label: 'Label for the day worked (optional)',
                extra_label_hint: 'A short name for the day, for example the day off it makes up for.',
                extra_add: 'Add day worked',
                extra_remove_aria: 'Remove make-up working day {date}',
                extra_is_holiday: '{date} is entered as a holiday. A date is either a holiday or a make-up working day.',
                extra_limit_reached: 'At most {max} make-up working days can be entered. Remove one before adding another.',
                extra_is_working_day: '{date} is a {weekday}, which is already a working day. A make-up working day is a date outside the working days of the week.',
                extra_on_working_weekday: 'These make-up working days fall on a working day of the week, so they would change nothing: {dates}. Remove them, or remove that day from the working days.',
            },
            // fix-sprint #431 F1 — Settings › Escalation ladder. English until Phase 25 (R-57).
            escalation_ladder: {
                heading: 'Escalation ladder',
                intro: "When a rights request reaches a tier's share of its period, the background SLA check acts on it once: it notifies the case's assignee and the people and role the tier names, in the platform or by email, as far as each person's own notification preferences allow. A reassign tier also moves the case. The share is the time from receipt of the request to the due date the case shows. Paused cases and cases with no due date are not escalated.",
                off_notice: 'Automatic escalation is off, so no tier acts. Warnings and breaches are still flagged. Turn it on under SLA Deadlines.',
                on_notice: 'Automatic escalation is on. The background SLA check runs these tiers.',
                suggested_notice: 'Your organisation has not saved a ladder, so these suggested tiers run. Save them, or change them and save, to make them your own.',
                caption: 'Escalation tiers: {count}',
                col_threshold: 'Share of period',
                col_name: 'Name',
                col_action: 'Action',
                col_targets: 'Notifies',
                col_channels: 'Sent by',
                col_actions: 'Actions',
                action_notify: 'Notify',
                action_escalate_to_role: 'Escalate to a role',
                action_reassign: 'Reassign',
                channel_in_app: 'In the platform',
                channel_email: 'Email',
                target_assignee: 'The assignee',
                target_role: 'Holders of {role}',
                target_users: 'Named: {names}',
                reassign_to: 'Moves the case to: {target}',
                threshold_breach: '100% (due date passed)',
                inactive: 'Inactive',
                edit: 'Edit',
                edit_aria: 'Edit the tier at {pct}%',
                remove: 'Remove',
                remove_aria: 'Remove the tier at {pct}%',
                add: 'Add tier',
                add_legend: 'Add a tier',
                edit_legend: 'Edit the tier at {pct}%',
                threshold_label: 'Share of the period elapsed (%)',
                threshold_hint: 'A whole number from 1 to 100. At 100 the tier acts once the due date has passed.',
                name_label: 'Name (optional)',
                name_hint: "Shown in notifications and in the case's escalation history. Left blank, the tier is named by its share.",
                action_label: 'Action',
                action_hint: 'Every tier notifies the assignee and the people it names. Escalate to a role also notifies everyone holding the role. Reassign moves the case to the first active person named, or else to the first person holding the role, unless the case is already with one of them.',
                role_label: 'Role',
                role_none: 'No role',
                users_label: 'Named people',
                users_hint: 'For a reassign tier, the case goes to the first active person in this list.',
                user_select_label: 'Person to add',
                user_placeholder: 'Choose a person',
                user_add: 'Add person',
                user_remove_aria: 'Remove {name}',
                users_empty: 'No one named.',
                users_retry_subject: 'the list of people',
                channels_legend: 'Send by',
                channels_hint: 'Each person still receives only what their own notification preferences allow.',
                active_label: 'Active',
                apply: 'Apply to the ladder',
                cancel: 'Cancel',
                save: 'Save escalation ladder',
                saved: 'Escalation ladder saved. The background SLA check runs it from its next sweep.',
                unsaved: 'Unsaved changes.',
                save_error: 'The escalation ladder was not saved. Your changes are kept.',
                conflict: 'Someone else saved the escalation ladder after you opened this page, so your changes were not saved. Reload the ladder to see that version, then make your changes again.',
                reload: 'Reload ladder',
                load_error: 'The escalation ladder could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                read_only: 'You can view the escalation ladder but not change it.',
                empty: 'No tiers. Add at least one tier, or turn automatic escalation off under SLA Deadlines.',
                last_saved: 'Last saved {date}.',
                err_ladder_empty: 'Add at least one tier. To stop automatic escalation, turn it off under SLA Deadlines.',
                err_too_many_tiers: 'A ladder holds at most {max} tiers.',
                err_invalid_value: 'Tier {n}: a value is not valid.',
                err_threshold_out_of_range: 'Tier {n}: the share must be a whole number from 1 to 100.',
                err_threshold_duplicate: 'Tier {n}: another tier already acts at this share.',
                err_tier_id_duplicate: 'Tier {n}: this tier appears twice.',
                err_channels_required: 'Tier {n}: choose at least one way to send.',
                err_target_role_required: 'Tier {n}: choose the role to escalate to.',
                err_target_role_unknown: 'Tier {n}: the role is not an active role of your organisation.',
                err_reassign_target_required: 'Tier {n}: name a person or a role to reassign the case to.',
                err_target_user_unknown: 'Tier {n}: {name} is not an active user of your organisation.',
            },
            deadline_labels: {
                access: 'Accés (DSAR)',
                deletion: 'Supressió',
                correction: 'Rectificació',
                portability: 'Portabilitat',
                objection: 'Oposició',
                restriction: 'Limitació',
                consent_withdrawal: 'Retirada del consentiment',
                protective_measures: 'Mesures de protecció',
                processing_information: 'Information about Processing',
                consent_decision: 'Consent Decision',
                complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
            },
            sla_source_platform_default: 'Not saved: cases of this type that no installed clock dates are dated from the platform default of {days} days, which is not a legal period. Save a value to set your own target.',
            sla_source_tenant_target: 'Your SLA target: it dates cases of this type that no installed clock in force dates.',
            request_type_enabled_not_offered: 'Enabled, but not a right under the frameworks this organisation has installed: the public form does not offer it. Untick it to clear the setting.',
            request_types_from_frameworks: 'The request types listed are the rights the installed frameworks grant.',
        },
        section_assignments: {
            title: 'Assignacions de secció',
            assign_section: 'Assignar secció',
            section_ref: 'Referència de secció',
            section_label: 'Etiqueta de secció',
            department_id: 'Departament',
            assignee_id: 'Responsable',
            mandatory: 'Obligatori',
            assign: 'Assignar',
            no_assignments: 'Cap assignació de secció',
            status: 'Estat',
            completion: 'Completat',
            mark_complete: 'Completar',
        },
        consolidation: {
            title: 'Progrés de consolidació',
            add_child: 'Afegir subtasca',
            child_task_id: 'ID de subtasca',
            department_id: 'Departament',
            link: 'Enllaç',
            overall_completion: 'Completat global',
            departments_complete: 'Departaments completats',
            overdue: 'Vençut',
            ready: 'Llest',
            not_ready: 'No llest',
            master_status: 'Estat general',
            department_breakdown: 'Desglos per departament',
            department: 'Departament',
            status: 'Estat',
            completion: 'Completat',
            due_date: 'Data de venciment',
            overdue_flag: 'Vençut',
            child_tasks: 'Subtasques',
            task: 'Tasca',
        },
        conflict_detection: {
            title: 'Detecció de conflictes',
            unresolved: 'sense resoldre',
            no_conflicts: "No s'han detectat conflictes",
            section: 'Secció',
            field: 'Camp',
            dept_a: 'Departament A',
            value_a: 'Valor A',
            dept_b: 'Departament B',
            value_b: 'Valor B',
            severity: 'Gravetat',
            status: 'Estat',
            resolved: 'Resolt',
            open: 'Obert',
            resolve: 'Resoldre',
        },
        org_maturity: {
            title: "Maduresa de l'Organització",
            departments: 'Departaments',
            department_hierarchy: 'Jerarquia de Departaments',
            create_department: 'Crear Departament',
            department_name: 'Nom del Departament',
            department_code: 'Codi',
            level: 'Nivell',
            division: 'Divisió',
            department: 'Departament',
            team: 'Equip',
            parent_department: 'Departament Superior',
            head_user: 'Cap de Departament',
            no_departments: 'No hi ha departaments configurats.',
            user_mappings: "Assignacions d'Usuaris",
            map_user: 'Assignar Usuari',
            user_id: "ID d'Usuari",
            role: 'Rol',
            no_users: 'No hi ha usuaris assignats a aquest departament.',
            remove: 'Eliminar',
            framework_assignments: 'Assignacions de Marcs',
            assign_framework: 'Assignar Marc',
            entity_id: "ID d'Entitat",
            entity_type: "Tipus d'Entitat",
            framework_id: 'ID del Marc',
            framework_code: 'Codi del Marc',
            no_assignments: 'No hi ha assignacions de marcs.',
            compliance_rollup: 'Resum de Compliment',
            entity_name: 'Entitat',
            total_assessments: 'Avaluacions Totals',
            completed: 'Completat',
            completion_pct: 'Completat %',
            open_gaps: 'Bretxes Obertes',
            critical_gaps: 'Crítiques',
            compliance_score: 'Puntuació',
            no_rollup: 'No hi ha dades de resum de compliment disponibles.',
            entity_summary: "Resum de Compliment de l'Entitat",
            frameworks_assigned: 'Marcs',
            overall_score: 'Puntuació Global',
            load_departments_error: 'Error en carregar els departaments',
            load_users_error: 'Error en carregar els usuaris',
            department_created: 'Departament creat',
            department_created_announce: 'Departament creat',
            create_department_error: 'Error en crear el departament',
            user_mapped: 'Usuari assignat',
            user_mapped_announce: 'Usuari assignat al departament',
            map_user_error: 'Error en assignar usuari',
            user_removed: 'Usuari eliminat',
            load_rollup_error: 'Error en carregar el resum',
            load_assignments_error: 'Error en carregar les assignacions',
            framework_assigned: 'Marc assignat',
            framework_assigned_announce: "Marc assignat a l'entitat",
            assign_framework_error: 'Error en assignar el marc normatiu',
            assignment_removed: 'Assignació eliminada',
            enter_entity_id_placeholder: "Introduïu l'ID de l'entitat per veure les assignacions",
            entity_type_organization: 'Organització',
            entity_type_department: 'Departament',
        },
        onboarding_maturity: {
            title: "Maduresa d'incorporació",
            quick_starts: 'Inicis ràpids',
            progress: "Progrés d'incorporació",
            personas: 'Camins de persona',
            persona_onboarding: 'Incorporació de Persona',
            quick_start_scenarios: "Escenaris d'inici ràpid",
            start: 'Iniciar',
            restart: 'Reinicieu',
            estimated_minutes: 'Minuts estimats',
            steps: 'Passos',
            scenario: 'Escenari',
            no_scenarios: "No hi ha escenaris d'inici ràpid disponibles.",
            persona_id: 'Identificació de la persona',
            persona_label: 'Persona',
            role_description: 'Descripció del rol',
            primary_modules: 'Mòduls de primària',
            recommended_tours: 'Tours recomanats',
            sample_data_modules: 'Mòduls de dades de mostra',
            quick_start_scenario: "Escenari d'inici ràpid",
            no_personas: 'No hi ha cap camí personal disponible.',
            framework_setup: 'Marcs reguladors',
            framework_code: 'Codi Marc',
            framework_name: 'Nom del marc',
            related_modules: 'Mòduls Relacionats',
            persona_relevance: 'Personas rellevants',
            no_frameworks: 'No hi ha opcions de configuració del marc disponibles.',
            tier_config: 'Configuració del mòdul de nivell',
            tier: 'Nivell',
            available_modules: 'Mòduls disponibles',
            module_count: 'Recompte de mòduls',
            overall_completion: 'Finalització global',
            wizard_completed: 'Assistent completat',
            tours_completed: 'Tours completats',
            quick_starts_completed: 'Inicis ràpids completats',
            selected_frameworks: 'Marcs seleccionats',
            no_progress: "No hi ha dades de progrés d'incorporació disponibles.",
            detected_persona: 'Persona detectada',
            current_tier: 'Nivell actual',
            completion_pct: 'Completat',
            progress_page: {
                loaded: 'Progrés carregat',
                no_progress: "No hi ha dades de progrés d'incorporació disponibles.",
                overall: 'Progrés general',
                detected_persona: 'Persona detectada',
                tier: 'Nivell',
                modules: 'mòduls',
                wizard: 'Assistent',
                completed: 'Completat',
                pending: 'Pendent',
                tours: 'Visites guiades',
                quick_starts: 'Inicis ràpids',
                frameworks: 'Marcs seleccionats',
                no_frameworks: 'Cap marc seleccionat.',
                go_to_wizard: "Completar l'assistent de configuració",
                framework_selection: 'Selecció de marc',
                framework_toggle: 'Habilitar o deshabilitar marcs per al vostre programa de compliment.',
                framework_updated: 'Selecció de marc actualitzada.',
                view_tours: 'Veure visites guiades',
                view_quick_starts: 'Veure inicis ràpids',
                // English until Phase 25 (R-57).
                // fix-sprint #447 W5 — no progress record yet; reading the page creates none.
                not_started: 'Not started',
                not_started_body: 'No onboarding progress record exists for you yet. Opening this page does not create one: it is created when you complete the Setup Wizard. The figures below count the tours and quick starts you have already completed.',
            },
            persona_page: {
                description: "Seleccioneu una persona per veure mòduls, visites guiades i escenaris d'inici ràpid recomanats.",
                no_personas: 'No hi ha rutes de persona disponibles.',
                modules: 'Mòduls',
                tours: 'Visites guiades',
                quick_start: 'Inici ràpid',
                details: 'Detalls',
                primary_modules: 'Mòduls principals',
                recommended_tours: 'Visites guiades recomanades',
                framework_options: 'Marcs reguladors',
                no_frameworks: 'No hi ha opcions de configuració del marc disponibles.',
                no_frameworks_explain: 'Encara no hi ha cap marc regulador instal·lat per a la vostra organització, així que no hi ha res a configurar aquí.',
                no_frameworks_action: 'Instal·leu un paquet de mercat per afegir els marcs que us són aplicables',
                start_tour: 'Comença',
                no_tours: 'No hi ha visites guiades disponibles.',
                help_center: "Centre d'ajuda",
                no_articles: "No s'ha trobat cap article.",
                primary: 'Principal',
                framework_code: 'Codi del marc',
                framework_label: 'Etiqueta',
                region: 'Regió',
                category: 'Categoria',
                enabled: 'Habilitat',
                disabled: 'Deshabilitat',
                tier_modules: 'Configuració de mòduls per nivell',
                tiers_loaded: 'Configuracions de nivell carregades',
                no_tier_config: 'No hi ha configuració de nivell disponible.',
                view_tours: 'Veure visites de mòduls',
                view_quick_start: "Iniciar l'inici ràpid",
                toggle_framework: 'Commutar marc',
                framework_updated: 'Selecció de marc actualitzada.',
                tier_desc_light: 'Fonaments de privacitat — bases de compliment PDPL',
                tier_desc_standard: 'Suite de compliment completa — tots els mòduls amb fluxos de treball',
                tier_desc_enterprise: 'Governança avançada i automatització — multi-entitat, informes de grup',
                persona_highlight: 'Els vostres mòduls',
                aria_go_to_module: '{name} — aneu al mòdul',
                aria_start_tour: 'Comença {title}',
                aria_quick_start: 'Inici ràpid: {title}',
                aria_help_article: "Article d'ajuda: {title}",
                aria_view_framework: '{label}{primary} — visualitza els detalls del marc',
                aria_primary_for_persona: '{name} (principal per a aquesta persona)',
                primary_for_persona_suffix: '(principal per a aquesta persona)',
                aria_view_in_persona_paths: '{name} — visualitza a Persona Paths',
                progress_of_total: '{completed} de {total} completat',
            },
            // Sprint 63.28.0 — Persona Paths revised. English copy until the R-57 deferral (sprint 57.7.0).
            persona_paths: {
                page_description: 'Find your role among the personas your organisation can use. The roles your installed frameworks name come first. Choose one to see the frameworks that name it, the registers it works in and its quick start.',
                recommended_heading: 'Recommended for you',
                your_persona: 'Your persona',
                source_chosen: 'Chosen by you',
                source_detected: 'Detected automatically',
                source_detected_role: 'Detected from your user role',
                source_detected_default: 'Suggested as a starting point, because nothing else matched',
                chosen_unavailable: 'The persona you chose is no longer available to your organisation, because the market pack it needs was removed. The detected persona is shown instead.',
                recommended_none_ranked: 'No installed framework names a role yet, so only your persona is shown.',
                recommended_failed: 'Your persona could not be read.',
                recommended_loading: 'Reading your persona…',
                recommended_ranked: 'Named by your installed frameworks',
                toolbar_label: 'Filter roles',
                search_label: 'Search roles',
                search_placeholder: 'Role, description, sector, framework name or ID',
                family_label: 'Role family',
                family_all: 'All',
                sector_label: 'Sector',
                sector_placeholder: 'All sectors',
                market_label: 'Market',
                market_all: 'All markets',
                named_toggle: 'Named by my installed frameworks',
                sort_label: 'Sort by',
                sort_relevance: 'Relevance',
                sort_name: 'Name',
                sort_id: 'Persona ID',
                showing: 'Showing {shown} of {total} roles',
                clear_filters: 'Clear filters',
                list_label: 'Roles by family',
                list_heading: 'All roles',
                list_heading_named: 'Roles named by your installed frameworks',
                group_count_one: '1 role',
                group_count: '{count} roles',
                relevance_named_one: 'Named by 1 installed framework',
                relevance_named: 'Named by {n} installed frameworks',
                relevance_legacy_only: 'Only legacy or draft frameworks installed',
                selected: 'selected',
                load_failed: 'The list of roles could not be loaded.',
                reload: 'Reload',
                none_available_title: 'No roles are available to your organisation',
                none_available_description: 'Install a market pack to make its roles available.',
                no_match_title: 'No role matches these filters',
                no_match_description: 'Change or clear the filters to see more roles.',
                no_match_outside_one: '1 role matches outside your installed frameworks.',
                no_match_outside: '{count} roles match outside your installed frameworks.',
                hidden_matches_one: '1 more matches outside your installed frameworks.',
                hidden_matches: '{count} more match outside your installed frameworks.',
                show_all: 'Show all',
                notice_unavailable: '{name} ({id}) needs a market pack for {market}, which your organisation has not installed.',
                notice_unknown: 'No persona has the ID "{id}", so nothing was opened.',
                notice_unknown_use_case: 'No use case has the ID "{id}", so nothing was opened.',
                detail_label: 'Role details',
                detail_back: 'Back to the list of roles',
                detail_loading: 'Loading this role…',
                detail_failed: 'The details of this role could not be loaded.',
                detail_market: 'Market',
                detail_sectors: 'Sectors',
                detail_family: 'Role family',
                detail_persona_id: 'Persona ID',
                frameworks_heading: 'Frameworks for this role',
                frameworks_description: 'Read from the regulatory packs: every framework that names this role, as primary or supporting.',
                frameworks_installed: 'Installed',
                frameworks_not_installed: 'Not installed',
                frameworks_not_installed_count: 'Not installed ({count})',
                frameworks_none_installed: 'None of your installed frameworks names this role.',
                frameworks_only_retired_installed: 'Only retired or draft instruments are installed.',
                frameworks_none_not_installed: 'Every framework that names this role is installed.',
                frameworks_retired: 'Retired and draft instruments ({count})',
                role_primary: 'Primary',
                role_supporting: 'Supporting',
                status_active: 'Active',
                status_upcoming: 'Upcoming, in force from {date}',
                status_upcoming_undated: 'Upcoming',
                status_legacy: 'Legacy',
                status_draft: 'Draft',
                status_unknown: 'Status not stated',
                status_temporary_active: 'Temporarily in force',
                find_in_catalog: 'Find in the Market Packs Catalog',
                find_in_catalog_aria: 'Find in the Market Packs Catalog: {name}',
                basis_label: 'Basis: {article}',
                basis_link_aria: 'Open {article} of {name} in the Source tab',
                attached_through: 'Attached through {parent}',
                second_read_pending: 'Second reading pending',
                basis_missing: 'No article recorded',
                destinations_heading: 'Where the work happens',
                destinations_description: 'The registers this role works in, each marked by whether your tier includes it.',
                in_tier: 'In your tier',
                not_in_tier: 'Not in your tier',
                tier_checking: 'Checking your tier…',
                tier_unchecked: 'Your tier could not be checked, so no register is marked.',
                tier_not_permitted: 'Your user role cannot see which modules your tier includes, so no register is marked.',
                destination_no_access: 'Your user role cannot open this register',
                quick_start_heading: 'Your quick start',
                quick_start_none: 'No quick start has been written for this role yet.',
                quick_start_not_listed: 'The quick start "{id}" is not in your list, so nothing is highlighted.',
                tours_heading: 'Guided tours',
                tour_title: '{name} tour',
                start_tour: 'Start',
                start_tour_aria: 'Start: {title}',
                starting: 'Starting…',
                no_tours: 'No tours for this role.',
                help_heading: 'Help articles',
                help_none: "No help articles for this role's modules.",
                use_cases_heading: 'Use cases',
                use_cases_none: 'No validated use case names this role as its primary persona.',
                about_link: 'About this persona',
                save_action: 'Use as my onboarding persona',
                save_action_aria: 'Use as my onboarding persona: {name}',
                saving: 'Saving…',
                save_success: '{name} is now your onboarding persona.',
                already_yours: 'This is your onboarding persona.',
                profile_label: 'Onboarding persona',
                change_in_persona_paths: 'Change it in Persona Paths',
            },
            // Sprint 63.28.0 (B8) — the shared persona select. English copy until the R-57 deferral (sprint 57.7.0).
            persona_select: {
                placeholder: 'Choose a persona',
                no_options: 'No persona matches',
                checking: 'Checking which personas your organisation can use…',
                availability_failed: 'Which personas your organisation can use could not be checked, so every persona is listed.',
                retry: 'Try again',
            },
            quick_starts_page: {
                description: "Practiqueu fluxos de treball habituals amb escenaris d'inici ràpid guiats.",
                no_scenarios: "No hi ha escenaris d'inici ràpid disponibles.",
                started: 'Inici ràpid iniciat',
                status_completed: 'Completat',
                status_in_progress: 'En curs',
                status_not_started: 'No iniciat',
                minutes: 'min',
                steps: 'passos',
                step: 'Pas',
                restart: 'Reiniciar',
                start: 'Iniciar',
                go_to_module: 'Anar al mòdul',
                next_step: 'Següent pas',
                complete: 'Completar',
                reset: 'Restablir',
                step_of: 'de',
                action_navigate: 'Navegar',
                action_form_fill: 'Emplenar formulari',
                action_review: 'Revisar',
                action_confirm: 'Confirmar',
                step_changed: 'Pas {current} de {total}: {title}',
                completed_message: 'Inici ràpid completat amb èxit!',
                reset_message: "L'inici ràpid s'ha restablert.",
            },
            persona_p_vn_01_label: 'Delegat de protecció de dades empresarial',
            persona_p_vn_01_description: 'Data Protection Officer running the enterprise-wide personal data protection programme: impact assessments and their filings, rights requests, breach decisions and liaison with the authority',
            persona_p_vn_02_label: 'Responsable de departament',
            persona_p_vn_02_description: 'Cap de departament responsable de l’informe de compliment de la seva àrea',
            persona_p_vn_03_label: 'Compliment de comerç electrònic',
            persona_p_vn_03_description: 'Compliance manager at an e-commerce platform owning its registration and change notifications, the registration dossier, and the seller and traceability information it supplies when an authority requests it',
            persona_p_vn_04_label: 'Telecom Regulatory Affairs Manager',
            persona_p_vn_04_description: 'Regulatory affairs lead at a telecom operator handling authority requests for subscriber information and disclosure governance',
            persona_p_vn_05_label: 'Responsable de governança de dades',
            persona_p_vn_05_description: 'Lead owning data mapping, classification and the annual governance attestation, and the data-service provider notification and reports where they apply',
            persona_p_vn_06_label: 'Operacions de ciberseguretat i jurídiques',
            persona_p_vn_06_description: 'Cybersecurity lead owning incident reporting decisions, authority requests and directions, disclosure logging, and data localisation and local presence readiness',
            persona_p_vn_07_label: 'Responsable de governança d’IA',
            persona_p_vn_07_description: 'Responsable de la supervisió de la governança dels sistemes d’IA, l’avaluació de riscos i la classificació',
            persona_p_vn_08_label: 'Governança transfronterera i de proveïdors',
            persona_p_vn_08_description: 'Lead owning cross-border personal data transfer impact assessments and their filings, recipient and vendor safeguards, and data localisation checks',
            persona_p_vn_09_label: 'Responsable de resposta a violacions',
            persona_p_vn_09_description: 'Responsable de coordinar la notificació de violacions i la resposta a incidents',
            persona_p_vn_10_label: 'Responsable d’auditoria interna',
            persona_p_vn_10_description: 'Lead running internal audits and regulator inspections across frameworks: evidence completeness, gaps, audit packs and sign-off',
            persona_p_vn_11_label: 'Responsable de dades de RRHH i empleats',
            persona_p_vn_11_description: 'HR manager handling employee consent records, electronic labour contracts and internal DSARs',
            persona_p_vn_12_label: 'Responsable de compliment de filial multinacional',
            persona_p_vn_12_description: "DPO en filial vietnamita d'empresa estrangera que necessita capa de compliment local",
            persona_p_vn_13_label: 'Responsable de compliment educatiu',
            persona_p_vn_13_description: "Responsable de compliment en escola estrangera que gestiona protecció de dades d'estudiants i pares",
            qs_hr_consent_title: "Registrar consentiments d'empleats",
            qs_hr_consent_description: "Recorregut del registre de consentiment d'empleats i generació d'informe de cobertura.",
            qs_subsidiary_dpia_title: 'Crear avaluació AIPD de filial',
            qs_subsidiary_dpia_description: "Recorregut de la creació d'una AIPD per a una activitat de tractament de filial.",
            qs_student_consent_title: 'Configurar recollida de consentiment estudiantil',
            qs_student_consent_description: "Recorregut de la creació de polítiques de consentiment parental per a dades d'estudiants.",
            persona_p_vn_14_label: 'Assessor jurídic extern',
            persona_p_vn_14_description: 'Soci sènior de despatx d’advocats que assessora múltiples organitzacions client en el compliment de la PDPL',
            persona_p_vn_15_label: 'Responsable d’operacions del despatx',
            persona_p_vn_15_description: 'Coordinador de la relació amb el client que gestiona el seguiment de lliuraments i la coordinació de terminis',
            persona_p_vn_16_label: 'Propietari de l’empresa / CEO',
            persona_p_vn_16_description: 'Director general o gerent que és legalment el responsable del tractament segons la PDPL i que ret comptes de la inversió en compliment',
            qs_dpia_filing_title: 'Completeu la vostra primera presentació d’AIPD',
            qs_dept_compliance_title: 'Envieu el primer informe de compliment del vostre departament',
            qs_ecommerce_consent_title: 'Prepare your e-commerce platform registration dossier',
            qs_telecom_data_title: 'Configureu el tractament de dades d’abonats de telecomunicacions',
            qs_data_flow_title: 'Mapegeu el vostre primer flux de dades',
            qs_incident_response_title: 'Configureu el flux de treball de resposta a incidents',
            qs_ai_risk_title: 'Executeu una avaluació de risc d’un sistema d’IA',
            qs_cross_border_title: 'Avalueu una transferència transfronterera de dades',
            qs_breach_notification_title: 'Simuleu un flux de treball de notificació de violació',
            qs_audit_trail_title: 'Reviseu una pista d’auditoria de compliment',
            qs_legal_advisor_portfolio_title: 'Reviseu la cartera de compliment multiclient',
            qs_legal_advisor_portfolio_description: 'Reviseu l’estat del compliment a diverses organitzacions client.',
            qs_law_firm_ops_title: 'Feu un seguiment de l’estat dels lliuraments dels clients',
            qs_law_firm_ops_description: 'Superviseu el progrés dels lliuraments d’assessorament i de la relació amb els clients.',
            qs_ceo_dashboard_title: 'Visualitzeu el tauler executiu de compliment',
            qs_ceo_dashboard_description: 'Reviseu l’estat de compliment d’alt nivell i els indicadors d’inversió.',
        },
        rights_requests: {
            response_templates: {
                title: 'Plantilles de resposta',
                subtitle: 'Gestioneu les plantilles de carta de resposta de DSR que ResponseGenerator utilitza per redactar respostes als interessats.',
                create_btn: 'Nova plantilla',
                edit_btn: 'Edita la plantilla',
                filter_right_type: 'Filtra per tipus correcte',
                filter_locale: 'Filtra per entorn local',
                filter_status: 'Filtra per estat',
                all_right_types: 'Tots els tipus correctes',
                any_right_type: 'Qualsevol (aplica a tots els tipus)',
                all_locales: 'Totes les localitats',
                all_statuses: 'Tots',
                status_active: 'Actius',
                status_inactive: 'Inactiu',
                empty_state: 'No hi ha cap plantilla que coincideixi amb els filtres actuals.',
                columns: {
                    template_id: 'ID de plantilla',
                    outcome: 'Resultat',
                    locale: 'Localització',
                    right_type: 'Tipus correcte',
                    is_active: 'Estat',
                    last_updated: 'Última actualització',
                    actions: 'Accions',
                },
                actions: {
                    edit: 'Edita',
                    clone: 'Clonar',
                    deactivate: 'Desactivar',
                    reactivate: 'Reactivar',
                    preview: 'Vista prèvia',
                },
                form: {
                    template_id: 'ID de plantilla',
                    template_id_help: 'Identificador en minúscula, p. dsr_access_fulfilled_vi',
                    outcome: 'Resultat',
                    locale: 'Localització',
                    right_type: 'Tipus correcte',
                    subject: 'Assumpte',
                    body: 'Plantilla del cos',
                    body_help: 'Utilitzeu marcadors de posició com {case_reference}, {data_subject_name}, {fulfilled_at}. Mínim 15 línies per a cartes de diverses seccions.',
                    is_active: 'Actius',
                    official_form_id: 'Identificació oficial del formulari (opcional)',
                },
                outcomes: {
                    fulfilled: 'Complert',
                    partially_fulfilled: 'Parcialment complert',
                    denied: 'Negat',
                },
                clone_modal_title: 'Plantilla de clonació',
                clone_target_locale: 'Localització objectiu',
                clone_help: 'Es crearà una plantilla nova a la configuració regional de destinació utilitzant aquesta plantilla com a punt de partida.',
                preview_modal_title: 'Vista prèvia de la plantilla',
                preview_note: 'La previsualització utilitza marcadors de posició de casos de demostració DSR-DEMO-0020. El lliurament real substitueix les dades dels casos en directe.',
                preview_unavailable: "La vista prèvia requereix un cas existent. Adjunteu aquesta plantilla a un cas i utilitzeu l'acció Esborrany de resposta a la pàgina de detalls del cas.",
                deactivate_confirm: 'Vols desactivar aquesta plantilla? Continuarà aplicant-se als casos existents que hi fan referència.',
                toasts: {
                    created: 'Plantilla creada',
                    updated: 'Plantilla actualitzada',
                    deactivated: 'Plantilla desactivada',
                    reactivated: 'Plantilla reactivada',
                    cloned: 'Plantilla clonada',
                    load_error: "No s'han pogut carregar les plantilles",
                    save_error: "No s'ha pogut desar la plantilla",
                    clone_error: "No s'ha pogut clonar la plantilla",
                },
            },
        },
        organizations: {
            departments: {
                title: 'Departaments',
                subtitle: 'Gestioneu els departaments que encaminen els casos de DSR, les tasques DPIA i els fluxos de treball de compliment.',
                create_btn: 'Nou Departament',
                edit_btn: "Departament d'edició",
                columns: {
                    name: 'Nom',
                    description: 'Descripció',
                    active_cases: 'Casos actius',
                    members: 'Membres',
                    status: 'Estat',
                    actions: 'Accions',
                },
                status_active: 'Actius',
                status_inactive: 'Inactiu',
                form: {
                    name: 'Nom',
                    description: 'Descripció',
                },
                actions: {
                    edit: 'Edita',
                    deactivate: 'Desactivar',
                    reactivate: 'Reactivar',
                    delete: 'Suprimeix',
                    manage_members: 'Gestionar els membres',
                },
                delete_modal_title: 'Esborra departament',
                delete_warning: 'Aquest departament encara té casos actius. Trieu un departament per reassignar-los abans de suprimir-los.',
                delete_no_cases: 'Aquest departament no té casos actius i es pot suprimir de manera segura.',
                reassign_prompt: 'Reassignar casos actius a:',
                reassign_none_available: 'No hi ha cap altre departament actiu disponible. Creeu primer un altre departament.',
                member_section_title: 'Membres',
                add_member: 'Afegeix un membre',
                remove_member: 'Eliminar',
                member_role: 'Rol',
                no_members: 'No hi ha cap membre associat a aquest departament.',
                toasts: {
                    created: 'Departament creat',
                    updated: 'Departament actualitzat',
                    deactivated: 'Departament desactivat',
                    reactivated: 'Departament reactivat',
                    deleted: 'Departament suprimit',
                    reassigned_count: "S'han reassignat {count} casos",
                    member_added: 'Membre afegit',
                    member_removed: 'Membre eliminat',
                    load_error: "No s'han pogut carregar els departaments",
                    save_error: "No s'ha pogut desar el departament",
                    delete_error: "No s'ha pogut suprimir el departament",
                },
            },
            retention_policies: {
                title: 'Polítiques de retenció',
                subtitle: "Configura finestres de retenció per tipus d'objectiu. Els documents de verificació d'identitat l'utilitzen per estampar retention_expires_at en futures càrregues.",
                create_btn: 'Nova política',
                edit_btn: 'Edita la política',
                columns: {
                    target_type: "Tipus d'objectiu",
                    retention_days: 'Retenció (dies)',
                    owner: 'Propietari de la política',
                    effective_from: 'Eficaç Des',
                    last_reviewed: 'Darrera revisió',
                    status: 'Estat',
                    actions: 'Accions',
                },
                status_active: 'Actius',
                status_inactive: 'Inactiu',
                form: {
                    target_type: "Tipus d'objectiu",
                    target_type_help: "A quina categoria de dades s'aplica la finestra de retenció. Actualment només s'admeten els documents de verificació d'identitat DSR.",
                    retention_days: 'Període de retenció (dies)',
                    retention_days_help: 'Entre 1 i 3650 dies (màxim 10 anys).',
                    owner: 'Propietari de la política',
                    owner_help: 'Normalment el DPO. Responsable de la decisió de retenció segons DG-2.',
                    effective_from: 'Eficaç Des',
                },
                target_types: {
                    dsr_identity_verification_document: "Documents de verificació d'identitat del DSR",
                },
                deactivate_notice: 'La desactivació no afecta els fitxers adjunts ja estampats; només les càrregues futures ja no rebran una finestra de retenció.',
                deactivate_confirm: 'Vols desactivar aquesta política de retenció?',
                toasts: {
                    created: "S'ha creat una política de retenció",
                    updated: 'Política de retenció actualitzada',
                    deactivated: 'Política de retenció desactivada',
                    load_error: "No s'han pogut carregar les polítiques de retenció",
                    save_error: "No s'ha pogut desar la política de retenció",
                },
            },
        },
    },
    data_mapping: {
        // Sprint 62.58.0 (Slice N) — English for key parity until Phase 25 (R-57).
        deployment_privacy: {
            audience_kinds: {
                public: 'The public',
                data_subject_group: 'Named data subjects',
            },
            channels: {
                website: 'Website',
                point_of_collection: 'At the point of collection',
                employment_documentation: 'Employment documentation',
                contract_or_terms: 'Contract or terms',
                in_app: 'In the app',
                email: 'Email',
                other: 'Other',
            },
            access_kinds: {
                view: 'View',
                extract: 'Extract',
                export: 'Export',
                delete: 'Delete',
                configure: 'Configure',
            },
            export: {
                coverage_heading: 'Privacy notices by data subject',
                coverage_withheld: 'Notice details are shown only to a reader who may read the privacy notice register.',
                col_activity: 'Processing activity',
                col_data_subject: 'Data subject',
                col_notice: 'Notice',
                col_revision: 'Revision',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_status: 'Status',
                not_recorded: 'Not recorded',
                no_current_notice: 'No current notice: every notice that names this data subject is withdrawn, superseded or replaced by a newer revision',
                status_replaced: '{status}, replaced by a newer revision',
                not_stated: 'Not stated',
                access_heading: 'Who may access the data',
                col_version: 'Version',
                col_reviewed_on: 'Reviewed on',
                col_role_or_position: 'Role or position',
                col_access_kinds: 'Permitted actions',
                col_means: 'By what means',
                col_conditions: 'On what conditions',
                no_access_statement: 'No access statement recorded',
                no_data_subjects: 'No data subjects recorded',
                public_linked: 'Also linked: {count} public notices, which name no data subject',
                public_linked_one: 'Also linked: 1 public notice, which names no data subject',
                coverage_truncated: 'More notices are linked to some processing activities than this export lists, so for those activities the notices are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. The processing activities concerned: {activities}. A data subject reads "Not recorded" only when no linked notice names it.',
                access_withheld: 'Access statements are shown only to a reader who may read processing activities.',
                col_approved_by: 'Approved by',
                col_systems: 'Systems',
            },
            notice_form: {
                heading: 'Audience, channels and deployment',
                intro: 'Say whom this revision is given to, how they are given it, from which day, and the processing activities and systems it is given for.',
                audience_label: 'Whom the notice is given to',
                audience_public_hint: 'Anyone can read it, for example on your website. A public notice names no data subject, so the notice coverage of a processing activity does not list it against any data subject.',
                audience_group_hint: 'Named data subjects of the linked processing activities, such as drivers, passengers or staff.',
                audience_group_need_grant: 'Naming data subjects needs the permission to read processing activities, which your role does not hold: the data subjects are chosen from the linked processing activities. The notice can be given to the public.',
                data_subjects_label: 'Data subjects the notice is given to',
                data_subjects_hint: 'Chosen from the data subjects recorded on the linked processing activities.',
                data_subjects_need_activity: 'Link at least one processing activity first: the data subjects are chosen from those it records.',
                data_subjects_none: 'The linked processing activities record no data subjects.',
                data_subjects_required: 'Choose at least one data subject.',
                data_subjects_unknown: '{value} (not recorded on the linked processing activities)',
                channels_label: 'How the audience is given the notice',
                channels_hint: 'Choose at least one. A sticker or a screen where the data is collected, such as inside a vehicle, is "At the point of collection".',
                channels_required: 'Choose at least one channel.',
                channel_note_label: 'Describe the other channel',
                channel_note_required: 'Describe the other channel.',
                effective_from_label: 'In effect from',
                effective_from_hint: 'The day the notice takes effect, as your organization states it. Leave it empty if the day is not yet known.',
                activities_label: 'Processing activities the notice is given for',
                activities_hint: 'The data subjects of these activities are the ones the notice can name.',
                systems_label: 'Data-map systems the notice covers',
                links_need_grant: 'Linking a notice to processing activities or systems needs the permission to read processing activities, which your role does not hold. The notice can be drafted without links.',
                systems_need_grant: 'Choosing data-map systems needs the permission to read systems, which your role does not hold.',
                picker_loading: 'Loading the list…',
                activities_empty: 'No processing activity is recorded.',
                systems_empty: 'No data-map system is recorded.',
                activities_failed: 'The processing activities could not be read.',
                systems_failed: 'The data-map systems could not be read.',
                activities_manual_label: 'Processing activity IDs',
                systems_manual_label: 'Data-map system IDs',
                manual_hint: 'Enter the IDs separated by commas; they are checked when you save.',
                off_list: '{value} (not in the list read)',
                retry: 'Reload',
                reloading: 'Reloading…',
                activities_retry_subject: 'the processing activities',
                systems_retry_subject: 'the data-map systems',
                error_link_grant: 'Your role cannot link a notice to processing activities or systems. Remove the links, or ask for the permission to read processing activities.',
                error_link_not_found: 'A linked processing activity or system was not found in this organization. What you entered is kept.',
                error_audience_invalid: 'A chosen data subject is not recorded on the linked processing activities. What you entered is kept.',
                col_audience: 'Audience',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_links: 'Linked to',
                links_activities: 'Processing activities: {names}',
                links_activities_count: '{count} processing activities',
                links_activities_count_one: '1 processing activity',
                links_systems: 'Systems: {names}',
                links_systems_count: '{count} systems',
                links_systems_count_one: '1 system',
                links_none: 'None',
                other_channel: 'Other: {note}',
                data_subjects_unconfirmed: '{value} (not confirmed: a linked processing activity could not be read)',
                error_systems_grant: 'Your role cannot link a notice to data-map systems. Remove the systems, or ask for the permission to read systems.',
                audience_with_subjects: '{label}: {subjects}',
            },
            coverage: {
                heading: 'Notices',
                intro: 'For each data subject recorded on this activity, the linked privacy notices that name it, with revision, channels, date in effect and status. Nothing here is counted or scored.',
                not_visible: 'Your role cannot read the privacy notice register, so the notices are not shown here. This does not say whether a notice exists.',
                no_subjects: 'This activity records no data subjects.',
                list_label: 'Notices by data subject',
                notice_line: '{title}, revision {revision}',
                notice_meta: 'Channels: {channels} · In effect from {date} · Status: {status}',
                load_failed: 'The notices could not be read.',
                retry_subject: 'the notices of this activity',
                pagination: 'Pages of data subjects',
                public_heading: 'Also linked: public notices',
                public_intro: 'These notices are addressed to the public. A public notice names no data subject, so it is not listed against any data subject above.',
                public_failed: 'The public notices linked to this activity could not be read.',
                public_retry_subject: 'the public notices',
                truncated: 'More notices are linked to this activity than are listed here, so they are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. A data subject reads "Not recorded" only when no linked notice names it.',
                subject_state: '{subject}: {state}',
            },
            access: {
                intro: 'Who may view, extract, export, delete or configure the data of this activity, by what means and on what conditions. Every revision is kept; the latest is first.',
                record: 'Record a revision',
                record_first: 'Record the access statement',
                current: 'Current',
                version: 'Version {version}',
                reviewed: 'Reviewed on {date}',
                approved: 'Approved by {name}',
                not_approved: 'No approver recorded',
                recorded: 'Recorded by {name} on {date}',
                systems: 'Systems: {names}',
                entries_label: 'Entries of version {version}',
                evidence_count: 'Documents: {count}',
                evidence_none: 'No document attached',
                evidence_open: 'Documents',
                evidence_open_aria: 'Documents of version {version}',
                load_failed: 'The access statement could not be read.',
                retry_subject: 'the access statement',
                pagination: 'Pages of access statement revisions',
                history_label: 'Revisions of the access statement',
                unknown_person: 'Unknown person',
            },
            access_form: {
                title: 'Record a revision of the access statement',
                replaces: 'This revision replaces version {version}. The earlier versions are kept.',
                first: 'This is the first revision of the access statement for this activity.',
                entries_heading: 'Who may access the data',
                entry_legend: 'Entry {n}',
                role_label: 'Role or position',
                role_hint: 'A role or a position, such as "Fleet safety officer". A person\'s name is not needed.',
                access_kinds_label: 'Permitted actions',
                means_label: 'By what means',
                means_hint: 'For example, the extraction software named, a console, or a request to IT.',
                conditions_label: 'On what conditions',
                conditions_hint: 'For example, a ticket, two people present, or a stated purpose.',
                add_entry: 'Add an entry',
                remove_entry: 'Remove entry {n}',
                reviewed_on_label: 'Reviewed on',
                reviewed_on_hint: 'The day the statement was reviewed. It cannot be later than today.',
                approver_label: 'Approved by (optional)',
                approver_none: 'No approver',
                approver_retry_subject: 'the list of people',
                systems_label: 'Data-map systems the statement covers',
                systems_kept: 'The systems the current version covers are kept, because your role cannot read the data-map systems.',
                missing: 'Give every entry a role or position and at least one permitted action, and give the review date.',
                submit: 'Record the revision',
                saving: 'Recording…',
                recorded: 'Revision recorded.',
                record_failed: 'The revision could not be recorded. What you entered is kept.',
                conflict: 'Someone recorded version {version} of this statement while you were editing, so yours was not recorded. What you entered now builds on version {version}: the entries and systems you did not change read as in version {version}, and your additions, changes and removals are kept. Check the entries, then record again.',
                conflict_reread_failed: 'Someone recorded a newer revision of this statement while you were editing, so yours was not recorded, and the newer revision could not be read. What you entered is kept; record again to read it once more.',
            },
            access_files: {
                heading: 'Documents',
                intro: "The documents behind this revision, such as the access policy, the extraction software's role list or the signed approval.",
                slot: 'Documents of this revision',
                none: 'No document is attached to this revision.',
                file_hash: 'SHA-256: {hash}',
                not_held: 'Document no longer held',
                download: 'Download',
                remove: 'Remove',
                upload: 'Add a document',
                bound: 'Document added.',
                removed: 'Document removed.',
                bind_failed: 'The document could not be added.',
                remove_failed: 'The document could not be removed.',
                not_owned: 'The revision this document belongs to was not found.',
                remove_confirm: 'Remove this document from the revision and delete it?',
                load_failed: 'The documents could not be loaded.',
            },
        },
        lawful_basis: {
            title: 'Lawful basis',
            load_failed: 'The lawful bases your installed frameworks declare could not be read.',
            none_declared: 'No installed framework declares a lawful-basis vocabulary. Record the basis and the article it rests on below.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            statement_label: 'Lawful basis (recorded by you)',
            statement_citation_label: 'Article this basis rests on',
            other_ground: 'Another lawful basis, recorded by you',
            needs_redetermination: 'This record carries "{basis}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{basis}" and is marked as needing re-determining. Confirm this basis or choose another, then save.',
            stored_unverified: 'This record carries "{basis}". Some installed frameworks have not been verified yet, so whether one of them declares it is not known.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no lawful basis',
            instrument_not_verified: '{instrument} — not yet verified',
            retry: 'Try again',
            list_needs_redetermination: 'Needs re-determining: {basis}',
            declared_version_changed: 'Recorded against {framework} {version}; installed now: {installed}.',
            declared_version_withdrawn: 'Recorded against {framework} {version}; no installed framework declares this basis now.',
            loading: 'Loading the lawful bases your installed frameworks declare.',
            save_blocked_unread: 'A lawful basis is required, and the bases your installed frameworks declare have not been read yet.',
            citation_blank: 'Enter the article this basis rests on. Spaces alone are not an article.',
            citation_without_statement: 'An article is entered but no basis is recorded. Record the basis it supports, or clear the article.',
        },
        // OD42 (fix-sprint-20261003-0625 F28) — the transfer-mechanism control and
        // its list sentences. The mechanism is what the installed frameworks declare.
        transfer_mechanism_vocabulary: {
            title: 'Transfer mechanism',
            load_failed: 'The transfer mechanisms your installed frameworks declare could not be read.',
            loading: 'Loading the transfer mechanisms your installed frameworks declare.',
            retry: 'Try again',
            none_declared: 'No installed framework declares a transfer mechanism. Describe the transfer in the detail field.',
            none_declared_unverified: 'No installed framework has declared a transfer mechanism, and some have not been verified yet. Describe the transfer in the detail field.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            needs_redetermination: 'This record carries "{mechanism}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{mechanism}" and is marked as needing re-determining. Confirm this mechanism or choose another, then save.',
            unverified: 'This record carries "{mechanism}". No verified installed framework declares it; not yet verified: {instruments}.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no transfer mechanism',
            instrument_not_verified: '{instrument} — not yet verified',
            list_needs_redetermination: 'Needs re-determining: {mechanism}',
            list_unverified: '{mechanism} (not yet verified: {instruments})',
            stored_unmeasured: 'This record carries "{mechanism}". No installed framework has stated which transfer mechanisms it provides yet, so whether it is declared has not been checked.',
        },
        nav: {
            systems: 'Sistemes',
            categories: 'Categories de dades',
            registry: 'Registre de dades',
            purposes: 'Finalitats del tractament',
            retention: 'Retenció',
            flows: 'Fluxos de dades',
            activities: 'Activitats de tractament',
            evidence: 'Evidència',
            exports: 'Exportacions',
            attestation: "Cicles d'acreditació",
            governance: 'Governança',
            review: 'Tasques de revisió',
            inventory: 'Inventari',
            exceptions: 'Excepcions',
            history: 'Historial',
            ropa_completeness: 'ROPA Exhaustivitat',
        },
        ropa_completeness_autofix: {
            ask_button: 'Pregunta a Forseti',
            drafting: 'Redacció…',
            accept: 'Acceptar',
            reject: 'Rebutja',
            accepted: "Acceptat: sol·liciteu-lo mitjançant l'editor d'activitats.",
            rejected: 'Rebutjat.',
            needs_human_input: "Forseti necessita més context d'activitat (per exemple, un propòsit documentat) abans de redactar aquest camp.",
            ask_again: 'Ask again',
            draft_failed: 'Forseti could not draft a value.',
            decision_failed: 'The decision could not be saved.',
        },
        ropa_completeness: {
            rules: {
                nd165_scope_rationale: 'Confirm and evidence the transfer scope; review mixed data before selecting the applicable assessment route.',
                nd165_scope_condition: 'Important/core cross-border data with mixed or unresolved personal-data scope',
                nd165_personal_dpia_rationale: 'Record the processing impact assessment reference when required, including confirmed personal core/important cross-border data.',
                nd165_personal_dpia_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_personal_transfer_rationale: 'Record the separate personal-data cross-border transfer impact assessment dossier reference.',
                nd165_personal_transfer_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_nonpersonal_assessment_rationale: 'Core non-personal data requires a regulator-issued assessment for cross-border transfers.',
                nd165_nonpersonal_assessment_condition: 'Confirmed non-personal core data + cross-border transfer',
            },
            title: "Informe d'exhaustivitat de la ROPA",
            subtitle: "Auditoria d'higiene per camp dels vostres registres d'activitats de processament, a l'abast del marc normatiu.",
            framework_label: 'Marc',
            kpi_total: 'Activitats puntuades',
            kpi_critical: 'Crític',
            kpi_high: 'Alt',
            kpi_conditional: 'Condicional',
            field_chart_heading: 'Camps que falten per freqüència',
            drilldown_heading: 'Activitats amb buits',
            col_activity: 'Activitat',
            col_field: 'Camp',
            col_severity: 'Severitat',
            col_condition: 'Condició',
            col_action: 'Fix',
            action_fix: 'Fix',
            severity_critical: 'Crític',
            severity_high: 'Alt',
            severity_conditional: 'Condicional',
            severity_informational: 'Informatiu',
            include_informational: 'Inclou informació',
            export_csv: 'Descarrega CSV',
            exporting: "S'està exportant...",
            loading: 'Carregant…',
            empty_state: 'Totes les activitats actives tenen registres ROPA complets per a aquest marc.',
            error_fetch: "No s'ha pogut carregar l'informe d'exhaustivitat.",
            error_unsupported_framework: 'Aquest marc no és compatible.',
            error_framework_not_installed: 'Aquest marc no està instal·lat per a la vostra organització.',
            no_misses_for_field: 'No hi ha activitats a faltar en aquest camp.',
            framework_PDPL: 'PDPL (Llei 91/2025 + Decret 356)',
            framework_GDPR: 'GDPR Article 30',
            framework_DECREE_165: 'Decret 165 (Llei de dades de Vietnam)',
            framework_ISO_27701: 'ISO 27701 (informació)',
        },
        registry: {
            // Sprint 64.72.0 — capçaleres de columna de l’exportació del registre de dades.
            export_col: {
                registry_item_id: 'ID de l’element del registre',
                name: 'Nom',
                description: 'Descripció',
                field_identifiers: 'Identificadors de camp',
                category_id: 'ID de categoria',
                category_name: 'Nom de la categoria',
                data_type: 'Tipus de dada',
                effective_sensitivity: 'Sensibilitat efectiva',
                sensitivity_override: 'Sensibilitat substitutiva',
                data_subjects: 'Interessats',
                volume_band: 'Interval de volum',
                source_system_ids: 'ID dels sistemes d’origen',
                processing_activity_ids: 'ID de les activitats de tractament',
                retention_policy_id: 'ID de la política de conservació',
                effective_retention_policy_id: 'ID de la política de conservació efectiva',
                purposes: 'Finalitats',
                collection_method: 'Mètode de recollida',
                legal_basis: 'Base jurídica',
                data_tier: 'Nivell de dades',
                data_tier_source: 'Origen del nivell de dades',
                storage_locations: 'Ubicacions d’emmagatzematge',
                cross_border: 'Transfronterer',
                status: 'Estat',
                source_form_submission_id: 'ID de tramesa del formulari d’origen',
                source_form_template_id: 'ID de plantilla del formulari d’origen',
                last_reviewed_at: 'Darrera revisió',
                next_review_at: 'Propera revisió',
                created_at: 'Creat el',
                updated_at: 'Actualitzat el',
                data_element: 'Element de dades',
                data_category: 'Categoria de dades',
                systems: 'Sistemes',
                retention_policy: 'Política de conservació',
                name_of_processing: 'Nom del tractament',
                categories_of_personal_data: 'Categories de dades personals',
                categories_of_data_subjects: 'Categories d’interessats',
                recipients: 'Destinataris',
                third_country_transfers: 'Transferències a tercers països',
                retention_period: 'Termini de conservació',
                doc_title: 'Registre de dades',
                doc_generated: 'Exportació generada el {timestamp}',
                doc_item_count: '{count} element(s) del registre',
                doc_template_full: 'Complet',
            },
            export_vocab: {
                data_type: {
                    text: 'Text',
                    number: 'Nombre',
                    date: 'Data',
                    datetime: 'Data i hora',
                    boolean: 'Sí/no',
                    binary: 'Binari',
                    structured: 'Estructurat',
                    file: 'Fitxer',
                    identifier: 'Identificador',
                    geolocation: 'Geolocalització',
                },
                collection_method: {
                    direct_from_subject: 'Directament de l’interessat',
                    third_party: 'D’un tercer',
                    derived: 'Derivat d’altres dades',
                    observed: 'Observat',
                    inferred: 'Inferit',
                    public_source: 'Font pública',
                },
                data_subject_type: {
                    customer: 'Client',
                    employee: 'Empleat',
                    contractor: 'Contractista',
                    prospect: 'Client potencial',
                    minor: 'Menor d’edat',
                    patient: 'Pacient',
                    student: 'Estudiant',
                    beneficiary: 'Beneficiari',
                    vendor_contact: 'Contacte del proveïdor',
                    website_visitor: 'Visitant del lloc web',
                    other: 'Altres',
                },
                volume_band: {
                    lt_1k: 'Menys de 1.000 registres',
                    '1k_to_10k': 'De 1.000 a 10.000 registres',
                    '10k_to_100k': 'De 10.000 a 100.000 registres',
                    '100k_to_1m': 'De 100.000 a 1 milió de registres',
                    gt_1m: 'Més d’1 milió de registres',
                    unknown: 'Es desconeix',
                },
                tier_source: {
                    assessed_and_approved: 'Avaluat i aprovat',
                    manual_legacy: 'Adoptat d’un valor heretat',
                    legacy_platform_field: 'Valor heretat de la plataforma',
                    scheme_scoped_only: 'Avaluat segons nivells propis de cada esquema — cap codi únic no abasta diverses escales de nivells legals',
                },
            },
            page: {
                title: 'Registre de dades',
                subtitle: 'Elements de dades personals que té la vostra organització, organitzats a Categories de dades.',
                empty_state: "Encara no hi ha elements de registre. Afegeix un element manualment o importa des d'un registre regulador.",
                empty_filter_state: 'Cap element coincideix amb els vostres filtres.',
            },
            filter: {
                category: 'Categoria',
                sensitivity: 'Sensibilitat',
                data_subject: 'Afectat de les dades',
                status: 'Estat',
                data_tier: 'Nivell de dades',
                cross_border: 'Transfronterer',
                has_owner: 'Té propietari',
                has_form_source: 'Importat del formulari',
                search_placeholder: "Cerca el nom, la descripció o l'identificador de camp...",
                clear: 'Esborra els filtres',
                option_all: 'Tots',
            },
            col: {
                name: 'Nom',
                category: 'Categoria',
                sensitivity: 'Sensibilitat',
                data_subjects: 'Supòsits de dades',
                systems: 'Sistemes',
                activities: 'Activitats',
                owner: 'Propietari',
                retention: 'Retenció',
                data_tier: 'Nivell de dades',
                status: 'Estat',
                last_reviewed: 'Última revisió',
                actions: 'Accions',
            },
            action: {
                add_manual: 'Afegeix un element',
                add_from_form: 'Afegir del registre regulador',
                import_history: 'Importa enviaments anteriors',
                export: 'Exporta',
                archive: 'Arxiu',
                restore: 'Restaurar',
                edit: 'Edita',
                assign_owner: 'Assigna el propietari',
                attach_evidence: 'Adjuntar proves',
                open_lineage: 'Llinatge obert',
            },
            modal_editor: {
                section_identity: 'Identitat',
                section_classification: 'Classificació',
                section_subjects: 'Temes i volum',
                section_location: 'Ubicació i trasllat',
                section_systems: 'Sistemes i activitats',
                section_retention: 'Retenció i legal',
                section_governance: 'Governança',
                section_notes: 'Notes',
                hint_inherited: '(heretat de la categoria)',
                hint_overridden: '(anul·lat)',
                placeholder_field_identifiers: 'p. ex., clients.primary_email, hris.employee_id',
                validation_name_required: 'El nom és obligatori',
                validation_category_required: 'Es requereix categoria',
                validation_data_subjects_required: 'Es requereix almenys un tipus de subjecte de dades',
                label_data_type: 'Tipus de dades',
                label_field_identifiers: 'Identificadors de camp (separats per comes)',
                label_data_subject_types: 'Tipus de subjectes de dades',
                label_volume_band: 'Banda de volum',
                label_storage_locations: "Ubicacions d'emmagatzematge (separades per comes)",
                placeholder_storage_locations: 'ISO-3166 codis o text lliure (p. ex., VN, Hetzner EU)',
                label_cross_border: 'Transferència transfronterera',
                label_source_systems: 'Sistemes font',
                label_processing_activities: 'Activitats de processament',
                label_collection_method: 'Mètode de recollida',
                label_purposes: 'Propòsits',
                hint_multi_select: 'Mantingueu premut Cmd/Ctrl per seleccionar múltiples.',
                option_inherit_category: '— (hereta de la categoria)',
                option_dash: '—',
                // fix-sprint-20260925-2123 (Finding 11) — a stale save refused with 409.
                error_conflict: 'This registry item was changed by someone else while you were editing, so your change was not saved. Close this form and open the item again to see the current version.',
                // The form was re-read and now shows the saved version.
                error_conflict_refreshed: 'This registry item was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            },
            modal_import: {
                scan_none: 'No mapped regulatory-register templates are registered yet.',
                scan_count: 'Scans {count} template(s) for historical submissions and imports items.',
                dry_run_heading: "Vista prèvia d'importació",
                label_to_create: 'Per crear',
                label_to_update: 'Per actualitzar',
                label_skipped_archived: 'Omès (arxivat)',
                label_conflicts: 'Conflictes',
                commit: 'Importar',
                cancel: 'Cancel·la',
                conflict_new_item: 'New item',
                conflict_value_own_words: 'a ground recorded in a person’s own words: “{statement}”',
                conflict_reason: 'Reason: {reason}',
                conflict_row_kept_edit: '{field} — the value you edited was kept: {current}. The form’s value was not applied: {mapper}.',
                conflict_row_kept_current: '{field} — the current value was kept: {current}. The form’s value was refused: {mapper}.',
                conflict_row_not_created: '{field} — the item was not created. The form’s value was refused: {mapper}.',
                conflict_reasons: {
                    field_user_edited: 'A person edited this field after an earlier import, and an import never overwrites a person’s edit.',
                    basis_not_declared: 'No installed framework declares a lawful basis, so a coded basis cannot be recorded.',
                    basis_required: 'This record needs a lawful basis, and the form gave none.',
                    basis_needs_person: 'Only an authenticated person may record or change this lawful basis, and the import did not run as one.',
                    basis_statement_rules: 'A lawful basis is an option your installed frameworks declare, or a ground in your own words with the article it rests on; the form’s value is neither.',
                    unknown: 'The import did not apply this value.',
                },
                conflict_fields: {
                    description: 'Description',
                    field_identifiers: 'Field identifiers',
                    data_type: 'Data type',
                    sensitivity_override: 'Sensitivity (override)',
                    data_subject_types: 'Data subject types',
                    volume_band: 'Volume band',
                    source_system_ids: 'Source systems',
                    processing_activity_ids: 'Processing activities',
                    retention_policy_id: 'Retention policy',
                    purposes: 'Purposes',
                    collection_method: 'Collection method',
                    legal_basis: 'Lawful basis',
                    data_tier: 'Data tier',
                    storage_locations: 'Storage locations',
                    cross_border: 'Cross-border transfer',
                },
            },
            drawer: {
                section_category: 'Categoria',
                section_systems: 'Sistemes font',
                section_activities: 'Activitats de processament',
                section_flows: 'Fluxos de dades',
                section_retention: 'Retenció efectiva',
                section_governance: 'Governança',
                section_evidence: 'Evidència',
                section_audit: "Pista d'auditoria recent",
                section_source_form: "Enviament del formulari d'origen",
                badge_inherited: 'Heretat',
                badge_overridden: 'Anul·lat',
            },
            toast: {
                created: 'Element de registre creat.',
                updated: 'Element del registre actualitzat.',
                archived: 'Element del registre arxivat.',
                imported: "S'han importat {count} elements.",
                conflict_warning: '{count} camp(s) han conservat les vostres edicions; vegeu la llista de conflictes.',
            },
            // Sprint 62.35.0 (AC6) — English placeholder; locale work beyond EN/VI defers to Phase 25 (R-57).
            evidence: {
                section_title: 'Provenance, notice and quality',
                section_intro: 'Evidence the organisation records about this data. These are records, not verdicts: an empty field means not recorded.',
                not_recorded: 'Not recorded',
                group_provenance: 'Provenance and authorisation',
                group_notice: 'Notice',
                group_quality: 'Authenticity and quality',
                label_source_description: 'Source of the data',
                hint_source_description: 'Collected directly, received from whom, or created by whom.',
                label_data_owner: 'Data owner',
                label_authorisation_basis: 'Authorisation relied on',
                label_authorisation_evidence_refs: 'Where the authorisation evidence is held (comma-separated)',
                label_notified_purpose: 'Purpose notified',
                label_notified_scope: 'Scope notified',
                label_notice_reference: 'Notice reference',
                label_authenticity_checked: 'Authenticity checked',
                label_authenticity_method: 'How authenticity was checked, and the result',
                label_quality_checked: 'Quality checked',
                label_quality_method: 'How quality was checked, and the result',
                label_matches_source: 'Collected data matches the source data',
                label_checked_on: 'Checked on',
                hint_checked_on: 'The day the check was made. It cannot be after today.',
                label_checked_by: 'Checked by',
                checked_by_retry_subject: 'the list of people who can be named as the checker',
                error_checked_on_in_future: "The check cannot be dated after today in the organisation's calendar ({date}).",
                error_checked_by_not_found: 'The person named as the checker is not a user of this organisation.',
                label_recorded_by: 'Check recorded by',
                label_recorded_at: 'Check recorded at',
                hint_recorded_stamp: 'Recorded automatically when any check field changes.',
                option_yes: 'Yes',
                option_no: 'No',
                option_not_determined: 'Not determined',
                vocabulary_none: 'No installed framework declares an authorisation vocabulary, so this can only be left not recorded.',
                vocabulary_not_in_force: 'Declared by a framework that is not yet in force (effective {date}).',
                vocabulary_load_failed: 'The authorisation options could not be loaded.',
                value_undeclared: '{value} (not declared by any installed framework)',
            },
        },
        systems: {
            title: 'Inventari de sistemes',
            new: 'Nou sistema',
            edit: 'Editar sistema',
            name: 'Nom',
            owner: 'Responsable',
            criticality: 'Criticitat',
            dpa_status: 'Estat DPA',
            location: 'Ubicació',
            system_type: 'Tipus de sistema',
            metadata: 'Metadades (JSON)',
            department: 'Departament',
            hosting_location: "Ubicació de l'allotjament",
            hosting_provider: "Proveïdor d'allotjament",
            data_residency_country: 'País de residència de dades',
            hosting: 'Allotjament',
            no_systems: "No s'han trobat sistemes",
            confirm_archive: 'Arxivar sistema "{name}"?',
            count: 'sistemes',
            count_one: 'sistema',
            type_internal: 'Intern',
            type_external: 'Extern',
            type_cloud_service: 'Servei al núvol',
            type_on_premise: 'Local',
            type_hybrid: 'Híbrid',
            status_active: 'Actiu',
            status_decommissioning: 'En desactivació',
            status_decommissioned: 'Desactivat',
            status_planned: 'Planificat',
            dpa_not_required: 'No requerit',
            dpa_required_missing: 'Requerit absent',
            dpa_in_progress: 'En progrés',
            dpa_signed: 'Signat',
            all_criticalities: 'Totes les criticitats',
            status_inactive: 'Inactive',
        },
        categories: {
            title: 'Categories de dades',
            new: 'Nova categoria',
            edit: 'Editar categoria',
            name: 'Nom',
            sensitivity: 'Sensibilitat',
            tags: 'Etiquetes',
            used_in: 'Utilitzat en',
            examples: 'Exemples',
            examples_hint: 'Exemples (separats per comes)',
            default: 'Predeterminat',
            all_sensitivities: 'Totes les sensibilitats',
            all_statuses: 'Tots els estats',
            no_categories: "No s'han trobat categories",
            confirm_delete: 'Eliminar categoria "{name}"?',
            count: 'categories',
            count_one: 'categoria',
            status_draft: 'Esborrany',
            status_proposed: 'Proposat',
            status_under_review: 'En revisió',
            status_approved: 'Aprovat',
            status_deprecated: 'Obsolet',
            status_updated: 'Estat de la categoria actualitzat',
            propose: 'Proposar',
            approve: 'Aprovar',
            deprecate: 'Obsolet',
            items: 'Registry Items',
        },
        purposes: {
            title: 'Finalitats del tractament',
            new: 'Nova finalitat',
            edit: 'Editar finalitat',
            name: 'Nom',
            legal_basis: 'Base jurídica',
            retention_period: 'Període de retenció (dies)',
            justification: "Justificació de l'interès legítim",
            legal_basis_detail: 'Detall de la base jurídica',
            all_legal_bases: 'Totes les bases jurídiques',
            no_purposes: "No s'han trobat finalitats",
            confirm_delete: 'Eliminar "{name}"?',
            default: 'Predeterminat',
            count: '{count} finalitats',
            count_one: '{count} finalitat',
        },
        retention: {
            title: 'Polítiques de retenció',
            new: 'Nova política',
            edit: 'Editar política',
            name: 'Nom',
            retention_days: 'Dies de retenció',
            disposal_method: "Mètode d'eliminació",
            data_categories: 'Categories de dades',
            activities: 'Activitats',
            review_frequency: 'Freqüència de revisió',
            review_frequency_days: 'Freqüència de revisió (dies)',
            no_policies: "No s'han trobat polítiques de retenció",
            confirm_delete: 'Eliminar "{name}"?',
            count: '{count} polítiques',
            count_one: '{count} política',
            days: 'dies',
            disposal_deletion: 'Supressió',
            disposal_anonymization: 'Anonimització',
            disposal_archival: 'Arxivament',
            review_overdue: 'Revisió endarrerida',
            review_status: 'Estat de revisió',
            review_ok: 'En curs',
            department: 'Departament',
            all_departments: 'Tots els departaments',
            status_draft: 'Draft',
            status_active: 'Active',
            status_superseded: 'Superseded',
            status_retired: 'Retired',
        },
        flows: {
            view_tabs_aria: 'Vista de fluxos',
            view_list: 'Llista',
            view_map: 'Mapa',
            title: 'Fluxos de dades',
            new: 'Nou flux',
            edit: 'Editar flux',
            name: 'Nom',
            from_system: "Sistema d'origen",
            to_system: 'Sistema de destinació',
            flow_type: 'Tipus de flux',
            cross_border: 'Transfronterer',
            risk_level: 'Nivell de risc',
            mechanism: 'Mecanisme',
            transfer_mechanism: 'Mecanisme de transferència',
            frequency: 'Freqüència',
            source_country: "País d'origen",
            destination_country: 'País de destinació',
            transfer_safeguard: 'Garantia de transferència',
            transfer_safeguard_detail: 'Transfer Safeguard Detail',
            all_risk_levels: 'Tots els nivells de risc',
            no_flows: "No s'han trobat fluxos de dades",
            confirm_archive: 'Arxivar "{name}"?',
            count: '{count} fluxos',
            count_one: '{count} flux',
            status_active: 'Actiu',
            status_inactive: 'Inactiu',
            status_under_review: 'En revisió',
            mechanism_api: 'API',
            mechanism_file_transfer: 'Transferència de fitxers',
            mechanism_database_replication: 'Replicació de base de dades',
            mechanism_manual: 'Manual',
            mechanism_email: 'Correu electrònic',
            mechanism_other: 'Altre',
            frequency_real_time: 'Temps real',
            frequency_hourly: 'Cada hora',
            frequency_daily: 'Diari',
            frequency_weekly: 'Setmanal',
            frequency_monthly: 'Mensual',
            frequency_on_demand: 'Sota demanda',
            frequency_one_time: 'Una sola vegada',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            safeguard: 'Safeguard',
            section_scope_title: 'Processing scope',
            section_scope_description: 'Data categories and purposes carried by this flow.',
            jurisdiction: 'Jurisdicció',
        },
        activities: {
            title: 'Activitats de tractament',
            new: 'Nova activitat',
            edit: 'Editar activitat',
            activity_title: 'Títol',
            category: 'Categoria',
            legal_basis: 'Base jurídica',
            dpa_required: 'DPIA Required',
            ropa_generated: 'RoPA generat',
            version: 'Versió',
            generate_ropa: 'Generar RoPA',
            controller: 'Responsable del tractament',
            controller_name: 'Nom del responsable del tractament',
            controller_contact: 'Contacte del responsable del tractament',
            legal_basis_detail: 'Detall de la base jurídica',
            organization_info: 'Organization Information',
            org_address: "Adreça de l'organització",
            tax_code: 'Codi fiscal',
            contact_person: 'Persona de contacte',
            contact_email: 'Correu de contacte',
            contact_phone: 'Telèfon de contacte',
            select_entity: 'Seleccioneu…',
            no_entities: "No s'han trobat elements",
            dpo_name: 'Nom del DPD',
            dpo_contact: 'Contacte del DPD',
            processor_name: "Nom de l'encarregat",
            processor_contact: "Contacte de l'encarregat",
            data_subjects: 'Interessats',
            data_subjects_hint: 'Interessats (separats per comes)',
            recipients: 'Destinataris',
            recipients_hint: 'Destinataris (separats per comes)',
            security_measures: 'Mesures de seguretat',
            dpia_reference: 'Referència AIPD',
            no_activities: "No s'han trobat activitats de tractament",
            confirm_archive: 'Arxivar "{name}"?',
            count: '{count} activitats',
            count_one: '{count} activitat',
            ropa_success: 'Registre RoPA generat. Aneu a Exportacions per descarregar-lo.',
            ropa_error: "No s'ha pogut generar el RoPA",
            required_label: 'Obligatori',
            status_draft: 'Esborrany',
            status_active: 'Actiu',
            status_under_review: 'En revisió',
            status_archived: 'Arxivat',
            decree_165_heading: 'Nivell de dades del Decret 165 (Llei de dades de Vietnam)',
            decree_165_helper: 'Record the data tier and whether the assessed transfer data is personal. Decree 356 Article 42(3) routes impact assessments for core or important personal data through personal-data protection law. Other applicable Data Law duties remain subject to review.',
            personal_data_scope: 'Personal-data scope of assessed transfer data',
            personal_data_scope_personal: 'Confirmed personal data',
            personal_data_scope_non_personal: 'Confirmed non-personal data',
            personal_data_scope_mixed: 'Mixed personal and non-personal data',
            personal_data_scope_unknown: 'Not yet determined',
            personal_data_scope_hint: 'Classify the assessed transfer data and record evidence before determining which impact-assessment route applies. No scope is inferred from the data tier.',
            personal_data_scope_evidence: 'Scope evidence and rationale',
            personal_data_scope_evidence_required: 'Record evidence before confirming personal or non-personal data scope.',
            cross_border_impact_assessment_reference: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_label: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_placeholder: 'Reference to the assessment for the applicable legal route',
            scope_review: 'Mixed or undetermined scope requires legal review before an impact-assessment route is selected. Record how each data component is assessed.',
            security_assessment_reference_help: 'Use only where the Decree 165 security-assessment route applies. Core or important personal-data impact assessments follow personal-data protection law under Decree 356 Article 42(3).',
            data_tier_label: 'Nivell de dades',
            data_tier_help: 'Record regular, important or core data separately from personal-data scope. A tier alone does not determine the impact-assessment route.',
            data_tier_column_header: 'Nivell de dades',
            data_tier_regular: 'Regular',
            data_tier_important: 'Important',
            data_tier_core: 'Nucli',
            transfer_mechanism_label: 'Mecanisme de transferència transfronterera',
            transfer_mechanism_placeholder: 'e.g. the transfer mechanism your installed frameworks provide, with its article',
            security_assessment_reference_label: "Referència d'avaluació de seguretat",
            security_assessment_reference_placeholder: 'p. ex. MPS-SA-2026-001',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            registry_items_label: 'Specific registry items (optional)',
            registry_items_hint: 'Most activities leave this empty — data categories already cover RoPA. Set it when an inspection asks which exact fields an activity touches.',
            registry_items_pending: 'Loading the registry items currently linked to this activity. Saving before they load will leave those links unchanged.',
            registry_items_unavailable: 'The registry items currently linked to this activity could not be loaded. Saving will leave those links unchanged.',
            registry_items_unlisted_option: 'Linked item {id} (not in the list)',
            retention_period_description: 'Retention Period Description',
        },
        evidence: {
            title: "Dipòsit d'evidències",
            new: 'Nova evidència',
            edit: 'Editar evidència',
            name: 'Nom',
            evidence_type: "Tipus d'evidència",
            linked_entities: 'Entitats vinculades',
            uploaded: 'Carregat',
            expiry: 'Caducitat',
            file_url: 'URL del fitxer',
            source_mode_label: "Font de l'evidència",
            source_mode_file: 'Carrega fitxer',
            source_mode_url: 'Enllaça URL externa',
            attachment_label: 'Fitxer adjunt',
            link_to: 'Vincular evidència a',
            entity_type: "Tipus d'entitat",
            entity_id: "ID de l'entitat",
            no_evidence: "No s'han trobat evidències",
            manage_links: 'Gestionar enllaços',
            unlink: 'Desenllaça',
            confirm_delete: 'Eliminar evidència "{name}"?',
            count: '{count} elements',
            count_one: '{count} element',
            type_document: 'Document',
            type_screenshot: 'Captura de pantalla',
            type_policy: 'Política',
            type_contract: 'Contracte',
            type_assessment: 'Avaluació',
            type_correspondence: 'Correspondència',
            type_other: 'Altre',
            linked_type_system: 'Sistema',
            linked_type_data_flow: 'Flux de dades',
            linked_type_processing_activity: 'Activitat de processament',
            linked_type_data_category: 'Categoria de dades',
            linked_type_purpose: 'Propòsit',
            linked_type_retention_policy: 'Política de retenció',
            link_entity: "Enllaç a l'entitat",
            link_success: 'Entitat enllaçada correctament',
            confirm_unlink: 'Esteu segur que voleu desenllaçar aquesta entitat?',
            unlink_success: "L'entitat s'ha desenllaçat correctament",
            no_linked_entities: 'No hi ha entitats vinculades',
            select_entity: 'Seleccioneu…',
            no_entities: 'No hi ha articles',
        },
        exports: {
            title: 'Exportacions de dades',
            recent: 'Exportacions recents',
            export_type: "Tipus d'exportació",
            format: 'Format',
            requested_by: 'Sol·licitat per',
            requested_at: 'Sol·licitat el',
            download: 'Descarregar',
            csv_export: 'Exportació CSV',
            csv_description: 'Descarregueu dades en format CSV per a anàlisi en full de càlcul.',
            pdf_reports: 'Informes PDF',
            pdf_description: 'Genereu informes amb format per a documentació de compliment.',
            csv_systems: 'Inventari de sistemes',
            csv_categories: 'Categories de dades',
            csv_flows: 'Fluxos de dades',
            csv_activities: 'Activitats de tractament',
            csv_cross_border: 'Fluxos transfronterers',
            csv_retention_policies: 'Polítiques de retenció',
            csv_evidence: 'Elements de prova',
            csv_governance_assignments: 'Encàrrecs de govern',
            pdf_ropa: 'Registre RoPA',
            pdf_systems: 'Informe de sistemes',
            pdf_cross_border: 'Informe transfronterer',
            pdf_flow_map: 'Mapa de fluxos de dades',
            export_failed: "L'exportació ha fallat",
            pdf_generated: 'PDF generat correctament.',
            ropa: 'Registre RoPA',
            retention_policies: 'Polítiques de retenció',
            governance_assignments: 'Encàrrecs de govern',
            col_entity: "Entitat d'exportació",
            col_description: 'Descripció',
            desc_ropa: 'Registre ROPA complet de totes les activitats de processament amb base legal, estat de DPIA i controls.',
            desc_systems: 'Inventari de sistemes amb propietari, etiquetes de categoria de dades i estat de compliment.',
            desc_activities: 'Activitats de tractament individual amb responsable del tractament, base legal i classificació DPIA.',
            desc_categories: 'Categories de dades i nivells de sensibilitat utilitzats en la classificació i les auditories.',
            desc_purposes: 'Finalitats del tractament amb la base jurídica registrada per a cadascuna.',
            desc_flows: 'Fluxos de dades entre sistemes, incloses les rutes internes i transfrontereres.',
            desc_cross_border: 'Només fluxos transfronterers, amb mecanisme de transferència i avaluació de riscos.',
            desc_retention_policies: "Polítiques de retenció, mètodes d'eliminació i cadència de revisió.",
            desc_evidence: 'Evidència adjunta a les activitats de processament: certificats, contractes DPA, informes DPIA.',
            desc_governance_assignments: 'Tasques de govern: propietaris, aprovadors i referències ISO 27001.',
            unified_heading: "Entitats d'exportació",
            unified_description: 'Exporteu qualsevol entitat de mapatge de dades en format CSV, XLSX, PDF o DOCX. Totes les exportacions es guarden al navegador de fitxers.',
            export_button_label: 'Exporta',
            csv_download_success: 'CSV descarregat correctament.',
            col_action: 'Export',
        },
        attestation: {
            title: "Cicles d'Atestació",
            create: 'Nou Cicle',
            name: 'Nom del Cicle',
            year: 'Any',
            deadline: 'Data Límit',
            scope: 'Abast',
            status: 'Estat',
            tasks: 'Tasques de Departament',
            assign_tasks: 'Assignar Tasques',
            department: 'Departament',
            assignee: 'Assignat',
            progress: 'Progrés',
            stale_entries: 'Entrades Obsoletes',
            detect_stale: 'Detectar Obsoletes',
            export_bundle: 'Exportar Paquet',
            no_cycles: "Encara no hi ha cicles d'atestació.",
            create_success: "Cicle d'atestació creat.",
            assign_success: 'Tasques assignades correctament.',
            complete_success: 'Tasca completada.',
            stale_detected: 'Entrades obsoletes detectades.',
            stale_systems: 'Sistemes obsolets',
            stale_flows: 'Fluxos obsolets',
            no_stale: "No s'han detectat entrades obsoletes.",
            stale_name: 'Nom',
            stale_announcement: "S'han trobat {0} sistemes obsolets i {1} fluxos obsolets",
            last_updated: 'Darrera actualització',
            status_draft: 'Esborrany',
            status_active: 'Actiu',
            status_completed: 'Completat',
            task_status_pending: 'Pendent',
            task_status_in_progress: 'En curs',
            task_status_completed: 'Completat',
            scope_systems: 'Sistemes',
            scope_flows: 'Fluxos de dades',
            scope_activities: 'Activitats',
            scope_categories: 'Categories',
            scope_purposes: 'Finalitats',
            scope_retention: 'Retenció',
            complete_cycle: 'Complete Cycle',
            confirm_activate: 'Are you sure you want to activate this cycle?',
            confirm_complete: 'Are you sure you want to complete this cycle? This action cannot be undone.',
            cycle_complete_success: 'Cycle completed successfully',
            task_status_verified: 'Verified',
            activate_success: 'Cycle activated successfully',
            activate: 'Activate Cycle',
        },
        governance: {
            title: 'Assignacions de Governança',
            assign: 'Assignar Propietat',
            entity_type: "Tipus d'Entitat",
            entity: 'Entitat',
            role: 'Rol',
            assigned_by: 'Assignat per',
            assigned_at: 'Assignat el',
            remove: 'Eliminar',
            no_assignments: 'Sense assignacions de governança.',
            assign_success: 'Propietat assignada.',
            remove_success: 'Assignació eliminada.',
            assignee: 'Assignat',
            department: 'Departament',
            all_departments: 'Tots els departaments',
            roles: {
                data_owner: 'Propietari de Dades',
                data_steward: 'Administrador de Dades',
                data_custodian: 'Custodi de Dades',
                informed: 'Informat',
            },
            entity_types: {
                system: 'Sistema',
                flow: 'Flux de dades',
                activity: 'Activitat',
                category: 'Categoria',
                purpose: 'Finalitat',
                retention_policy: 'Política de retenció',
            },
        },
        risk_assessment: {
            title: 'Avaluació de Riscos',
            create: 'Nova Avaluació',
            safeguard_type: 'Tipus de Salvaguarda',
            adequacy_status: "Estat d'Adequació",
            risk_factors: 'Factors de Risc',
            mitigations: 'Mesures de Mitigació',
            reviewer: 'Revisor',
            decision: 'Decisió',
            approve: 'Aprovar',
            reject: 'Rebutjar',
            no_assessments: 'Sense avaluacions de riscos.',
            create_success: 'Avaluació de riscos creada.',
            approve_success: 'Avaluació aprovada.',
            reject_success: 'Avaluació rebutjada.',
            one_per_line: 'Un per línia',
            adequacy_adequate: 'Adequat',
            adequacy_inadequate: 'Inadequat',
            adequacy_under_review: 'En revisió',
            adequacy_not_assessed: 'No avaluat',
            decision_approved: 'Aprovat',
            decision_rejected: 'Rebutjat',
        },
        review: {
            title: 'Tasques de revisió',
            description: 'Les tasques de revisió es creen automàticament quan es proposa una categoria de dades. Per crear una nova tasca, aneu a Categories de dades i feu clic a Proposar en una categoria esborrany.',
            count: 'tasques de revisió',
            summary: 'Resum de la revisió',
            pending: 'Pendents',
            resolved_today: 'Resoltes avui',
            escalated: 'Escalades',
            overdue: 'Endarrerides (>5 dies)',
            all_statuses: 'Tots els estats',
            status_pending: 'Pendent',
            status_resolved: 'Resolt',
            status_escalated: 'Escalat',
            category_name: 'Categoria',
            system_name: 'Sistema',
            owner: 'Responsable',
            resolve: 'Resoldre',
            resolve_task: 'Resoldre tasca de revisió',
            resolution: 'Resolució',
            resolution_approve: 'Aprovar',
            resolution_modify: 'Modificar i aprovar',
            resolution_reject: 'Rebutjar',
            resolution_escalate: 'Escalar',
            notes: 'Notes de resolució',
            notes_required: 'Notes obligatòries (mínim 10 caràcters) per a Modificar, Rebutjar o Escalar',
            resolved_success: 'Tasca de revisió resolta',
            bulk_approve: 'Aprovació massiva',
            bulk_reject: 'Rebuig massiu',
            bulk_success: '{count} tasques processades',
            no_tasks: "No s'han trobat tasques de revisió",
        },
        inventory: {
            title: 'Inventari de classificació',
            count: 'sistemes',
            summary: "Resum de l'inventari",
            total_systems: 'Total de sistemes',
            total_labels: "Total d'etiquetes",
            system_name: 'Sistema',
            label_count: 'Etiquetes',
            approved_count: 'Aprovats',
            pending_count: 'Pendents',
            no_items: "No s'han trobat elements d'inventari",
        },
        exceptions: {
            title: 'Excepcions de classificació',
            count: 'excepcions',
            all_severities: 'Totes les severitats',
            all_statuses: 'Tots els estats',
            system_name: 'Sistema',
            severity_label: 'Severitat',
            status_open: 'Obert',
            status_resolved: 'Resolt',
            status_accepted_risk: 'Risc acceptat',
            resolve: 'Resoldre',
            resolve_exception: 'Resoldre excepció',
            resolution_type: 'Tipus de resolució',
            notes: 'Notes',
            justification_required: 'Justificació obligatòria (mínim 10 caràcters) per a risc acceptat',
            resolved_success: 'Excepció resolta',
            no_exceptions: "No s'han trobat excepcions",
        },
        history: {
            title: 'Historial de classificació',
            count: 'esdeveniments',
            actor: 'Actor',
            action: 'Acció',
            entity_type: "Tipus d'entitat",
            changes: 'Canvis',
            view_lineage: 'Veure llinatge',
            lineage_title: 'Llinatge de classificació',
            no_events: "No s'han trobat esdeveniments d'historial",
            no_lineage: 'No hi ha dades de llinatge disponibles',
            modified: 'Modificat',
            created: 'Creat',
            removed: 'Eliminat',
            action_created: 'Creat',
            action_updated: 'Actualitzat',
            action_archived: 'Arxivat',
            action_approved: 'Aprovat',
            action_deprecated: 'Obsolet',
            entity_label: 'Etiqueta',
            entity_taxonomy: 'Taxonomia',
            entity_review_task: 'Tasca de revisió',
            entity_exception: 'Excepció',
            before: 'Abans',
            after: 'Després',
        },
    },
    governance: {
        // Sprint 62.57.0 Slice E (§12.13.9) — the controller-side vendor certificate record.
        vendor_service_certificate: enVendorServiceCertificate,
        audit_trail_title: "Traça d'auditoria del govern",
        audit_trail_subtitle: "Flux d'auditoria multi-entitat per a accions de govern — aprovacions, execucions de llibres de jugades, enllaços d'evidència i més.",
        audit_trail_item_count: "esdeveniments d'auditoria",
        audit_trail_fetch_error: "No s'han pogut obtenir els esdeveniments d'auditoria.",
        audit_trail_no_events: "No s'han trobat esdeveniments d'auditoria amb els filtres actuals.",
        audit_trail_entity_type: "Tipus d'entitat",
        audit_trail_entity_id: "ID d'entitat",
        audit_trail_action: 'Acció',
        audit_trail_actor: 'Actor',
        audit_trail_timestamp: 'Marca de temps',
        audit_trail_date_from: 'Des de',
        audit_trail_date_to: 'Fins a',
        audit_trail_no_rows_to_export: 'No hi ha files per exportar.',
        // Gate E12.5 remediation A (Sprint 63.8.2) — the audit trail filter options.
        audit_trail_entity_types: {
            vendor: 'Vendor',
            vendor_subprocessor: 'Sub-processor',
            transfer_mechanism: 'Transfer mechanism',
            drift_event: 'Drift event',
            playbook: 'Playbook',
            execution: 'Execution',
            approval: 'Approval',
        },
        audit_trail_actions: {
            created: 'Created',
            updated: 'Updated',
            status_changed: 'Status changed',
            assigned: 'Assigned',
            approved: 'Approved',
            rejected: 'Rejected',
            evidence_linked: 'Evidence linked',
            step_completed: 'Step completed',
            executed: 'Executed',
            terminated: 'Terminated',
            exported: 'Exported',
        },
        nav: {
            vendors: 'Proveïdors',
            subprocessors: 'Subencarregats',
            cross_border: 'Transfronterer',
            drift: 'Monitor de desviació',
            scores: 'Puntuació de compliment',
            playbooks: "Guies d'actuació",
            approvals: 'Aprovacions',
            audit_trail: "Traça d'auditoria",
            board_report: 'Informe del Consell',
            evidence: 'Evidència',
            onboarding: "Pipeline d'incorporació",
            dpa_reviews: 'Revisions de DPA',
            periodic_reviews: 'Revisions periòdiques',
            offboarding: 'Desvinculació',
        },
        vendors: {
            data_categories_processed: 'Data categories processed',
            title: 'Registre de proveïdors',
            new: 'Nou proveïdor',
            edit: 'Editar proveïdor',
            name: 'Nom',
            jurisdiction: 'Jurisdicció',
            dpa_status: 'Estat DPA',
            risk_level: 'Nivell de risc',
            onboarding_status: "Estat d'incorporació",
            contact_email: 'Correu de contacte',
            contact_name: 'Nom de contacte',
            contact_phone: 'Telèfon de contacte',
            website: 'Lloc web',
            services_csv: 'Serveis (separats per comes)',
            country: 'País',
            country_placeholder: 'p. ex. ES',
            last_reviewed: 'Darrera revisió',
            offboard: 'Donar de baixa',
            offboard_vendor: 'Donar de baixa proveïdor',
            offboard_reason: 'Motiu de la baixa',
            no_vendors: "No s'han trobat proveïdors",
            vendor_count: '{count} proveïdor(s)',
            all_dpa_status: 'Tots els estats DPA',
            all_risk_levels: 'Tots els nivells de risc',
            all_onboarding: 'Totes les incorporacions',
            fetch_error: 'Error. Si us plau, torneu-ho a provar.',
            save_error: "No s'ha pogut desar el proveïdor",
            offboard_error: "No s'ha pogut donar de baixa",
            reach: {
                action: 'Foreign reach',
                title: 'Foreign reach: {name}',
                intro: "An installed framework asks whether this vendor is within its foreign reach. A person records the answer and the reason; the vendor's country and processing locations do not decide it.",
                none_declared: 'No installed framework asks a foreign-reach question.',
                not_determined: 'Not determined',
                within: 'Within reach',
                not_within: 'Not within reach',
                decided: 'Recorded {date}',
                related: 'Provisions that act on a party within it: {list}',
                question: 'Question',
                answer: 'Is this vendor within the reach?',
                rationale: 'Reason',
                citation: 'Citation (optional)',
                save: 'Record determination',
                saved: 'Foreign-reach determination recorded.',
                load_error: 'The foreign-reach questions could not be read.',
                retry: 'Reload',
                retry_sr: 'the foreign-reach questions',
                loading: 'Reading the foreign-reach questions…',
                retry_busy: 'Reloading…',
                stale: 'This vendor was changed by someone else. The latest version has been loaded; review it and record the determination again.',
                stale_unreadable: 'This vendor was changed by someone else, and the latest version could not be read. Close this dialog and open it again before recording.',
                save_error: 'The determination could not be recorded. Try again.',
                answer_required: 'Choose an answer and give a reason.',
                country_context: 'Country on record: {country}',
                read_only: 'Recording a determination needs permission to edit vendors.',
            },
        },
        vendor_types: {
            processor: 'Encarregat del Tractament',
            sub_processor: 'Subencarregat',
            controller: 'Responsable del Tractament',
            joint_controller: 'Corresponsable del Tractament',
        },
        onboarding_statuses: {
            pending_review: 'Pendent de Revisió',
            approved: 'Aprovat',
            active: 'Actiu',
            suspended: 'Suspès',
            offboarded: 'Donat de Baixa',
        },
        dpa_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            not_required: 'No Requerit',
            required_missing: 'Requerit Absent',
            draft: 'Esborrany',
            under_review: 'En Revisió',
            signed: 'Signat',
            expired: 'Vençut',
        },
        risk_levels: {
            critical: 'Crític',
            high: 'Alt',
            medium: 'Mitjà',
            low: 'Baix',
        },
        approval_statuses: {
            pending: 'Pendent',
            approved: 'Aprovat',
            rejected: 'Rebutjat',
            expired: 'Vençut',
        },
        approval_action_types: {
            vendor_onboarding: 'Alta de Proveïdor',
            vendor_offboarding: 'Baixa de Proveïdor',
            cross_border_acceptance: 'Acceptació Transfronterera',
            playbook_activation: 'Activació del Playbook',
            drift_accept_risk: 'Desviació Risc Acceptat',
            activity_activation: "Activació d'Activitat",
        },
        drift_check_types: {
            dpa_expiry: 'Venciment del DPA',
            dpa_missing: 'DPA Absent',
            review_overdue: 'Revisió Endarrerida',
            cross_border_gap: 'Buit Transfronterer',
            sub_processor_chain: 'Cadena de Subencarregats',
            risk_assessment_overdue: 'Avaluació de Risc Endarrerida',
            retention_policy_missing: 'Política de Retenció Absent',
            evidence_expiring: 'Evidència a Punt de Vèncer',
        },
        drift_statuses: {
            open: 'Obert',
            acknowledged: 'Confirmat',
            in_progress: 'En Progrés',
            resolved: 'Resolt',
            accepted_risk: 'Risc Acceptat',
        },
        drift_entity_types: {
            vendor: 'Proveïdor',
            system: 'Sistema',
            data_flow: 'Flux de Dades',
            processing_activity: 'Activitat de Tractament',
            evidence_item: "Element d'Evidència",
        },
        playbook_statuses: {
            draft: 'Esborrany',
            active: 'Actiu',
            archived: 'Arxivat',
        },
        playbook_action_types: {
            delete_data: 'Eliminar Dades',
            verify_deletion: 'Verificar Eliminació',
            notify_vendor: 'Notificar Proveïdor',
            confirm_receipt: 'Confirmar Recepció',
            document_evidence: 'Documentar',
            other: 'Altre',
        },
        playbook_target_types: {
            system: 'Sistema',
            vendor: 'Proveïdor',
        },
        dpa_chain_statuses: {
            complete: 'Complet',
            incomplete: 'Incomplet',
            unknown: 'Desconegut',
        },
        sub_processor_statuses: {
            active: 'Actiu',
            terminated: 'Finalitzat',
        },
        evidence_types: {
            dpa_agreement: 'Acord DPA',
            risk_assessment: 'Avaluació de Risc',
            audit_report: "Informe d'Auditoria",
            certification: 'Certificació',
            deletion_confirmation: "Confirmació d'Eliminació",
            training_record: 'Registre de Formació',
            incident_report: "Informe d'Incident",
            document: 'Document',
            screenshot: 'Captura de Pantalla',
            policy: 'Política',
            contract: 'Contracte',
            assessment: 'Avaluació',
            correspondence: 'Correspondència',
            other: 'Altre',
        },
        linkable_entity_types: {
            vendor: 'Proveïdor',
            transfer_mechanism: 'Mecanisme de Transferència',
            playbook: 'Llibre de procediments',
            execution: 'Execució',
            system: 'Sistema',
            activity: 'Activitat',
        },
        subprocessors: {
            title: 'Relacions amb subencarregats',
            new: 'Nou vincle',
            vendor: 'Proveïdor',
            subprocessor: 'Subencarregat',
            relationship_type: 'Tipus de relació',
            countries: 'Països',
            countries_csv: 'Països (separats per comes)',
            dpa_chain: 'Cadena DPA',
            dpa_chain_status: 'Estat de la cadena DPA',
            no_processors: "No s'han trobat encarregats.",
            no_subprocessors: "No s'han trobat subencarregats.",
            processor_vendor: 'Proveïdor encarregat',
            select_processor: 'Seleccionar encarregat',
            select_vendor: 'Seleccionar proveïdor',
            services: 'Serveis',
            sub_processor_vendor_id: 'ID del proveïdor subencarregat',
            terminate: 'Finalitzar',
            confirm_terminate: 'Esteu segur que voleu finalitzar aquesta relació?',
            create_error: "No s'ha pogut crear el vincle.",
            fetch_error: "No s'han pogut obtenir els subencarregats.",
            processor: 'Encarregat',
            services_csv: 'Serveis (separats per comes)',
            terminate_error: "No s'ha pogut finalitzar.",
            version_history_heading: 'Canvi de versions del subprocessador',
            change_tracking_note: "Tots els canvis de relació de subprocessador es versionen. Cada modificació registra l'estat abans/després de la pista d'auditoria.",
            history_retention_note: "L'historial de canvis es conserva durant tot el període de conservació de dades. Reviseu els registres de canvis mitjançant la vista de detalls del proveïdor.",
            vendor_risk_cross_view_heading: 'Registre de subprocessadors (Vendor Risk)',
            vendor_risk_cross_view_source: 'Font: Registre de Vendor Risk',
            vendor_risk_cross_view_empty: 'Sense entrades al registre de Vendor Risk.',
            vendor_risk_cross_view_load_error: 'The Vendor Risk sub-processor register could not be read.',
            partial_load: 'The sub-processors of {count} processor(s) could not be read, so the register below is incomplete.',
        },
        cross_border: {
            title: 'Informe de governança transfronterera',
            flows: 'Fluxos transfronterers',
            transfers: 'Transferències transfrontereres',
            detected_gaps: 'Llacunes detectades',
            safeguards: 'Garanties',
            cross_border_vendors: 'Proveïdors transfronterers',
            destination: 'Destinació',
            no_cross_border_vendors: "No s'han trobat proveïdors transfronterers.",
            no_safeguard: 'Sense garantia',
            gap_needs_redetermination: 'Recorded mechanism needs re-determining',
            gap_unverified: 'Recorded mechanism not yet verified',
            gap_recorded_mechanism: 'Recorded: {mechanism}',
            redetermine_mechanism: 'Re-determine',
            redetermine_mechanism_title: "Re-determine the vendor's transfer mechanism",
            redetermine_mechanism_intro: '{vendor} records the transfer mechanism "{mechanism}" for {source} to {destination}, and no installed framework declares it as it stands. Choose a mechanism an installed framework declares. Only the mechanism is saved.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            processing_locations: 'Ubicacions de tractament',
            export_error: "No s'ha pogut exportar l'informe.",
            export_pdf: 'Exportar PDF',
            export_success: 'Informe exportat correctament.',
            exporting: 'Exportant...',
            fetch_error: "No s'han pogut obtenir les dades transfrontereres.",
            source: 'Origen',
            pdpl_p2_heading: 'PDPL-P2 Compliment de la transferència transfronterera',
            dossier_prep_note: "Elaboració de dossiers: reunir l'avaluació d'impacte, la base legal i la documentació de salvaguarda abans de la presentació.",
            filing_tracking_note: "Seguiment de la presentació: supervisar l'estat de la presentació amb l'Autoritat (PDPC) des de la sol·licitud inicial fins a l'aprovació.",
            supplement_loop_note: "Bucle de suplement: respondre a les sol·licituds d'informació de l'Autoritat i tornar a presentar els expedients esmenats segons sigui necessari.",
            official_forms_note: 'Official forms: choose the official form this filing uses in the Form type selector when you create it.',
            vendor_search_label: 'Cerca de proveïdor',
            vendor_search_placeholder: 'Cerca per nom de proveïdor…',
            jurisdiction_filter_label: 'Jurisdicció',
            filings_section_heading: 'Presentacions transfrontereres',
            filings_empty: 'Encara no hi ha cap presentació. Creeu-ne un per iniciar un dossier.',
            filings_create_button: 'Nova fitxa',
            filings_table_id: 'ID de presentació',
            filings_table_jurisdiction: 'Jurisdicció',
            filings_table_type: 'Tipus de formulari',
            filings_table_status: 'Estat',
            filings_table_updated: 'Actualitzat',
            filings_table_open: 'Obert',
            filings_detail_title: 'Arxiu transfronterer',
            filings_back_to_list: 'Torna a Transfronterera',
            filings_status_heading: 'Cicle de vida',
            filings_evidence_heading: 'Evidència',
            filings_history_heading: "Pista d'auditoria",
            filings_field_filing_id: 'ID de presentació',
            filings_field_jurisdiction: 'Jurisdicció',
            filings_field_type: 'Tipus de formulari',
            filings_field_status: 'Estat',
            filings_field_reference: "Referència de l'autoritat",
            filings_field_mps_receipt: 'MPS rebut',
            filings_field_submitted_at: 'Presentat a',
            filings_field_acknowledged_at: 'Reconegut a',
            filings_field_archived_at: 'Arxivat a',
            filings_field_version: 'Versió',
            filings_field_notes: 'Notes',
            filings_action_advance: 'Estat avançat',
            filings_action_reject: 'Rebutja',
            filings_action_archive: 'Arxiu',
            filings_action_submit: "Mark s'ha enviat",
            filings_action_acknowledge: 'Mark va reconèixer',
            filings_action_resume_draft: 'Reprèn com a esborrany',
            filings_status_draft: 'Esborrany',
            filings_status_assessed: 'Avaluat',
            filings_status_pending_review: 'Pendent de revisió',
            filings_status_ready_to_file: 'A punt per arxivar',
            filings_status_submitted: 'Presentat',
            filings_status_acknowledged: 'Reconegut',
            filings_status_rejected: 'Rebutjat',
            filings_status_archived: 'Arxivat',
            filings_type_none_installed: 'No fileable form is installed for this organisation',
            filings_new_dialog_title: 'Nova presentació transfronterera',
            filings_new_form_jurisdiction: 'Jurisdicció',
            filings_new_form_type: 'Tipus de formulari',
            filings_new_form_notes: 'Notes (opcional)',
            filings_new_submit: 'Crear',
            filings_new_cancel: 'Cancel·la',
            filings_reason_label: 'Raó',
            filings_reference_label: "Número de referència de l'autoritat",
            filings_mps_receipt_label: 'MPS referència del rebut',
            filings_create_error: "No s'ha pogut crear l'arxiu.",
            filings_create_success: 'Arxiu creat.',
            filings_update_error: "No s'ha pogut actualitzar l'arxiu.",
            filings_update_success: 'Arxiu actualitzat.',
            filings_fetch_error: "No s'han pogut recuperar els fitxers.",
            filings_archive_confirm: 'Arxiu aquest fitxer? Això no es pot desfer.',
            filings_status_rejected_detour: 'La presentació es va rebutjar després de la revisió. Reprèn com a esborrany per reiniciar el flux.',
            recent_exports_heading: 'Exportacions recents',
            recent_exports_empty: 'Encara no hi ha exportacions. Feu clic a "Exporta PDF" per generar-ne un.',
            filings_forms_empty: 'No regulatory forms generated yet. Use the Forms module to generate the required outputs for this filing.',
            filings_forms_heading: 'Regulatory Forms',
            filings_type_placeholder: 'Select a form type…',
        },
        drift: {
            title: 'Monitor de desviació de configuració',
            drift_type: 'Tipus de desviació',
            severity: 'Gravetat',
            detected_at: 'Detectat el',
            resolve: 'Resoldre',
            all_check_types: 'Tots els tipus de verificació',
            all_entity_types: "Tots els tipus d'entitat",
            event_count: "Recompte d'esdeveniments",
            accept_risk: 'Acceptar risc',
            acknowledge: 'Confirmar',
            assign_error: "No s'ha pogut assignar.",
            assign_event: 'Assignar',
            assigned: 'Assignat',
            enter_resolution_notes: 'Introduïu notes de resolució...',
            enter_user_id: "Introduïu l'ID d'usuari...",
            entity: 'Entitat',
            fetch_error: "No s'han pogut obtenir els esdeveniments de desviació.",
            no_events: "No s'han trobat esdeveniments de desviació.",
            resolution_notes: 'Notes de resolució',
            update_status_error: "No s'ha pogut actualitzar l'estat.",
        },
        scores: {
            title: 'Tauler de puntuació de compliment',
            overall: 'Puntuació global de compliment',
            breakdown: 'Desglossament per mòdul',
            trend: 'Tendència històrica',
            module: 'Mòdul',
            score: 'Puntuació',
            grade: 'Qualificació',
            calculated_at: 'Calculat el',
            generate_board_report: 'Generar informe per al consell',
            lang_english: 'Anglès',
            lang_vietnamese: 'Vietnamita',
            fetch_error: "No s'han pogut obtenir les puntuacions de compliment.",
            generate_report: 'Generar informe',
            generating: 'Generant...',
            recommendations: 'Recomanacions',
            report_error: "No s'ha pogut generar l'informe.",
            factor_owner_assigned: 'Propietari assignat',
            factor_data_categories_linked: 'Categories de dades vinculades',
            factor_purposes_linked: 'Finalitats vinculades',
            factor_data_flows_documented: 'Fluxos de dades documentats',
            factor_processing_activity_linked: 'Activitat de tractament vinculada',
            factor_dpa_status: 'Estat del DPA',
            factor_retention_policy_linked: 'Política de retenció vinculada',
            factor_last_reviewed: 'Última revisió dins del llindar',
            factor_risk_assessment_current: 'Avaluació de riscos actualitzada',
            factor_sub_processors_documented: 'Subencarregats documentats',
            factor_cross_border_safeguards: 'Salvaguardes transfrontereres',
            factor_contact_info_complete: 'Informació de contacte completa',
            factor_onboarding_approved: 'Incorporació aprovada',
            factor_avg_system_score: 'Puntuació mitjana del sistema',
            factor_avg_vendor_score: 'Puntuació mitjana del proveïdor',
            factor_processing_activity_coverage: 'Cobertura de les activitats de tractament',
            factor_ropa_completeness: 'Completesa del RoPA',
            factor_open_drift_events: 'Esdeveniments de desviació oberts',
        },
        playbooks: {
            title: "Guies d'actuació per a supressió",
            new: 'Nova guia',
            edit: 'Editar guia',
            steps: 'Passos',
            add_step: 'Afegir pas',
            remove_step: 'Eliminar pas',
            data_categories_csv: 'Categories de dades (separats per comes)',
            estimated_duration: 'Durada estimada',
            estimated_duration_label: 'Durada estimada',
            evidence_description: "Descripció de l'evidència",
            no_playbooks: "No s'han trobat guies.",
            playbook_count: 'Nombre de guies',
            requires_evidence: 'Requereix evidència',
            step_label: 'Etiqueta del pas',
            target_name: "Nom de l'objectiu",
            target_type: "Tipus d'objectiu",
            action_type: "Tipus d'acció",
            archive_error: "No s'ha pogut arxivar.",
            confirm_archive: 'Esteu segur que voleu arxivar aquesta guia?',
            data_categories: 'Categories de dades',
            days: 'dies',
            fetch_error: "No s'han pogut obtenir les guies.",
            sla_days: 'SLA (dies)',
            target_id: "ID de l'objectiu",
            template_class_heading: 'Classificació de plantilles',
            official: 'Oficial',
            internal: 'Interna',
            classification: 'Classificació',
            official_note: 'Oficial: plantilles obligatòries per reglament alineades amb PDPL, GDPR o requisits específics del sector.',
            internal_note: "Intern: procediments específics de l'organització no derivats de plantilles normatives.",
            active_executions_heading: 'Execucions actives',
            no_active_executions: 'No hi ha execucions actives.',
            playbook_label: 'Manual',
            ticket_id_label: 'Tiquet',
            progress_label: 'Progrés',
            actions_label: 'Accions',
            view_steps_label: 'Veure passos',
            execution_detail_title: "Detall de l'execució",
            playbook_missing: 'Manual no trobat.',
            evidence_required_label: 'Evidència requerida',
            upload_evidence_label: 'Puja evidència',
            mark_complete_label: 'Marcar com a completat',
            evidence_required_toast: 'Cal adjuntar evidència abans de completar.',
            evidence_blocking_message: "Adjunta un fitxer d'evidència per continuar.",
            step_completed_toast: 'Pas completat.',
            executions_fetch_error: "No s'han pogut carregar les execucions.",
            step_evidence_default_name: "Evidència del pas d'execució",
            attached_evidence_label: 'Evidències adjuntes',
        },
        approvals: {
            // Sprint 63.30.0 (#485) — approval expiry.
            expiry_setting_heading: 'Approval expiry',
            expiry_setting_label: 'Approval requests expire after (days)',
            expiry_setting_hint: 'Whole days from {min} to {max}. Applies to requests made after the change. A request still undecided at its expiry is marked Expired and its requester is told.',
            expiry_setting_value: 'Approval requests expire after {days} days.',
            expiry_setting_save: 'Save expiry',
            expiry_setting_saved: 'Approval expiry set to {days} days',
            expiry_setting_load_failed: 'The approval expiry setting could not be read.',
            expiry_setting_save_failed: 'The approval expiry setting could not be saved.',
            expires_on: 'Expires {date}',
            expired_undecided_note: 'Past its expiry; it can no longer be decided.',
            already_decided: 'This request has already been decided. The list has been refreshed to show its decision.',
            expired_refused: 'This request is past its expiry and can no longer be decided. The list has been refreshed.',
            title: "Fluxos d'aprovació",
            new: 'Nova aprovació',
            edit: 'Editar aprovació',
            request_type: 'Tipus de sol·licitud',
            requester: 'Sol·licitant',
            approve: 'Aprovar',
            reject: 'Rebutjar',
            justification: 'Justificació',
            all_action_types: "Tots els tipus d'acció",
            decided_by: 'Decidit per',
            no_approvals: "No s'han trobat aprovacions.",
            approval_count: "Nombre d'aprovacions",
            approval_notes_optional: 'Notes (opcional)',
            approve_error: "No s'ha pogut aprovar.",
            approve_request: 'Aprovar sol·licitud',
            decision: 'Decisió',
            entity: 'Entitat',
            fetch_error: "No s'han pogut obtenir les aprovacions.",
            reject_error: "No s'ha pogut rebutjar.",
            reject_request: 'Rebutjar sol·licitud',
            rejection_notes_required: 'Notes de rebuig obligatòries',
            rejection_notes_required_msg: 'Si us plau, indiqueu el motiu del rebuig.',
            requested_at: 'Sol·licitat el',
            decision_rules_heading: 'How decisions are recorded',
            decision_rule_second_person: 'A request is decided by someone other than the person who made it. You cannot approve your own request.',
            decision_rule_notes: 'Rejecting needs a written reason. Approving can carry notes, and either decision can attach supporting evidence.',
            decision_rule_scope: 'A decision is recorded here and in the governance audit trail. It does not carry out the action the request is about.',
            own_request_note: 'Your request. Another person decides it.',
            supporting_evidence_label: 'Evidència de suport (opcional)',
            decision_evidence_name: 'Evidència de decisió',
        },
        evidence: {
            title: 'Evidència de governança',
            new: 'Nova evidència',
            link_to: 'Vincular a',
            entity_id_placeholder: "ID de l'entitat",
            file_path_placeholder: '/ruta/al/fitxer',
            evidence_label: "Etiqueta de l'evidència",
            file_path: 'Camí del fitxer',
            source_mode_label: "Font de l'evidència",
            source_mode_file: 'Carrega fitxer',
            source_mode_url: 'Enllaça URL externa',
            attachment_label: 'Fitxer adjunt',
            item_count: "Nombre d'elements",
            link_button: 'Vincular evidència',
            confirm_delete: 'Esteu segur que voleu eliminar aquesta evidència?',
            enter_entity_id: "Introduïu l'ID de l'entitat...",
            entity_id: "ID de l'entitat",
            entity_type: "Tipus d'entitat",
            expires: 'Venç',
            expires_at: 'Venç el',
            fetch_error: "No s'han pogut obtenir les evidències.",
            link_error: "No s'ha pogut vincular l'evidència.",
            no_evidence: "No s'han trobat evidències.",
            tags_csv: 'Etiquetes (separades per comes)',
            uploaded: 'Carregat',
            url: 'URL',
            framework_tag_heading: 'Evidència etiquetada amb marc',
            framework_tag_note: "Cada registre d'evidència captura codi de marc, actor i marca de temps. Filtreu per marc per aïllar els registres rellevants per a l'auditoria.",
            framework_code: 'Codi Marc',
            filter_by_framework: 'Filtra per codi marc...',
            actor: 'Actor',
        },
        certification: {
            not_applicable: 'No aplicable',
            not_certified: 'No certificat',
            pending: 'Pendent',
            certified: 'Certificat',
            revoked: 'Revocat',
        },
        onboarding_pipeline: {
            title: "Pipeline d'incorporació",
            new_workflow: "Nou flux d'incorporació",
            vendor_id: 'ID de proveïdor',
            stages: 'Etapes',
            current_stage: 'Etapa actual',
            advance: "Avançar l'etapa",
            advance_success: 'Etapa avançada amb èxit',
            workflow_status: 'Estat del flux',
            initiated: 'Iniciat',
            in_progress: 'En curs',
            completed: 'Completat',
            cancelled: 'Cancel·lat',
            no_workflows: "No s'han trobat fluxos d'incorporació",
            stages_placeholder: 'Etapa 1, Etapa 2, Etapa 3',
            assigned_to: 'Assignat a',
            notes: 'Notes',
            pdpl_lifecycle_heading: 'PDPL Cicle de vida del proveïdor — Incorporació',
            due_diligence_note: "L'etapa de diligència deguda és obligatòria abans d'incorporar qualsevol processador sota PDPL. Verificar l'abast del tractament de dades, la base legal i les mesures de seguretat.",
            contract_review_note: 'La revisió del contracte ha de confirmar les clàusules DPA, els límits de retenció de dades i les restriccions del subprocessador.',
            create_dpa_review_cta: 'Crea revisió DPA',
            dpa_review_linked: 'Revisió DPA vinculada',
        },
        dpa_review: {
            title: 'Revisions de DPA',
            new_review: 'Nova revisió de DPA',
            review_type: 'Tipus de revisió',
            findings: 'Troballes',
            outcome: 'Resultat',
            complete_review: 'Completar revisió',
            complete_success: 'Revisió completada amb èxit',
            link_assessment: 'Vincular avaluació de seguretat',
            link_success: 'Avaluació vinculada amb èxit',
            assessment_id: "ID d'avaluació",
            combined_title: 'Avaluació combinada',
            no_reviews: "No s'han trobat revisions de DPA",
            telecom_disclosure_heading: 'Govern de la divulgació de telecomunicacions',
            disclosure_trigger_note: "Les revisions activades per la divulgació capturen esdeveniments d'intercanvi de dades dels subscriptors de telecomunicacions segons la Llei de telecomunicacions de VN.",
            telecom_t2_note: "Cal l'aprovació del nivell TELECOM-T2 abans de revelar les dades dels subscriptors a les autoritats.",
            quarterly_log_note: "Utilitzeu el registre trimestral per documentar tots els esdeveniments de divulgació i mantenir una pista d'auditoria contínua.",
        },
        periodic_review: {
            title: 'Revisions periòdiques',
            new_review: 'Nova revisió periòdica',
            review_type: 'Tipus de revisió',
            due_date: 'Data de venciment',
            next_review: 'Propera data de revisió',
            trigger_reassessment: 'Desencadenar reavaluació',
            reassessment_reason: 'Motiu de reavaluació',
            reassessment_success: 'Reavaluació desencadenada amb èxit',
            complete_success: 'Revisió completada amb èxit',
            scheduled: 'Programada',
            in_progress: 'En curs',
            completed: 'Completada',
            overdue: 'Vençuda',
            material_change: 'Canvi significatiu',
            no_reviews: "No s'han trobat revisions periòdiques",
            mark_complete: 'Marcar com a completat',
            pdpl_periodic_heading: 'PDPL Cicle de vida del proveïdor — Revisions periòdiques',
            scheduled_review_note: "Les revisions periòdiques s'han de programar com a mínim anualment per a tots els processadors actius. Les revisions vençudes activen alertes de compliment.",
            material_change_note: 'Els canvis materials en el processament de dades requereixen una reavaluació immediata, independentment del calendari.',
            overdue_badge: 'En retard',
            overdue_aria: 'Revisió en retard',
            only_overdue_filter: 'Només en retard',
        },
        offboarding: {
            title: 'Desvinculació de proveïdor',
            initiate: 'Iniciar desvinculació',
            initiate_success: 'Desvinculació iniciada amb èxit',
            reason: 'Motiu de desvinculació',
            data_return_required: 'Devolució de dades requerida',
            verify_data: 'Verificar devolució de dades',
            verify_success: 'Devolució de dades verificada amb èxit',
            sign_off: 'Aprovar',
            sign_off_success: 'Desvinculació aprovada amb èxit',
            status_initiated: 'Iniciada',
            status_data_pending: 'Devolució de dades pendent',
            status_data_verified: 'Devolució de dades verificada',
            status_completed: 'Completada',
            status_cancelled: 'Cancel·lada',
            task_id: 'ID de tasca',
            no_tasks: "No s'han trobat tasques de desvinculació",
            evidence_id: "ID d'evidència",
            pdpl_offboarding_heading: 'PDPL Cicle de vida del proveïdor: baixa i supressió',
            deletion_capture_note: "L'exclusió ha de capturar la confirmació d'eliminació de dades del venedor. Verifiqueu la devolució de dades abans de tancar la sessió.",
            evidence_attachment_note: "Adjunteu proves de supressió (certificats, captures de pantalla, confirmacions del proveïdor) per completar el registre d'exclusió.",
            verify_mode_label: 'Mètode de verificació',
            verify_mode_upload: 'Puja nou fitxer',
            verify_mode_link: 'Enllaça evidència existent',
            verify_mode_none: 'No hi ha dades a retornar',
            upload_data_return_evidence: 'Puja evidència de retorn de dades',
            no_data_justification_label: 'Justificació',
            no_data_justification_placeholder: 'Explica per què no hi ha dades a retornar',
            no_data_justification_hint: 'Mínim 20 caràcters',
            upload_required_toast: 'Adjunta un fitxer per continuar',
            evidence_link_required_toast: 'Selecciona una evidència per continuar',
            no_data_justification_required_toast: 'Proporciona una justificació de 20 caràcters mínim',
            data_return_evidence_name: 'Evidència de retorn de dades',
            verify_before_signoff: 'Verify data return before sign-off',
        },
        dpa_review_types: {
            initial: 'Inicial',
            annual: 'Anual',
            renewal: 'Renovació',
            incident_triggered: 'Per incident',
        },
        periodic_review_types: {
            initial: 'Inicial',
            annual: 'Anual',
            renewal: 'Renovació',
            incident_triggered: 'Per incident',
            material_change: 'Canvi material',
            disclosure_triggered: 'Per divulgació',
            quarterly_log: 'Registre trimestral',
        },
        dpa_review_statuses: {
            pending: 'Pendent',
            in_progress: 'En curs',
            completed: 'Completat',
            cancelled: 'Cancel·lat',
        },
        onboarding_stage_names: {
            due_diligence: 'Diligència deguda',
            contract_review: 'Revisió de contracte',
            security_assessment: 'Avaluació de seguretat',
            data_mapping: 'Mapatge de dades',
            approval: 'Aprovació',
            integration: 'Integració',
        },
        access_denied_title: 'Accés denegat',
        access_denied: 'No teniu permís per veure aquesta pàgina. Contacteu amb el vostre administrador.',
    },
    consent: {
        nav: {
            policies: 'Polítiques',
            purposes: 'Catàleg de finalitats',
            workflows: 'Fluxos de treball',
            snapshots: 'Instantànies CMP',
            change_awareness: 'Consciència de canvis',
            audit: 'Auditoria de consentiment',
            evidence: 'Consent Evidence',
            marketing_consent: 'Marketing Consent',
            marketing_suppression: 'Suppression Register',
            marketing_suppression_enforcement: 'Suppression Enforcement',
            marketing_sender_name_certificates: 'Sender-name Certificates',
        },
        gap_analysis: {
            nav_label: 'Anàlisi de buits',
            page_title: 'Anàlisi de la bretxa de consentiment',
            page_subtitle: 'Forseti checks your active consent purposes against the consent families of your installed frameworks, and suggests a remediation for each critical or high gap. Only installed families are analysed.',
            framework_picker_label: 'Marcs',
            org_flags_label: "Context de l'organització",
            flag_withdrawal: 'Té un mecanisme de retirada global documentat',
            flag_parental: 'Té un flux de consentiment parental per a subjectes menors de 16 anys',
            flag_cross_border: 'Té un registre transfronterer',
            run_label: "Executa l'anàlisi de les bretxes",
            running_label: 'Córrer…',
            requires_advisor_label: 'Requereix ai_advisor:permís de xat.',
            results_heading: 'Van sorgir buits ({count})',
            tokens_label: '≈ {count} fitxes',
            empty_label: "No s'han detectat buits per als marcs seleccionats.",
            remediation_label: 'Correcció suggerida:',
            dismiss_label: 'Descartar com a no aplicable',
            resolve_label: 'Resol',
            dismissed_label: 'Descartat com a no aplicable',
            resolved_label: 'Solucionat',
            // Sprint 62.27.0 gate E12.5 pass 2 — R-57: English until Phase 25.
            families_loading: 'Reading the installed frameworks…',
            families_load_error: 'The installed frameworks could not be read, so no family can be offered for analysis.',
            families_retry_subject: 'the installed frameworks',
            families_none: 'None of your installed frameworks belongs to a family this analysis reads, so there is nothing to analyse.',
            family_instruments: 'Installed: {codes}',
            analysed_label: 'Analysed: {families}',
            not_analysed_label: 'Not installed, so not analysed: {families}',
            nothing_analysed: 'Nothing was analysed: none of the selected families is installed, so no gap can be reported.',
            acknowledged_count_label: '{count} acknowledged finding(s), not counted as open gaps',
            run_failed: 'The gap analysis could not be run.',
            triage_failed: 'The decision could not be recorded.',
            purpose_scope: 'Purpose: {name}',
            org_wide_scope: 'Organisation-wide',
            attributes: {
                legal_basis_set: 'Lawful basis recorded',
                withdrawal_mechanism: 'Withdrawal mechanism',
                granular_per_purpose: 'Consent separable per purpose',
                parental_consent_for_minors: 'Consent for children',
                cross_border_filing: 'Cross-border transfer record',
                retention_period: 'Retention period',
                sensitive_data_ground_review: 'Sensitive-data ground review',
            },
            acknowledge_label: 'Acknowledge review',
            acknowledge_title: 'Acknowledge the sensitive-data ground review',
            acknowledge_description: 'Purpose: {name}. Record that you reviewed whether your installed frameworks set a condition for processing sensitive data for this purpose beyond its lawful basis and, if so, which. Later analyses show the finding as acknowledged until the purpose’s sensitivity or its recorded ground changes, or a framework family you did not review is installed.',
            acknowledge_note_label: 'Note (required)',
            acknowledge_note_hint: 'What you reviewed, and whether a condition is recorded on the purpose and, if so, which, with its article.',
            acknowledge_submit: 'Acknowledge',
            acknowledge_done: 'Review acknowledged',
            acknowledge_failed: 'The acknowledgement could not be recorded.',
            acknowledged_by: 'Acknowledged by {name} on {date}: {note}',
            acknowledgement_lapsed: 'An acknowledgement by {name} on {date} lapsed because {reasons}.',
            lapse_sensitivity_changed: 'the purpose’s sensitivity changed',
            lapse_basis_changed: 'its recorded ground changed',
            lapse_family_added: 'a family it did not review was installed: {families}',
            lapse_reason_joiner: ' and ',
        },
        import: {
            title: 'Importar fluxos de consentiment',
            nav_title: 'Importació massiva',
            description: 'Pugeu un fitxer CSV per importar massivament fluxos de treball de consentiment.',
            select_file: 'Seleccionar fitxer CSV',
            file_help: 'CSV amb capçaleres que coincideixin amb els camps del flux de treball.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            required_columns_help: 'Columnes obligatòries: Tipus de flux, Subjecte de dades, Canal.',
            file_too_large: 'Fitxer massa gran. La mida màxima és de 2MB.',
            preview: 'Vista prèvia',
            columns_detected: 'Columnes detectades: {count}',
            column_mapping: 'Assignació de columnes',
            import_button: 'Importar',
            import_complete: 'Importació completada',
            import_error: 'La importació ha fallat',
            result: 'Resultat de la importació',
            total: 'Total',
            imported: 'Importat',
            skipped: 'Omès',
            failed: 'Fallat',
            errors: 'Errors',
            error_row: 'Fila',
            error_field: 'Camp',
            error_message: 'Missatge',
            history: "Historial d'importació",
            no_history: "Sense historial d'importació.",
            history_file: 'Nom del fitxer',
            history_date: 'Data',
            create_profiles_label: 'Crear perfils de Subjecte de dades per als nous subjectes',
            create_profiles_help: 'Les importacions sense un perfil coincident crearan un automàticament. Desmarqueu per importar només fluxos de treball.',
            profiles_created: 'Perfils creats',
            profiles_matched: 'Perfils coincidents',
            subject_kind_help: 'Columnes opcionals: subject_kind, subject_email, subject_display_name, subject_phone, external_ref_key.',
            error_subject_invalid_email: 'Correu electrònic no vàlid a la columna subject_email.',
            error_subject_unknown_kind: 'Valor subject_kind desconegut. Esperat: email, visitor_uuid, employee_id, student_id, external_ref.',
            error_missing_external_ref_key: 'external_ref_key és obligatori quan subject_kind=external_ref',
        },
        policies: {
            title: 'Polítiques de consentiment',
            new: 'Nova política',
            edit: 'Editar política',
            name: 'Nom',
            version: 'Versió',
            policy_text: 'Text de la política',
            publish: 'Publicar',
            versions: 'Versions',
            hide_versions: 'Amagar versions',
            submit_for_review: 'Enviar per Revisar',
            suspend: 'Suspendre',
            retire: 'Retirar',
            jurisdictions: 'Jurisdiccions',
            jurisdictions_csv: 'Jurisdiccions (separades per comes)',
            approval_mode: "Mode d'aprovació",
            reviewer_ids: 'IDs de revisors (separats per comes)',
            tags_csv: 'Etiquetes (separades per comes)',
            effective_date: "Data d'efecte",
            review_date: 'Revisar Data',
            consent_model_statement: 'Declaració del model de consentiment',
            consent_model_placeholder: 'Declaració opcional del model de consentiment...',
            default_settings: 'Predeterminat Configuració (JSON)',
            changes: 'Canvis',
            fetch_error: 'Error en fetch consent policies. Si us plau, torneu-ho a intentar.',
            save_error: 'Error en save consentiment política. Si us plau, torneu-ho a intentar.',
            submit_review_error: 'Error en submit policy for review. Si us plau, torneu-ho a intentar.',
            activate_error: 'Error en activate policy. Si us plau, torneu-ho a intentar.',
            suspend_error: 'Error en suspend policy. Si us plau, torneu-ho a intentar.',
            retire_error: 'Error en retire policy. Si us plau, torneu-ho a intentar.',
            submit_review_success: 'Política enviada per a revisió.',
            activate_success: 'Política activada.',
            suspend_success: 'Política suspesa.',
            retire_success: 'Política retirada.',
            versions_error: 'Error en fetch policy versions. Si us plau, torneu-ho a intentar.',
            invalid_json: 'JSON no vàlid al camp de configuració predeterminada. Torneu-ho a intentar.',
            page_title: 'Polítiques de consentiment',
            version_updated: 'Actualitzat',
            version_initial: 'Versió inicial',
            json_format: 'Format JSON.',
            sensitive: 'Sensible',
            flag_sensitive: 'Sensible a la bandera',
            flag_sensitive_title: 'Marca la política com a sensible',
            require_enhanced_review: 'Requereix una revisió millorada',
            enhanced_review_success: "S'ha activat el requisit de revisió millorat.",
            return_to_draft: 'Return to draft',
            return_to_draft_hint: 'End this review round and make the policy editable again',
            return_to_draft_title: 'Return policy to draft',
            return_to_draft_body: "The decisions recorded in this review round are kept in the policy's approval history. The policy becomes a draft, to be edited and submitted for review again.",
            return_to_draft_reason: 'Reason (required)',
            return_to_draft_reason_required: 'Enter the reason for returning this policy to draft.',
            return_to_draft_success: 'Policy returned to draft.',
            return_to_draft_error: 'The policy could not be returned to draft.',
            in_review_locked_note: 'In review: the policy cannot be edited, flagged sensitive or routed to enhanced review. Return it to draft to change it.',
            refusal_policy_in_review: 'This policy is in review and cannot be changed. Return it to draft to change it.',
            refusal_policy_not_in_review: 'This policy is no longer in review. The list has been refreshed.',
            refusal_policy_changed_concurrently: 'The policy changed while this was being saved. The list has been refreshed; check the policy and try again.',
            refusal_not_a_named_reviewer: 'Only the reviewers this policy names can decide it.',
            select_categories: "Seleccioneu les categories de dades sensibles de l'PDPL aplicables a l'article 2:",
            no_policies: 'No s’han trobat polítiques de consentiment. Creeu la vostra primera política per començar.',
            approval_success: 'Política aprovada.',
            reviewers_label: 'Revisors',
            enhanced_review_title: 'Revisió ampliada requerida',
            enhanced_review_badge: 'Revisió ampliada requerida',
            enhanced_review_status_pending: 'Revisió pendent',
            enhanced_review_status_approved: 'Revisió aprovada',
            enhanced_review_status_rejected: 'Revisió rebutjada',
            enhanced_review_reviewed_by: 'Revisat per',
            enhanced_review_notes: 'Notes de revisió',
            enhanced_review_approve: 'Aprovar revisió',
            enhanced_review_reject: 'Rebutjar revisió',
            enhanced_review_notes_required: 'Les notes de revisió són obligatòries',
            enhanced_review_approved: 'Revisió ampliada aprovada',
            enhanced_review_rejected: 'Revisió ampliada rebutjada — política restablerta a esborrany',
            enhanced_review_reset_to_draft: 'Revisió ampliada rebutjada — política restablerta a esborrany',
            enhanced_review_reject_warning: "El rebuig restablirà aquesta política a l'estat Esborrany. El DPO ha de tornar a enviar-la per a revisió després de resoldre els problemes.",
            submitted_modal_title: 'Enviat per a revisió ampliada',
            submitted_modal_body: "Aquesta política ha estat enviada. Un revisor Legal/Privadesa ha d'aprovar-la abans que pugui ser activada.",
            activate_hint: 'Make this policy effective',
            approval_comments: 'Comments (optional)',
            approve_title: 'Approve Policy',
            flag_sensitive_hint: 'Mark as involving sensitive data',
            reactivate_hint: 'Resume this suspended policy',
            reject_hint: 'Decline this policy and return it to draft',
            reject_title: 'Reject Policy',
            rejection_reason: 'Rejection reason (required)',
            rejection_success: 'Policy rejected',
            require_enhanced_review_hint: 'Route to a second-level reviewer',
            retire_hint: 'Permanently retire this policy',
            review_context_description: 'Confirm the policy scope and review controls before saving.',
            review_context_hint: 'Reviewers, jurisdictions, dates, and consent defaults determine how this policy is governed.',
            review_context_title: 'Review context',
            sensitive_filter: 'Sensitive',
            suspend_hint: 'Temporarily pause this policy',
            suspend_reason_label: 'Suspension reason (required)',
            suspend_title: 'Suspend Policy',
            type_content_edit: 'Content Edit',
            type_status_change: 'Status Change',
            reactivate: 'Re-activate',
            no_versions: 'No version history yet.',
            approval_mode_hint: 'All: every named reviewer must approve, and only a named reviewer may decide. Any: one approval decides. In both, one rejection rejects the policy.',
            refusal_use_enhanced_review: 'This policy is routed to enhanced review, so it is decided there, not through the standard approval. The list has been refreshed.',
            refusal_reviewer_cannot_decide_named: 'These named reviewers could never decide this policy: {names}. Every named reviewer must be an active member of the organisation who holds the permission to approve consent policies, or, for a policy routed to enhanced review, the permission to carry out enhanced review.',
            refusal_reviewer_cannot_decide_none: 'This policy names no reviewer, and every named reviewer must approve it. Name at least one reviewer before submitting.',
            refusal_invalid_status_transition: 'This policy is no longer in a status that allows this. The list has been refreshed.',
            approval_waiting: 'Your approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            enhanced_review_waiting: 'Your enhanced-review approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            decision_recorded_in_review: 'Your decision is recorded. The policy is still in review.',
            decision_recorded: 'Your decision is recorded.',
            review_history: 'Review history',
            review_history_current: 'Decisions in the current review round',
            review_history_earlier: 'Earlier review rounds',
            review_history_empty: 'No review decision has been recorded for this policy yet.',
            history_col_reviewer: 'Reviewer',
            history_col_decision: 'Decision',
            history_col_review_kind: 'Review',
            history_col_decided_at: 'Decided',
            history_col_notes: 'Notes',
            history_kind_standard: 'Standard approval',
            history_kind_enhanced: 'Enhanced review',
            history_decision_approved: 'Approved',
            history_decision_rejected: 'Rejected',
            history_col_ended_at: 'Round ended',
            history_col_outcome: 'How it ended',
            history_col_ended_by: 'Ended by',
            history_col_reason: 'Reason',
            history_col_round_decisions: 'Decisions in the round',
            history_ended_resubmitted: 'Submitted for review again',
            history_ended_returned: 'Returned to draft',
            history_no_decisions: 'No decision was recorded',
            history_decision_by: '{decision} by {name}',
        },
        sensitive_categories: {
            health: 'Dades de salut',
            biometric: 'Dades biometriques',
            political: 'Opinions politiques',
            ethnic: 'Origen etnic',
            genetic: 'Dades genetiques',
            criminal: 'Antecedents penals',
        },
        purposes: {
            title: 'Catàleg de finalitats',
            new: 'Nova finalitat',
            edit: 'Editar finalitat',
            name: 'Nom',
            legal_basis: 'Base jurídica',
            retention_days: 'Període de retenció (dies)',
            is_active: 'Actiu',
            filter_by_category: 'Filtra per categoria',
            policy_ids: 'IDs de polítiques (separats per comes)',
            ropa_activity_ids: "IDs d'activitats ROPA (separats per comes)",
            policies: 'Polítiques',
            deprecate: 'Deprecar',
            fetch_error: 'Error en fetch consent purposes. Si us plau, torneu-ho a intentar.',
            save_error: 'Error en save consent purpose. Si us plau, torneu-ho a intentar.',
            deprecate_error: 'Error en deprecate consent purpose. Si us plau, torneu-ho a intentar.',
            page_title: 'Catàleg de finalitats',
            no_purposes: 'No s’han trobat finalitats de consentiment. Creeu la vostra primera finalitat per començar.',
            category_personal_data: 'Dades personals',
            category_sensitive_data: 'Dades sensibles',
            category_health_data: 'Dades de salut',
            category_financial_data: 'Dades financeres',
            category_biometric_data: 'Dades biomètriques',
            category_children_data: 'Dades infantils',
            category_location_data: "Dades d'ubicació",
            category_communications_data: 'Dades de comunicacions',
            category_other: 'Altres',
        },
        workflows: {
            title: 'Fluxos de treball de consentiment',
            new: 'Nou flux de treball',
            edit: 'Editar flux de treball',
            workflow_type: 'Tipus de flux de treball',
            user_id: "ID d'usuari",
            purpose_id: 'ID de finalitat',
            purpose_ids_csv: 'Finalitat ID (comma-separated)',
            reason: 'Motiu',
            process: 'Processar',
            data_subject_ref: 'Dades Titular Reference',
            data_subject: 'Dades Titular',
            channel: 'Canal',
            policy_id: 'Política ID',
            scope: 'Àmbit',
            legal_grounds: 'Bases legals',
            assessment: 'Avaluació',
            select_outcome: 'Seleccioneu Outcome',
            partially_approved: 'Aprovat parcialment',
            denied: 'Denegat',
            outcome_upheld: 'Mantingut',
            outcome_pending: 'Pendent',
            outcome_denied: 'Denegat',
            status_count: 'Recompte per estat',
            channel_filter: 'Filtrar per canal',
            add_action: '+ Acció',
            by_type: 'Per Tipus',
            by_status: 'Per Estat',
            downstream_actions: 'Accions posteriors',
            downstream_actions_for: 'Downstream Accions per {subject} ({type})',
            add_downstream_action: 'Afegir Downstream Action',
            assigned_to: 'Assignat A',
            fetch_error: 'Error en fetch consent workflows. Si us plau, torneu-ho a intentar.',
            dashboard_error: 'Error en fetch workflow dashboard. Si us plau, torneu-ho a intentar.',
            save_error: 'Error en save consent workflow. Si us plau, torneu-ho a intentar.',
            status_update_error: 'Error en update workflow status. Si us plau, torneu-ho a intentar.',
            assessment_error: 'Error en update workflow assessment. Si us plau, torneu-ho a intentar.',
            add_action_error: 'Error en add workflow action. Si us plau, torneu-ho a intentar.',
            update_action_error: 'Error en update workflow action. Si us plau, torneu-ho a intentar.',
            type_refusal: 'Rebuig',
            type_withdrawal: 'Retirada',
            type_restriction: 'Restricció',
            type_objection: 'Oposició',
            status_received: 'Rebut',
            status_processing: 'Tractament',
            status_under_assessment: 'En avaluació',
            status_implemented: 'Implementat',
            evidence_button: 'Evidència',
            evidence_modal_title: 'Evidència del flux de treball',
            no_evidence: "No s'adjunta cap prova a aquest flux de treball.",
            upload_evidence_label: 'Carregueu proves del flux de treball',
            completed_at: 'Completat a les',
            effective_date: "Data d'entrada en vigor",
            page_title: 'Fluxos de treball de consentiment',
            no_workflows: 'No s’han trobat fluxos de consentiment. Els fluxos es creen quan els interessats exerceixen els seus drets.',
            type_grant: 'Concessó',
            propagation_none: 'Cap',
            propagation_pending: 'Pendent',
            propagation_pending_warning: 'No es pot completar — la propagació de retirada encara està pendent',
            propagation_completed: 'Completat',
            propagation_partial: 'Parcial',
            propagation_incomplete_error: 'No es pot completar el flux de treball mentre la propagació de retirada estigui pendent',
            propagation_status: 'Estat de propagació',
        },
        snapshots: {
            title: 'Instantànies CMP',
            new: 'Nova instantània',
            label: 'Etiqueta',
            snapshot_date: 'Data de la instantània',
            purposes_count: 'Nombre de finalitats',
            compare: 'Comparar',
            snapshot_data: 'Dades de la instantània (JSON)',
            select_left: 'Seleccioneu Left Snapshot',
            select_right: 'Seleccioneu Right Snapshot',
            comparison_results: 'Resultats de comparació',
            field: 'Camp',
            left: 'Esquerra',
            right: 'Dreta',
            close_comparison: 'Tancar comparació',
            trigger: 'Activador',
            trigger_manual: 'Manual',
            trigger_policy_approval: 'Aprovació de polítiques',
            cmp_version: 'Versió de CMP',
            policy_version_id: 'Política Versió ID',
            banner_config: 'Configuració del banner (JSON)',
            active_purposes: 'Actiu Finalitats (JSON)',
            active_legal_bases: 'Bases jurídiques actives (JSON)',
            domain_scope: 'Àmbit del domini',
            domain_scope_csv: 'Àmbit del domini (separat per comes)',
            raw_config: 'Configuració en brut (JSON)',
            checksum: 'Suma de verificació',
            created: 'Creat',
            fetch_error: 'Error en fetch cmp snapshots. Si us plau, torneu-ho a intentar.',
            save_error: 'Error en save cmp snapshot. Si us plau, torneu-ho a intentar.',
            compare_error: 'Error en compare snapshots. Si us plau, torneu-ho a intentar.',
            page_title: 'Instantània de consentiment',
            json_format: 'Format JSON.',
            json_array_format: 'JSON format de matriu',
            manual_snapshot_note: 'Només per a administradors/desenvolupadors. En funcionament normal, les instantànies es creen automàticament mitjançant el webhook CMP o en aprovar polítiques. Enganxeu el JSON exportat del vostre proveïdor CMP (p.ex. OneTrust, Cookiebot).',
            banner_config_hint: "Enganxeu l'objecte de configuració del banner des de la vostra exportació CMP.",
            active_purposes_hint: "Array JSON d'objectes de finalitats actives copiats de la vostra exportació CMP.",
            active_legal_bases_hint: "Array JSON d'objectes de bases legals actives copiats de la vostra exportació CMP.",
            raw_config_hint: "JSON de configuració brut complet del vostre CMP. Els altres camps JSON se n'extreuen. Una suma de verificació SHA-256 es calcula automàticament.",
            changed_field: 'Camp canviat:',
            no_snapshots: 'No s’han trobat instantànies CMP. Les instantànies es creen quan s’aproven polítiques o es generen manualment.',
            detail_title: 'Detall de la instantània',
            view_snapshot_detail: 'Veure el detall de la instantània',
            policy_version: 'Versió de la política',
            removed: 'Abans',
            added: 'Després',
        },
        change_awareness: {
            title: 'Consciència de canvis de consentiment',
            change_type: 'Tipus de canvi',
            entity_type: "Tipus d'entitat",
            entity_id: "ID de l'entitat",
            detected_at: 'Detectat el',
            acknowledge: 'Confirmar',
            new_alert: 'Nou alerta',
            new_alert_form: 'Nou Change Awareness Alert',
            total_open: 'Total Obrir',
            by_severity: 'Per Gravetat',
            snapshot_id: "ID d'instantània",
            update_status: 'Actualitzar Estat',
            resolution_notes: 'Notes de resolució',
            resolution_required: 'Notes de resolució (obligatòries)',
            resolution_required_msg: 'Les notes de resolució són obligatòries en resoldre un element.',
            status_open: 'Obrir',
            status_under_review: 'En revisió',
            status_resolved: 'Resolt',
            status_accepted: 'Acceptat',
            severity_informational: 'Informatiu',
            severity_requires_review: 'Requereix revisió',
            severity_policy_violation: 'Infracció de política',
            create_reconciliation: 'Crear una tasca de conciliació',
            reconciliation_created: 'Tasca de conciliació creada correctament.',
            reconciliation_error: "No s'ha pogut crear la tasca de conciliació. Si us plau, torna-ho a provar.",
            fetch_error: 'Error en fetch change awareness items. Si us plau, torneu-ho a intentar.',
            dashboard_error: 'Error en fetch change awareness dashboard. Si us plau, torneu-ho a intentar.',
            create_error: 'Error en create change awareness item. Si us plau, torneu-ho a intentar.',
            status_update_error: 'Error en update change awareness status. Si us plau, torneu-ho a intentar.',
            page_title: 'Consciència del canvi',
            no_items: 'No s’han trobat elements de consciència de canvis. Els elements es creen quan es detecten canvis en la configuració del consentiment.',
            affected_policies: 'Affected Policies',
            affected_purposes: 'Affected Purposes',
            estimated_affected_subjects: 'Estimated Affected Subjects',
            impact_scope: 'Impact Scope',
            affected_policies_short: 'Policies',
            affected_purposes_short: 'Purposes',
            affected_subjects_short: 'Subjects',
        },
        audit: {
            title: "Pista d'auditoria de consentiment",
            event_type: "Tipus d'esdeveniment",
            user_id: "ID d'usuari",
            timestamp: 'Marca de temps',
            details: 'Detalls',
            actor: 'Actor',
            filter_by_entity_type: "Filtra per tipus d'entitat",
            filter_by_action: 'Filtra per acció',
            fetch_error: 'Error en fetch consent audit trail. Si us plau, torneu-ho a intentar.',
            page_title: 'Auditoria de consentiment',
            export_csv: 'Exporta CSV',
            exporting: "S'està exportant...",
            export_success: "La pista d'auditoria s'ha exportat correctament.",
            export_error: "No s'ha pogut exportar la pista d'auditoria del consentiment. Si us plau, torna-ho a provar.",
            no_entries: 'No s’han trobat entrades d’auditoria. Els esdeveniments d’auditoria es registren automàticament quan canvien les dades de consentiment.',
            entity_policy: 'Política',
            entity_purpose: 'Finalitat',
            entity_workflow: 'Flux de treball',
            entity_consent_record: 'Registre de consentiment',
            entity_change_awareness: 'Consciència de canvis',
            entity_cmp_snapshot: 'Instantània CMP',
            action_created: 'Creat',
            action_updated: 'Actualitzat',
            action_status_changed: 'Estat canviat',
            action_drift_detected: 'Desviació detectada',
            action_snapshot_created: 'Instantània creada',
            action_approval_submitted: 'Aprovació enviada',
            action_archived: 'Arxivat',
            action_purpose_auto_mapped: 'Finalitat assignada automàticament',
            action_purpose_mapping_updated: 'Assignació de finalitat actualitzada',
            action_webhook_configured: 'Webhook configurat',
            entity_propagation: 'Propagació',
            entity_reconciliation: 'Reconciliació',
            entity_marketing_consent: 'Marketing consent',
            entity_marketing_suppression: 'Marketing suppression',
            entity_marketing_suppression_enforcement: 'Suppression enforcement check',
            action_propagation_confirmed: 'Propagació confirmada',
            action_propagation_failed: 'La propagació ha fallat',
            action_consent_captured: 'Consentiment capturat',
            action_reconciliation_created: 'Reconciliació creada',
            action_reconciliation_resolved: 'Reconciliació resolta',
            action_submitted_for_review: 'Enviat per a revisió',
            action_activated: 'Activat',
            action_suspended: 'Suspès',
            action_retired: 'Retirat',
            action_enhanced_review_required: 'Revisió millorada requerida',
            action_enhanced_review_approved: 'Revisió ampliada aprovada',
            action_enhanced_review_rejected: 'Revisió ampliada rebutjada — política restablerta a esborrany',
            action_consented: 'Consentit',
            action_consented_customize: 'Consentiment personalitzat',
            action_revoked: 'Revocat',
            action_rejected: 'Rebutjat',
        },
        web_records: {
            title: 'Registres de consentiment web',
            page_title: 'Registres de consentiment web',
            domain: 'Domini',
            visitor_id: 'Identificador de visitant',
            consent_action: 'Acció de consentiment',
            received_at: 'Rebut a les',
            admin_status: "Estat d'administrador",
            revoke: 'Revocar',
            reject: 'Rebutja',
            reason: 'Raó',
            reason_placeholder: "Motiu d'aquesta acció (opcional)",
            revoke_confirm: 'Vols revocar aquest registre de consentiment?',
            reject_confirm: 'Vols rebutjar aquest registre de consentiment?',
            override_success: "L'estat del registre s'ha actualitzat correctament.",
            override_error: "No s'ha pogut actualitzar l'estat del registre.",
            no_records: "No s'han trobat registres de consentiment web.",
            fetch_error: "No s'han pogut carregar els registres de consentiment web.",
            filter_by_domain: 'Filtra per domini',
            filter_by_action: 'Filtra per acció',
            active: 'Actius',
        },
        legal_bases: {
            all: 'Totes les bases jurídiques',
        },
        common_statuses: {
            suspended: 'Suspès',
            retired: 'Retirat',
            deprecated: 'Obsolet',
        },
        approval_modes: {
            all: 'All',
            any: 'Any',
        },
        cmp_integration: {
            title: 'Integració AesirX CMP',
            back_to_consent: 'Enrere a Consentiment',
            back_to_integration: 'Enrere a Integració',
            webhook_status: 'Webhook Estat',
            events_received: 'Esdeveniments rebuts',
            web_consent_records: 'Web Consentiment Registres',
            unmapped_purposes: 'Finalitats sense mapar',
            integration_management: 'Integració Gestió',
            webhook_configuration: 'Webhook Configuració',
            webhook_config_desc: "Configureu l'endpoint del webhook de CMP i gestioneu el secret HMAC.",
            purpose_mapping: 'Finalitat Mapeig',
            purpose_mapping_desc: 'Assigneu els identificadors de finalitat de CMP a les finalitats del catàleg de consentiment de GRC.',
            bulk_sync: 'Sincronització massiva',
            bulk_sync_desc: "Importeu dades històriques de consentiment i visualitzeu l'historial de sincronització.",
            consent_analytics: 'Anàlisi de consentiment web (últims 30 dies)',
            consent_grant_rate: 'Consentiment Grant Taxa',
            top_domain: 'Domini principal',
            records: 'registres',
            banner_interactions: 'Interaccions del bàner',
            consent_actions: 'Accions de consentiment',
            opt_in_trend: 'Tendència opt-in/opt-out',
            records_by_domain: 'Registres per domini',
            date: 'Data',
            grants: 'Consentiments atorgats',
            denials: 'Denegacions',
            not_configured: 'No configurat',
            load_error: 'Error en load integration data. Si us plau, torneu-ho a intentar.',
            webhook_not_configured: 'Webhook no configured.',
            webhook_not_configured_msg: 'Si us plau, primer {link}.',
            configure_webhook_link: 'configurar el webhook',
            setup_wizard: 'Assistent de configuració',
            step_configure_webhook: 'Configurar Webhook',
            step_map_purposes: 'Mapar finalitats',
            step_run_sync: 'Executar sincronització massiva inicial',
            step_verify_data: 'Verificar Dades en Tauler de control',
            configure: 'Configurar',
            view_dashboard: 'Veure tauler de control',
            syncing: 'Sincronitzant...',
            sync_now: 'Sincronitzar Ara',
            sync_completed: 'Sincronitzar completat: {imported} importat, {skipped} skipped, {failed} ha fallat.',
            sync_failed: 'Sincronitzar ha fallat',
            last_sync_result: 'Últim Sincronitzar Resultat',
            imported: 'Importat',
            skipped: 'Omès',
            errors: 'Errors',
            index: 'Índex',
            error: 'Error',
            sync_history: 'Sincronitzar Historial',
            no_sync_jobs: "Encara no s'han registrat treballs de sincronització.",
            load_sync_error: 'Error en load sync data. Si us plau, torneu-ho a intentar.',
            purpose_mapping_title: 'CMP Finalitat Mapeig',
            auto_mapping: 'Mapejant auto...',
            auto_map_purposes: 'Auto-Map Finalitats',
            unmapped_warning: "{count} finalitat(s) sense mapes -- aquests CMP finalitats s'han de mapar a propòsits GRC.",
            mapping_saved: 'Mapeig desat.',
            mapping_deleted: 'Mapeig eliminat.',
            auto_map_complete: 'Mapatge automàtic completat: {mapped} mapeats, {unmatched} sense mapar.',
            auto_map_failed: 'Auto-map ha fallat',
            save_mapping_error: 'Error en save mapping. Si us plau, torneu-ho a intentar.',
            delete_mapping_error: 'Error en delete mapping. Si us plau, torneu-ho a intentar.',
            load_data_error: 'Error en load data. Si us plau, torneu-ho a intentar.',
            cmp_purpose: 'Finalitat CMP',
            grc_purpose: 'Finalitat GRC',
            select_grc_purpose: '-- Seleccionar GRC Finalitat --',
            auto_mapped: 'Mapejat auto.',
            mapped: 'Mapejat',
            unmapped: 'Sense mapar',
            no_mappings: 'No s’han trobat assignacions de finalitat. Les assignacions es creen automàticament quan es reben esdeveniments de consentiment.',
            total_mappings: 'Total: {count} assignació(ns)',
            webhook_config_title: 'Webhook Configuració',
            current_configuration: 'Actual Configuració',
            setup_webhook: 'Configurar webhook',
            setup_webhook_desc: "Configureu el webhook d'AesirX CMP per rebre esdeveniments de consentiment.",
            allowed_domains: 'Dominis permesos (un per línia)',
            webhook_secret_warning: 'Secret del webhook (deseu-lo ara -- no es mostrarà de nou):',
            last_event: 'Últim Esdeveniment',
            none_received: 'Cap',
            update_domains: 'Actualitzar dominis',
            pause_webhook: 'Pausar webhook',
            resume_webhook: 'Reprendre webhook',
            regenerate_secret: 'Regenerar secret',
            configuring: 'Configurant...',
            create_webhook_config: 'Afegir webhook configuració',
            webhook_endpoint: 'Endpoint del webhook',
            webhook_endpoint_desc: 'Configureu el vostre AesirX CMP per enviar esdeveniments de consentiment a:',
            webhook_signature_note: 'Incloeu la signatura HMAC-SHA256 a la capçalera {header}.',
            configured_success: 'Webhook configured amb èxit',
            config_updated: 'Configuració actualitzat.',
            config_error: 'Error en configure webhook. Si us plau, torneu-ho a intentar.',
            update_error: 'Error en update configuration. Si us plau, torneu-ho a intentar.',
            toggle_error: 'Error en toggle webhook. Si us plau, torneu-ho a intentar.',
            deactivated: 'Webhook desactivat.',
            deactivate_error: 'Error en deactivate webhook. Si us plau, torneu-ho a intentar.',
            page_title: 'CMP Integració',
            webhook_config_page_title: 'CMP Configuració del webhook',
            bulk_sync_page_title: 'Remapejar els registres',
            purpose_mapping_page_title: "Cartografia d'objectius",
            data_loaded: "S'han carregat CMP dades d'integració",
            sync_all: 'Sincronitzar tot',
            sync_selected: 'Sincronitzar seleccionats',
            sync_status: 'Estat de sincronització',
            last_sync: 'Última sincronització',
            no_records: 'No s’han trobat registres.',
            sync_in_progress: 'Sincronització en curs...',
            sync_complete: 'Sincronització completada.',
            sync_error: 'La sincronització ha fallat. Si us plau, torneu-ho a intentar.',
            map_purpose: 'Assignar finalitat',
            unmap: 'Desassignar',
            mapping_status: 'Estat de l’assignació',
            save_mapping: 'Desar assignació',
            remap_unmapped_records: 'Torneu a mapejar els registres no assignats',
            remap_records: 'Remapejar els registres',
            add_mappings_first: 'Afegiu mapes de propòsit primer (pas 2)',
            remap_result: 'Resultat de re-mapeig',
            remap_result_desc: "S'han processat tots els registres de consentiment amb finalitats no assignades o parcialment assignades.",
            total_processed: 'Total de processats',
            newly_mapped: 'Mapejat recentment',
            still_unmapped_label: 'Encara sense cartografiar',
            still_unmapped_msg: '{count} registres encara tenen propòsits sense mapejar. {link} i, a continuació, feu clic a Torna a mapejar de nou.',
            add_more_mappings: 'Afegeix més mapes',
            remap_complete: "S'ha completat la reassignació: {remapped} s'ha assignat nou, {still_unmapped} encara no s'ha assignat.",
            add_mapping: 'Afegeix mapeig',
            cmp_purpose_id_label: 'CMP ID de propòsit *',
            cmp_purpose_id_required: 'CMP Cal un identificador de propòsit',
            cmp_purpose_name_label: 'CMP Nom de la finalitat',
            grc_purpose_label: 'Objectiu del GRC',
        },
        propagation: {
            title: 'Propagació de retirada',
            systems: 'Sistemes vinculats',
            status: 'Estat',
            confirmed: 'Confirmat',
            pending: 'Pendent',
            failed: 'Fallat',
            confirm: 'Confirmar retirada',
            fail: 'Marcar com a fallat',
            failure_reason: 'Motiu de la fallada',
            can_close: 'Tots els sistemes confirmats — el flux es pot tancar',
            cannot_close: 'Esperant confirmacions del sistema',
            no_tasks: 'No hi ha tasques de propagació.',
            confirm_success: 'Retirada del sistema confirmada.',
            fail_success: 'Sistema marcat com a fallat.',
            propagate_success: 'Retirada propagada als sistemes vinculats.',
            propagate: 'Propagació a sistemes',
            add_system: 'Afegeix sistema',
            remove_system: 'Eliminar',
            system_id: 'ID del sistema',
            system_name: 'Nom del sistema',
            owner_id: 'ID del propietari',
            propagate_error: "No s'ha pogut propagar la retirada. Si us plau, torna-ho a provar.",
            no_systems: 'Afegiu almenys un sistema.',
            page_title: 'Propagació de la retirada',
            workflow_id: 'Seleccioneu Flux de treball de retirada',
            system_id_hint: 'p. ex. crm-001, erp-prod',
            owner_id_hint: 'Propietari del sistema',
            elapsed: 'Transcorregut',
        },
        sensitive: {
            title: 'Polítiques de dades sensibles',
            flag: 'Marcar com a sensible',
            categories: 'Categories sensibles',
            enhanced_review: 'Requerir revisió ampliada',
            enhanced_review_success: 'Revisió ampliada requerida.',
            flag_success: 'Política marcada com a sensible.',
            no_sensitive: 'No hi ha polítiques de dades sensibles.',
        },
        reconciliation: {
            title: 'Reconciliació de discrepàncies',
            create: 'Nova tasca de reconciliació',
            system: 'Sistema',
            owner: 'Propietari',
            description: 'Descripció',
            resolution: 'Resolució',
            resolve: 'Resoldre',
            update_grc: 'Actualitzar GRC',
            update_cmp: 'Actualitzar CMP',
            accept_difference: 'Acceptar diferència',
            escalate: 'Escalar',
            resolution_notes: 'Notes de resolució',
            no_tasks: 'No hi ha tasques de reconciliació.',
            resolve_success: 'Tasca de reconciliació resolta.',
            create_success: 'Tasca de reconciliació creada.',
            open: 'Obert',
            resolved: 'Resolt',
            escalated: 'Escalat',
            dashboard_open: 'Tasques obertes',
            dashboard_resolved: 'Tasques resoltes',
            dashboard_escalated: 'Tasques escalades',
            page_title: 'Reconciliació desajustada',
            my_tasks: 'Les meves tasques',
            all_tasks: 'Totes les tasques',
            severity: 'Severitat',
        },
        sync: {
            title: 'Sincronització CMP',
            direction: 'Direcció',
            cmp_to_grc: 'CMP → GRC',
            grc_to_cmp: 'GRC → CMP',
            conflict: 'Conflicte',
            resolve_conflict: 'Resoldre conflicte',
            no_events: 'No hi ha esdeveniments de sincronització.',
            resolve_success: 'Conflicte de sincronització resolt.',
            create_success: 'Esdeveniment de sincronització registrat.',
            page_title: 'CMP Sincronitza',
            detect_drift: 'Detectar conflictes',
            detecting: "S'està detectant...",
            drift_result: "S'han detectat {detected} conflictes amb {total} finalitats comparades.",
            no_drift: "No s'han detectat nous conflictes.",
            drift_error: "No s'ha pogut executar la detecció de conflictes.",
            resolve_modal_title: 'Resol Conflicte',
            resolve_field: 'Camp',
            resolve_grc_value: 'Valor GRC',
            resolve_cmp_value: 'CMP Valor',
            accept_cmp: 'Accepteu el valor CMP.',
            accept_cmp_desc: 'Actualitza GRC perquè coincideixi amb el CMP — el valor CMP es converteix en el nou valor GRC.',
            keep_grc: 'Mantenir el valor GRC',
            keep_grc_desc: "Tanqueu aquest conflicte sense canviar el GRC: aviseu al propietari de CMP perquè s'alinei.",
            resolve_applied: "Conflicte resolt. El propòsit del GRC s'ha actualitzat perquè coincideixi amb CMP.",
        },
    },
    assessments: {
        /*
         * Sprint 65.4.0 (AC5) — the DPIA review-reminder task body.
         *
         * It used to be an English sentence concatenated inside
         * `dpia-review-scheduler-service.ts`, asserting a "6-month review" over a
         * record free to declare any cadence at all. The period is gone from the
         * wording because the record owns it, and the string lives here so a
         * Vietnamese tenant does not read one English line in its task list.
         */
        review_reminder: {
            task_title: 'DPIA review due: {title}',
            task_description: 'The review scheduled on this assessment is due on {date}. Confirm the safeguards remain adequate, or file an updated assessment.',
        },
        // ── Sprint 65.4.0 (AC2) — the operator-recorded commencement moment ──
        //
        // Shared by the DPIA and cross-border transfer surfaces: one fact, one
        // vocabulary. The undated wording is deliberately explicit that a
        // lifecycle timestamp is NOT the answer, because a blank date field
        // reads as "nobody filled this in" while the truth is "the clock cannot
        // lawfully start yet".
        commencement: {
            section_title_dpia: 'When the processing began',
            section_title_transfer: 'When the transfer began',
            loading: 'Loading the recorded moment',
            load_failed: 'The recorded commencement moment could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded commencement moment',
            undated_label: 'Statutory clock undated',
            undated_icon: 'Undated',
            undated_reason: 'No one has recorded when this began, so the statutory period has nothing lawful to count from. It is never counted from the date this record was created, approved or last edited — those are the moments somebody opened a form here, not the moment the activity started.',
            recorded_label: 'Statutory clock anchored',
            recorded_icon: 'Anchored to a recorded moment',
            occurred_at_label: 'Began',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            instant_label: 'Date and time it began',
            instant_hint: 'Entered in your own time zone. The statutory period is counted from this moment, not from when you enter it.',
            note_label: 'Why is this being restated?',
            note_hint: 'Required. The record has to be able to explain why its due date moved.',
            save: 'Record the moment',
            save_correction: 'Correct the moment',
            saving: 'Saving',
            saved: 'Commencement moment recorded',
            saved_correction: 'Commencement moment corrected',
            saved_unchanged: 'That is already what this record states. Nothing was changed.',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. A statutory period cannot be counted from something that has not happened yet.',
            error_note_required: 'This record already states when it began. Say why you are restating it before saving.',
            error_note_not_correction: 'Nothing has been recorded here yet, so there is nothing to correct. Remove the note and record the moment.',
            error_generic: 'The commencement moment could not be recorded.',
        },
        // ── Sprint 65.7.0 — the two moments a FILED dossier carries ──
        //
        // Shared by the DPIA and cross-border transfer surfaces, like the
        // commencement vocabulary above: one fact, one wording. The first filing is
        // write-once, so this block carries NO edit copy for it — a "Change the
        // filing date" control would offer an act the server refuses, and the
        // sentence that explains why is the affordance instead.
        dossier_update: {
            section_title_dpia: 'The dossier filed for this assessment',
            section_title_transfer: 'The dossier filed for this transfer',
            loading: 'Loading the recorded dossier moments',
            load_failed: 'The recorded dossier moments could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded dossier moments',
            filing_heading: 'First filing',
            filing_absent_label: 'No first filing recorded',
            filing_absent_icon: 'No filing recorded',
            filing_absent_reason: 'No one has recorded when this dossier was first filed, so anything counted from that filing has nothing to count from. It is never counted from the date this record was created, approved or last edited — a dossier is filed by a person, on a day, outside this platform, and told to the platform afterwards.',
            filing_recorded_label: 'First filing recorded',
            filing_recorded_icon: 'Anchored to a recorded filing',
            filing_write_once_notice: 'Recorded once, and never moved. The update cycle is counted from the first filing, so changing it here would silently move every date derived from it. If the dossier was filed again, record what changed in it below.',
            filed_at_label: 'Filed on',
            filing_instant_label: 'Date and time it was first filed',
            filing_instant_hint: 'Entered in your own time zone. This is the moment the dossier was filed, not the moment you enter it here.',
            filing_save: 'Record the first filing',
            filing_saving: 'Saving',
            filing_saved: 'First filing recorded',
            filing_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            change_heading: 'Change to the registered particulars',
            change_blocked_label: 'Nothing filed yet',
            change_blocked_reason: 'There are no registered particulars to change until a dossier has been filed. Record the first filing above, then record what changed in it.',
            change_absent_label: 'No change recorded',
            change_absent_reason: 'The dossier has been filed, and no one has recorded a change to the particulars registered in it.',
            change_recorded_label: 'Change recorded',
            change_latest_notice: 'The most recent change stands here. Recording another replaces it, and each statement is kept as its own fact in the record history.',
            changed_at_label: 'Changed on',
            change_description_value_label: 'What changed',
            change_instant_label: 'Date and time the particulars changed',
            change_instant_hint: 'Entered in your own time zone.',
            change_description_label: 'What changed?',
            change_description_hint: 'Required. Say what changed in your own words. Which changes oblige an update is stated by the frameworks you have installed, and the person acting on it needs your account of what happened.',
            change_note_label: 'Why is this being restated?',
            change_note_hint: 'Optional. Use it when this statement replaces one that already stood.',
            change_save: 'Record the change',
            change_save_restate: 'Replace the recorded change',
            change_saving: 'Saving',
            change_saved: 'Change to the registered particulars recorded',
            change_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. Nothing can be counted from something that has not happened yet.',
            error_already_recorded: 'This record already states when its dossier was first filed, and that statement cannot be moved — every date counted from it would move too. If the dossier was filed again, record what changed in it instead.',
            error_before_first_filing: 'No dossier has been filed for this record yet, so there are no registered particulars to change. Record the first filing first.',
            error_description_required: 'Say what changed. A change with no account of what changed cannot be acted on by the person who has to review it.',
            error_generic: 'The dossier moment could not be recorded.',
        },
        dossier_preflight: {
            advisory_notice: 'Aquest informe de preparació és orientatiu. No aprova, munta, materialitza, presenta ni envia el dossier.',
            run_label: 'Executar abans del vol',
            running: "S'està executant abans del vol...",
            assemble_label: 'Paquet de muntatge automàtic',
            assembling: 'Muntatge…',
            verdict_label: 'Veredicte previ al vol',
            tokens_label: '≈ {count} fitxes',
            remediation_label: 'Remediació',
            no_gaps: "No s'han detectat buits abans del vol. L'arxiu està llest per muntar.",
            override_label: "Anul·la el veredicte de bloqueig: el muntatge estarà segellat d'auditoria.",
            accept_label: "Accepta l'informe",
            reject_label: "Rebutja l'informe",
            assembled_label: 'Paquet de presentació muntat',
            package_id_label: 'ID del paquet',
            package_filename_label: 'Nom del fitxer',
            package_hash_label: 'Hash del paquet',
            evidence_count_label: "{count} elements d'evidència agrupats",
            error_generic: "No s'ha pogut executar el prevol ni muntar el paquet. Torneu-ho a provar o comproveu l'estat de la presentació.",
        },
        errors: {
            fetch: 'Error en load assessments. Si us plau, torneu-ho a intentar.',
            save: 'Error en save assessment. Si us plau, torneu-ho a intentar.',
            fetch_gap_report_failed: 'Error en fetch gap report. Si us plau, torneu-ho a intentar.',
            fetch_transfers_failed: 'Error en fetch transfers. Si us plau, torneu-ho a intentar.',
            save_transfer_failed: 'Error en save transfer. Si us plau, torneu-ho a intentar.',
            submit_review_failed: 'Error en submit for review. Si us plau, torneu-ho a intentar.',
            process_decision_failed: 'Error en process decision. Si us plau, torneu-ho a intentar.',
            deprecate_failed: 'Error en deprecate. Si us plau, torneu-ho a intentar.',
            evaluate_risk_failed: 'Error en evaluate risk. Si us plau, torneu-ho a intentar.',
            fetch_safeguards_failed: 'Error en fetch safeguards. Si us plau, torneu-ho a intentar.',
            save_safeguard_failed: 'Error en save safeguard. Si us plau, torneu-ho a intentar.',
            update_safeguard_status_failed: 'Error en actualitzar estat de la salvaguarda. Si us plau, torneu-ho a intentar.',
        },
        nav: {
            dpias: 'AIPD',
            transfers: 'Avaluacions de transferències',
            dashboard: 'Tauler de control',
            gap_report: 'Informe de llacunes',
        },
        risk_level_critical: 'Risc crític',
        risk_level_high: 'Risc alt',
        risk_level_medium: 'Risc mitjà',
        risk_level_low: 'Risc baix',
        severity_critical: 'Severitat crítica',
        severity_high: 'Severitat alta',
        severity_medium: 'Severitat mitjana',
        severity_low: 'Severitat baixa',
        overdue: 'Vençut',
        framework_filter_label: 'Marc',
        aria_filter_by_framework: 'Filtra per marc',
        dashboard: {
            title: "Tauler d'avaluacions",
            page_title: "Tauler d'avaluacions",
            announce_loaded: "Tauler d'avaluacions carregat",
            announce_chart_view: 'Mostrant vista de gràfic',
            announce_table_view: 'Mostrant vista de taula de dades',
            aria_metrics_table: "Mètriques del tauler d'avaluacions",
            metric_col: 'Mètrica',
            value_col: 'Valor',
            dpia_prefix: 'AIPD',
            transfer_prefix: 'Transferència',
            risk_prefix: 'Risc',
            aria_dpias_by_status: 'AIPD per estat',
            aria_transfers_by_status: 'Transferències per estat',
            aria_top_risks: 'Principals riscos',
            aria_top_risks_table: 'Principals riscos en les AIPD',
            overdue_dpias_attention: '{count} avaluacions AIPD vençudes requereixen atenció',
            no_overdue_dpias: 'Sense avaluacions AIPD vençudes',
            overdue_transfers_attention: '{count} avaluacions de transferència vençudes requereixen atenció',
            no_overdue_transfers: 'Sense avaluacions de transferència vençudes',
            dpias_overview: "Resum d'AIPD",
            transfers_overview: "Resum d'avaluacions de transferències",
            overdue_items: 'Elements vençuts',
            overdue_dpias: 'AIPD vençudes',
            overdue_transfers: 'Transferències vençudes',
            count: 'Recompte',
            no_data: 'Sense dades disponibles.',
            no_risks: "No s'han trobat risks",
            no_dashboard_data: 'No hi ha dades del tauler disponibles.',
            fetch_error: 'Error en fetch dashboard data. Si us plau, torneu-ho a intentar.',
        },
        dpias: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            continue: 'Continue',
            review: 'Review',
            section_basic_info: 'Basic Information',
            page_title: 'AIPDs',
            announce_submitted_for_review: 'AIPD enviada per a revisió',
            announce_deprecated: 'AIPD obsoleta',
            announce_risks_loaded: '{count} risc(os) carregat(s)',
            announce_mitigations_loaded: '{count} mesura(es) de mitigació carregada(es)',
            announce_risk_deleted: 'Risc eliminat',
            announce_mitigation_status_updated: 'Estat de la mesura de mitigació actualitzat',
            aria_filter: 'Filtrar AIPDs',
            aria_filter_by_status: 'Filtrar per estat',
            aria_filter_by_lead: "Filtrar per líder d'avaluació",
            aria_table: "Avaluacions d'impacte en la protecció de dades",
            announce_found: '{count} AIPD(s) trobada(es)',
            announce_decision: 'AIPD {decision}',
            mitigation_start: 'Iniciar',
            mitigation_mark_implemented: 'Marcar com a implementat',
            mitigation_verify: 'Verificar',
            title: "Avaluacions d'impacte en la protecció de dades",
            new: 'Nova AIPD',
            edit: 'Editar AIPD',
            dpia_title: 'Títol',
            edit_dpia_aria: 'Edit DPIA: {title}',
            approve_or_reject_aria: 'Approve or reject: {title}',
            more_actions_for_aria: 'More actions for {title}',
            risk_details_aria: 'Risk details for {title}',
            edit_risk_aria: 'Edit risk: {title}',
            delete_risk_aria: 'Delete risk: {title}',
            mitigation_details_aria: 'Mitigation details for {title}',
            edit_mitigation_aria: 'Edit mitigation: {title}',
            dossier_filing_details_aria: 'Dossier filing details for {title}',
            activity: 'Activitat',
            risk_score: 'Puntuació de risc',
            risks: 'Riscos',
            mitigations: 'Mitigacions',
            add_risk: 'Afegir risc',
            add_mitigation: 'Afegir mitigació',
            necessity: 'Necessitat / Proporcionalitat',
            likelihood: 'Probabilitat',
            impact: 'Impacte',
            severity_label: 'Gravetat',
            purpose: 'Finalitat',
            consultation_notes: 'Notes de consulta',
            dpo_opinion: 'Opinió del DPD',
            processing_activity_ids: "IDs d'activitats de tractament (separats per comes)",
            system_ids: 'IDs de sistema (separats per comes)',
            vendor_ids: 'IDs de proveïdors (separats per comes)',
            data_types: 'Tipus de dades (separats per comes)',
            data_types_short: 'Tipus de dades',
            data_subjects: 'Interessats (separats per comes)',
            assessment_lead_id: 'ID del responsable avaluació',
            assessment_lead: "Responsable de l'avaluació",
            dpo_id: 'ID DPD',
            reviewer_ids: 'IDs de revisors (separats per comes)',
            approval_mode: "Mode d'aprovació",
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Requerir aprovació del DPD',
            review_frequency_days: 'Review cadence (days)',
            review_frequency_days_hint: 'Optional. Days between reviews of this DPIA once it is approved. Leave empty and the DPIA gets no review date.',
            error_dpo_required: 'This DPIA requires a DPO sign-off. Choose the DPO before submitting it for review.',
            error_dpo_invalid: 'The DPO named on this DPIA is not an active user of this organisation. Choose an active DPO before submitting it for review.',
            error_dpo_cannot_approve: 'The DPO named on this DPIA does not hold the permission to approve DPIAs, so the sign-off could never be given. Choose a DPO who holds it before submitting it for review.',
            decision_awaiting_dpo: 'Your decision is recorded. The DPIA stays in review until its DPO signs it off.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this DPIA to draft so it can be edited',
            withdraw_review_error: 'Failed to withdraw DPIA from review. Please try again.',
            announce_withdrawn: 'DPIA withdrawn from review',
            review_date: 'Revisar Data',
            submit_for_review: 'Enviar per Revisar',
            approve_reject: 'Aprovar/Rebutjar',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the DPIA. Please try again.',
            announce_reopened: 'DPIA reopened as a draft',
            decision_named_reviewers_note: 'This DPIA names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this DPIA.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This DPIA is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Deprecar',
            deprecated: 'Obsolet',
            hide_risks: 'Amagar Riscos',
            hide_mitigations: 'Amagar mitigacions',
            comments: 'Comentaris',
            submit_decision: 'Enviar decisió',
            category_label: 'Categoria',
            category_data_breach: 'Dades Violació',
            category_unauthorized_access: 'Accés no autoritzat',
            category_function_creep: 'Expansió de funcions',
            category_re_identification: 'Re-identificació',
            category_discrimination: 'Discriminació',
            category_surveillance: 'Vigilància',
            category_other: 'Altre',
            affected_rights: 'Drets afectats (separats per comes)',
            no_risks: "No s'han trobat risks",
            no_mitigations: "No s'han trobat mitigations",
            no_dpias: "No s'han trobat dpias",
            mitigation_type: 'Tipus',
            mitigation_technical: 'Tècnic',
            mitigation_organizational: 'Organitzatiu',
            mitigation_contractual: 'Contractual',
            risk_ids: 'IDs de riscos (separats per comes)',
            responsible_party: 'Part responsable',
            responsible_party_placeholder: 'O introduir nom manualment',
            affected_rights_placeholder: 'Els drets seleccionats apareixen aquí',
            affected_rights_freetext_placeholder: 'Introduir drets afectats (separats per comes)',
            responsible: 'Responsable',
            target_date: 'Data objectiu',
            created: 'Creat',
            fetch_error: 'Error en fetch dpias. Si us plau, torneu-ho a intentar.',
            save_error: 'Error en save dpia. Si us plau, torneu-ho a intentar.',
            submit_review_error: 'Error en submit dpia for review. Si us plau, torneu-ho a intentar.',
            decision_error: 'Error en process decision. Si us plau, torneu-ho a intentar.',
            deprecate_error: 'Error en deprecate dpia. Si us plau, torneu-ho a intentar.',
            fetch_risks_error: 'Error en fetch risks. Si us plau, torneu-ho a intentar.',
            save_risk_error: 'Error en save risk. Si us plau, torneu-ho a intentar.',
            delete_risk_error: 'Error en delete risk. Si us plau, torneu-ho a intentar.',
            fetch_mitigations_error: 'Error en fetch mitigations. Si us plau, torneu-ho a intentar.',
            save_mitigation_error: 'Error en save mitigation. Si us plau, torneu-ho a intentar.',
            update_mitigation_status_error: 'Error en actualitzar estat de la mitigació. Si us plau, torneu-ho a intentar.',
            link_form_template: "Plantilla de formulari regulador d'enllaç",
            link_form_template_placeholder: 'Seleccioneu una plantilla de formulari DPIA (opcional)',
            link_form_template_none: 'Cap',
            linked_form: 'Formulari enllaçat',
            linked_form_creating: "S'està creant un formulari enllaçat...",
            linked_form_error: "No s'ha pogut crear una instància de formulari enllaçada. El DPIA es va crear sense un formulari enllaçat.",
            view_linked_form: 'Veure formulari',
            announce_created_with_form: 'DPIA creat amb una instància de formulari enllaçada',
            creation_mode: 'DPIA Mode de creació',
            mode_standard: 'Estàndard DPIA',
            mode_template: 'Utilitzeu la plantilla de formulari regulador',
            select_form_template: 'Seleccioneu Plantilla de formulari',
            create_and_fill_form: 'Crear i omplir el formulari',
            template_sections_title: 'Seccions de plantilla',
            field_singular: 'camp',
            field_plural: 'camps',
            no_sections: "No s'ha trobat cap secció en aquesta plantilla.",
            template_mode_hint: 'Després de la creació, se us redirigirà per omplir el formulari regulador complet.',
            linked_dossier_filings: 'Presentacions de dossier vinculades',
            no_linked_dossier_filings: 'No hi ha presentacions de dossier vinculades a aquesta EIPD.',
            view_dossier_filing: 'Veure presentació',
            hide_dossier_filings: 'Amagar presentacions',
            supporting_evidence: 'Evidència de suport',
            hide_supporting_evidence: "Amaga l'evidència",
            no_supporting_evidence: "No s'adjunta cap prova de suport.",
            upload_supporting_evidence: 'Carregueu proves de suport',
            view_form_responses: 'Veure respostes del formulari',
            regulatory_framework: 'Marc regulatori',
            all_frameworks: 'Tots els marcs',
            no_templates_for_market: 'No hi ha plantilles disponibles per al mercat seleccionat. Seleccioneu un mercat diferent o creeu una plantilla.',
            affected_rights_freetext_hint: 'No framework rights available — enter manually (comma-separated)',
            ai_draft_eyebrow: 'AI assist',
            deprecate_hint: 'Retire this DPIA without deleting it',
            inherent_risk_level: 'Inherent Risk',
            mitigation_contractual_hint: 'DPA clauses, SCCs, contractual obligations',
            mitigation_description_placeholder: 'Describe the mitigation measure in detail',
            mitigation_organizational_hint: 'Policies, training, procedures, staff awareness',
            mitigation_technical_hint: 'Encryption, pseudonymisation, access controls, system hardening',
            mitigation_title_placeholder: 'e.g. Encrypt data at rest',
            no_risks_to_link: 'No risks found for this DPIA. Add risks first.',
            score_high: 'High',
            score_low: 'Low',
            score_medium: 'Medium',
            score_very_high: 'Very High',
            score_very_low: 'Very Low',
            section_assessment_details: 'Assessment Details',
            section_implementation: 'Implementation',
            section_processing: 'Processing Details',
            section_scoring: 'Risk Scoring',
            section_team: 'Team & Approval',
            selected_rights_count: 'Selected',
            submit_for_review_hint: 'Advance this DPIA to review',
        },
        transfers: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            description_placeholder: 'Brief summary of this transfer',
            title_placeholder: 'e.g. EU–US CRM Data Transfer',
            vendor_id_placeholder: 'Vendor ID (optional)',
            page_title: 'Transferències de dades',
            announce_found: '{count} avaluació(ns) de transferència trobada(es)',
            announce_submitted_for_review: 'Transferència enviada per a revisió',
            announce_deprecated: 'Transferència obsoleta',
            announce_decision: 'Transferència {decision}',
            announce_risk_eval_completed: 'Avaluació de riscos completada',
            announce_safeguard_status_updated: 'Estat de la mesura de protecció actualitzat',
            announce_safeguards_loaded: '{count} mesura(es) de protecció carregada(es)',
            aria_filter: 'Filtrar avaluacions de transferència',
            aria_filter_by_status: 'Filtrar per estat',
            aria_filter_by_mechanism: 'Filtrar per mecanisme de transferència',
            aria_table: 'Avaluacions de transferència de dades',
            title: 'Avaluacions de transferències transfrontereres',
            new: 'Nova transferència',
            edit: 'Editar transferència',
            name: 'Nom',
            from_country: "País d'origen",
            to_country: 'País de destinació',
            safeguards: 'Garanties',
            risk_level: 'Nivell de risc',
            assessment_notes: "Notes de l'avaluació",
            mechanism: 'Mecanisme',
            transfer_mechanism: 'Mecanisme de transferència',
            transfer_mechanism_detail: 'Detall del mecanisme de transferència',
            all_mechanisms: 'Tots els mecanismes',
            mechanism_other: 'Altre',
            data_types: 'Tipus de dades (separats per comes)',
            data_subjects: 'Interessats (separats per comes)',
            purpose: 'Finalitat',
            recipient_name: 'Nom del destinatari',
            create_success: 'Avaluació de transferència creada.',
            update_success: 'Avaluació de transferència actualitzada.',
            recipient_details: 'Detalls del destinatari',
            recipient_details_placeholder: 'Rol, ubicació i sistemes del destinatari, i detalls de transferència ulterior',
            vendor_id: 'Proveïdor ID',
            processing_activity_ids: "IDs d'activitats de tractament (separats per comes)",
            reviewer_ids: 'IDs de revisors (separats per comes)',
            approval_mode: "Mode d'aprovació",
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Requerir aprovació del DPD',
            review_date: 'Revisar Data',
            created: 'Creat',
            deprecated: 'Obsolet',
            submit_for_review: 'Enviar per Revisar',
            approve_reject: 'Aprovar/Rebutjar',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the transfer assessment. Please try again.',
            announce_reopened: 'Transfer assessment reopened as a draft',
            redetermine_mechanism: 'Re-determine mechanism',
            redetermine_mechanism_title: 'Re-determine the transfer mechanism',
            redetermine_mechanism_intro: 'This record is {status}, so it cannot be edited, but its transfer mechanism is waiting for a person to re-determine it. Choose a mechanism an installed framework declares. Only the mechanism is saved; the status and the approval record do not change.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this transfer assessment to draft. The review round is closed and its decisions no longer count. Use this when a named reviewer can no longer decide.',
            withdraw_review_error: 'Could not withdraw the transfer assessment from review. Please try again.',
            announce_withdrawn: 'Transfer assessment withdrawn from review',
            decision_named_reviewers_note: 'This transfer assessment names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this transfer assessment.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This transfer assessment is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status, but a cross-border identity exchange flow cannot rest on it. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Deprecar',
            risk_eval: 'Avaluació de riscos',
            hide_safeguards: 'Amagar salvaguardes',
            mechanism_proof: 'Prova de mecanisme',
            hide_mechanism_proof: 'Oculta la prova del mecanisme',
            mechanism_proof_hint: 'Upload the signed document the transfer rests on (for example the contract, or the impact-assessment dossier your installed frameworks require) to prove this transfer assessment.',
            no_mechanism_proof: "No s'adjunta cap prova de mecanisme.",
            upload_mechanism_proof: 'Carregueu la prova del mecanisme',
            comments: 'Comentaris',
            submit_decision: 'Enviar decisió',
            data_sensitivity: 'Sensibilitat de les dades (1-5)',
            country_risk: 'País Risc (1-5)',
            safeguard_adequacy: 'Adequació de la salvaguarda (1-5)',
            volume_factor: 'Factor de volum (1-5)',
            evaluate: 'Avaluar',
            add_safeguard: 'Afegir Safeguard',
            responsible_party: 'Part responsable',
            responsible_party_placeholder: 'O introduir nom manualment',
            responsible: 'Responsable',
            implementation_date: "Data d'implementació",
            implementation: 'Implementació',
            expiry_date: 'Data de caducitat',
            expiry: 'Caducitat',
            expire: 'Caducar',
            activate_unavailable_expired: 'An expired safeguard cannot be reactivated. Record a new safeguard instead.',
            no_safeguards: "No s'han trobat safeguards",
            no_transfers: "No s'han trobat transfer assessments",
            fetch_error: 'Error en fetch transfer assessments. Si us plau, torneu-ho a intentar.',
            save_error: 'Error en save transferència avaluació. Si us plau, torneu-ho a intentar.',
            submit_review_error: 'Error en submit for review. Si us plau, torneu-ho a intentar.',
            decision_error: 'Error en process decision. Si us plau, torneu-ho a intentar.',
            deprecate_error: 'Error en deprecate transfer. Si us plau, torneu-ho a intentar.',
            risk_eval_error: 'Error en evaluate risk. Si us plau, torneu-ho a intentar.',
            fetch_safeguards_error: 'Error en fetch safeguards. Si us plau, torneu-ho a intentar.',
            save_safeguard_error: 'Error en save safeguard. Si us plau, torneu-ho a intentar.',
            update_safeguard_status_error: 'Error en actualitzar estat de la salvaguarda. Si us plau, torneu-ho a intentar.',
            safeguard_type_encryption_in_transit: 'Xifratge en trànsit',
            safeguard_type_encryption_at_rest: 'Xifratge en repòs',
            safeguard_type_access_controls: 'Accés Controls',
            safeguard_type_contractual_clauses: 'Clàusules contractuals',
            safeguard_type_data_minimization: 'Minimització de dades',
            safeguard_type_pseudonymization: 'Pseudonimització',
            safeguard_type_audit_rights: "Drets d'auditoria",
            safeguard_type_breach_notification: 'Violació Notificació',
            safeguard_type_data_localization: 'Localització de dades',
            safeguard_type_other: 'Altre',
            activate: 'Activar',
            all_statuses: 'Tots els estats',
            any: 'Any',
            mechanism_detail: 'Detall del mecanisme',
            safeguard_types: {
                encryption_in_transit: 'Xifrat en trànsit',
                encryption_at_rest: 'Xifrat en repòs',
                access_controls: "Controls d'accés",
                contractual_clauses: 'Clàusules contractuals',
                data_minimization: 'Minimització de dades',
                pseudonymization: 'Pseudonimització',
                audit_rights: "Drets d'auditoria",
                breach_notification: 'Notificació de violació',
                data_localization: 'Localització de dades',
                other: 'Altre',
            },
            activity_ids_placeholder: 'Activity IDs (optional)',
            deprecate_hint: 'Retire this assessment without deleting it',
            file_dossier: 'File Dossier',
            file_dossier_hint: 'Start a regulatory filing from this assessment',
            mark_implemented: 'Implement',
            mark_verified: 'Verify',
            mechanism_detail_placeholder: 'e.g. the contract or document the transfer rests on',
            purpose_placeholder: 'e.g. CRM, analytics, support',
            csv_hint: '(comma-separated)',
            data_subjects_placeholder: 'e.g. employees, customers',
            data_types_placeholder: 'e.g. name, email, phone',
            from_country_placeholder: 'e.g. EU',
            to_country_placeholder: 'e.g. US',
            recipient_placeholder: 'e.g. Salesforce Inc.',
            reviewers_placeholder: 'Select reviewers…',
            risk_eval_hint: 'Score data sensitivity, country & safeguards',
            section_mechanism: 'Mechanism & Data',
            section_review: 'Review & Approval',
            section_route: 'Transfer Route',
        },
        gap_report: {
            page_title: "Informe de bretxes d'avaluacions",
            announce_loaded: 'Informe de bretxes carregat. {count} bretxa(es) identificada(es)',
            aria_activities_without_dpia: 'Activitats sense avaluació AIPD',
            aria_activities_unclassified: "Activitats amb requisit d'AIPD sense classificar",
            activities_unclassified_section_title: "Activitats amb requisit d'AIPD sense classificar",
            activities_unclassified_description: "Aquestes activitats no s'han revisat per determinar si és legalment necessària una AIPD. Classifiqueu cadascuna per eliminar el senyal.",
            activities_unclassified_empty: 'Totes les activitats actives estan classificades.',
            aria_unmitigated_risks: 'Riscos alts i crítics no mitigats',
            aria_transfers_without_safeguards: 'Transferències sense mesures de protecció adequades',
            aria_expired_assessments: 'Avaluacions vençudes que requereixen renovació',
            title: "Informe de llacunes d'avaluació",
            no_dpia: 'Activitats sense AIPD',
            no_assessment: 'Transferències sense avaluació',
            unmitigated: 'AIPD amb riscos no mitigats',
            activity_id: "ID d'activitat",
            dpia_id: 'ID AIPD',
            assessment_id: 'Avaluació ID',
            expired_assessments: 'Caducat Avaluacions',
            no_gaps_activities: "No s'han trobat buits. Totes les activitats tenen DPIAs.",
            no_unmitigated_risks: "No s'han trobat unmitigated high/critical risks",
            no_gaps_safeguards: "No s'han trobat buits. Totes les transferències tenen salvaguardes adequades.",
            no_expired: "No s'han trobat expired assessments",
            no_report_data: "No hi ha dades de l'informe de buits disponibles.",
            fetch_error: 'Error en fetch gap report. Si us plau, torneu-ho a intentar.',
            no_expired_assessments: 'Sense avaluacions caducades.',
            no_gaps_dpia: "No s'han trobat dpia gaps",
            no_data: 'Sense dades disponibles.',
            all_clear: 'No compliance gaps detected.',
            list_truncated_notice: 'Only the first 50 activities are listed; more match. Resolve these, then reload the report to see the rest.',
            subtitle: 'Identify compliance gaps across DPIAs, risks, transfers, and assessments.',
        },
        dossier_filings: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            aria_filters: 'Filter dossier filings',
            description_placeholder: 'Optional summary of this filing',
            title_placeholder: 'e.g. DPIA Filing – Annex I',
            page_title_detail: "Detall de presentació de l'expedient",
            page_title_list: "Casos de presentació de l'expedient",
            announce_filings_found: '{count} presentació(ns) trobada(es)',
            announce_results: '{count} resultats',
            aria_filter_by_status: 'Filtrar per estat',
            title: 'Dossier Arxiu de casos',
            nav_label: 'Presentació de dossiers',
            create: "Creació d'Arxiu de Dossier",
            form_type: 'Tipus de formulari',
            phu_luc_i_label: 'Standard assessment workflow (internal)',
            phu_luc_ii_label: 'Cross-border / high-risk assessment workflow (internal)',
            dpia_reference: 'DPIA Referència',
            transfer_reference: 'Transfer Assessment Reference',
            transfer_select_placeholder: 'Link to a transfer assessment',
            arrived_parent_heading: 'Filing for',
            prior_filing_heading: 'This organisation has already filed this dossier',
            prior_filing_body: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation already filed one: {filing}. For a new purpose or another change to its content, start the update on this assessment instead. You can still open a first filing, for example to file again after the authority assessed the earlier dossier as not meeting the requirements.',
            prior_filing_start_update: 'Start the update: {form}',
            prior_filing_no_update_form: 'No update form is installed for this kind of dossier.',
            arrived_parent_loading: 'Looking up the assessment record…',
            arrived_parent_unresolved: 'The assessment this filing was started from could not be found. It may have been deleted, or it may belong to another organisation. Choose a parent below before filing.',
            parent_none: 'No assessment linked',
            parent_unresolved: 'linked record not found ({id})',
            error_parent_required: 'Choose the assessment this filing discharges — either an impact assessment or a transfer assessment.',
            error_transfer_parent_not_found: 'The transfer assessment this filing names no longer exists. Pick another one, or reopen the filing from the transfer register.',
            error_dpia_parent_not_found: 'The impact assessment this filing names no longer exists. Pick another one.',
            submitted_date: 'Data de presentació',
            outcome: 'Resultat',
            parent_filing: 'Pare',
            no_filings: "No s'han trobat expedients.",
            fetch_error: "No s'han pogut recuperar els expedients. Si us plau, torna-ho a provar.",
            save_error: "No s'ha pogut crear l'arxiu de l'expedient. Si us plau, torna-ho a provar.",
            created: "S'ha creat la presentació del dossier.",
            loaded: "S'ha carregat l'arxiu del dossier.",
            not_found: "No s'ha trobat el dossier.",
            back_to_list: 'Tornar a la llista',
            section_assignments: 'Tasca de la secció',
            assign_sections: 'Assigna seccions',
            section_id: 'Secció',
            department: 'Departament',
            assignee: 'Cesionari',
            deadline: 'Data límit',
            no_assignments: 'Encara no hi ha assignacions de secció.',
            sections_assigned_msg: 'Seccions assignades.',
            assign_error: "No s'han pogut assignar les seccions. Si us plau, torna-ho a provar.",
            lifecycle: 'Cicle de vida de la presentació',
            submission: 'Detalls de presentació',
            submission_channel: "Canal d'enviament",
            confirmation_number: 'Número de confirmació',
            // Fix sprint #447 (round 2) — the Submit dialog offers the declared channels.
            select_channel: 'Select a channel',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            status_submitted: 'Filed — no result recorded',
            submit_for_review: 'Enviar per revisió',
            submitted_for_review: 'Presentat per a revisió interna.',
            submit_review_error: "No s'ha pogut enviar a revisió. Si us plau, torna-ho a provar.",
            legal_signoff: 'Firma legal',
            legal_signoff_done: 'Acceptació legal completada.',
            legal_signoff_error: "No s'ha pogut completar la signatura legal. Si us plau, torna-ho a provar.",
            approve: 'Approve internally',
            approved_msg: 'Filing approved internally. It has not been filed with the authority.',
            approve_error: "No s'ha pogut aprovar la presentació. Si us plau, torna-ho a provar.",
            materialisation_status_column: 'Artefacte de presentació',
            materialisation_na: 'No aplicable',
            materialisation_pending: 'Pendent',
            materialisation_success: 'Materialitzat',
            materialisation_failed: 'Ha fallat',
            materialisation_banner_title: "No s'ha pogut crear l'artefacte d'enviament",
            materialisation_banner_body: 'The filing was approved internally, but the underlying dossier record could not be created. Cover-letter and submission exports are unavailable until you retry.',
            materialisation_retry_button: 'Torna-ho a provar',
            materialisation_retry_count: 'Intents de reintent anteriors:',
            materialisation_retry_success: "L'artefacte d'enviament s'ha materialitzat correctament.",
            materialisation_retry_announce: "S'ha tornat a intentar",
            filter_materialisation_failed: 'Mostra només els errors de materialització',
            column_title: 'Títol',
            column_form_type: 'Formulari',
            submit_to_mps: 'Record filing with the authority',
            submitted_to_mps_msg: 'Your organisation’s submission to the authority is recorded.',
            submit_mps_error: 'Failed to record the filing with the authority. Please try again.',
            supplement_request: 'Sol·licitud de suplement',
            supplement_requests: 'Sol·licituds de suplements',
            supplement_response: 'Resposta complementària',
            record_supplement: 'Sol·licitud de suplement de registre',
            supplement_recorded: "S'ha registrat la sol·licitud de suplement.",
            supplement_absence_declaring_framework_unreadable: 'No deadline: the framework that states the period could not be read.',
            supplement_absence_declaring_framework_not_in_force: 'No deadline: the framework that states the period is not yet in force.',
            supplement_absence_uncomputable_period: 'No deadline: the installed period cannot be counted as a date.',
            supplement_absence_multiple_candidate_clocks: 'No deadline: more than one installed period could apply and the platform does not choose between them. Enter the deadline stated in the authority’s letter.',
            supplement_absence_no_installed_clock: 'No deadline: no installed framework states a period for responding.',
            supplement_absence_receipt_not_evidenced: 'No deadline: the date the applicant received the authority’s result has not been recorded.',
            supplement_absence_declaring_framework_reference_mode: 'No deadline: the framework that states the period is installed as Reference only, so its clocks are not run.',
            supplement_absence_declaring_framework_not_operational: 'No deadline: the framework that states the period is superseded or retired, so it no longer sets a deadline.',
            supplement_deadline_warning_differs_from_installed: 'The date you entered falls on a different day from the one the installed period gives. The date from the authority’s letter stands.',
            supplement_deadline_warning_in_the_past: 'The date you entered was already past when it was recorded.',
            supplement_deadline_platform_estimate: 'Platform estimate recorded earlier: not a period stated by an installed framework or by the authority’s letter.',
            supplement_deadline_installed_due: 'The installed period gives {date}.',
            supplement_deadline_none: 'No deadline is recorded for this request.',
            supplement_deadline_source_unknown: 'The source of this deadline was not recorded',
            supplement_deadline_source_operator_anon: 'Stated in the authority’s letter; entered by a user',
            supplement_deadline_source_operator: 'Stated in the authority’s letter; entered by {user}',
            supplement_deadline_source_installed: 'Counted from the date of receipt, using the period in the installed framework',
            supplement_response_received: 'Responded',
            supplement_response_pending: 'Awaiting response',
            supplement_response_deadline: 'Response deadline',
            supplement_items_requested: 'Items requested',
            supplement_received_future: 'The date of receipt cannot be in the future.',
            supplement_deadline_help: 'A completion period is counted from a communication your organisation recorded, once you state that it asks for the dossier to be completed, or that it states the dossier is incomplete, invalid or not in accordance with the regulations. With a deadline stated in the authority’s letter, the platform records that deadline and who entered it. With neither, the request has no deadline and the page says why.',
            supplement_response_deadline_input: 'Deadline stated in the authority’s letter (optional)',
            supplement_received_at: 'Date the applicant received the authority’s result',
            supplement_items: 'Items the authority requested (one per line)',
            supplement_error: "No s'ha pogut registrar la sol·licitud de suplement. Si us plau, torna-ho a provar.",
            submit_supplement: 'Envieu la resposta del suplement',
            supplement_submitted_msg: "S'ha enviat la resposta al suplement.",
            supplement_submit_error: "No s'ha pogut enviar la resposta al suplement. Si us plau, torna-ho a provar.",
            response_status: 'Estat de resposta',
            response_notes: 'Notes de resposta',
            resubmit: 'Torna a enviar',
            resubmitted_msg: "S'ha tornat a enviar el dossier.",
            resubmit_error: "No s'ha pogut tornar a enviar l'expedient. Si us plau, torna-ho a provar.",
            outcome_error: "No s'ha pogut registrar el resultat. Si us plau, torna-ho a provar.",
            accepted: 'Acceptat',
            rejected: 'Rebutjat',
            close: 'Tancar',
            closed_msg: 'Expedient tancat.',
            close_error: "No s'ha pogut tancar l'arxiu. Si us plau, torna-ho a provar.",
            change_filing: 'Crea un fitxer de canvis',
            change_filing_created: "S'ha creat l'arxiu de canvis.",
            change_filing_error: "No s'ha pogut crear l'arxiu de canvis. Si us plau, torna-ho a provar.",
            change_reason: 'Motiu del canvi',
            filing_lineage: "Llinatge d'arxiu",
            timers: 'Temporitzadors actius',
            timer_mps_review: 'Authority review',
            timer_supplement_response: 'Termini del suplement',
            timer_resubmission_review: 'Termini de tornada a presentar',
            timer_department_input: 'Termini del Departament',
            days_remaining: 'dies restants',
            days_overdue: 'dies vençuts',
            cover_letter: 'Carta de presentació',
            checklist: 'Llista de verificació',
            evidence_index: "Índex d'evidència",
            status_draft: 'Esborrany',
            status_internal_review: 'Revisió interna',
            status_legal_signoff: 'Firma legal',
            status_approved: 'Approved internally — not filed',
            status_supplement_requested: 'Suplement sol·licitat',
            status_supplement_submitted: 'Suplement enviat',
            status_resubmitted: "S'ha tornat a enviar",
            status_accepted: 'Result recorded: Accepted',
            status_rejected: 'Result recorded: Rejected',
            status_closed: 'Tancat',
            // Sprint 62.57.0 (gate E12.5 pass 1, D10) — step_approved, step_submitted,
            // step_accepted and step_rejected are English until Phase 25 (R-57); the
            // other step_* keys keep this locale's translation.
            step_draft: 'Esborrany',
            step_internal_review: 'Revisió interna',
            step_legal_signoff: 'Firma legal',
            step_approved: 'Approved internally',
            step_submitted: 'Filed',
            step_supplement_requested: 'Suplement sol·licitat',
            step_supplement_submitted: 'Suplement enviat',
            step_resubmitted: "S'ha tornat a enviar",
            step_accepted: 'Result recorded: Accepted',
            step_rejected: 'Result recorded: Rejected',
            step_closed: 'Tancat',
            linked_dpia: 'EIPD vinculada',
            linked_dpia_none: 'No hi ha cap EIPD vinculada a aquesta presentació.',
            linked_parents: 'Linked assessment',
            linked_parents_none: 'This filing names no assessment.',
            parent_kind_dpia: 'Impact assessment',
            parent_kind_transfer: 'Transfer assessment',
            view_linked_dpia: 'Veure EIPD',
            linked_dpia_label: 'Aquesta presentació de dossier està vinculada a la següent EIPD:',
            all_sections_must_complete: 'All sections must be completed or reviewed before submitting',
            change_section_status: 'Change status for',
            dpia_select_placeholder: 'Link to a DPIA (optional)',
            no_departments_hint: 'No departments available. You can still assign by selecting a user below.',
            no_users_hint: 'Unable to load users. Please check your connection and try again.',
            phu_luc_i_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            phu_luc_ii_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            section_add_new: 'Add new section...',
            section_assignments_explanation: 'Assign sections of this dossier filing to departments for completion. Each assigned section creates a task for the assignee with a deadline. All sections must be completed before the filing can proceed to internal review.',
            section_consultation: 'Consultation & Stakeholders',
            section_custom_placeholder: 'Enter custom section name',
            section_data_categories: 'Data Categories & Processing',
            section_data_subject_rights: 'Data Subject Rights',
            section_impact_assessment: 'Impact Assessment',
            section_legal_basis: 'Legal Basis & Necessity',
            section_risk_mitigation: 'Risk Mitigation Measures',
            section_status_label: 'Section status',
            section_status_updated: 'Section status updated to',
            transition_to: 'Transition to...',
            view_filing: 'View',
            // Sprint 63.37.0 (Stream J) — the forms a filing files, and the dossier assembled from it.
            filing_forms: 'Forms in this filing',
            filing_forms_none: 'No form is linked to this filing yet.',
            filing_forms_failed: 'The forms of this filing could not be read.',
            filing_form_label: 'Form this filing records',
            assembled_dossier: 'Assembled dossier {reference}',
            dossier_role_primary_application: 'Primary form of the dossier',
            dossier_role_supporting_document: 'Supporting document',
            dossier_role_authority_response: 'Authority response',
            dossier_role_supplement: 'Supplement',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the person who recorded the submission; the platform records, the organisation files.
            submission_recorded_by: 'Recorded by',
            submission_notes: 'Notes',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the four section statuses (DossierSectionStatus); the table printed the stored code.
            section_status_assigned: 'Assigned',
            section_status_in_progress: 'In Progress',
            section_status_completed: 'Completed',
            section_status_reviewed: 'Reviewed',
            timer_estimate_passed: '{days} days past the estimate',
            timer_platform_estimate: 'Platform estimate — not a statutory period',
            reload: 'Reload',
            reloading: 'Reloading…',
            timers_load_failed: 'The filing timers could not be read, so none are shown. That does not mean the filing has none.',
            retry_subject_timers: 'the filing timers',
            lineage_load_failed: 'The filing lineage could not be read, so earlier and later filings are not shown.',
            retry_subject_lineage: 'the filing lineage',
            form_status_load_failed: 'The status of the forms could not be read, so the Forms column may be incomplete.',
            form_status_not_read: 'Not read',
            retry_subject_form_status: 'the form status',
            supplement_absence_declaring_framework_phase_reference_unresolved: 'No deadline: the period applies from a phase of its framework, and the framework does not date that phase.',
            // Sprint 62.57.0 (Slice R) — authority communications on a filing, recorded as
            // documents; the limitation notice; the latest-communication column and filter.
            result_recorded_qualifier: 'as your organisation recorded it from the authority’s notice',
            limitation_heading: 'What ComplianceOne records about the authority',
            limitation_body: 'ComplianceOne records the authority’s written communications as documents, and records the handling body, the result and any completion request as your organisation states them from the authority’s communications, and an update’s recipient and competent authority as your organisation records them. Completion dates are provisional where the governing text’s start event is pending legal confirmation, and ComplianceOne never decides which body handles a dossier or an update.',
            comms_heading: 'Authority communications',
            comms_intro: 'The written communications your organisation received from an authority about this filing, as a person in your organisation recorded them. Recording one changes nothing else on the filing: no status, result or deadline.',
            comms_record: 'Record a communication',
            comms_export: 'Export communications',
            comms_export_done: 'The communications were exported.',
            comms_export_failed: 'The communications could not be exported.',
            comms_none: 'No communication has been recorded on this filing.',
            comms_loading: 'Loading the communications…',
            comms_load_failed: 'The communications could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_comms: 'the authority communications',
            comms_pagination: 'Pages of authority communications',
            comms_col_kind: 'Kind',
            comms_col_sender: 'Sender',
            comms_col_communication_date: 'Date on the communication',
            comms_col_received_at: 'Received',
            comms_col_wording: 'The authority’s wording',
            comms_col_evidence: 'Evidence',
            comms_col_correction: 'Correction',
            comms_col_recorded: 'Recorded',
            comms_row_statement: 'Recorded by {person} from the authority’s communication — ComplianceOne has not verified it.',
            comms_reference: 'Reference: {reference}',
            comms_stated_items: 'Items stated',
            comms_stated_period: 'Period stated: {period}',
            comms_notes: 'Notes: {notes}',
            comms_no_wording: 'No wording recorded',
            comms_not_evidenced: 'Not evidenced',
            comms_evidence_count: '{count} file(s)',
            comms_evidence_open: 'Files',
            comms_evidence_open_label: 'Evidence files of the communication received on {date}',
            comms_evidence_load_failed: 'The evidence files could not be read.',
            comms_corrects: 'Corrects the entry of {date}',
            comms_corrects_unknown: 'Corrects an earlier entry',
            comms_corrected_by: 'Corrected by a later entry',
            comms_export_corrects: 'Corrects the entry received {date} ({kind})',
            comms_export_corrected_by: 'Corrected by a later entry (received {date})',
            comms_correction_note: 'Why: {note}',
            comm_kind_receipt_acknowledgement: 'Receipt acknowledgement',
            comm_kind_transfer_notice: 'Transfer notice',
            comm_kind_result_notice: 'Result notice',
            comm_kind_supplement_request: 'Supplement request',
            comm_kind_refusal: 'Refusal',
            comm_kind_other_written_communication: 'Other written communication',
            comms_form_intro: 'Record what the authority sent, as it is written. This record changes no status, result or deadline on the filing.',
            comms_form_evidence_hint: 'Evidence files are attached after the communication is recorded, from its row in the history.',
            comms_field_kind: 'Kind of communication',
            comms_field_kind_placeholder: 'Choose the kind',
            comms_field_sender: 'Sender, as the communication names it',
            comms_field_reference: 'Reference on the communication',
            comms_field_communication_date: 'Date on the communication',
            comms_field_received_at: 'Date your organisation received it',
            comms_field_wording: 'The authority’s wording, verbatim',
            comms_field_wording_hint: 'Up to {max} characters. Required for a result notice or a refusal.',
            comms_field_wording_count: '{count} of {max} characters used',
            comms_field_items: 'Items the communication states',
            comms_field_item: 'Item {n}',
            comms_add_item: 'Add an item',
            comms_remove_item: 'Remove item {n}',
            comms_items_none: 'No item added.',
            comms_field_period: 'Period the communication states, verbatim',
            comms_field_period_hint: 'Recorded as written. No deadline is computed from it.',
            comms_field_notes: 'Internal notes',
            comms_field_corrects: 'Corrects an earlier entry',
            comms_field_corrects_none: 'Not a correction',
            comms_field_corrects_option: '{kind}, received {date}',
            comms_field_corrects_truncated: 'Only the {count} most recent entries are listed.',
            comms_field_corrects_failed: 'The earlier entries could not be read, so none can be chosen.',
            comms_field_correction_note: 'Why the earlier entry is corrected',
            comms_error_required: 'Enter this before recording.',
            comms_error_kind_required: 'Choose the kind of communication.',
            comms_error_wording_required: 'Enter the authority’s wording for a result notice or a refusal.',
            comms_error_correction_note_required: 'Say why the earlier entry is corrected.',
            comms_error_receipt_invalid: "The date of receipt must not be later than today in your organisation's time zone.",
            comms_error_not_submitted: 'This filing has not been filed, so no communication can be recorded. The page has updated the filing’s details; what you entered is kept.',
            comms_record_failed: 'The communication could not be recorded. What you entered is kept.',
            comms_recorded: 'Communication recorded.',
            comms_recording: 'Recording…',
            comms_files_heading: 'Evidence files',
            comms_files_intro: 'The files this communication rests on, as your organisation attached them. ComplianceOne has not verified them.',
            comms_files_slot: 'Files of this communication',
            comms_files_none: 'No file is attached. This communication is not evidenced.',
            comms_file_hash: 'SHA-256: {hash}',
            comms_file_not_held: 'File no longer held',
            comms_file_download: 'Download',
            comms_file_remove: 'Remove',
            comms_file_upload: 'Attach a file',
            comms_file_bound: 'File attached.',
            comms_file_removed: 'File removed.',
            comms_file_bind_failed: 'The file could not be attached.',
            comms_file_remove_failed: 'The file could not be removed.',
            comms_file_not_owned: 'The communication this file belongs to was not found.',
            comms_file_remove_confirm: 'Remove this file from the communication and delete it?',
            latest_communication: 'Latest communication',
            latest_communication_none: 'None recorded',
            latest_communication_value: '{kind}, received {date}',
            aria_filter_by_communication: 'Filter by latest communication',
            filter_communication_none: 'No communication recorded',
            comms_export_heading: 'Communications recorded by the organisation',
            comms_export_filing: 'Filing: {filing}',
            comms_export_entries: 'Entries: {count}',
            comms_export_col_reference: 'Reference',
            comms_export_col_statement: 'Recorded',
            comms_export_truncated: 'Only the newest {shown} of {total} entries are included.',
            // Sprint 62.57.0 (Slices A and B) — the governing text and receipt, the handling
            // authority, the result from pack options, the post-result supplement and follow-up,
            // the provisional authority period and its legal reservation.
            // English until this locale is translated (R-57).
            status_result_recorded: 'Result recorded',
            status_result_supplement_required: 'Result recorded — supplement required',
            status_result_supplement_submitted: 'Result recorded — supplement submitted',
            step_result_recorded: 'Result recorded',
            step_result_supplement_required: 'Supplement required after the result',
            step_result_supplement_submitted: 'Supplement submitted after the result',
            timer_authority_review: 'Authority review',
            timer_provisional: 'Provisional — pending legal confirmation',
            timer_obligation_authority: 'The authority’s period, not your organisation’s',
            timer_calendar: 'Counted on the installed public-service calendar of working days and public holidays',
            timer_authority_stated: 'Result date stated by the authority',
            timer_provisional_passed: 'Provisional period passed — no result recorded',
            timer_absence_complete_receipt: 'No provisional date — complete-dossier receipt not recorded',
            timer_absence_calendar: 'No provisional date — the public-service calendar for {year} is not installed',
            timer_absence_no_period: 'No authority period declared',
            timer_absence_governing_text: 'No provisional date — governing text not determined',
            timer_absence_multiple_periods: 'No provisional date — the installed content declares more than one period, and none is chosen',
            timer_absence_uncomputable: 'No provisional date — the declared period cannot be counted as a date',
            timer_absence_calendar_not_declared: 'No provisional date — the declared period names no calendar to count on',
            timer_absence_calendar_unavailable: 'No provisional date — the public-service calendar is not installed',
            timer_absence_reference_mode: 'No provisional date — the content that states the period is installed for reference only',
            timer_absence_unreadable: 'No provisional date — the content that states the period could not be read',
            timer_absence_reappraisal_not_declared: 'No provisional date after the completion — not declared',
            timer_absence_completed_receipt: 'No provisional date — receipt of the completed dossier not recorded',
            timer_period_closed: 'A result is recorded; the period no longer runs',
            timer_absence_heading: 'Authority review',
            reservation_pending: 'Pending legal confirmation',
            comm_pick_empty: 'No communication is recorded on this filing yet. Record it under Authority communications first.',
            comm_pick_failed: 'The communications on this filing could not be read.',
            comm_pick_manual_label: 'Communication record id',
            comm_pick_manual_hint: 'Enter the id of a communication recorded on this filing; it is checked when you save.',
            comm_pick_off_list: 'Communication {value} (not in the list)',
            comm_pick_none: 'No communication',
            comm_pick_option_reference: '{kind}, received {date}, reference {reference}',
            error_communication_invalid: 'Choose a communication recorded on this filing.',
            error_corrected_entry_not_found: 'The entry this corrects was not found on this filing. Choose it again.',
            error_entry_already_corrected: 'That entry has already been corrected. Correct the later entry instead.',
            correction_target_moved: 'The entry you chose was corrected by another entry; this now corrects that entry. Check it before you save.',
            correction_target_required: 'Choose the entry this corrects.',
            receipt_error_already_recorded: 'A receipt of this kind is already recorded. To change it, correct that entry.',
            error_assignee_not_in_organisation: 'A person chosen is not a member of your organisation. Choose someone else.',
            error_filing_changed: 'The filing changed while you were entering this. The page has read it again; what you entered is kept. Save again.',
            error_not_submitted: 'This filing has not been filed, so this cannot be recorded. The page has updated the filing’s details; what you entered is kept.',
            error_field_refused: 'This value was not accepted. Check it and try again.',
            gov_heading: 'Governing text',
            gov_intro: 'The text that governed this dossier when the authority received it, matched from the receipt date your organisation recorded. It is never worked out from the date the filing was created or submitted.',
            gov_not_determined: 'Governing text not determined — record the receipt date',
            gov_reason_receipt_not_evidenced: 'No receipt date has been recorded.',
            gov_reason_no_declaration: 'The installed content declares no governing text for this kind of dossier.',
            gov_reason_receipt_outside_declared_windows: 'The receipt date falls outside every period the installed content declares.',
            gov_reason_declaration_unreadable: 'The installed declaration could not be read.',
            gov_requirement: 'Practice requirement: {title}',
            gov_requirement_framework: 'Practice requirement: {title} ({framework})',
            gov_receipt_date: 'Receipt date used',
            gov_evidenced_by: 'Receipt recorded by',
            gov_end_label: 'End of this period',
            gov_declared_by: 'Declared by',
            gov_declared_by_not_installed: 'A framework that is not installed',
            gov_review_required: 'The stored governing text no longer matches the installed declaration — review.',
            receipt_heading: 'Receipt by the authority',
            receipt_record: 'Record the receipt date',
            receipt_correct: 'Correct the receipt date',
            receipt_form_intro: 'Record the date the authority received the dossier, as its receipt or your evidence shows it. The date decides which text governs the dossier. A correction is a new entry; the earlier one stays in the history.',
            receipt_field_event: 'What the authority received',
            receipt_event_dossier_received: 'Dossier received',
            receipt_event_complete_dossier_received: 'Complete and valid dossier received',
            receipt_field_received_on: 'Date the authority received it',
            receipt_field_postmarked_on: 'Postmark date (dossier sent by post)',
            receipt_field_postmarked_hint: 'Only for a dossier sent by post. Kept beside the receipt date, and read only where the installed text counts the postmark.',
            receipt_field_communication: 'Communication that evidences it (optional)',
            receipt_field_corrects: 'Entry this corrects',
            receipt_field_corrects_none: 'Not a correction',
            receipt_corrects_option: '{event}, {date}',
            receipt_error_date_invalid: 'The date cannot be later than today, and the dates must run in order: postmark, dossier received, then complete and valid dossier received.',
            receipt_error_event_required: 'Choose what the authority received.',
            receipt_error_event_mismatch: 'A correction must state the same thing the authority received as the entry it corrects.',
            receipt_record_failed: 'The receipt could not be recorded. What you entered is kept.',
            receipt_recorded: 'Receipt date recorded.',
            receipt_history: 'Receipt entries',
            receipt_col_event: 'Event',
            receipt_col_received_on: 'Received on',
            receipt_col_postmarked_on: 'Postmark',
            receipt_col_communication: 'Communication',
            receipt_col_correction: 'Correction',
            receipt_col_recorded: 'Recorded',
            receipt_source_submission_acknowledgment: 'Carried over from the date of the authority’s acknowledgement of receipt, recorded with the submission',
            receipt_row_statement: 'Recorded by {person} — ComplianceOne has not verified it.',
            handling_heading: 'Handling authority',
            handling_intro: 'The body the dossier was filed with is the one the installed form names. The body that handles the dossier is recorded only by a person in your organisation, from the authority’s own communication; ComplianceOne never works it out or proposes one.',
            handling_filed_with: 'Filed with',
            handling_filed_with_unknown: 'Not declared by the installed form',
            handling_handled_by: 'Handled by',
            handling_not_known: 'Not known — no transfer recorded',
            handling_other_body: 'Other body named',
            handling_option_not_named: 'A body the installed content no longer names',
            handling_class_label: 'Body',
            handling_named_as: 'Named in the communication',
            handling_undeclared: 'The installed form for this filing declares no handling authority, so none can be recorded here.',
            handling_transfer_basis: 'Transfer basis',
            handling_transfer_none_stated: 'No transfer is provided for in this text',
            handling_transfer_awaiting_receipt: 'The transfer basis is shown once the receipt is recorded',
            handling_criteria: 'Criteria for a transfer',
            handling_may_transfer_to: 'Bodies the installed form names',
            handling_applies_from: 'The installed form states this applies to dossiers received from {date}.',
            handling_record: 'Record the handling authority',
            handling_history: 'Statements recorded',
            handling_none: 'No statement has been recorded.',
            handling_loading: 'Loading the statements…',
            handling_load_failed: 'The statements could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_handling: 'the handling statements',
            handling_pagination: 'Pages of handling statements',
            handling_col_body: 'Body',
            handling_col_basis: 'Basis',
            handling_col_communication: 'Communication',
            handling_col_evidence: 'Evidence',
            handling_col_correction: 'Correction',
            handling_col_recorded: 'Recorded',
            handling_basis_transfer_notice: 'Transfer notice',
            handling_basis_authority_result: 'The authority’s result',
            handling_basis_other_written_communication: 'Other written communication',
            handling_source_corrected: 'Source communication corrected — review',
            handling_communication_value: '{kind}, received {date}',
            handling_communication_unknown: 'A communication not on the first page of the history',
            handling_form_intro: 'State the body that handles the dossier as the authority’s communication names it. Choose it from the bodies the installed form names, or name another body. Nothing is filled in for you.',
            handling_field_basis: 'Basis for the statement',
            handling_field_basis_placeholder: 'Choose the basis',
            handling_field_communication: 'Communication it rests on',
            handling_field_option: 'Body',
            handling_field_option_placeholder: 'Choose the body',
            handling_option_other: 'Another body (name it below)',
            handling_field_body_name: 'The body’s name, as the communication gives it',
            handling_field_body_name_hint: 'Up to {max} characters, copied from the communication.',
            handling_field_corrects: 'Statement this corrects',
            handling_field_corrects_none: 'Not a correction',
            handling_corrects_option: '{body}, recorded {date}',
            handling_error_basis_required: 'Choose the basis for the statement.',
            handling_error_option_required: 'Choose the body, or choose another body and name it.',
            handling_error_option_not_declared: 'That body is not among those the installed form names. Choose another, or name it as another body.',
            handling_record_failed: 'The statement could not be recorded. What you entered is kept.',
            handling_recorded: 'Handling authority recorded.',
            result_heading: 'The authority’s result',
            result_frame: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            result_legacy_frame: 'Result recorded: {label} — recorded before results followed the governing text',
            result_none: 'No result has been recorded.',
            result_label_not_installed: 'Result recorded — its label is not in the installed content',
            result_different_text: 'Recorded under a different governing text — review',
            result_date: 'Date on the notice',
            result_notice_received: 'Date your organisation received the notice',
            result_communication: 'Communication',
            result_notes: 'Notes',
            result_record: 'Record the authority’s result',
            result_needs_receipt: 'Recording a result needs the receipt date first: the result options are those of the text that governed the dossier when the authority received it.',
            result_absent: 'No installed declaration offers result options for this filing, so no result can be recorded here. Record the authority’s notice under Authority communications.',
            result_absent_update: 'No result is recorded on an update dossier. Record the authority’s notice under Authority communications.',
            result_form_intro: 'Record the result as the authority’s notice states it. The options are those of the text that governed the dossier when the authority received it.',
            result_field_option: 'Result, as the notice states it',
            result_field_option_placeholder: 'Choose the result',
            result_field_communication: 'Communication the result comes from',
            result_error_option_required: 'Choose the result the notice states.',
            result_error_option_not_offered: 'That result is not offered for this filing’s governing text.',
            result_error_date_invalid: 'The date must not be later than today or earlier than the day the filing was submitted.',
            result_error_notice_before_result: 'The date your organisation received the notice cannot be before the date on the notice.',
            result_error_not_determined: 'The governing text is not determined. Record the receipt date first.',
            result_error_declaration_absent: 'No installed declaration offers result options for this filing.',
            result_error_status: 'A result cannot be recorded in the filing’s current state. The page has read the filing again; what you entered is kept.',
            result_record_failed: 'The result could not be recorded. What you entered is kept.',
            result_saved: 'The result is recorded.',
            result_correct: 'Correct this result',
            result_correct_intro: 'Record the result again as the authority’s notice states it, and say why. The earlier entry stays in the history. A result can be corrected only while it is the latest result, the filing is not closed, and no supplement request, supplement submission or follow-up has been recorded for it.',
            result_correcting_target: 'You are correcting: {result}, dated {date}',
            result_field_correction_note: 'Why the result is corrected',
            result_corrects_earlier: 'Corrects an earlier entry. Reason: {note}',
            result_earlier_heading: 'Earlier entries',
            result_corrected: 'The result is corrected.',
            result_error_correction_not_open: 'This result can no longer be corrected: it is not the latest result, the filing is closed, or a supplement request, supplement submission or follow-up has been recorded for it. The page has read the filing again.',
            result_error_not_found: 'The result being corrected was not found on this filing. The page has read the filing again; what you entered is kept.',
            result_error_already_corrected: 'This result has already been corrected by another entry. The page is reading the filing again.',
            result_error_correction_blocked: 'Nothing can be saved from this dialog. Close it; what you entered will not be recorded. If the page still offers a correction, start it again from the current result.',
            result_row_statement: 'Recorded by {person} from the authority’s notice — ComplianceOne has not verified it.',
            post_result_citation: 'What the governing text provides after this result: {citation}',
            stated_date_heading: 'Result dates stated by the authority',
            stated_date_record: 'Record a result date the authority stated',
            stated_date_form_intro: 'Record the result date the authority itself states, on a receipt slip or a later notice. It replaces the provisional date. A correction is a new entry.',
            stated_date_field_date: 'Result date the communication states',
            stated_date_field_communication: 'Communication that states it',
            stated_date_field_corrects: 'Entry this corrects',
            stated_date_corrects_option: '{date}, recorded {recorded}',
            stated_date_value: 'Stated by the authority: {date}',
            stated_date_recorded: 'Stated result date recorded.',
            stated_date_record_failed: 'The stated date could not be recorded. What you entered is kept.',
            rsupp_heading: 'Supplement after the result',
            rsupp_intro: 'The result and any concrete request are separate records. Record the items and any period the notice states; ComplianceOne computes no date from the result.',
            rsupp_no_request: 'Supplement required — no request recorded',
            rsupp_requested: 'Supplement requested — not yet submitted',
            rsupp_submitted: 'Supplement submitted',
            rsupp_record: 'Record the supplement request',
            rsupp_no_period: 'No period stated in the notice',
            rsupp_stated_deadline: 'Date stated in the notice: {date}',
            rsupp_item_owner: 'Owner: {person}',
            rsupp_item_task: 'Task assigned',
            rsupp_col_items: 'Items requested',
            rsupp_col_communication: 'Communication',
            rsupp_col_period: 'Period',
            rsupp_col_recorded: 'Recorded',
            rsupp_form_intro: 'Record what the notice asks for, item by item. An item with an owner becomes a task for that person. Only a date the notice itself states is recorded; none is computed.',
            rsupp_field_communication: 'Communication the request comes from',
            rsupp_field_items: 'Items the notice asks for',
            rsupp_field_item: 'Item {n}',
            rsupp_field_item_owner: 'Owner of item {n} (optional)',
            rsupp_add_item: 'Add an item',
            rsupp_remove_item: 'Remove item {n}',
            rsupp_field_period: 'Period the notice states, verbatim',
            rsupp_field_deadline: 'Date the notice states (optional)',
            rsupp_field_deadline_hint: 'Only a date the notice itself states. Each item with an owner becomes a task due on this date, or with no due date when the notice states none.',
            rsupp_error_items_required: 'Add at least one item the notice asks for.',
            rsupp_error_not_open: 'No supplement is open after this result. The page has read the filing again; what you entered is kept.',
            rsupp_record_failed: 'The supplement request could not be recorded. What you entered is kept.',
            rsupp_recorded: 'Supplement request recorded.',
            followup_heading: 'Follow-up after the result',
            followup_intro: 'This result leaves the filing open. Record the reasons the authority stated, the remediation your organisation chose and any enforcement risk. Nothing on any other record changes.',
            followup_not_recorded: 'No follow-up recorded yet',
            followup_refusal: 'The authority’s communication',
            followup_reasons: 'Reasons stated',
            followup_remediation: 'Remediation',
            followup_actions: 'Remediation actions',
            followup_action_due: 'Your organisation’s own date: {date}',
            followup_action_owner: 'Owner: {person}',
            followup_enforcement_risk: 'Potential enforcement risk',
            followup_enforcement_risk_none: 'None stated',
            followup_revision: 'Recorded by {person} on {date}',
            followup_record: 'Record the follow-up',
            followup_revise: 'Revise the follow-up',
            followup_form_intro: 'Record the reasons the notice states and the remediation your organisation chose. Each action’s owner and date are your organisation’s own choice; ComplianceOne computes no date.',
            followup_field_reasons: 'Reasons the notice states',
            followup_field_summary: 'Remediation',
            followup_field_actions: 'Remediation actions',
            followup_field_action: 'Action {n}',
            followup_field_action_title: 'What is to be done (action {n})',
            followup_field_action_owner: 'Owner (action {n})',
            followup_field_action_due: 'Your organisation’s own date (action {n})',
            followup_add_action: 'Add an action',
            followup_remove_action: 'Remove action {n}',
            followup_actions_none: 'No action added.',
            followup_field_enforcement: 'Potential enforcement risk (optional)',
            followup_field_enforcement_hint: 'Leave it empty when none is stated; the record then says so.',
            followup_field_correction_note: 'Why the follow-up is revised',
            followup_error_not_open: 'No follow-up is open for this filing. The page has read the filing again; what you entered is kept.',
            followup_record_failed: 'The follow-up could not be recorded. What you entered is kept.',
            followup_recorded: 'Follow-up recorded.',
            close_title: 'Close the filing',
            close_intro: 'Closing records that your organisation has finished with this filing. It is not a decision of the authority.',
            close_field_reason: 'Closing reason',
            close_field_reason_hint: 'Required where the page says so; otherwise optional.',
            close_refusal_DOSSIER_RESULT_SUPPLEMENT_OPEN: 'The supplement after the result is still open. Record it as submitted before closing.',
            close_refusal_DOSSIER_FAILURE_FOLLOW_UP_OPEN: 'No follow-up is recorded for this result. Record the follow-up, or give the reason for closing without one.',
            close_refusal_DOSSIER_CLOSE_REASON_REQUIRED: 'Give the reason for closing this filing.',
            close_closing: 'Closing…',
            close_failed: 'The filing could not be closed. What you entered is kept.',
            closing_reason_value: 'Closing reason: {reason}',
            column_handled_by: 'Handled by',
            aria_filter_by_handling: 'Filter by handling authority',
            filter_handling_not_known: 'Not known — no transfer recorded',
            filter_handling_other: 'Other body named',
            prior_filing_open_follow_up: 'Open — follow-up recorded',
            prior_filing_open_no_follow_up: 'Open — the result did not pass, and no follow-up is recorded yet',
            prior_filing_body_open: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation filed one: {filing}. A result is recorded on it. For a new purpose or another change to its content, start the update on this assessment instead.',
            // Sprint 62.57.0 (Slices A, B, F; A-1) — the filing record export’s labels.
            record_export_receipt_heading: 'Receipt of the dossier and the governing text',
            record_export_receipt_none: 'No receipt of the dossier has been recorded.',
            record_export_col_event: 'Receipt',
            record_export_col_received_on: 'Received by the authority',
            record_export_col_postmarked_on: 'Postmark',
            record_export_col_status: 'Entry',
            record_export_entry_standing: 'Standing',
            record_export_entry_corrected: 'Corrected by a later entry',
            record_export_governing_text: 'Governing text: {label}',
            record_export_governing_end: 'End of the window: {label}',
            governing_text_not_determined: 'Governing text not determined — record the receipt date',
            record_export_governing_review: 'The recorded governing text no longer matches the installed declaration — review',
            record_export_reservation: 'Pending legal confirmation: {note}',
            record_export_handling_heading: 'Handling authority',
            record_export_filed_with: 'Filed with: {body}',
            record_export_handled_by_unknown: 'Handled by: not known — no transfer recorded',
            record_export_transfer_basis: 'Transfer basis: {text}',
            record_export_transfer_none_stated: 'No transfer is provided for in this text',
            record_export_transfer_no_stamp: 'The transfer basis is shown once the receipt is recorded',
            record_export_col_body: 'Body named in the communication',
            record_export_col_option: 'Declared body',
            record_export_col_basis: 'Basis',
            record_export_col_source: 'Source communication',
            record_export_other_body: 'Other body named',
            record_export_source_corrected: 'Source communication corrected — review',
            record_export_result_heading: 'Result recorded',
            record_export_result_none: 'No result has been recorded.',
            record_export_result_corrected_by: 'Corrected by a later entry',
            record_export_result_corrects: 'Corrects an earlier entry. Reason: {note}',
            record_export_result_label: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            record_export_result_legacy: 'Result recorded: {legacy} — recorded before results followed the governing text',
            record_export_result_dates: 'Date on the notice: {result_date}. Received by the organisation: {received}.',
            result_recorded_under_different_governing_text: 'Recorded under a different governing text — review',
            record_export_result_follow_up: 'What the result opens: {citation}',
            record_export_supplement_heading: 'Supplement requests after the result',
            result_supplement_no_request: 'Supplement required — no request recorded',
            record_export_supplement_period: 'Period stated in the notice: {period}',
            no_period_stated_in_notice: 'No period stated in the notice',
            record_export_follow_up_heading: 'Follow-up to the result',
            record_export_follow_up_none: 'No follow-up has been recorded.',
            record_export_follow_up_reasons: 'Reasons stated: {text}',
            record_export_follow_up_remediation: 'Remediation: {text}',
            record_export_follow_up_risk: 'Potential enforcement risk: {text}',
            record_export_col_action: 'Remediation action',
            record_export_col_owner: 'Person responsible',
            record_export_col_own_date: 'Your organisation’s own date',
            record_export_period_heading: 'The authority’s period',
            record_export_period_stated: 'Result date stated by the authority: {date}',
            record_export_period_counted: 'Counted from {start} on the {calendar} calendar: {date}',
            record_export_period_passed: 'Provisional period passed — no result recorded',
            record_export_period_not_dated: 'No provisional date ({reason})',
            // Sprint 62.57.0 (Slices C and D, list half of AC-F5) — the completion period, the
            // completion request and submission, the update's recipient and competent authority,
            // the list's governing-text column and filters, the requirement link.
            status_result_completion_submitted: 'Completion submitted — no further result recorded',
            step_result_completion_submitted: 'Completion submitted',
            receipt_event_completed_dossier_received: 'Completed dossier received',
            receipt_field_completion_submission: 'Completion submission received',
            receipt_completion_option: 'Completion submitted on {date}',
            receipt_completion_option_recorded: 'Completion recorded on {date}',
            receipt_error_completion_invalid: 'Choose the completion submission the authority received.',
            completion_heading: 'Completion period',
            completion_intro: 'A completion period opens only when your organisation records that a communication asks it to complete the dossier, or records a result for which the governing text provides one. The dates are worked out from the installed regulatory content each time the filing is read.',
            completion_none: 'No completion period is open on this filing.',
            completion_current: 'Current period',
            completion_history: 'Earlier periods',
            completion_date_provisional: 'Provisional date — start event pending legal confirmation',
            completion_date_confirmed: 'Completion date',
            completion_date_label: 'Completion date',
            completion_passed_provisional: 'Provisional completion date passed',
            completion_passed_confirmed: 'Completion period passed',
            completion_running: 'Within the period',
            completion_completed: 'Completion submitted on {date}',
            completion_completed_no_date: 'Completion submitted',
            completion_not_dated: 'No completion date',
            completion_state: 'State',
            completion_notice_date: 'Notice date',
            completion_received_on: 'Received by your organisation',
            completion_authority_stated: 'Date stated by the authority',
            completion_trigger: 'Opened by',
            completion_trigger_completion_request: 'A completion request your organisation recorded',
            completion_trigger_result: 'The result your organisation recorded',
            completion_source: 'Source communication',
            completion_deficiency: 'What the governing text provides',
            completion_citation: 'Citation',
            completion_start_event: 'Start event',
            completion_period_length: 'Period: {period} {unit}',
            completion_unit_calendar_days: 'calendar days',
            completion_unit_working_days: 'working days',
            completion_end_roll_none: 'ComplianceOne shows the end date as counted and does not move it to the next working day when it falls on a day off.',
            completion_end_roll_next_statutory_working_day: 'An end that falls on a day off moves to the next working day.',
            completion_obligation: 'Your organisation’s period, not the authority’s',
            completion_task_raised: 'A task was raised when the date passed.',
            completion_col_period: 'Period',
            completion_absence_trigger_not_documented: 'No completion period — this request cites no recorded communication',
            completion_absence_trigger_not_declared: 'No completion period is declared for this result under the governing text',
            completion_absence_governing_text_not_determined: 'No completion date — the governing text is not determined; record the receipt date',
            completion_absence_no_period_declared: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_periods: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_statutory_calendar_unavailable: 'No completion date — the statutory calendar is not installed',
            completion_absence_receipt_not_evidenced: 'No completion date — your organisation’s receipt of the communication is not recorded',
            completion_absence_declaring_framework_reference_mode: 'No completion date — the content that declares the period is installed for reference only',
            completion_absence_declaring_framework_not_in_force: 'No completion date — the content that declares the period was not in force at the dossier’s receipt',
            completion_absence_declaring_framework_unreadable: 'No completion date — the content that declares the period could not be read',
            completion_absence_no_installed_clock: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_clocks: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_uncomputable_period: 'No completion date — the declared period cannot be counted as a date',
            completion_absence_declaring_framework_phase_reference_unresolved: 'No completion date — the content that declares the period does not date the phase it names',
            completion_absence_declaring_framework_not_operational: 'No completion date — the content that declares the period is not in operation',
            completion_consequence_heading: 'Potential enforcement risk',
            completion_body_handled_by: '{name} — from your recorded handling statement',
            completion_body_filed_with: '{name} — the body the dossier was filed with; no handling statement recorded',
            completion_fine_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            completion_fine_area_articles: 'Articles: {articles}',
            completion_fine_area_reference: 'Installed for reference only',
            completion_fine_area_link: 'Open {area} in Penalty Readiness',
            completion_requests_heading: 'Completion requests recorded',
            completion_requests_none: 'No completion request has been recorded.',
            completion_col_communication: 'Communication',
            completion_col_recorded: 'Recorded',
            completion_col_correction: 'Correction',
            completion_request_record: 'Record a completion request',
            completion_request_correct: 'Correct this request',
            completion_request_intro: 'Choose the communication and state what it asks. ComplianceOne does not read the communication’s wording to decide this.',
            completion_field_communication: 'Communication',
            completion_statement: 'The communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations',
            completion_statement_required: 'Tick the statement to record the request.',
            completion_context_heading: 'What the governing text provides (for reference)',
            completion_field_corrects: 'Request this corrects',
            completion_field_corrects_none: 'Not a correction — a new request',
            completion_corrects_option: '{communication}, recorded {date}',
            completion_request_recorded: 'Completion request recorded.',
            completion_request_failed: 'The completion request could not be recorded. What you entered is kept.',
            completion_error_not_open: 'A completion request can be recorded here only while a supplement request or a result is recorded on the filing. Before a result, record it with the supplement request.',
            completion_request_row: 'Recorded by {person} on {date}',
            completion_request_withdraw: 'Withdraw this entry',
            completion_request_withdraw_title: 'Withdraw this completion request entry',
            completion_request_withdraw_intro: 'Withdraw an entry that recorded a request in error: no completion period is counted from it any more. The entry stays in the history with your note. If another communication asks your organisation to complete the dossier, record a new request instead.',
            completion_request_withdraw_target: 'Entry withdrawn',
            completion_request_withdraw_note: 'Why this entry is withdrawn',
            completion_request_withdraw_note_required: 'Say why the entry is withdrawn.',
            completion_request_withdrawing: 'Withdrawing…',
            completion_request_withdrawn_msg: 'Completion request entry withdrawn.',
            completion_request_withdraw_failed: 'The withdrawal could not be recorded. What you entered is kept.',
            completion_request_withdraw_gone: 'This entry has been corrected or withdrawn meanwhile, or is no longer on this filing. The page has read the filing again.',
            completion_request_withdrawn: 'Withdrawn',
            completion_request_withdraws: 'Withdraws the entry recorded on {date}',
            completion_request_withdrawn_by: 'Withdrawn by a later entry',
            supplement_field_communication: 'Communication that asks for the supplement',
            supplement_communication_hint: 'A completion period is counted only from a recorded communication. Record the communication first under Authority communications.',
            supplement_error_completion_needs_communication: 'Choose the communication before you tick the statement.',
            supplement_request_failed: 'The supplement request could not be recorded. What you entered is kept.',
            result_completion_hint: 'Tick only if this communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations. A result alone opens a completion period only where the governing text provides for one.',
            completion_submit: 'Record completion submitted',
            completion_submitted_on: 'Date submitted',
            completion_submit_intro: 'Record the day your organisation submitted the completed dossier to the authority. A further result may then be recorded on this filing.',
            completion_submit_intro_supplement: 'Record the day your organisation submitted the completed dossier in answer to the supplement request, and what it submitted.',
            supplement_submit_intro: 'Record what your organisation submitted in answer to the supplement request.',
            completion_submitted_msg: 'Completion submitted recorded.',
            completion_submit_failed: 'The submission could not be recorded. What you entered is kept.',
            completion_error_date_invalid: 'The date cannot be in the future or before your organisation received the request.',
            completion_error_status: 'This filing’s state no longer allows this. The page has been read again; what you entered is kept.',
            column_governing_text: 'Governing text',
            aria_filter_by_governing_text: 'Filter by governing text',
            filter_governing_not_determined: 'Not determined',
            filter_completion_passed: 'Completion period: date passed',
            list_completion_due_provisional: 'Provisional completion date: {date}',
            list_completion_due: 'Completion date: {date}',
            chip_governed_by_requirement: 'Filings governed by the requirement you opened',
            chip_governed_by_requirement_title: 'Filings governed by: {title}',
            chip_updates_of: 'Updates of the original filing',
            chip_remove: 'Remove filter: {label}',
            requirement_filings_link: 'Filings this governs: {n}',
            requirement_filings_link_failed: 'The filings this governs could not be counted.',
            change_revise: 'Revise this filing',
            change_update: 'Prepare an update filing',
            change_mode_legend: 'What do you want to do?',
            change_update_form: 'Update form',
            change_update_loading: 'Reading the original filing and the installed update forms…',
            change_update_load_failed: 'The original filing or the installed update forms could not be read.',
            change_update_intro: 'The update is a new filing in ComplianceOne that names this one; preparing it sends nothing to the authority. Choose each body yourself; nothing is chosen for you.',
            update_sent_to: 'Sent to',
            update_sent_to_help: 'The body your organisation files this update with; the cover letter is addressed to it when it is not the body the original was filed with.',
            update_competent: 'Competent to handle',
            update_competent_help: 'The body your organisation records, from the authority’s communications, as competent to handle the dossier. ComplianceOne does not determine it.',
            update_context: 'Original dossier — filed with: {filed_with}. Handled by: {handled_by}.',
            update_context_not_known: 'not known — no transfer recorded',
            update_context_filed_with_not_known: 'not declared by the installed form',
            update_choice_filed_with: 'The body the original dossier was filed with: {name}',
            update_choice_handled_by: 'The body recorded as handling the original dossier: {name}',
            update_choice_named: 'Another body (name it as the communication does)',
            update_named_label: 'Name of the body, as the communication gives it',
            update_reason: 'Why this differs from the body handling the original dossier',
            update_reason_now_required: 'A reason is now required: Why this differs from the body handling the original dossier',
            update_error_choice_required: 'Choose a body.',
            update_error_name_required: 'Enter the body’s name.',
            update_error_reason_required: 'Say why this differs from the body handling the original dossier.',
            update_error_body_invalid: 'This body can no longer be chosen: the original dossier has no recorded handling body. Choose another.',
            update_error_not_declared: 'No installed update form of this kind provides for recording the recipient and competent authority.',
            update_error_form_invalid: 'The update form could not be used for this filing. Start again.',
            update_error_target_invalid: 'An update cannot be prepared from an update. Open the original filing.',
            update_error_locked: 'These can no longer be changed: the update dossier has been prepared for filing or filed.',
            update_error_original_not_found: 'The original filing was not found. Choose it again.',
            update_error_original_invalid: 'This filing cannot be chosen as the original. Choose another.',
            update_error_forms_denied: 'Your role does not give you access to forms, so an update filing cannot be prepared here. Ask an administrator for access to forms.',
            update_original_choose_first: 'Choose the original filing first.',
            update_error_not_submitted: 'The original filing has not been filed, so no update can be prepared from it.',
            update_create_failed: 'The update could not be created. What you entered is kept.',
            update_form_kept: 'An update form was started but not linked to this filing. Prepare the update again from this filing to use it; do not complete it from the assessment’s forms, which would create an update that names no original filing.',
            update_form_reused: 'This update reuses the update form already started from this filing: {form}.',
            completion_mode_changed: 'The completion period on this filing changed while you were entering this. The dialog now records the supplement response only; what you entered is kept.',
            completion_mode_changed_completion: 'The completion period on this filing changed while you were entering this. The dialog now records the completion submitted and asks for the date submitted; what you entered is kept.',
            supplement_request_no_longer_open: 'A supplement request can no longer be recorded on this filing: its status changed meanwhile (for example, another person recorded one). The page has read it again. Close this dialog.',
            update_form_no_longer_offered: 'The update form started earlier is no longer offered by your installed content; choose a form.',
            update_created: 'Update filing created. Fill in the update form.',
            update_creating: 'Creating…',
            update_section_heading: 'Recipient and competent authority',
            update_section_intro: 'Your organisation records where it files the update, and, from the authority’s communications, which body is competent to handle it. ComplianceOne never decides which body handles a dossier or an update.',
            update_not_recorded: 'Not recorded yet.',
            update_record: 'Record the recipient and competent authority',
            update_edit: 'Change the recipient and competent authority',
            update_saving: 'Saving…',
            update_recorded_msg: 'Recipient and competent authority recorded.',
            update_record_failed: 'The recipient and competent authority could not be recorded. What you entered is kept.',
            update_history: 'Earlier records',
            update_recorded_by: 'Recorded by {person} on {date}',
            update_source_filed_with: 'The body the original dossier was filed with',
            update_source_handled_by: 'The body recorded as handling the original dossier',
            update_source_named: 'Named by your organisation',
            update_addressee_review_prompt: 'The body recorded as handling the original dossier has changed since this was recorded — review',
            update_window_rule_flag: 'The text that governs this update provides for filing it with {body}; your organisation recorded {sent_to} — review',
            update_window_rule_flag_unnamed: 'The text that governs this update provides for filing it with another body than the one your organisation recorded ({sent_to}) — review',
            update_bases_disclosure: 'Which body an update is filed with depends on when the authority receives it',
            update_basis_own_window: 'What the text that governs this update provides',
            update_original: 'Updates the filing',
            update_original_open: 'Open the original filing',
            updates_of_this: 'Updates of this dossier ({n})',
            gov_update_own_receipt: 'An update is dated by its own receipt, not by the original dossier’s.',
            completion_task_title_provisional: 'Provisional completion date passed: {filing}',
            completion_task_title_confirmed: 'Completion period passed: {filing}',
            update_original_pick_loading: 'Reading the filings…',
            update_original_pick_empty: 'No filed filing can be chosen as the original.',
            update_original_pick_failed: 'The filings could not be read.',
            update_original_pick_manual_label: 'Original filing record ID',
            update_original_pick_manual_hint: 'Enter the ID of a filed filing; it is checked when you save.',
            update_original_pick_off_list: 'Filing {value} (not in the list)',
            followup_completion_link: 'See the completion period',
            record_export_completion_heading: 'Completion period',
            record_export_completion_none: 'No completion request has been recorded.',
            record_export_completion_trigger_request: 'Completion request recorded from the communication {reference}',
            record_export_completion_trigger_result: 'Result recorded: {label}',
            record_export_completion_dates: 'Notice date: {notice}. Received by your organisation: {received}.',
            record_export_completion_provisional: 'Provisional date — start event pending legal confirmation: {date}',
            record_export_completion_confirmed: 'Completion date: {date}',
            record_export_completion_stated: 'Date stated by the authority: {date}',
            record_export_completion_passed_provisional: 'Provisional completion date passed',
            record_export_completion_passed: 'Completion period passed',
            record_export_completion_completed: 'Completion submitted: {date}',
            record_export_completion_body_not_recorded: 'the body competent to handle the dossier (not recorded on this filing)',
            record_export_completion_area: 'Penalty area: {name} ({articles})',
            record_export_completion_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            record_export_update_heading: 'Recipient and competent authority',
            record_export_update_sent_to: 'Sent to: {body}',
            record_export_update_competent: 'Competent to handle: {body}',
            record_export_update_reason: 'Why this differs from the body handling the original dossier: {reason}',
            record_export_update_basis: '{window}: {basis}',
            record_export_update_window_flag: 'The text that governs this update provides for filing it with another body than the one recorded — review',
        },
        materialisation_health: {
            nav_label: "Salut de l'artefacte de presentació",
            page_title: "Presentació Salut de l'artefacte",
            page_description: "Detectar i corregir problemes de materialització de dossiers. Les llistes estan a l'abast de la vostra organització.",
            failed_title: 'Materialitzacions fallides',
            failed_empty: 'No hi ha materialitzacions fallides, bé.',
            pending_title: 'Aprovat però no materialitzat',
            pending_empty: 'Tots els expedients aprovats tenen un dossier de suport.',
            orphan_title: 'Expedients orfes',
            orphan_description: "Dossiers creats per materialització als quals ja no es fa referència a cap presentació, normalment el perdedor d'una carrera concurrent. Llistat per a la investigació; la neteja és manual.",
            orphan_empty: "No s'han detectat expedients orfes.",
            materialise_button: 'Materialitzar-se ara',
            column_failure_reason: 'Motiu del fracàs',
            column_failed_at: 'Ha fallat a',
            column_retries: 'Reintents',
            column_approved_at: 'Aprovat a',
            column_dossier_id: 'Dossier ID',
            column_source_filing_id: "Arxiu d'origen",
            column_dossier_type: 'Tipus',
            column_created_at: 'Creat a',
        },
        // Sprint 62.35.0 (Slice C) — structural parity; English until Phase 25 (R-57).
        pre_collection: {
            nav_label: 'Pre-Collection Assessments',
            page_title: 'Pre-Collection Assessments',
            page_description: 'An assessment of the protection measures for a data registry item, made before that data is first collected. The completion date may not be after the first collection date.',
            module_guide: 'Record, for each data registry item, the assessment of protection measures made before collection begins: the collection procedure and its version, the assessment, the measures applied, the date the assessment was completed and the date the data was first collected. Whether an assessment is required is set by your installed frameworks.',
            new_button: 'New assessment',
            create_title: 'New pre-collection assessment',
            edit_title: 'Edit pre-collection assessment',
            edit: 'Edit',
            field_registry_item: 'Data registry item',
            registry_item_placeholder: 'Select a registry item',
            registry_item_unresolved: 'Registry item not found',
            registry_load_error: 'The data registry could not be read, so no registry item can be offered.',
            registry_empty: 'The data registry has no items yet.',
            field_procedure_reference: 'Collection procedure reference',
            field_procedure_version: 'Procedure version',
            field_summary: 'Assessment',
            field_measures: 'Measures applied',
            field_measures_hint: 'One measure per line.',
            field_completed_on: 'Assessment completed on',
            field_first_collection_on: 'First collected on',
            date_hint: 'The assessment must be completed on or before the first collection date. Leave the first collection date empty if collection has not begun.',
            error_after_first_collection: 'The assessment must be made before the data is first collected: the completion date is after the first collection date.',
            field_status: 'Status',
            status_draft: 'Draft',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            column_registry_item: 'Registry item',
            column_procedure: 'Collection procedure',
            column_actions: 'Actions',
            not_recorded: 'Not recorded',
            empty: 'No pre-collection assessments have been recorded yet.',
            list_load_error: 'The pre-collection assessments could not be read.',
            reload: 'Reload',
            reload_sr: 'reload the pre-collection assessments',
            registry_reload_sr: 'reload the data registry',
            save: 'Save',
            saving: 'Saving…',
            cancel: 'Cancel',
            created_toast: 'Pre-collection assessment recorded.',
            updated_toast: 'Pre-collection assessment updated.',
            save_error: 'The assessment could not be saved.',
            total: 'Total',
            section_scope: 'What is assessed',
            section_assessment: 'Assessment and dates',
            approved_locked: 'This assessment is approved, so its content cannot be changed. It can only be deprecated.',
            error_conflict: 'This assessment was changed by someone else while you were editing, so your change was not saved. Close this form and open the assessment again to see the current version.',
            error_conflict_refreshed: 'This assessment was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            registry_not_readable: 'Your role cannot read the data registry, so no registry item can be offered here.',
            create_needs_registry_read: 'Your role cannot read the data registry, so it cannot choose the registry item a new assessment needs. Existing assessments can still be edited.',
            registry_item_name_hidden: 'Name not visible to your role',
        },
    },
    classification: {
        access_denied_title: 'Accés denegat',
        access_denied: 'No teniu els permisos necessaris per accedir al mòdul Classificació. Contacta amb el teu administrador.',
        nav: {
            taxonomies: 'Taxonomies',
            labeling: 'Etiquetatge',
            lifecycle: 'Cicle de vida',
            gaps: 'Informe de llacunes',
            exports: 'Exportacions',
        },
        exports: {
            title: 'Exportacions de classificació',
            csv_section: 'Exportacions CSV',
            pdf_section: 'Informes PDF',
            inventory: 'Inventari',
            classification_register: 'Registre de classificació',
            raci: 'Matriu RACI',
            exceptions: 'Excepcions',
            evidence_pack: "Paquet d'evidències",
            delta_report: 'Informe delta',
            executive_summary: 'Resum executiu',
            exporting: 'Exportant…',
            export_success: 'Exportació completada amb èxit.',
            export_error: "L'exportació ha fallat. Si us plau, torneu-ho a provar.",
            csv_download: 'Descàrrega CSV preparada.',
            pdf_download: 'Generació de PDF iniciada.',
            no_data: 'No hi ha dades disponibles per exportar.',
        },
        delta: {
            title: 'Detall dels canvis',
            added: 'Afegits',
            removed: 'Eliminats',
            changed: 'Modificats',
            added_count: 'Afegits ({count})',
            removed_count: 'Eliminats ({count})',
            changed_count: 'Modificats ({count})',
            object_id: "ID d'objecte",
            object_type: "Tipus d'objecte",
            taxonomy: 'Taxonomia',
            level: 'Nivell',
            previous: 'Anterior',
            current: 'Actual',
            no_records: 'Cap registre en aquesta secció.',
            empty_section: 'Aquesta secció és buida.',
        },
        schedule: {
            title: 'Programar cicle de descobriment',
            enabled: 'Activat',
            interval_days: 'Interval (dies)',
            interval_label: 'Executar cada N dies',
            manual_only: 'Només manual',
            every_n_days: 'Cada {n} dies',
            save: 'Desar programació',
            cancel: 'Cancel·lar',
            schedule_updated: 'Programació actualitzada amb èxit.',
        },
        taxonomies: {
            title: 'Taxonomies de classificació',
            new: 'Nova taxonomia',
            edit: 'Editar taxonomia',
            name: 'Nom',
            levels: 'Nivells de classificació',
            seed_defaults: 'Valors inicials predeterminats',
            scope: 'Àmbit',
            scope_organization: 'Organització',
            scope_module_specific: 'Específic del mòdul',
            deprecate: 'Deprecar',
            hide_levels: 'Amagar nivells',
            versions: 'Versions',
            hide_versions: 'Amagar versions',
            add_level: 'Afegir nivell',
            new_level: 'Nou nivell',
            edit_level: 'Editar Nivell',
            code: 'Codi',
            label: 'Etiqueta',
            color: 'Color',
            ordinal: 'Ordinal',
            handling_requirements: 'Requisits de tractament',
            version_history: 'Versió Historial',
            changes: 'Canvis',
            failed_fetch: 'Error en fetch taxonomies. Si us plau, torneu-ho a intentar.',
            failed_save: 'Error en save taxonomy. Si us plau, torneu-ho a intentar.',
            failed_activate: 'Error en activate taxonomy. Si us plau, torneu-ho a intentar.',
            failed_deprecate: 'Error en deprecate taxonomy. Si us plau, torneu-ho a intentar.',
            failed_seed: 'Error en seed default taxonomies. Si us plau, torneu-ho a intentar.',
            failed_fetch_levels: 'Error en fetch taxonomy levels. Si us plau, torneu-ho a intentar.',
            failed_save_level: 'Error en save taxonomy level. Si us plau, torneu-ho a intentar.',
            failed_delete_level: 'Error en delete taxonomy level. Si us plau, torneu-ho a intentar.',
            failed_fetch_versions: 'Error en fetch taxonomy versions. Si us plau, torneu-ho a intentar.',
            loaded_count: 'Carregades {count} taxonomies',
            defaults_seeded: 'Taxonomies per defecte inicialitzades.',
            no_levels: 'No hi ha nivells de classificació definits — afegiu nivells per definir la vostra jerarquia de classificació.',
            no_versions: "Encara no hi ha historial de versions — les versions es creen quan s'actualitza una taxonomia activa.",
        },
        labeling: {
            title: 'Etiquetatge de dades',
            new: 'Nova etiqueta',
            edit: 'Editar etiqueta',
            entity_type: "Tipus d'entitat",
            entity_id: "ID de l'entitat",
            taxonomy: 'Taxonomia',
            classification_level: 'Nivell de classificació',
            applied_by: 'Aplicat per',
            applied_at: 'Aplicat el',
            bulk_apply: 'Aplicació massiva',
            bulk_remove: 'Eliminació massiva',
            all_taxonomies: 'Totes les taxonomies',
            system: 'Sistema',
            dataset: 'Conjunt de dades',
            level_code_placeholder: 'Codi de nivell...',
            select_taxonomy: 'Seleccioneu taxonomy...',
            select_level: 'Seleccioneu level...',
            rationale: 'Fonamentació',
            review_date: 'Revisar Data',
            review: 'Revisar',
            selected: 'seleccionat',
            select_all_labels: 'Selecciona totes les etiquetes',
            loaded_count: 'Carregades {count} etiquetes',
            hide_stale_labels: 'Amagar etiquetes obsoletes',
            show_conflicts: 'Mostrar conflictes',
            hide_conflicts: 'Amagar conflictes',
            label_conflicts: "Conflictes d'etiquetes",
            conflict_reason: 'Motiu del conflicte',
            labels: 'Etiquetes',
            failed_fetch: 'Error en fetch labels. Si us plau, torneu-ho a intentar.',
            failed_fetch_taxonomies: 'Error en fetch taxonomies. Si us plau, torneu-ho a intentar.',
            failed_fetch_levels: 'Error en fetch taxonomy levels. Si us plau, torneu-ho a intentar.',
            failed_save: 'Error en save label. Si us plau, torneu-ho a intentar.',
            failed_review: 'Error en review label. Si us plau, torneu-ho a intentar.',
            failed_bulk: 'Error en bulk label. Si us plau, torneu-ho a intentar.',
            failed_stale: 'Error en fetch stale labels. Si us plau, torneu-ho a intentar.',
            failed_conflicts: 'Error en fetch label conflicts. Si us plau, torneu-ho a intentar.',
            review_permission_denied: 'No teniu permís per revisar aquesta etiqueta.',
            review_not_found: 'Etiqueta no trobada — pot haver estat eliminada.',
            approve_selected: 'Aprovar seleccionades',
            reject_selected: 'Rebutjar seleccionades',
            approved: 'Aprovada',
            rejected: 'Rebutjada',
            labels_count: 'etiquetes',
            failed_count: 'fallides',
            confirm_approve: "Confirmar l'aprovació",
            confirm_reject: 'Confirmar el rebuig',
            approve_n_labels: 'Aprovar {count} etiquetes?',
            reject_n_labels: 'Rebutjar {count} etiquetes?',
            submit_for_review: 'Enviar per a revisió',
            approve: 'Aprovar',
            reject: 'Rebutjar',
            parent_system: 'Sistema pare',
            parent_system_placeholder: 'Seleccioneu el sistema pare...',
            parent_system_help: 'Utilitzat per a la deteccio de conflictes.',
            dataset_level: 'Nivell del conjunt de dades',
            parent_level: 'Nivell pare',
            bulk_created: 'Creat {created} etiqueta/es',
            bulk_skipped: '{skipped} omeses (ja etiquetades)',
            bulk_all_skipped: 'Totes les {skipped} omeses',
            ids_hint: 'Introduiu un o mes ID separats per comes',
            delete_confirm: 'Eliminar etiqueta per a "{id}"?',
        },
        lifecycle: {
            title: 'Monitoratge del cicle de vida de les dades',
            new: 'Nova entrada',
            current_stage: 'Etapa actual',
            last_transition: 'Última transició',
            transition: 'Transició',
            view_history: 'Veure historial',
            stages: {
                creation: 'Creació',
                collection: 'Recollida',
                processing: 'Tractament',
                storage: 'Emmagatzematge',
                retention: 'Retenció',
                disposal: 'Eliminació',
                archived: 'Arxivat',
                active: 'Actiu',
                pending_deletion: 'Pendent Eliminació',
                deleted: 'Eliminat',
                on_hold: 'En espera',
            },
            all_stages: 'Totes les etapes',
            object_id: "ID de l'objecte",
            object_type: "Tipus d'objecte",
            policy_reference: 'Referència de política',
            retention_period_days: 'Retenció Període (days)',
            retention_basis: 'Retenció Base',
            deletion_method: "Mètode d'eliminació",
            deletion_secure_erase: 'Esborrat segur',
            deletion_anonymization: 'Anonimització',
            deletion_physical_destruction: 'Destrucció física',
            deletion_other: 'Altre',
            responsible_party: 'Part responsable',
            responsible_user_id: "ID de l'usuari responsable",
            retention_expiry: 'Expiració de la retenció',
            hold_reason: "Motiu de l'espera",
            hold: 'En espera',
            release: 'Llançament',
            hide_events: 'Amagar Esdeveniments',
            events_timeline: 'Esdeveniments Cronologia',
            add_event: 'Afegir esdeveniment',
            event_type: 'Esdeveniment Tipus',
            create_event: 'Afegir esdeveniment',
            from_stage: "Des de l'etapa",
            to_stage: "Cap a l'etapa",
            actor: 'Actor',
            reason_placeholder: 'Motiu...',
            go: 'Go',
            show_expired_retentions: 'Mostrar Caducat Retentions',
            hide_expired_retentions: 'Amagar Caducat Retentions',
            expired_retentions_alert: 'Caducat Retentions Alerta',
            failed_fetch: 'Error en fetch lifecycle records. Si us plau, torneu-ho a intentar.',
            failed_save: 'Error en save lifecycle record. Si us plau, torneu-ho a intentar.',
            failed_transition: 'Error en transition lifecycle stage. Si us plau, torneu-ho a intentar.',
            failed_hold: 'Error en place hold. Si us plau, torneu-ho a intentar.',
            failed_release: 'Error en release hold. Si us plau, torneu-ho a intentar.',
            failed_fetch_events: 'Error en fetch lifecycle events. Si us plau, torneu-ho a intentar.',
            failed_create_event: 'Error en create lifecycle event. Si us plau, torneu-ho a intentar.',
            failed_expired: 'Error en fetch expired retentions. Si us plau, torneu-ho a intentar.',
            hold_reason_prompt: 'Introduïu hold reason:',
            loaded_count: 'Carregats {count} registres de cicle de vida',
            stage_transition_for: "Transició d'etapa per a {id}",
            event_created: 'Creat',
            event_updated: 'Actualitzat',
            event_approved: 'Aprovat',
            event_deprecated: 'Obsolet',
            event_proposed: 'Proposat',
            event_reviewed: 'Revisat',
            event_archived: 'Arxivat',
            event_restored: 'Restaurat',
        },
        gap_report: {
            title: 'Informe de llacunes de classificació',
            unlabeled: 'Dades sense etiquetar',
            missing_lifecycle: 'Cicle de vida absent',
            stale_labels: 'Etiquetes obsoletes',
            run_detection: 'Executar detecció de buits',
            detecting: 'Detectant...',
            detection_complete: 'Detecció de buits completada. {count} buit(s) detectat(s).',
            detection_failed: 'La detecció de buits ha fallat.',
            by_severity: 'Per Gravetat',
            by_gap_type: 'Per tipus de buit',
            all_gap_types: 'Tots els tipus de bretxa',
            conflicting_labels: 'Etiquetes en conflicte',
            expired_retention: 'Caducat Retenció',
            missing_handling: 'Tractament de valors mancants',
            info: 'Informació',
            gap_type: 'Tipus de bretxa',
            object_id: "ID de l'objecte",
            object_type: "Tipus d'objecte",
            detected_at: 'Detectat el',
            open: 'Obrir',
            acknowledged: 'Reconegut',
            remediated: 'Remediat',
            accepted: 'Acceptat',
            acknowledge: 'Reconèixer',
            remediate: 'Remediar',
            accept_risk: 'Acceptar risc',
            accept_risk_prompt: 'Introduïu risk acceptance justification:',
            failed_fetch: 'Error en fetch gaps. Si us plau, torneu-ho a intentar.',
            failed_acknowledge: 'Error en acknowledge gap. Si us plau, torneu-ho a intentar.',
            failed_remediate: 'Error en remediate gap. Si us plau, torneu-ho a intentar.',
            failed_accept_risk: 'Error en accept gap risk. Si us plau, torneu-ho a intentar.',
            failed_detect: 'Error en run gap detection. Si us plau, torneu-ho a intentar.',
            loaded_count: 'Carregades {count} llacunes de classificació',
            related_object_id: "ID de l'objecte relacionat",
            related_object_type: "Tipus de l'objecte relacionat",
            gap_types: {
                unlabeled_asset: 'Actius sense etiqueta',
                missing_lifecycle: 'Falta el cicle de vida',
                expired_retention: 'Retenció caducada',
                extended_pending_deletion: 'Supressió pendent ampliada',
            },
        },
        label_workflow: {
            title: "Flux de treball d'etiquetes",
            proposed: 'Proposada',
            under_review: 'En revisió',
            approved: 'Aprovada',
            deprecated: 'Obsoleta',
            submit_review: 'Enviar a revisió',
            approve: 'Aprovar etiqueta',
            deprecate: 'Marcar com a obsoleta',
            bulk_approve: 'Aprovació massiva',
            bulk_approve_success: 'Etiquetes aprovades correctament.',
            submit_success: 'Etiqueta enviada a revisió.',
            approve_success: 'Etiqueta aprovada.',
            deprecate_success: 'Etiqueta marcada com a obsoleta.',
        },
        review_tasks: {
            title: 'Tasques de revisió',
            create: 'Nova tasca de revisió',
            system: 'Sistema',
            owner: 'Propietari',
            description: 'Descripció',
            resolution: 'Resolució',
            resolve: 'Resoldre',
            approve: 'Aprovar',
            modify: 'Modificar',
            reject: 'Rebutjar',
            escalate: 'Escalar',
            escalated: 'Tasques escalades',
            no_tasks: 'No hi ha tasques de revisió.',
            resolve_success: 'Tasca de revisió resolta.',
            status_pending: 'Pendent',
            status_resolved: 'Resolt',
            status_escalated: 'Escalat',
            notes: 'Notes',
            create_success: 'Tasca de revisió creada.',
            no_tasks_detail: "Encara no hi ha tasques de revisió — creeu una tasca per assignar revisions d'etiquetes als propietaris de sistemes.",
            label_id: "ID d'etiqueta",
            system_id: 'ID de sistema',
            system_name: 'Nom del sistema',
            owner_id: 'ID del propietari',
            resolution_col: 'Resolucio',
            notes_col: 'Notes',
        },
        exceptions: {
            title: "Cua d'excepcions",
            create: 'Nova excepció',
            severity: 'Gravetat',
            system: 'Sistema',
            owner: 'Propietari',
            resolve: 'Resoldre',
            no_exceptions: "No s'han trobat excepcions.",
            resolve_success: 'Excepció resolta.',
            dashboard_open: 'Excepcions obertes',
            dashboard_resolved: 'Excepcions resoltes',
            dashboard_total: 'Total',
            dashboard_by_severity: 'Per gravetat',
            status_open: 'Obert',
            status_resolved: 'Resolt',
            status_accepted_risk: 'Risc acceptat',
            create_success: 'Excepció creada.',
            no_exceptions_detail: 'Encara no hi ha excepcions — les excepcions es creen a partir de llacunes de gravetat alta que requereixen un tractament especial.',
            gap_id: 'ID de llacuna',
            system_id: 'ID de sistema',
            system_name: 'Nom del sistema',
            owner_id: 'ID del propietari',
            resolution_notes: 'Notes de resolució',
            resolution_notes_placeholder: 'Descriu com es va resoldre aquesta excepció...',
            dashboard_accepted_risk: 'Accepted Risk',
            resolution_outcome: 'Outcome',
            resolve_success_accepted_risk: 'Exception closed as accepted risk.',
        },
        enterprise_map: {
            title: 'Mapa de dades empresarial',
            inventory: 'Inventari de dades',
            total_systems: 'Total de sistemes',
            total_labels: "Total d'etiquetes",
            approved_count: 'Etiquetes aprovades',
            proposed_count: 'Etiquetes proposades',
            no_data: 'No hi ha dades de classificació disponibles.',
            no_approved_labels: 'Encara no hi ha objectes etiquetats — un objecte apareix al mapa de dades tan bon punt té una etiqueta de classificació, ja sigui proposada, en revisió o aprovada.',
            loaded_count: "S'ha carregat el mapa de dades de l'empresa",
        },
        discovery: {
            title: 'Cicles de descobriment',
            create: 'Nou cicle',
            cycle_name: 'Nom del cicle',
            run_delta: 'Executar detecció delta',
            delta_success: 'Detecció delta completada.',
            create_success: 'Cicle de descobriment creat.',
            no_cycles: 'No hi ha cicles de descobriment.',
            status_created: 'Creat',
            status_scanning: 'Escaneig',
            status_review: 'Revisió',
            status_approved: 'Aprovat',
            status_published: 'Publicat',
            status_draft: 'Esborrany',
            running: 'En execució',
            completed: 'Completat',
            cancelled: 'Cancel·lat',
            delta_result_summary: "S'han trobat {added} afegides, {removed} eliminades, {changed} etiquetes canviades.",
            delta_results: 'Resultats delta',
            view_results: 'Veure resultats',
            scope_type: "Tipus d'abast",
            scope_type_all: 'Tots',
            scope_type_systems: 'Sistemes',
            scope_type_datasets: 'Conjunts de dades',
            scope_ids: "Identificadors d'abast",
            scope_ids_placeholder: 'Introduïu els ID separats per comes',
            cycle_explanation: "Un cicle de descoberta escaneja la vostra infraestructura de dades per identificar, classificar i fer un seguiment dels actius de dades. Configureu l'abast per orientar sistemes o conjunts de dades específics.",
            empty_state_title: 'Encara no hi ha cicles de descobriment',
            empty_state_description: 'Creeu el vostre primer cicle de descoberta per començar a escanejar i classificar els vostres actius de dades.',
            empty_state_cta: 'Crear primer cicle',
            validation_name_required: 'El nom del cicle és obligatori',
            validation_scope_type_required: "El tipus d'abast és obligatori",
            validation_scope_ids_required: "Cal com a mínim un identificador d'abast",
        },
        regulatory: {
            sensitivity_importance_info: 'La Llei de dades (60/2024/QH15, requeriment 18e4bfd1) requereix la classificació tant per la sensibilitat com per la importància com a dimensions separades. Creeu una taxonomia per a cada dimensió per satisfer aquest requisit.',
            handling_rules_info: 'Cada nivell de classificació ha de definir regles de gestió per requeriment. b89981ac ("Definir regles de maneig per nivell de classificació").',
            template_internal_ids_info: 'Totes les plantilles de classificació utilitzen identificadors interns (p. ex., vn_data_law_data_classification_record). No es presenta cap plantilla com a formulari oficial del ministeri.',
            ai_risk_tiers_info: "La Llei d'IA (Llei 134/2025/QH15, requisit 3a87ad63) defineix quatre nivells de risc dels sistemes d'IA: mínim, limitat, alt i inacceptable. Creeu una taxonomia de risc d'IA amb aquests nivells.",
            ai_risk_tier_minimal: 'Minim',
            ai_risk_tier_limited: 'Limitat',
            ai_risk_tier_high: 'Alt',
            ai_risk_tier_unacceptable: 'Inacceptable',
            ai_register_info: "Els registres de classificació AI haurien d'enllaçar amb una entrada del Registre del sistema AI (req. 0f4475d3) que captura: nom del sistema, propietari, propòsit, context de desplegament, nivell de risc, implicació del proveïdor.",
            ai_template_internal_info: "Les plantilles de classificació d'IA utilitzen identificadors interns (p. ex., AI-RISK-01). No s'utilitza cap identificador de marcador de posició oficial d'IA (AI-HC-01 fins a AI-HC-08).",
            ai_audit_trail_info: "Els canvis de classificació de AI produeixen una pista d'auditoria versionada per UC-VN-06: actor, marca de temps, valor anterior, valor nou i raonament.",
            lifecycle_data_d1_info: "Les transicions del cicle de vida s'assignen als passos DATA-D1 del procediment de la llei de dades: catalogar, classificar, definir regles de gestió, capturar proves, programar revisions.",
            lifecycle_deletion_link_info: "Un actiu de dades classificat que arribi al final de la seva vida útil hauria d'activar un registre de programació d'eliminació mitjançant el mòdul Orquestració de supressió.",
            lifecycle_evidence_info: "Cada canvi d'estat del cicle de vida (esborrany, actiu, subrevisió, arxivat, suprimit) captura l'actor, la marca de temps i la justificació com a entrada de pista d'auditoria.",
            escalation_timers_info: "UC-VN-10 terminis d'escalada: 5 dies laborables per a la revisió del propietari, 48 hores per a l'escalada d'excepcions, 72 hores per a articles d'alt risc o sensibles.",
            escalation_owner_review: '5 dies laborables',
            escalation_exception: '48 hores',
            escalation_high_risk: '72 hores',
            gap_risk_areas_info: 'Llei de dades requerida. f6c3060a ("Avaluació del risc de les dades") requereix una anàlisi de llacunes en 4 àrees de risc: privadesa, ciberseguretat, control d\'accés i risc operacional.',
            gap_risk_area_privacy: 'Privacitat',
            gap_risk_area_cybersecurity: 'Ciberseguretat',
            gap_risk_area_access: "Control d'acces",
            gap_risk_area_operational: 'Operacional',
            gap_framework_filter_info: 'Filtreu les llacunes per marc normatiu (VN_DATA_LAW o VN_AI_LAW_2026) per veure les llacunes rellevants per a cada àmbit regulador.',
            framework_vn_data_law: 'Llei de Dades VN',
            framework_vn_ai_law: "Llei d'IA VN 2026",
            enterprise_map_approved_info: 'Aquest mapa de dades només mostra els registres de classificació aprovats per UC-VN-10 pas 10 del flux de treball ("Les etiquetes aprovades es converteixen en la veritat actual de l\'empresa"). Queden exclosos els esborranys i els registres proposats.',
            exports_bundle_info: "Els paquets d'exportació inclouen: registre de classificació, regles de gestió, propietat/RACI i paquet d'evidències, que coincideixin amb els requisits d'exportació UC-VN-10 i el format de full de proves DATA-EVD-01.",
        },
    },
    monitoring: {
        no_permission_title: 'Permisos insuficients',
        no_permission_message: 'No teniu permis per accedir a aquest modul de monitoratge.',
        errors: {
            fetch: 'Error en load. Si us plau, torneu-ho a intentar.',
            save: 'Error en save. Si us plau, torneu-ho a intentar.',
            fetch_programs_failed: 'Error en fetch programs. Si us plau, torneu-ho a intentar.',
            fetch_kpi_failed: 'Error en fetch kpi data. Si us plau, torneu-ho a intentar.',
            create_snapshot_failed: 'Error en create snapshot. Si us plau, torneu-ho a intentar.',
            fetch_periods_failed: 'Error en fetch periods. Si us plau, torneu-ho a intentar.',
            create_period_failed: 'Error en create period. Si us plau, torneu-ho a intentar.',
            auto_generate_failed: 'Error en auto-generate periods. Si us plau, torneu-ho a intentar.',
            start_period_failed: 'Error en start period. Si us plau, torneu-ho a intentar.',
            close_period_failed: 'Error en close period. Si us plau, torneu-ho a intentar.',
            save_program_failed: 'Error en save program. Si us plau, torneu-ho a intentar.',
            activate_program_failed: 'Error en activate program. Si us plau, torneu-ho a intentar.',
            pause_program_failed: 'Error en pause program. Si us plau, torneu-ho a intentar.',
            complete_program_failed: 'Error en complete program. Si us plau, torneu-ho a intentar.',
            archive_program_failed: 'Error en archive program. Si us plau, torneu-ho a intentar.',
            fetch_ric_templates_failed: 'Error en fetch ric templates. Si us plau, torneu-ho a intentar.',
            fetch_ric_cases_failed: 'Error en fetch ric cases. Si us plau, torneu-ho a intentar.',
            create_ric_case_failed: 'Error en create ric case. Si us plau, torneu-ho a intentar.',
            transition_ric_failed: 'Error en transition ric case. Si us plau, torneu-ho a intentar.',
            add_follow_up_failed: 'Error en add follow-up. Si us plau, torneu-ho a intentar.',
            respond_follow_up_failed: 'Error en respond to follow-up. Si us plau, torneu-ho a intentar.',
            link_evidence_failed: 'Error en link evidence. Si us plau, torneu-ho a intentar.',
            fetch_task_templates_failed: 'Error en fetch task templates. Si us plau, torneu-ho a intentar.',
            save_task_template_failed: 'Error en save task template. Si us plau, torneu-ho a intentar.',
            delete_task_template_failed: 'Error en delete task template. Si us plau, torneu-ho a intentar.',
            delete_framework_link_failed: 'Error en eliminar l’enllaç al marc normatiu. Si us plau, torneu-ho a intentar.',
            seed_defaults_failed: 'Error en seed defaults. Si us plau, torneu-ho a intentar.',
            fetch_tasks_failed: 'Error en fetch tasks. Si us plau, torneu-ho a intentar.',
            create_task_failed: 'Error en create task. Si us plau, torneu-ho a intentar.',
            instantiate_tasks_failed: 'Error en instantiate tasks. Si us plau, torneu-ho a intentar.',
            start_task_failed: 'Error en start task. Si us plau, torneu-ho a intentar.',
            complete_task_failed: 'Error en complete task. Si us plau, torneu-ho a intentar.',
            defer_task_failed: 'Error en defer task. Si us plau, torneu-ho a intentar.',
            block_task_failed: 'Error en block task. Si us plau, torneu-ho a intentar.',
            override_complete_failed: 'Error en override complete. Si us plau, torneu-ho a intentar.',
            assign_task_failed: 'Error en assign task. Si us plau, torneu-ho a intentar.',
            update_checklist_failed: 'Error en update checklist. Si us plau, torneu-ho a intentar.',
        },
        nav: {
            programs: 'Programes',
            periods: 'Períodes',
            task_templates: 'Plantilles de tasques',
            tasks: 'Tasques',
            kpi: 'Tauler de KPI',
            ric: 'Consultes reguladores',
            framework_link: 'Supervisió de marc',
            authority_readiness: 'Preparació autoritat',
            change_review: 'Revisió de canvis',
            raci: 'Matriu RACI',
        },
        programs: {
            page_title: 'Programes de supervisió',
            title: 'Programes de supervisió',
            new: 'Nou programa',
            edit: 'Editar programa',
            name: 'Nom',
            scope: 'Abast',
            frequency: 'Freqüència',
            owner: 'Responsable',
            activate: 'Activar',
            pause: 'Pausar',
            resume: 'Reprendre',
            complete: 'Completar',
            archive: 'Arxivar',
            custom_frequency_days: 'Personalitzat Freqüència (days)',
            select_user: 'Seleccioneu usuari…',
            assign_training: 'Assignar formació',
            linked_forms: 'Formularis vinculats',
            linked_forms_none: 'Encara no hi ha formularis vinculats',
            loaded_count: '{0} programes carregats',
            scope_labels: {
                full: 'Complet',
                partial: 'Parcial',
                targeted: 'Dirigit',
                consent: 'Consentiment',
                dpia: 'DPIA',
                vendors: 'Proveïdors',
                classification: 'Classificació',
                rights_requests: 'Sol·licituds de drets',
                data_mapping: 'Mapeig de dades',
                transfers: 'Transferències',
                security_monitoring: 'Monitorització de la seguretat',
                authority_response: "Resposta de l'autoritat",
                evidence_preservation: 'Preservació de proves',
                localization_review: 'Revisió de la localització',
                ai_system_registration: 'AI Registre del sistema',
                ai_pre_deployment: 'AI Avaluació prèvia al desplegament',
                ai_transparency: 'AI Comprovació de transparència',
                ai_incident_review: "AI Revisió d'incident",
            },
            framework_cadence_info: 'Cybersecurity Law programs typically use continuous/monthly/quarterly cadences. AI Law programs typically use annual review cadences.',
        },
        periods: {
            page_title: 'Períodes de seguiment',
            title: 'Períodes de revisió',
            new: 'Nou període',
            label: 'Etiqueta del període',
            start_date: "Data d'inici",
            end_date: 'Data de fi',
            reviewers: 'Revisors',
            auto_generate: 'Generació automàtica de períodes',
            start: 'Iniciar',
            close: 'Tancar',
            change_digest: 'Resum de canvis',
            no_digest: 'Cap resum disponible.',
            reviewers_count: 'Recompte de revisors',
            select_program: 'Seleccioneu Program',
            select_program_prompt: 'Seleccioneu program to view periods.',
            hide_digest: 'Amagar resum',
            loaded_count: "S'han carregat {0} períodes",
            periods_auto_generated: 'Períodes generats automàticament',
            label_placeholder: 'p. ex. 2026-Q1',
        },
        task_templates: {
            page_title: 'Plantilles de tasques',
            title: 'Plantilles de tasques',
            new: 'Nova plantilla',
            edit: 'Editar plantilla',
            category: 'Categoria',
            priority: 'Prioritat',
            checklist_items: 'Elements de la llista de verificació',
            effort: 'Esforç estimat (hores)',
            pre_seeded: 'Predefinit',
            seed_defaults: 'Generar valors per defecte',
            add_checklist: 'Afegir element de la llista',
            guidance: 'Notes orientatives',
            all_categories: 'Totes les categories',
            loaded_count: "S'han carregat {0} plantilles de tasques",
            defaults_seeded: 'Plantilles de tasques predeterminades sembrades',
            checklist_item_label: 'Element de la llista de verificació {0}',
            remove_checklist_item: "Elimina l'element de la llista de comprovació {0}",
            category_info: 'Categories cover PDPL compliance, Cybersecurity Law tasks, and AI Law tasks.',
        },
        tasks: {
            page_title: 'Tasques de revisió',
            title: 'Tasques de revisió',
            new: 'Nova tasca',
            assigned_to: 'Assignat a',
            due_date: 'Data límit',
            checklist: 'Llista de verificació',
            start: 'Iniciar',
            complete: 'Completar',
            defer: 'Ajornar',
            block: 'Bloquejar',
            override_complete: 'Completar amb anul·lació',
            link_evidence: 'Vincular evidència',
            direct_upload_label: 'Puja un fitxer de prova',
            instantiate: 'Instanciar des de plantilles',
            defer_reason: "Motiu de l'ajornament",
            override_justification: "Justificació de l'anul·lació",
            filter_assignee: 'Filtrar per assignat',
            select_period: 'Seleccioneu període',
            select_program: 'Seleccioneu Program',
            select_program_period_prompt: 'Seleccioneu program and period to view tasks.',
            assign_placeholder: 'Assignar a...',
            loaded_count: '{0} tasques carregades',
            tasks_instantiated: 'Tasques creades des de plantilles',
            overdue: 'Vençuda',
            checklist_for: 'llista de verificació per a',
            manage_checklist: 'Gestiona la llista de verificació',
            add_checklist_item: 'Afegeix un element',
            checklist_item_label: 'Element',
            checklist_item_description: 'Descripció',
            remove_checklist_item: 'Elimina l’element',
            save_checklist: 'Desa la llista de verificació',
            no_checklist_items: 'Encara no hi ha cap element.',
            confirm_delete_attachment: 'Esteu segur que voleu suprimir aquest fitxer d’evidència? Aquesta acció no es pot desfer.',
        },
        kpi: {
            page_title: 'Tauler KPI',
            title: 'Tauler de KPI',
            kpi_name: 'Nom del KPI',
            current_value: 'Valor actual',
            target: 'Objectiu',
            trends: 'Tendències',
            capture_snapshot: 'Capturar instantània',
            select_program: 'Seleccioneu Program',
            select_program_prompt: 'Seleccioneu program to view KPIs.',
            snapshot: 'Instantània',
            kpi_labels: {
                on_track: 'A Rastrejar',
                at_risk: 'A Risc',
                behind: 'Endarrerit',
                rights_request_sla_rate: 'Tarifa de sol·licitud de drets SLA.',
                vendor_review_currency: 'Moneda de revisió del proveïdor',
                dpia_completion_rate: 'DPIA Percentatge de finalització',
                classification_coverage: 'Cobertura de classificació',
                consent_policy_currency: 'Política de consentiment Moneda',
                security_event_detection_rate: "Taxa de detecció d'esdeveniments de seguretat",
                evidence_preservation_compliance_rate: "Taxa de compliment de la preservació de l'evidència",
                authority_response_timeliness: "Oportunitat de la resposta de l'autoritat",
                localization_readiness_score: 'Puntuació de preparació per a la localització',
                ai_register_completeness: 'AI Complet del registre del sistema',
                pre_deployment_gate_pass_rate: 'Percentatge de superació de la porta prèvia al desplegament',
                transparency_disclosure_coverage: 'Cobertura de divulgació de transparència',
                ai_incident_readiness_score: 'AI Puntuació de preparació per a incidents',
            },
            pdpl_kpi_info: 'The predefined KPI codes are PDPL-specific metrics. For Cybersecurity Law or AI Law monitoring, use framework-specific KPI codes.',
            cybersec_kpi_info: "KPI de la llei de ciberseguretat: taxa de detecció d'esdeveniments de seguretat, taxa de compliment de la preservació de l'evidència, puntualitat de la resposta de l'autoritat, puntuació de preparació per a la localització.",
            ai_law_kpi_info: "KPI de la Llei d'IA: integritat del registre de sistemes d'IA, percentatge de superació de la porta prèvia al desplegament, cobertura de divulgació de transparència, puntuació de preparació per a incidents d'IA.",
            no_data: 'Sense dades disponibles.',
            snapshot_date: "Data d'instantània",
            loaded_count: '{0} instantànies KPI carregades',
            snapshot_captured: 'Instantània KPI capturada',
            showing_chart: 'Mostrant vista de gràfic',
            showing_table: 'Mostrant vista de taula',
            aria_current_values: 'Valors actuals KPI',
            kpi_label: 'KPI',
            actual: 'Real',
            trend: 'Tendència',
            notes: 'Notes',
            trend_up: "A l'alça",
            trend_down: 'A la baixa',
            trend_stable: 'Estable',
        },
        ric: {
            page_title: 'Casos RIC',
            title: 'Casos de consulta reguladora',
            new: 'Nou cas RIC',
            reference: 'Referència',
            regulator: 'Regulador',
            inquiry_type: 'Tipus de consulta',
            deadline: 'Termini de resposta',
            lead: 'Responsable',
            follow_ups: 'Seguiments',
            add_follow_up: 'Afegir seguiment',
            respond: 'Respondre',
            question: 'Pregunta',
            response: 'Resposta',
            asked_at: 'Preguntat el',
            no_follow_ups: 'Sense seguiments encara.',
            received_date: 'Data de recepció',
            responded_at: 'Respost el',
            hide_follow_ups: 'Amagar Follow-ups',
            inquiry_types: {
                general: 'General',
                investigation: 'Investigació',
                audit: 'Auditoria',
                complaint: 'Queixa',
                general_audit: 'Auditoria general',
                rights_requests_review: 'Revisió de sol·licituds de drets',
                cross_border_transfer_review: 'Revisió de transferències transfrontereres',
                vendor_governance_review: 'Revisió de governança de proveïdors',
                consent_management_review: 'Revisió de gestió del consentiment',
                data_mapping_ropa_review: 'Revisió de mapeig de dades / ROPA',
                incident_readiness_review: 'Revisió de preparació davant incidents',
                security_access_control_review: "Revisió de seguretat i control d'accés",
                targeted_processing_review: 'Revisió de processament dirigit',
                bank_readiness_pack: 'Paquet de preparació bancària',
            },
            link_evidence: 'Enllaçar Evidència',
            loaded_count: "S'han carregat {0} casos RIC",
            to_follow_up: 'a la pregunta de seguiment',
            overdue: 'Endarrerit',
            overdue_indicator: "S'ha superat el termini de resposta",
            template_info: 'RIC templates are internal platform templates. They are not official government-mandated forms.',
            status_labels: {
                received: 'Rebut',
                in_triage: 'En triatge',
                evidence_gathering: "Recollida d'evidències",
                response_drafting: 'Esborrany de resposta',
                internal_review: 'Revisió interna',
                submitted: 'Enviat',
                follow_up: 'Seguiment',
                closed: 'Tancat',
            },
        },
        framework_link: {
            title: 'Supervisió vinculada al marc',
            link_program: 'Vincular programa al marc',
            framework_code: 'Codi del marc',
            select_framework: 'Seleccionar un marc...',
            select_program: 'Seleccionar un programa...',
            readiness_score: 'Puntuació de preparació',
            no_links: "No s'han trobat programes vinculats al marc",
            seed_defaults: 'Crear plantilles predeterminades',
            program_id: 'ID del programa',
            program: 'Programa',
        },
        authority_readiness: {
            title: "Preparació per a sol·licituds d'autoritat",
            create_entry: 'Nova entrada de preparació',
            select_framework: 'Seleccionar un marc...',
            framework_code: 'Marc',
            readiness_status: 'Estat de preparació',
            readiness_package: 'Paquet de preparació',
            run_drill: 'Executar simulacre de preparació',
            drill_success: 'Simulacre de preparació registrat',
            last_drill: 'Últim simulacre',
            never_drilled: 'Mai',
            no_readiness: "No s'han trobat dades de preparació d'autoritat",
            entry_created: 'Entrada de preparació creada',
            ready: 'Preparat',
            partial: 'Parcial',
            not_ready: 'No preparat',
            items: 'items',
        },
        change_review: {
            title: 'Revisió de canvis regulatoris',
            new_review: 'Nova revisió de canvis',
            change_id: 'ID de canvi',
            select_change: 'Seleccionar un canvi...',
            select_framework: 'Seleccionar un marc...',
            impact_assessment: "Avaluació d'impacte",
            decision: 'Decisió',
            no_action: 'Sense acció',
            internal_update: 'Actualització interna',
            authority_filing: 'Presentació davant autoritat',
            no_reviews: "No s'han trobat revisions de canvis",
            auto_cycle: 'Cicle de revisió automàtic',
            cadence: 'Freqüència de revisió',
            framework: 'Marc',
        },
        export_csv: 'Exporta CSV',
        exporting: "S'està exportant...",
        export_success: "CSV s'ha exportat correctament.",
        export_failed: "CSV ha fallat l'exportació.",
        export_attestation_pack: 'Exporta el paquet de certificació',
        staleness: {
            badge_label: '{0} elements obsolets',
            days_since: "{0} dies des de l'última actualització",
            detail_title: 'Articles obsolets',
            accept_justification: 'Acceptar amb justificació',
            justification_label: 'Justificació',
            no_stale: "No s'han trobat articles obsolets.",
            threshold_label: 'Llindar de caducitat (dies)',
            threshold_min: 'Mínim 30 dies',
            threshold_max: 'Màxim 730 dies',
            threshold_saved: "S'ha actualitzat el llindar de caducitat.",
        },
        csv_columns: {
            name: 'Nom',
            status: 'Estat',
            owner: 'Propietari',
            created_at: 'Creat a',
            updated_at: 'Actualitzat a les',
            scope: 'Àmbit',
            frameworks: 'Marcs',
            period_label: 'Etiqueta del període',
            start_date: "Data d'inici",
            end_date: 'Data de finalització',
            task_count: 'Recompte de tasques',
            completion_rate: 'Taxa de finalització',
            title: 'Títol',
            assigned_to: 'Assignat a',
            due_date: 'Data de venciment',
            priority: 'Prioritat',
            completed_at: 'Completat a les',
            kpi_code: 'KPI Codi',
            kpi_label: 'KPI Etiqueta',
            target: 'Objectiu',
            actual: 'Real',
            trend: 'Tendència',
            reference: 'Referència',
            regulator: 'Regulador',
            inquiry_type: 'Tipus de consulta',
            lead: 'Plom',
            received_date: 'Data de recepció',
            deadline: 'Data límit',
            framework_code: 'Codi Marc',
            readiness_status: 'Estat de preparació',
            package_count: 'Recompte de paquets',
            change_id: "Canvia l'identificador",
            impact_assessment: "Avaluació d'impacte",
            decision: 'Decisió',
            program: 'Programa',
            readiness_score: 'Puntuació de preparació',
            category: 'Categoria',
            checklist_count: 'Recompte de llista de verificació',
            effort_hours: 'Esforç (hores)',
            version: 'Versió',
            department: 'Departament',
            function_area: 'Àrea de funcions',
            role: 'Rol',
            assignee: 'Cesionari',
            frequency: 'Freqüència',
        },
        raci: {
            page_title: 'Governança Matriu RACI',
            title: 'Governança Matriu RACI',
            department: 'Departament',
            function_area: 'Àrea de funcions',
            responsible: 'Responsable',
            accountable: 'Responsable',
            consulted: 'Consultat',
            informed: 'Informat',
            assign_role: 'Assigna el rol RACI',
            select_program: 'Seleccioneu Programa',
            select_program_prompt: 'Seleccioneu un programa per veure la matriu RACI.',
            no_assignments: "No s'han trobat assignacions RACI.",
            role_updated: 'Tasca RACI actualitzada.',
            role_labels: {
                R: 'Responsable',
                A: 'Responsable',
                C: 'Consultat',
                I: 'Informat',
            },
            loaded_count: "S'han carregat {0} tasques RACI",
            single_accountability_warning: "Només es permet una funció de responsable (A) per àrea de funció. L'assignació d'un nou rol de responsable substituirà l'existent.",
            cybersec_function_areas: "Àrees de funció de la llei de ciberseguretat: resposta a incidents, preservació d'evidències, cooperació amb l'autoritat, revisió de localització.",
            ai_law_function_areas: "AI Àrees de funció de la llei: registre del sistema, avaluació prèvia al desplegament, transparència, gestió d'incidències.",
            suggested_function_areas: 'Àrees de funció suggerides',
        },
        prior_cycle: {
            link_label: 'Enllaç a Cicle previ',
            select_prior: 'Selecciona el cicle anterior...',
            linked: 'Any rere any',
            no_prior: 'No hi ha cicles anteriors disponibles.',
            linked_success: 'Cicle anterior vinculat.',
        },
        digest_table: {
            module: 'Mòdul',
            entity_type: "Tipus d'entitat",
            change_type: 'Canvia el tipus',
            description: 'Descripció',
            changed_at: 'Canviat a les',
        },
        view_related_ric: 'Veure casos RIC relacionats',
        linked_program: 'Programa vinculat',
        nav_raci: 'Matriu RACI',
    },
    incidents: {
        authority_response_draft: {
            draft_label: 'Esborrany de resposta',
            drafting: 'Redacció…',
            results_heading: 'Respostes redactades ({count})',
            tokens_label: '≈ {count} fitxes',
            counsel_label: "Cal revisió de l'advocat:",
            counsel_default: "Almenys una resposta requereix una revisió de l'advocat abans d'enviar-la.",
            requires_counsel_label: "Requereix la revisió de l'advocat.",
            accept_label: "Accepta l'esborrany",
            reject_label: "Rebutja l'esborrany",
            error_generic: "No s'ha pogut redactar la resposta. Torna-ho a provar o millora el text de la consulta.",
            question_action_accepted: 'Accept answer',
            question_action_rejected: 'Reject answer',
            question_action_needs_counsel: 'Needs counsel',
            question_action_aria: '{action} ({question})',
            question_decision_label: 'Decision on this answer: {decision}',
            question_decision_accepted: 'accepted',
            question_decision_rejected: 'rejected',
            question_decision_needs_counsel: 'sent for counsel review',
            download_docx_label: 'Download response template (DOCX)',
            draft_resolved_accepted: 'Every answer is decided. The draft is accepted.',
            draft_resolved_rejected: 'Every answer is decided. The draft is rejected.',
        },
        breach_timeline: {
            compute_label: "Calculeu la cronologia de notificació d'incompliments",
            computing: 'Informàtica...',
            results_heading: "Terminis de notificació d'incompliment ({count})",
            tokens_label: '≈ {count} fitxes',
            handoff_label: 'Forseti suggereix:',
            empty_label: 'Cap marc instal·lat no declara un deure de notificació per a aquest incident.',
            mandatory_label: 'Camps obligatoris:',
            draft_label: 'Esborrany de notificació',
            remaining_label: 'Queden {count}h',
            overdue_label: 'VENÇAT per {count}h',
            acknowledge_label: 'Reconeix la cronologia',
            acknowledging: 'Reconeixement...',
            error_generic: "No s'ha pogut calcular la cronologia de l'incompliment. Torna-ho a provar.",
            tasks_note: 'Urgent tasks were raised for the duties that match this incident’s jurisdictions. Computing again reuses them instead of creating more.',
        },
        breach_determination: {
            yes: 'Confirmat Violació',
            no: 'No un Violació',
            uncertain: 'Pendent',
        },
        errors: {
            fetch: 'Error en load incidents. Si us plau, torneu-ho a intentar.',
            save: 'Error en save incident. Si us plau, torneu-ho a intentar.',
            fetch_failed: 'Error en fetch incidents. Si us plau, torneu-ho a intentar.',
            save_failed: 'Error en save incident. Si us plau, torneu-ho a intentar.',
            transition_failed: 'Error en transition incident. Si us plau, torneu-ho a intentar.',
            close_failed: 'Error en close incident. Si us plau, torneu-ho a intentar.',
            fetch_timeline_failed: 'Error en fetch timeline. Si us plau, torneu-ho a intentar.',
            fetch_decisions_failed: 'Error en fetch decisions. Si us plau, torneu-ho a intentar.',
            triage_failed: 'Error en update triage. Si us plau, torneu-ho a intentar.',
            impact_update_failed: 'Error en update impacte avaluació. Si us plau, torneu-ho a intentar.',
            breach_update_failed: 'Error en update violació avaluació. Si us plau, torneu-ho a intentar.',
            create_timeline_failed: 'Error en create timeline entry. Si us plau, torneu-ho a intentar.',
            create_decision_failed: 'Error en create decision. Si us plau, torneu-ho a intentar.',
            notification_update_failed: 'Error en update notification. Si us plau, torneu-ho a intentar.',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-1) — English until Phase 25 (R-57)
            breach_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the breach assessment was not saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
            notification_status_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the notification status was not changed. The latest version of the incident is being loaded: check the status and change it again if it still needs changing.",
            fetch_tasks_failed: 'Error en fetch tasks. Si us plau, torneu-ho a intentar.',
            create_task_failed: 'Error en create task. Si us plau, torneu-ho a intentar.',
            update_task_status_failed: 'Error en actualitzar estat de la tasca. Si us plau, torneu-ho a intentar.',
            update_checklist_failed: 'Error en update checklist. Si us plau, torneu-ho a intentar.',
            assign_task_failed: 'Error en assign task. Si us plau, torneu-ho a intentar.',
            escalate_task_failed: 'Error en escalate task. Si us plau, torneu-ho a intentar.',
            fetch_templates_failed: 'Error en fetch templates. Si us plau, torneu-ho a intentar.',
            permission_denied: 'No teniu permisos per accedir a aquesta seccio. Contacteu amb el vostre administrador per sol·licitar acces.',
        },
        harm_likelihood: {
            unlikely: 'Improbable',
            possible: 'Possible',
            likely: 'Probable',
            very_likely: 'Molt probable',
        },
        // Sprint 63.37.0 (legal second read S12) — the impact assessment's two vocabularies as words.
        data_category_options: {
            personal_data: 'Personal data',
            sensitive_personal_data: 'Sensitive personal data',
            financial_data: 'Financial data',
            health_data: 'Health data',
            childrens_data: "Children's personal data",
            other: 'Other personal data',
        },
        consequence_options: {
            identity_theft: 'Identity theft',
            financial_loss: 'Financial loss',
            reputational_damage: 'Reputational damage',
            discrimination: 'Discrimination',
            emotional_distress: 'Emotional distress',
            physical_harm: 'Physical harm',
            loss_of_confidentiality: 'Loss of confidentiality',
            loss_of_access: 'Loss of access to the data',
        },
        reported_via: {
            email: 'Correu electrònic',
            phone: 'Telèfon',
            internal_system: 'Intern Sistema',
            third_party: 'Tercer',
            automated_detection: 'Detecció automatitzada',
        },
        task_transition_labels: {
            start: 'Inici',
            complete: 'Completar',
            block: 'Bloquejar',
            defer: 'Ajornar',
            cancel: 'Cancel·la',
            unblock: 'Desbloqueja',
        },
        transition_labels: {
            start: 'Inici',
            complete: 'Completar',
            block: 'Bloquejar',
            defer: 'Ajornar',
        },
        nav: {
            all: 'Tots els incidents',
            tasks: 'Tasques de resposta',
            reports: 'Informes',
            breach_notifications: 'Notificacions de violació',
            supplements: 'Suplements',
            remediation: 'Remediació',
            authority_requests: "Sol·licituds d'autoritat",
            disclosure_register: 'Registre de divulgació',
        },
        list: {
            page_title: 'Incidències',
            title: 'Incidències',
            new: 'Nou incident',
            edit: 'Editar incident',
            incident_title: 'Títol',
            severity: 'Gravetat',
            reported_at: 'Reportat el',
            reported_by: 'Reportat per',
            assigned_to: 'Assignat a',
            view_detail: 'Veure detall',
            reference: 'Referència',
            lead: 'Responsable',
            reported_via: 'Reportat via',
            discovery_date: 'Data de descobriment',
            // fix-sprint #447 W10 — discovery is a date and time
            discovery_hint: 'The date and time the incident was discovered. Hour-based notification clocks run from this moment.',
            discovery_no_time: '(no time of day recorded)',
            discovery_day_only_hint: 'Discovery was recorded as the day {day}, with no time of day. Enter the date and time to record it, or leave it empty to keep the day.',
            hide_details: 'Amagar Detalls',
            close_incident: 'Tancar Incident',
            close_justification_prompt: 'Si us plau, proporcioneu una justificació per tancar aquest incident.',
            justification: 'Justificació',
            lead_id: 'ID del responsable',
            lead_not_assigned: 'Not assigned',
            date_not_recorded: 'Not recorded',
            contact: 'Contacte',
            category: 'Categoria',
            not_assessed: 'No avaluat',
        },
        detail: {
            page_title: "Detall de l'incident",
            title: "Detall de l'incident",
            page_description: 'One incident in full: its overview, triage, impact and breach assessments, timeline, decisions, evidence and audit trail.',
            forseti_heading: 'Forseti for this incident',
            tabs: {
                overview: 'Resum',
                triage: 'Triatge',
                impact: "Avaluació d'impacte",
                breach: 'Avaluació de violació',
                timeline: 'Cronologia',
                decisions: 'Decisions',
                evidence: 'Proves',
                audit_trail: "Pista d'auditoria",
            },
            evidence_upload_label: 'Adjunta un fitxer de prova',
            evidence_uploaded: 'Prova pujada',
            evidence_deleted: 'Prova eliminada',
            evidence_delete_error: 'No s’ha pogut eliminar la prova',
            evidence_uploaded_at: 'Pujat',
            evidence_empty: 'No hi ha fitxers de prova adjunts a aquest incident.',
            evidence_size: 'Mida',
            triage_notes: 'Notes de triatge',
            initial_classification: 'Classificació inicial',
            individuals_affected: 'Nombre de persones afectades',
            data_categories: 'Categories de dades implicades',
            systems_compromised: 'Sistemes compromesos',
            geographic_scope: 'Abast geogràfic',
            breach_determination: 'Determinació de violació de dades',
            // fix-sprint #447 W10 — the notification clock runs from discovery
            notification_clock: {
                label: 'Notification clock',
                started_from_discovery: 'Running from the recorded discovery: {date}',
                started_at_triage: 'Running from {date}, when the breach was determined; no discovery was recorded then. Recording the discovery dates the clock from it.',
                started_from_earlier_discovery: 'Running from {date}, the discovery recorded when the breach was determined. The discovery recorded now is different; the clock is not moved.',
                not_started_no_discovery: 'Not started: a breach was determined, but no discovery date and time is recorded. The clock runs from discovery and is never started from triage. Record when the incident was discovered to start it.',
                not_started_no_determination: 'Not started: no breach has been determined. Once one is, the clock runs from the recorded discovery.',
            },
            notification_required: 'Notificació requerida',
            notification_deadline: 'Termini de notificació',
            pdpc_reportable: 'Notificable al PDPC',
            add_timeline: 'Afegir entrada a la cronologia',
            add_decision: 'Afegir decisió',
            not_found: 'Incident no found.',
            team_members: "Membres de l'equip",
            checklist: 'Llista de verificació',
            completed: 'Completat',
            impact_severity_score: 'Calculated Impacte Gravetat Puntuació',
            records_affected: 'Registres afectats',
            potential_consequences: 'Conseqüències potencials',
            harm_likelihood: 'Probabilitat de dany',
            legal_basis: 'Base jurídica',
            legal_basis_not_recorded: 'Not recorded',
            jurisdictions: 'Jurisdiccions',
            jurisdictions_comma: 'Jurisdiccions (separades per comes)',
            jurisdiction: 'Jurisdicció',
            authority_status: "Estat de l'autoritat",
            subject_status: 'Titular Estat',
            authority_deadline: "Termini de l'autoritat",
            subject_deadline: 'Titular Termini',
            mark_authority_done: 'Marcar autoritat com a completada',
            mark_subject_done: 'Marcar interessat com a completat',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-2) — English until Phase 25 (R-57)
            requirement_statuses: {
                not_required: 'Not required',
                required: 'Required',
                in_progress: 'In progress',
                completed: 'Completed',
                overdue: 'Overdue',
            },
            status_contradiction: {
                declared_required: 'The assessment now declares the {notification} required; its recorded status is {status}.',
                declared_not_required: 'The assessment now declares the {notification} not required; its recorded status is {status}.',
                notifications: {
                    authority_status: 'notification to the authority',
                    subject_status: 'notification to the data subjects',
                    other_status: 'notification to other parties',
                },
            },
            // Sprint 62.35.0 (AC9) — English until Phase 25 (R-57)
            affected_parties: {
                title: 'Affected parties notified',
                intro: 'Organisations and individuals concerned, other than the data subjects, who were told of this incident under this requirement, who are still to be told, or who need not be told.',
                requirement_heading: 'Requirement: {jurisdiction}',
                no_requirements: 'No notification requirement is recorded on this incident, so there is no requirement to record affected parties against.',
                empty: 'No affected party is recorded for this requirement.',
                not_recorded: 'Not recorded',
                add: 'Add affected party',
                add_aria: 'Add affected party for requirement {jurisdiction}',
                edit: 'Edit',
                edit_aria: 'Edit affected party: {name}',
                remove: 'Remove',
                remove_aria: 'Remove affected party: {name}',
                remove_confirm_title: 'Remove this affected party?',
                remove_confirm_message: 'The row is removed from this requirement. The audit trail keeps what it recorded.',
                modal_add_title: 'Add affected party',
                modal_edit_title: 'Edit affected party',
                table_label: 'Affected parties for requirement {jurisdiction}',
                col_party: 'Party',
                col_type: 'Type',
                col_status: 'Status',
                col_date: 'Date notified',
                field_party_name: 'Party name',
                field_party_type: 'Party type',
                field_contact: 'Contact',
                field_status: 'Notification status',
                field_notified_on: 'Date notified',
                field_reason: 'Why notification is not required',
                field_note: 'Note',
                types: {
                    data_owner: 'Data owner',
                    partner: 'Partner',
                    handover_recipient: 'Recipient of a handover',
                    other: 'Other',
                },
                statuses: {
                    pending: 'Pending',
                    notified: 'Notified',
                    not_required: 'Not required',
                },
                errors: {
                    party_name_required: 'Enter the name of the party.',
                    notified_on_required: 'Enter the date the party was notified.',
                    notified_on_in_future: 'The date notified cannot be in the future.',
                    reason_required: 'Enter why notification is not required.',
                    save_failed: 'The affected party could not be saved.',
                    remove_failed: 'The affected party could not be removed.',
                    conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so nothing was saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
                    remove_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the party was not removed. The latest version of the incident is being loaded: check the list and remove the party again if it is still there.",
                    conflict_party_changed: 'Another save changed this affected party after you opened it, so nothing was saved. The form now shows what that save recorded, and the entries you made were not kept: review the values and enter your changes again before saving.',
                    conflict_party_gone: 'Another save removed this affected party after you opened it, so nothing was saved and the form was closed.',
                    conflict_requirement_gone: 'Another save removed this notification requirement from the incident after you opened the form, so nothing was saved and the form was closed.',
                },
                saved: 'Affected party saved',
                removed: 'Affected party removed',
            },
            // Sprint 62.45.0 — the service-interruption window on the incident record
            interruption_window: {
                title: 'Interruption window',
                intro: 'When a system or service stopped, when it came back, and when remediation was completed. Recorded once, here, so anything that follows from these times reads them from this record rather than retyping them. Remediation completed is not the same as closing the incident.',
                // Gate E12.5 pass 3 (QA P2) — shown only to a viewer who can read the register.
                intro_register: 'When a supervised system or service stopped, when it came back, and when remediation was completed. Recorded once, here: the Supervisory Reporting register reads these times for the interruption notice and the incident report and never retypes them. Remediation completed is not the same as closing the incident.',
                started_at: 'Interruption started',
                ended_at: 'Interruption ended',
                remediation_completed_at: 'Remediation completed',
                not_recorded: 'Not recorded',
                ongoing: 'No end is recorded, so the interruption is treated as continuing.',
                edit: 'Edit interruption window',
                save: 'Save interruption window',
                saving: 'Saving…',
                cancel: 'Cancel',
                local_time_hint: 'Your local date and time. Leave a field empty to clear it.',
                unchanged: 'Change a time to save.',
                saved: 'Interruption window saved',
                save_failed: 'The interruption window could not be saved.',
                errors: {
                    start_first: 'Record when the interruption started first.',
                    before_start: 'This cannot be earlier than the interruption start.',
                    invalid: 'The interruption window is not in order, so nothing was saved.',
                },
            },
            not_assessed: 'No avaluat',
            event_type: 'Esdeveniment Tipus',
            timestamp: 'Marca de temps',
            actor: 'Actor',
            no_timeline_events: 'Encara sense esdeveniments a la cronologia.',
            decision_type: 'Tipus de decisió',
            decision: 'Decisió',
            rationale: 'Fonamentació',
            decision_made: 'Decisió presa',
            no_decisions: "Encara no s'han registrat decisions.",
            no_audit_events: "Encara no s'ha registrat cap esdeveniment d'auditoria.",
            export_pdf: 'Exportar PDF',
            pdf_title: "Informe Resum d'Incident",
            description: 'Descripció',
            linked_records: 'Registres Vinculats',
            export_success: 'Incident report exported',
        },
        tasks: {
            page_title: 'Tasques de resposta a incidents',
            title: 'Tasques de resposta a incidents',
            new: 'Nova tasca',
            edit: 'Editar tasca',
            task_type: 'Tipus de tasca',
            assignee: 'Assignat',
            due_date: 'Data límit',
            dependencies: 'Dependències',
            mark_complete: 'Marcar com a completat',
            templates: 'Plantilles de tasques',
            select_incident: 'Seleccionar incident',
            all_statuses: 'Tots els estats',
            all_categories: 'Totes les categories',
            new_response_task: 'Nova tasca de resposta',
            category: 'Categoria',
            priority: 'Prioritat',
            depends_on: 'Depèn de (IDs de tasques separats per comes)',
            checklist_items: 'Elements de la llista de verificació',
            remove: 'Eliminar',
            add: 'Afegir',
            new_checklist_item: 'Nou element de la llista',
            no_templates: 'No hi ha plantilles disponibles.',
            assign_task: 'Assignar tasca',
            assign_to: 'Assignar a',
            assign: 'Assignar',
            select_incident_prompt: 'Seleccioneu un incident per veure les seves tasques de resposta.',
            checklist: 'Llista de verificació',
            hide_checklist: 'Amagar llista de verificació',
            escalate: 'Escalar',
            escalations: 'Escalats',
            no_checklist_items: 'No hi ha elements a la llista de verificació.',
        },
        reports: {
            page_title: "Informes d'incidències",
            title: "Informes d'incidents",
            summary: 'Estadístiques resum',
            by_severity: 'Per gravetat',
            by_status: 'Per estat',
            recent: 'Incidents recents',
            total_incidents: "Total d'incidents",
            open: 'Oberts',
            closed: 'Tancats',
            export_csv: 'Exportar CSV',
            export_pdf: 'Exportar PDF',
            export_success: 'Informe exportat correctament.',
            export_error: "Error en exportar l'informe.",
            reference: 'Referència',
            view_report: 'Veure informe',
        },
        statuses: {
            reported: 'Reportat',
            triage: 'Triatge',
            contained: 'Contingut',
            investigation: 'Investigació',
            remediation: 'Remediació',
            closed: 'Tancat',
            false_positive: 'Fals positiu',
        },
        task_statuses: {
            pending: 'Pendent',
            in_progress: 'En curs',
            completed: 'Completat',
            blocked: 'Bloquejat',
            cancelled: 'Cancel·lat',
        },
        severity: {
            low: 'Baix',
            medium: 'Mitjà',
            high: 'Alt',
            critical: 'Crític',
        },
        task_types: {
            containment: 'Contenció',
            investigation: 'Investigació',
            remediation: 'Remediació',
            communication: 'Comunicació',
            documentation: 'Documentació',
            notification: 'Notificació',
        },
        categories: {
            ai_system: 'AI System',
            data_breach: 'Dades Violació',
            unauthorized_access: 'Accés no autoritzat',
            data_loss: 'Pèrdua de dades',
            system_compromise: 'Compromís del sistema',
            malware: 'Programari maliciós',
            phishing: 'Phishing',
            insider_threat: 'Amenaça interna',
            other: 'Altre',
        },
        reported_via_options: {
            email: 'Correu electrònic',
            phone: 'Telèfon',
            internal_system: 'Intern Sistema',
            third_party: 'Tercer',
            automated_detection: 'Detecció automatitzada',
        },
        harm_likelihood_options: {
            unlikely: 'Improbable',
            possible: 'Possible',
            likely: 'Probable',
            very_likely: 'Molt probable',
        },
        breach_determination_options: {
            confirmed_breach: 'Confirmat Violació',
            suspected_breach: 'Violació sospitada',
            not_a_breach: 'No un Violació',
            pending: 'Pendent',
        },
        timeline_event_types: {
            detection: 'Detecció',
            escalation: 'Escalament',
            containment: 'Contenció',
            investigation_start: 'Inici de la investigació',
            evidence_collected: 'Evidències recopilades',
            notification_sent: 'Notificació Enviat',
            remediation_action: 'Acció de remediació',
            status_change: "Canvi d'estat",
            communication: 'Comunicació',
            other: 'Altre',
        },
        decision_types: {
            containment: 'Contenció',
            notification: 'Notificació',
            remediation: 'Remediació',
            escalation: 'Escalament',
            closure: 'Tancament',
            other: 'Altre',
        },
        task_transitions: {
            start: 'Inici',
            complete: 'Completar',
            block: 'Bloquejar',
            cancel: 'Cancel·lar',
            unblock: 'Desbloquejar',
        },
        announce: {
            loaded: "S'han carregat {count} incidents",
            reports_loaded: "S'han carregat {count} informes d'incidències",
            tasks_loaded: "S'han carregat {count} tasques",
            created: "Incident s'ha creat correctament",
            updated: "L'incident s'ha actualitzat correctament",
            closed: "L'incident s'ha tancat correctament",
            status_changed: "L'estat de l'incident ha canviat",
            triage_completed: "El triatge s'ha completat correctament",
            impact_updated: "Avaluació d'impacte actualitzada",
            breach_updated: "S'ha actualitzat l'avaluació de l'incompliment",
            timeline_added: "S'ha afegit un esdeveniment de cronologia",
            decision_recorded: 'Decisió registrada',
            notification_updated: 'Estat de notificació actualitzat',
            task_created: 'Tasca creada correctament',
            task_status_changed: "L'estat de la tasca ha canviat",
            checklist_completed: 'Element de la llista de verificació completat',
            checklist_unchecked: 'Element de la llista de comprovació desmarcat',
            task_assigned: 'Tasca assignada correctament',
            task_escalated: 'La tasca ha augmentat',
            export_initiated: 'Exportació iniciada',
        },
        breach_notifications: {
            page_title: "Notificacions d'incompliment",
            title: "Notificacions d'incompliment",
            new_notification: 'Notificació nova',
            no_notifications: "No s'han trobat notificacions d'incompliment",
            status: 'Estat',
            submit_to_mps: 'Envia a MPS',
            decision: 'Decisió',
            decision_maker: 'Pres de decisions',
            notify: 'Notificar',
            do_not_notify: 'No aviseu',
            rationale: 'Justificació',
            filing_deadline: 'Termini de presentació',
            overdue: 'Vençut',
            due_soon: 'Proper a vèncer',
            on_track: 'En termini',
            legal_review: 'Revisió jurídica',
            incident_id: "ID d'incident",
            // Fix sprint #447 W1 — the decision moves the status; submission through /submit.
            decision_hint: 'Notify moves the notification to Decision to Notify, ready to draft. Do Not Notify closes it; the decision and its rationale are kept.',
            rationale_required: 'Enter the rationale for this decision.',
            mark_drafted: 'Mark notification drafted',
            submission_channel: 'Submission channel',
            submission_confirmation: 'Confirmation or reference',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            select_channel: 'Select a channel',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            notification_statuses: {
                assessment: 'Avaluació',
                decision_to_notify: 'Decisió a notificar',
                notification_drafted: 'Notificació redactada',
                legal_review: 'Revisió jurídica',
                submitted_to_authority: "Presentat a l'autoritat",
                supplement_requested: 'Suplement sol·licitat',
                supplement_submitted: 'Suplement enviat',
                acknowledged: 'Reconegut',
                closed: 'Tancat',
            },
            select_incident: 'Linked Incident',
            // Breach notifications page layout — summary band, details dialog, forms dialog.
            page_description: 'Decide whether each personal data breach has to be notified, record that decision with its rationale, and follow every filing deadline through to the submission your organisation makes to the authority.',
            kpi_total: 'Notifications',
            kpi_awaiting_decision: 'Awaiting decision',
            kpi_deadline_at_risk: 'Due soon or overdue',
            kpi_submitted: 'Submitted',
            filter_toolbar: 'Filter breach notifications',
            results_count: 'Showing {shown} of {total} notifications',
            incident: 'Incident',
            forms: 'Forms',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            no_forms: 'No forms',
            forms_dialog_title: 'Forms filed on {incident}',
            view_details: 'View details',
            details_title: 'Breach notification details',
            details_decision_section: 'Decision and submission',
            created_at: 'Created',
            submitted_at: 'Submitted on',
        },
        supplements: {
            page_title: 'Sol·licituds de suplements',
            title: 'Sol·licituds de suplements',
            new_supplement: 'Nou suplement',
            no_supplements: "No s'han trobat sol·licituds de suplement",
            request_details: 'Detalls de la sol·licitud',
            response_package: 'Paquet de resposta',
            submit_response: 'Envia la resposta',
            deadline: 'Data límit',
            notification_id: 'Identificador de notificació',
            computed_deadline: 'Computed deadline',
            computed_deadline_none: 'None computed',
            computed_deadline_pending: 'Not started yet',
            enter_notification_id: 'Introduïu un identificador de notificació per veure els suplements.',
            supplement_statuses: {
                requested: 'Sol·licitat',
                drafting: 'Redacció',
                submitted: 'Presentat',
                accepted: 'Acceptat',
                rejected: 'Rebutjat',
            },
        },
        remediation: {
            page_title: 'Accions de reparació',
            title: 'Accions de reparació',
            new_action: 'Nova Acció',
            no_actions: "No s'han trobat accions de correcció",
            description: 'Descripció',
            owner: 'Propietari',
            department: 'Departament',
            deadline: 'Data límit',
            statuses: {
                planned: 'Planificada',
                in_progress: 'En curs',
                completed: 'Completat',
                verified: 'Verificat',
            },
            verify: 'Verificar',
            incident_id: "ID d'incident",
            enter_incident_id: "Introduïu un identificador d'incident per veure les accions de correcció.",
            start: 'Comença',
            complete_action: 'Completar',
        },
        authority_requests: {
            page_title: "Sol·licituds de l'autoritat",
            title: "Sol·licituds de l'autoritat",
            new_request: 'Nova sol·licitud',
            no_requests: "No s'ha trobat cap sol·licitud d'autoritat",
            authority_name: "Nom de l'autoritat",
            request_details: 'Detalls de la sol·licitud',
            response_package: 'Paquet de resposta',
            submission_channel: "Canal d'enviament",
            submit_response: 'Envia la resposta',
            deadline: 'Data límit',
            confirmation: 'Confirmació',
            incident_id_optional: "Identificador d'incidència (opcional)",
            response_context_label: 'Context de resposta (per a la materialització de document)',
            response_context_none: '(ometre materialització)',
            response_context_supplement: 'Resposta de suplement',
            response_context_information: "Resposta a sol·licitud d'informació",
            response_context_inspection: 'Resposta a inspecció',
            materialisation_status_column: 'Document de presentació',
            materialisation_pending: 'Pendent',
            materialisation_success: 'Materialitzat',
            materialisation_failed: 'Fallat',
            materialisation_retry_button: 'Reintentar',
            ar_statuses: {
                received: 'Rebut',
                verified: 'Verificat',
                assembling: 'Muntatge',
                submitted: 'Presentat',
                closed: 'Tancat',
                response_packaged: 'Response Packaged',
                scope_assessed: 'Scope Assessed',
                legal_review: 'Revisió jurídica',
            },
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            channel_email: 'Email',
            verification_checklist_title: 'Verification Checklist',
            checklist: 'Llista de verificació',
            no_checklist_items: 'No checklist items yet. Add items below.',
            add_checklist_item: 'Add checklist item...',
            advance_to_verified: 'Advance to Verified',
            advance_to_scope_assessed: 'Advance to Scope Assessed',
            advance_to_assembling: 'Advance to Assembling',
            advance_to_response_packaged: 'Advance to Response Packaged',
            advance_to_legal_review: 'Advance to Legal Review',
            advance_to_submitted: 'Advance to Submitted',
            advance_to_closed: 'Advance to Closed',
        },
        disclosure_register: {
            page_title: 'Registre de divulgació',
            title: 'Registre de divulgació',
            new_entry: 'Nova entrada',
            no_entries: "No s'han trobat entrades del registre de divulgació",
            disclosure_type: 'Tipus de divulgació',
            disclosure_date: 'Data de divulgació',
            recipient: 'Destinatari',
            authority_name: "Nom de l'autoritat",
            channel: 'Canal',
            content_summary: 'Resum del contingut',
            incident_id_optional: "Identificador d'incidència (opcional)",
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            type_court_order: 'Court-Ordered Disclosure',
            type_data_breach: 'Data Breach Notification',
            type_regulatory_response: 'Regulatory Response',
            type_voluntary: 'Voluntary Disclosure',
            channel_email: 'Email',
        },
        log_disclosure: 'Log Disclosure',
    },
    onboarding: {
        nav: {
            wizard: 'Assistent de configuració',
            tours: 'Visites guiades',
            sample_data: 'Dades de mostra',
        },
        wizard: {
            title: 'Assistent de configuració',
            welcome: 'Benvingut a AesirX ComplianceOne',
            welcome_message: 'Configurem la vostra organització. Aquest assistent us guiarà a través de la configuració inicial.',
            setup_complete: 'Configuració completada',
            skipped_message: "L'assistent de configuració s'ha omès. Podeu executar-lo en qualsevol moment per configurar la vostra organització.",
            start_setup: 'Iniciar configuració',
            skip_wizard: "Ometre l'assistent",
            go_to_admin: 'Aneu a Admin',
            skip_for_now: 'Ometre per ara',
            next_step: 'Pas següent',
            previous_step: 'Pas anterior',
            finish_setup: 'Finalitzar configuració',
            rerun: "Tornar a executar l'assistent",
            step_label: 'Pas {number}',
            progress_label: 'Setup progress: Step {number} of {total}',
            validation_org_name_required: "El nom de l'organització és obligatori.",
            validation_admin_email_required: "El correu electrònic de l'administrador és obligatori.",
            validation_admin_email_invalid: 'Introduïu una adreça de correu electrònic vàlida.',
            validation_cmp_endpoint_required: "El punt d'accés CMP és obligatori quan el CMP està habilitat.",
            steps: {
                license_activation: 'Activació de la llicència',
                org_profile: "Perfil de l'organització",
                module_selection: 'Selecció de mòduls',
                cmp_integration: 'Integració CMP',
                notification_preferences: 'Notificacions',
                review: 'Revisió i confirmació',
            },
            step_descriptions: {
                license_activation: 'Introduïu la vostra clau de llicència per activar el vostre paquet i determinar quins mòduls estan disponibles.',
                org_profile: "Configureu el nom de l'organització, jurisdicció, sector, mida i idioma preferit.",
                module_selection: 'Trieu quins mòduls de compliment activar per a la vostra organització.',
                cmp_integration: 'Configureu la integració amb la Consent Management Platform (opcional).',
                notification_preferences: 'Configureu com rebeu notificacions sobre esdeveniments de compliment.',
                review: 'Reviseu la vostra configuració i finalitzeu la instal·lació.',
            },
            license_step: {
                validate_button: 'Valida la llicència',
                skip_warning: "L'accés al mòdul es limitarà al nivell de paquet predeterminat. Podeu activar una llicència més endavant a Plataforma > Configuració de la llicència.",
            },
            module_descriptions: {
                rights_requests: "Gestioneu sol·licituds d'accés i eliminació de dades personals.",
                data_mapping: 'Mapeu sistemes, fluxos de dades i activitats de processament.',
                governance: "Superviseu proveïdors, aprovacions, desviacions i els registres d'evidència.",
                consent: 'Administreu polítiques de consentiment i catàlegs de finalitats.',
                consent_properties: 'Gestioneu el consentiment en múltiples propietats digitals.',
                assessments: "Realitzeu avaluacions d'impacte i transferències transfrontereres de dades.",
                classification: 'Classifiqueu i etiqueteu actius de dades amb seguiment del cicle de vida.',
                monitoring: 'Revisions de compliment programades i seguiment de KPI.',
                incidents: 'Reporteu i gestioneu incidents de seguretat i privadesa.',
                connectors: "Connecteu-vos a sistemes externs per a l'intercanvi automatitzat de dades.",
                vendor_risk: 'Puntueu proveïdors, rastregeu contractes i gestioneu la diligència deguda.',
                deletion_orchestration: "Coordineu l'eliminació de dades entre sistemes i proveïdors.",
                discovery: 'Descobriu i classifiqueu dades a tota la vostra infraestructura.',
                incident_ops: "Playbooks d'incidents avançats, seguiment d'SLA i escalacions.",
                program_governance: 'Avaluacions de maduresa, informes executius i preparació per a auditories.',
                access_accountability: "Superviseu l'accés d'usuaris, sessions i fluxos de justificació.",
                localization: 'Paquets de compliment específics del mercat i marcs reguladors.',
                sector_overlays: 'Install sector overlays and open the registers an activated overlay brings into use, by sector.',
                partner_workspace: 'Espai de treball de socis i revenedors.',
                privacy_scanner: 'Monitoratge de privadesa en temps real de propietats digitals.',
                onboarding: "Assistent de configuració, visites guiades i gestió de dades d'exemple.",
                compliance_forms: 'Plantilles de compliment interactives i generació de documents.',
                audit_trail: "Registre d'auditoria immutable complet i retenció.",
                organizations: 'Gestió multiorganització i de grups.',
                task_management: 'Planificació unificada de tasques, motor de flux de treball i vistes de calendari.',
            },
            module_categories: {
                data_privacy_rights: 'Privadesa de dades i drets',
                vendor_third_party: 'Gestió de proveïdors i tercers',
                risk_assessment: 'Risc i avaluació',
                operations_automation: 'Operacions i Automatització',
                platform_admin: 'Plataforma i Administració',
            },
            package_tier_label: 'El Vostre Paquet',
            package_module_unavailable: 'No inclòs al vostre paquet',
            select_all: 'Seleccionar Tot',
            deselect_all: 'Desseleccionar Tot',
            load_sample_data: "Carregar dades d'exemple després de la configuració",
            load_sample_data_description: "Instal·leu dades de demostració realistes per explorar la plataforma. Es pot eliminar més tard des d'Incorporació > Dades d'Exemple.",
            language_label: 'Idioma Preferit',
            language_description: "Trieu l'idioma predeterminat per a la vostra organització.",
            admin_account_title: "Compte d'Administrador",
            admin_account_created: "Compte d'administrador creat",
            admin_full_name: 'Nom complet',
            admin_email: 'Adreça de correu electrònic',
            admin_password: 'Contrasenya',
            admin_confirm_password: 'Confirmar contrasenya',
            admin_show_password: 'Mostrar contrasenya',
            admin_hide_password: 'Amagar contrasenya',
            admin_name_placeholder: 'p. ex. Jane Smith',
            admin_email_placeholder: 'admin@example.com',
            admin_password_placeholder: 'Mínim 8 caràcters',
            admin_password_confirm_placeholder: 'Tornar a introduir la contrasenya',
            validation_admin_name_required: "El nom de l'administrador és obligatori",
            validation_admin_password_required: 'La contrasenya és obligatòria',
            validation_admin_password_min_length: 'La contrasenya ha de tenir almenys 8 caràcters',
            validation_passwords_mismatch: 'Les contrasenyes no coincideixen',
            notification_email_enabled: 'Activar Notificacions per Correu',
            notification_email_description: "Rebeu notificacions per correu sobre assignacions de tasques, incompliments d'SLA i esdeveniments de compliment.",
            notification_digest_frequency: 'Freqüència del Resum',
            notification_digest_daily: 'Diari',
            notification_digest_weekly: 'Setmanal',
            notification_digest_none: 'Sense resum',
            notification_in_app_enabled: 'Activar Notificacions de Plataforma',
            notification_in_app_description: 'Mostrar insígnia i calaix de notificacions per a alertes en temps real.',
            jurisdictions: {
                vietnam: 'Vietnam',
                asean: 'ASEAN',
                eu_eea: 'UE/EEE',
                global: 'Global',
            },
            languages: {
                en: 'Anglès',
                vi: 'Tiếng Việt',
                da: 'Dansk',
                sv: 'Svenska',
                nb: 'Norsk Bokmål',
                nn: 'Norsk Nynorsk',
                de: 'Alemany',
                fi: 'Suomi',
                nl: 'Nederlands',
                fr: 'Francès',
                lb: 'Lëtzebuergesch',
                ga: 'Gaeilge',
                es: 'Espanyol',
                ca: 'Català',
                eu: 'basc',
                pt: 'portuguès',
            },
            industries: {
                financial_services: 'Serveis financers',
                healthcare: 'Salut',
                technology: 'Tecnologia',
                telecommunications: 'Telecomunicacions',
                government: 'Govern',
                education: 'Educació',
                manufacturing: 'Manufactura',
                retail: 'Comerç minorista',
                other: 'Altre',
            },
            placeholders: {
                org_name: 'per exemple, ABC',
                admin_email: 'admin@company.vn',
                team_emails: 'usuari1@empresa.vn, usuari2@empresa.vn',
                cmp_endpoint: 'https://cmp.example.com/api',
            },
            fields: {
                org_name: "Nom de l'organització",
                jurisdiction: 'Jurisdicció',
                industry: 'Sector',
                org_size: "Mida de l'organització",
                language: 'Idioma Preferit',
                admin_email: "Correu electrònic de l'administrador",
                team_emails: "Correus electrònics de l'equip (separats per comes)",
                enable_cmp: 'Habilitar integració CMP',
                cmp_endpoint: "Punt d'accés de l'API CMP",
                email_notifications: 'Notificacions per Correu',
                in_app_notifications: 'Notificacions de Plataforma',
                digest_frequency: 'Freqüència del Resum',
            },
            validation_jurisdiction_required: 'Jurisdiction is required.',
        },
        tours: {
            title: 'Visites guiades dels mòduls',
            start: 'Iniciar visita',
            continue_tour: 'Continuar visita',
            replay: 'Repetir visita',
            exit: 'Sortir de la visita',
            skip: 'Ometre visita',
            next: 'Següent',
            previous: 'Anterior',
            finish: 'Finalitzar visita',
            step_of: 'Pas {current} de {total}',
            info: 'Feu visites guiades de cada mòdul per conèixer les funcionalitats i els fluxos de treball clau. Les visites es poden repetir en qualsevol moment.',
            autoplay: 'Reproducció automàtica',
            interactive: 'Interactiu',
            play_all: 'Juga-ho tot',
            search_tours: 'Cerca excursions...',
            duration: 'Durada',
            steps_label: '{count} passos',
            mode: 'Mode',
            pause: 'Pausa',
            resume: 'Currículum',
            speed: 'Velocitat',
            restart: 'Reinicieu',
            reset: 'Restableix',
            reset_success: "El progrés de la gira s'ha restablert.",
            category_core_privacy: 'Privadesa bàsica',
            category_governance: 'Governança',
            category_operations: 'Operacions',
            category_platform: 'Plataforma',
            category_advanced: 'Avançat',
            category_data_privacy_rights: 'Privadesa de dades i drets',
            category_vendor_third_party: 'Gestió de proveïdors i tercers',
            category_risk_assessment: 'Risc i avaluació',
            category_operations_automation: 'Operacions i Automatització',
            category_platform_admin: 'Plataforma i Administració',
            steps_count: '{count} passos',
            autoplay_mode: 'Auto-play mode',
            interactive_mode: 'Interactive mode',
            mode_switch_name: '{current}: switch to {other}',
            revision_conflict: 'This tour has changed since you started it. Restart the tour to see the current version.',
        },
        sample_data: {
            evaluation_title: 'Forseti Evaluation — 100 Vietnam Cases',
            evaluation_info: 'Install synthetic linked assessment records for repeatable evaluation. Loading makes no AI calls. Ordinary sample data is unchanged. Run evaluations separately; legal review is still required.',
            evaluation_loaded: '{count} of {total} cases installed',
            evaluation_load: 'Install evaluation cases',
            evaluation_clear: 'Reset evaluation cases',
            evaluation_confirm_load: 'Install all 100 synthetic Vietnam cases without replacing existing evaluation work? Installation does not run AI.',
            evaluation_confirm_clear: 'Remove only this evaluation dataset’s activity and assessment records? AI runs and audit history remain.',
            title: 'Gestió de dades de mostra',
            select_modules: 'Seleccionar mòduls',
            current_status: 'Estat actual',
            operation_history: "Historial d'operacions",
            load: 'Carregar dades de mostra',
            clear: 'Esborrar dades de mostra',
            processing: 'Processant...',
            loaded: 'Carregat',
            not_loaded: 'No carregat',
            records: 'Registres de mostra',
            confirm_clear_all: 'Clear the sample data of every module? Only cross-industry sample records are removed; real records are not affected. Industry packages are kept: remove a package from its own card.',
            confirm_clear: 'Esteu segur que voleu esborrar totes les dades de mostra dels mòduls seleccionats?',
            confirm_clear_title: "Esborrar dades d'exemple",
            confirm_load_title: 'Carregar dades de mostra',
            confirm_load: 'Esteu segur que voleu carregar dades de mostra per als mòduls seleccionats?',
            action_load: 'Carregar',
            action_clear: 'Esborrar',
            info: 'Carregueu dades de demostració realistes per a formació i demostracions. Les dades de mostra estan etiquetades i es poden eliminar de manera segura sense afectar les dades reals.',
            clear_success: 'Sample data cleared successfully',
            load_success: 'Sample data loaded successfully',
            action_install: 'Install package',
            action_remove: 'Remove package',
            // Sprint 63.37.0 (I2) — the forms module's standing counts in the cross-industry status table.
            status_forms_counts: '{official} official, {internal} internal',
            cross_industry_title: 'Cross-industry sample data',
            cross_industry_info: 'Fictional records for every module, shared by all industries. Choose the modules to load or clear below.',
            packages: {
                title: 'Industry sample data',
                info: 'Each package is one connected, fictional scenario named for its industry, never for a customer. Installing writes sample records in this organisation only, and sends no notification, e-mail, webhook, connector run or AI call.',
                list_aria: 'Industry sample-data packages',
                list_failed: 'The industry packages could not be read.',
                list_retry_subject: 'the industry package list',
                list_forbidden: 'Reading industry packages needs the {permission} permission.',
                empty: 'No industry package is available on this platform.',
                synthetic_badge: 'Synthetic data',
                industry: 'Industry',
                line_of_business: 'Line of business',
                version: 'Version',
                reference_prefix: 'Reference prefix',
                contents_heading: 'Contents',
                contents_item: '{area}: {count} records',
                branches_heading: 'Optional parts',
                branch_default: 'chosen by default',
                branches_none: 'None',
                prerequisites_heading: 'Prerequisites',
                prerequisite_frameworks: 'Frameworks the package reads',
                prerequisite_modules: 'Modules the package writes',
                prerequisite_present: 'Installed',
                prerequisite_missing: 'Not installed',
                prerequisite_module_available: 'Included in your plan',
                prerequisite_module_unavailable: 'Not included in your plan',
                prerequisite_note: 'Records that need a missing framework or module are left out at install and listed with the reason.',
                status_heading: 'Status',
                status_not_installed: 'Not installed',
                status_installed: 'Installed',
                installed_language: 'Content language',
                installed_branches: 'Optional parts',
                installed_at: 'Installed on',
                installed_by: 'Installed by',
                installed_records: 'Records written',
                evidence_pack: 'Audit evidence pack',
                evidence_pack_link: 'Open audit evidence packs (pack {id})',
                evidence_pack_none: 'None assembled',
                attachments: 'Stored synthetic files',
                upgrade_available: 'Version {version} is available. Reinstall to replace the installed version {installed}.',
                install: 'Install',
                reinstall: 'Reinstall',
                remove: 'Remove',
                installing: 'Installing…',
                removing: 'Removing…',
                load_needs: 'Installing needs the {permission} permission.',
                clear_needs: 'Removing needs the {permission} permission.',
                dialog_install_title: 'Install {name}',
                dialog_reinstall_title: 'Reinstall {name}',
                language_legend: 'Content language',
                language_en: 'English',
                language_vi: 'Vietnamese',
                language_required: 'Choose the content language.',
                branches_legend: 'Optional parts',
                branches_none_declared: 'This package has no optional parts.',
                will_write: 'Writes up to {count} sample records in these modules:',
                will_drop: 'A record whose framework is not installed, or whose module your plan does not include, is left out and listed with the reason.',
                nothing_sent: 'No notification, e-mail, webhook, connector run or AI call is sent. When the Immutable Audit Trail is on, the audit entries for the stored files and the evidence pack are anchored like any other audit entry.',
                no_accounts: 'No sign-in accounts are created.',
                reinstall_note: "With the same language and parts, nothing changes. With different ones, the package's records are removed and written again.",
                remove_confirm_title: 'Remove {name}',
                remove_confirm: "Remove {name}? This deletes the {count} records the package wrote in its modules and its {files} stored synthetic files. Its audit evidence pack is deleted too, unless a person reviewed it or a framework evidence pack collected it: then it is kept as your organisation's own record. Audit entries recording the install and the removal stay. Cross-industry sample data and your real records stay.",
                install_failed: 'The package could not be installed.',
                remove_failed: 'The package could not be removed.',
                result_heading: 'Result of the last action',
                result_installed: '{name} installed.',
                result_reinstalled: '{name} reinstalled.',
                result_unchanged: 'Nothing changed: {name} is already installed at this version, in this language, with these parts.',
                result_removed: '{name} removed.',
                result_written: 'Records written: {count}',
                result_removed_first: 'Records removed before writing: {count}',
                result_attachments: 'Synthetic files stored: {count}',
                result_dropped_heading: 'Records left out ({count})',
                result_dropped_none: 'No record was left out.',
                drop_framework_not_installed: 'a framework it needs is not installed ({detail})',
                drop_requirement_not_installed: 'the requirement it maps to is not in the installed framework ({detail})',
                drop_module_not_available: 'your plan does not include this module ({detail})',
                drop_reference_dropped: 'it refers to a record that was left out ({detail})',
                dropped_item: '{record} ({area}): {reason}',
                // Sprint 63.37.0 (I2) — the Forms sub-section of a package card and the install result.
                forms_heading: 'Forms',
                forms_counts: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
                forms_list_aria: 'Forms of {name}',
                form_official: 'Official',
                form_internal: 'Internal — no official form declares this',
                form_not_installed: 'Not installed on this organisation; it needs {framework}',
                form_not_installed_unnamed: 'Not installed on this organisation',
                form_restricted: 'Not available on this organisation: the form it needs is restricted',
                form_not_fileable: 'Not available on this organisation: the installed form cannot be filed',
                forms_failed: 'The forms of this package could not be read.',
                forms_retry_subject: 'the package forms',
                result_forms_written: 'Forms written ({count})',
                result_forms_dropped: 'Forms not written ({count})',
                result_form_written_item: '{record}: {form} ({standing})',
                drop_form_not_installed: 'no installed form answers it ({detail})',
                drop_form_restricted: 'the form it needs is restricted on this organisation ({detail})',
                drop_form_not_fileable: 'the installed form cannot be filed ({detail})',
                evidence_pack_assembled: 'Audit evidence pack assembled, not verified: {id}.',
                evidence_pack_failed: 'The audit evidence pack could not be assembled: {error}. The records stand.',
                evidence_pack_not_assembled: 'No audit evidence pack was assembled.',
                guide_heading: 'Scenario guide',
                guide_intro: 'The demonstration path, in order. Each step opens the record or its list.',
                guide_language: 'Guide language: {language}',
                guide_no_accounts: 'Sample data includes no sign-in accounts. Present with your own account; a record that names a person by account names the user who installed the package.',
                guide_reviewers: "Pending approvals and the impact assessment are decided by your organisation's own reviewers, not during the demonstration.",
                guide_point: 'Point',
                guide_do_not_claim: 'Do not claim',
                guide_step_unavailable: "This step's record was not installed.",
                guide_failed: 'The scenario guide could not be read.',
                guide_retry_subject: 'the scenario guide',
                result_removed_records: 'Records deleted from the modules: {count}',
                result_removed_pack: 'Audit evidence pack removed ({count} stored rows)',
                kept_heading: 'Audit evidence pack versions kept ({count})',
                kept_reviewed: "Pack {pack_id}, version {version}: kept because it was reviewed. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                kept_collected: "Pack {pack_id}, version {version}: kept because a framework evidence pack collected it. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                reinstall_failed_removed: 'The reinstall failed after the previous install was removed: {count} records were deleted and nothing was written. The package is no longer installed.',
                kept_outputs_heading: 'Exports of sample forms kept ({count})',
                kept_output_item: "Export {output_id} (file {file_id}): kept because {cited_by} names its file. It is now your organisation's own file. The sample form it was printed from went with the package.",
                kept_output_by_compliance_execution_proof: 'compliance proof {id}',
                kept_output_by_cross_border_filing_evidence: 'cross-border filing {id}',
                kept_output_by_handbook_assessment_evidence: 'handbook assessment {id}',
                kept_output_by_ai_obligation_evidence: 'AI obligation record {id}',
                kept_output_by_evidence_pack_dispatch_proof: 'evidence pack dispatch {id}',
                kept_output_by_legal_hold: 'legal hold {id}',
                kept_output_by_other: 'record {id}',
                kept_output_by_legal_hold_policy: 'the legal hold set on {id} files',
                kept_held_files_heading: 'Sample files kept, not deleted ({count})',
                kept_held_file_item: "File {file_id}: kept, not deleted, because {cited_by} names it. It stays as your organisation's file, and can be deleted in Files once nothing names it.",
            },
            // Sprint 63.37.0 (rehearsal J2) — the forms row when the load left forms out because their framework is not installed.
            status_forms_counts_not_installed: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
        },
        help: {
            title: "Centre d'ajuda",
            search: 'Cercar',
            search_placeholder: "Cercar articles d'ajuda...",
            clear: 'Esborrar',
            all_categories: 'Totes les categories',
            all_modules: 'Tots els Mòduls',
            back: "Tornar al centre d'ajuda",
            callout_important: 'Important',
            callout_tip: 'Consell',
            no_results: "No s'han trobat articles que coincideixin amb la vostra cerca.",
            no_articles: "Encara no hi ha articles d'ajuda disponibles. Els articles apareixeran aquí a mesura que s'afegeixi contingut.",
            results_for: '{count} resultat(s) per a «{query}»',
            quick_links: {
                getting_started: 'Primers passos',
                getting_started_desc: "Instal·lació, configuració i guia d'inici ràpid.",
                user_guide: "Guia d'usuari",
                user_guide_desc: "Documentació d'ús mòdul per mòdul.",
                admin_guide: "Guia d'administrador",
                admin_guide_desc: 'Configuració, manteniment i resolució de problemes.',
                faq: 'PMF',
                faq_desc: 'Preguntes més freqüents i respostes.',
            },
            search_articles_aria: "Cercar articles d'ajuda",
            article_metadata_aria: "Metadades de l'article",
            faq_editor_title: "Editor de preguntes freqüents del centre d'ajuda",
            faq_editor_description: "Afegiu, editeu i elimineu parells de pregunta i resposta a qualsevol article del centre d'ajuda. L'editor encara no està disponible; aquesta adreça hi està reservada.",
            faq_editor_empty: "L'editor de preguntes freqüents encara no està disponible. Mentrestant, demaneu al vostre administrador que afegeixi seccions de preguntes freqüents a un article del centre d'ajuda.",
            articles_list_aria: "Articles d'ajuda",
            category_nav_aria: 'Navegació per categories',
            quick_links_aria: "Enllaços ràpids a categories d'ajuda",
            returned_to_list: "Retorn a la llista d'articles",
            opening_article: "Obrint l'article: {title}",
            read_article_aria: 'Llegir article: {title}',
            pagination_nav_aria: "Paginació d'articles",
            error_unavailable: "El centre d'ajuda no està disponible en aquest moment. Torneu-ho a provar més tard.",
            retry: 'Tornar-ho a provar',
            list_truncated: 'Only {shown} of {total} help articles are listed. Use search to find the rest.',
        },
        // Sprint 63.11.0 (#34) — the persona and use-case detail pages. Every
        // value these pages show ABOUT a persona or a use case is read from the
        // shared registries; these strings are only the frame around it.
        // English seed for structural parity (R-57): translation is
        // docs/product/phases/phase-25/sprint-58.7.0.md.
        persona_detail: {
            page_title: 'Persona',
            text_loading: 'Loading the role text…',
            text_failed: 'The role text could not be loaded.',
            not_found_title: 'Persona not found',
            not_found_body: 'No persona in the reference set has the identifier "{slug}". Choose one from the persona paths instead.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open onboarding path',
            profile_heading: 'Profile',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            field_quick_start: 'Suggested first task',
            modules_heading: 'Primary modules',
            modules_description: 'The modules this persona works in most, limited to those enabled for your organisation.',
            modules_checking: 'Checking which of these modules are enabled for your organisation…',
            modules_unchecked: 'We could not check which of these modules are enabled for your organisation, so all of them are listed.',
            modules_not_permitted: 'Your role cannot see which modules are enabled for your organisation, so all of them are listed.',
            modules_not_enabled: 'Not enabled for your organisation: {names}',
            modules_none_enabled: "None of this persona's primary modules is enabled for your organisation.",
            use_cases_heading: 'Use cases',
            use_cases_description: 'Validated use cases that name this persona as their primary persona.',
            use_cases_none: 'No validated use case names this persona as its primary persona.',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this persona is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — this persona is not tied to one market.',
            availability_unavailable: 'This persona belongs to a market your organisation has not installed ({market}). Its onboarding path, quick start and recommendations are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this persona is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
            market_vn: 'Vietnam',
            market_eu: 'European Union / EEA',
            market_uk: 'United Kingdom',
            market_gl: 'Global — not tied to one market',
        },
        use_case_detail: {
            page_title: 'Use case {id}',
            description: 'A validated use case from the reference set and the persona it is written for. The full narrative is in the reference document named below; this page does not restate it.',
            not_found_title: 'Use case not found',
            not_found_body: 'No use case in the reference set has the identifier "{id}". Open a persona to see the use cases written for it.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open the onboarding path for its primary persona',
            details_heading: 'Details',
            field_use_case_id: 'Use case ID',
            field_primary_persona: 'Primary persona',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this use case is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — its primary persona is not tied to one market.',
            availability_unavailable: 'This use case belongs to a market your organisation has not installed ({market}). Its primary persona and onboarding path are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this use case is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
        },
    },
    common: {
        vendor: 'Vendor',
        // R-57 — structural parity with English values; real translations are EN and VI.
        list_truncation: {
            loading: 'Loading the list…',
            failed: 'The list could not be loaded, so nothing can be chosen from it right now.',
            partial_with_total: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
            partial_without_total: 'Showing the first {shown}. Narrow the search to reach the rest — how many more there are is not known.',
        },
        // fix-sprint-20261013-0200 R1 — the remedy a failed directory read offers, said once for every
        // DirectoryNotice and UserSelect (components/register/DirectoryNotice.tsx `directoryRetry`).
        directory_retry: 'Reload',
        directory_retrying: 'Reloading…',
        directory_retry_subject: 'the user directory',
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        change: 'Change',
        current: 'current',
        next_page: 'Next page',
        selected: 'selected',
        // Fix sprint F30 — the shared "for example" wrapper. Three pages built
        // `e.g. ${code}` as a template literal, so the words rendered in English in
        // all 16 locales; the abbreviation differs per language (see
        // `escalation_contact_placeholder`, which already had it right).
        example_value: 'p. ex. {value}',
        /* R-57 — structural parity; real translation deferred to the locale programme. */
        export_incomplete_heading: 'This export is incomplete',
        export_incomplete_body: '{count} value(s) in {fieldCount} field(s) ({fields}) were withheld because you are not authorized to view them. The file marks each one.',
        export_not_scanned: 'This export was produced from pre-formatted content, so its completeness was not checked.',
        save: 'Desar',
        cancel: 'Cancel·lar',
        system_actor: 'Sistema',
        delete: 'Eliminar',
        edit: 'Editar',
        create: 'Crear',
        update: 'Actualitzar',
        loading: 'Carregant...',
        error: "S'ha produït un error",
        success: 'Correcte',
        logout: 'Tancar sessió',
        actions: 'Accions',
        retrying: "S'està tornant a provar…",
        permission_denied: 'No tens accés a aquesta pàgina.',
        status: 'Estat',
        description: 'Descripció',
        name: 'Nom',
        created_at: 'Creat el',
        updated_at: 'Actualitzat el',
        yes: 'Sí',
        no: 'No',
        confirm: 'Confirmar',
        confirm_dialog_title: 'Confirm action',
        back: 'Enrere',
        next: 'Següent',
        previous: 'Anterior',
        search: 'Cercar',
        filter: 'Filtrar',
        page_x_of_y: 'Pàgina {page} de {total}',
        pagination_label: 'Paginació',
        view_mode_label: 'Mode de visualització',
        export: 'Exportar',
        exporting: "S'està exportant...",
        sidebar_navigation: "Navegació de la barra lateral de l'administrador",
        items_count: '{count} elements',
        items_count_one: '{count} element',
        export_success: 'Document generat correctament.',
        generate_pdf: 'Generar PDF',
        import: 'Importar',
        download: 'Descarregar',
        upload: 'Carregar',
        view: 'Veure',
        close: 'Tancar',
        close_notification: 'Close notification',
        discard_prompt: {
            title: 'Discard unsaved changes?',
            message: 'This dialog has unsaved changes. Closing it now discards them.',
            confirm: 'Discard',
            cancel: 'Keep editing',
        },
        submit: 'Enviar',
        saved: 'Desat correctament',
        saving: 'Desant...',
        submitting: 'Enviant...',
        created: 'Creat correctament',
        all: 'Tot',
        none: 'Cap',
        select: 'Seleccionar',
        select_user: "Seleccioneu l'usuari...",
        custom_input: 'Introduir manualment',
        required: 'Obligatori',
        optional: 'Opcional',
        notes: 'Notes',
        tags: 'Etiquetes',
        archive: 'Arxivar',
        clone: 'Clonar',
        refresh: 'Actualitzar',
        retry: 'Reintentar',
        // fix-sprint-20260810-2128 residue — English placeholder for structural
        // parity; locale work beyond EN/VI defers to Phase 25 (R-57, sprint-57.7.0).
        options_unreadable: 'The choices for this field could not be read. That does not mean there are none.',
        options_unreadable_pointer: 'Could not be read — see the message below.',
        reset: 'Restablir',
        add: 'Afegir',
        remove: 'Eliminar',
        removing: "S'està eliminant…",
        attachment: 'Adjunt',
        upload_file: 'Puja un fitxer',
        upload_file_required: 'Puja un fitxer (obligatori)',
        // fix-sprint-20260808-0511-followup-2 F1 — FileUpload.tsx drop-zone,
        // progress and validation-toast strings (were hardcoded EN in a shared
        // component reached from every file_upload form field).
        upload_drag_or_browse: 'Arrossega i deixa anar o fes clic per examinar',
        upload_max_size: 'Mida màxima: {size} MB',
        upload_accepted_types: 'Acceptats: {types}',
        upload_in_progress: "S'està pujant {name}...",
        upload_size_exceeded: 'La mida del fitxer supera la mida màxima permesa de {size} MB.',
        upload_type_not_accepted: 'Tipus de fitxer no acceptat. Tipus acceptats: {types}',
        upload_succeeded: 'El fitxer "{name}" s\'ha pujat correctament.',
        upload_failed: "No s'ha pogut pujar el fitxer.",
        select_entity_type_first: "Seleccioneu primer un tipus d'entitat",
        failed_to_remove_file: "No s'ha pogut eliminar el fitxer.",
        entity_type_label: "Tipus d'entitat",
        entity_label: 'Entitat',
        entity_reference: "Referència d'entitat",
        failed_to_load_entities: "No s'han pogut carregar les entitats",
        enable: 'Habilitar',
        disable: 'Deshabilitar',
        no_data: "No s'han trobat dades",
        confirm_delete: 'Esteu segur que voleu eliminar aquest element?',
        confirm_action: 'Esteu segur que voleu continuar?',
        save_success: 'Desat correctament',
        save_error: 'Error en desar',
        delete_success: 'Eliminat correctament',
        delete_error: 'Error en eliminar',
        hour: 'hora',
        hours: 'hores',
        load_error: 'Error en carregar les dades',
        operation_error: 'Operació fallida',
        something_went_wrong: 'Alguna cosa ha anat malament',
        go_home: 'Anar al tauler de control',
        access_denied: 'Accés denegat',
        no_permission: 'No teniu permís per accedir a aquesta pàgina.',
        no_permission_title: 'Permisos insuficients',
        no_permission_message: 'No teniu permís per accedir a aquesta pàgina.',
        unknown: 'Desconegut',
        back_to_dashboard: 'Tornar al tauler de control',
        id: 'ID',
        type: 'Tipus',
        date: 'Data',
        created_by: 'Creat per',
        updated_by: 'Actualitzat per',
        page_of: 'Pàgina {page} de {total}',
        showing_items: 'Mostrant {count} elements',
        items_per_page: 'Elements per pàgina',
        no_results: 'Cap registre coincideix amb els vostres filtres',
        no_items: "No s'han trobat elements",
        sort_by: 'Ordenar per',
        ascending: 'Ascendent',
        descending: 'Descendent',
        all_statuses: 'Tots els estats',
        all_types: 'Tots els tipus',
        all_categories: 'Totes les categories',
        all_severities: 'Totes les gravetats',
        date_range: 'Rang de dates',
        from_date: 'Des de',
        to_date: 'Fins a',
        clear_filters: 'Esborrar filtres',
        apply_filters: 'Aplicar filtres',
        save_changes: 'Desar canvis',
        discard_changes: 'Descartar canvis',
        confirm_discard: 'Teniu canvis sense desar. Voleu descartar-los?',
        copy: 'Copiar',
        duplicate: 'Duplicar',
        move: 'Moure',
        assign: 'Assignar',
        unassign: 'Desassignar',
        approve: 'Aprovar',
        reject: 'Rebutjar',
        deleting: 'Eliminant...',
        processing: 'Processant...',
        loading_page: 'Carregant pàgina...',
        operation_success: 'Operació completada correctament',
        operation_failed: "L'operació ha fallat. Torneu-ho a provar.",
        /*
         * Sprint 64.x-batch-17 — moved out of two inline `{ en, vi }` maps.
         *
         * `CHECKED_DOWNLOAD_FAILED_COPY` lived in `useCheckedDownload.ts` and is
         * the fallback toast for EVERY one of the 26 migrated download surfaces;
         * `RETRIEVAL_DENIED_COPY` lived in `EvidencePackDetailPage.tsx`. I18N.md
         * names `{ en, vi }` lookup objects as the documented root cause of "14
         * languages showing only English" and bans them outright, and neither
         * string had a key in any of the 16 bundles — so both were invisible to
         * the Phase 25 translation batches and could never reach parity.
         *
         * The hook's docblock justified the inline map with "a key earns its place
         * when more than one surface needs the same string", a rule that is not in
         * I18N.md; the string was in fact read by every migrated call site.
         */
        download_failed: 'Download failed.',
        download_forbidden: 'The file could not be downloaded: downloading files needs a permission your role does not hold.',
        export_needs_download: 'Exporting needs the permission to download files, which your role does not hold.',
        export_download_forbidden: 'The export was made, but the file could not be downloaded: downloading files needs a permission your role does not hold.',
        retrieval_denied: 'Retrieving this version requires a permission your role does not hold.',
        retrieval_denied_generic: 'Downloading this file requires a permission your role does not hold.',
        /* Sprint 62.58.0 PR 3 (product-qa walk N-2) — a stored file refused because
           it printed sections read under grants the role does not all hold; the
           grants named in plain words (`download_permission_names`). */
        download_refusal_sections: 'The file could not be downloaded: it includes sections that need these permissions, which your role does not hold: {permissions}.',
        /* Step-2 confirmation P3-4 — the same refusals in *File details*, which
           the reader only opened: nothing was downloaded. */
        details_refusal_sections: 'The file details cannot be shown: the file includes sections that need these permissions, which your role does not hold: {permissions}.',
        details_refused_generic: 'The file details cannot be shown: viewing this file needs a permission your role does not hold.',
        download_permission_names: {
            privacy_notice_read: 'read privacy notices',
            dm_activity_read: 'read processing activities',
            dm_system_read: 'read data-map systems',
            biometric_capture_read: 'read biometric capture components',
        },
        date_cleared: 'Date cleared',
        clear_date: 'Clear date',
        date_picker: 'Date picker',
        date_selected: 'Selected',
        not_measured: 'Not measured',
        empty_dash: '—',
        network_error: 'Error de xarxa. Comproveu la vostra connexió.',
        session_expired: 'La sessió ha caducat. Inicieu sessió de nou.',
        test: 'Provar',
        pause: 'Pausar',
        resume: 'Reprendre',
        activate: 'Activar',
        last_sync: 'Última sincronització',
        health: 'Estat de salut',
        email: 'Correu electrònic',
        role: 'Rol',
        title: 'Títol',
        owner: 'Responsable',
        priority: 'Prioritat',
        category: 'Categoria',
        details: 'Detalls',
        overview: 'Resum',
        configuration: 'Configuració',
        history: 'Historial',
        new_item: 'Nou',
        edit_item: 'Editar',
        select_option: 'Seleccionar...',
        total: 'Total',
        active: 'Actiu',
        inactive: 'Inactiu',
        enabled: 'Habilitat',
        disabled: 'Deshabilitat',
        last_login: 'Últim inici de sessió',
        never: 'Mai',
        password_changed: 'Contrasenya canviada. Es tancarà la vostra sessió.',
        password_change_failed: 'Error en canviar la contrasenya',
        passwords_mismatch: 'Les contrasenyes no coincideixen',
        install: 'Instal·lar',
        install_named: 'Instal·lar {name}',
        uninstall: 'Desinstal·lar',
        installed: 'Instal·lat',
        available: 'Disponible',
        catalog: 'Catàleg',
        published: 'Publicat',
        version: 'Versió',
        market: 'Mercat',
        language: 'Idioma',
        framework: 'Marc regulador',
        template: 'Plantilla',
        connector_type: 'Tipus de connector',
        polling_interval: 'Interval de consulta (minuts)',
        new_instance: 'Nova instància',
        change_password: 'Canviar contrasenya',
        current_password: 'Contrasenya actual',
        new_password: 'Nova contrasenya',
        confirm_password: 'Confirmar contrasenya',
        comments: 'Comentaris',
        completed: 'Completat',
        contact: 'Contacte',
        hide: 'Amagar',
        hide_details: 'Amagar Detalls',
        label: 'Etiqueta',
        max: 'Max',
        min: 'Min',
        na: 'N/A',
        of: 'of',
        showing: 'Mostrant',
        page: 'Pàgina',
        preview: 'Previsualització',
        print: 'Imprimir',
        show: 'Mostrar',
        status_active: 'Actiu',
        status_completed: 'Completat',
        status_draft: 'Esborrany',
        status_in_progress: 'En curs',
        status_pending: 'Pendent',
        view_details: 'Veure detalls',
        all_priorities: 'Totes les prioritats',
        pipeline: 'Gasoducte',
        all_requests: 'Tots Sol·licituds',
        schedule: 'Programar',
        deploy: 'Desplegar',
        rollback: 'Revertir',
        advance_stage: 'Avançar etapa',
        submit_for_approval: 'Enviar per Approval',
        approval_log: "Registre d'aprovació",
        approver: 'Aprovador',
        decision: 'Decisió',
        decided_at: 'Decidit el',
        rollout_stages: 'Etapes de desplegament',
        approval_comment: "Comentari d'aprovació",
        optional_comment: 'Comentari opcional...',
        impact_summary: 'Impacte Resum',
        target_property_ids: 'IDs de propietats de destinació (separats per comes)',
        policy_id: 'Política ID',
        target_version: 'Versió de destinació',
        target_version_placeholder: 'p. ex. 2.0.0',
        scheduled_label: 'Programat',
        scheduled_start: 'Programat Inici',
        scheduled_end: 'Programat Fi',
        properties_count: '{count} propietats',
        results: 'Resultats',
        comparison_results: 'Resultats de comparació',
        past_comparison_reports: 'Informes de comparació anteriors',
        scanning: 'Escanejant...',
        run_inconsistency_scan: 'Executar Inconsistency Escanejar',
        property_ids_label: 'IDs de propietat (separats per comes, mín 2)',
        comparison_type: 'Tipus de comparació',
        golden_template_id: 'ID de plantilla de referència (opcional)',
        leave_blank_compare: 'Deixeu en blanc per comparar propietats entre elles',
        add_notes_placeholder: 'Afegir your analysis notes here...',
        covered_of_active: '{covered} de {active} propietats actives cobertes',
        fetch_error: 'Error en fetch data. Si us plau, torneu-ho a intentar.',
        run_comparison_error: 'Error en run comparison. Si us plau, torneu-ho a intentar.',
        save_notes_error: 'Error en save notes. Si us plau, torneu-ho a intentar.',
        scan_complete: 'Inconsistency scan completat amb èxit',
        scan_error: 'Error en run inconsistency scan. Si us plau, torneu-ho a intentar.',
        min_two_properties: 'Proporcioneu almenys 2 IDs de propietat per a la comparació.',
        comma_separated: 'separats per comes',
        language_english: 'Anglès',
        language_vietnamese: 'Vietnamita',
        language_vietnamese_native: 'Tiếng Việt',
        author: 'Autor',
        dependencies: 'Dependències',
        checksum: 'Suma de verificació',
        changelog: 'Registre de canvis',
        tier: 'Nivell',
        phone: 'Telèfon',
        launch: 'Llançar',
        complete: 'Completar',
        back_to_list: 'Enrere a Llista',
        clear_selection: 'Esborrar selecció',
        deactivate: 'Desactivar',
        scope: 'Àmbit',
        deadline: 'Termini',
        progress: 'Progrés',
        frequency: 'Freqüència',
        reviewer: 'Revisor',
        user: 'Usuari',
        campaign: 'Campanya',
        items: 'Elements',
        next_run: 'Següent Executar',
        last_run: 'Últim Executar',
        overdue: 'VENÇUT',
        creating: 'Creant...',
        launching: 'Llançant...',
        loaded: 'loaded',
        page_size: 'Mida de la pàgina',
        per_page: '/ pàgina',
        urgent: 'Urgent',
        pagination: 'Paginació',
        metric: 'mètrica',
        value: 'Valor',
        country: 'País',
        showing_chart_view: 'Mostrant vista de gràfic',
        showing_table_view: 'Mostrant vista de taula de dades',
        policies: 'Polítiques',
        compliant: 'Complint',
        non_compliant_status: 'Incompliment',
        all_data: 'Totes les dades',
        sr_urgent: 'Urgent',
        n_per_page: '{n} / pàgina',
        select_user_placeholder: '-- Seleccioneu usuari --',
        field_required: 'Aquest camp és obligatori',
        results_found: 'resultats trobats',
        select_placeholder: '-- Seleccionar --',
        unknown_user: 'Usuari desconegut',
        // fix-sprint #431 F7 — the names of the platform's own actors
        // (`SystemActorId`), as the audit trail and an assignee column show them.
        // English until Phase 25 (R-57).
        system_actors: {
            intake: 'System Intake',
            sla_engine: 'SLA Engine',
            export_job: 'Export Job',
            cmp_gateway: 'CMP Gateway',
            notification: 'Notification System',
            migration: 'System Migration',
            other: 'System: {name}',
        },
        export_csv: 'Exportar CSV',
        not_applicable: 'N/A',
        aria_overdue_since: 'Vençut des de {date}',
        placeholders: {
            enter_justification: 'Introduïu justification',
            justification: 'Justificació',
            campaign_name_example: 'Q1 2026 Accés Revisar',
            optional_description: 'Descripció opcional',
            enter_user_id: 'Introduïu user ID of the reviewer',
            enter_user_id_short: 'Introduïu user ID',
            schedule_name_example: 'Trimestral Accés Attestation',
            enter_property_id: 'Introduïu property ID',
            enter_policy_id: 'Introduïu política id',
            property_or_group_id: 'ID de propietat o grup',
            describe_template_purpose: 'Descriviu el propòsit de la plantilla...',
            step_title: 'Títol del pas...',
            detailed_instructions: 'Instruccions detallades...',
            filter_by_taxonomy_id: 'Filtra per ID de taxonomia...',
            describe_classification_rule: 'Descriviu la regla de classificació...',
            describe_dedup_rule: 'Descriviu la regla de deduplicació...',
            describe_import_template: "Descriviu la plantilla d'importació...",
            reason_for_rejection: 'Motiu del rebuig...',
            jurisdiction_codes_example: 'VN, SG, UE',
            filter_by_locale: 'Filtrar per idioma...',
            filter_by_event_type: "Filtrar per tipus d'esdeveniment",
            filter_by_actor_id: "Filtrar per ID d'actor",
            filter_by_market_code: 'Filtrar per codi de mercat...',
            filter_by_framework_id: 'Filtrar per marc ID...',
            add_comment: 'Afegir comment...',
            provide_rejection_reason: 'Proporcioneu un motiu per rebutjar aquesta presentació...',
            filter_by_jurisdiction: 'Filtrar per codi de jurisdicció...',
            enter_report_title: 'Introduïu report title',
            enter_name: 'Introduïu name',
            enter_description: 'Introduïu description',
            enter_org_id: 'Introduïu Org ID to add',
            describe_evidence_gap: "Descriviu la bretxa d'evidència...",
            expected_evidence: "Quina evidència s'espera",
            entity_identifier: "Identificador de l'entitat",
            remediation_steps: 'Passos per remediar aquesta bretxa',
            user_id_or_name: "ID d'usuari o nom",
            reporting_period_id: "ID del període d'informe",
            maturity_model_id: 'ID del model de maduresa',
            describe_maturity_model: 'Descriviu aquest model de maduresa...',
            describe_role_purpose: "Describe this role's purpose...",
            provide_deny_reason: 'Proporcioneu un motiu per denegar aquesta sol·licitud',
            describe_emergency: "Descriviu la situació d'emergència que requereix accés elevat (mínim 20 caràcters)",
            enter_delegate_id: 'Introduïu user ID of the delegate',
            provide_delegation_reason: 'Proporcioneu un motiu per a aquesta delegació',
            client_identifier: 'Identificador de client',
            client_secret: 'Secret del client',
            time_format: 'HH:MM',
            describe_sod_rule: "Descriviu el propòsit d'aquesta regla SoD...",
            enter_role_id: 'Introduïu role ID',
            explain_exception: 'Expliqueu per què cal aquesta excepció...',
            describe_compensating_controls: 'Descriviu els controls compensatoris vigents...',
            full_name: 'Nom complet',
            min_chars: 'Mín 12 caràcters',
            enter_override_justification: 'Introduïu justification for overriding this gate evaluation',
            enter_evidence_id: "Introduïu l'identificador de prova per enllaçar:",
            date_format: 'DD/MM/AAAA',
            describe_pack_contents: 'Descriviu el contingut i el propòsit del paquet',
            select_reviewers: 'Seleccioneu revisors...',
        },
        aria_previous_page: 'Pàgina anterior',
        aria_next_page: 'Pàgina següent',
        aria_results_per_page: 'Resultats per pàgina',
        per_page_25: '25 / pàgina',
        per_page_50: '50 / pàgina',
        per_page_100: '100 / pàgina',
        select_language: 'Seleccionar idioma',
        today: 'Avui',
        tomorrow: 'Demà',
        dashboard_load_error: "No s'ha pogut carregar el tauler. Si us plau, torneu-ho a provar.",
        enum_grade: {
            red: 'Vermell',
            amber: 'Ambre',
            yellow: 'groc',
            green: 'Verd',
        },
        enum_severity: {
            critical: 'Crític',
            high: 'Alt',
            medium: 'Mitjà',
            low: 'Baix',
        },
        enum_priority: {
            critical: 'Crític',
            high: 'Alt',
            medium: 'Mitjà',
            low: 'Baix',
        },
        enum_status: {
            pending: 'Pendent',
            in_progress: 'En curs',
            completed: 'Completat',
            blocked: 'Bloquejat',
            escalated: 'Escalat',
            cancelled: 'Cancel·lat',
            overdue: 'Endarrerit',
            not_started: 'No començat',
            new: 'Nou',
            open: 'Obert',
            closed: 'Tancat',
            acknowledged: 'Reconegut',
            resolved: 'Solucionat',
        },
        enum_department: {
            unassigned: 'Sense assignar',
        },
        enum_form_category: {
            assessment: 'Avaluació',
            assessment_report: "Informe d'avaluació",
            dpia: 'DPIA',
            authority_submission: "Presentació de l'autoritat",
            cross_border_transfer: 'Transferència transfronterera',
            vendor: 'Venedor',
            breach: 'Incompliment',
            consent: 'Consentiment',
            governance: 'Governança',
            register: "Registra't",
            monitoring: 'Seguiment',
            dpa: 'DPA',
            dsr: 'DSR',
            privacy: 'Privadesa',
            incident: 'Incident',
            org: 'Organització',
            sector: 'Sector',
            classification: 'Classificació',
            discovery: 'Descobriment',
            // Sprint 63.37.0 (Stream J) — the pack categories the forms dashboard showed as raw codes.
            evidence_record: 'Evidence Record',
            application: 'Application',
            report: 'Report',
            data_mapping: 'Data Mapping',
            record: 'Record',
            evidence: 'Evidence',
            certificate: 'Certificate',
            log: 'Log',
            sandbox: 'Controlled Testing',
            decision: 'Decision',
            licence: 'Licence',
            declaration: 'Declaration',
            administrative_notice: 'Administrative Notice',
            plan: 'Plan',
            dossier: 'Dossier',
            training: 'Training',
            checklist: 'Checklist',
            training_report: 'Training Report',
            reference_list: 'Reference List',
            evidence_pack: 'Evidence Pack',
            forms: 'Forms',
            authority_response_record: 'Authority Response Record',
            request: 'Request',
            attestation: 'Attestation',
            notification: 'Notification',
            evidence_pack_manifest: 'Evidence Pack Manifest',
            email_submission_record: 'Email Submission Record',
            evidence_file: 'Evidence File',
            worksheet: 'Worksheet',
            uncategorized: 'Uncategorized',
        },
        enum_module: {
            rights_requests: 'Sol·licituds de drets',
            assessments: 'Avaluacions',
            incidents: 'Incidències',
            incident_ops: "Operacions d'incidències",
            monitoring: 'Seguiment',
            vendor_risk: 'Risc del venedor',
            governance: 'Governança',
            deletion_orchestration: 'Supressió',
            access_review: "Revisió d'accés",
            consent: 'Consentiment',
            consent_properties: 'Propietats del consentiment',
            forms: 'Formes',
            localization: 'Localització',
            program_governance: 'Govern del programa',
            privacy_scanner: 'Escàner de privadesa',
            classification: 'Classificació',
            discovery: 'Descobriment',
            manual: 'Manual',
            data_mapping: 'Mapatge de dades',
        },
        enum_risk_level: {
            critical: 'Crític',
            high: 'Alt',
            medium: 'Mitjà',
            low: 'Baix',
        },
        enum_request_type: {
            all: 'All',
            access: 'Accés (DSAR)',
            deletion: 'Supressió',
            correction: 'Correcció',
            objection: 'Objecció',
            portability: 'Portabilitat',
            restriction: 'Restricció',
            consent_withdrawal: 'Retirada del consentiment',
            protective_measures: 'Mesures de protecció',
            processing_information: 'Information about Processing',
            consent_decision: 'Consent Decision',
            complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
        },
        enum_locale: {
            en: 'Anglès',
            vi: 'vietnamita',
            all: 'Tots dos',
        },
        enum_event_type: {
            case_created: 'Cas creat',
            status_changed: 'Estat canviat',
            case_assigned: 'Cas assignat',
            note_added: 'Nota afegida',
            attachment_added: "S'ha afegit un fitxer adjunt",
            attachment_deleted: "S'ha suprimit el fitxer adjunt",
            reference_added: 'Referència afegida',
            checklist_updated: 'Llista de verificació actualitzada',
            identity_verified: 'Identitat verificada',
            closure_summary_added: 'Resum de tancament',
            approval_granted: 'Aprovació concedida',
            template_applied: 'Plantilla aplicada',
            export_generated: 'Exportació generada',
            sla_warning: 'SLA Avís',
            sla_overdue: 'SLA Endarrerit',
            sla_flag_revisited: 'SLA flag revisited',
            third_party_involvement_changed: 'Processor or third-party involvement changed',
        },
        more: 'more',
        use: 'Use',
        ok: 'OK',
        back_to: 'Return to',
        click_to_copy: 'click to copy',
        copied: 'Copied',
        more_actions: 'More actions',
        actions_for: 'Actions for {title}',
        previous_page: 'Previous page',
        lookup_read_failed: 'A list this page offers for choosing could not be loaded, so a picker may be empty or incomplete.',
        lookup_read_subject: 'the lists offered for choosing',
        export_error: "L'exportació ha fallat",
        you: 'You',
        export_failed: "L'exportació ha fallat. Si us plau, torneu-ho a provar.",
        filters: 'Filtres',
        failed: 'Fallat',
        attachment_release_action: 'Release and destroy the file',
        attachment_release_title: 'Release this evidence file',
        attachment_release_body: 'Releasing destroys the file and records the destruction on the deletion proof chain. Every entry that cites the file will then say it was destroyed, and when. This cannot be undone.',
        attachment_release_basis_legend: 'Why is the file released?',
        attachment_release_basis_retention: 'Its retention period has ended',
        attachment_release_basis_erasure: 'An erasure request reached it',
        attachment_release_reason: 'Reason (required)',
        attachment_release_retention_date: 'Date the retention period ended (required)',
        attachment_release_retention_date_hint: 'No retention period is installed for this evidence, so state the date its retention ended. The file cannot be released before that date.',
        attachment_release_erasure_request: 'Erasure request (required)',
        attachment_release_erasure_placeholder: 'Choose the erasure request',
        attachment_release_erasure_none: 'No erasure request is recorded.',
        attachment_release_erasure_none_not_decided: 'An erasure request about this person is recorded but has not been decided for fulfilment yet, so none is listed. Release the file once the erasure is decided.',
        attachment_release_erasure_none_not_qualifying: 'No erasure request recorded about this person qualifies.',
        attachment_release_confirm: 'Release and destroy',
        attachment_release_releasing: 'Releasing…',
        attachment_release_done: 'The file was released and destroyed.',
        attachment_release_failed: 'The file was not released.',
        attachment_release_not_ended: 'The retention period ends on {date}, so the file cannot be released before then.',
        attachment_release_under_hold: 'The file is under a hold, so it is not released.',
        attachment_release_not_needed: 'No evidence entry rests on this file, so it is not released: delete it the ordinary way.',
        attachment_release_decided_by_register: 'This file is evidence on a register whose own retention rule decides when its evidence is released, so it is not released by a stated date.',
        attachment_release_date_installed: 'The installed retention period ends on {date}; a different date cannot be stated.',
        attachment_released_retention: 'Evidence destroyed at the end of its retention period on {date}',
        attachment_released_erasure: 'Evidence destroyed for erasure request {request}',
        attachment_released_erasure_unnamed: 'Evidence destroyed for an erasure request',
        attachment_release_erasure_label_none: 'Erasure request',
        attachment_released_on: 'Destroyed on {date}',
        attachment_released_heading: 'Files destroyed at the end of their retention or for an erasure request',
        attachment_released_retention_stated: 'Evidence destroyed at the end of its retention period on {date}, a date stated by the person who released it',
        attachment_release_installed_date: 'Date the installed retention period ends',
        attachment_release_installed_date_hint: 'This date comes from the retention period installed for this evidence, so it is not stated here.',
        attachment_release_installed_floor_hint: 'A retention period installed for other evidence citing this file ends on {date}; the date stated may not be before it.',
        attachment_release_date_before_installed: 'A retention period installed for evidence citing this file ends on {date}; a date before it cannot be stated.',
        attachment_release_in_progress: 'This file is already being released by another request, so it was not released twice.',
        // fix-sprint-20261026-0900 F10 — the 502 the release returns when the stored object cannot be deleted.
        attachment_release_storage_delete_failed: 'The stored file could not be destroyed, so the file was not released. Submit the release again to retry it.',
        attachment_release_not_computable: 'A retention period is installed for this evidence, but when it ends cannot be computed, so the file is not released.',
        attachment_release_erasure_hint: "Only requests for erasure about the person this evidence concerns are listed: decided for fulfilment, not rejected, withdrawn or closed without being fulfilled, and with the requester's identity verified.",
        attachment_release_erasure_subject_not_named: 'The register this evidence is on does not record which person it concerns, so the file cannot be released for an erasure request.',
        attachment_release_erasure_subject_not_linked: 'The entry this evidence is on is not linked to a data subject profile, so the file cannot be released for an erasure request.',
        attachment_release_erasure_different_subjects: 'This file is evidence for entries about different people, so it cannot be released for one erasure request.',
        attachment_release_erasure_other_subject: 'That erasure request is about a different person from the one this evidence concerns.',
        attachment_release_erasure_not_actionable: "That erasure request was rejected, withdrawn or closed without being fulfilled, or the requester's identity is not verified.",
        attachment_release_erasure_not_decided: 'That erasure request has not been decided for fulfilment yet, so it does not reach this evidence. Release the file once the erasure is decided.',
        attachment_release_erasure_read_required: 'Naming a rights request as the reason for a release needs the permission to read rights requests, which you do not hold.',
        attachment_release_erasure_withheld: 'You do not hold the permission to read rights requests, so no erasure request can be listed or named here. A person who holds it can release the file for an erasure request.',
        attachment_release_preview_load_error: "What this file's release rests on could not be read.",
    },
    common_statuses: {
        removed: 'Eliminat',
        draft: 'Esborrany',
        active: 'Actiu',
        inactive: 'Inactiu',
        pending: 'Pendent',
        in_progress: 'En curs',
        completed: 'Completat',
        archived: 'Arxivat',
        published: 'Publicat',
        approved: 'Aprovat',
        rejected: 'Rebutjat',
        cancelled: 'Cancel·lat',
        blocked: 'Bloquejat',
        deferred: 'Ajornat',
        not_started: 'No iniciat',
        dismissed: 'Descartat',
        failed: 'Fallat',
        in_review: 'En Revisar',
        deprecated: 'Obsolet',
        scheduled: 'Programat',
        deploying: 'Desplegant',
        deployed: 'Desplegat',
        paused: 'En pausa',
        rolled_back: 'Revertit',
        received: 'Rebut',
        processing: 'Tractament',
        under_assessment: 'En avaluació',
        implemented: 'Implementat',
        pending_approval: "Pendent d'aprovació",
        pending_deletion: 'Pendent Eliminació',
        on_hold: 'En espera',
        deleted: 'Eliminat',
        expired: 'Caducat',
        outdated: 'Desactualitzat',
        drifted: 'Desviat',
        decommissioned: 'Descomissionat',
        match: 'Coincidència',
        different: 'Diferent',
        missing: 'Faltant',
        confirmed: 'Confirmat',
        revoked: 'Revocat',
        escalated: 'Escalat',
        system: 'Sistema',
        custom: 'Personalitzat',
        warning: 'Advertència',
        blocking: 'Bloquejant',
        analyst: 'Analista',
        viewer: 'Visualitzador',
        daily: 'Diari',
        weekly: 'Setmanal',
        policies: 'Polítiques',
        denied: 'Denegat',
        verified: 'Verificat',
        detected: 'Detectat',
        ignored: 'Ignorat',
        open: 'Obert',
        resolved: 'Solucionat',
        suspended: 'Suspès',
        retired: 'Jubilat',
        review: 'Revisió',
        generated: 'Generat',
        exported: 'Exportat',
        // Sprint 63.37.0 (Stream J) — the embedded form statuses a page prints.
        finalized: 'Finalized',
        superseded: 'Superseded',
    },
    severity: {
        low: 'Baix',
        medium: 'Mitjà',
        high: 'Alt',
        critical: 'Crític',
    },
    sensitivity: {
        standard: 'Estàndard',
        sensitive: 'Sensible',
        special_category: 'Categoria especial',
        badge_sensitive: 'Sensible',
    },
    change_types: {
        policy_update: 'Política Actualitzar',
        new_binding: 'Nou Binding',
        remove_binding: 'Eliminar vinculació',
        configuration_change: 'Canvi de configuració',
        banner_update: 'Bàner Actualitzar',
    },
    comparison_types: {
        full: 'Complet',
        banner_only: 'Bàner Només',
        purposes_only: 'Finalitats Només',
        legal_bases_only: 'Només bases jurídiques',
        consent_settings: 'Consentiment Configuració',
    },
    lifecycle_stages: {
        active: 'Actiu',
        archived: 'Arxivat',
        pending_deletion: 'Pendent Eliminació',
        deleted: 'Eliminat',
        on_hold: 'En espera',
        creation: 'Creation',
        exception_hold: 'On Hold',
    },
    object_types: {
        personal_data: 'Dades personals',
        consent_record: 'Consentiment Registre',
        user_account: 'Usuari Compte',
        audit_log: 'Auditoria Registre',
        document: 'Document',
        backup: 'Còpia de seguretat',
    },
    risk_categories: {
        data_breach: 'Dades Violació',
        unauthorized_access: 'Accés no autoritzat',
        data_loss: 'Pèrdua de dades',
        regulatory_non_compliance: 'Incompliment regulatori',
        third_party_risk: 'Third-Party Risc',
        operational: 'Operacional',
        technical: 'Tècnic',
        legal: 'Jurídic',
    },
    mitigation_types: {
        technical: 'Tècnic',
        organizational: 'Organitzatiu',
        contractual: 'Contractual',
        procedural: 'Procedimental',
    },
    safeguard_types: {
        encryption_in_transit: 'Encriptació en trànsit',
        encryption_at_rest: 'Xifratge en repòs',
        access_controls: "Controls d'accés",
        contractual_clauses: 'Clàusules contractuals',
        data_minimization: 'Minimització de dades',
        pseudonymization: 'Pseudonimització',
        audit_rights: "Drets d'auditoria",
        breach_notification: "Notificació d'incompliment",
        data_localization: 'Localització de dades',
        other: 'Altres',
    },
    scoring_methods: {
        weighted_average: 'Mitjana ponderada',
        simple_average: 'Mitjana simple',
        maximum: 'Màxim',
        manual: 'Manual',
    },
    dpa_status: {
        not_required: 'No requerit',
        required: 'Requerit',
        in_progress: 'En curs',
        completed: 'Completat',
        signed: 'Signat',
    },
    disposal_methods: {
        anonymize: 'Anonimitzar',
        pseudonymize: 'Pseudonimitzar',
        aggregate: 'Agregar',
        delete: 'Eliminar',
    },
    dashboard: {
        workspace: {
            tabs_label: 'Dashboard sections',
            overview_heading: 'Your compliance posture',
            overview_description: 'Score, recommendations, coverage and operational signals.',
            tasks_heading: 'Turn priorities into progress',
            tasks_description: 'Ownership, overdue work, upcoming tasks and department delivery.',
            insights_heading: 'The full compliance picture',
            insights_description: 'Filings, third-party risk, assessments, incidents and regulatory activity.',
            visible_widgets: '{count} widgets visible',
            widgets_view: 'Widgets',
            config_description: 'Choose which widgets to show and their order. Changes are saved when you apply them.',
            search_widgets: 'Find a widget',
            search_widgets_placeholder: 'Search widgets',
            density: 'Widget spacing',
            density_comfortable: 'Comfortable',
            density_compact: 'Compact',
            default_tab: 'Open dashboard on',
            apply_configuration: 'Apply configuration',
            cancel_configuration: 'Cancel',
            move_up: 'Move {widget} up',
            move_down: 'Move {widget} down',
            enabled_count: '{enabled} of {total} items enabled',
            empty_section: 'No widgets are visible in this section. Use Configure Dashboard to choose available widgets.',
        },
        title: 'Tauler de control',
        no_data: 'No hi ha dades disponibles al tauler.',
        no_data_heading: 'Encara no hi ha dades en temps real',
        no_data_detail: 'Live figures could not be loaded. The layout is shown; values will appear once the connection is restored.',
        consent_coverage: 'Cobertura consentiment',
        ctia_filings: 'Presentacions CTIA',
        next_dpia_review: 'Pròxima revisió DPIA',
        next_review_none: 'Cap programada',
        /*
         * Sprint 65.4.0 (AC5) — why the next-review tile has four states.
         *
         * A blank date reads as "nothing due soon". Measured, it almost always
         * meant something else: the only production writer of
         * `grc_dpia_records.next_review_at` was the demo seed, so every real
         * tenant saw the same dash. The tile now says which absence it is.
         */
        next_review_no_dpias: 'No DPIAs recorded',
        next_review_cadence_absent: 'No review cadence declared',
        next_review_not_scheduled: 'Not yet scheduled',
        incident_mps_filings: "Presentacions MPS d'incident",
        compliance_score: 'Puntuació de compliment',
        grade: 'Qualificació',
        no_score: "Encara no s'ha calculat la puntuació de compliment.",
        rights_requests: 'Sol·licituds de drets',
        open: 'Obert',
        sla_breached: 'SLA incomplert',
        sla_approaching: 'SLA proper',
        closed_month: 'Tancats (mes)',
        incidents: 'Incidents i violacions de dades',
        active: 'Actiu',
        breaches: 'Violacions de dades',
        overdue_tasks: 'Tasques vençudes',
        breach_72h_notice: "S'aplica un termini de notificació reglamentària de 72 hores a les infraccions actives",
        recommendations: 'Recomanacions',
        take_action: 'Preneu acció',
        drift_events: 'Desviacions i aprovacions',
        open_events: 'Esdeveniments oberts',
        pending_approvals: 'Aprovacions pendents',
        monitoring: 'Tasques de supervisió',
        programs: 'Programes',
        overdue: 'Vençut',
        pending: 'Pendent',
        ric_cases: 'Casos RIC',
        coverage: 'Cobertura de dades',
        systems: 'Sistemes',
        activities: 'Activitats',
        vendors: 'Proveïdors',
        dpias: 'AIPD',
        open_gaps: 'llacunes obertes',
        view_as_table: 'Veure com a taula',
        view_as_chart: 'Veure com a grafic',
        table_metric_header: 'Metrica',
        table_value_header: 'Valor',
        announce_chart_view: 'Mostrant vista de grafic',
        announce_table_view: 'Mostrant vista de taula',
        section_core_metrics: 'Metriques principals',
        section_task_management: 'Gestio de tasques',
        section_compliance_insights: 'Perspectives de compliment',
        configure_dashboard: 'Configurar tauler',
        config_col_active: 'Actiu',
        config_col_widget: 'Giny',
        collapse_section: 'Reduir',
        expand_section: 'Ampliar',
        reset_to_defaults: 'Restableix els valors predeterminats',
        preferences_saved: 'Preferències del tauler desades',
        preferences_reset: 'Tauler restablert als valors predeterminats del rol',
        preferences_load_error: 'Error en carregar les preferències del tauler',
        preferences_save_error: 'Dashboard preferences could not be saved.',
        factor_avg_system_score: 'Puntuació mitjana del sistema',
        factor_avg_vendor_score: 'Puntuació mitjana del proveïdor',
        factor_processing_coverage: "Cobertura de l'activitat de processament",
        factor_ropa_completeness: 'Completitud de RoPA',
        factor_drift_penalty: "Pena d'esdeveniments de deriva",
        rec_improve_system_gov: 'Millorar la documentació de govern del sistema',
        rec_improve_vendor_gov: 'Millorar la documentació de govern del proveïdor',
        rec_document_processing: 'Activitats de processament de documents per a sistemes no coberts',
        rec_generate_ropa: 'Genereu registres RoPA per a activitats de processament actives',
        rec_resolve_drift: "Resoldre esdeveniments de deriva crítics i d'alta gravetat oberts",
        score_good: 'Bo',
        score_fair: 'Acceptable',
        score_poor: 'Dolent',
        score_critical: 'Crític',
        drag_to_reorder: 'Arrossega per reordenar',
        widget_moved: 'Widget mogut',
        press_space_to_grab: 'Premeu Espai per agafar, tecles de fletxa per moure, Espai per deixar anar',
        reorder_instructions: 'Utilitzeu arrossegar i deixar anar o dreceres de teclat per reordenar els widgets',
        page_title: 'Compliance Dashboard',
        welcome_back: 'Welcome back, {name}',
        subtitle: 'Your real-time view of compliance posture, risk, and progress across your organization.',
        section_overview: 'Overview',
        kpi_strip_label: 'KPI overview strip',
        today_label: 'Today',
        avatar_aria: 'Signed in as {name}',
        no_prior_period: 'No prior period yet',
        period_current: 'Current period',
        gauge_aria: '{score} out of 100, {label}',
        donut_aria: '{total} vendors by risk level',
        bar_aria: '{label}: {value} percent',
        config_saving: 'Saving…',
        task_widgets: {
            tasks_overview: 'Visió general de les tasques',
            pending: 'Pendent',
            in_progress: 'En curs',
            overdue: 'Endarrerit',
            done_7d: 'Fet (7 d)',
            by_priority: 'Per prioritat',
            view_all_tasks: 'Veure totes les tasques',
            upcoming_tasks: 'Pròximes tasques (7 dies següents)',
            no_upcoming: 'No hi ha cap tasca pendent en els propers 7 dies.',
            view_calendar: 'Veure calendari',
            overdue_alerts: 'Endarrerit',
            no_overdue: 'Sense tasques vençudes. Tot va en bon camí.',
            assigned_to: 'Assignat a',
            unassigned_actor: 'Unassigned',
            untitled_task: 'Untitled task',
            view_all_overdue: 'Mostra-ho tot vençut',
            notifications: 'Notificacions recents',
            unread: 'unread',
            no_notifications: 'No hi ha notificacions. Esteu tots atrapats!',
            view_all_notifications: 'Veure totes les notificacions',
            load_error: "No s'han pogut carregar les dades.",
            day_late: 'dia tard',
            days_late_plural: 'dies tard',
            due_today: 'Degut avui',
            just_now: 'just ara',
            minutes_short: 'm',
            hours_short: 'h',
            days_short: 'd',
            cross_org_stats: 'Estadístiques entre organitzacions',
            by_organization: 'Per Organització',
            overdue_across_orgs: 'tasques vençudes entre organitzacions',
            overdue_count_label: '{count} overdue tasks',
            total: 'Total',
            tasks: 'tasks',
            organization: 'Organització',
            current_org: 'Organització actual',
            all_orgs: 'Totes les organitzacions',
            all_organizations: 'Totes les Organitzacions',
            filter_by_org: 'Filtra per organització',
            cross_org: 'Organització creuada',
            empty_calendar: 'No hi ha tasques per a aquest període. Creeu la vostra primera tasca o comproveu els vostres filtres.',
            empty_my_tasks: 'No tens cap tasca assignada. Gran feina!',
            empty_overdue: 'Sense tasques vençudes. Tot va en bon camí.',
            empty_notifications: 'No hi ha notificacions. Esteu tots atrapats!',
            empty_workflows: "No s'ha configurat cap flux de treball. Exploreu les plantilles per començar.",
            remediation_pipeline: 'Tasks & Remediation Pipeline',
            no_open_tasks: 'No open tasks — the pipeline is clear.',
            open_tasks: 'Open tasks',
        },
        my_tasks_widget: {
            title: 'Les Meves Tasques',
            no_tasks: 'No hi ha tasques assignades a vostè.',
            load_error: 'No ha estat possible carregar les tasques.',
            overdue: 'vençuda',
            remaining: 'restant',
            view_all: 'Veure Totes les Tasques',
        },
        my_section_status_widget: {
            title: 'Estat de les Meves Seccions',
            overall_completion: 'General',
            sections_count: 'Seccions',
            no_sections: 'No hi ha seccions assignades al seu departament.',
            load_error: "No ha estat possible carregar l'estat de les seccions.",
            department_tasks_title: 'Tasques del departament',
            no_department_tasks: 'El vostre departament no té tasques.',
            department_tasks_error: "No s'han pogut carregar les tasques del departament.",
            no_department: 'No esteu assignat a cap departament.',
        },
        revision_requests_widget: {
            title: 'Sol·licituds de Revisió',
            no_revisions: 'No hi ha sol·licituds de revisió. Totes les seccions van per bon camí.',
            load_error: 'No ha estat possible carregar les sol·licituds de revisió.',
        },
        consent_summary: {
            title: 'Consentiment',
            active: 'Actius',
            withdrawn_30d: 'Retirada (30 d)',
            expiring_7d: 'Caducitat (7 d)',
        },
        transfer_assessments: {
            title: 'Avaluacions de transferència',
            open: 'Obert',
            approved: 'Aprovat',
            gaps_flagged: 'Buits marcats',
        },
        breach_notifications: {
            title: "Notificacions d'incompliment",
            deadline_72h: 'Termini 72h',
            submitted: 'Presentat',
            pending: 'Pendent',
        },
        authority_requests: {
            title: "Sol·licituds de l'autoritat",
            pending: 'Pendent',
            overdue: 'Endarrerit',
            responded_30d: 'Respost (30 d)',
        },
        data_subjects_widget: {
            title: 'Assumptes de dades',
            total: 'Total',
            high_risk: 'Alt risc',
            active_30d: 'Actiu (30 d)',
        },
        audit_activity_summary: {
            title: "Activitat d'Auditoria",
            events_today: 'Esdeveniments avui',
            events_7d: 'Esdeveniments (7d)',
            unique_actors_30d: 'Actors únics (30 d)',
        },
        onboarding_progress: {
            title: "Progrés d'incorporació",
            completion_pct: 'Completa',
            status_label: 'Estat',
            current_step: 'Pas actual',
            status: {
                in_progress: 'En curs',
                completed: 'Completat',
                skipped: 'Saltat',
                not_started: 'No començat',
            },
        },
        sector_overlay_reports_due: {
            title: 'Informes de superposició sectorial pendents',
        },
        kpi: {
            framework_coverage: 'Framework Coverage',
            open_actions: 'Open Actions',
            high_priority: '{count} high priority',
            vendor_health: 'Vendor Health',
            at_risk: '{count} at risk',
            open_count: '{count} open',
            active_count: '{count} active',
            grade_label: 'Grade: {grade}',
            consent_coverage_hint: 'of active consent workflows',
            consent_coverage_source_web_consent_records: 'of web consent records, last 30 days',
            consent_coverage_source_web_consent_events: 'of web consent events, last 30 days',
            consent_coverage_source_consent_records: 'of consent records, last 30 days',
            of_installed_frameworks: 'of installed frameworks',
            no_frameworks_installed: 'No frameworks installed',
            unavailable: 'Not available',
            consent_no_records: 'No consent records in the last 30 days',
            tile_hint: {
                no_score: 'Not calculated yet',
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'Mandatory: none installed',
                vendor_at_risk: '{at_risk} of {total} high or critical',
                vendor_load_error: 'Could not be read',
                no_vendor_data: 'No vendor data',
                consent_no_records: 'No records, last 30 days',
                consent_source_web_consent_records: 'of web consent records',
                consent_source_web_consent_events: 'of web consent events',
                consent_source_consent_records: 'of consent records',
                not_reported: 'Not reported',
                delta_since: 'points since {period}',
            },
        },
        summary_reloaded: 'Dashboard reloaded.',
        scope_incidents: 'Incidents still open. Breaches are the subset flagged as a personal-data breach.',
        scope_breach_response: 'The same open breaches, by notification and remediation state.',
        scope_breach_notifications: 'Notification filings. Due in 72h counts those with a filing deadline inside the next 72 hours; submitted and pending count every filing in that state, whatever its deadline.',
        vendor_health_hint: '{at_risk} of {total} vendors at high or critical risk',
        vendor_health_load_error: 'Vendor data could not be read.',
        figure_not_reported: 'This figure was not reported in the last dashboard read.',
        framework_coverage: {
            title: 'Framework Coverage',
            overall: 'Overall coverage',
            none: 'The installed frameworks declare no requirements yet.',
            none_installed: 'No frameworks installed. Install a regulatory pack to see its coverage here.',
            requirements_mapped: '{mapped} of {total} requirements mapped',
            no_requirements: 'No requirements declared',
            overall_denominator: 'across {count} frameworks that declare requirements',
            no_requirements_count: '{count} installed frameworks declare no requirements yet',
            no_requirements_count_one: '1 installed framework declares no requirements yet',
            showing_lowest: 'Showing the {shown} lowest-covered of {total}',
            gaps: '{count} open gaps',
            view_all: 'View frameworks',
            load_error: 'Framework coverage could not be loaded.',
        },
        automation_impact: {
            title: 'Automation Impact',
            scope: 'The controls your installed frameworks declare as applicable to this organization, and how many of them have automated work recorded.',
            load_error: 'Automation impact could not be loaded.',
            no_frameworks_installed: 'No installed framework is one your organization must comply with',
            not_declared: 'No installed framework states which of its controls apply to this organization',
            profile_incomplete: 'Record your organization profile to see which controls apply',
            none_applicable: 'No declared control applies to this organization',
            no_emitter: 'No automated work is recorded against controls yet',
            no_point: 'No completed month has a figure to show yet',
            figure_hint: '{automated} of {applicable} applicable controls automated in {period}',
            figure_hint_one: '{automated} of 1 applicable control automated in {period}',
            delta_since: 'percentage points since {period}',
            bucket_applicable: 'Applicable',
            bucket_out_of_scope: 'Declared not to apply to you',
            bucket_not_declared: 'Applicability not declared',
            bucket_profile_incomplete: 'Awaiting your organization profile',
            applicable_from: 'The applicable controls come from {count} installed frameworks',
            applicable_from_one: 'The applicable controls come from 1 installed framework',
            review_profile: 'Review your organization profile',
            tile_hint: {
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'No mandatory framework installed',
                not_declared: 'Applicability not declared',
                profile_incomplete: 'Organization profile needed',
                none_applicable: 'No declared control applies',
                no_emitter: 'No automated work recorded',
                no_point: 'No month to show yet',
                figure: '{automated} of {applicable} controls, {period}',
                figure_one: '{automated} of 1 control, {period}',
            },
        },
        recommendations_widget: {
            title: 'Key Recommendations',
            view_all: 'View all ({count})',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            none: 'No recommendations. Every score factor is within its healthy range.',
        },
        rights_status: {
            title: 'Rights Requests',
            open_requests: 'Open requests',
        },
        module_status: {
            title: 'Module Status',
            enabled: 'Enabled',
            modules_count: '{count} modules',
            modules_count_one: '{count} module',
            none: 'No modules are enabled for this organization.',
        },
        recent_activity: {
            title: 'Recent Activity',
            view_all: 'View all',
            empty: 'No recent activity.',
            load_error: 'Recent activity could not be loaded.',
        },
        persona_widgets: {
            framework_filter: 'Filtrar per marc normatiu',
            all_frameworks: 'Tots els marcs',
            filing_lifecycle: 'Cicle de vida de presentacions',
            filing_pipeline: 'Pipeline de presentacions',
            draft: 'Esborrany',
            internal_review: 'Revisió interna',
            approved: 'Aprovat',
            submitted: 'Enviat',
            supplement_requested: 'Suplement sol·licitat',
            resubmitted: 'Reenviat',
            accepted: 'Acceptat',
            rejected: 'Rebutjat',
            closed: 'Tancat',
            deadline: 'Termini',
            days_remaining: 'dies restants',
            days_overdue: 'dies de retard',
            no_filings: 'No hi ha presentacions actives.',
            department_consolidation: 'Consolidació de departaments',
            departments_completed: 'Departaments completats',
            departments_pending: 'Departaments pendents',
            departments_overdue: 'Departaments amb retard',
            no_consolidation_cases: 'No hi ha casos multidepartamentals en curs.',
            vendor_risk: 'Risc de proveïdors i tercers',
            pending_assessment: 'Avaluació pendent',
            overdue_questionnaires: 'Qüestionaris endarrerits',
            dpa_review_queue: 'Cua de revisió DPA',
            risk_distribution: 'Distribució del risc',
            total_vendors: 'Total de proveïdors',
            by_risk_level: 'Per nivell de risc',
            no_vendor_data: 'No hi ha dades de risc de proveïdors disponibles.',
            discovery_summary: 'Descobriment de dades',
            discovery_classification: 'Descobriment i classificació de dades',
            active_scans: 'Escanejos actius',
            labels_pending: 'Etiquetes pendents de revisió',
            exceptions_unresolved: 'Excepcions no resoltes',
            pending_review: 'Revisió pendent',
            exceptions: 'Excepcions',
            approved_labels: 'Etiquetes aprovades',
            total_classified: 'Total classificat',
            no_discovery_data: 'No hi ha dades de descobriment disponibles.',
            ai_governance: "Governança de sistemes d'IA",
            systems_inventory: 'Sistemes en inventari',
            systems_in_inventory: 'Sistemes en inventari',
            classification_breakdown: 'Desglossament de la classificació',
            dossier_completion: 'Compleció de dossier',
            by_classification: 'Per classificació',
            labeling_status: "Estat d'etiquetatge",
            not_started: 'No iniciat',
            no_ai_data: "No hi ha dades de governança d'IA disponibles.",
            obligations_complete: 'Obligations Complete',
            obligations_complete_of: '{complete} of {applicable} applicable obligations',
            no_obligations_apply: 'No applicable obligation recorded',
            ai_register_restricted: 'Your role cannot read the AI system register.',
            breach_response: 'Resposta a incompliments i incidents',
            active_breaches: 'Incompliments actius',
            notification_status: 'Estat de notificació',
            remediation_progress: 'Progrés de remediació',
            supplement_loops: 'Bucles de suplement',
            no_breach_data: "No hi ha casos d'incompliment actius.",
            audit_readiness: 'Preparació per a auditories i avaluacions',
            assessments: 'Avaluacions',
            gap_findings: 'Troballes de buits',
            evidence_completion: "Compleció d'evidències",
            export_ready: 'Llest per exportar',
            export_ready_yes: 'Ready',
            export_ready_no: 'Not ready',
            unspecified_framework_group: 'No framework recorded',
            not_ready: 'No llest',
            no_audit_data: "No hi ha dades de preparació d'auditoria disponibles.",
            framework_label_vn_pdpl: 'VN PDPL 2025',
            framework_label_vn_cybersecurity: 'VN Ciberseguretat 2018',
            framework_label_vn_ecommerce: 'VN Comerç electrònic 2023',
            framework_label_vn_telecom: 'VN Telecomunicacions 2023',
            framework_label_vn_ai: 'VN Llei IA 2025',
            framework_label_gdpr: 'RGPD',
            framework_label_unspecified: 'Sense especificar',
            my_department: 'El meu departament',
            my_tasks: 'Les meves tasques',
            section_status: 'Estat de la secció',
            revision_requests: 'Sol·licituds de revisió',
            completion: 'Compleció',
            reason: 'Motiu',
            requested_at: 'Sol·licitat el',
            no_tasks_assigned: 'No hi ha tasques assignades.',
            no_sections: 'No hi ha seccions assignades al vostre departament.',
            no_revisions: 'No hi ha sol·licituds de revisió.',
            load_error: 'Error en carregar les dades.',
            vendor_health_needs_attention: '{count} vendors require immediate attention',
            review_vendors: 'Review Vendors',
            total_vendors_label: 'Total Vendors',
        },
    },
    connectors: {
        nav: {
            instances: 'Instàncies',
            credentials: 'Dipòsit de credencials',
            event_bus: "Bus d'esdeveniments",
            definitions: 'Definicions',
            brds: 'Captura de BRD',
            security_reviews: 'Revisions de seguretat',
            poc_scorecards: 'Targetes de puntuació POC',
            automation_planning: 'Planificació automatització',
            system_inventory: 'Inventari de sistemes',
            traceability_matrix: 'Matriu de traçabilitat',
            gap_register: 'Registre escletxes',
        },
        instances: {
            title: 'Instàncies de connectors',
            page_title: 'Connectors',
            add: 'Afegir instància',
            test: 'Provar connexió',
            sync_now: 'Sincronitzar ara',
            new_instance: 'Nou Connector Instance',
            no_instances: "No s'han trobat connector instances",
            instance_count: '{count} instància(es)',
            confirm_delete: 'Suprimiu la instància del connector "{name}"?',
            all_connector_types: 'Tots els tipus de connector',
            select_connector: 'Seleccioneu connector...',
            fetch_error: 'Error en fetch connector instances. Si us plau, torneu-ho a intentar.',
            fetch_definitions_error: 'Error en fetch connector definitions. Si us plau, torneu-ho a intentar.',
            create_error: 'Error en create connector instance. Si us plau, torneu-ho a intentar.',
            test_error: 'Error en test connector. Si us plau, torneu-ho a intentar.',
            status_error: 'Error en actualitzar estat del connector. Si us plau, torneu-ho a intentar.',
            delete_error: 'Error en delete connector instance. Si us plau, torneu-ho a intentar.',
            confirm_disable: 'Disable "{name}"? Sync will stop until the instance is reactivated.',
            credentials_label: 'Credentials',
            instance_created: 'Connector instance created',
            base_url_label: 'Base URL',
            base_url_hint: "The https address of this connection's endpoint. Test sends the connector type's declared health check to a path on it.",
            base_url_invalid: 'Enter an https address, such as https://api.example.com.',
            edit_instance: 'Edit Connector Instance',
            edit_instance_action: 'Edit',
            instance_updated: 'Connector instance updated',
            update_error: 'The connector instance could not be updated.',
            no_credentials_hint: 'No credentials available. Create one in the Credential Vault first.',
            test_passed: 'Test passed',
            test_failed: 'Test failed',
            status_changed: 'Estat actualitzat',
            reactivate: 'Reactivar',
            mark_ready_for_testing: 'Mark ready for testing',
            marked_ready: 'Marked ready for testing. No connection check was made: this connector type declares no health check.',
            test_healthy: 'Health check passed ({detail})',
            test_unhealthy: 'Health check failed ({detail})',
            probe_detail_expected_status: 'the endpoint answered with the expected status',
            probe_detail_unexpected_status: 'the endpoint answered with a status other than the expected one',
            probe_detail_timeout: 'no answer before the timeout',
            probe_detail_network_error: 'the endpoint could not be reached',
            probe_detail_target_refused: "the endpoint's address is not allowed",
            record_sync: 'Record sync result',
            record_sync_title: 'Record sync result: {name}',
            record_sync_note: 'Record the outcome of a sync run that ran outside the platform. The platform does not run the sync itself.',
            sync_outcome: 'Outcome',
            sync_outcome_success: 'Success',
            sync_outcome_partial: 'Partial',
            sync_outcome_failure: 'Failure',
            records_processed: 'Records processed',
            records_created: 'Records created',
            records_failed: 'Records failed',
            sync_recorded: 'Sync result recorded.',
            sync_record_error: 'Failed to record the sync result.',
            recording: 'Recording…',
            refusal_base_url_invalid: 'This base URL cannot be used: enter the https address of an endpoint the platform may call, such as https://api.example.com. Private and local addresses are not allowed.',
            refusal_base_url_missing: 'This connection has no base URL, so its health check has nowhere to go. Use Edit to add the base URL, then test again.',
            refusal_base_url_missing_ask_admin: 'This connection has no base URL, so its health check has nowhere to go. Ask an administrator who can edit connections to add the base URL, then test again.',
            refusal_base_url_invalid_ask_admin: "This connection's base URL cannot be used: it must be the https address of an endpoint the platform may call, and private and local addresses are not allowed. Ask an administrator who can edit connections to correct it, then test again.",
        },
        credentials: {
            title: 'Dipòsit de credencials',
            page_title: 'Credencials del connector',
            add: 'Afegir credencial',
            rotate: 'Rotar',
            expired: 'Caducat',
            expiring: 'Pròxim a caducar',
            orphaned: 'Orfe',
            new_credential: 'Nou Credential',
            no_credentials: "No s'han trobat credentials",
            no_expiry: 'Sense caducitat',
            linked_connectors: 'Connectors vinculats',
            expires_at: 'Caduca A',
            value_label: 'Valor * (només escriptura)',
            confirm_delete_title: 'Confirmar eliminació',
            confirm_delete_message: "Eliminar aquesta credencial? Els connectors enllaçats perdran l'accés.",
            rotate_title: 'Rotar credencial',
            new_credential_value: 'Nou valor de credencial',
            fetch_error: 'Error en load credentials. Si us plau, torneu-ho a intentar.',
            health_error: 'Error en load health. Si us plau, torneu-ho a intentar.',
            create_error: 'Error en create credential. Si us plau, torneu-ho a intentar.',
            rotate_error: 'Error en rotate credential. Si us plau, torneu-ho a intentar.',
            delete_error: 'Error en delete credential. Si us plau, torneu-ho a intentar.',
        },
        event_bus: {
            title: "Bus d'esdeveniments",
            page_title: "Bus d'esdeveniments",
            events: 'Esdeveniments',
            subscriptions: 'Subscripcions',
            dlq: 'Cua de missatges no processats',
            publish: 'Publicar esdeveniment',
            retry: 'Reintentar',
            dismiss: 'Descartar',
            search_events: 'Cercar esdeveniments...',
            filter_event_type: "Filtrar per tipus d'esdeveniment...",
            event_type: 'Esdeveniment Tipus',
            source_module: 'Mòdul font',
            timestamp: 'Marca de temps',
            delivery_status: 'Estat de lliurament',
            correlation_id: 'ID de correlació',
            tracking_id: 'ID de seguiment',
            delivery_status_hint: "L'estat de lliurament s'actualitza automàticament a mesura que es processen els esdeveniments.",
            no_events: "No s'han trobat events",
            total_with_count: '{count} en total',
            prev: 'Ant.',
            new_subscription: '+ Nou Subscripció',
            subscriber_type: 'Tipus de subscriptor',
            subscriber_id: 'ID del subscriptor',
            event_type_pattern: 'Esdeveniment Tipus Pattern',
            event_type_pattern_placeholder: 'p. ex. incident.*',
            filter_expression: 'Expressió de filtre',
            filter_expression_placeholder: 'Filtre opcional',
            subscriber_type_hint: 'Trieu com es lliuraran els esdeveniments: webhook envia sol·licituds HTTP, correu envia notificacions, cua encamina a una cua de missatges.',
            subscriber_id_hint: 'Introduïu la destinació dels esdeveniments. Per a webhooks, feu servir un URL. Per a correu, feu servir una adreça. Per a cues, feu servir el nom de la cua.',
            event_type_pattern_hint: 'Coincidiu esdeveniments per tipus amb comodins. Feu servir * per coincidir amb qualsevol segment (ex. incident.* coincideix amb incident.created, incident.resolved).',
            filter_expression_hint: 'Opcional. Filtreu més esdeveniments pel contingut (ex. severity=high).',
            subscriber_id_placeholder_webhook: 'ex. https://la-vostra-app.com/webhook',
            subscriber_id_placeholder_email: 'ex. alertes@la-vostra-empresa.com',
            subscriber_id_placeholder_queue: 'ex. cua-esdeveniments',
            event_pattern: "Patró d'esdeveniment",
            filter: 'Filtrar',
            enabled: 'Activat',
            delivery_stats: 'Estadístiques de lliurament',
            no_subscriptions: "No s'han trobat subscriptions",
            on: 'ON',
            off: 'OFF',
            delivered_failed: '{delivered} lliurats / {failed} fallats',
            confirm_delete_subscription: 'Eliminar aquest subscription?',
            unresolved: 'No resolt',
            resolved: 'Resolt',
            event_id: 'Esdeveniment ID',
            subscription_id: 'Subscripció ID',
            error_code: "Codi d'error",
            error_message: "Missatge d'error",
            attempts: 'Intents',
            last_failed: 'Últim Ha fallat',
            no_dlq_entries: "No s'han trobat dead letter entries",
            dlq_count: '{count} entrada(es) en total',
            fetch_events_error: 'Error en fetch events. Si us plau, torneu-ho a intentar.',
            fetch_subscriptions_error: 'Error en fetch subscriptions. Si us plau, torneu-ho a intentar.',
            fetch_dlq_error: 'Error en fetch dlq entries. Si us plau, torneu-ho a intentar.',
            create_subscription_error: 'Error en create subscription. Si us plau, torneu-ho a intentar.',
            toggle_subscription_error: 'Error en toggle subscription. Si us plau, torneu-ho a intentar.',
            delete_subscription_error: 'Error en delete subscription. Si us plau, torneu-ho a intentar.',
            retry_error: 'Reintentar ha fallat',
            dismiss_error: 'Descart fallat',
            event_published: 'Esdeveniment publicat',
            publish_event_error: "No s'ha pogut publicar l'esdeveniment. Torneu-ho a provar.",
            priority_label: 'Prioritat',
            priority_low: 'Baixa',
            priority_normal: 'Normal',
            priority_high: 'Alta',
            payload_helper_text: 'Introduïu les dades de l\'esdeveniment en format JSON. Exemple: {"incident_id": "INC-001", "severity": "high"}',
            subscription_created: 'Subscripció creada',
            subscription_toggled: 'Subscripció commutada',
            subscription_deleted: 'Subscripció suprimida',
            dlq_retried: 'Missatge de DLQ reintentat',
            dlq_dismissed: 'Missatge de DLQ descartat',
        },
        definitions: {
            title: 'Definicions de connectors',
            page_title: 'Definicions de connectors',
            add: 'Afegeix una definició',
            new_definition: 'Nova definició del connector',
            edit_definition: 'Edita la definició del connector',
            no_definitions: "No s'han trobat definicions de connector",
            definition_count: '{count} definicions',
            all_categories: 'Totes les categories',
            select_category: 'Seleccioneu una categoria...',
            capabilities: 'Capacitats',
            capabilities_hint: 'Capacitats (separades per comes)',
            capabilities_placeholder: 'p. ex. llegir, escriure, sincronitzar',
            created: "S'ha creat la definició del connector",
            updated: "S'ha actualitzat la definició del connector",
            fetch_error: "No s'han pogut obtenir les definicions dels connectors. Si us plau, torna-ho a provar.",
            create_error: "No s'ha pogut crear la definició del connector",
            update_error: 'Error en actualitzar la definició del connector',
            health_check: 'Health check',
            health_check_none: 'None declared',
            health_check_declare: 'This connector type declares a health check',
            health_check_method: 'Method',
            health_check_path: 'Path',
            health_check_expected_status: 'Expected HTTP status',
            health_check_timeout: 'Timeout (seconds)',
            health_check_hint: "Test sends this one request to the connection's base URL (its base_url setting) and records it as healthy only when the answer has the expected status. Without a health check, Test only marks a connection ready for testing.",
        },
        brds: {
            title: 'Captura de BRD',
            page_title: 'Captura BRD',
            create_brd: 'Crear BRD',
            brd_title: 'Títol del BRD',
            description: 'Descripció',
            requirements: 'Requisits',
            status: 'Estat',
            stakeholders: 'Parts interessades',
            connector_label: 'Connector',
            select_connector: 'Seleccionar un connector...',
            approval: 'Aprovació',
            no_brds_found: "No s'han trobat BRDs",
            decision_records: 'Registres de decisions',
            create_decision: 'Crear decisió',
            alternatives_considered: 'Alternatives considerades',
            alternative_name: "Nom de l'alternativa",
            reason_rejected: 'Motiu de rebuig',
            add_alternative: 'Afegir alternativa',
            remove_alternative: "Eliminar l'alternativa {name}",
            alternatives: 'Alternatives',
            rationale: 'Justificació',
            selected_connector: 'Connector seleccionat',
            comma_separated_names: 'Noms separats per comes',
            brd_created: 'BRD creat',
            decision_record_created: 'Registre de decisió creat',
            no_decision_records_found: "No s'han trobat registres de decisió",
            aria_brds_table: 'BRDs',
            aria_decision_records_table: 'Registres de decisió',
            aria_approve: 'Aprovar {name}',
            aria_archive: 'Arxivar {name}',
            brd_title_help: 'Un nom breu i llegible pels humans per a aquest BRD, p. "Integració d\'entrades de Jira", "Lançament d\'Okta SSO".',
            brd_title_placeholder: 'p. ex. Integració de ticketing Jira',
            description_help: 'El problema empresarial que resol aquest connector. Quin resultat espereu un cop estigui en directe?',
            description_placeholder: "p. ex. Automatitzeu la creació de tiquets d'incidències a partir de les alertes de vigilància de manera que les trucades s'apunten en 60 segons.",
            connector_help: "Opcional. Trieu un connector per a aquest BRD; deixeu-lo en blanc si encara no s'ha escollit el connector.",
            requirements_help: 'Llista separada per comes de les capacitats imprescindibles, p. "Inici de sessió OAuth2, lliurament de webhook, exportació d\'auditoria".',
            requirements_placeholder: "Inici de sessió OAuth2, lliurament de webhook, exportació d'auditoria",
            stakeholders_help: 'Noms o correus electrònics separats per comes de les persones que han de tancar la sessió.',
            selected_connector_help: "Trieu el connector al qual s'aplica aquesta decisió.",
            rationale_help: 'Per què es va triar aquest connector. Cobriu els factors decisius perquè un futur lector entengui la trucada.',
            rationale_placeholder: "p. ex. Va triar Jira perquè ja té el nostre historial d'entrades i l'equip està entrenat.",
            alternatives_considered_help: "Altres connectors avaluats i per què no es van triar. Afegeix la defensa per a l'auditoria.",
            alternative_name_placeholder: 'p. ex. Servei Ara',
            reason_rejected_placeholder: "p. ex. Cost 3 vegades més alt, sense cap avantatge per al nostre cas d'ús",
        },
        security_reviews: {
            title: 'Revisions de seguretat',
            page_title: 'Revisions de seguretat',
            create_review: 'Crear revisió',
            connector_name: 'Nom del connector',
            connector_label: 'Connector',
            select_connector: 'Seleccionar un connector...',
            reviewer: 'Revisor',
            select_reviewer: 'Seleccionar un revisor...',
            findings: 'Troballes (una per línia)',
            risk_level: 'Nivell de risc',
            risk_level_placeholder: 'p. ex. alt, mitjà, baix',
            notes: 'Notes',
            review_status: 'Estat de la revisió',
            checklist: 'Llista de verificació',
            privacy_impact: 'Impacte en la privadesa',
            approval_gate: "Porta d'aprovació",
            no_reviews_found: "No s'han trobat revisions",
            review_created: 'Revisió de seguretat creada',
            create_control_mapping: 'Crear mapeig de control',
            control_mapping_created: 'Mapeig de control creat',
            connector_id_label: 'ID del connector',
            control_id_label: 'ID del control',
            control_name_label: 'Nom del control',
            aria_approve: 'Aprovar {name}',
            aria_reject: 'Rebutjar {name}',
            scan_imported: 'Escaneig importat amb èxit',
            import_scan: 'Importa escaneig',
            scan_format: "Format d'escaneig",
            scan_data: "Dades d'escaneig",
            import_results: "Resultats d'importació",
            recommendations: 'Recomanacions',
            auto_populated_from: 'Emplenat automàticament des de {tool}',
            manual_entry: 'Entrada manual',
            automation_type: "Tipus d'automatització",
            select_automation_type: "Seleccioneu el tipus d'automatització...",
            automation_full: 'Complet: el connector automatitza completament aquest control',
            automation_partial: 'Parcial: el connector aporta proves; els humans encara actuen',
            automation_monitoring: "Només monitorització: el connector observa, no s'aplica",
            control_id_help: 'Utilitzeu el codi del vostre esquema de compliment (p. ex., ISO-27001-A.12.1, SOC2-CC6.1 o el vostre identificador de control intern).',
            control_name_help: 'Nom breu llegible pels humans (p. ex., "Restricció d\'accés a la xarxa").',
            description_label: 'Descripció',
            load_sarif_example: 'Exemple de càrrega SARIF',
            load_json_example: "Carregueu l'exemple de JSON personalitzat",
            scan_format_help: "SARIF és l'estàndard OASIS emès per la majoria d'escàners (Snyk, CodeQL, Semgrep). Utilitzeu JSON personalitzat per a eines que no emeten SARIF.",
            scan_data_help: "Enganxeu la sortida completa de l'escaneig, pengeu un fitxer .sarif/.json o feu clic al botó Carrega exemple per veure la forma.",
            upload_file_label: 'Carregueu un fitxer .sarif o .json',
            invalid_scan_json: "No s'han pogut analitzar les dades escanejades com a JSON. Comproveu si hi ha errors de sintaxi a prop del caràcter ressaltat.",
            save_as_review: 'Desa com a revisió de seguretat',
            discard_preview: 'Descarta la vista prèvia',
            preview_hint: 'Aquesta és una vista prèvia; encara no s\'ha desat res. Feu clic a "Desa com a revisió de seguretat" per crear una revisió pendent emplenada prèviament amb aquests valors.',
            reviewer_required_for_save: 'Trieu un revisor per desar aquesta exploració com a revisió de seguretat.',
        },
        poc_scorecards: {
            title: 'Targetes de puntuació POC',
            page_title: 'Quadres POC',
            create_scorecard: 'Crear targeta de puntuació',
            connector_label: 'Connector',
            select_connector: 'Seleccionar un connector...',
            vendor_name: 'Nom del proveïdor',
            recommendation: 'Recomanació',
            recommendation_placeholder: 'p. ex. Continuar, No continuar, Necessita avaluació addicional',
            criteria: "Criteris d'avaluació",
            criterion_name: 'Nom del criteri',
            score: 'Puntuació',
            max_score: 'Puntuació màxima',
            notes: 'Notes',
            add_criterion: 'Afegir criteri',
            remove_criterion: 'Eliminar criteri {name}',
            total_score: 'Puntuació total',
            api_quality: "Qualitat d'API",
            reliability: 'Fiabilitat',
            support_quality: 'Qualitat del suport',
            cost_score: 'Puntuació de cost',
            overall_score: 'Puntuació global',
            evidence: 'Evidència',
            no_scorecards_found: "No s'han trobat targetes de puntuació",
            scorecard_created: 'Quadre POC creat',
            connector_help: 'Trieu el connector per al qual és aquesta avaluació POC.',
            vendor_name_help: 'El venedor o el producte que s\'està avaluant, p. "Okta", "Datadog", "Foc de neu".',
            vendor_name_placeholder: 'p. ex. Okta, Datadog, Floquet de neu',
            recommendation_help: "La teva última trucada després de l'avaluació. Feu-ho breu i decisiu.",
            criteria_help: "Puntua el proveïdor en cada criteri d'avaluació. Afegiu una fila per criteri (API qualitat, fiabilitat, suport, cost, etc.).",
            criterion_name_help: 'El que estàs anotant, p. ex. "API Qualitat", "Fiabilitat", "Suport", "Cost".',
            criterion_name_placeholder: 'p. ex. API Qualitat',
            score_help: 'Puntuació atorgada per aquest criteri. No ha de superar la puntuació màxima.',
            max_score_help: 'Màxima puntuació disponible per a aquest criteri. Utilitzeu la mateixa escala entre les files perquè els totals siguin comparables.',
            notes_help: 'Opcional. Afegiu proves, advertències o enllaços, qualsevol cosa que expliqui la puntuació.',
        },
        common_labels: {
            aria_filter_instances: 'Filtrar instàncies de connectors',
            aria_filter_definitions: 'Filtrar definicions de connectors',
            aria_filter_credentials: 'Filtrar credencials',
            aria_filter_events: 'Filtrar esdeveniments',
            aria_filter_dlq: 'Filtrar cua de missatges morts',
            aria_instance_summary: 'Resum de la instància del connector',
            aria_active_connectors: 'Connectors actius',
            aria_error_connectors: 'Connectors amb errors',
            aria_credential_health: 'Resum de salut de credencials',
            aria_items_per_page: 'Elements per pàgina',
            show_password: 'Mostrar contrasenya',
            hide_password: 'Amagar contrasenya',
            payload_json_label: 'Càrrega útil (JSON)',
            payload_invalid_json: 'La càrrega útil ha de ser JSON vàlid',
            placeholder_event_type: 'p. ex. incident.created',
            placeholder_source_module: 'p. ex. monitoring',
            placeholder_version: 'p. ex. 1.0',
        },
        statuses: {
            configuring: 'Configuració',
            testing: 'Prova',
            active: 'Actius',
            paused: 'En pausa',
            error: 'Error',
            disabled: 'Inhabilitat',
            draft: 'Esborrany',
            approved: 'Aprovat',
            archived: 'Arxivat',
            pending: 'Pendent',
            rejected: 'Rebutjat',
        },
        system_types: {
            application: 'Aplicació',
            database: 'Base de dades',
            service: 'Service',
            infrastructure: 'Infraestructura',
            third_party: 'Third party',
        },
        hosting_types: {
            on_premise: 'Local',
            cloud: 'Núvol',
            hybrid: 'Híbrid',
            managed: 'Gestionat',
        },
        system_statuses: {
            active: 'Actiu',
            decommissioned: 'Retirat',
            planned: 'Planificat',
            under_review: 'En revisió',
        },
        automation_statuses: {
            planning: 'Planificació',
            in_progress: 'En curs',
            completed: 'Completat',
            on_hold: 'En espera',
        },
        gap_types: {
            functional: 'Funcional',
            technical: 'Tècnic',
            security: 'Seguretat',
            compliance: 'Compliment',
            process: 'Procés',
        },
        severity_labels: {
            critical: 'Crític',
            high: 'Alt',
            medium: 'Mitjà',
            low: 'Baix',
        },
        gap_statuses: {
            open: 'Obert',
            in_progress: 'En curs',
            mitigated: 'Mitigat',
            closed: 'Tancat',
            accepted: 'Acceptat',
        },
        verification_statuses: {
            not_started: 'No iniciat',
            in_progress: 'En curs',
            verified: 'Verificat',
            failed: 'Fallat',
        },
        subscriber_types: {
            webhook: 'Webhook',
            internal: 'Intern',
            email: 'Correu',
            queue: 'Cua',
        },
        credential_types: {
            password: 'Contrasenya',
            api_key: 'Clau API',
            oauth2_client: 'Client OAuth2',
            certificate: 'Certificat',
            token: 'Token',
            ssh_key: 'Clau SSH',
            imap_credentials: 'Credencials IMAP',
        },
        categories: {
            hr: 'RRHH',
            crm: 'CRM',
            erp: 'ERP',
            cloud: 'Núvol',
            security: 'Seguretat',
            itsm: 'ITSM',
            custom: 'Personalitzat',
        },
        delivery_statuses: {
            pending: 'Pendent',
            delivered: 'Lliurat',
            partial: 'Parcial',
            failed: 'Fallat',
        },
        automation_planning: {
            title: 'Planificació automatització',
            page_title: 'Planificació automatització',
            program_name: 'Nom del programa',
            program_status: 'Estat',
            description: 'Descripció',
            linked_artifacts: 'Artefactes vinculats',
            create_program: 'Crear programa',
            edit_program: 'Editar programa',
            no_programs_found: "No s'han trobat programes",
            program_created: 'Programa creat',
            program_updated: 'Programa actualitzat',
            target_date: 'Data objectiu',
            owner: 'Propietari',
            select_status: 'Seleccionar estat...',
            aria_programs_table: "Programes d'automatització",
            aria_edit: 'Editar {name}',
            export_programs: 'Exportar programes',
            title_label: 'Títol del programa',
            title_help: 'Un nom breu i llegible pels humans per al programa d\'automatització, p. "Llançament d\'incident a Jira Q3", "PDPL automatització de sol·licituds d\'accés".',
            title_placeholder: "p. ex. Llançament d'incident a Jira Q3",
            description_help: 'Què cobreix aquest programa i el resultat que espereu quan estigui en directe. Opcional però recomanable.',
            description_placeholder: 'p. ex. Les alertes de monitorització de cables a Jira, de manera que les trucades es detecten en 60 segons.',
            owner_label: 'Propietari del programa',
            owner_help: "La persona responsable d'aquest programa. Rebran notificacions sobre el progrés i les portes.",
            select_owner: 'Seleccioneu un propietari...',
            frameworks_in_scope: "Marcs a l'abast",
            frameworks_in_scope_help: "Marcs de compliment als quals suporta aquest programa. Ajuda a la pista d'auditoria a mostrar per quina normativa es regeix l'automatització.",
            select_frameworks: 'Seleccioneu marcs...',
            status_draft: 'Esborrany',
            status_active: 'Actius',
            status_completed: 'Completat',
            status_archived: 'Arxivat',
            change_status: "Canvia l'estat",
            status_updated: 'Estat actualitzat',
            aria_change_status: "Canvia l'estat de {name}",
        },
        system_inventory: {
            title: 'Inventari de sistemes',
            page_title: 'Inventari de sistemes',
            system_name: 'Nom del sistema',
            system_type: 'Tipus de sistema',
            hosting: 'Allotjament',
            status: 'Estat',
            description: 'Descripció',
            create_system: 'Crear sistema',
            edit_system: 'Editar sistema',
            no_systems_found: "No s'han trobat sistemes",
            system_created: 'Sistema creat',
            system_updated: 'Sistema actualitzat',
            select_type: 'Seleccionar tipus...',
            select_hosting: 'Seleccionar allotjament...',
            select_status: 'Seleccionar estat...',
            owner: 'Propietari',
            department: 'Departament',
            data_categories: 'Categories de dades',
            data_categories_placeholder: 'Separats per comes, p. ex. PII, Financer, Salut',
            jurisdiction: 'Jurisdicció',
            jurisdiction_placeholder: 'p. ex. UE, EUA, Global',
            interfaces: 'Interfícies',
            interfaces_placeholder: 'Separats per comes, p. ex. REST API, SFTP, JDBC',
            owner_placeholder: 'Seleccioneu un propietari responsable...',
            department_placeholder: "Seleccioneu la unitat d'organització responsable...",
            system_name_hint: 'Utilitzeu el nom del sistema o de l’aplicació reconegut per l’empresa.',
            system_type_hint: 'Classifiqueu el sistema perquè els informes puguin agrupar actius similars.',
            hosting_hint: 'Identifiqueu on s’executa el sistema per a la residència, la seguretat i la revisió dels proveïdors.',
            status_hint: "Establiu l'estat del cicle de vida utilitzat per les revisions, les exportacions i les evidències d'auditoria.",
            owner_hint: 'Trieu l’usuari actiu responsable de la governança del sistema i de les tasques de seguiment.',
            department_hint: "Utilitzeu la unitat d'organització responsable d'operar o governar aquest sistema.",
            data_categories_hint: 'Llista les classes de dades processades per aquest sistema, separades per comes.',
            jurisdiction_hint: "Enregistreu la jurisdicció legal o d'allotjament principal per al mapatge de compliment.",
            interfaces_hint: "Llista els camins de connector o d'integració utilitzats per intercanviar dades.",
            description_hint: 'Resumir el que fa el sistema i per què és important per al mapa de dades.',
            owner_department_required: 'El propietari i el departament són obligatoris.',
            aria_systems_table: 'Inventari de sistemes',
            aria_edit: 'Editar {name}',
            export_systems: 'Exportar sistemes',
            aria_filter_systems: 'Filter systems',
            governance_context: 'Governance context',
            governance_context_hint: 'Confirm ownership, jurisdiction, data categories, and interfaces before saving.',
            governance_context_summary: 'This context drives data mapping, residency review, and connector accountability.',
        },
        traceability_matrix: {
            title: 'Matriu de traçabilitat',
            page_title: 'Matriu de traçabilitat',
            brd_selector: 'Seleccionar BRD',
            requirement: 'Requisit',
            linked_artifact: 'Artefacte vinculat',
            verification_status: 'Estat de verificació',
            verified_by: 'Verificat per',
            create_entry: 'Crear entrada',
            edit_entry: 'Editar entrada',
            no_entries_found: "No s'han trobat entrades",
            entry_created: 'Entrada creada',
            entry_updated: 'Entrada actualitzada',
            select_brd: 'Seleccionar un BRD...',
            select_status: 'Seleccionar estat...',
            aria_matrix_table: 'Matriu de traçabilitat',
            aria_edit: 'Editar entrada per a {name}',
            export_matrix: 'Exportar matriu',
            brd_selector_hint: 'Select the Business Requirement Document (BRD) this entry belongs to.',
            fetch_brds_error: 'Failed to load BRD list',
            requirement_id_hint: 'Unique reference code from the BRD or regulatory source (e.g. REQ-001).',
            requirement_text: 'Requirement Text',
            requirement_text_hint: 'Detailed description of the requirement as defined in the source document.',
            verification_method: 'Verification Method',
            verification_method_hint: 'Process used to confirm the requirement is met (e.g. Unit Test, Audit).',
            verification_method_placeholder: 'e.g. test, inspection, review',
            verification_status_hint: 'Current verification state of the requirement implementation.',
            requirement_id: 'ID del requisit',
        },
        gap_register: {
            title: 'Registre escletxes',
            page_title: 'Registre escletxes',
            gap_title: 'Títol escletxa',
            gap_type: 'Tipus escletxa',
            severity: 'Gravetat',
            status: 'Estat',
            description: 'Descripció',
            remediation_plan: 'Pla de remediació',
            assigned_to: 'Assignat a',
            due_date: 'Data de venciment',
            create_gap: 'Crear escletxa',
            edit_gap: 'Editar escletxa',
            no_gaps_found: "No s'han trobat escletxes",
            gap_created: 'Escletxa creada',
            gap_updated: 'Escletxa actualitzada',
            select_type: 'Seleccionar tipus...',
            select_severity: 'Seleccionar gravetat...',
            select_status: 'Seleccionar estat...',
            aria_gaps_table: 'Registre escletxes',
            aria_edit: 'Editar {name}',
            export_gaps: 'Exportar escletxes',
            gap_title_hint: 'Provide a concise name for the identified gap.',
            gap_type_hint: 'Select the nature of the gap (e.g., missing coverage or integration issue).',
            severity_hint: 'The impact of this gap on compliance and operations.',
            status_hint: 'Current stage of the gap resolution lifecycle.',
            owner_hint: 'The person responsible for tracking and remediating this gap.',
            description_hint: 'Detailed context regarding the gap, its root cause, and impact.',
            mitigation_plan_hint: 'Steps planned or taken to address and close the gap.',
            linked_requirement_ids_hint: 'Comma-separated requirement codes this gap affects.',
            linked_system_ids_hint: 'Comma-separated system IDs associated with this gap.',
            gap_types: {
                coverage: 'Coverage',
                capability: 'Capability',
                integration: 'Integration',
                compliance: 'Compliance',
            },
            linked_ids_placeholder: 'Comma-separated IDs',
            linked_requirement_ids: 'Linked Requirement IDs',
            linked_system_ids: 'Linked System IDs',
            mitigation_plan: 'Mitigation Plan',
            select_owner: 'Select owner',
        },
        regulatory: {
            system_inventory_governance_info: 'System inventory aligns with Data Law enterprise governance. System types map to regulatory categories, hosting classification determines jurisdiction-specific controls. Use framework linkage to connect systems to applicable regulatory frameworks.',
            automation_planning_workflow_info: 'Automation programs align with compliance workflow obligations defined by applicable regulatory frameworks. Framework scope determines which requirements the program addresses.',
            traceability_regulatory_source_info: 'Each traceability entry connects a BRD requirement to its regulatory source. Verification status tracks whether implementation satisfies the regulatory obligation.',
            security_reviews_control_mapping_info: 'Security reviews evaluate framework-specific controls applicable to each connector. Risk level captures assessed exposure, findings document deviations from control requirements.',
            gap_register_framework_info: 'Gap Register captures compliance gaps per regulatory framework. Use framework filter to view gaps for a specific framework. Severity levels align with framework risk classification.',
            poc_scorecards_criteria_info: 'POC scorecards evaluate connectors against regulatory criteria. Criteria structure supports weighted scoring with evidence notes. Vendor linkage connects to vendor record.',
            evidence_trail_info: 'All connector module actions generate audit events for cross-module evidence trail integrity. CSV exports preserve referential integrity.',
        },
        system_inventory_extra: {
            framework_linkage: 'Vinculació de marc',
            framework_linkage_placeholder: 'p. ex. GDPR, ISO 27001, NIS2',
        },
        automation_planning_extra: {
            framework_scope: 'Abast del marc',
            framework_scope_placeholder: 'p. ex. GDPR, PCI-DSS',
            procedure_linkage: 'Vinculació de procediment',
            procedure_linkage_placeholder: 'p. ex. SOP-001, PROC-DATA-003',
        },
        traceability_matrix_extra: {
            regulatory_source: 'Font regulatòria',
            regulatory_source_placeholder: 'p. ex. GDPR Art. 30, ISO 27001 A.8',
        },
        security_reviews_extra: {
            control_mapping_col: 'Mapatge de controls',
            control_mappings_section: 'Mapes de control',
            no_control_mappings: 'Encara no hi ha mapes de control. Feu clic a "Crea un mapa de control" per afegir-ne un.',
            automation_type_col: 'Automatització',
        },
        gap_register_extra: {
            framework_filter: 'Marc',
            framework_id: 'ID del marc',
            framework_id_placeholder: 'p. ex. GDPR, ISO 27001',
            all_frameworks: 'Tots els marcs',
        },
        poc_scorecards_extra: {
            criteria_count: 'Criteris',
        },
        export_labels: {
            export_brds: 'Exportar BRDs',
            export_decision_records: 'Exportar registres de decisions',
            export_security_reviews: 'Exportar revisions de seguretat',
            export_poc_scorecards: 'Exportar targetes POC',
            export_instances: "Instàncies d'exportació",
            export_definitions: 'Exportar definicions',
            export_credentials: 'Exportar credencials',
            export_events: 'Exporta esdeveniments',
            exporting: 'Exportant...',
            export_success: 'Exportació completada',
            export_error: "Error en l'exportació",
        },
    },
    // fix-sprint-20260506-2121-followup F16 — the contracts module's own
    // section (it was filed under vendor_risk).
    contracts: {
        obligation_extraction: {
            extract_label: 'Extreure obligacions',
            extracting: "S'està extraient…",
            results_heading: 'Obligacions extretes ({count})',
            tokens_label: '≈ {count} fitxes',
            no_obligations: "No s'han detectat patrons d'obligació en aquest text del contracte.",
            col_class: 'Classe',
            col_summary: 'Resum (editable)',
            col_excerpt: 'Fragment de clàusula',
            col_due_date: 'Data de venciment (editable)',
            col_priority: 'Prioritat',
            due_date_placeholder: 'YYYY-MM-DD',
            class_notice: 'Termini de preavís',
            class_audit: "Dret d'auditoria",
            class_insurance: 'Assegurança',
            class_sla: 'Resposta SLA',
            class_breach: "Notificació d'incompliment",
            class_renewal: 'Finestra de renovació',
            citations_label: 'Citacions',
            accept_hint: 'Accept crearà una tasca per obligació (cap 8) amb la prioritat + data de venciment mostrada.',
            accept_label: 'Acceptar obligacions',
            reject_label: "Rebutja l'informe",
            resolve_summary: 'Resolució: {status} · {tasks} tasques creades',
            error_generic: "No s'han pogut extreure obligacions. Torna-ho a provar o consulta el text del contracte.",
            summary_input_label: 'Summary for obligation {n}: {classification}',
            due_date_input_label: 'Due date for obligation {n}: {classification}',
            tasks_dropped: '{count} task(s) could not be created. Create them from the obligations above.',
        },
    },
    vendor_risk: {
        risk_acceptance_reasoning: {
            draft_label: 'Esborrany de raonament',
            drafting: 'Elaboració de raonaments...',
            results_heading: 'AI-esborrany de raonament ({risk_level})',
            tokens_label: '≈ {count} fitxes',
            narrative_label: 'Justificació narrativa',
            controls_heading: 'Controls compensadors',
            no_controls: 'No es recomana cap control compensador.',
            citations_label: 'Citacions',
            accept_label: 'Acceptar el raonament',
            reject_label: "Rebutja l'esborrany",
            accept_hint: "Accepta els registres del raonament sobre l'esborrany persistent per al llinatge IAT. La narrativa + els controls no actualitzen automàticament el registre d'acceptació; cal enganxar enrere l'operador.",
            resolve_summary: 'Resolució: {status}',
            error_generic: "No s'ha pogut redactar el raonament. Torneu-ho a provar o comproveu l'estat d'acceptació del risc.",
        },
        redline_assistant: {
            run_label: 'Línia vermella amb biblioteca',
            running: "S'està executant la línia vermella...",
            results_heading: 'Suggeriments de línia vermella ({count})',
            tokens_label: '≈ {count} fitxes',
            no_suggestions: "No s'han detectat buits de clàusula estàndard.",
            jurisdiction_label: 'Jurisdicció',
            col_kind: 'amable',
            col_severity: 'Severitat',
            col_title: 'Suggeriment',
            col_excerpt: 'Fragment coincident',
            col_apply: 'Aplicar',
            kind_missing: "Falta l'estàndard",
            kind_weaker: "Més feble que l'estàndard",
            kind_jurisdiction: 'Superposició de la jurisdicció',
            proposed_label: 'Text proposat',
            citations_label: 'Citacions',
            accept_label: 'Accepta seleccionat',
            reject_label: "Rebutja l'informe",
            partial_label: 'Aplica la selecció',
            accept_hint: "Aplicar registres de selecció els suggeriments escollits a l'esborrany del llinatge IAT. No s'aplica cap edició automàtica al text del contracte: l'operador condueix la línia vermella.",
            resolve_summary: 'Resolució: {status} · {count} suggeriment(s) aplicat',
            error_generic: "No s'ha pogut produir una línia vermella. Torna-ho a provar o comprova l'estat del contracte.",
            clause_library_link: 'Gestionar la biblioteca de clàusules',
            text_source_extracted: 'the extracted contract text',
            text_source_inline: 'the text supplied for this scan',
            text_source_summary: 'the contract description',
            scanned_text_note: 'Scanned {source} ({count} characters). The uploaded contract file is not read.',
            rescan_button: 'Scan again',
            rescan_label: 'Scan for another jurisdiction (country code)',
            jurisdiction_provided: '{code} (chosen for this scan)',
            jurisdiction_no_overlay: '{code} (no jurisdiction overlay exists for it; only your clause library was checked)',
            jurisdiction_assumed: '{code} (assumed: the contract records no jurisdiction)',
        },
        clause_library: {
            page_title: 'Biblioteca de clàusules',
            add_label: 'Afegeix una clàusula',
            edit_label: 'Edita',
            delete_label: 'Suprimeix',
            slug_label: 'Llimac',
            title_label: 'Títol',
            body_label: 'Cos',
            jurisdiction_label: 'Jurisdicció',
            tags_label: 'Etiquetes (separades per comes)',
            must_contain_label: 'Ha de contenir marcadors (separats per comes)',
            severity_label: 'Gravetat en faltar',
            no_clauses: "Encara no hi ha clàusules. Afegiu-ne un per sembrar l'assistent de línia vermella.",
            save_label: 'Desa',
            cancel_label: 'Cancel·la',
            load_error: "No s'ha pogut carregar la biblioteca de clàusules.",
            save_error: "No s'ha pogut desar la clàusula.",
            delete_error: "No s'ha pogut suprimir la clàusula.",
            delete_confirm: 'Voleu suprimir la clàusula "{slug}"? Això no es pot desfer.',
        },
        subprocessor_impact: {
            assess_label: "Avaluar l'impacte",
            assessing: "S'està avaluant l'impacte...",
            results_heading: 'Impacte del subprocessador ({severity})',
            tokens_label: '≈ {count} fitxes',
            risk_delta_label: 'Delta del risc',
            notification_heading: 'Notificació al client',
            subject_label: 'Assumpte',
            deadline_label: 'Termini de notificació',
            body_label: 'Òrgan de notificació',
            required_fields_label: 'Camps de la notificació (article 28(2))',
            mitigation_heading: 'Mesures de mitigació',
            citations_label: 'Citacions',
            accept_label: "Acceptar l'avaluació",
            reject_label: "Rebutja l'avaluació",
            accept_hint: 'Acceptar crearà una tasca de notificació al client amb la data límit SLA com a data de venciment.',
            resolve_summary: 'Resolució: {status}{task}',
            error_generic: "No s'ha pogut avaluar aquest canvi de subprocessador. Torneu-ho a provar o comproveu el registre de canvis.",
        },
        onboarding_evidence: {
            collect_label: "Executeu AI recopilació d'evidències",
            collecting: 'Recollint proves...',
            results_heading: "Recollida d'evidències ({tier})",
            tokens_label: '≈ {count} fitxes',
            findings_heading: 'Evidència existent',
            missing_heading: 'Artefactes que falten',
            no_findings: "No s'han detectat proves existents.",
            no_missing: 'No falta cap artefacte obligatori per a aquest nivell de risc.',
            next_step_label: 'Següent pas',
            class_dpa: 'Acord de tractament de dades',
            class_soc2: 'Informe SOC 2',
            class_iso27001: 'Certificat ISO 27001',
            class_insurance: "Pòlissa d'assegurança cibernètica",
            class_incident_history: 'Incident previ',
            accept_hint: 'Accepting creates a task for each missing artefact (up to 5) and records each document found as draft evidence on the vendor. Draft evidence stays unapproved until a person with evidence approval rights reviews it. Prior incidents are listed for your review only.',
            accept_label: "Accepta l'informe",
            reject_label: "Rebutja l'informe",
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created · {evidence} draft evidence record(s) attached',
            error_generic: "No s'ha pogut executar AI recopilació d'evidències. Torna-ho a provar o comprova l'estat del cas d'incorporació.",
        },
        no_permission_title: 'Permisos insuficients',
        no_permission_message: 'No teniu permís per accedir a la gestió de riscos del proveïdor.',
        unknown_vendor: 'Venedor desconegut',
        nav: {
            dashboard: 'Tauler de riscos',
            scoring: 'Puntuació de riscos',
            questionnaires: 'Qüestionaris',
            contracts: 'Contractes',
            gates: "Portes d'aprovació",
            reviews: 'Avaluacions de proveïdors',
            signals: 'Senyals de risc',
            reassessments: 'Reavaluacions',
            portfolio: 'Cartera de riscos',
            acceptances: 'Acceptació de riscos',
            review_routing: 'Encaminament de revisions',
            onboarding_cases: "Casos d'incorporació",
            sub_processor_register: 'Registre de subprocessadors',
        },
        dashboard: {
            title: 'Tauler de risc de proveïdors',
            page_title: 'Tauler de control de risc del proveïdor',
            loading: 'Carregant dashboard data...',
            risk_tier_header: 'Nivell de risc',
            count_header: 'Compte',
            percentage_header: 'Percentatge',
            sections_nav: 'Seccions de risc del venedor',
            urgent_expiry: 'Urgent: caduca en 14 dies',
            no_previous_tier: 'Sense nivell anterior',
            view_as_chart: 'Veure com a gràfic',
            view_as_table: 'Veure com a taula',
            showing_chart: 'Mostrant vista de gràfic',
            showing_table: 'Mostrant vista de taula',
            load_error: 'Error en load vendor risk dashboard. Si us plau, torneu-ho a intentar.',
            risk_distribution: 'Risc Distribution Visió general',
            recent_tier_changes: 'Recent Nivell Changes',
            no_tier_changes: 'Sense canvis de nivell recents.',
            review_kpis: 'KPIs de revisió',
            total_vendors_reviewed: 'Total Proveïdors Reviewed',
            reviews_by_outcome: 'Revisions per resultat',
            contract_coverage: 'Cobertura contractual',
            total_vendors: 'Total Proveïdors',
            with_active_dpa: 'Amb Actiu DPA',
            without_dpa: 'Sense DPA',
            dpa_coverage: 'Cobertura DPA',
            active_contracts_by_type: 'Actiu Contractes per Tipus',
            expiring_contracts: 'Contractes que caduquen (propers 90 dies)',
            no_expiring_contracts: 'Cap contracte que caduqui en els propers 90 dies.',
            vendor: 'Proveïdor',
            previous_tier: 'Anterior Nivell',
            current_tier: 'Actual Nivell',
            changed_at: 'Canviat el',
            expires_at: 'Caduca A',
            days_remaining: 'Dies restants',
            days: 'dies',
            vendors_label: 'proveïdors',
            no_data: 'Sense dades',
            critical: 'Crític',
            high: 'Alt',
            medium: 'Mitjà',
            low: 'Baix',
            expiring_acceptances_heading: 'Acceptacions de risc en venciment',
            expiring_30d_label: 'Venciment en 30 dies',
            expiring_60d_label: 'Venciment en 60 dies',
            expiring_90d_label: 'Venciment en 90 dies',
        },
        scoring: {
            title: 'Puntuació de riscos',
            page_title: 'Puntuació de risc del venedor',
            filter_scores: 'Filtre les puntuacions de risc',
            filter_by_tier: 'Filtreu per nivell de risc',
            scores_pagination: 'Paginació de puntuacions de risc',
            config_dimensions: 'Configuració de dimensions de puntuació',
            scores_loaded: "S'han carregat {count} puntuacions de risc",
            weight_must_equal_100: 'El pes total ha de ser igual a 100 (actual: {current})',
            out_of_10: 'sobre 10, puntuat amb {tier}',
            dimension_name: 'Nom de la dimensió {index}.',
            dimension_weight: '{name} pes (percentatge)',
            must_equal_100: 'ha de ser igual a 100',
            config_title: 'Configuració de puntuació',
            scores_title: 'Proveïdor Risc Scores',
            new_score_title: 'Puntuació un Proveïdor',
            new: 'Nou puntuació',
            vendor_id: 'Proveïdor ID',
            risk_tier: 'Risc Nivell',
            score: 'Puntuació',
            scored_at: 'Puntuat el',
            no_scores: "No s'han trobat risk scores",
            all_tiers: 'Tots els nivells',
            load_config_error: 'Error en load risk scoring configuration. Si us plau, torneu-ho a intentar.',
            update_config: 'Actualitzar configuració de puntuació',
            update_config_error: 'Error en update risk scoring configuration. Si us plau, torneu-ho a intentar.',
            load_scores_error: 'Error en load vendor risk scores. Si us plau, torneu-ho a intentar.',
            calculate_error: 'Error en calculate vendor risk score. Si us plau, torneu-ho a intentar.',
            weight: 'pes',
            score_range: 'Puntuació (1-5)',
            method: 'Mètode',
        },
        questionnaires: {
            title: 'Qüestionaris',
            page_title: 'Qüestionaris de proveïdors',
            filter_questionnaires: 'Filtres de qüestionaris',
            questionnaire_views: 'Vistes de qüestionaris',
            templates_list: 'Llista de plantilles de qüestionari',
            instances_list: "Llista d'instàncies de qüestionari",
            filter_by_status: 'Filtrar per estat del qüestionari',
            templates_per_page: 'Plantilles per pàgina',
            instances_per_page: 'Instàncies per pàgina',
            select_template: 'Seleccioneu la plantilla del qüestionari',
            template_category: 'Categoria de la plantilla',
            templates_tab: 'Plantilles',
            instances_tab: 'Instàncies',
            new: 'Nou qüestionari',
            new_template: 'Nova plantilla',
            new_instance: 'Nova instància',
            vendor_id: 'ID del proveïdor',
            template_id: 'ID de la plantilla',
            assigned_to: 'Assignat a',
            due_date: 'Data de venciment',
            responses: 'Respostes',
            risk_score: 'Puntuació de risc',
            no_questionnaires: "No s'ha trobat cap qüestionari.",
            load_templates_error: "No s'han pogut carregar les plantilles de qüestionari",
            load_instances_error: "No s'han pogut carregar els qüestionaris",
            create_template_error: "No s'ha pogut crear la plantilla de qüestionari",
            initiate_error: "No s'ha pogut iniciar el qüestionari",
            submit_error: "No s'ha pogut enviar el qüestionari",
            review_error: "No s'ha pogut revisar el qüestionari",
            resubmit: 'Tornar a enviar',
            approve_error: "No s'ha pogut aprovar el qüestionari",
            reject_error: "No s'ha pogut rebutjar el qüestionari",
            resubmit_error: "No s'ha pogut tornar a enviar el qüestionari",
            templates_loaded: "S'han carregat {count} plantilles de qüestionari",
            instances_loaded: "S'han carregat {count} instàncies de qüestionari",
            fill_responses_cta: 'Emplenar respostes',
            fill_responses_title: 'Emplenar respostes del qüestionari',
            template_missing: 'No s’ha trobat la plantilla.',
            responses_saved: 'Respostes desades.',
            load_template_error: "No s'ha pogut carregar la plantilla.",
            version_history: 'Historial de versions',
            download_previous_version: 'Baixar la versió anterior',
            trigger: 'Activador',
            template_label: 'Plantilla',
            reviewer: 'Revisor',
            reviewer_required: 'Cal assignar un revisor per als qüestionaris de tipus {trigger} (separació de funcions).',
            select_option: 'Seleccioneu una opció',
            multi_choice_hint: 'Seleccioneu totes les opcions aplicables',
            category_initial_onboarding: 'Incorporació inicial',
            category_annual_review: 'Revisió anual',
            category_incident_triggered: 'Activat per incident',
            category_contract_renewal: 'Renovació del contracte',
            template_editor: {
                page_title: 'Editar plantilla de qüestionari',
                preview_page_title: 'Previsualitzar plantilla de qüestionari',
                edit_structure: "Editar l'estructura",
                preview: 'Previsualització',
                actions: 'Accions',
                back_to_list: 'Tornar a les plantilles',
                load_error: "No s'ha pogut carregar la plantilla",
                save_error: "No s'ha pogut desar l'estructura de la plantilla",
                saved: "S'ha desat l'estructura de la plantilla.",
                sections_heading: 'Seccions',
                questions_heading: 'Preguntes',
                add_section: 'Afegir secció',
                edit_section: 'Editar secció',
                delete_section: 'Suprimir secció',
                move_section_up: 'Pujar la secció',
                move_section_down: 'Baixar la secció',
                confirm_delete_section: 'Voleu suprimir la secció "{title}"? Se n’eliminaran totes les preguntes.',
                section_title: 'Títol de la secció',
                section_description: 'Descripció de la secció',
                section_id: 'ID de la secció',
                section_id_hint: "Es genera automàticament si no n'indiqueu un explícitament.",
                no_sections: 'Encara no hi ha cap secció. Afegiu-ne una per començar.',
                no_questions: 'Encara no hi ha cap pregunta en aquesta secció.',
                add_question: 'Afegir pregunta',
                edit_question: 'Editar pregunta',
                delete_question: 'Suprimir pregunta',
                move_question_up: 'Pujar la pregunta',
                move_question_down: 'Baixar la pregunta',
                confirm_delete_question: 'Voleu suprimir la pregunta "{text}"?',
                question_text: 'Text de la pregunta',
                question_type: 'Tipus de pregunta',
                question_id: 'ID de la pregunta',
                evidence_required: 'Evidència obligatòria',
                risk_weight: 'Pes de risc',
                scoring_map: 'Mapa de puntuació',
                scoring_map_json_hint: 'Objecte JSON que assigna valors de resposta a puntuacions numèriques.',
                scoring_map_invalid_json: 'El mapa de puntuació ha de ser un JSON vàlid.',
                options_heading: 'Opcions de resposta',
                add_option: 'Afegir opció',
                remove_option: 'Eliminar opció',
                option_label: 'Etiqueta',
                option_value: 'Valor',
                option_risk_weight: 'Pes de risc',
                min_options_error: 'Les preguntes d’opció múltiple han de tenir com a mínim 2 opcions.',
                option_label_required: 'Cada opció necessita una etiqueta.',
                option_value_required: 'Cada opció necessita un valor.',
                option_value_unique: 'Els valors de les opcions han de ser únics.',
                branching_rules_heading: 'Regles de bifurcació',
                branching_rules_hint: 'Mostrar o amagar aquesta pregunta segons la resposta a una pregunta anterior.',
                add_branching_rule: 'Afegir regla de bifurcació',
                remove_branching_rule: 'Eliminar regla de bifurcació',
                branching_condition_question: 'Si la pregunta',
                branching_condition_value: 'té el valor',
                branching_action: 'aleshores',
                branching_action_show: 'mostrar aquesta pregunta',
                branching_action_hide: 'amagar aquesta pregunta',
                branching_select_condition_question: 'Seleccioneu una pregunta anterior',
                branching_select_condition_value: 'Seleccioneu el valor',
                save: "Desar l'estructura",
                saving: 'Desant…',
                cancel: 'Cancel·lar',
                close: 'Tancar',
                question_text_required: 'El text de la pregunta és obligatori.',
                section_title_required: 'El títol de la secció és obligatori.',
                type_single_choice: 'Opció única',
                type_multi_choice: 'Opció múltiple',
                type_free_text: 'Text lliure',
                type_numeric: 'Numèric',
                type_date: 'Data',
                type_file_upload: 'Càrrega de fitxer',
                type_yes_no: 'Sí / No',
                preview_heading: 'Previsualització de la plantilla',
                preview_subtitle: 'Previsualització de només lectura de com es mostra el qüestionari al proveïdor.',
                preview_no_content: 'Aquesta plantilla encara no conté seccions ni preguntes.',
                preview_branching_notice: "Les regles de bifurcació s'avaluen durant l'emplenament; la previsualització mostra totes les preguntes.",
            },
            review_modal: {
                title: 'Revisar les respostes del qüestionari',
                open_cta: 'Revisar',
                loading: 'Carregant qüestionari…',
                template_missing: 'No s’ha trobat la plantilla.',
                load_error: "No s'ha pogut carregar el qüestionari per revisar-lo.",
                annotation_heading: 'Anotacions',
                annotation_placeholder: 'Afegiu una anotació del revisor per a aquesta pregunta…',
                annotation_status_label: "Estat de l'anotació",
                annotation_status_acceptable: 'Acceptable',
                annotation_status_concern: 'Preocupació',
                annotation_status_requires_action: 'Requereix acció',
                review_comments_label: 'Comentaris generals de la revisió',
                review_comments_placeholder: 'Resumiu les conclusions de la revisió…',
                justification_label: 'Justificació de la decisió',
                justification_placeholder: 'Expliqueu la motivació de la vostra decisió…',
                reject_requires_comments: 'Per rebutjar un qüestionari calen comentaris de revisió.',
                decision_heading: 'Decisió',
                decision: {
                    approved: 'Aprovar',
                    rejected: 'Rebutjar',
                    additional_info_requested: 'Sol·licitar informació addicional',
                },
                submit: 'Enviar revisió',
                submitting: 'Enviant la revisió…',
                cancel: 'Cancel·lar',
                submit_success: "S'ha enviat la revisió.",
                submit_error: "No s'ha pogut enviar la revisió.",
                separation_of_duties_error: 'No podeu revisar un qüestionari que us havien assignat per emplenar. La separació de funcions exigeix un revisor diferent.',
                decision_required: 'Seleccioneu una decisió de revisió.',
                announce_opened: 'Modal de revisió obert.',
                announce_submitted: 'Revisió enviada.',
            },
            instance_detail: {
                submitted_at: 'Enviat',
                reviewed_at: 'Revisat',
                reviewer: 'Revisor',
                review_decision: 'Decisió',
                review_comments: 'Comentaris de la revisió',
                decision_approved: 'Aprovat',
                decision_rejected: 'Rebutjat',
                decision_additional_info_requested: 'Sol·licitada més informació',
                decision_pending: 'Revisió pendent',
                expand_comments: 'Mostrar tots els comentaris',
                collapse_comments: 'Amagar tots els comentaris',
                no_reviewer: 'Cap revisor assignat',
                earlier_reviews: 'Earlier reviews',
                earlier_review_entry: '{decision} by {name} on {date}.',
                earlier_review_comments: 'Comments: {comments}',
                earlier_review_question_note: 'Note on {question}: {comments}',
                earlier_review_note: 'Question note: {comments}',
                earlier_review_unknown_person: 'an unrecorded reviewer',
                earlier_review_unknown_date: 'an unrecorded date',
                // Sprint 62.58.0 gate E12.5 pass 1 (P3-4) — the instance export's line
                // for one earlier review: its number, then the page's own sentence
                // (`earlier_review_entry`), so the two never word a review two ways.
                earlier_review_export_line: 'Earlier review {index}: {entry}',
                earlier_review_no_decision: 'No decision recorded',
            },
            instance_statuses: {
                draft: 'Esborrany',
                in_progress: 'En curs',
                submitted: 'Submitted',
                under_review: 'Under review',
                approved: 'Aprovat',
                rejected: 'Rebutjat',
                expired: 'Caducat',
            },
            export: {
                portfolio_label: 'Exportar la cartera',
                instance_label: 'Exportar',
                portfolio_success: "L'exportació de la cartera està a punt.",
                instance_success: "L'exportació del qüestionari està a punt.",
                portfolio_error: "No s'ha pogut exportar la cartera de qüestionaris.",
                instance_error: "No s'ha pogut exportar el qüestionari.",
                header_line: '{label}: {value}',
                default_title: 'Vendor questionnaire',
                unassigned: 'Unassigned',
                risk_score_none: 'Not scored',
                column_question: 'Question',
                column_answer: 'Answer',
                column_evidence: 'Evidence',
                column_annotation: 'Reviewer annotation',
                section_no_questions: 'No questions in this section.',
                portfolio_title: 'Vendor questionnaires portfolio',
                portfolio_generated: 'Generated',
                portfolio_rows: 'Rows',
                portfolio_status_filter: 'Status filter',
                portfolio_vendor_filter: 'Vendor filter',
                portfolio_assignee_filter: 'Assignee filter',
            },
            branching: {
                hidden_notice: 'Hi ha preguntes amagades per regles de bifurcació.',
                cycle_warning: 'S’ha detectat un cicle a les regles de bifurcació. Les preguntes afectades es mostren per defecte.',
            },
        },
        contracts: {
            edit_error: 'Failed to update contract',
            edit_success: 'Contract updated',
            edit_contract: 'Edit contract details',
            record_signature: 'Record signature',
            signature_section: 'Signatures on this contract',
            signature_none: 'No signature has been recorded for this contract.',
            signatory_name: 'Signatory name',
            signatory_title: 'Signatory title',
            signatory_party: 'Signing party',
            signatory_party_organization: 'Our organisation',
            signatory_party_counterparty: 'Counterparty',
            signed_on: 'Date signed',
            signature_evidence: 'Evidence reference',
            signature_evidence_none: 'No evidence reference is recorded on this contract yet. Attach the signed document below instead.',
            signature_evidence_attachment: 'Signed document',
            signature_evidence_attachment_hint: 'Upload the signed document when there is no evidence reference to select.',
            signature_evidence_hint: 'A contract enters into force on a recorded signature, so a signature needs evidence: reference evidence already on this contract, or attach the signed document.',
            signature_saved: 'Signature recorded',
            signature_error: 'Failed to record the signature',
            title: 'Contractes',
            page_title: 'Contractes de proveïdors',
            new: 'Nou contracte',
            vendor_id: 'Proveïdor ID',
            filter_contracts: 'Filtre els contractes',
            filter_by_document_type: 'Filtra per tipus de document',
            filter_by_status: 'Filtra per estat del contracte',
            per_page: 'Contractes per pàgina',
            expired_notice: 'Aquest contracte ha expirat',
            transition_reason: 'Reason for this change',
            transition_reason_required: 'A reason is required to take this contract out of force.',
            transition_reason_placeholder: 'Why is this contract changing status?',
            transition_none_available: 'This status is final. A successor contract is a new record.',
            document_type: 'Document Tipus',
            expiry_date: 'Data de caducitat',
            no_contracts: "No s'han trobat contracts",
            all_document_types: 'Tots Document Types',
            load_error: 'Error en load contracts. Si us plau, torneu-ho a intentar.',
            create_error: 'Error en create contract. Si us plau, torneu-ho a intentar.',
            delete_error: 'Error en delete contract. Si us plau, torneu-ho a intentar.',
            update_error: 'Error en actualitzar estat. Si us plau, torneu-ho a intentar.',
            confirm_delete: 'Eliminar contracte "{title}"?',
            renewal_date: 'Data de renovació',
            effective_date: "Data d'efecte",
            parties: 'Parts',
            summary: 'Resum',
            n_contracts: '{count} contractes',
            n_contract: '{count} contracte',
            contracts_loaded: '{count} contractes carregats',
            doc_type_dpa: 'DPA',
            doc_type_security_addendum: 'Addenda de seguretat',
            doc_type_scc_annex: 'Annex SCC',
            doc_type_sub_processor_list: 'Llista de subprocessadors',
            doc_type_bcr: 'BCR',
            doc_type_nda: 'NDA',
            doc_type_msa: 'MSA',
            doc_type_sla: 'SLA',
            doc_type_other: 'Altre',
            status_draft: 'Esborrany',
            status_under_negotiation: 'En negociació',
            status_pending_signature: 'Pendent de signatura',
            status_active: 'Actiu',
            status_expired: 'Expirat',
            status_terminated: 'Finalitzat',
            status_superseded: 'Substituït',
            contract_file: 'File',
            upload_contract_file: 'Contract File',
            auto_renew: 'Renews automatically',
            auto_renew_hint: 'On the expiry date the contract renews for one more term of the same length, recorded in its history. It does not renew if its effective date or renewal notice period is missing, or if it is not active.',
            renewal_period_days: 'Renewal notice (days)',
            renewal_period_days_hint: 'A renewal reminder task is raised this many days before the expiry date.',
            clauses_heading: 'Clauses',
            clause_title: 'Clause title',
            clause_title_required: 'A clause with text needs a title.',
            clause_content: 'Clause text',
            clause_mandatory: 'Mandatory clause',
            add_clause: 'Add clause',
            remove_clause: 'Remove clause',
            contract_file_save_first: 'Save the contract first. Its file can be attached once the contract exists.',
            expiry_date_keep_hint: 'An expiry date, once set, can be changed but not removed.',
            renewal_history: 'Renewal history',
            renewal_history_note: 'Each row is an automatic renewal the platform made because the contract renews automatically: the expiry date moved by one term.',
            renewal_col_previous_expiry: 'Previous expiry',
            renewal_col_new_expiry: 'New expiry',
            renewal_col_term: 'Term',
            renewal_col_renewed_on: 'Renewed on',
            renewal_term_months: '{count} month(s)',
            renewal_term_days: '{count} day(s)',
            clause_field_label: 'Clause {n}, {field}',
            field_other: 'Another field',
        },
        gates: {
            title: "Portes d'aprovació",
            page_title: 'Avaluacions de la porta',
            new: 'Nou Gate Evaluation',
            vendor_id: 'Proveïdor ID',
            gate_name: 'Nom de la porta',
            outcome: 'Resultat',
            evaluator: 'Avaluador',
            no_gates: "No s'han trobat gate evaluations",
            load_error: 'Error en load gate evaluations. Si us plau, torneu-ho a intentar.',
            evaluate_error: 'Error en evaluate vendor gate. Si us plau, torneu-ho a intentar.',
            detail_error: 'Error en fetch evaluation detail. Si us plau, torneu-ho a intentar.',
            override_error: 'Error en override gate evaluation. Si us plau, torneu-ho a intentar.',
            criteria_results: 'Resultats dels criteris',
            evaluate_form: 'Avaluar la porta del venedor',
            per_page: 'Avaluacions per pàgina',
            passed: 'Aprovat',
            failed: 'Ha fallat',
            override: 'Substitució',
            override_justification: 'Justificació de substitució',
            override_placeholder: 'Introduïu justification for overriding this gate evaluation',
            approval_id: "ID d'aprovació",
            overall_passed: 'Aprovat',
            overall_failed: 'Fallat',
            overall_overridden: 'Anul·lat',
            evaluations_loaded: '{count} avaluacions de porta carregades',
            create_approval_cta: 'Crear aprovació',
        },
        reviews: {
            title: 'Avaluacions de proveïdors',
            page_title: 'Revisions de venedors',
            new: 'Nou revisar',
            vendor_id: 'Proveïdor ID',
            filter_by_outcome: 'Filtreu per resultat de la revisió',
            per_page: 'Ressenyes per pàgina',
            kpi_summary: 'Revisa KPI resum',
            review_outcome_label: 'Revisió del resultat',
            program_unresolved: 'Program {id} (name not available)',
            programs_unavailable: 'Programs could not be listed. Enter the program ID.',
            edit_outcome: 'Edita el resultat de la revisió',
            reviews_loaded: "S'han carregat {count} ressenyes de proveïdors",
            reviewer: 'Revisor',
            review_type: 'Revisar Tipus',
            outcome: 'Resultat',
            findings: 'Troballes',
            no_reviews: "No s'han trobat vendor reviews",
            load_kpis_error: 'Error en fetch vendor review kpis. Si us plau, torneu-ho a intentar.',
            load_error: 'Error en fetch vendor reviews. Si us plau, torneu-ho a intentar.',
            update_error: "No s'ha pogut actualitzar la revisió del proveïdor",
            create_error: 'Error en create vendor review. Si us plau, torneu-ho a intentar.',
            total_reviewed: 'Total Proveïdors Reviewed',
            total_reviews: 'Total de revisions',
            satisfactory: 'Satisfactori',
            concerns_identified: 'Preocupacions identificades',
            remediation_required: 'Remediació requerida',
            escalated: 'Escalat',
            program_id: 'ID del programa',
            period_id: 'Període ID',
            task_id: 'Tasca ID',
            recommendations: 'Recomanacions',
            reviewed_at: 'Reviewed At',
        },
        risk_signals: {
            title: 'Senyals de Risc',
            page_title: 'Senyals de risc',
            new_signal: 'Nou senyal de risc',
            signal_type: 'Tipus de senyal',
            severity: 'Gravetat',
            description: 'Descripció',
            source: 'Font',
            vendor_id: 'ID del venedor',
            certification_expiry: 'Venciment de certificació',
            breach_notification: 'Notificació de violació',
            financial_instability: 'Inestabilitat financera',
            compliance_change: 'Canvi de compliment',
            custom: 'Personalitzat',
            active: 'Actiu',
            acknowledged: 'Reconegut',
            resolved: 'Resolt',
            no_signals: "No s'han trobat senyals de risc",
            source_external_feed: 'Font externa',
            source_manual: 'Manual',
            source_integration: 'Integració',
            source_regulatory: 'Regulador',
            source_media: 'Mitjans',
            trigger_reassessment_prompt: "Aquest senyal és d'alta gravetat. Voleu iniciar una reavaluació del proveïdor?",
            reassessment_triggered: 'Reavaluació iniciada.',
            reassessment_trigger_error: "No s'ha pogut iniciar la reavaluació.",
        },
        reassessments: {
            title: 'Tasques de Reavaluació',
            page_title: 'Tasques de reavaluació',
            trigger: 'Iniciar reavaluació',
            trigger_success: 'Reavaluació iniciada correctament',
            reason: 'Motiu',
            deadline: 'Data límit',
            assigned_to: 'Assignat a',
            vendor_id: 'ID del venedor',
            outcome: 'Resultat',
            new_risk_level: 'Nou nivell de risc',
            deadline_hint: 'The deadline is the end of the chosen day (UTC).',
            reason_required: 'Enter a reason.',
            vendor_required: 'Choose a vendor.',
            already_completed: 'This reassessment has already been completed.',
            complete: 'Completar reavaluació',
            complete_success: 'Reavaluació completada correctament',
            pending: 'Pendent',
            in_progress: 'En progrés',
            completed: 'Completat',
            cancelled: 'Cancel·lat',
            no_tasks: "No s'han trobat tasques de reavaluació",
            risk_trend: 'Tendència de risc',
            notes: 'Notes',
        },
        portfolio: {
            title: 'Cartera de Riscos',
            page_title: 'Cartera de riscos',
            dashboard: 'Panell de la cartera',
            vendor_id: 'ID del venedor',
            total_vendors: 'Total de proveïdors',
            average_score: 'Puntuació mitjana de risc',
            high_risk_count: 'Proveïdors de risc alt',
            trend_direction: 'Direcció de la tendència',
            improved: 'Millorat',
            stable: 'Estable',
            degraded: 'Degradat',
            vendor_trend: 'Tendència de proveïdors',
            outstanding_questionnaires: 'Qüestionaris pendents',
            overdue_assessments: 'Avaluacions vençudes',
            no_outstanding: 'Cap qüestionari pendent',
            no_overdue: 'Cap avaluació vençuda',
            due_date: 'Data de venciment',
        },
        risk_acceptance: {
            expiring_within: 'Expiring within {days} days',
            title: "Registre d'Acceptació de Riscos",
            page_title: "Registre d'acceptació de riscos",
            new_acceptance: 'Nova acceptació de risc',
            vendor_id: 'ID del venedor',
            risk_level: 'Nivell de risc',
            conditions: 'Condicions',
            justification: 'Justificació',
            expiry_date: 'Data de venciment',
            create_success: 'Acceptació de risc creada correctament',
            review_condition: 'Revisar condició',
            condition_met: 'Condició complerta',
            condition_not_met: 'Condició no complerta',
            review_success: 'Condició revisada correctament',
            export_register: 'Exportar registre',
            active: 'Actiu',
            expired: 'Vençut',
            revoked: 'Revocat',
            no_acceptances: "No s'han trobat acceptacions de risc",
            met_count: '{met}/{total} es van reunir',
        },
        review_routing: {
            title: 'Encaminament de Revisions',
            page_title: "Revisa l'encaminament",
            route_to_reviewers: 'Enviar als revisors',
            route_success: 'Enviat als revisors correctament',
            vendor_id: 'ID del venedor',
            reviewer_id: 'ID del revisor',
            reviewer_role: 'Rol del revisor',
            questionnaire_id: 'ID del qüestionari',
            review_status: 'Estat de la revisió',
            reviewers_label: 'Revisors (user_id, funció per línia)',
            pending: 'Pendent',
            in_review: 'En revisió',
            completed: 'Completat',
            no_routing: "No s'ha trobat encaminament de revisions",
            primary_reviewer: 'Revisor principal',
            secondary_reviewer: 'Revisor secundari',
            approver: 'Aprovador',
        },
        onboarding: {
            title: "Casos d'Incorporació de Proveïdors",
            page_title: "Casos d'Incorporació de Proveïdors",
            new_case: "Nou cas d'incorporació",
            case_detail: "Cas d'Incorporació",
            open_mps_dossier_filing: 'Obrir presentació MPS',
            dossier_filing_description: "Crea una presentació MPS pre-omplida a partir d'aquest cas d'onboarding. La presentació es vincularà al cas.",
            dossier_filing_dpia_id: 'ID DPIA',
            dossier_filing_dpia_id_placeholder: 'p.ex. dpia_01TEST000000000000000001',
            dossier_filing_title: 'Títol de la presentació',
            dossier_filing_form_type: 'Tipus de formulari',
            dossier_filing_submit: 'Crear presentació',
            dossier_filing_validation_error: "Cal l'ID DPIA i el títol de la presentació.",
            dossier_filing_created: 'Presentació MPS creada',
            dossier_filing_error: 'Error en crear la presentació MPS.',
            linked_questionnaire_due_date: 'Data límit',
            linked_questionnaire_assignee: 'Assignat a',
            linked_questionnaire_submitted_at: 'Enviat el',
            score_last_scored: 'Darrera puntuació',
            score_dimensions: 'Dimensions',
            linked_contract_document_type: 'Tipus de document',
            linked_contract_effective_date: "Data d'entrada en vigor",
            linked_contract_expiry_date: 'Data de caducitat',
            linked_sub_processor_countries: 'Països de tractament',
            linked_sub_processor_last_assessment: 'Darrera avaluació',
            linked_sub_processor_next_assessment: 'Propera avaluació',
            case_id: 'ID del cas',
            vendor: 'Proveïdor',
            case_type: 'Tipus de cas',
            service_description: 'Descripció del servei',
            data_categories: 'Categories de dades',
            systems_accessed: 'Sistemes accedits',
            jurisdiction: 'Jurisdicció',
            hosting_model: "Model d'allotjament",
            controller_role: 'Rol de controlador/processador',
            status_pipeline: "Pipeline d'estat",
            tabs_label: "Seccions del cas d'incorporació",
            per_page: 'Casos per pàgina',
            filter_cases: "Filtrar casos d'incorporació",
            cases_count: 'casos',
            cases_loaded: "casos d'incorporació carregats",
            no_cases: "No s'han trobat casos d'incorporació.",
            vendor_lookup_placeholder: 'Introduïu ID del proveïdor',
            data_categories_placeholder: 'p.ex., dades_personals, financeres, salut',
            systems_placeholder: 'p.ex., CRM, ERP, Sistema HR',
            status_open: 'Obert',
            status_in_progress: 'En curs',
            status_awaiting_approval: "En espera d'aprovació",
            status_approved: 'Aprovat',
            status_remediation_required: 'Remediació requerida',
            status_closed: 'Tancat',
            type_new_vendor: 'Nou proveïdor',
            type_service_change: 'Canvi de servei',
            type_renewal: 'Renovació',
            type_reassessment: 'Reavaluació',
            tab_overview: 'Visió general',
            tab_due_diligence: 'Diligència deguda',
            tab_security_assessment: 'Avaluació de seguretat',
            tab_contractual_controls: 'Controls contractuals',
            tab_sub_processors: 'Subprocessadors',
            tab_approval_gates: "Portes d'aprovació",
            tab_evidence: 'Evidència',
            tab_placeholder: "El contingut d'aquesta secció es completarà durant el procés d'incorporació.",
            completion_not_started: 'No iniciat',
            completion_in_progress: 'En curs',
            completion_completed: 'Completat',
            completion_requiring_attention: 'Requereix atenció',
            gate_name: 'Nom de la porta',
            approver: 'Aprovador',
            decision: 'Decisió',
            comments: 'Comentaris',
            decide: 'Prendre decisió',
            decision_approved: 'Aprovat',
            decision_rejected: 'Rebutjat',
            decision_deferred: 'Diferit',
            decision_pending: 'Pending',
            no_approvals: "No hi ha portes d'aprovació configurades per a aquest cas.",
            approval_success: "Decisió d'aprovació registrada correctament",
            approval_error: "No s'ha pogut registrar la decisió d'aprovació",
            transition_status: "Transitar l'estat",
            confirm_transition: 'Transitar el cas de "{from}" a "{to}"?',
            transition_success: "L'estat del cas s'ha transitat correctament",
            transition_error: "No s'ha pogut transitar l'estat del cas",
            create_success: "Cas d'incorporació creat correctament",
            create_error: "No s'ha pogut crear el cas d'incorporació",
            load_error: "No s'han pogut carregar els casos d'incorporació",
            detail_error: "No s'han pogut carregar els detalls del cas",
            export: 'Exportar',
            export_error: "No s'ha pogut exportar el cas d'incorporació",
            evidence_tab_description: "Puja i exporta evidències per al cas d'incorporació.",
            upload_evidence_label: 'Puja evidència',
            attached_evidence_heading: 'Evidències adjuntes',
            no_evidence_attached: 'Encara no hi ha evidències adjuntes.',
            export_bundle_button: "Exporta paquet d'evidències",
            load_attachments_error: "No s'han pogut carregar les evidències.",
            linked_questionnaires_heading: 'Qüestionaris vinculats',
            no_linked_questionnaires: 'Sense qüestionaris vinculats.',
            linked_scores_heading: 'Puntuacions vinculades',
            no_linked_scores: 'Sense puntuacions vinculades.',
            latest_score_label: 'Darrera puntuació',
            linked_contracts_heading: 'Contractes vinculats',
            no_linked_contracts: 'Sense contractes vinculats.',
            linked_sub_processors_heading: 'Subprocessadors vinculats',
            no_linked_sub_processors: 'Sense subprocessadors vinculats.',
            approval_created: 'Approval created',
            create_approval: 'Create Approval',
            export_bundle_success: 'Evidence bundle generated',
            gate_type: 'Gate Type',
            no_recent_bundles: 'No evidence bundles generated yet.',
            recent_bundles: 'Recent Bundles',
            tab_completed: 'Section marked complete',
            tab_gate_not_met: 'This section is already marked complete.',
            tab_linked_records_missing: 'This section cannot be marked complete yet: it needs at least one {kind}.',
            linked_kind_questionnaire: 'due-diligence questionnaire for this vendor that has not expired',
            linked_kind_risk_score: 'risk score for this vendor',
            linked_kind_contract: 'active contract with this vendor that is in force today',
            linked_kind_sub_processor: 'sub-processor entry recorded for this case',
            decide_again: 'Decide again',
            resolve_rejection: 'Resolve rejection',
            resolution_notes: 'Documented resolution',
            resolution_notes_hint: 'Required. Say how the reason for the rejection was addressed. The rejection stays in the gate history and the gate returns to undecided, to be decided again.',
            resolution_notes_required: 'Enter the resolution before reopening the gate.',
            resolve_success: 'Rejection resolved. The gate can be decided again.',
            resolve_error: 'The rejection could not be resolved.',
            prior_gate_blocked: 'An earlier gate is rejected or deferred. Decide it again, or resolve its rejection, first.',
            case_gate_blocked: 'The case cannot be approved while a gate is rejected or deferred.',
            case_gate_undecided: 'The case cannot be approved while a gate is still undecided. Decide every gate first.',
            case_final_gate_missing: 'The case cannot be approved until its final gate has been created and approved.',
            case_status_changed: 'The case or its gates changed while this was being saved. The case has been reloaded; check it and try again.',
            gate_already_decided: 'This gate has already been decided. Reload to see the decision.',
            deferred_gate_hint: 'Deferred: this gate and every gate after it wait until it is decided again.',
            mark_complete: 'Marcar com a completat',
            earlier_decisions: 'Earlier decisions on this gate',
            history_entry: '{decision} by {name} on {date}.',
            history_notes: 'Notes: {notes}.',
            history_conditions: 'Conditions: {conditions}.',
            history_redecided: 'Decided again on {date}.',
            history_resolved: 'Rejection resolved by {name} on {date}: {notes}',
            history_unknown_person: 'a person not recorded',
            history_unknown_date: 'a date not recorded',
        },
        sub_processors: {
            appointment_decision: 'Decision to appoint anyway',
            appointment_decision_required: 'The DPA chain is not resolved. Record who is accepting that, and why.',
            appointment_decision_placeholder: 'Why is this sub-processor being appointed before the chain is in place?',
            title: 'Registre de Subprocessadors',
            page_title: 'Registre de Subprocessadors',
            new_record: 'Nou subprocessador',
            edit_record: 'Editar subprocessador',
            onboarding_case: "Cas d'incorporació",
            services: 'Serveis proporcionats',
            data_categories: 'Categories de dades',
            processing_countries: 'Països de processament',
            dpa_chain_status: 'Estat de la cadena DPA',
            certifications: 'Certificacions',
            last_assessment: 'Última avaluació',
            next_assessment: 'Pròxima avaluació',
            change_history: 'Historial de canvis',
            history: 'Historial',
            changed_by: 'Canviat per',
            comma_separated: 'Valors separats per comes',
            filter_records: 'Filtrar registres de subprocessadors',
            filter_by_dpa: 'Filtrar per estat de cadena DPA',
            all_dpa_statuses: 'Tots els estats DPA',
            per_page: 'Registres per pàgina',
            records_count: 'subprocessadors',
            records_loaded: 'registres de subprocessadors carregats',
            no_records: "No s'han trobat registres de subprocessadors.",
            no_history: 'No hi ha historial de canvis disponible.',
            dpa_active: 'Actiu',
            dpa_pending: 'Pendent',
            dpa_expired: 'Expirat',
            dpa_not_required: 'No requerit',
            create_success: 'Subprocessador creat correctament',
            create_error: "No s'ha pogut crear el subprocessador",
            update_success: 'Subprocessador actualitzat correctament',
            update_error: "No s'ha pogut actualitzar el subprocessador",
            load_error: "No s'ha pogut carregar el registre de subprocessadors",
            history_error: "No s'ha pogut carregar l'historial de canvis",
            export_csv: 'Exportar CSV',
            export_json: 'Exportar JSON',
            export_error: "No s'han pogut exportar les dades del subprocessador",
        },
    },
    deletion_orchestration: {
        nav: {
            dashboard: "Tauler d'orquestració",
            runs: "Execucions d'orquestració",
            tasks: "Tasques d'execució",
            templates: 'Plantilles de llista de verificació',
            vendor_requests: 'Sol·licituds de proveïdors',
            proof_chain: 'Cadena de proves',
            dsr_integration: 'Integració DSR',
            vendor_verification: 'Verificació de proveïdors',
            completeness: 'Tauler de completesa',
        },
        dashboard: {
            title: "Tauler d'orquestració",
            loading: 'Carregant dashboard data...',
            active_runs: 'Execucions actives',
            completion_rate: 'Taxa de finalització',
            avg_completion_time: 'Temps mitjà de finalització',
            overdue_tasks: 'Vençut Tasques',
            vendor_compliance: 'Proveïdor Compliment',
            vendor_compliance_none: 'No vendor request sent in the last 90 days',
            recent_runs: 'Execucions recents',
            no_recent_runs: "Sense execucions d'orquestració recents.",
            run_id: 'Executar ID',
            ticket_id: 'ID del tiquet',
            progress: 'Progrés',
            systems: 'Sistemes',
            vendors: 'Proveïdors',
            load_error: "No s'ha pogut carregar el tauler d'orquestració. Si us plau, torna-ho a provar.",
            aria_table: "Últimes sessions d'orquestració",
        },
        runs: {
            title: "Execucions d'orquestració",
            new: 'Nou executar',
            run_id: 'Executar ID',
            ticket_id: 'ID del tiquet',
            progress: 'Progrés',
            systems: 'Sistemes',
            vendors: 'Proveïdors',
            data_subject_ref: 'Dades Titular Ref',
            data_categories: 'Categories de dades',
            enable_proof_chain: 'Activar cadena de prova',
            start: 'Iniciar',
            started: 'Va començar la carrera',
            mark_ready: 'Marca llest',
            marked_ready: 'Executar marcat com a llest',
            complete: 'Completa',
            complete_exceptions: 'Completa amb excepcions',
            pause: 'Pausa',
            resume: 'Currículum',
            cancel: 'Cancel·la',
            no_runs: "No s'han trobat orchestration runs",
            load_error: "No s'han pogut carregar les execucions d'orquestració. Si us plau, torna-ho a provar.",
            create_error: "No s'ha pogut crear l'execució d'orquestració",
            aria_filter_status: "Filtra per estat d'execució",
            aria_table: "Corres d'orquestració",
            placeholder_ticket_id: 'p. ex., TKT-000123',
            placeholder_data_subject_ref: 'p. ex., DS-REF-001',
            placeholder_data_categories: 'p. ex., personal_data, financer, de salut',
            cancel_confirm_title: 'Cancel this run?',
            cancel_confirm_body: 'Run {run} will be cancelled. A cancelled run cannot be started or resumed again.',
            cancel_reason_label: 'Reason (optional)',
            cancel_reason_hint: 'Recorded with the cancellation.',
            cancel_keep: 'Keep run',
            cancel_confirm: 'Cancel run',
            cancelling: 'Cancelling…',
            cancelled_success: 'Run cancelled',
            cancel_error: 'The run could not be cancelled.',
            title_label: 'Title',
            run_type: 'Run Type',
            run_type_labels: {
                data_subject_request: 'Data Subject Request',
                retention_expiry: 'Retention Expiry',
                manual: 'Manual',
                scheduled: 'Scheduled',
            },
            field_guidelines: {
                title: 'A descriptive name for this orchestration run.',
                run_type: 'The origin or trigger for the deletion process.',
                ticket_id: 'The associated ticket ID from your DSR or support system.',
                data_subject_ref: 'A unique identifier for the data subject (e.g., internal ID, email hash).',
                data_categories: 'Specific categories of personal data to be deleted (comma-separated).',
                proof_enabled: 'Maintains an immutable cryptographic chain of evidence for this run.',
            },
            actions: 'Actions',
            progress_guideline: 'Calculated based on completed system tasks and vendor requests.',
            placeholder_title: 'e.g., Q2 2026 Customer Deletion Batch',
            refusal_run_executing: 'This run is executing and cannot be cancelled. Pause it first, then cancel it.',
            refusal_invalid_status_transition: 'The run is no longer in a status that allows this change. The list has been refreshed.',
            refusal_run_status_changed: 'The run changed status while this was being saved. The list has been refreshed; check the run and try again.',
            refusal_cancel_permission_required: 'Cancelling a deletion run needs the permission to cancel deletion runs, which you do not hold.',
        },
        tasks: {
            title: "Tasques d'execució",
            task_id: 'Tasca ID',
            run_id: 'Executar ID',
            system_name: 'Sistema Nom',
            assigned_to: 'Assignat A',
            sla_deadline: 'SLA Termini',
            progress: 'Progrés',
            enter_run_id: 'Introduïu Run ID to filter...',
            select_run: 'Seleccioneu una execució per veure les tasques',
            unassigned: 'Sense assignar',
            overdue: 'Vençut',
            assign_task: 'Assignar Tasca',
            assignee_id: 'Assignat ID',
            update_status: 'Actualitzar Estat',
            new_status: 'Nou estat',
            no_transitions: 'Aquesta tasca està en un estat final i no es pot canviar.',
            select_status_placeholder: 'Seleccioneu un estat nou',
            skip_justification: 'Justificació de l’omissió',
            failure_reason: 'Motiu de l’error',
            no_tasks: "No s'han trobat execution tasks",
            new_task: 'Nova tasca',
            system_id: 'ID del sistema',
            data_classification: 'Classificació de dades',
            load_error: 'Error en carregar execution tasks. Torneu-ho a provar.',
            assign_error: 'Error en assign task',
            status_error: 'Error en actualitzar estat de la tasca',
            no_tasks_for_run: 'Aquesta execució d’eliminació encara no té cap tasca d’execució.',
            aria_filter_run_id: "Filtreu per identificador d'execució",
            aria_filter_status: 'Filtra per estat de la tasca',
            aria_table: "Tasques d'execució",
            view_steps: 'Veure els passos',
            steps_modal_title: 'Passos d’execució',
            step_title: 'Pas',
            step_status: 'Estat del pas',
            step_notes: 'Notes',
            step_evidence: 'Fitxers de prova',
            step_evidence_required: 'Es requereix prova per a aquest pas',
            step_evidence_missing: 'Encara no s’ha pujat cap prova',
            step_save: 'Desa el pas',
            step_saved: 'Pas actualitzat',
            step_save_error: 'No s’ha pogut actualitzar el pas',
            create_success: 'Tasca d’execució creada',
            create_error: 'No s’ha pogut crear la tasca d’execució',
            step_upload_label: 'Adjunta prova',
            // fix-sprint #431 (gate E12.5 remediation 1) — separation of duties on verification.
            verify_own_task: 'You are assigned to this task, so another person verifies it.',
            verify_separation_of_duties: 'You cannot verify a task you are assigned to: another person must verify it.',
        },
        templates: {
            title: 'Plantilles de llista de verificació',
            new: 'Nou plantilla',
            template_id: 'Plantilla ID',
            system_type: 'Sistema Tipus',
            est_duration: 'Durada est.',
            steps: 'Passos',
            action_type: 'Acció Tipus',
            instructions: 'Instruccions',
            mandatory: 'Obligatori',
            add_step: 'Afegir Step',
            step_n: 'Pas',
            no_templates: "No s'han trobat checklist templates",
            all_system_types: 'Tots Sistema Types',
            load_error: "No s'han pogut carregar les plantilles de llista de comprovació. Si us plau, torna-ho a provar.",
            create_error: "No s'ha pogut crear la plantilla de llista de comprovació",
            aria_filter_system_type: 'Filtra per tipus de sistema',
            aria_filter_status: 'Filtra per estat de la plantilla',
            aria_table: 'Plantilles de llista de verificació',
            placeholder_name: "p. ex., Llista de verificació d'eliminació de bases de dades",
            guideline_name: 'Use a clear operating name, for example “Database Deletion Checklist”.',
            guideline_system_type: 'Choose the system category this checklist will usually run against.',
            guideline_status: 'Draft templates can be reviewed before activation; active templates can be reused in runs.',
            guideline_description: 'Summarise scope, retention context, and when teams should select this template.',
            update_success: 'Plantilla actualitzada correctament',
            create_success_msg: 'Plantilla creada correctament',
            update_error: 'Error en actualitzar la plantilla',
            activate_success: 'Plantilla activada correctament',
            activate_error: 'Error en activar la plantilla',
            archive_success: 'Plantilla arxivada correctament',
            archive_error: 'Error en arxivar la plantilla',
            clone_success: 'Plantilla clonada correctament',
            clone_error: 'Error en clonar la plantilla',
        },
        vendor_requests: {
            title: 'Proveïdor Eliminació Sol·licituds',
            new: 'Nou sol·licitud',
            run: 'Orchestration Run',
            no_run: 'Not part of a run',
            run_hint: 'Optional. A request recorded against a run counts toward that run’s progress.',
            request_id: 'Sol·licitud ID',
            vendor_name: 'Proveïdor Nom',
            vendor_id: 'Proveïdor ID',
            data_subject_ref: 'Dades Titular Ref',
            data_categories: 'Categories de dades',
            legal_basis: 'Base jurídica',
            sla_days: 'Dies SLA',
            requested_completion: 'Finalització sol·licitada',
            communications: 'Comunicacions',
            total_requests: 'Total Sol·licituds',
            no_requests: "No s'han trobat vendor deletion requests",
            filter_vendor_id: 'Filtrar per Proveïdor ID...',
            change_status: 'Canviar Estat',
            new_status: 'Nou Estat',
            actions: 'Accions',
            load_error: "No s'han pogut carregar les sol·licituds d'eliminació del proveïdor. Si us plau, torna-ho a provar.",
            create_error: "No s'ha pogut crear la sol·licitud d'eliminació del proveïdor",
            dashboard_error: "No s'ha pogut carregar el tauler de supressió del proveïdor. Si us plau, torna-ho a provar.",
            aria_filter_status: 'Filtra per estat de la sol·licitud',
            aria_filter_vendor_id: 'Filtra per ID de proveïdor',
            aria_table: 'Sol·licituds de supressió de proveïdors',
            placeholder_vendor_id: 'p. ex., VND-001',
            placeholder_vendor_name: 'per exemple, Acme Analytics',
            placeholder_data_subject_ref: 'p. ex., DS-REF-001',
            placeholder_data_categories: 'p. ex., personal_data, analítiques',
            placeholder_legal_basis: 'p. ex., PDPL article 19',
            // fix-sprint #431 (gate E12.5 remediation 1) — record the vendor’s confirmation; a failed runs read.
            runs_read_error: 'The list of orchestration runs could not be read, so no run can be chosen. Retry, or record the request without a run.',
            confirm_action: 'Record confirmation',
            confirm_title: 'Record the vendor’s confirmation',
            confirm_intro: 'Record that {vendor} confirmed the deletion for request {request}. Recording it completes the request.',
            confirm_date: 'Date confirmed',
            confirm_method: 'How the vendor confirmed',
            confirm_method_choose: 'Choose how…',
            confirm_methods: {
                email: 'Email',
                portal: 'Vendor portal',
                letter: 'Letter',
                phone: 'Phone',
                other: 'Other',
            },
            confirm_contact: 'Vendor contact person',
            confirm_systems: 'Systems affected',
            confirm_systems_hint: 'Optional. Separate systems with commas.',
            confirm_notes: 'Notes',
            confirm_submit: 'Record confirmation',
            confirm_success: 'Vendor confirmation recorded; the request is completed.',
            confirm_refused_status: 'This request cannot receive a confirmation: it is a draft, cancelled or already completed.',
            confirm_missing_fields: 'Enter the date confirmed, how the vendor confirmed and the vendor’s contact person.',
            confirm_not_found: 'This request no longer exists.',
            confirm_error: 'The confirmation could not be recorded.',
            confirm_changed: 'Nothing was recorded: the request changed on the server while this confirmation was being saved. The list behind this dialog is read again; check the request there, and record the confirmation again if it still needs one.',
        },
        proof_chain: {
            title: 'Visualitzador de cadena de prova',
            enter_run_id: 'Introduïu executar id...',
            load_chain: 'Carregar cadena',
            verify_chain: 'Verificar cadena',
            verifying: 'Verificant...',
            chain_valid: 'La cadena és vàlida',
            chain_invalid: 'La cadena està trencada',
            chain_length: 'Longitud de cadena',
            broken_at_index: "Trencat a l'índex",
            no_proof_records: "No s'han trobat registres de prova per a aquesta execució.",
            proof_id: 'ID de prova',
            entity_type: "Tipus d'entitat",
            entity_id: "ID de l'entitat",
            event_hash: "Hash de l'esdeveniment",
            previous_hash: 'Hash anterior',
            attestation: 'Atestació',
            attestation_actor: 'Realitzat per',
            attestation_signed_at: 'Signat el',
            attestation_signature: 'Signatura',
            attestation_evidence_refs: 'Referències de proves',
            run_field_label: "Execució d'eliminació",
            select_run: "Seleccioneu una execució d'eliminació…",
            aria_select_run: "Execució d'eliminació per a la cerca de la cadena de proves",
            chain_broken_here: 'Cadena trencada aquí',
            load_error: "No s'ha pogut carregar la cadena de prova. Si us plau, torna-ho a provar.",
            verify_error: "No s'ha pogut verificar la cadena de proves. Si us plau, torna-ho a provar.",
            aria_run_id: "Executeu l'identificador per a la cerca de la cadena de proves",
            // Sprint 62.35.0 — a displayed value the event hash does not cover.
            not_covered_by_hash: 'not covered by the hash',
            // Sprint 62.35.0 (AC8) — English until Phase 25 (R-57).
            destruction: {
                action: 'Record destruction',
                modal_title: 'Record destruction',
                intro: 'Identify the data this run destroyed. The record is added to the proof chain and covered by its hash, so it cannot be changed once saved.',
                section_title: 'Destruction record',
                name: 'Name',
                type: 'Type',
                size: 'Size (bytes)',
                format: 'Format',
                locations: 'Locations',
                locations_hint: 'One location per line. List every copy, including backups, replicas and exports.',
                identifier: 'Identifier',
                hash: 'Hash',
                hash_algorithm: 'Hash algorithm',
                hash_algorithm_hint: 'Required when a hash is given, for example SHA-256.',
                lawful_data_unaffected: 'Lawful data left unaffected',
                lawful_yes: 'Yes, lawful data was left unaffected',
                lawful_no: 'No, lawful data was affected',
                lawful_not_recorded: 'Not recorded',
                lawful_data_note: 'Note on lawful data',
                lawful_data_note_hint: 'Required when lawful data was affected.',
                yes: 'Yes',
                no: 'No',
                not_recorded: 'Not recorded',
                submit: 'Record destruction',
                saving: 'Recording…',
                saved: 'Destruction record added to the proof chain.',
                save_error: 'The destruction record could not be saved.',
                err_required: 'This field is required.',
                err_locations: 'Enter at least one location.',
                err_size: 'Enter a number of bytes, zero or more.',
                err_hash_algorithm: 'Enter the algorithm used to compute this hash.',
                err_algorithm_without_hash: 'Remove the algorithm, or enter the hash it was used for.',
                err_lawful_note: 'Explain how lawful data was affected.',
            },
        },
        dsr_integration: {
            change_status: 'Canvia l’estat',
            request_type: 'Tipus de sol·licitud',
            data_subject_hint: "Identificador de l'interessat les dades del qual s'esborraran (p. ex., correu o ID de client). Normalment coincideix amb el sol·licitant del cas DSR seleccionat.",
            title: "Integració d'eliminació DSR",
            create_run: "Crear execució d'eliminació DSR",
            dsr_case_id: 'ID del cas DSR',
            data_subject_id: "ID de l'interessat",
            systems: 'Sistemes objectiu',
            route_to_department: 'Enviar al departament',
            confirm_deletion: 'Confirmar eliminació',
            no_runs: "No s'han trobat execucions d'eliminació DSR",
            status: "Estat d'eliminació",
            deadline: 'Data límit',
            no_further_status_changes: 'No further status changes',
            refusal_run_executing: 'This deletion run is in progress and cannot be cancelled. Complete it instead, with or without exceptions.',
        },
        vendor_verification: {
            title: "Verificació d'eliminació de proveïdors",
            create_followup: 'Crear seguiment',
            deadline: 'Termini de confirmació',
            escalation_contact: "Contacte d'escalada",
            escalate: 'Escalar',
            confirm_escalate_followup: 'Escalate this follow-up? Its status becomes Escalated. Nothing is sent to the vendor or the escalation contact.',
            no_followups: "No s'han trobat seguiments de proveïdors",
            vendor_request: 'Sol·licitud del venedor',
            vendor: 'Proveïdor',
            unknown_vendor: 'Proveïdor desconegut',
            overdue_badge: 'Vençut',
            mark_confirmed: 'Marcar com a confirmat',
            mark_confirmed_for: "Marcar l'eliminació com a confirmada per a {vendor}",
            confirm_mark_followup_confirmed: 'Mark this follow-up confirmed? The vendor deletion request is not changed; record the vendor’s confirmation on Vendor Requests as well.',
            confirmation_success: 'Eliminació de proveïdor confirmada',
            confirmation_failed: "No s'ha pogut confirmar l'eliminació del proveïdor",
            select_request: 'Seleccioneu una sol·licitud del venedor…',
            escalation_contact_placeholder: 'p. ex. dpo@vendor.example',
            create_success: 'Seguiment del proveïdor creat correctament',
            create_failed: 'No s’ha pogut crear el seguiment del proveïdor',
            deadline_help: 'The date by which you expect the vendor to confirm. Every follow-up is listed; a pending one past this date is marked Overdue.',
            escalation_success: 'L’eliminació del proveïdor s’ha escalat correctament',
            escalation_failed: 'No s’ha pogut escalar l’eliminació del proveïdor',
            overdue_filter: 'Overdue (pending, deadline passed)',
            status_labels: {
                pending: 'Pending',
                confirmed: 'Confirmed',
                escalated: 'Escalated',
            },
        },
        completeness: {
            title: "Completesa d'eliminació",
            check_conflicts: 'Comprovar conflictes de retenció',
            no_conflicts: "No s'han trobat conflictes de retenció",
            conflict_resolution: 'Resolució de conflictes',
            extend_retention: 'Ampliar retenció',
            proceed_deletion: "Procedir amb l'eliminació",
            escalate_conflict: 'Escalar',
            resolve: 'Resoldre conflicte',
            data_subject_id: 'ID del subjecte de dades',
            placeholder_data_subject_id: "Introduïu l'identificador del subjecte de dades",
            data_subject_hint: "Identificador de la persona de qui esteu verificant l'eliminació (p. ex., correu, ID de client o referència de DSR). Trieu un subjecte conegut o escriviu-ne un.",
            guidance: "Introduïu o seleccioneu un ID d'interessat, opcionalment trieu sistemes i executeu la comprovació per veure la integritat de l'eliminació i els conflictes de retenció.",
            completion: "Finalització de l'eliminació",
            pending_items: 'Elements pendents',
            system: 'Sistema',
            policy: 'Política',
            retention_end: 'Final de retenció',
            completed_items: 'Elements Completats',
            last_review_label: 'Última revisió',
            days_ago: 'fa dies',
            review_overdue: "S'ha superat el cicle de revisió. Cal tornar a revisar.",
            conflicts_not_checked: 'Retention conflicts were not checked because no system is selected. Choose at least one system and run the check again to see them.',
        },
        run_status_labels: {
            pending: 'Pendent',
            planning: 'Planificació',
            ready: 'Preparat',
            in_progress: 'En Progrés',
            paused: 'En Pausa',
            completed: 'Completat',
            completed_with_exceptions: 'Completat amb Excepcions',
            cancelled: 'Cancel·lat',
        },
        task_status_labels: {
            pending: 'Pendent',
            assigned: 'Assignat',
            in_progress: 'En Progrés',
            awaiting_verification: 'Pendent de Verificació',
            verified: 'Verificat',
            failed: 'Fallat',
            skipped: 'Omès',
        },
        system_type_labels: {
            database: 'Base de Dades',
            saas_application: 'Aplicació SaaS',
            file_storage: 'Emmagatzematge de Fitxers',
            email_system: 'Sistema de Correu',
            analytics_platform: 'Plataforma Analítica',
            backup_system: 'Sistema de Còpies',
            vendor_managed: 'Gestionat pel Proveïdor',
            custom: 'Personalitzat',
        },
        template_status_labels: {
            draft: 'Esborrany',
            active: 'Actiu',
        },
        action_type_labels: {
            manual_check: 'Verificació Manual',
            api_call: 'Crida API',
            sql_query: 'Consulta SQL',
            script_execution: 'Execució de Script',
            file_deletion: 'Eliminació de Fitxer',
            verification: 'Verificació',
            notification: 'Notificació',
            approval: 'Aprovació',
        },
        request_status_labels: {
            draft: 'Esborrany',
            sent: 'Enviat',
            acknowledged: 'Confirmat',
            in_progress: 'En Progrés',
            completed: 'Completat',
            overdue: 'Vençut',
            escalated: 'Escalat',
            disputed: 'Disputat',
            cancelled: 'Cancelled',
        },
        event_type_labels: {
            initiation: 'Iniciació',
            data_deletion: 'Eliminació de Dades',
            completion: 'Finalització',
            verification: 'Verificació',
            rollback: 'Reversió',
        },
        count_labels: {
            one_template: '1 plantilla',
            n_templates: '{n} plantilles',
            one_run: '1 execució',
            n_runs: '{n} execucions',
            one_task: '1 tasca',
            n_tasks: '{n} tasques',
            one_request: '1 sol·licitud',
            n_requests: '{n} sol·licituds',
        },
        aria: {
            completion_rate: 'Taxa de finalització: {pct}%',
            vendor_compliance: 'Compliment del proveïdor: {pct}%',
            run_progress: 'Progrés: {pct}%',
            run_progress_systems: 'Progrés: {completed} de {total} sistemes processats',
            chain_broken: 'Cadena trencada en aquest registre',
            chain_valid: 'Cadena vàlida en aquest registre',
            chain_unknown: 'Estat de la cadena desconegut',
            escalate_vendor: 'Escalar {id}',
            resolve_conflict: 'Resoldre conflicte per {system}',
        },
        regulatory: {
            annual_review_cycle_info: "La totalitat de la supressió s'ha de revisar com a mínim cada 365 dies. Els registres que superen aquest llindar es marquen per a una revisió obligatòria per mantenir el compliment de la normativa.",
            runs_data_d1_lifecycle_info: "Els estats d'execució de l'orquestració s'assignen al cicle de vida DATA-D1: planificació = catàleg, llest = classificació, in_progress = aplicar regles, completat = evidència generada, completed_with_exceptions = revisió necessària. Cada transició es registra a la cadena de proves.",
            vendor_record_linkage_info: "Cada sol·licitud d'eliminació de proveïdor s'enllaça amb el seu registre de proveïdor mitjançant vendor_id. La confirmació de finalització requereix proves criptogràfiques de la cadena de proves abans que la sol·licitud es pugui marcar com a completada.",
            templates_internal_classification_info: "Totes les plantilles de llista de verificació d'eliminació es classifiquen com a documents organitzatius interns. No porten identificacions oficials de formulari regulador i no s'han de presentar com a expedients reguladors.",
            proof_chain_evidence_info: "Cada registre de prova recull l'assumpte/categoria de dades, la marca de temps, l'actor, la base legal i un hash SHA-256. En conjunt, aquests camps produeixen proves d'eliminació criptogràfica que compleixen els requisits d'auditoria reglamentària.",
            retention_data_law_info: "Els horaris de retenció d'aquest tauler es deriven de la classificació de la Llei de dades assignada a cada categoria de dades. Quan canvia una classificació, les regles de retenció enllaçades s'actualitzen automàticament per garantir l'alineació normativa.",
            dsr_erasure_route_info: "Les execucions d'esborrat de DSR estan vinculades al cas de sol·licitud de drets d'origen. El termini reglamentari (normalment 30 dies segons l'GDPR Art. 17) es fa un seguiment automàtic. Els casos vençuts es marquen per a l'escalada.",
        },
    },
    discovery: {
        nav: {
            dashboard: 'Tauler de descobriment',
            scans: 'Escaneigs de descobriment',
            staging_area: 'Àrea de preparació',
            dedup_rules: 'Regles de deduplicació',
            classification_rules: 'Regles de classificació',
            suggestions: 'Cua de suggeriments',
            analytics: 'Anàlisi de suggeriments',
            import_templates: "Plantilles d'importació",
            connectors: 'Gestió de connectors',
            discovery_cycles: 'Cicles de descobriment',
            owner_review: 'Revisió del propietari',
            classification_lineage: 'Historial de classificació',
        },
        dashboard: {
            title: 'Tauler de descobriment',
            loading: 'Carregant dashboard data...',
            total_scans: "Total d'escaneigs",
            active_scans: 'Escaneigs actius',
            completed_30d: 'Completat (30d)',
            failed_30d: 'Ha fallat (30d)',
            systems_discovered: 'Sistemes descoberts',
            pending_staged_records: 'Pendent Staged Registres',
            recent_scans: 'Escaneigs recents',
            no_recent_scans: 'Sense escaneigs recents.',
            scan_id: 'Escanejar ID',
            discovered: 'Descobert',
            view_all_scans: 'Veure All Scans',
            staging_area: 'Àrea de staging',
            suggestion_queue: 'Cua de suggeriments',
            records_reviewed: 'Registres revisats',
            records_approved: 'Registres aprovats',
            exceptions_pending: 'Excepcions pendents',
            view_as_chart: 'Mostra com a gràfic',
            view_as_table: 'Mostra com a taula',
        },
        scans: {
            title: 'Escaneigs de descobriment',
            new: 'Nou escanejar',
            scan_id: 'Escanejar ID',
            scan_type: 'Escanejar Tipus',
            progress: 'Progrés',
            discovered: 'Descobert',
            no_scans: "No s'han trobat discovery scans",
            all_scan_types: 'Tots Escanejar Types',
            source_config: 'Config. de la font (JSON)',
            invalid_json: 'JSON invàlid a la configuració de la font',
            load_error: 'Error en load discovery scans. Si us plau, torneu-ho a intentar.',
            create_error: 'Error en create discovery scan. Si us plau, torneu-ho a intentar.',
            change_status: "Canvia l'estat",
            new_status: 'Nou estat',
            actions: 'Accions',
            type_endpoint: 'Punt final',
            type_spreadsheet_import: 'Importació de full de càlcul',
            type_cmdb_sync: 'Sincronització CMDB',
            type_cloud_sync: 'Sincronització al núvol',
            status_pending: 'Pendent',
            status_running: 'En execució',
            status_completed: 'Completat',
            status_failed: 'Ha fallat',
            status_cancelled: 'Cancel·lat',
            source_config_placeholder: '{"host": "...", "port": 443}',
            adapter_not_implemented_toast: "Aquest tipus d'anàlisi encara no està disponible. Només es poden crear anàlisis de punts finals.",
            adapter_not_implemented_warning: "Encara no està disponible. No es poden crear anàlisis d'aquest tipus fins que no se'n publiqui la compatibilitat.",
            source_config_endpoint_help: 'Provide a list of FQDNs in the "domains" array. The worker resolves DNS (CNAME / MX / TXT / A) and probes HTTPS / HTTP for each domain.',
            source_config_example_label: 'Example:',
            source_config_spreadsheet_help: 'Provide the file_id of a previously uploaded CSV / XLSX and the template_id of the import template whose column mappings apply.',
            placeholder_source_config: '{"domains": ["example.com"]}',
        },
        staging_area: {
            title: 'Àrea de preparació',
            record_id: 'Registre ID',
            source_scan: "Escaneig d'origen",
            entity_type: "Tipus d'entitat",
            suggested_name: 'Nom suggerit',
            confidence: 'Confiança',
            no_records: "No s'han trobat staged records",
            promote: 'Promoure',
            discard: 'Descartar',
            review: 'Revisar',
            merge: 'Fusionar',
            merge_title: 'Combina el registre',
            merge_target_id: "ID de l'objectiu de fusió",
            reject_reason_placeholder: 'Motiu del rebuig...',
            promotion_status: 'Estat de promoció',
            dedup_status: 'Estat de dedup.',
            all_source_types: 'Tots els tipus de font',
            all_promotion_statuses: 'Tots els estats de promoció',
            all_dedup_statuses: 'Tots els estats de dedup.',
            load_error: 'Error en load staged records. Si us plau, torneu-ho a intentar.',
            promote_error: 'Error en promote record. Si us plau, torneu-ho a intentar.',
            reject_error: 'Error en reject record. Si us plau, torneu-ho a intentar.',
            merge_error: 'Error en merge record. Si us plau, torneu-ho a intentar.',
            review_error: 'Error en mark record for review. Si us plau, torneu-ho a intentar.',
            bulk_promote_error: 'Error en bulk promote records. Si us plau, torneu-ho a intentar.',
            bulk_reject_error: 'Error en bulk reject records. Si us plau, torneu-ho a intentar.',
            service_type_web_server: 'Servidor web',
            service_type_database: 'Base de dades',
            service_type_api_gateway: 'Passarel·la API',
            service_type_storage: 'Emmagatzematge',
            service_type_authentication: 'Autenticació',
            service_type_analytics: 'Analítica',
            service_type_unknown: 'Desconegut',
            source_type_endpoint_scan: 'Escaneig de punt final',
            source_type_spreadsheet: 'Full de càlcul',
            source_type_cmdb: 'CMDB',
            source_type_cloud_inventory: 'Inventari al núvol',
            promotion_status_new: 'Nou',
            promotion_status_under_review: 'En revisió',
            promotion_status_promoted: 'Promogut',
            promotion_status_rejected: 'Rebutjat',
            promotion_status_merged: 'Fusionat',
            dedup_status_unmatched: 'Sense coincidència',
            dedup_status_potential_match: 'Coincidència potencial',
            dedup_status_exact_match: 'Coincidència exacta',
            dedup_status_resolved: 'Resolt',
            confirm_bulk_reject: 'Esteu segur que voleu rebutjar els registres seleccionats?',
            scan_batch_id: 'ID de lot',
            promote_to_registry_title: 'Promoció al Registre de dades',
            promote_modal_instructions: "Crea un element de registre de dades amb collection_method: enllaç observat i de procedència amb aquest registre de descobriment. L'element comença en estat d'esborrany per revisar-lo.",
            promote_label_category: 'Categoria',
            promote_label_name_override: 'Nom (anul·lació opcional)',
            placeholder_name_override: "Per defecte, el nom/nom d'amfitrió del registre de descoberta",
            promote_label_sensitivity: 'Sensibilitat proposada',
            inherit_from_category: 'Heretar de la categoria',
            dedup_status_help: 'Whether this discovered item already exists in inventory. Unmatched = safe to promote; Potential / Exact Match = review or merge first.',
            promote_to_registry: 'Promote to Registry',
            promotion_status_help: 'Where this record is in the review pipeline: New → Under Review → Promoted (or Rejected / Merged).',
            route_to_owner: 'Route to Owner',
            route_to_owner_error: 'Failed to route to owner.',
            route_to_owner_success: 'Owner review task created. Open Owner Review to resolve it.',
            service_type: 'Service Type',
        },
        dedup_rules: {
            title: 'Regles de deduplicació',
            new: 'Nou Rule',
            rule_name: 'Nom de la regla',
            match_fields: 'Camps de coincidència',
            threshold: 'Llindar',
            no_rules: "No s'han trobat dedup rules",
            seed_defaults: 'Valors inicials predeterminats',
            match_type: 'Tipus de coincidència',
            similarity_threshold: 'Llindar de similitud',
            field_name: 'Camp Nom',
            load_error: 'Error en load dedup rules. Si us plau, torneu-ho a intentar.',
            create_error: 'Error en create dedup rule. Si us plau, torneu-ho a intentar.',
            toggle_error: 'Error en toggle dedup rule. Si us plau, torneu-ho a intentar.',
            seed_error: 'Error en seed defaults. Si us plau, torneu-ho a intentar.',
            priority: 'Prioritat',
            enabled_status: 'Habilitat',
            disabled_status: 'Inhabilitat',
            default_badge: 'Per defecte',
            enable: 'Activa',
            disable: 'Desactivar',
            match_type_exact: 'Exacte',
            match_type_fuzzy: 'Aproximat',
            match_type_regex: 'Regex',
            match_type_ip_range: 'Rang IP',
            threshold_validation: 'Must be a number between 0 and 1',
        },
        classification_rules: {
            title: 'Regles de classificació',
            new: 'Nou Rule',
            rule_name: 'Nom de la regla',
            pattern: 'Patró',
            classification: 'Classificació',
            no_rules: "No s'han trobat classification rules",
            seed_defaults: 'Valors inicials predeterminats',
            run_rules_now: 'Executeu les regles ara',
            run_rules_success: "S'han generat {count} suggeriment(s) de classificació nous",
            run_rules_no_results: "No s'ha generat cap suggeriment nou: totes les regles de concordança ja tenen suggeriments pendents",
            run_rules_error: "No s'han pogut executar les regles de classificació. Si us plau, torna-ho a provar.",
            taxonomy_id: 'ID de taxonomia',
            level_id: 'Nivell ID',
            condition_logic: 'Lògica de condició',
            weight: 'Pes',
            conditions: 'Condicions',
            condition_field: 'Camp',
            condition_operator: 'Operador',
            condition_value: 'Valor',
            effectiveness: 'Eficàcia',
            match_count: 'Coincidències',
            approval_count: 'Aprovacions',
            rejection_count: 'Rebutjaments',
            filter_taxonomy: 'Filtra per ID de taxonomia...',
            load_error: 'Error en load classification rules. Si us plau, torneu-ho a intentar.',
            create_error: 'Error en create classification rule. Si us plau, torneu-ho a intentar.',
            seed_error: 'Error en seed defaults. Si us plau, torneu-ho a intentar.',
            toggle_error: 'Error en toggle rule. Si us plau, torneu-ho a intentar.',
            priority: 'Prioritat',
            enabled_status: 'Habilitat',
            disabled_status: 'Inhabilitat',
            enable: 'Activa',
            disable: 'Desactivar',
            logic_and: 'I',
            logic_or: 'O',
            operator_equals: 'Igual a',
            operator_contains: 'Conté',
            operator_matches_regex: 'Coincideix amb Regex',
            operator_in_list: 'A la llista',
            operator_not_equals: 'No és igual a',
            operator_not_contains: 'No conté',
            enabled_label: 'Activat',
            disabled_label: 'Desactivat',
            taxonomy_label: 'Taxonomia',
            operator_label: 'Operador',
            value_label: 'Valor',
            conditions_description: 'Define the signals that must match before this classification is applied.',
        },
        suggestions: {
            title: 'Cua de suggeriments',
            entity_type: "Tipus d'entitat",
            suggested_name: 'Nom suggerit',
            source: 'Font',
            confidence: 'Confiança',
            accept: 'Acceptar',
            reject: 'Rebutjar',
            no_suggestions: 'Sense suggeriments a la cua.',
            modify_approve: 'Modificar i aprovar',
            delegate: 'Delegar',
            delegate_user_id: "ID de l'usuari delegat",
            new_level_id: 'Nou nivell id',
            reject_reason_placeholder: 'Motiu del rebuig...',
            current_level: 'Actual Nivell',
            suggested_level: 'Nivell suggerit',
            filter_taxonomy: 'Filtra per ID de taxonomia...',
            all_confidence: 'Totes les bandes de confiança',
            load_error: 'Error en load suggestions. Si us plau, torneu-ho a intentar.',
            approve_error: 'Error en approve suggestion. Si us plau, torneu-ho a intentar.',
            reject_error: 'Error en reject suggestion. Si us plau, torneu-ho a intentar.',
            modify_error: 'Error en modify & approve suggestion. Si us plau, torneu-ho a intentar.',
            delegate_error: 'Error en delegate suggestion. Si us plau, torneu-ho a intentar.',
            bulk_approve_error: 'Error en bulk approve suggestions. Si us plau, torneu-ho a intentar.',
            bulk_reject_error: 'Error en bulk reject suggestions. Si us plau, torneu-ho a intentar.',
            suggestion_type_new_label: 'Etiqueta nova',
            suggestion_type_validation: 'Validació',
            suggestion_type_reclassification: 'Reclassificació',
            status_pending: 'Pendent',
            status_approved: 'Aprovat',
            status_rejected: 'Rebutjat',
            status_superseded: 'Substituït',
            confidence_high: 'Alta',
            confidence_medium: 'Mitjana',
            confidence_low: 'Baixa',
            confirm_bulk_reject: 'Esteu segur que voleu rebutjar els suggeriments seleccionats?',
            unknown_level_tooltip: 'This rule references a level that is no longer defined in the taxonomy.',
            all_taxonomies: 'Totes les taxonomies',
        },
        analytics: {
            title: 'Anàlisi de suggeriments',
            total_suggestions: 'Total de suggeriments',
            accepted: 'Acceptat',
            rejected: 'Rebutjat',
            pending: 'Pendent',
            acceptance_rate: "Taxa d'acceptació",
            by_entity_type: "Per tipus d'entitat",
            by_source: 'Per font',
            avg_decision_time: 'Temps mitjà de decisió',
            rule_effectiveness: 'Eficàcia de la regla',
            by_status: 'Per Estat',
            by_confidence: 'Per banda de confiança',
            load_error: 'Error en load suggestion analytics. Si us plau, torneu-ho a intentar.',
            confidence_high: 'Alta',
            confidence_medium: 'Mitjana',
            confidence_low: 'Baixa',
            by_confidence_band: 'Per banda de confiança',
            approval_rate: "Taxa d'aprovació",
            by_label_state: "Per estat d'etiqueta",
            no_data_hint: 'No suggestions yet. Promote a staged record or click "Run Rules Now" on the Classification Rules page to generate the first batch.',
            rejection_rate: 'Rejection Rate',
        },
        import_templates: {
            title: "Plantilles d'importació",
            new: 'Nou plantilla',
            template_name: 'Plantilla Nom',
            file_format: 'Format de fitxer',
            column_mappings: 'Assignacions de columnes',
            no_templates: "No s'han trobat import templates",
            source_column: 'Columna font',
            target_field: 'Camp de destinació',
            transform: 'Transformar',
            source_column_label: 'Columna Font',
            target_field_label: 'Camp objectiu',
            transform_label: 'Transformar',
            confirm_delete: 'Esteu segur que voleu eliminar this import template?',
            load_error: 'Error en load import templates. Si us plau, torneu-ho a intentar.',
            save_error: 'Error en save import template. Si us plau, torneu-ho a intentar.',
            delete_error: 'Error en delete import template. Si us plau, torneu-ho a intentar.',
            file_type_csv: 'CSV',
            file_type_xlsx: 'XLSX',
        },
        connectors: {
            title: 'Gestió de connectors',
            new: 'Nou Connector',
            connector_name: 'Nom del connector',
            connector_type: 'Tipus',
            last_sync: 'Últim Sincronitzar',
            no_connectors: "No s'han trobat connectors",
            cmdb_syncs: 'Sincronitzacions CMDB',
            cloud_syncs: 'Sincronitzacions cloud',
            new_sync: 'Nou sincronitzar',
            no_syncs: "No s'han trobat syncs",
            cmdb_error: 'Error en load cmdb scans. Si us plau, torneu-ho a intentar.',
            cloud_error: 'Error en load cloud scans. Si us plau, torneu-ho a intentar.',
            trigger_error: 'Error en trigger sync. Si us plau, torneu-ho a intentar.',
            trigger_sync_error: 'Error en trigger {type}',
            cmdb_section_title: 'Sincronitzacions CMDB',
            cloud_section_title: 'Sincronitzacions al núvol',
            column_connector: 'Connector',
            action_start: 'Inicia',
            confirm_cancel_sync: 'Voleu cancel·lar aquesta sincronització?',
            start_error: 'No s’ha pogut iniciar la sincronització',
            cancel_error: 'No s’ha pogut cancel·lar la sincronització',
            type_endpoint: 'Escaneig d’extrem',
            type_spreadsheet_import: 'Importació de full de càlcul',
            type_cmdb_sync: 'Sincronització CMDB',
            type_cloud_sync: 'Sincronització al núvol',
        },
        discovery_cycles: {
            title: 'Cicles de descobriment',
            new_cycle: 'Nou cicle de descobriment',
            cycle_name: 'Nom del cicle',
            scope_type: "Tipus d'abast",
            scope_ids: "IDs d'abast",
            cadence_days: 'Freqüència (dies)',
            status: 'Estat',
            no_cycles: "No s'han trobat cicles de descobriment",
            compare: 'Comparar cicles',
            delta_report: 'Informe de canvis',
            new_records: 'Registres nous',
            changed_records: 'Registres modificats',
            removed_records: 'Registres eliminats',
            schedule: 'Programar escaneig',
            scope_organization: 'Organització',
            scope_department: 'Departament',
            scope_system: 'Sistema',
            scope_systems: 'Sistemes',
            bulk_actions: 'Accions massives',
            selected: '{count} seleccionat(s)',
            bulk_create: 'Creació massiva',
            bulk_assign_scope: "Assignació massiva d'àmbit",
            bulk_advance: "Avançament massiu d'estat",
            confirm_bulk_action: 'Confirma acció massiva',
            bulk_confirm_message: 'Esteu segur que voleu realitzar aquesta acció?',
            cycles_affected: '{count} cicles seran afectats',
            select_all: 'Selecciona-ho tot',
            select_cycle: 'Selecciona el cicle {name}',
            from_template: 'des de plantilla',
            bulk_results: 'Resultats massius',
            failed_label: 'fallat',
            template_id: 'ID de plantilla',
            count: 'Quantitat',
            advance_to: 'Estat objectiu',
            bulk_success: 'Operació massiva completada',
        },
        owner_review: {
            title: 'Tasques de revisió del propietari',
            assign_owner: 'Assignar al propietari',
            owner_id: 'Propietari',
            decision: 'Decisió',
            approve: 'Aprovar',
            modify: 'Modificar',
            reject: 'Rebutjar',
            escalate: 'Escalar',
            no_tasks: "No s'han trobat tasques de revisió",
            bulk_resolve: 'Resolució massiva',
            exceptions: "Elements d'excepció",
            no_exceptions: "No s'han trobat elements d'excepció",
            record_id_label: 'Identificació de registre',
            selected: 'seleccionat',
            notes: 'Notes',
            select_all: 'Seleccionar Tot',
            select: 'Seleccionar',
            confirm_resolve: 'Confirma',
        },
        classification_lineage: {
            title: 'Historial de classificació',
            link_suggestion: "Vincular a l'etiqueta proposada",
            suggestion_id: 'Suggeriment',
            proposed_label_id: 'Etiqueta proposada',
            no_lineage: "No s'han trobat dades d'historial",
            full_lineage: 'Historial complet',
            record_id_label: 'Identificació de registre',
            staged_record_label: 'Registre escènic',
            enter_staged_record_id: "Introduïu l'identificador de registre per fases",
            scan_id_label: "Identificació d'escaneig",
            select_record: 'Seleccionar un registre',
            select_suggestion: 'Seleccionar una suggerència',
            select_label: 'Seleccionar una etiqueta',
        },
        common_labels: {
            n_rules: '{n} regles',
            one_rule: '1 regla',
            n_scans: '{n} escanejos',
            one_scan: '1 escaneig',
            n_syncs: '{n} se sincronitza',
            one_sync: '1 sincronització',
            n_records: '{n} registres',
            one_record: '1 registre',
            n_templates: '{n} plantilles',
            one_template: '1 plantilla',
            n_suggestions: '{n} suggeriments',
            one_suggestion: '1 suggeriment',
            n_selected: '{n} seleccionat',
            dashboard_loaded: "S'ha carregat el tauler de control de descobriment",
            n_scans_loaded: "S'han carregat {n} escanejos",
            n_staged_records_loaded: "S'han carregat {n} registres escènics",
            confirm_delete_import_template: "Esteu segur que voleu suprimir aquesta plantilla d'importació?",
            invalid_json_source_config: 'JSON no vàlid a source_config',
            scope_organization: 'Organització',
            scope_department: 'Departament',
            scope_system: 'Sistema',
            aria_filter_scans: 'Exploracions de filtres',
            aria_filter_staged_records: 'Filtreu els registres escenificats',
            aria_filter_suggestions: 'Filtres',
            aria_filter_by_taxonomy_id: 'Filtra per identificador de taxonomia',
            aria_filter_by_enabled_status: "Filtra per estat d'habilitat",
            aria_filter_by_source_type: 'Filtra per tipus de font',
            aria_filter_by_promotion_status: 'Filtra per estat de promoció',
            aria_filter_by_dedup_status: 'Filtra per estat de desduplicació',
            aria_filter_by_scan_type: "Filtra per tipus d'escaneig",
            aria_filter_by_status: 'Filtra per estat',
            aria_filter_by_confidence: 'Filtra per banda de confiança',
            aria_select_all_records: 'Seleccioneu tots els registres',
            aria_select_record: 'Seleccioneu el registre {id}',
            aria_select_all_suggestions: 'Seleccioneu tots els suggeriments',
            aria_select_suggestion: 'Seleccioneu el suggeriment {id}',
            aria_promote_record: 'Promociona el registre {id}',
            aria_discard_record: 'Descarta el registre {id}',
            aria_merge_record: 'Combina el registre {id}',
            aria_review_record: 'Revisa el registre {id}',
            aria_accept_suggestion: 'Accepta el suggeriment {id}',
            aria_reject_record: 'Rebutja el registre',
            aria_merge_record_dialog: 'Combina el registre',
            aria_create_new_scan: 'Crea una exploració nova',
            aria_sync_progress: 'Progrés de sincronització: {processed} de {total} elements processats',
            aria_scan_progress: "Progrés de l'escaneig: {processed} de {total} elements processats",
            aria_confidence: 'Confiança: {pct}%',
            aria_effectiveness: 'Eficàcia: {pct}',
            aria_promote_n_records: 'Promocioneu els {n} registres seleccionats',
            aria_discard_n_records: 'Descarta els {n} registres seleccionats',
            placeholder_pii_detection_rule: 'p. ex., Regla de detecció de PII',
            placeholder_tax_id: 'p. ex., TAX-001',
            placeholder_lvl_id: 'p. ex., LVL-001',
            placeholder_service_type: 'p. ex., service_type',
            placeholder_database: 'per exemple, base de dades',
            placeholder_hostname_match: "p. ex., Coincidència exacta del nom d'amfitrió",
            placeholder_hostname: "p. ex., nom d'amfitrió",
            placeholder_hr_systems_csv: 'p. ex., CSV Import de sistemes de recursos humans',
            placeholder_server_name: 'p. ex., Nom del servidor',
            placeholder_hostname_field: "p. ex., nom d'amfitrió",
            placeholder_lowercase: 'per exemple, minúscules',
            placeholder_source_config: '{"host": "...", "port": 443}',
            placeholder_stg_id: 'p. ex., STG-000123',
            placeholder_lvl_002: 'p. ex., LVL-002',
            placeholder_usr_id: 'p. ex., USR-000123',
            decision_for: 'Decisió per a {id}',
        },
        exports: {
            title: 'Exportar',
            inventory: 'Inventari de dades',
            classification_register: 'Registre de classificació',
            raci: 'Matriu RACI',
            exceptions: "Informe d'excepcions",
            evidence_pack: "Paquet d'evidències",
            delta_report: 'Informe de canvis',
            executive_summary: 'Resum executiu',
            export_button: 'Exportar',
            downloading: 'Descarregant...',
            download_complete: 'Descàrrega completada.',
            download_error: "Error en descarregar l'exportació. Si us plau, torneu-ho a intentar.",
        },
        flags: {
            title: 'Marcadors derivats',
            pending: 'Marcadors pendents',
            cross_border_review_needed: 'Revisió transfronterera necessària',
            sharing_disclosure_needed: 'Divulgació de compartició necessària',
            dpia_review_needed: 'Revisió EIPD necessària',
            acknowledge: 'Reconèixer',
            resolve: 'Resoldre',
            no_flags: 'No hi ha marcadors derivats.',
        },
        delta: {
            title: 'Resum de canvis',
            new_count: '{n} nous',
            removed_count: '{n} eliminats',
            changed_count: '{n} modificats',
            re_review_triggered: 'Revisió repetida activada',
            create_re_review: 'Crear revisió repetida',
            recent_alerts: 'Alertes de canvis recents',
            no_alerts: 'No hi ha alertes de canvis recents.',
            threshold_exceeded: 'Llindar de canvis superat (>10%)',
        },
        regulatory: {
            dashboard_governance_info: 'Aquest tauler mostra l\'estat de governança a nivell de cicle: sistemes escanejats, registres en fase, registres revisats, registres aprovats i excepcions pendents. Això coincideix amb els criteris d\'èxit del lideratge de govern: "tauler en directe de qui ha actualitzat el seu inventari, classificació i registres compartits" (P-VN-05).',
            export_artifacts_info: "Les exportacions de descobriment inclouen tots els tipus d'artefactes requerits per UC-VN-10 i DATA-EVD-01: inventari de dades aprovat, registre de classificació, mapeig de propietat/RACI, cua d'excepcions, resum executiu, paquet d'evidències i informe delta.",
            scan_inventory_pipeline_info: "Cada escaneig completat produeix registres compatibles amb la plantilla vn_data_law_data_inventory_register (requisit 88dd9877). Els actius de dades descoberts omplen automàticament el registre d'inventari de dades per complir l'obligació de la Llei de dades de mantenir un inventari de dades digitals.",
            connector_linkage_info: "Cada configuració de connector ha d'incloure: el tipus de sistema (p. ex., CMDB, la plataforma al núvol), la referència del magatzem de dades (base de dades, magatzem de fitxers, SaaS) i l'enllaç de processos empresarials (UC-VN-10 registres de suport necessaris). Aquests camps garanteixen que els actius descoberts siguin traçables al seu sistema d'origen i al seu context empresarial.",
            owner_routing_info: "Les tasques de revisió s'envien al departament correcte o al propietari de l'aplicació en funció de la propietat del sistema/de les dades (UC-VN-10 pas 5). L'encaminament utilitza el registre de propietat del sistema, no l'assignació ad-hoc.",
            owner_actions_audit_info: "Els propietaris poden aprovar, modificar, rebutjar o augmentar cada classificació (UC-VN-10 pas 6). Cada acció captura l'actor, la marca de temps i la raó, produint esdeveniments d'evidència d'auditoria de govern segons el requisit f12fc090 (atestació anual de govern). La pista d'auditoria mostra qui ha revisat, què s'ha canviat i quan.",
            escalation_timers_info: "Temporitzadors d'escalada: el termini de revisió del propietari és de 5 dies laborables; l'excepció escalada és de 48 hores (UC-VN-10 temporitzadors). Els elements no resolts s'escalen automàticament quan expira el termini. Els temporitzadors es poden configurar segons la política de l'organització.",
            original_suggestion_preserved_info: "El suggeriment original de classificació de la màquina/sistema es conserva al costat de cada substitució del propietari (UC-VN-10 requisit de proves). El suggeriment original no es sobreescriu mai i roman visible per a finalitats d'auditoria.",
            dedup_lineage_info: "Les regles de desduplicació impedeixen les entrades d'inventari duplicades quan es descobreix el mateix actiu de dades a través de diverses exploracions o connectors. Les metadades d'escaneig més recents es conserven com a registre principal, mantenint les referències de llinatge a tots els descobriments anteriors per a la traçabilitat de l'auditoria.",
            label_states_info: "Analytics distingeix quatre estats d'etiqueta per a la revisió de govern: suggerit per la màquina (pendent), aprovat pel propietari, modificat pel propietari i rebutjat. Aquests estats permeten als auditors avaluar la precisió de l'automatització i la implicació del propietari.",
            import_pipeline_info: "Les plantilles d'importació han de produir registres compatibles amb la canalització de descobriment. Els registres importats entren a l'escenificació amb els mateixos requisits de metadades que els registres escanejats: identificació del sistema d'origen, identificador de lot i marca de temps. Els mapes de columnes haurien d'orientar els camps definits a vn_data_law_data_inventory_register.",
            lineage_full_path_info: "El llinatge de classificació rastreja el camí complet: exploració/importació -> posada en escena -> revisió del propietari -> etiqueta aprovada -> registre d'entrada (UC-VN-10 passos 1-10). Cada etapa mostra l'identificador de registre, l'actor, la marca de temps i la transició d'estat per a una traçabilitat completa de l'auditoria.",
            staging_workflow_info: "Els registres per fases es distingeixen visualment de les entrades d'inventari aprovades per la seva insígnia d'estat de promoció (UC-VN-10 pas 3). Cada registre conserva metadades de procedència: sistema d'origen, identificador de lot d'escaneig i marca de temps de descobriment per a una traçabilitat total de l'auditoria.",
            suggestion_data_d1_info: "Els suggeriments s'alineen amb el procediment de classificació de la Llei de dades DATA-D1: (1) catalogar els actius de dades, (2) classificar per nivell de sensibilitat, (3) definir regles de gestió. Cada suggeriment inclou dimensions de sensibilitat i importància per requisit 18e4bfd1.",
            downstream_flags_info: "Els elements marcats per compartir, transferir-se transfronterer o tractar d'alt risc desencadenen fluxos de treball enllaçats aigües avall: avaluació de transferència transfronterera, avaluació de riscos compartits i DPIA revisió (UC-VN-10 pas 8).",
            delta_detection_info: "Els cicles de descoberta admeten la detecció delta per UC-VN-10 pas 11: cada cicle mostra quins registres són nous, s'han modificat o s'han eliminat des de l'última línia de base aprovada. Els canvis de material (>10% delta) desencadenen automàticament els fluxos de treball de revisió.",
        },
    },
    incident_ops: {
        errors: {
            fetch: 'No s’ha pogut carregar.',
            save: 'No s’ha pogut desar.',
            fetch_escalation_log_failed: 'No s’ha pogut obtenir el registre d’escalats.',
            fetch_activations_failed: 'No s’han pogut obtenir les activacions.',
            deactivate_failed: 'No s’ha pogut desactivar.',
            complete_activation_failed: 'No s’ha pogut completar l’activació.',
            fetch_playbook_templates_failed: 'No s’han pogut obtenir les plantilles de playbook.',
            create_playbook_template_failed: 'No s’ha pogut crear la plantilla de playbook.',
            clone_template_failed: 'No s’ha pogut clonar la plantilla.',
            update_status_failed: 'No s’ha pogut actualitzar l’estat.',
            deprecate_template_failed: 'No s’ha pogut declarar obsoleta la plantilla.',
            seed_defaults_failed: 'No s’han pogut generar els valors per defecte.',
            fetch_regulator_exports_failed: 'No s’han pogut obtenir les exportacions al regulador.',
            create_regulator_export_failed: 'No s’ha pogut crear l’exportació al regulador.',
            update_submission_status_failed: 'No s’ha pogut actualitzar l’estat de tramesa.',
            fetch_response_metrics_failed: 'No s’han pogut obtenir les mètriques de resposta.',
            fetch_sla_dashboard_failed: 'No s’ha pogut obtenir el tauler d’SLA.',
            fetch_sla_definitions_failed: 'No s’han pogut obtenir les definicions d’SLA.',
            create_sla_definition_failed: 'No s’ha pogut crear la definició d’SLA.',
            seed_sla_defaults_failed: 'No s’han pogut generar els valors d’SLA per defecte.',
            fetch_vendor_coordination_failed: 'No s’ha pogut obtenir la coordinació de proveïdors.',
            fetch_vendor_detail_failed: 'No s’han pogut obtenir les dades del proveïdor.',
            create_vendor_coordination_failed: 'No s’ha pogut crear la coordinació de proveïdors.',
            add_communication_failed: 'No s’ha pogut afegir la comunicació.',
            add_info_request_failed: 'No s’ha pogut afegir la sol·licitud d’informació.',
            add_action_request_failed: 'No s’ha pogut afegir la sol·licitud d’acció.',
        },
        nav: {
            playbooks: 'Plantilles de playbook',
            sla_definitions: 'Definicions d’SLA',
            sla_dashboard: 'Tauler d’SLA',
            escalation_log: 'Registre d’escalats',
            regulator_exports: 'Exportacions al regulador',
            deadlines: 'Terminis de notificació',
            vendor_coordination: 'Coordinació amb proveïdors',
            metrics: 'Mètriques de resposta',
            playbook_links: 'Enllaços de playbook',
            regulatory_sla: 'SLA regulatori',
            coordination_dashboard: 'Coordinació',
            evidence_chain: 'Cadena de custòdia',
            post_review: 'Revisió post-incident',
        },
        playbooks: {
            page_title: 'Plantilles de playbook',
            activations_page_title: 'Activacions de playbook',
            title: 'Plantilles de playbook',
            new: 'Nova plantilla',
            seed_defaults: 'Generar valors per defecte',
            scenario_type: "Tipus d'escenari",
            severity_applicability: 'Aplicabilitat segons gravetat',
            all_scenario_types: "Tots els tipus d'escenari",
            new_playbook: 'Nova plantilla de playbook',
            clone: 'Clonar',
            clone_template: 'Clonar plantilla',
            template_name: 'Nom de la plantilla',
            deprecate: 'Declarar obsoleta',
            confirm_deprecate: 'Voleu declarar obsoleta la plantilla "{name}"?',
            create_template: 'Crear plantilla',
            update_status: 'Actualitzar estat',
            no_templates: 'No s’ha trobat cap plantilla de playbook.',
            deactivation_reason_placeholder: 'Motiu de la desactivació…',
            description_placeholder: 'Descripció del playbook…',
            name_placeholder: 'Nom del playbook…',
            procedure_steps: 'Procedure Steps',
        },
        sla_definitions: {
            page_title: 'Definicions d’SLA',
            title: 'Definicions d’SLA',
            new: 'Nova definició d’SLA',
            sla_name: 'Nom de l’SLA',
            target_hours: 'Hores objectiu',
            escalation_after: 'Escalat després de',
            applies_to: "S'aplica a",
            no_definitions: 'No s’ha trobat cap definició d’SLA.',
            target_value_placeholder: 'Valor objectiu',
            name_placeholder: 'Nom de l’SLA…',
            threshold_hours_label: 'Hores llindar',
            severity_label: 'Gravetat',
            all_types: 'Tots els tipus',
            all_severities: 'Totes les gravetats',
            default_label: 'Per defecte',
            bound_heading: 'Regulatory clocks behind the default notification target',
            bound_capped: 'Default notification-time targets are capped at {hours} h — the shortest notification period among the installed clocks already in force.',
            bound_not_capped: 'No installed notification clock in force states a period, so default notification-time targets are not capped.',
            bound_bounding_label: 'Bounded by — in force',
            bound_upcoming_label: 'Left out — not yet in force',
            bound_unreadable_label: 'Left out — timing unknown',
            bound_date_not_stated_label: 'Left out — no effective date stated',
            bound_hours: '{hours} h',
            bound_unreadable: 'The regulatory clocks behind the default notification target could not be read.',
            bound_retry_subject: 'the notification clocks',
            load_retry_subject: 'the SLA definitions',
            bound_phase_unresolved_label: 'Left out — phase start not dated',
        },
        sla_dashboard: {
            compliant: 'Complint',
            page_title: 'Tauler d’SLA',
            title: 'Tauler d’SLA',
            loading: 'Carregant dades d’SLA…',
            compliance_rate: 'Taxa de compliment',
            breached: 'Incomplert',
            at_risk: 'En risc',
            on_track: 'Dins del termini',
            by_severity: 'Per gravetat',
            total_records: 'Total de registres',
            met: 'Complert',
            sla_records: 'Registres d’SLA',
            sla_reference: 'Referència SLA',
            record_id: 'ID del registre',
            metric_type: 'Tipus de mètrica',
            severity: 'Gravetat',
            elapsed: 'Temps transcorregut',
            clock_start: 'Inici del rellotge',
            statutory_baseline: 'Statutory baseline: the notification deadline required by the frameworks this organization has installed.',
            statutory_baseline_heading: 'Statutory Baseline',
        },
        escalation_log: {
            page_title: 'Registre d’escalats',
            title: 'Registre d’escalats',
            incident_id: 'ID de l’incident',
            escalated_by: 'Escalat per',
            escalated_to: 'Escalat a',
            reason: 'Motiu',
            escalated_at: 'Escalat el',
            no_escalations: 'No s’ha trobat cap escalat.',
            filter_incident_id: 'Filtrar per ID d’incident',
            escalation_id: 'ID d’escalat',
            level: 'Nivell',
            level_range: 'Nivell (L1–L4)',
            create_escalation: 'Crear escalat',
            incident_id_placeholder: 'p. ex., INC-001',
            target_role_placeholder: 'p. ex., dpo, incident_manager',
            select_placeholder: '— seleccioneu —',
        },
        regulator_exports: {
            page_title: 'Exportacions al regulador',
            title: 'Exportacions al regulador',
            new: 'Nova exportació',
            incident_id: 'ID de l’incident',
            regulator: 'Regulador',
            export_format: 'Format',
            exported_at: 'Exportat el',
            no_exports: 'No s’ha trobat cap exportació al regulador.',
            enter_incident_id: 'Introduïu l’ID de l’incident',
            export_id: 'ID d’exportació',
            watermark: 'Marca d’aigua',
            submission_status: 'Estat de tramesa',
            all_jurisdictions: 'Totes les jurisdiccions',
            no_transitions: 'No hi ha més transicions disponibles des de',
            select_new_status: '— seleccioneu el nou estat —',
            sections_included: 'Seccions incloses',
            form_classification: 'Classificació',
            submission_channel: 'Canal de presentació',
        },
        deadlines: {
            title: 'Terminis de notificació',
            incident_id: 'ID de l’incident',
            deadline: 'Termini',
            regulator: 'Regulador',
            remaining: 'Restant',
            no_deadlines: 'No s’ha trobat cap termini de notificació.',
        },
        vendor_coordination: {
            page_title: 'Coordinació amb proveïdors',
            title: 'Coordinació amb proveïdors',
            new: 'Nova coordinació',
            incident_id: 'ID de l’incident',
            vendor_name: 'Nom del proveïdor',
            contact: 'Contacte',
            coordination_status: 'Estat de la coordinació',
            status_labels: {
                initiated: 'Initiated',
                in_progress: 'In progress',
                awaiting_vendor: 'Awaiting vendor',
                vendor_responded: 'Vendor responded',
                resolved: 'Resolved',
                closed: 'Closed',
                escalated: 'Escalated',
            },
            no_items: 'No s’ha trobat cap element de coordinació amb proveïdors.',
            assessment_placeholder: 'Avaluació inicial del proveïdor…',
            coordination_id: 'ID de coordinació',
            communications: 'Comunicacions',
            info_requests: 'Sol·licituds d’informació',
            action_requests: 'Sol·licituds d’acció',
            direction: 'Direcció',
            channel_label: 'Canal',
            no_transitions: 'No hi ha més transicions disponibles.',
            vendor_ref_number: 'Número de referència',
            report_date: 'Data de l’informe',
            contact_email: 'Correu electrònic',
            contact_phone: 'Telèfon',
            channel_email: 'Correu electrònic',
            channel_meeting: 'Reunió',
            channel_phone: 'Telèfon',
            channel_portal: 'Portal',
            incident_id_placeholder: 'Filtrar per ID d’incident…',
            priority_critical: 'Crítica',
            priority_high: 'Alta',
            priority_low: 'Baixa',
            priority_medium: 'Mitjana',
            vendor_id_placeholder: 'Filtrar per ID de proveïdor…',
            description_placeholder: 'Descripció…',
            filter_vendor_id: 'Filtrar per ID de proveïdor',
            inbound: 'Entrant',
            outbound: 'Sortint',
            summary_placeholder: 'Resum…',
            priority_label: 'Prioritat',
            due_date_label: 'Data de venciment',
            info_request_desc: 'Descripció de la sol·licitud d’informació',
            action_request_desc: 'Descripció de la sol·licitud d’acció',
            content_label: 'Contingut',
            select_incident: 'Seleccionar incident',
            select_vendor: 'Seleccionar proveïdor',
            discovery_date: 'Data de descobriment',
            contact_role: 'Contact Role',
            processor_role: 'Processor Role',
            sla_elapsed: 'SLA Elapsed',
            overdue: 'Overdue',
            actor: 'Actor',
        },
        metrics: {
            page_title: 'Mètriques de resposta',
            title: 'Mètriques de resposta',
            loading: 'Carregant les dades de mètriques…',
            avg_response_time: 'Temps mitjà de resposta',
            avg_resolution_time: 'Temps mitjà de resolució',
            total_incidents: 'Total d’incidents',
            by_severity: 'Per gravetat',
            by_status: 'Per estat',
            trend: 'Tendència',
            start_sla_clock: 'Iniciar el rellotge d’SLA',
            sla_records: 'Registres d’SLA',
            no_sla_records: 'Encara no hi ha registres d’SLA. Feu clic a «Iniciar el rellotge d’SLA» per crear-ne un.',
            record_id: 'ID del registre',
            severity: 'Gravetat',
            progress: 'Progrés',
            incident_id_required: 'L’ID de l’incident és obligatori',
            sla_definition_required: 'La definició d’SLA és obligatòria',
            metric_type_required: 'El tipus de mètrica és obligatori',
            threshold_required: 'Les hores llindar són obligatòries',
            sla_clock_started: 'Rellotge d’SLA iniciat',
            sla_definition: 'Definició d’SLA',
            no_definitions: 'No s’ha trobat cap definició d’SLA. Creeu-ne una primer a Definicions d’SLA.',
            select_definition: 'Seleccioneu una definició d’SLA…',
            select_metric_type: 'Seleccioneu el tipus de mètrica…',
            select_severity: 'Seleccioneu la gravetat…',
            threshold_hours: 'Llindar (hores)',
            warning_threshold_pct: 'Llindar d’advertiment (%)',
            start_clock: 'Iniciar rellotge',
            starting: 'Iniciant…',
            metric_label: 'Mètrica',
            value_label: 'Valor',
            incident_id: 'ID de l’incident',
            threshold_placeholder: 'p. ex., 24',
            metric_types: {
                triage_time: 'Temps de triatge',
                containment_time: 'Temps de contenció',
                notification_time: 'Temps de notificació',
                resolution_time: 'Temps de resolució',
            },
            severities: {
                critical: 'Crítica',
                high: 'Alta',
                medium: 'Mitjana',
                low: 'Baixa',
            },
            sla_reference: 'Referència SLA',
        },
        playbook_links: {
            page_title: 'Enllaços de playbook',
            title: 'Enllaços de playbook a notificacions',
            new_link: 'Vincular playbook a una notificació',
            activation_id: 'ID d’activació',
            notification_id: 'ID de notificació',
            select_activation: 'Seleccioneu una activació…',
            select_notification: 'Seleccioneu una notificació…',
            no_links: 'No s’ha trobat cap enllaç de playbook.',
            seed_pdpl: 'Afegir enllaços de playbook de plantilla',
            created: 'Enllaç de playbook creat',
            seeded: 'Enllaços de playbook de plantilla afegits',
            create_failed: 'No s’ha pogut crear l’enllaç de playbook',
            seed_failed: 'No s’han pogut afegir els enllaços de playbook de plantilla',
            seed_already: 'The template playbook links are already present; nothing was added',
            template_activation: 'Template (not an activation of your own)',
            template_notification: 'Template (not a notification of your own)',
        },
        regulatory_sla: {
            // fix-sprint-20260811-2123-followup — keyed on the route's
            // `skipped_reason_code`. The English prose the route also returns is a
            // fallback for an unrecognised code, never the operator's explanation.
            // fix-sprint-20260808-2111 (finding #35) — the declared incident-notification
            // deadlines a seed did not turn into a mapping, and why.
            not_seeded_heading: 'Declared deadlines that were not added',
            not_seeded_intro: 'These incident-notification deadlines are declared by your installed frameworks, but no mapping was added for them:',
            not_seeded_reason: {
                unclassified: 'its deadline type is not one this table records',
                no_numeric_period: 'it states its period in words, not as a number of hours or days, so a mapping cannot hold it',
                not_yet_in_force: 'it is not yet in force: its clock starts to bind on {date}',
                activation_not_determinable: 'neither the framework nor the phase it names states when its clock starts to bind',
            },
            seed_skipped: {
                no_governing_framework: 'Cap marc instal·lat regeix la resposta a incidents, de manera que no hi ha terminis per afegir.',
                declared_but_none_active: 'Hi ha un marc que regeix la resposta a incidents instal·lat, però no és vigent.',
                no_declared_deadlines: 'Els marcs de resposta a incidents instal·lats no estableixen terminis de notificació.',
                declared_deadlines_withheld: 'The installed frameworks declare notification deadlines, but none could be added yet. They are listed below under the deadlines that were not added.',
                already_seeded: 'Every notification deadline the installed frameworks declare already has its mapping. Nothing new was added.',
            },
            // Fix sprint F25 — the period is rendered WITH the unit the framework
            // declared. The table hardcoded an `h` suffix, so a deadline the pack
            // states as 15 working days was shown to the operator as "15h": a
            // misstated statutory period on a regulator-facing screen. `deadline_unit`
            // had one writer and zero readers; this is the reader.
            //
            // The pack unit vocabulary is open (26 distinct values in the corpus), so
            // `unit_unknown` carries anything not listed rather than guessing hours.
            deadline_period_label: 'Data límit',
            deadline_units: {
                hours: '{count} hores',
                working_days: '{count} dies hàbils',
                calendar_days: '{count} dies naturals',
                days: '{count} dies',
                weeks: '{count} setmanes',
                months: '{count} mesos',
                years: '{count} anys',
            },
            deadline_unit_unknown: '{count} {unit}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 (owner ruling OR2) — a reference table: it sets no clock,
            // and a row is refused without the unit its period is stated in.
            reference_notice: 'This is a reference table. A mapping sets no clock and escalates nothing: no incident, breach notification or SLA reads these rows. Every regulatory deadline in the product comes from the deadline records your installed frameworks declare.',
            seed_only_conditional: 'The installed frameworks declare notification deadlines only for specific activities or conditions, so none was added automatically.',
            seed_conditional_heading: 'Deadlines not added automatically',
            seed_conditional_intro: 'These installed deadlines apply only to a stated activity or condition. The platform cannot tell whether your organisation carries it, so a person decides: if it does, add the mapping from here.',
            seed_conditional_scope_activity: 'Applies to the activity: {value}',
            seed_conditional_scope_condition: 'Applies only if: {value}',
            seed_conditional_add: 'Add this mapping',
            deadline_amount: 'Period',
            deadline_unit: 'Unit',
            select_deadline_unit: 'Select a unit...',
            deadline_unit_options: {
                hours: 'Hours',
                working_days: 'Working days',
                calendar_days: 'Calendar days',
                days: 'Days',
                weeks: 'Weeks',
                months: 'Months',
                years: 'Years',
            },
            page_title: 'Assignacions d’SLA regulatori',
            title: 'Assignacions d’SLA regulatori',
            new_mapping: 'Nova assignació d’SLA',
            sla_definition: 'Definició d’SLA',
            select_sla_definition: 'Seleccioneu una definició d’SLA…',
            framework_code: 'Codi del marc',
            deadline_type: 'Tipus de termini',
            deadline_hours: 'Hores del termini',
            seed_pdpl: 'Afegir assignacions a partir dels terminis instal·lats',
            no_mappings: 'No s’ha trobat cap assignació d’SLA regulatori.',
            dashboard: 'Tauler d’SLA regulatori',
            deadline_types: {
                initial_notification: 'Notificació inicial',
                supplement_response: 'Informació complementària',
                authority_response: 'Resposta de l’autoritat',
                remediation_report: 'Informe de remediació',
                subject_notification: 'Data Subject Notification',
            },
            statutory_reference: 'Referència legal',
        },
        coordination_dashboard: {
            page_title: 'Tauler de coordinació',
            title: 'Tauler de coordinació',
            incident_id: 'ID de l’incident',
            select_incident: 'Seleccioneu un incident…',
            team_role: 'Rol de l’equip',
            members: 'Membres',
            tasks: 'Tasques',
            sla_status: 'Estat de l’SLA',
            no_teams: 'No s’ha trobat cap equip de coordinació.',
            new_team: 'Nou equip de coordinació',
            enter_incident_id: 'Introduïu un ID d’incident per veure els equips de coordinació.',
            member_ids_placeholder: 'usuari1, usuari2',
            edit_team: 'Edita equip',
            task_count: 'Recompte de tasques',
            tasks_completed: 'Completat',
            team_roles: {
                security: 'Seguretat',
                legal: 'Legal',
                it: 'TI',
                management: 'Direcció',
                department_owner: 'Responsable de departament',
                external: 'Extern',
            },
        },
        evidence_chain: {
            page_title: 'Cadena de custòdia',
            title: 'Cadena de custòdia de les evidències',
            new_entry: 'Nova entrada d’evidència',
            select_incident: 'Seleccioneu un incident…',
            actor: 'Actor',
            select_actor: 'Seleccioneu un actor…',
            evidence_id: 'ID d’evidència',
            action: 'Acció',
            recipient: 'Destinatari',
            no_entries: 'No s’ha trobat cap entrada de la cadena de custòdia.',
            enter_incident_id: 'Introduïu un ID d’incident per veure la cadena de custòdia.',
            evidence_id_placeholder: 'p. ex. EVD-2026-0001',
            evidence_id_help: 'Una referència única per a l’element de prova: un fitxer de registre, una imatge de disc o una captura de pantalla. Es proposa un suggeriment; editeu-lo perquè coincideixi amb el vostre catàleg de proves.',
            select_incident_first: 'Seleccioneu un incident a dalt abans d’afegir una entrada de prova.',
            for_incident: 'Per a l’incident',
            actions: {
                collected: 'Recollida',
                shared: 'Compartida',
                exported: 'Exportada',
                archived: 'Arxivada',
            },
        },
        post_review: {
            page_title: 'Revisió post-incident',
            title: 'Revisió post-incident',
            new_review: 'Nova revisió',
            select_incident: 'Seleccioneu un incident…',
            lessons_learned: 'Lliçons apreses',
            recommendations: 'Recomanacions',
            improvement_actions: 'Accions de millora',
            submit_for_review: 'Enviar per a revisió',
            aria_submit_review: 'Enviar la revisió {name} per a revisió',
            aria_sign_off: 'Aprovar la revisió {name}',
            sign_off: 'Aprovar',
            no_reviews: 'No s’ha trobat cap revisió post-incident.',
            enter_incident_id: 'Introduïu un ID d’incident per veure la revisió post-incident.',
            one_per_line: 'Un per línia',
            statuses: {
                draft: 'Esborrany',
                in_review: 'En revisió',
                signed_off: 'Aprovada',
                closed: 'Tancada',
            },
        },
        activations: {
            title: 'Activacions de playbook',
            activation_id: 'ID d’activació',
            phases: 'Fases',
            activated_at: 'Activat el',
            activated_by: 'Activat per',
            deactivate: 'Desactivar',
            complete: 'Completar',
            deactivation_reason: 'Motiu de la desactivació',
            evidence_button: 'Evidència',
            evidence_modal_title: 'Evidència de l’activació',
            no_evidence: 'No hi ha cap evidència adjunta a aquesta activació.',
            upload_evidence_label: 'Pujar evidència de l’activació',
            procedure_followed: 'Procedure Followed',
            phases_completed_sr: '{completed} of {total} phases completed',
        },
    },
    program_governance: {
        // ── Sprint 65.8.0 (AC9) — the audit engagement register ─────
        //
        // The record an audit is planned, scoped, staffed, worked and closed as.
        // Every gate this copy names is the SERVER's — the lifecycle, the two
        // mandatory reasons, the calendar-date period, and the refusal to close an
        // engagement over live findings. Each is stated BEFORE the operator meets
        // it, because a rule an operator first learns as a 409 is a rule the
        // product kept to itself.
        audit_engagements: {
            title: 'Audit Engagements',
            description: 'Plan an audit, record what it covers and who is performing it, raise findings inside it, and close it once every finding has been resolved.',
            module_guide_summary: 'An audit engagement carries its scope, the period audited, who is auditing, and a lifecycle that runs from planning through fieldwork and reporting to closed. Findings are raised inside an engagement and never on their own, and an engagement cannot be closed while any of its findings is still unresolved.',
            load_error: 'The audit engagements could not be loaded. Try again.',
            counts_error: 'The engagement counts could not be read, so no totals are shown.',
            counts_label: 'Engagements by state',
            total: 'All engagements',
            filter_state: 'State',
            filter_all_states: 'All states',
            filter_auditor_kind: 'Audit type',
            filter_all_auditor_kinds: 'All audit types',
            filters_label: 'Filter audit engagements',
            col_title: 'Engagement',
            col_period: 'Period audited',
            col_auditor: 'Auditor',
            col_state: 'State',
            no_engagements: 'No audit engagements have been planned yet.',
            no_engagements_filtered: 'No audit engagement matches these filters.',
            select_hint: 'Select an engagement to see its findings and the actions available on it.',
            open_engagement: 'Open this engagement',
            selected_label: 'Selected engagement',
            new_engagement: 'Plan an engagement',
            create_title: 'Plan an audit engagement',
            section_scope: 'What is being audited',
            section_auditor: 'Who is auditing',
            field_title: 'Title',
            field_scope: 'Scope',
            field_scope_hint: 'What this engagement covers, in the words of whoever scoped it.',
            field_period_start: 'Period start',
            field_period_end: 'Period end',
            period_hint: 'Calendar dates. The period audited is a span of days somebody wrote down, so it carries no time of day.',
            period_invalid: 'The period end must not fall before the period start.',
            period_required: 'Both period dates are required, each as a calendar date.',
            field_auditor_kind: 'Audit type',
            auditor_internal: 'Internal',
            auditor_external: 'External',
            field_auditor_name: 'Auditor',
            auditor_name_hint_internal: 'The person or team performing the audit.',
            auditor_name_hint_external: 'The firm performing the audit. An external auditor has no account on this platform, so no user is named.',
            field_auditor_user: 'Platform user performing the audit',
            auditor_user_placeholder: 'Select a user…',
            auditor_user_required: 'An internal audit must name the platform user performing it.',
            created: 'Audit engagement planned',
            create_error: 'The audit engagement could not be created.',
            lifecycle: 'Lifecycle',
            lifecycle_actions: 'Actions on this engagement',
            advance_to: 'Move to {state}',
            transitioned: 'Engagement moved to {state}',
            transition_error: 'The engagement could not be moved.',
            terminal_notice: 'This engagement is {state}. Its scope, period and auditor can no longer be changed.',
            cancel_engagement: 'Cancel engagement',
            cancel_title: 'Cancel this audit engagement',
            cancel_reason: 'Why is it being cancelled?',
            cancel_reason_required: 'Cancelling an audit engagement requires a reason.',
            cancel_confirm: 'Cancel the engagement',
            cancellation_reason: 'Cancellation reason',
            close_engagement: 'Close engagement',
            close_gate_label: 'Whether this engagement can be closed',
            close_blocked: 'This engagement cannot be closed: {count} finding(s) raised inside it are still unresolved. Close or reject each one first.',
            close_ready: 'Every finding raised inside this engagement is closed or rejected, so the engagement can be closed.',
            close_gate_unknown: 'The unresolved-finding count could not be read, so the platform cannot say whether this engagement may be closed.',
            closed_on: 'Closed on',
            cancelled_on: 'Cancelled on',
            created_on: 'Planned on',
            states: {
                planned: 'Planned',
                in_fieldwork: 'In fieldwork',
                reporting: 'Reporting',
                closed: 'Closed',
                cancelled: 'Cancelled',
            },
            directory_truncated: 'Only the first {count} users are listed here.',
            directory_unavailable: 'The user directory could not be loaded, so no user can be chosen right now.',
            directory_reload: 'Reload',
            directory_reloading: 'Reloading…',
            directory_retry_subject_auditor: 'the user directory for the auditor',
            directory_retry_subject_owner: 'the user directory for the finding owner',
            reload: 'Reload',
            list_retry_subject: 'the audit engagements',
            findings_retry_subject: 'the findings',
            findings: {
                title: 'Findings',
                section_label: 'Findings raised inside this engagement',
                unresolved: 'Unresolved',
                load_error: 'The findings could not be loaded. Try again.',
                no_findings: 'No findings have been raised inside this engagement.',
                new_finding: 'Raise a finding',
                create_title: 'Raise a finding inside this engagement',
                engagement: 'Engagement',
                anchor_hint: 'A finding is always raised inside an engagement. It cannot be recorded without one.',
                field_title: 'Title',
                field_observation: 'What was observed',
                field_severity: 'Severity',
                field_owner: 'Owner',
                owner_placeholder: 'Select a user…',
                col_title: 'Finding',
                col_severity: 'Severity',
                col_owner: 'Owner',
                col_state: 'State',
                created: 'Finding raised',
                create_error: 'The finding could not be raised.',
                transitioned: 'Finding moved to {state}',
                transition_error: 'The finding could not be moved.',
                accept: 'Accept',
                start_remediation: 'Start remediation',
                close: 'Close finding',
                reject: 'Reject',
                reject_title: 'Reject this finding',
                reject_reason: 'Why is this not a finding?',
                reject_reason_required: 'Rejecting an audit finding requires a reason.',
                reject_confirm: 'Reject the finding',
                rejection_reason: 'Rejection reason',
                reject_only_from_open: 'A finding can only be rejected while it is open. Once it has been accepted, the organisation has already said it is a finding.',
                states: {
                    open: 'Open',
                    accepted: 'Accepted',
                    remediating: 'Remediating',
                    closed: 'Closed',
                    rejected: 'Rejected',
                },
            },
        },
        maturity_walkthrough: {
            run_label: "Passa'm per les llacunes",
            running: 'Caminant per buits...',
            results_heading: 'Full de ruta de millora ({count} bretxa{plural})',
            tokens_label: '≈ {count} fitxes',
            no_gaps: "No s'han detectat buits: tots els dominis a l'objectiu o per sobre.",
            col_priority: 'Prioritat',
            col_domain: 'Domini',
            col_levels: 'Actual → Objectiu',
            col_severity: 'Severitat',
            col_actions: 'Accions de reparació',
            citations_label: 'Citacions',
            accept_label: 'Acceptar el full de ruta',
            reject_label: "Rebutja l'esborrany",
            export_label: 'Exporta DOCX',
            exported_label: "S'ha exportat el full de ruta",
            accept_hint: 'Accepting records your decision on the saved draft; it does not change the assessment. Use Export DOCX to download the roadmap, then carry the actions you choose into tasks or your own plan.',
            resolve_summary: 'Resolució: {status}',
            resolve_status_accepted: 'Acceptat',
            resolve_status_rejected: 'Rebutjat',
            error_generic: "No s'ha pogut executar la guia de bretxa. Torna-ho a provar o comprova l'estat de l'avaluació.",
        },
        policy_gap: {
            run_label: 'Detectar llacunes polítiques',
            running: 'Detectant buits...',
            results_heading: 'Buits de política ({count})',
            tokens_label: '≈ {count} fitxes',
            no_gaps: "No s'han detectat buits de cobertura.",
            col_severity: 'Severitat',
            col_kind: 'amable',
            col_clause: 'Clàusula / requisit',
            kind_missing: 'Falta la clàusula',
            kind_weak: 'Cobertura feble',
            kind_stale: 'Obsolet (revisió vençuda)',
            remediation_label: 'Suggeriment de reparació',
            citations_label: 'Citacions',
            accept_label: "Accepta l'informe de mancances",
            reject_label: "Rebutja l'informe",
            export_label: 'Exporta DOCX',
            accept_hint: "Accepta els registres de l'informe de buits sobre l'esborrany persistent. Utilitzeu Exporta DOCX per generar un informe d'auditoria de polítiques; l'operador condueix l'edició real de la política.",
            resolve_summary: 'Resolució: {status}',
            error_generic: "No s'han pogut detectar les llacunes de la política. Torneu-ho a provar o comproveu l'estat del document.",
        },
        remediation_plan_generator: {
            run_label: 'Generar pla a partir de buits',
            running: "S'està generant pla...",
            results_heading: 'Pla de correcció ({phase_count} fase{plural})',
            tokens_label: '≈ {count} fitxes',
            phase_label: 'Fase {index}',
            window_label: 'Finestra',
            owner_label: 'Rol de propietari',
            evidence_label: 'Tipus de proves necessaris',
            tasks_label: 'Tasques',
            no_phases: "No s'han produït fases: el conjunt de bretxes d'entrada pot estar buit.",
            citations_label: 'Citacions',
            accept_label: 'Acceptar el pla',
            reject_label: 'Rebutja el pla',
            export_label: 'Exporta DOCX',
            accept_hint: "Accepting creates one task for each task listed (up to 5 per phase and 12 in all). Each task's description names this plan; the tasks are not linked to a record in the Remediation Plans register and have no assignee, so give each one an owner from the task list. Use Export DOCX for the full deliverable.",
            resolve_summary: 'Resolució: {status} · {tasks} tasques creades',
            error_generic: "No s'ha pogut generar el pla de reparació. Torneu-ho a provar o comproveu l'entrada del conjunt d'espais.",
            source_picker_label: 'Font',
            source_all: 'Tots',
            source_consent: 'Anàlisi de la bretxa de consentiment',
            source_policy: 'Detecció de bretxes polítiques',
            source_vendor: 'Incorporació de venedors',
            load_upstream_label: 'Carrega des de aigües amunt',
            loading_upstream: "S'està carregant…",
            upstream_count: "S'han carregat {count} buits",
            export_done: 'Saved {filename} to your downloads.',
            no_gaps_in_source: 'The selected source holds no gaps, so nothing was generated and no tasks can be created. Run that analysis first, or choose another source.',
            no_gaps: 'No gaps are loaded, so there is nothing to plan. Choose a source and click Load from upstream.',
        },
        nav: {
            audit_engagements: 'Audit Engagements',
            handbook_assessments: 'Handbook Assessments',
            maturity_models: 'Models de maduresa',
            maturity_assessments: 'Avaluacions de maduresa',
            trend_dashboard: 'Tauler de tendències',
            reporting_periods: "Períodes d'informe",
            executive_summaries: 'Resums executius',
            audit_readiness: 'Preparació per a auditoria',
            evidence_gaps: "Llacunes d'evidència",
            program_health: 'Estat del programa',
            obligation_mappings: "Assignacions d'obligacions",
            governance_documents: 'Documents de governança',
            remediation_plans: 'Plans de remediació',
            governance_model: 'Model de governança',
            document_drafting: 'Redacció de documents',
            document_issuance: 'Emissió de documents',
            acknowledgment_tracking: 'Seguiment de confirmacions',
            change_log: 'Registre de canvis',
            review_cadence: 'Cadència de revisió',
        },
        maturity_models: {
            title: 'Models de maduresa',
            new: 'Nou Model',
            seed_defaults: 'Valors inicials predeterminats',
            domains: 'Dominis',
            activate: 'Activar',
            no_models: "No s'han trobat maturity models",
            new_maturity_model: 'Nou Maturity Model',
            create_model: 'Afegir Model',
            load_error: "No s'han pogut carregar els models de maduresa. Torneu-ho a provar.",
            create_error: "No s'ha pogut crear el model de maduresa",
            status_error: "No s'ha pogut actualitzar l'estat del model",
            seed_error: "No s'han pogut generar els valors per defecte",
            aria_seed_defaults: 'Generar models de maduresa per defecte',
            aria_create_new: 'Crear nou model de maduresa',
            loading_sr: 'Carregant models de maduresa',
            model_name_placeholder: 'p. ex., Model de maduresa de privacitat',
            status_changed: "L'estat del model ha canviat a {status}",
        },
        maturity_assessments: {
            models_load_error: 'The maturity model list could not be loaded, so model names may show as unknown.',
            no_models_action: 'Open Maturity Models',
            no_models_body: 'An assessment scores your programme against a maturity model, and this organization has none. Create one first.',
            no_models_title: 'No maturity models yet',
            title: 'Avaluacions de maduresa',
            new: 'Nou avaluació',
            model: 'Model',
            assessor: 'Avaluador',
            overall_score: 'Puntuació general',
            overall_score_help: 'Una qualificació de maduresa ponderada de 0 a 5, calculada automàticament quan es finalitza una avaluació. Augmenteu-la puntuant més alt cada domini durant l’avaluació i després fent passar l’avaluació per Enviar per revisió → Finalitzar.',
            assessed_at: 'Avaluat el',
            no_assessments: "No s'han trobat maturity assessments",
            load_error: "No s'han pogut carregar les avaluacions. Torneu-ho a provar.",
            create_error: "No s'ha pogut crear l'avaluació",
            start_error: "No s'ha pogut iniciar l'avaluació",
            archive_error: "No s'ha pogut arxivar l'avaluació",
            eval_error: "No s'ha pogut carregar l'avaluació per avaluar",
            eval_min_score: 'Puntueu almenys un domini',
            eval_save_error: "No s'han pogut desar les avaluacions de domini",
            review_error: "No s'ha pogut enviar per revisió",
            finalize_error: "No s'ha pogut finalitzar l'avaluació",
            start: 'Iniciar',
            start_confirm_title: 'Iniciar avaluació',
            start_confirm_message: 'Esteu segur que voleu iniciar aquesta avaluació?',
            archive_confirm_title: 'Arxivar avaluació',
            archive_confirm_message: 'Esteu segur que voleu arxivar aquesta avaluació?',
            evaluate_domains: 'Avaluar',
            evaluate_domains_title: 'Avaluar dominis',
            submit_for_review: 'Enviar per revisió',
            finalize: 'Finalitzar',
            archive: 'Arxivar',
            all_reviewers: "Tots els revisors han d'aprovar",
            any_reviewer: 'Qualsevol revisor pot aprovar',
            select_level: 'Seleccioneu el nivell de maduresa...',
            findings_placeholder: "Troballes, notes, referències d'evidència...",
            no_domains: "No s'han trobat dominis per a aquest model.",
        },
        trend_dashboard: {
            title: 'Tauler de tendències',
            loading: 'Carregant trend data...',
            score_trend: 'Tendència de puntuació',
            domain_comparison: 'Comparació de dominis',
            period: 'Període',
            load_error: "No s'ha pogut carregar el resum de tendència. Torneu-ho a provar.",
            metrics_load_error: "No s'han pogut carregar les mètriques. Torneu-ho a provar.",
            module: 'Mòdul',
            metric: 'Mètrica',
            value: 'Valor',
            trend: 'Tendència',
            aria_filters: 'Filtres de mètriques de tendència',
            showing_chart: 'Mostrant vista de gràfic',
            showing_table: 'Mostrant vista de taula',
            create_metric: 'Crea una mètrica',
            data_points: 'Punts de dades',
            trend_direction: 'Direcció de la tendència',
            unit: 'Unitat',
        },
        reporting_periods: {
            title: "Períodes d'informe",
            new: 'Nou període',
            period_name: 'Període Nom',
            start_date: 'Inici Data',
            end_date: 'Fi Data',
            no_periods: "No s'han trobat reporting periods",
            load_error: "No s'han pogut carregar els períodes. Torneu-ho a provar.",
            create_error: "No s'ha pogut crear el període",
            auto_generate_error: "No s'han pogut generar automàticament els períodes",
            status_error: "No s'ha pogut actualitzar l'estat del període",
            start_collecting: 'Començar a recollir',
            mark_as_draft: 'Marcar com a esborrany',
            submit_for_review: 'Enviar per revisió',
            publish: 'Publicar',
            auto_generate: "Generar automàticament períodes d'informe",
            period_label_placeholder: 'p. ex., T1 2026',
            status_updated: 'Estat del període actualitzat',
        },
        executive_summaries: {
            title: 'Resums executius',
            new: 'Nou resum',
            period: 'Període',
            generated_at: 'Generat el',
            generated_by: 'Generat per',
            no_summaries: "No s'han trobat executive summaries",
            generate: 'Generar',
            load_error: "No s'han pogut carregar els resums executius. Torneu-ho a provar.",
            generate_error: "No s'ha pogut generar el resum executiu",
            save_error: "No s'ha pogut desar el resum executiu",
        },
        audit_readiness: {
            title: 'Preparació per a auditoria',
            loading: 'Carregant audit readiness data...',
            overall_readiness: 'Preparació general',
            by_domain: 'Per Domini',
            evidence_coverage: "Cobertura d'evidències",
            gaps: 'Bretxes',
            load_error: "No s'ha pogut carregar l'última preparació. Torneu-ho a provar.",
            history_error: "No s'ha pogut carregar l'historial. Torneu-ho a provar.",
            calculate_error: "No s'ha pogut calcular la preparació",
            aria_recalculate: 'Recalcular la puntuació de preparació',
            aria_current_readiness: 'Preparació actual',
            aria_history: 'Historial de puntuació de preparació',
            recalculated: "Preparació per a l'auditoria recalculada",
            loading_sr: 'Carregant dades de preparació',
        },
        evidence_gaps: {
            title: "Llacunes d'evidència",
            domain: 'Domini',
            control: 'Control',
            gap_type: 'Tipus de bretxa',
            severity: 'Gravetat',
            no_gaps: "No s'han trobat evidence gaps",
            load_error: "No s'han pogut carregar els buits d'evidència. Torneu-ho a provar.",
            stats_error: "No s'han pogut carregar les estadístiques. Torneu-ho a provar.",
            create_error: "No s'ha pogut crear el buit d'evidència",
            update_error: "No s'ha pogut actualitzar el buit",
            status_error: "No s'ha pogut actualitzar l'estat",
            aria_create_new: "Crear nou buit d'evidència",
            aria_statistics: "Estadístiques de buits d'evidència",
            loading_sr: "Carregant buits d'evidència",
            status_updated: 'Estat del buit actualitzat',
            entity_type_placeholder: 'p. ex., proveïdor, sistema',
            all_modules: 'Tots els dominis',
            all_severities: 'Totes les gravetats',
        },
        program_health: {
            title: 'Estat del programa',
            loading: 'Carregant program health data...',
            overall_health: 'Salut general',
            by_module: 'Per mòdul',
            risk_areas: 'Àrees de risc',
            recommendations: 'Recomanacions',
            load_error: "No s'han pogut carregar les dades. Torneu-ho a provar.",
            aria_key_metrics: 'Mètriques clau de salut del programa',
            aria_module_scorecard: 'Desglossament de puntuació per mòdul',
            loading_sr: 'Carregant dades de salut del programa',
            trending_up: "Tendència a l'alça",
            trending_down: 'Tendència a la baixa',
            trending_stable: 'Estable',
            data_loaded: 'Dades de salut del programa carregades',
        },
        obligation_mappings: {
            title: "Assignacions d'obligacions",
            create_mapping: "Crear assignació d'obligació",
            framework: 'Marc normatiu',
            select_framework: 'Seleccionar un marc...',
            obligation: 'Obligació',
            obligation_ref: "Referència d'obligació",
            obligation_description: "Descripció d'obligació",
            role: 'Rol',
            assigned_role: 'Rol assignat',
            select_role: 'Selecciona un rol...',
            assigned_department: 'Departament assignat',
            select_department: 'Seleccionar un departament...',
            control_ids: 'IDs de control',
            control_ids_placeholder: 'p. ex. A.5.1, CC6.1',
            control_ids_hint: 'Codis de referència de control separats per comes.',
            raci_assignments: 'Assignacions RACI',
            create_raci: 'Crear assignació RACI',
            activity: 'Activitat',
            activity_name: "Nom de l'activitat",
            activity_description: "Descripció de l'activitat",
            responsible: 'Responsable',
            select_users: 'Seleccionar usuaris...',
            accountable: 'Rendidor de comptes',
            select_user: 'Seleccionar un usuari...',
            consulted: 'Consultat',
            informed: 'Informat',
            review_frequency: 'Freqüència de revisió',
            frequency_monthly: 'Mensual',
            frequency_quarterly: 'Trimestral',
            frequency_semi_annual: 'Semestral',
            frequency_annual: 'Anual',
            no_mappings_found: "No s'han trobat assignacions d'obligacions.",
            no_raci_found: "No s'han trobat assignacions RACI.",
            mapping_created: "Mapatge d'obligació creat",
            mapping_updated: "Assignació d'obligació actualitzada",
            mapping_deleted: "Assignació d'obligació eliminada",
            raci_created: 'Assignació RACI creada',
            raci_updated: 'Assignació RACI actualitzada',
            raci_deleted: 'Assignació RACI eliminada',
            aria_edit_mapping: 'Editar assignació {name}',
            aria_delete_mapping: 'Eliminar assignació {name}',
            aria_edit_raci: 'Editar RACI {name}',
            aria_delete_raci: 'Eliminar RACI {name}',
            confirm_delete_mapping: "Eliminar aquesta assignació d'obligació?",
            confirm_delete_raci: 'Eliminar aquesta assignació RACI?',
            table_view: 'Vista de taula',
            matrix_view: 'Vista de matriu',
            print_export: 'Imprimeix / Exporta',
            filter_framework: 'Filtra per marc',
            no_matrix_data: 'No hi ha assignacions RACI per mostrar a la vista de matriu.',
            aria_print_export: 'Imprimeix o exporta la matriu RACI',
        },
        governance_documents: {
            title: 'Documents de governança',
            create_document: 'Crear document de governança',
            document_title: 'Títol del document',
            document_type: 'Tipus de document',
            content_url: 'URL del contingut',
            content_url_placeholder: 'https://...',
            framework_codes: 'Codis de marc',
            select_frameworks: 'Seleccionar marcs...',
            effective_date: "Data d'efectivitat",
            review_date: 'Data de revisió',
            status: 'Estat',
            version: 'Versió',
            acknowledgments: 'Confirmacions',
            acknowledge: 'Confirmar',
            acknowledged_by: 'Confirmat per',
            department: 'Departament',
            no_documents_found: "No s'han trobat documents de governança.",
            draft: 'Esborrany',
            review: 'Revisió',
            approved: 'Aprovat',
            issued: 'Emès',
            superseded: 'Substituït',
            document_created: 'Document creat',
            document_acknowledged: 'Document reconegut',
            policy: 'Política',
            procedure: 'Procediment',
            standard: 'Estàndard',
            guideline: 'Directriu',
        },
        governance_model: {
            title: 'Model de governança',
            create_model: 'Model de governança',
            edit_model: 'Model de governança',
            model_name: 'Model',
            description: 'Descripció',
            governance_type: 'Type',
            type_centralized: 'Centralitzat',
            type_federated: 'Federat',
            type_hybrid: 'Híbrid',
            framework_codes: 'Framework',
            select_frameworks: '...',
            review_frequency: 'Frequency',
            freq_monthly: 'Mensualment',
            freq_quarterly: 'Trimestral',
            freq_semi_annual: 'Semestral',
            freq_annual: 'Anual',
            committees: 'Comitès',
            committee_name: 'Name',
            committee_members: 'Members',
            committee_mandate: 'Mandat',
            add_committee: '+',
            no_committees: '—',
            delegation_rules: 'Delegation',
            from_role: 'From',
            to_role: 'To',
            scope: 'Àmbit',
            add_delegation_rule: '+',
            control_owner_summary: 'RACI',
            no_raci_data: '—',
            activity: 'Activitat',
            responsible: 'R',
            accountable: 'A',
            consulted: 'C',
            informed: 'I',
            no_models_found: '—',
            model_created: 'OK',
            model_updated: 'OK',
            model_deleted: 'OK',
            confirm_delete: '?',
            expand: '+',
            collapse: '-',
            aria_expand: '{name}',
            aria_edit: '{name}',
            aria_delete: '{name}',
            aria_export_csv: '{name} CSV',
            aria_export_json: '{name} JSON',
            framework_read_permission_warning: "Necessites el regulatory_framework:permís de lectura per seleccionar marcs. Demana a un administrador que t'ho concedeixi.",
            select_members: 'Selecciona els membres del comitè',
            select_from_role: 'Seleccioneu la funció de delegació',
            select_to_role: 'Seleccioneu el rol receptor',
            mandate_markdown_hint: 'Reducció admesa: **negreta**, llistes, enllaços.',
            scope_tab_obligation: 'Obligació',
            scope_tab_framework: 'Marc',
            scope_tab_free_text: 'Text lliure',
            select_obligation: 'Seleccioneu una obligació',
            select_framework: 'Seleccioneu un marc',
            aria_remove_committee: 'Elimina el comitè {n}',
            aria_remove_delegation_rule: 'Elimina la regla de delegació {n}',
            no_frameworks_available: 'No hi ha marcs disponibles. Afegiu primer marcs reguladors.',
            confirm_discard_changes: 'Discard your unsaved governance model changes?',
        },
        document_drafting: {
            title: 'Redacció de documents',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Estat',
            drafter: 'Dibuixant',
            deadline: 'Data límit',
            assign_drafter: 'Assign',
            select_drafter: '...',
            assign: 'Assignar',
            submit_for_review: 'Submit',
            legal_review: 'Review',
            reviewer: 'Revisor',
            select_reviewer: '...',
            comments: 'Comentaris',
            review_action: 'Action',
            approve: 'Aprovar',
            request_changes: 'Sol·licitar canvis',
            submit_review: 'Submit',
            executive_approval: 'Approval',
            no_drafts_found: '—',
            drafter_assigned: 'OK',
            status_updated: 'OK',
            review_approved: 'OK',
            changes_requested: 'OK',
            document_approved: 'OK',
            aria_assign: '{name}',
            aria_submit_review: '{name}',
            aria_review: '{name}',
            aria_approve: '{name}',
        },
        document_issuance: {
            title: 'Emissió de documents',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Estat',
            version: 'Versió',
            effective_date: 'Date',
            distribution_scope: 'Distribution',
            distribution_placeholder: '...',
            issuance_number: 'Number',
            issuance_number_placeholder: 'ISS-2026-001',
            supersedes: 'Supersedes',
            none: '—',
            issue_document: 'Issue',
            ready_for_issuance: 'Ready',
            issued_documents: 'Issued',
            no_issued_documents: '—',
            view_lineage: 'Lineage',
            supersession_lineage: 'Supersession',
            document_issued: 'OK',
            document_superseded: 'OK',
            aria_issue: '{name}',
            aria_view_lineage: '{name}',
            no_approved_documents: 'No approved documents available to issue.',
        },
        acknowledgment_tracking: {
            title: 'Seguiment de confirmacions',
            document_title: 'Document',
            acknowledged_count: 'Reconegut',
            pending_count: 'Pendent',
            percentage: '%',
            details: 'Detalls',
            send_reminder: 'Remind',
            acknowledgment_details: 'Details',
            user: 'Usuari',
            department: 'Departament',
            acknowledged_at: 'Date',
            status: 'Estat',
            acknowledged: 'OK',
            no_issued_documents: '—',
            no_acknowledgments: '—',
            reminder_sent: 'OK',
            aria_expand: '{name}',
            aria_send_reminder: '{name}',
            // Fix sprint #447 W3 — recipients, acknowledging, and the reminder count.
            // English until Phase 25 (R-57).
            recipients: 'Recipients',
            record_recipients: 'Record recipients',
            aria_record_recipients: 'Record recipients for {name}',
            recipients_dialog_title: 'Recipients of {name}',
            recipients_dialog_help: 'Choose the people asked to acknowledge this document. Only people whose role can open Acknowledgment Tracking are listed, because only they can acknowledge.',
            recipients_none_eligible: 'No one in your organization holds a role that can acknowledge documents.',
            recipients_saved: 'Recipients recorded',
            recipients_not_recorded: 'Not recorded',
            acknowledge: 'Acknowledge',
            aria_acknowledge: 'Acknowledge {name}',
            acknowledge_dialog_title: 'Acknowledge {name}',
            acknowledge_dialog_body: 'You confirm that you have read this document. Your name and the time are recorded.',
            acknowledge_department: 'Department (optional)',
            reminder_sent_count: 'Reminder sent to {count} of {pending} people who have not acknowledged',
            export_documents_count: 'Documents',
            export_acknowledgments_count: 'Acknowledgments',
        },
        change_log: {
            title: 'Registre de canvis',
            date: 'Data',
            document: 'Document',
            change_type: 'Type',
            actor: 'Actor',
            summary: 'Resum',
            export_csv: 'Exporta CSV',
            filters: 'Filters',
            filter_document: 'Document',
            all_documents: 'All',
            filter_change_type: 'Type',
            all_types: 'All',
            date_from: 'From',
            date_to: 'To',
            version_history: 'History',
            version_archive: 'Archive',
            no_entries: '—',
            no_history: '—',
            exported: 'OK',
            aria_export: 'CSV',
            aria_view_history: '{name}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 — `DocumentChangeType` labels; the page showed raw codes.
            type_created: 'Created',
            type_status_transition: 'Status changed',
            type_fields_updated: 'Fields updated',
            type_superseded: 'Superseded',
            type_acknowledged: 'Acknowledged',
            // fix-sprint #447 round 2 — metadata line of the server-rendered export.
            export_generated_at: 'Generated at',
        },
        review_cadence: {
            title: 'Cadència de revisió',
            configure_cadence: 'Cadència de revisió',
            edit_cadence: 'Cadència de revisió',
            document: 'Document',
            select_document: '...',
            review_frequency: 'Frequency',
            freq_30: '30',
            freq_60: '60',
            freq_90: '90',
            freq_180: '180',
            freq_365: '365',
            last_reviewed: 'Last',
            next_review: 'Next',
            status: 'Estat',
            overdue: 'Endarrerit',
            on_track: 'OK',
            days: 'days',
            horizon: 'Horizon',
            overdue_warning: 'Overdue',
            overdue_count: '{count}',
            no_cadences: '—',
            cadence_created: 'OK',
            cadence_updated: 'OK',
            aria_edit: '{name}',
        },
        remediation_plans: {
            title: 'Plans de remediació',
            create_plan: 'Crear pla de remediació',
            gap_id: 'Referència de llacuna',
            select_gap: 'Seleccionar una llacuna...',
            plan_title: 'Títol',
            description: 'Descripció',
            priority: 'Prioritat',
            priority_critical: 'Crítica',
            priority_high: 'Alta',
            priority_medium: 'Mitjana',
            priority_low: 'Baixa',
            assigned_to: 'Assignat a',
            select_user: 'Seleccionar un usuari...',
            target_date: 'Data objectiu',
            framework_code: 'Marc',
            select_framework: 'Seleccionar un marc...',
            gap_finding: 'Troballa de llacuna',
            action_items: "Elements d'acció",
            owner: 'Propietari',
            deadline: 'Data límit',
            planned: 'Planificat',
            in_progress: 'En curs',
            completed: 'Completat',
            verified: 'Verificat',
            no_plans_found: "No s'han trobat plans de remediació.",
            evidence: 'Evidència',
            plan_created: 'Pla de remediació creat',
            action_items_hint: "Elements d'acció (un per línia)",
        },
        statuses: {
            identified: 'Identificat',
            assigned: 'Assignat',
            in_remediation: 'En Reparació',
            resolved: 'Solucionat',
            accepted_risk: 'Risc acceptat',
            planned: 'Planificada',
            in_progress: 'En curs',
            under_review: 'En revisió',
            finalized: 'Finalitzat',
            archived: 'Arxivat',
            draft: 'Esborrany',
            active: 'Actius',
            upcoming: 'Properament',
            collecting: 'Col·leccionant',
            published: 'Publicat',
            review: 'Revisió',
            approved: 'Aprovat',
            issued: 'Emès',
            superseded: 'Substituït',
            completed: 'Completat',
            verified: 'Verificat',
        },
        severities: {
            critical: 'Crític',
            high: 'Alt',
            medium: 'Mitjana',
            low: 'Low',
        },
        priorities: {
            critical: 'Crític',
            high: 'Alt',
            medium: 'Mitjana',
            low: 'Low',
        },
        gap_types: {
            missing: 'Falta',
            expired: 'Caducat',
            insufficient: 'Insuficient',
        },
        document_types: {
            policy: 'Política',
            procedure: 'Procediment',
            standard: 'Estàndard',
            guideline: 'Directriu',
            template: 'Plantilla (interna)',
        },
        maturity_levels: {
            level_1: 'Nivell 1 — Inicial',
            level_2: 'Nivell 2 — Desenvolupament',
            level_3: 'Nivell 3 — Definit',
            level_4: 'Nivell 4 — Gestionat',
            level_5: 'Nivell 5 — Optimització',
        },
        grades: {
            excellent: 'Excel·lent',
            good: 'Bé',
            fair: 'Fira',
            poor: 'Pobre',
            critical: 'Crític',
            needs_improvement: 'Necessita una millora',
            not_assessed: 'No avaluat',
        },
        modules: {
            data_mapping: 'Mapatge de dades',
            consent: 'Consentiment',
            governance: 'Governança',
            incidents: 'Incidències',
            monitoring: 'Seguiment',
            classification: 'Classificació',
            vendor_risk: 'Risc del venedor',
            discovery: 'Descobriment',
            deletion: 'Supressió',
        },
        cadences: {
            monthly: 'Mensualment',
            quarterly: 'Trimestral',
            semi_annual: 'Semestral',
            annual: 'Anual',
        },
        section_labels: {
            opening: 'Obertura',
            key_metrics: 'Mètriques clau',
            highlights: 'Destacats',
            concerns: 'Preocupacions',
            recommendations: 'Recomanacions',
            outlook: 'Outlook',
        },
        out_of_100: '/ 100',
        count_gaps: '{count} buit',
        count_gaps_plural: '{count} buits',
        count_summaries: '{count} resum',
        count_summaries_plural: '{count} resums',
        count_assessments: '{count} avaluació',
        count_assessments_plural: '{count} avaluacions',
        count_models: 'Model {count}.',
        count_models_plural: '{count} models',
        count_periods: '{count} punt',
        count_periods_plural: '{count} períodes',
        count_metrics: '{count} mètrica',
        count_metrics_plural: '{count} mètriques',
        unknown_user: 'Usuari desconegut',
        unknown_gap: 'Gap desconegut',
        no_permission_title: 'Permisos insuficients',
        no_permission_message: 'No teniu permís per accedir a aquesta pàgina.',
        ac_vn_frameworks_title: 'Cobertura del marc VN',
        ac_vn_frameworks_body: "Les avaluacions de maduresa cobreixen els 6 marcs reguladors vietnamites: PDPL, llei de dades, llei de telecomunicacions, llei de ciberseguretat, AI llei i llei de comerç electrònic, cadascun amb criteris d'avaluació específics del marc.",
        ac_controller_processor_title: 'Controlador vs. processador',
        ac_controller_processor_body: "Els mapes d'obligacions distingeixen entre les responsabilitats del responsable del tractament i les del processador de dades. Assigneu rols mitjançant la matriu RACI per assegurar-vos que cada obligació té un propietari clar, tant si l'organització actua com a controlador o encarregat del tractament.",
        ac_versioned_evidence_title: "Ruta d'evidència versionada",
        ac_versioned_evidence_body: "Cada transició d'estat (Esborrany, Revisió, Aprovat, Emès) crea un registre amb segell de temps i versions que serveix com a evidència auditable. El registre de canvis captura l'historial complet del cicle de vida del document.",
        ac_remediation_gap_link_title: "Vinculat a buits d'avaluació",
        ac_remediation_gap_link_body: "Cada pla de reparació enllaça directament amb una bretxa d'avaluació identificada. Assigna un propietari i estableix un termini objectiu per fer un seguiment del progrés de la resolució des del buit fins al tancament.",
        ac_annual_attestation_title: "Cicle d'Acreditació Anual",
        ac_annual_attestation_body: "Els períodes d'informe s'alineen amb el requisit d'acreditació anual de la Llei de dades vietnamita. Utilitzeu la cadència \"Anual\" (cicle de 365 dies) per generar períodes alineats amb l'acreditació automàticament.",
        ac_evidence_per_obligation_title: 'Evidència per Obligació Marc',
        ac_evidence_per_obligation_body: "Les llacunes d'evidència identifiquen proves faltes, caduques o insuficients per a cada obligació marc. Filtreu per domini per veure quins requisits reglamentaris no tenen proves de suport.",
        ac_cross_framework_title: 'Completitud del marc creuat',
        ac_cross_framework_body: "La preparació per a l'auditoria proporciona una visió de l'exhaustivitat del marc transversal. Les puntuacions dels components reflecteixen la cobertura de tots els marcs assignats, destacant les àrees que necessiten atenció abans d'una auditoria.",
        ac_cadence_default_title: 'Cadència de revisió per defecte',
        ac_cadence_default_body: 'La cadència de revisió predeterminada és de 180 dies. Les revisions vençudes es marquen automàticament. Ajusteu la freqüència per document segons sigui necessari: les opcions oscil·len entre 30 i 365 dies.',
        ac_templates_internal_title: 'Classificació de plantilles',
        ac_templates_internal_body: 'Les plantilles es classifiquen com a documents interns. Utilitzeu el tipus de document "Plantilla" per marcar els artefactes de govern reutilitzables amb un àmbit de distribució només intern.',
        // fix-sprint-20260822-2115 F1/F3 — this panel used to carry a module-local
        // `const COPY = { en: {…}, vi: {…} }` and pick a branch with
        // `locale.startsWith('vi') ? 'vi' : 'en'`. Both shapes are named in
        // I18N.md §2 and §3 as the root cause of 14 locales rendering English, and
        // the strings were registered in no bundle at all, so deepMerge had nothing
        // to fall back to. The four enum label maps below close F3: `status`,
        // `legal_review_status`, `evidence_state` and `answer_state` were rendering
        // raw slugs next to an already-labelled badge on the same row.
        handbook_assessment: {
            ask_forseti: 'Ask Forseti about this checklist',
            candidate_link: 'Link this record',
            candidate_linked: 'Already linked',
            candidate_linking: 'Linking…',
            candidate_no_date: 'No date recorded',
            candidate_open: 'Open record',
            evidence_kind_manual: 'Manual entry',
            evidence_kind_platform: 'Platform record',
            link_manual_fallback: 'If none of the above applies, record the evidence manually.',
            reason_module_not_readable: 'The regulatory pack names a module this platform build cannot read for evidence. Link the record manually instead.',
            reason_module_not_permitted: 'Your role cannot read the module that holds this evidence. Ask an administrator for access, or link the record manually.',
            reason_no_matching_record: 'No record of this kind exists in this organization yet.',
            reason_record_not_accepted: 'A record exists but is not in an accepted state. Complete or approve it, then resolve again.',
            source_form: 'Form',
            source_module: 'Module',
            source_role: 'Evidence role',
            sources_error: 'The platform sources for this question could not be loaded.',
            sources_heading: 'Evidence the platform can supply',
            sources_intro: 'The regulatory pack declares which of your own records can satisfy this question. These are the matching records this organization holds.',
            sources_loading: 'Loading platform sources…',
            unreconciled_question: 'This question is not on this assessment\u2019s own record and cannot be answered here: {question}',
            sources_toggle: 'Show platform sources',
            collapse_all: 'Collapse all questions',
            crosslink_action: 'Open handbook assessments',
            crosslink_body: 'A maturity assessment scores your programme against a maturity model. The official handbook checklist is a separate record: it collects evidence against a regulatory handbook and deliberately produces no score.',
            crosslink_title: 'Official handbook evidence checklist',
            expand_all: 'Expand all questions',
            group_progress: '{answered} of {total} answered',
            module_guide_summary: 'Complete an official regulatory handbook evidence checklist. Each question records an answer, an evidence state and the supporting records held in the platform. The checklist produces no compliance score — a human legal review closes it.',
            page_description: 'Official regulatory handbook evidence checklists. Each question records an answer, an evidence state, and the platform records that support it.',
            page_title: 'Handbook Assessments',
            question_fields: 'Question response',
            loading: 'Loading official handbook assessment',
            load_error: 'The official handbook assessment could not be loaded.',
            start: 'Start official handbook assessment',
            empty: 'No official handbook assessment has been started for this organization.',
            progress: 'questions answered',
            no_score: 'This evidence checklist does not calculate or claim a legal-compliance score. Human legal review is required.',
            save: 'Save answer',
            saving: 'Saving…',
            resolve: 'Resolve existing evidence',
            resolving: 'Resolving…',
            evidence: 'Evidence state',
            answer: 'Answer',
            notes: 'Notes and applicability rationale',
            linked: 'Linked evidence',
            no_evidence: 'No evidence is linked yet.',
            manual: 'Link manual evidence',
            description: 'Evidence description',
            source: 'Evidence source',
            owner: 'Evidence owner',
            date: 'Evidence date',
            url: 'Evidence URL',
            invalid_url: 'Enter a complete http:// or https:// URL.',
            link: 'Link evidence',
            linking: 'Linking…',
            remediate: 'Create remediation task',
            remediating: 'Creating task…',
            review_question: 'Mark question reviewed',
            reviewing: 'Reviewing…',
            submit: 'Submit all 30 questions for review',
            finalize: 'Complete legal review',
            return_revision: 'Return for revision',
            export: 'Export reviewed evidence report',
            exporting: 'Exporting…',
            status: 'Assessment status',
            legal_review: 'Legal review',
            saved: 'Answer saved.',
            resolved: 'Existing evidence resolution completed.',
            linked_done: 'Evidence linked for review.',
            remediation_done: 'Remediation task created.',
            review_done: 'Review state updated.',
            exported: 'Reviewed evidence report exported.',
            export_format: 'Format',
            export_language: 'Language',
            export_format_pdf: 'PDF',
            export_format_docx: 'DOCX',
            export_language_en: 'English',
            export_language_vi: 'Tiếng Việt',
            export_language_bilingual: 'English / Tiếng Việt',
            answer_state: {
                unanswered: 'Unanswered',
                yes: 'Yes',
                no: 'No',
                unknown: 'Unknown',
                not_applicable: 'Not applicable',
            },
            assessment_status: {
                in_progress: 'In progress',
                under_review: 'Under review',
                reviewed: 'Reviewed',
                archived: 'Archived',
            },
            legal_review_status: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                revision_requested: 'Revision requested',
            },
            evidence_state: {
                resolved: 'Resolved',
                missing: 'Missing',
                stale: 'Stale',
                unsupported: 'Unsupported',
                manual_pending_review: 'Manual — pending review',
            },
        },
    },
    mcp_pending: {
        title: 'MCP — Escriptures pendents',
        subtitle: 'Els clients Claude externs proposen escriptures aquí. Aproveu o refuseu cada entrada — Forseti només executa després de la validació de l’administrador.',
        no_pending: 'No hi ha escriptures pendents.',
        filter_status_label: 'Filter by status',
        filter_all: 'All',
        no_results_for_status: 'No writes with this status.',
        col_tool: 'Eina',
        col_preview: 'Previsualització',
        col_key: 'Tecla API.',
        col_requested: 'Sol·licitada',
        col_expires: 'Expira',
        col_status: 'Estat',
        status_pending: 'Pendent',
        status_approved: 'Aprovada',
        status_declined: 'Refusada',
        status_expired: 'Expirada',
        status_executed: 'Executada',
        status_failed: 'Failed — nothing written',
        approve_failed: 'Approved, but the write did NOT happen — {tool} was not carried out ({code}). Nothing was created or changed.',
        approve_executed: '{tool} was carried out.',
        approve_label: 'Aprovar',
        decline_label: 'Refusar',
        refresh_label: 'Actualitzar',
        error_generic: "No s'han pogut carregar les escriptures pendents.",
        confirm_approve: "L'aprovació executarà l'eina sobre la vostra organització amb els permisos del titular de l'API key. Voleu continuar?",
        confirm_decline: 'Voleu refusar aquesta escriptura pendent? Aquesta acció no es pot desfer.',
        // Sprint 64.123.0 — approving a queued MCP write now mints the human
        // confirmation the tool manifest requires, so the surface has to say what
        // that approval is worth, and when it is self-attestation.
        confirmation_note: 'Approving records the human confirmation these tools require, and the write is then carried out with the API key holder’s permissions — not yours. Where you proposed the write yourself, your approval is recorded as self-approved and is not an independent review.',
        self_approval_badge: 'You proposed this write — approving it is self-attestation, not a second review.',
        confirm_approve_self: 'You proposed this write. Approving it records YOUR OWN confirmation — no second person will review it. The audit trail will show it as self-approved. Continue?',
        advisory_banner: 'Les escriptures pendents caduquen 24 hores després de la creació. El barrido GC horari refusa automàticament les entrades caducades.',
        consequential_label: 'Regulatorily consequential filing',
        consequential_target: 'Target record',
        consequential_authority: 'Authority',
        consequential_authority_unknown: 'Not declared by the installed template',
        consequential_prior: 'Prior',
        consequential_proposed: 'Proposed',
        consequential_empty: '(none)',
        consequential_unresolved: 'The target record could not be read when this was queued —',
        consequential_captured_at: 'Values read at',
        // fix-sprint-20261003-1200 F5 — the approve refusals, by server code.
        refusal_proposer_lacks_forseti: 'This write was not carried out: the role of the person who proposed it no longer allows Forseti, so it cannot run as them. Nothing was changed. Decline it to remove it from the queue.',
        refusal_proposer_inactive: 'This write was not carried out: the person who proposed it is no longer an active member of your organisation. Nothing was changed. Decline it to remove it from the queue.',
        refusal_key_inactive: 'This write was not carried out: the API key or connected app that proposed it may no longer make it. Nothing was changed. Decline it to remove it from the queue.',
        refusal_already_resolved: 'Someone has already resolved this write. Refresh to see its current status.',
        refusal_expired: 'This write has expired and can no longer be approved.',
        refusal_state_changed: 'This write changed status while you were approving it. Refresh to see its current status.',
        decline_dialog_title: 'Decline pending write',
        decline_reason_label: 'Reason for declining',
        decline_reason_hint: 'Required, up to 500 characters. It is recorded with your decision.',
        decline_submit: 'Decline write',
        decline_cancel: 'Keep pending',
        col_actions: 'Accions',
    },
    audit_walkthrough: {
        title: 'Forseti — Generador de recorregut d’auditoria',
        subtitle: 'Genereu un recorregut estructurat per a l’inici d’una auditoria: pregunta d’obertura per control, evidència esperada, preguntes de seguiment, possibles llacunes.',
        audit_picker_label: 'Auditoria',
        audit_picker_hint: 'Trieu una auditoria planificada per generar un recorregut.',
        run_label: 'Generar recorregut',
        running: 'Generant…',
        results_heading: 'Recorregut — {count} control(s)',
        tokens_label: '≈ {count} fitxes',
        no_controls: 'No hi ha controls a l’abast. Afegiu controls al registre d’auditoria.',
        col_control: 'Control',
        col_question: 'Pregunta d’obertura',
        col_evidence: 'Evidència',
        col_gaps: 'Possibles llacunes',
        expected_evidence_label: 'Evidència esperada',
        followup_probes_label: 'Preguntes de seguiment',
        accept_label: 'Acceptar el recorregut',
        reject_label: 'Refusar',
        export_label: 'Exportar DOCX',
        resolve_summary: 'Resolució: {status}',
        resolve_status_accepted: 'Acceptat',
        resolve_status_rejected: 'Rebutjat',
        controls_range: 'Controls {from}–{to} of {total}',
        next_page_label: 'Generate next controls',
        error_generic: "No s'ha pogut generar el recorregut. Torneu-ho a provar o reviseu l'estat de l'auditoria.",
    },
    privilege_creep: {
        title: 'Forseti — Detecció d’acumulació de privilegis',
        subtitle: 'Anàlisi periòdica de les assignacions de rols. Els resultats són consultius — Forseti mai no revoca de manera automàtica.',
        run_label: 'Analitzar ara',
        running: 'Analitzant…',
        findings_heading: '{count} resultat(s)',
        no_findings: 'No s’ha detectat acumulació de privilegis.',
        col_user: 'Usuari',
        col_job_function: 'Funció',
        col_severity: 'Gravetat',
        col_unexpected: 'Permisos inesperats',
        col_remediation: 'Remediació suggerida',
        col_status: 'Estat',
        severity_critical: 'Crítica',
        severity_high: 'Alta',
        severity_medium: 'Mitjana',
        severity_low: 'Baixa',
        status_open: 'Obert',
        status_dismissed: 'Descartat',
        status_remediated: 'Remediat',
        dismiss_label: 'Descartar',
        remediate_label: 'Marcar com a remediat',
        note_label: 'Nota (opcional)',
        error_generic: "No s'ha pogut analitzar l'acumulació de privilegis. Torneu-ho a provar més tard.",
        advisory_banner: "Aquests resultats són consultius. Forseti mai no revoca permisos automàticament — reviseu-ho amb el propietari del rol abans d'actuar.",
    },
    compliance_rules: {
        rule_mapping: {
            title: 'Mapeig automàtic de regles de compliment',
            subtitle: "Forseti recorre un paquet regulador instal·lat i suggereix regles de compliment noves o actualitzades basades en els articles d'origen.",
            pack_picker_label: 'Paquet normatiu',
            pack_picker_hint: 'Seleccioneu un paquet instal·lat per mapejar.',
            run_label: 'Mapa del pack',
            running: 'Mapeig...',
            results_heading: '{count} suggeriment(s)',
            tokens_label: '≈ {count} fitxes del teu cub mensual',
            no_suggestions: 'Sense suggeriments: és possible que el contingut del paquet ja estigui totalment cobert per les regles existents.',
            kind_new: 'Nova regla',
            kind_refresh: 'Already covered (review only)',
            kind_no_change: 'No es requereix cap canvi',
            col_kind: 'amable',
            col_title: 'Títol proposat',
            col_existing: 'Norma existent',
            col_source: 'Font',
            proposed_body_label: 'Òrgan proposat',
            source_quote_label: 'Cita de la font',
            accept_selected: 'Accepta seleccionat',
            reject_all: 'Rebutja-ho tot',
            accept_all: 'Acceptar-ho tot',
            resolve_summary: 'Resolution: {status} · {created} draft rule(s) created · {skipped} left unchanged because a rule already covers the requirement',
            error_generic: "No s'ha pogut executar l'assignació de regles. Torneu-ho a provar o comproveu el codi del paquet.",
            write_permission_hint: 'Accepting suggestions needs permission to edit regulatory frameworks. You can still reject the run.',
            examined_label: 'Read passages {from}–{to} of the {total} that this pack search returns, ranked by one fixed search. A new run starts again at passage 1.',
            examined_remaining: '{remaining} passage(s) have not been read yet. Use Read next passages to continue from where this run stopped.',
            read_next_label: 'Read next passages',
            read_next_blocked: 'Accept or reject the suggestions above first; the next passages are read after this draft is resolved.',
            run_blocked: 'Accept or reject the suggestions above first; mapping again from this pack replaces the draft on screen.',
            drafts_hint: 'Accepted suggestions are saved as draft rules in the rule library. A draft rule is not active until someone activates it, and the product has no page for compliance rules yet. A passage that an existing rule already covers is shown for review only and creates nothing.',
        },
    },
    access_accountability: {
        nav: {
            session_dashboard: 'Tauler de sessió',
            access_log: "Registre d'accessos",
            justification_review: 'Revisió de justificacions',
            investigations: 'Investigacions',
            pattern_analysis: 'Anàlisi de patrons',
            anchoring_status: "Estat d'ancoratge",
            configuration: 'Configuració',
            access_policies: "Polítiques d'accés",
            review_campaigns: 'Campanyes de revisió',
            evidence_packages: "Paquets d'evidència",
        },
        session_dashboard: {
            title: 'Tauler de sessió',
            loading: 'Carregant les dades del tauler...',
            total_sessions: 'Total de sessions',
            active_sessions: 'Sessions actives',
            avg_duration: 'Durada mitjana',
            peak_day_sessions: 'Dia amb més activitat',
            auth_method_breakdown: "Desglossament de mètodes d'autenticació",
            geo_country_breakdown: 'Desglossament per país',
            recent_session_events: 'Esdeveniments de sessió recents',
            user_id: "ID d'usuari",
            session_id: 'ID de sessió',
            ip_address: 'Adreça IP',
            auth_method: "Mètode d'autenticació",
            started_at: 'Iniciat el',
            event_type: "Tipus d'esdeveniment",
            all_event_types: "Tots els tipus d'esdeveniment",
            filter_by_user: 'Filtrar per usuari',
            no_active_sessions: 'Sense sessions actives',
            no_session_events: "No s'han trobat esdeveniments de sessió",
            revoke: 'Revocar',
            revoke_session: 'Revocar sessió',
            aria_revoke_session: 'Revocar la sessió {user}',
            revoke_reason: 'Motiu',
            revoke_placeholder: 'Proporcioneu un motiu per revocar aquesta sessió...',
            session_label: 'Sessió:',
            load_failed: 'Error en carregar el tauler de sessió. Si us plau, torneu-ho a intentar.',
            events_load_failed: 'Error en carregar els esdeveniments de sessió. Si us plau, torneu-ho a intentar.',
            revoke_failed: 'Error en revocar la sessió. Si us plau, torneu-ho a intentar.',
            session_events_loaded: '{n} esdeveniments de sessió carregats',
            event_types: {
                session_started: 'Sessió iniciada',
                session_refreshed: 'Sessió actualitzada',
                session_expired: 'Sessió caducada',
                session_revoked: 'Sessió revocada',
                session_terminated: 'Sessió finalitzada',
            },
        },
        access_log: {
            title: "Registre d'accessos",
            user_id: 'Usuari ID',
            resource: 'Recurs',
            action: 'Acció',
            ip_address: 'IP Adreça',
            timestamp: 'Marca de temps',
            no_logs: "No s'han trobat access log entries",
            filter_by_user: 'Filtrar per Usuari ID...',
            load_failed: 'Error en load access log. Si us plau, torneu-ho a intentar.',
            export_success: 'Exportar initiated amb èxit',
            export_failed: 'Error en export access log. Si us plau, torneu-ho a intentar.',
            record_count: 'registre',
            record_count_plural: 'registres',
            unclassified: 'Sense classificar',
            classification_missing: 'Classificació absent',
            classification_level: 'Nivell de classificació',
            response_code: 'Codi de resposta',
            access_surface: "Canal d'accés",
            api_key: 'Clau API',
            classification_levels: {
                public: 'Públic',
                internal: 'Interna',
                confidential: 'Confidencial',
                restricted: 'Restringit',
                unclassified: 'Sense classificar',
            },
        },
        justification_review: {
            title: 'Revisió de justificacions',
            user_id: 'Usuari ID',
            resource: 'Recurs',
            justification: 'Justificació',
            submitted_at: 'Enviat el',
            reviewer: 'Revisor',
            no_reviews: "No s'han trobat justification reviews",
            load_failed: 'Error en load justifications. Si us plau, torneu-ho a intentar.',
            review_failed: 'Error en review justification. Si us plau, torneu-ho a intentar.',
            decline_failed: 'Error en decline access. Si us plau, torneu-ho a intentar.',
            justification_count: 'justificació',
            justification_count_plural: 'justificacions',
            reviewed: 'Revisat',
            concerning: 'Preocupant',
            escalated: 'Escalat',
            link_investigation_placeholder: 'Vincular a una investigació (opcional)',
            review_queue: 'Cua de revisió',
            access_id_label: "ID d'accés",
            access_id_placeholder: "ID d'entrada del registre d'accés",
            resource_type_label: 'Tipus de recurs',
            resource_type_placeholder: 'p. ex. document, database',
            resource_id_label: 'ID del recurs',
            resource_id_placeholder: 'Identificador del recurs',
            reason_placeholder: 'Motiu per accedir a aquest recurs...',
            create_failed: 'Error en create justification',
            unclassified: 'Sense classificar',
            review_statuses: {
                pending: 'Pendent',
                reviewed: 'Revisat',
                concerning: 'Referent',
                escalated: 'Escalada',
            },
            reason_codes: {
                business_need: 'Necessitat empresarial',
                legal_obligation: 'Obligació legal',
                data_subject_request: 'Sol·licitud del subjecte de dades',
                audit_requirement: "Requisit d'auditoria",
                incident_response: 'Resposta a incidents',
                research: 'Recerca',
                administrative: 'Administratiu',
                other: 'Altres',
            },
            classification_level: 'Nivell de classificació',
            review_decision: 'Revisió de la decisió',
        },
        investigations: {
            title: "Espai d'investigació",
            new: 'Nou Investigation',
            subject: 'Titular',
            investigator: 'Investigador',
            opened_at: 'Obert A',
            findings: 'Troballes',
            no_investigations: "No s'han trobat investigations",
            load_failed: 'Error en load investigations. Si us plau, torneu-ho a intentar.',
            create_failed: 'Error en create investigation. Si us plau, torneu-ho a intentar.',
            timeline_add_failed: 'Error en add timeline entry. Si us plau, torneu-ho a intentar.',
            link_access_failed: 'Error en link access ids. Si us plau, torneu-ho a intentar.',
            link_justification_failed: 'Error en link justification ids. Si us plau, torneu-ho a intentar.',
            close_failed: 'Error en close investigation. Si us plau, torneu-ho a intentar.',
            export_success: 'Exportar initiated amb èxit',
            export_failed: 'Error en export investigation. Si us plau, torneu-ho a intentar.',
            refresh_failed: 'Error en refresh investigation detail. Si us plau, torneu-ho a intentar.',
            update_failed: 'Error en actualitzar la investigació',
            investigation_count: 'investigació',
            investigation_count_plural: 'investigacions',
            title_placeholder: 'Títol de la investigació',
            description_placeholder: 'Descripció detallada de la investigació...',
            assignee_placeholder: "ID d'usuari de l'assignat (opcional)",
            timeline_action_placeholder: "p.ex., Registres d'accés revisats",
            timeline_note_placeholder: 'Nota opcional',
            access_ids_placeholder: 'access-id-1, access-id-2',
            justification_ids_placeholder: 'justificació-id-1, justificació-id-2',
            findings_placeholder: 'Resumiu les conclusions de la investigació...',
            by_actor: 'by',
            close_investigation: 'Tancar investigació',
            add_timeline_failed: 'Error en add timeline entry. Si us plau, torneu-ho a intentar.',
            fetch_failed: 'Error en fetch investigations. Si us plau, torneu-ho a intentar.',
            change_status: "Canviar l'estat de la investigació",
            details: 'Detalls de la investigació',
            link_evidence: 'Vincular evidència',
            link_justifications: 'Vincular justificacions',
            access_ids_label: "IDs d'accés",
            justification_ids_label: 'IDs de justificació',
            access_ids_link_placeholder: 'access-id-1, access-id-2',
            justification_ids_link_placeholder: 'justificació-id-1, justificació-id-2',
            investigations_loaded: '{n} investigations carregats',
            status_changed_to: "L'estat ha canviat a {status}",
            add_timeline_entry: 'Afegir entrada de cronologia',
            add_access_ids: "Afegir IDs d'accés",
            add_justification_ids: 'Afegir IDs de justificació',
            investigation_types: {
                anomaly_review: "Revisió d'anomalies",
                complaint_driven: 'Impulsat per denúncies',
                scheduled_review: 'Revisió programada',
                whistleblower_report: 'Informe del denunciant',
            },
            severity_labels: {
                low: 'Baixa',
                medium: 'Mitjana',
                high: 'Alt',
                critical: 'Crític',
            },
            status_labels: {
                open: 'Obert',
                in_progress: 'En curs',
                pending_review: 'Pendent de revisió',
                closed_substantiated: 'Tancat (acreditat)',
                closed_unsubstantiated: 'Tancat (sense fonament)',
                closed_inconclusive: 'Tancat (no concloent)',
            },
            disposition_labels: {
                substantiated: 'Justificada',
                unsubstantiated: 'Sense fonaments',
                inconclusive: 'Inconcloent',
            },
        },
        pattern_analysis: {
            accesses_count: '{n} accesses',
            title: 'Anàlisi de patrons',
            resource_type_header: 'Resource type',
            resource_id_header: 'Resource ID',
            loading: 'Carregant pattern data...',
            anomalies_detected: 'Anomalies detectades',
            patterns: 'Patrons',
            by_user: 'Per Usuari',
            by_resource: 'Per recurs',
            time_range: 'Rang de temps',
            load_failed: 'Error en analyze patterns. Si us plau, torneu-ho a intentar.',
            top_resources: 'Top 10',
            access_by_hour: 'Accés per hora del dia',
            access_by_day: 'Accés per Day de Setmana',
            fetch_failed: 'Error en fetch pattern analysis. Si us plau, torneu-ho a intentar.',
            analysis_loaded: 'Anàlisi de patrons carregada per a {user}, {n} sessions trobades',
            date_format_placeholder: 'DD/MM/AAAA',
            access_by_hour_heading: 'Accés per hora del dia',
            access_by_day_heading: 'Accés per dia de la setmana',
            by_classification: 'Per nivell de classificació',
            top_accessed_resources: 'Recursos més consultats',
            user_access_summary: "Resum d'accés d'usuari",
            days: {
                sunday: 'Diumenge',
                monday: 'Dilluns',
                tuesday: 'Dimarts',
                wednesday: 'Dimecres',
                thursday: 'Dijous',
                friday: 'Divendres',
                saturday: 'Dissabte',
            },
            accesses_in_period: 'Accesses in period',
            sessions_label: 'Sessions',
            recent_accesses: 'Recent accesses in the period',
            recent_accesses_desc: "This user's most recent access log entries in the analysed period, newest first (up to {n}).",
        },
        anchoring_status: {
            kpi_summary_label: 'Anchoring status summary',
            title: "Estat d'ancoratge",
            loading: 'Carregant anchoring data...',
            anchored: 'Ancorat',
            pending: 'Pendent',
            failed: 'Ha fallat',
            last_anchor: 'Últim ancoratge',
            no_records: "No s'han trobat anchoring records",
            status_load_failed: 'Error en load anchoring status. Si us plau, torneu-ho a intentar.',
            proofs_load_failed: 'Error en load access proofs. Si us plau, torneu-ho a intentar.',
            verify_failed: 'Error en verify proof chain. Si us plau, torneu-ho a intentar.',
            anchor_failed: 'Error en anchor access proof. Si us plau, torneu-ho a intentar.',
            batch_anchor_failed: 'Error en batch anchor access proofs. Si us plau, torneu-ho a intentar.',
            proof_count: 'prova',
            proof_count_plural: 'proves',
            anchor_single: 'Ancoratge individual',
            anchor_batch: 'Ancoratge per lot',
            access_id_placeholder: 'Introduïu access ID to anchor',
            batch_ids_placeholder: 'access-id-1, access-id-2, access-id-3',
            verify_chain: 'Verificar cadena',
            chain_breaks: 'Ruptures de cadena',
            total_checked: 'Total verificat',
            anchor_placeholder: 'Introduïu access ID to anchor',
            fetch_failed: 'Error en fetch anchoring status. Si us plau, torneu-ho a intentar.',
            select_to_anchor: 'Select access log entry to anchor',
            select_to_anchor_batch: 'Select entries to anchor',
            no_unanchored_entries: 'No unanchored access-log entries found.',
            loading_unanchored: 'Loading access log entries...',
            fetch_unanchored_failed: 'Failed to load unanchored access log entries.',
            fetch_proofs_failed: 'Error en fetch proofs. Si us plau, torneu-ho a intentar.',
            access_id: "ID d'accés",
            chain_sequence: 'Seq. cadena',
            proof_hash_label: 'Hash de la prova',
            batch_id: 'ID de lot',
            proofs_loaded: '{n} access proofs carregats',
            proof_version_label: 'Version',
            proof_version_value: 'Version {n}',
            verify_valid_summary: 'The chain is intact. Every link and hash recomputes.',
            verify_invalid_summary: 'The chain does not verify. The proofs listed below no longer match what was anchored.',
            verify_proofs_v1: 'Version 1 proofs',
            verify_proofs_v2: 'Version 2 proofs',
            verify_content_verified: 'Log rows matching their proof',
            verify_content_destroyed: 'Content destroyed after retention',
            verify_content_destroyed_note: 'The chain still verifies for these proofs. The access-log row each one anchors has passed its retention date and been destroyed, so its content can no longer be proven.',
            verify_content_removed_sample: 'Demo log rows removed when sample data was cleared',
            verify_content_removed_sample_note: 'The chain still verifies for these proofs. Each one anchors a demo access-log row that was removed when the sample data was cleared; the removal is recorded by the platform. These are not retention destructions.',
            verify_v1_note: 'Version 1 proofs protect the order of the chain and the fact that an access event existed. They do not cover the content of the log row.',
            verify_v2_note: 'A version 2 proof also covers the content of its log row, for as long as the row exists.',
            verify_breaks_heading: 'Proofs that failed',
            verify_break_types: {
                field_mismatch: 'Log row changed after it was anchored',
                hash_mismatch: 'Proof hash does not recompute',
                chain_link_mismatch: 'Link to the previous proof is broken',
                unsupported_proof_version: 'Proof version is not supported',
                content_missing: 'Log row missing — the platform has no record of removing it',
            },
            anchor_statuses: {
                chained: 'Chained',
                pending: 'Pendent',
                confirmed: 'Confirmat',
                failed: 'Ha fallat',
            },
            anchor_types: {
                sha256_chain: 'SHA-256 Cadena',
                blockchain_anchor: 'Àncora Blockchain',
                zkp_stub: 'ZKP Stub',
            },
        },
        configuration: {
            title: 'Configuració',
            session_settings: 'Sessió Configuració',
            log_retention: 'Registre Retenció (days)',
            anomaly_threshold: "Llindar d'anomalia",
            anchoring_enabled: 'Ancoratge activat',
            save_config: 'Desar Configuració',
            load_failed: 'Error en load configuration. Si us plau, torneu-ho a intentar.',
            save_success: 'Configuració desat amb èxit',
            save_failed: 'Error en save configuration. Si us plau, torneu-ho a intentar.',
            justification_rules: 'Regles de justificació',
            reason_codes: 'Codis de motiu',
            anchoring_settings: "Configuració d'ancoratge",
            anchoring_method: "Mètode d'ancoratge",
            anchoring_frequency: "Freqüència d'ancoratge",
            batch_interval: 'Interval de lot (minuts)',
            eligibility_criteria: "Criteris d'elegibilitat",
            add_criterion_placeholder: 'Afegir eligibility criterion...',
            retention_settings: 'Retenció Configuració',
            standard_retention: 'Estàndard Retenció (days)',
            sensitive_retention: 'Sensible Retenció (days)',
            working_hours: 'Hores de treball',
            work_start: 'Inici Hora',
            work_end: 'Fi Hora',
            timezone: 'Zona horària',
            timezone_placeholder: 'p. ex. Asia/Ho_Chi_Minh',
            working_days: 'Dies laborables',
            concurrent_sessions: 'Límits de sessions concurrents',
            concurrent_threshold: 'Llindar de sessions simultànies',
            classification_level: 'Classificació Nivell',
            prompt_mode: "Mode d'avís",
            grace_period: 'Període de gràcia (s)',
            code_placeholder: 'codi',
            is_custom: 'Personalitzat',
            label_placeholder: 'Etiqueta',
            description_placeholder: 'Descripció',
            custom: 'Personalitzat',
            fetch_failed: 'Error en fetch configuration. Si us plau, torneu-ho a intentar.',
            classification_levels: {
                public: 'Públic',
                internal: 'Interna',
                confidential: 'Confidencial',
                restricted: 'Restringit',
                unclassified: 'Sense classificar',
            },
            prompt_modes: {
                reason_only: 'Només Raó',
                reason_text: 'Raó + text',
                reason_text_supervisor: 'Raó + Text + Supervisor',
            },
            anchoring_methods: {
                sha256_chain: 'SHA-256 Cadena',
                blockchain_anchor: 'Àncora Blockchain',
                zkp_stub: 'ZKP Stub',
            },
            anchoring_frequencies: {
                realtime: 'En temps real',
                batched: 'En lots',
                on_demand: 'A demanda',
            },
            retention_position: {
                title: 'Log Retention Position',
                description: 'What the retention sweep would destroy, and what it would keep. Reading this changes nothing.',
                as_of: 'As of',
                window_in_force: 'Window in force',
                days: '{n} days',
                swept: 'Past its window',
                retained_held: 'Kept — legal hold or retention rule',
                retained_anchored: 'Kept — anchored by an older proof',
                retained_investigation: 'Kept — linked to an open access investigation',
                unstamped: 'No retention date recorded',
                skipped_swept: 'Already accounted for',
                eligible_sample: 'Oldest entries past their window',
                resource: 'Resource',
                expires_at: 'Window ended',
                run_sweep: 'Run retention sweep',
                running: 'Running...',
                sweep_success: 'Retention sweep complete: {swept} destroyed, {retained} kept.',
                sweep_failed: 'The retention sweep could not be run. Please try again.',
                load_failed: 'The retention position could not be read. Please try again.',
                reload: 'Reload',
                empty: 'No access-log entry is past its retention window.',
                unstamped_note: 'An entry with no retention date is never destroyed. It is counted above so the number is visible rather than assumed.',
                anchored_note: 'An entry anchored by a version 1 proof (made before proofs covered content) is kept past its window, because destroying it would leave that proof pointing at nothing. An entry anchored by a newer proof is destroyed on its date and the chain still verifies.',
            },
        },
        access_policies: {
            title: "Polítiques d'accés",
            create_policy: "Crear política d'accés",
            policy_name: 'Nom de la política',
            framework_code: 'Marc normatiu',
            select_framework: 'Seleccioneu un marc normatiu...',
            review_frequency: 'Freqüència de revisió',
            scope: 'Abast',
            no_policies_found: "No s'han trobat polítiques d'accés.",
            policy_created: "Política d'accés creada",
            frequency_labels: {
                monthly: 'Mensual',
                quarterly: 'Trimestral',
                semi_annual: 'Semestral',
                annual: 'Anual',
            },
        },
        review_campaigns: {
            title: "Campanyes de revisió d'accés",
            create_campaign: 'Crear campanya de revisió',
            reviewer: 'Revisor',
            due_date: 'Data de venciment',
            certify: 'Certificar',
            no_campaigns_found: "No s'han trobat campanyes de revisió d'accés.",
            status_open: 'Oberta',
            status_in_review: 'En revisió',
            status_completed: 'Completada',
            campaign_created: 'Campanya de revisió creada',
            review_certified: "Revisió d'accés certificada",
            certify_campaign: 'Certificar la campanya {id}',
            policy_id: 'ID de la política',
            select_policy: 'Seleccioneu una política...',
            reviewer_ids_label: 'Revisors',
            select_reviewers: 'Seleccioneu revisors...',
            status_labels: {
                open: 'Obert',
                in_review: 'En revisió',
                in_progress: 'En curs',
                cancelled: 'Cancel·lada',
                completed: 'Completat',
            },
        },
        evidence_packages: {
            title: "Paquets d'evidència",
            generate_package: "Generar paquet d'evidència",
            export_type: "Tipus d'exportació",
            package_type: 'Tipus de paquet',
            framework_scope: 'Abast del marc',
            period: 'Període',
            generated_by: 'Generat per',
            generated_at: 'Generat el',
            no_packages_found: "No s'han trobat paquets d'evidència.",
            package_generated: "Paquet d'evidències generat",
            export_type_full: 'Exportació completa',
            export_type_summary: 'Resum',
            export_type_certification_only: 'Només certificació',
            package_type_access_review: "Revisió d'accés",
            package_type_investigation: 'Investigació',
            package_type_certification: 'Certificació',
            package_type_authority_request: "Sol·licitud d'autoritat",
            period_start: 'Inici del període',
            period_end: 'Fi del període',
            select_frameworks: 'Seleccioneu marcs...',
            completeness_col: 'Completesa',
            status_col: 'Estat',
            generated_by_col: 'Generat per',
        },
        evidence_package_detail: {
            title: "Detall del paquet d'evidències",
            not_found: "No s'ha trobat el paquet d'evidències.",
            package_metadata: 'Metadades del paquet',
            status: 'Estat',
            item_count: "Nombre d'elements",
            disposition: 'Disposició',
            closed_at: 'Tancat el',
            completeness_dashboard: 'Tauler de completesa',
            no_completeness_data: 'No hi ha dades de completesa disponibles.',
            controls_covered: 'controls coberts',
            missing: 'Falten',
            sign_off_tracker: "Seguiment d'aprovació",
            stage_framework_owner: 'Propietari del marc',
            stage_legal_compliance: 'Legal i Compliment',
            stage_internal_audit: 'Auditoria interna',
            approved: 'Aprovat',
            returned: 'Retornat',
            pending: 'Pendent',
            approve: 'Aprovar',
            return_btn: 'Retornar',
            sign_off_submitted: 'Aprovació enviada',
            artifacts: 'Artefactes',
            no_artifacts: "No s'han trobat artefactes.",
            template_id: 'ID de plantilla',
            description: 'Descripció',
            classification: 'Classificació',
            evidence_type: "Tipus d'evidència",
            framework_section: 'Secció del marc',
            history: "Historial d'aprovació",
            expand: 'Expandir',
            collapse: 'Contraure',
            action: 'Acció',
            actor: 'Actor',
            timestamp: 'Marca de temps',
            comment: 'Comentari',
            close_case: 'Tancar cas',
            confirm_close: 'Confirmar tancament',
            closure_notes: 'Notes de tancament',
            disposition_compliant: 'Conforme',
            disposition_non_compliant: 'No conforme',
            disposition_partial: 'Parcial',
            case_closed: "Paquet d'evidències tancat",
            export_package: 'Exportar paquet',
            export_complete: "Paquet d'evidències exportat",
            completeness_explainer: "A control is one of the organisation's active access policies for the framework. It is covered when at least one access-review certification was recorded in the package period under a campaign of that policy.",
            completeness_not_measured: 'No active access policy is declared for this framework, so there is no control to measure coverage against.',
            artifacts_explainer: "Each artifact is an access-review certification recorded in the package period under a campaign of one of the framework's access policies.",
            policy: 'Access policy',
            decision: 'Decision',
            certified_at: 'Certified at',
            certified_by: 'Certified by',
            decision_approved: 'Access approved',
            decision_revoked: 'Access revoked',
            evidence_type_access_certification: 'Access certification',
        },
    },
    consent_properties: {
        nav: {
            dashboard: 'Tauler de control',
            properties: 'Propietats',
            groups: 'Grups',
            bindings: 'Vinculacions',
            change_requests: 'Sol·licituds de canvi',
            comparison: 'Comparació',
            inconsistencies: 'Inconsistències',
            snapshot_schedules: "Programació d'instantànies",
            golden_templates: 'Plantilles de referència',
            evidence_packs: "Paquets d'evidència",
            audit: 'Auditoria',
        },
        select_property: 'Seleccionar propietat…',
        select_policy: 'Seleccionar política…',
        select_properties: 'Seleccionar propietats…',
        select_target_properties: 'Seleccionar propietats objectiu…',
        select_owner: 'Seleccionar propietari…',
        select_technical_contact: 'Seleccionar contacte tècnic…',
        property_statuses: {
            active: 'Actiu',
            inactive: 'Inactiu',
            pending: 'Pendent',
            decommissioned: 'Fora de servei',
        },
        dashboard: {
            title: 'Tauler de propietats creuades',
            loading: 'Carregant dashboard data...',
            total_properties: 'Total de propietats',
            active_properties: 'Propietats actives',
            snapshot_coverage: "Cobertura d'instantànies",
            properties_covered: 'propietats cobertes',
            total_bindings: "Total d'enllaços",
            deployed_bindings: 'Desplegat',
            pending_bindings: 'Desplegament pendent',
            deployment_progress: 'Progrés del desplegament',
            on_target: "A l'objectiu",
            below_target: "Sota l'objectiu",
            target_label: 'Objectiu',
            total_groups: 'Total Grups',
            pending_changes: 'Canvis pendents',
            inconsistencies: 'Inconsistències',
            recent_activity: 'Activitat recent',
            no_recent_activity: 'Sense activitat recent.',
            fetch_error: 'Error en load dashboard. Si us plau, torneu-ho a intentar.',
        },
        properties: {
            title: 'Registre de propietats',
            new: 'Nou Property',
            edit: 'Editar propietat',
            property_name: 'Nom',
            property_type: 'Tipus',
            identifier: 'Identificador',
            environment: 'Entorn',
            owner_id: 'Propietari ID',
            technical_contact_id: 'ID del contacte tècnic',
            decommission: 'Descomissionar',
            confirm_decommission: 'Esteu segur que voleu descomissionar aquesta propietat?',
            no_properties: "No s'han trobat properties",
            all_environments: 'Tots els entorns',
            all_types: 'Tots els tipus',
            cmp_instance_id: 'CMP ID de la instància',
            deployment_date: 'Data de desplegament',
            identifier_placeholder: 'p. ex., www.example.com o com.example.app',
            owner_name: 'Propietari',
            technical_contact_name: 'Contacte tècnic',
            sensitive_data_categories: 'Categories de dades sensibles',
            sensitive_data_categories_placeholder: 'Seleccioneu categories de dades sensibles',
            requires_enhanced_review: 'Requereix una revisió millorada',
            sensitive_review_status: 'Revisió de dades sensibles',
            sensitive_review_pending: 'Pendent de revisió',
            sensitive_review_approved: 'Aprovat',
            sensitive_review_rejected: 'Rebutjat',
            sensitive_review_submit: 'Envia la revisió',
            sensitive_review_approve: 'Aprovar',
            sensitive_review_reject: 'Rebutja',
            sensitive_review_notes: 'Notes de revisió',
            sensitive_review_notes_placeholder: 'Introdueix notes de revisió...',
            record_withdrawal: 'Retirada del registre',
            withdrawal_subject_id: 'ID del subjecte',
            withdrawal_consent_id: 'ID de consentiment',
            withdrawal_reason: 'Raó',
            withdrawal_downstream_systems: 'Sistemes avall',
            withdrawal_downstream_placeholder: 'sistema-1, sistema-2',
            withdrawal_success: "La retirada s'ha propagat correctament.",
            withdrawal_error: "No s'ha pogut propagar la retirada.",
            withdrawal_data_subject_id: 'ID del subjecte de dades',
            withdrawal_data_subject_id_placeholder: 'p. ex. usr_abc123',
            withdrawal_consent_record_id: 'ID del registre de consentiment',
            withdrawal_consent_record_id_placeholder: 'p. ex. consent_xyz789',
            withdrawal_purposes: 'Propòsits retirats',
            withdrawal_purposes_placeholder: 'Seleccioneu els objectius per retirar el consentiment per...',
            withdrawal_withdrawn_at: 'Data de retirada',
            withdrawal_tasks_created: 'Tasques creades',
            withdrawal_affected_bindings: 'Enquadernacions afectades',
            withdrawal_affected_activities: 'Activitats de processament afectades',
            withdrawal_history: 'Historial de retirada',
        },
        groups: {
            title: 'Grups de propietats',
            new: 'Nou grup',
            group_name: 'Grup Nom',
            member_count: 'Membres',
            no_groups: "No s'han trobat property groups",
            policies: 'Polítiques',
            confirm_delete: 'Esteu segur que voleu suprimir aquest grup?',
        },
        bindings: {
            title: 'Matriu de vinculacions',
            property: 'Propietat',
            purpose: 'Finalitat',
            legal_basis: 'Base jurídica',
            bound: 'Vinculat',
            no_bindings: "No s'han trobat bindings",
            bind_policy: 'Política de vinculació',
            bulk_bind: 'Enquadernació a granel',
            bind_policy_to_property: 'Vincular la política a la propietat',
            bulk_bind_policy: "Política d'enllaç massiu",
            unbound: 'Sense lligar',
            policy_id: 'ID de política',
            version_placeholder: 'p. ex., 1.0.0',
            property_ids_placeholder: 'prop-id-1, prop-id-2, prop-id-3',
            binding_name: 'Vinculació',
            property_name: 'Nom de propietat',
            policy_name: 'Nom de política',
            bindings_list: 'Llista de vinculacions',
            binding_actions: {
                start_deploy: 'Inicia el desplegament',
                mark_deployed: 'Marca com a desplegat',
                rollback: 'Revertir',
            },
        },
        change_requests: {
            title: 'Procés de sol·licituds de canvi',
            new: 'Nou Change Request',
            requester: 'Sol·licitant',
            change_type: 'Tipus de canvi',
            property: 'Propietat',
            submitted_at: 'Enviat el',
            no_requests: "No s'han trobat change requests",
            fetch_pipeline_error: 'Error en fetch pipeline. Si us plau, torneu-ho a intentar.',
            fetch_list_error: 'Error en fetch change requests. Si us plau, torneu-ho a intentar.',
            create_error: 'Error en create change request. Si us plau, torneu-ho a intentar.',
            action_error: 'Error en perform action. Si us plau, torneu-ho a intentar.',
            approver_name: 'Aprovador',
            placeholder_target_props: 'prop-id-1, prop-id-2',
            view_all: 'Tots',
            view_my_properties: 'Les Meves Propietats',
            stage_requests: 'Sol·licituds {stage}',
            policy_required: "Choose the policy this change applies to. A policy update, a new binding or a binding removal changes that policy's bindings.",
        },
        comparison: {
            title: 'Espai de comparació',
            select_properties: 'Seleccioneu Properties to Compare',
            compare: 'Comparar',
            no_comparison: 'Seleccioneu at least two properties to compare.',
            fetch_error: 'Error en fetch comparison reports. Si us plau, torneu-ho a intentar.',
            run_error: 'Error en run comparison. Si us plau, torneu-ho a intentar.',
            save_notes_error: 'Error en save notes. Si us plau, torneu-ho a intentar.',
            scan_complete: 'Inconsistency scan completat amb èxit',
            scan_error: 'Error en run inconsistency scan. Si us plau, torneu-ho a intentar.',
            min_two_properties: 'Proporcioneu almenys 2 IDs de propietat per a la comparació.',
            view_inconsistencies: 'Veure Inconsistències',
            click_to_select: 'Feu clic per seleccionar dos o més propietats',
            no_properties_available: 'Cap propietat disponible',
            no_matching_properties: 'Cap propietat coincident',
            select_at_least_two_props: 'Seleccioneu almenys dues propietats de la llista desplegable.',
            no_differences_found: "No s'han trobat diferències entre les propietats seleccionades.",
            found_inconsistencies: "S'han trobat {count} inconsistències en {properties} propietats i {bindings} enllaços.",
        },
        inconsistencies: {
            title: "Llista d'inconsistències",
            property: 'Propietat',
            field: 'Camp',
            expected: 'Esperat',
            actual: 'Real',
            severity: 'Gravetat',
            no_inconsistencies: "No s'han trobat inconsistencies",
            trend: "Tendència d'incoherència",
            trend_chart_label: "Gràfic de tendències d'incoherència que mostra els recomptes al llarg del temps",
            acknowledge: 'Reconèixer',
            resolve: 'Resoldre',
            suppress: 'Suprimir',
            cr_id_placeholder: 'ID CR',
            view_all: 'Tots',
            view_my_properties: 'Les Meves Propietats',
            view_dpo_review: 'DPO Cua de revisió',
            change_request_id_required: "Cal canviar l'identificador de sol·licitud per resoldre aquesta incoherència.",
        },
        snapshot_schedules: {
            title: "Programació d'instantànies",
            new: 'Nou programar',
            schedule_name: 'Programar Nom',
            frequency: 'Freqüència',
            next_run: 'Següent Executar',
            last_run: 'Últim Executar',
            no_schedules: "No s'han trobat snapshot schedules",
            target_type: "Tipus d'objectiu",
            target_id: 'ID objectiu',
            custom_frequency_days: 'Freqüència personalitzada (dies)',
            time_of_day: 'Hora del dia',
            time: 'Temps',
            retention_days: 'Retenció (dies)',
            execute_now: 'Executar ara',
            placeholder_days: 'per exemple, 10',
            target_name: 'Nom objectiu',
        },
        golden_templates: {
            title: 'Plantilles de referència',
            new: 'Nou plantilla',
            template_name: 'Plantilla Nom',
            property_type: 'Tipus de propietat',
            no_templates: "No s'han trobat golden templates",
            applicable_group_ids: 'Identificadors de grup aplicables (separats per comes)',
            applicable_groups: 'Grups aplicables',
            purposes: 'Propòsits',
            legal_bases: 'Bases legals',
            banner_config: 'Configuració del bàner',
            consent_settings: 'Configuració de consentiment',
            group_ids_placeholder: 'grup-id-1, grup-id-2',
            apply_to_property: 'Aplicar a Propietat',
            apply_to_group: 'Aplicar a Grup',
            apply_success: 'Plantilla aplicada correctament.',
            applications: "Historial d'aplicacions",
            no_applications: 'Encara no hi ha aplicacions de plantilla.',
            no_configuration_details: 'La plantilla no té detalls de configuració. Edita la plantilla per afegir finalitats, bases legals, configuració del bàner o configuració del consentiment.',
        },
        rollouts: {
            title: 'Desplegaments de plantilles',
            rollout_status: 'Estat del desplegament',
            confirmed: 'Confirmat',
            pending: 'Pendent',
            failed: 'Fallat',
            confirm: 'Confirmar Propietat',
            confirm_success: 'Propietat confirmada en el desplegament.',
            can_close: 'Totes les propietats confirmades — desplegament completat',
            cannot_close: 'Esperant confirmacions de propietats',
            no_rollouts: "No s'han trobat desplegaments.",
            in_progress: 'En curs',
            completed: 'Completat',
            cancelled: 'Cancel·lat',
            reject: 'Rebutjar Propietat',
            reject_success: 'Propietat rebutjada del desplegament.',
            confirm_all: 'Confirmar Tot',
            reject_all: 'Rebutjar Tot',
            confirm_label: 'Confirmar desplegament per a',
            reject_label: 'Rebutjar desplegament per a',
            confirm_all_label: 'Confirmar Tots els Desplegaments Pendents',
            reject_all_label: 'Rebutjar Tots els Desplegaments Pendents',
            no_properties_in_rollout: 'Aquest desplegament no té propietats.',
        },
        analytics: {
            title: 'Anàlisi de propietats',
            opt_in_rate: "Taxa d'acceptació",
            opt_out_rate: 'Taxa de rebuig',
            withdrawal_rate: 'Taxa de retirada',
            total_consents: 'Total de consentiments',
            total_withdrawals: 'Total de retirades',
            compliance_score: 'Puntuació de compliment',
            non_compliant: 'Propietats no conformes',
            no_non_compliant: 'Totes les propietats són conformes.',
            threshold: 'Llindar de compliment',
            property_id: 'ID de la propietat',
            compliant: 'Complint',
            non_compliant_status: 'Incompliment',
            compliance_help_summary: 'Com es calcula la puntuació de compliment i com la soluciono?',
            compliance_help_intro: 'Una propietat es marca com a no conforme quan la seva puntuació és inferior al 80%. La puntuació es calcula com:',
            compliance_help_with_template_label: "Si s'aplica una plantilla daurada:",
            compliance_help_with_template_text: 'Percentatge de claus de configuració (propòsits, legal_bases, banner_config, consent_settings) que coincideixen exactament amb la plantilla.',
            compliance_help_no_template_label: "Si no s'aplica cap plantilla daurada:",
            compliance_help_no_template_text: "% d'enllaços (tipus de consentiment assignats a la propietat) amb deployment_status = desplegat. Una propietat sense enllaços per defecte és 100%.",
            compliance_help_fix_intro: "Per eliminar una propietat d'aquesta llista, feu una de les accions següents:",
            compliance_help_fix_deploy_label: 'Desplegueu els seus enllaços',
            compliance_help_fix_deploy_text: "obriu la pàgina Matriu d'enllaç i configureu l'estat de desplegament de cada vincle com a desplegat.",
            compliance_help_fix_template_label: 'Apliqueu una plantilla daurada',
            compliance_help_fix_template_text: "utilitzeu Aplica al grup a la plantilla i, a continuació, confirmeu el desplegament d'aquesta propietat. Assegureu-vos que la configuració de la propietat coincideixi amb la plantilla.",
            compliance_help_fix_decommission_label: 'Desafectar la propietat',
            compliance_help_fix_decommission_text: "utilitzeu el botó de desactivació del modal de detall de la propietat si ja no s'utilitza.",
            compliance_help_fix_no_bindings_label: 'Traieu totes les fixacions',
            compliance_help_fix_no_bindings_text: 'si una propietat no té plantilla ni enllaços, obté un 100% per defecte.',
        },
        evidence_packs: {
            title: "Paquets d'evidència",
            new: 'Nou Pack',
            pack_name: 'Nom del paquet',
            properties: 'Propietats',
            generated_at: 'Generat el',
            no_packs: "No s'han trobat evidence packs",
            generate: 'Generar',
            content_type_consent_records: 'Consentiment Registres',
            content_type_policy_versions: 'Versions de la política',
            content_type_binding_history: 'Historial de vinculació',
            content_type_change_requests: 'Sol·licituds de canvi',
            content_type_inconsistency_reports: "Informes d'inconsistència",
            content_type_snapshot_data: "Dades d'instantània",
            content_type_audit_trail: "Pista d'auditoria",
        },
        audit: {
            title: 'Auditoria de propietats de consentiment',
            event_type: 'Esdeveniment Tipus',
            entity_type: "Tipus d'entitat",
            entity_id: "ID de l'entitat",
            actor: 'Actor',
            timestamp: 'Marca de temps',
            no_events: "No s'han trobat audit events",
            entity_type_property: 'Propietat',
            entity_type_group: 'Grup',
            entity_type_binding: 'Vinculació',
            entity_type_change_request: 'Sol·licitud de canvi',
            entity_type_golden_template: 'Plantilla daurada',
            entity_type_snapshot_schedule: 'Programació instantània',
            entity_type_inconsistency: 'Inconsistència',
            entity_type_evidence_pack: 'Paquet evidència',
            action_created: 'Creat',
            action_updated: 'Actualitzat',
            action_deleted: 'Eliminat',
            action_decommissioned: 'Fora de servei',
            action_status_changed: 'Estat canviat',
            action_submitted: 'Enviat',
            action_approved: 'Aprovat',
            action_rejected: 'Rebutjat',
            action_deployed: 'Desplegat',
            action_rolled_back: 'Revertit',
            action_paused: 'Pausat',
            action_resumed: 'Reprès',
            action_cancelled: 'Cancel·lat',
            action_acknowledged: 'Reconegut',
            action_resolved: 'Resolt',
            action_suppressed: 'Suprimit',
            action_executed: 'Executat',
        },
        property_types: {
            website: 'Lloc web',
            mobile_app: 'Aplicació mòbil',
            ott: 'OTT',
            iot_device: 'Dispositiu IoT',
            kiosk: 'Quiosc',
            api_service: 'Servei API',
            sdk: 'SDK',
        },
        environments: {
            production: 'Producció',
            staging: 'Preproducció',
            development: 'Desenvolupament',
            testing: 'Proves',
        },
        group_types: {
            brand: 'Marca',
            region: 'Regió',
            product_line: 'Línia de productes',
            business_unit: 'Unitat de negoci',
            custom: 'Personalitzat',
        },
        target_types: {
            property: 'Propietat',
            group: 'Grup',
            all: 'Tots',
        },
        frequencies: {
            hourly: 'Cada hora',
            daily: 'Diari',
            weekly: 'Setmanal',
            monthly: 'Mensual',
            custom: 'Personalitzat',
        },
        binding_statuses: {
            current: 'Actual',
            outdated: 'Obsolet',
            pending: 'Pendent',
            deploying: 'Desplegant',
            rolled_back: 'Revertit',
        },
        sensitive_categories: {
            health: 'Dades de salut',
            biometric: 'Dades biomètriques',
            genetic: 'Dades genètiques',
            racial_ethnic: 'Origen racial/ètnic',
            political_opinions: 'Opinions polítiques',
            religious_beliefs: 'Creences religioses',
            trade_union: 'Afiliació sindical',
            sexual_orientation: 'Orientació sexual',
            criminal_records: 'Antecedents Penals',
            financial: 'Dades financeres',
            children: 'Dades infantils',
            geolocation: 'Geolocalització precisa',
        },
        export_csv: 'Exportar CSV',
        exporting: 'Exportant...',
        page_x_of_y: 'Pagina {page} de {total}',
        regulatory: {
            capture_method_guidance: "PDPL requereix una prova de consentiment per coincidir amb el canal de recollida. Els tipus de propietat s'assignen als mètodes de captura de consentiment: lloc web/OTT = consentiment basat en bàners, aplicació mòbil/SDK = flux de consentiment a l'aplicació, quiosc = captura de consentiment físic/a la pantalla, servei API = consentiment programàtic, dispositiu IoT = mecanisme de consentiment a nivell de dispositiu.",
            dashboard_metrics_info: "Aquest tauler proporciona al DPO una visió única de la postura del consentiment a totes les propietats digitals. Les mètriques clau inclouen: percentatge de cobertura de l'enllaç (desplegat en comparació amb enllaços totals), adherència a la plantilla mitjançant el widget d'incoherències, sol·licituds de canvi pendents que requereixen revisió i desglossament de l'estat de la propietat que mostra propietats amb estats actius, inactius o fora de servei. Les propietats amb categories de dades sensibles es fan un seguiment mitjançant el registre de la propietat.",
            change_request_impact_info: "Les sol·licituds de canvi que afecten les propietats del consentiment han de documentar l'impacte regulatori: quins requisits PDPL es veuen afectats, si el canvi afecta el processament de dades sensibles (art. 2.4) i si el canvi requereix el nou consentiment de les persones interessades. El resum d'impacte de cada sol·licitud de canvi detalla les PDPL referències normatives per a l'evidència d'auditoria.",
            outdated_binding_warning: 'Els enllaços amb l\'estat "obsolet" indiquen que una propietat executa una versió de política que no és actual. És possible que aquestes propietats no reflecteixin els darrers PDPL requisits ni les actualitzacions de polítiques organitzatives. Reviseu i actualitzeu ràpidament els enllaços obsolets per mantenir el compliment de PDPL a totes les propietats de consentiment.',
        },
    },
    localization: {
        nav: {
            dashboard: 'Tauler de control',
            market_packs: 'Paquets de mercat',
            language_packs: "Paquets d'idiomes",
            frameworks: 'Marcs reguladors',
            mappings: 'Assignacions de mòduls',
            submissions: 'Enviaments de paquets',
            sector_overlay_catalog: 'Overlay Catalog',
            translations: 'Previsualització de la traducció',
            audit: "Pista d'auditoria",
        },
        dashboard: {
            title: 'Tauler de localització',
            loading: 'Carregant dashboard data...',
            active_market_packs: 'Actiu Mercat Packs',
            published_packs_available: 'Paquets de mercat publicats disponibles',
            language_packs: "Paquets d'idioma",
            total_language_packs: "Total de paquets d'idioma registrats",
            active_regulatory_frameworks: 'Actiu Regulatory Marcs',
            frameworks_active: 'Marcs amb estat actiu',
            pending_pack_submissions: 'Presentacions de paquets pendents',
            submissions_awaiting_review: 'Presentacions pendents de revisió',
            recent_audit_events: 'Recent Auditoria Esdeveniments',
            no_recent_audit: "Sense esdeveniments d'auditoria recents.",
            event_type: 'Esdeveniment Tipus',
            entity_type: "Tipus d'entitat",
            entity_id: "ID de l'entitat",
            actor: 'Actor',
            timestamp: 'Marca de temps',
        },
        market_packs: {
            title: 'Catàleg de paquets de mercat',
            new: 'Nou Market Pack',
            pack_name: 'Nom del paquet',
            market: 'Mercat',
            locale: 'Idioma',
            no_packs: "No s'han trobat market packs",
        },
        language_packs: {
            title: "Paquets d'idiomes",
            new: 'Nou Language Pack',
            language: 'Idioma',
            locale_code: "Codi d'idioma",
            completeness: 'Completesa',
            completeness_detail: "Detall d'exhaustivitat",
            overall_completeness: 'Completitud general',
            total_keys: 'Claus totals',
            translated_keys: 'Claus traduïdes',
            missing_keys: 'Falten claus',
            namespace_breakdown: "Desglossament de l'espai de noms",
            namespace: 'Espai de noms',
            translated: 'Traduït',
            no_packs: "No s'han trobat language packs",
            install_language_pack: "Instal·lar paquet d'idioma",
            available_packs: "Paquets d'idiomes disponibles",
            install: 'Instal·lar',
            all_installed: "Tots els paquets d'idiomes ja estan instal·lats.",
            install_success: "El paquet d'idioma s'ha instal·lat correctament.",
            market: 'Mercat',
            featured: 'Destacat',
        },
        frameworks: {
            // Sprint 63.32.0 (#543) — every framework_type code, in the reader's language.
            type_national_law: 'National law',
            type_parent_framework: 'Parent framework',
            type_child_decree: 'Child decree',
            type_child_decision: 'Child decision',
            type_child_procedure_decree: 'Procedural decree',
            type_conditional_child_decree: 'Conditional child decree',
            type_implementing_decree: 'Implementing decree',
            type_enforcement_decree: 'Sanctions decree',
            type_sanctions_reference: 'Sanctions reference',
            type_official_guidance: 'Official guidance',
            type_guidance_overlay: 'Guidance overlay',
            type_sector_overlay: 'Sector overlay',
            type_security_overlay: 'Security overlay',
            type_security_control_overlay: 'Security control overlay',
            type_temporary_procedural_overlay: 'Temporary procedural overlay',
            type_cross_sector: 'Cross-sector',
            type_directive: 'Directive',
            type_regulation: 'Regulation',
            type_national_transposition: 'National transposition',
            type_international_standard: 'International standard',
            title: 'Marcs reguladors',
            new: 'Nou marc',
            framework_name: 'Marc Nom',
            jurisdiction: 'Jurisdicció',
            effective_date: "Data d'efecte",
            no_frameworks: "No s'han trobat regulatory frameworks",
            view_requirements: 'Veure requisits',
            hide_requirements: 'Amagar requisits',
            show_legacy: 'Mostrar marcs legals antics',
            effective_until: 'Vigent fins',
            effective_on: 'Vigent des de',
            replaced_by: 'Reemplaçat per',
            replaces_label: 'Reemplaça',
            status_active: 'Actiu',
            status_draft: 'Esborrany',
            status_unclassified: 'Unrecognised status',
            status_legacy: 'Antic',
            status_upcoming: 'Pròximament',
            status_active_reference: 'In force, reference only',
            status_temporary_active: 'Temporarily in force',
            watchlist: 'Llista de seguiment',
            watchlist_empty: 'No hi ha esborranys ni marcs propers',
            watchlist_empty_desc: 'Els marcs amb l\'estat "esborrany" o "pròxim" apareixeran aquí per al seguiment.',
            expected_effective: 'Eficaç esperat',
            effective_date_tbd: "Data d'entrada en vigor: TBD",
            activate: 'Activar',
            ai_law_title: 'Llei del AI del Vietnam',
            ai_risk_classification: 'AI Classificació del risc',
            ai_system_dossier: 'AI Dossier del sistema',
            ai_labeling_checklist: 'AI Etiquetatge i divulgació',
            ai_incident_report: "AI Informe d'incident",
            ai_impact_assessment: "AI Avaluació d'impacte",
            ai_sandbox_application: 'Aplicació Sandbox',
            ai_sandbox_monitoring: 'Monitorització de sandbox',
            ai_sandbox_completion: 'Finalització Sandbox',
            ai_submission_tracker: "Seguiment d'enviaments",
            ai_evidence_capture: "Captura d'evidències",
            risk_level_minimal: 'Risc mínim',
            risk_level_limited: 'Risc limitat',
            risk_level_high: 'Alt Risc',
            risk_level_unacceptable: 'Risc inacceptable',
            submission_status_draft: 'Esborrany',
            submission_status_submitted: 'Presentat',
            submission_status_returned: 'Tornat per esmena',
            submission_status_accepted: 'Acceptat',
            submission_status_rejected: 'Rebutjat',
            submission_status_closed: 'Tancat',
            ecommerce_law_title: 'Llei de comerç electrònic de Vietnam',
            ecommerce_platform_classification: 'Classificació de la plataforma',
            ecommerce_seller_register: 'Registre de venedors',
            ecommerce_complaint_register: 'Registre de queixes',
            ecommerce_takedown_register: 'Registre de retirada',
            ecommerce_submission_tracking: "Seguiment de l'enviament",
            ecommerce_dossier_checklist: 'Llista de verificació del dossier',
            ecommerce_cross_border: 'Compliment transfronterer',
            ecommerce_trustmark: 'Programa de marques de confiança',
            ecommerce_notification: 'Notificació',
            ecommerce_registration: 'Inscripció',
            ecommerce_termination: 'Terminació',
            ecommerce_reporting_readiness: 'Preparació per informar',
            ecommerce_evidence_capture: "Captura d'evidències",
            submission_status_approved: 'Aprovat',
            cybersec_law_2025_title: 'Llei de ciberseguretat del Vietnam (2025)',
            cybersec_system_classification: 'Classificació del sistema',
            cybersec_risk_assessment: 'Avaluació de riscos',
            cybersec_incident_report: "Informe d'incidències",
            cybersec_authority_cooperation: "Cooperació de l'autoritat",
            cybersec_data_localization: 'Localització de dades',
            cybersec_vendor_security: 'Seguretat del venedor',
            cybersec_submission_tracker: "Seguiment d'enviaments",
            cybersec_evidence_capture: "Captura d'evidències",
            cybersec_dossier_checklist: 'Llista de verificació del dossier',
            cybersec_classification_general: 'General',
            cybersec_classification_important: 'Important',
            cybersec_classification_critical: 'Crític',
            data_law_title: 'Llei de dades de Vietnam',
            data_inventory: 'Inventari de dades',
            data_classification: 'Classificació de dades',
            data_sharing: 'Compartició de dades',
            data_governance: 'Governança de dades',
            data_submission_tracker: "Seguiment d'enviaments",
            data_evidence_capture: "Captura d'evidències",
            data_dossier_checklist: 'Llista de verificació del dossier',
            telecom_law_title: 'Llei de telecomunicacions del Vietnam',
            telecom_authority_request: "Sol·licitud de l'autoritat",
            telecom_disclosure: 'Divulgació',
            telecom_confidentiality: 'Confidencialitat',
            telecom_submission_tracker: "Seguiment d'enviaments",
            telecom_evidence_capture: "Captura d'evidències",
            telecom_dossier_checklist: 'Llista de verificació del dossier',
            pdpl_law_title: 'Llei de protecció de dades personals del Vietnam',
            pdpl_breach_notification: "Notificació d'incompliment",
            pdpl_dpo_notification: 'DPO Notificació de cita',
            pdpl_data_subject_notification: 'Notificació del subjecte de dades',
            pdpl_submission_tracker: "Seguiment d'enviaments",
            pdpl_evidence_capture: "Captura d'evidències",
            pdpl_dossier_checklist: 'Llista de verificació del dossier',
            pdpl_authority_filing: "Arxiu d'autoritats",
            pdpl_mops_filing: 'Presentació MoPS',
        },
        mappings: {
            title: 'Assignacions de mòduls',
            new: 'Nou mapeig',
            module: 'Mòdul',
            // fix-sprint #447 W8 — module labels, from the bundle (no `{en, vi}` map on the page).
            module_consent: 'Consentiment',
            module_dpia: 'DPIA',
            module_incident: 'Incident',
            module_rights_request: 'Sol·licitud de drets',
            module_data_mapping: 'Mapatge de dades',
            module_classification: 'Classificació',
            module_monitoring: 'Seguiment',
            module_governance: 'Governança',
            module_vendor: 'Venedor',
            module_risk: 'Risc',
            module_sector_licensing: 'Sector Licensing',
            module_investor_verification: 'Investor Verification',
            module_account_data_governance: 'Account Data Governance',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_authority_direction: 'Authority Direction',
            module_issued_record: 'Issued Records',
            module_publication_assessment: 'Publication Assessment',
            module_legal_deposit: 'Legal Deposit',
            module_legal_representative: 'Legal Representative',
            module_service_certificate: 'Processing Service Certificate',
            market_pack: 'Paquet de mercat',
            mapping_status: 'Mapeig Estat',
            // fix-sprint #431 — the list count, chosen by `count === 1` (the bundle's `count_one` convention).
            count: '{count} mappings',
            count_one: '{count} mapping',
            no_mappings: "No s'han trobat module mappings",
            incomplete_entries_warning: "{count} mapeig(s) ocult(s) per manca d'ID de marc, ID de requisit o mòdul.",
            // fix-sprint #431 — seeding is a person's action, never a side effect of opening the page.
            seed_action: 'Create mappings from installed frameworks',
            seed_hint: 'Creates a direct mapping for each requirement that names a module, in every installed framework that has no mappings yet. Opening this page creates nothing.',
            seed_confirm_title: 'Create mappings from installed frameworks?',
            seed_confirm: 'This creates a mapping for every requirement that names a module, in each installed framework that has no mappings yet. It can add many rows, and each can be edited or deleted afterwards.',
            seed_done: '{count} mapping(s) created.',
            seed_none: 'No mappings were created: every installed framework already has mappings, or none of its requirements names a module.',
            seed_error: 'The mappings could not be created.',
            // fix-sprint #431 (gate E12.5 remediation 1) — a failed list read says so, and nothing else.
            load_error: 'The module mappings could not be read, so none are shown and none can be created from installed frameworks here.',
        },
        submissions: {
            title: 'Enviaments de paquets',
            new: 'Nou Submission',
            submitter: 'Presentador',
            pack: 'Paquet',
            submitted_at: 'Enviat el',
            no_submissions: "No s'han trobat pack submissions",
            installed_sector_overlays_title: 'Superposicions sectorials ({count})',
            installed_sector_overlays_aria: 'Superposicions sectorials instal·lades',
            installed_col_name: 'Nom',
            installed_col_code: 'Codi',
            installed_col_sector: 'Sector',
            installed_col_mode: 'Mode',
            installed_col_status: 'Estat',
            installed_refresh: 'Actualitzar',
            installed_count_one: '1 installed pack',
            installed_count: '{count} installed packs',
            installed_market_packs_title: 'Market Packs ({count})',
            installed_market_packs_aria: 'Installed Market Packs',
            installed_language_packs_title: 'Language Packs ({count})',
            installed_language_packs_aria: 'Installed Language Packs',
            installed_frameworks_title: 'Regulatory Frameworks ({count})',
            installed_frameworks_aria: 'Installed Regulatory Frameworks',
            installed_col_market_code: 'Market Code',
            installed_col_display_name: 'Display Name',
            installed_col_locale: 'Locale',
            installed_col_version: 'Version',
            tab_submissions: 'Submissions',
            tab_install_zip: 'Install from Zip',
            tab_installed: 'Installed Packs',
        },
        translations: {
            title: 'Previsualització de la traducció',
            select_locale: 'Seleccioneu Locale',
            select_module: 'Seleccioneu Module',
            key: 'Key',
            value: 'Valor',
            no_translations: "No s'han trobat translations",
        },
        audit: {
            title: "Pista d'auditoria de localització",
            event_type: 'Esdeveniment Tipus',
            // Gate E12.5 remediation 1 (fix-sprint #431) — Module Mappings audit events: the key is
            // `event_type_` + the recorded action with `.` read as `_`.
            event_type_regulatory_mapping_created: 'Module mapping created',
            event_type_regulatory_mapping_updated: 'Module mapping updated',
            event_type_regulatory_mapping_deleted: 'Module mapping deleted',
            event_type_regulatory_mapping_seeded_from_frameworks: 'Mappings created from installed frameworks',
            // Gate E12.5 remediation 2 — every other event the localization services record (event_type, else action).
            event_type_market_pack_created: 'Market pack created',
            event_type_market_pack_updated: 'Market pack updated',
            event_type_market_pack_status_changed: 'Market pack status changed',
            event_type_market_pack_installed: 'Market pack installed',
            event_type_market_pack_uninstalled: 'Market pack uninstalled',
            event_type_pack_created: 'Market pack created',
            event_type_pack_updated: 'Market pack updated',
            event_type_pack_status_changed: 'Market pack status changed',
            event_type_pack_installed: 'Market pack installed',
            event_type_pack_uninstalled: 'Market pack uninstalled',
            event_type_cluster_installed: 'Market pack cluster installed',
            event_type_pack_installed_from_zip: 'Pack installed from an uploaded file',
            event_type_pack_zip_install_refused: 'Uploaded pack refused: it carries no signature',
            event_type_pack_submission_created: 'Pack submission created',
            event_type_pack_submission_review_started: 'Pack submission review started',
            event_type_pack_submission_comment_added: 'Comment added to a pack submission',
            event_type_pack_submission_approved: 'Pack submission approved',
            event_type_pack_submission_rejected: 'Pack submission rejected',
            event_type_pack_artifact_created: 'Pack artifact created',
            event_type_pack_artifact_updated: 'Pack artifact updated',
            event_type_pack_artifact_deleted: 'Pack artifact deleted',
            event_type_regulatory_framework_updated: 'Framework updated',
            event_type_regulatory_framework_status_changed: 'Framework status changed',
            event_type_regulatory_framework_requirement_added: 'Requirement added to a framework',
            event_type_regulatory_framework_requirement_removed: 'Requirement removed from a framework',
            event_type_regulatory_framework_stack_layer_status_changed: 'Framework Stack layer status changed',
            event_type_regulatory_framework_transition_review_updated: 'Transition review updated',
            event_type_language_pack_created: 'Language pack created',
            event_type_language_pack_updated: 'Language pack updated',
            event_type_language_pack_activated: 'Language pack activated',
            event_type_language_pack_deactivated: 'Language pack deactivated',
            event_type_language_pack_installed: 'Language pack installed',
            event_type_language_pack_uninstalled: 'Language pack uninstalled',
            event_type_sector_overlay_installed: 'Sector overlay installed',
            event_type_sector_overlay_uninstalled: 'Sector overlay uninstalled',
            event_type_regulatory_overlay_installed: 'Regulatory overlay installed',
            event_type_regulatory_overlay_uninstalled: 'Regulatory overlay uninstalled',
            event_type_installed_framework_mode_changed: 'Installed framework mode changed',
            event_type_phased_obligation_early_implemented: 'Phased obligation marked early-implemented',
            entity_type: "Tipus d'entitat",
            entity_id: "ID de l'entitat",
            entity_name: "Nom de l'entitat",
            actor: 'Actor',
            timestamp: 'Marca de temps',
            payload: 'Càrrega útil',
            collapse_payload: 'Replegar detalls de la càrrega',
            payload_none: 'No details recorded',
            payload_one_field_recorded: '1 field recorded',
            payload_fields_recorded: '{count} fields recorded',
            payload_one_field_changed: '1 field changed',
            payload_fields_changed: '{count} fields changed',
            payload_one_field_removed: '1 field removed',
            payload_fields_removed: '{count} fields removed',
            payload_col_field: 'Field',
            payload_col_value: 'Value',
            payload_col_before: 'Before',
            payload_col_after: 'After',
            payload_empty_value: '(empty)',
            payload_not_set: 'Not set',
            proof_not_anchored: 'Not anchored',
            proof_not_anchored_tooltip: 'No proof record exists for this event. Events written before Immutable Audit Trails was enabled are not anchored, and events in batch mode appear here once their batch is sealed.',
            expand_payload: 'Expandir detalls de la càrrega',
            collapse: 'Replegar',
            expand: 'Expandir',
            all_entity_types: "Tots els tipus d'entitat",
            no_events: "No s'han trobat audit events",
        },
        market_packs_detail: {
            installed_at: 'Instal·lat el',
            pack_details_label: 'Detalls del paquet',
            market_code: 'Codi de mercat',
            supported_locales: 'Idiomes suportats',
            min_platform_version: 'Versió mínima de la plataforma',
            pack_information: 'Informació del paquet',
            pack_contents: 'Contingut del paquet',
            included_frameworks: 'Marcs inclosos',
            pack_artifacts: 'Artefactes del paquet',
            no_artifacts: "No s'han trobat artefactes per a aquest paquet",
            pack_not_found: 'Paquet no trobat',
        },
        mappings_form: {
            framework_id: 'ID del marc',
            requirement_id: 'ID del requisit',
            module_label: 'Mòdul',
            mapping_type: 'Tipus de mapeig',
            configuration_json: 'Configuració (JSON)',
            conflict_resolution: 'Resolució de conflictes',
            is_override: 'És una substitució',
            all_modules: 'Tots els mòduls',
            all_mapping_types: 'Tots els tipus de mapeig',
        },
        submissions_detail: {
            submission_id: 'ID de presentació',
            validation: 'Validació',
            reviewers: 'Revisors',
            start_review: 'Iniciar revisió',
            hide_comments: 'Amagar comentaris',
            start_review_named: 'Start Review for {name}',
            approve_submission_named: 'Approve submission {name}',
            reject_submission_named: 'Reject submission {name}',
            comments_named: 'Comments for {name}',
            hide_comments_named: 'Hide comments for {name}',
            review_comments: 'Comentaris de revisió',
            loading_comments: 'Carregant comentaris...',
            no_comments: 'Encara no hi ha comentaris.',
            add_comment: 'Afegir comentari',
            reject_submission: 'Rebutjar presentació',
            reason_for_rejection: 'Motiu del rebuig',
            reviewer_ids: 'IDs dels revisors (separats per comes)',
            submit_new_pack: 'Enviar nou paquet',
            pack_type: 'Tipus de paquet',
            market_code: 'Codi de mercat',
            target_locales: 'Idiomes de destinació',
            pack_file: 'Fitxer del paquet',
            pack_format_info: "Format del paquet: els paquets de mercat haurien d'incloure fitxers JSON específics de la configuració regional amb traduccions d'espai de nom/clau. Els paquets d'idioma haurien de proporcionar traduccions per a les localitzacions de destinació especificades. Consulteu la documentació per obtenir un esquema detallat.",
            accepted_formats: 'Formats acceptats: .json o .zip. Els fitxers JSON han de contenir un objecte pla o amb espai de nom de parells clau-valor de traducció. Els fitxers ZIP haurien de contenir un o més fitxers de configuració regional de JSON.',
            comma_separated_locales: "Codis de localització separats per comes per a aquest paquet d'idioma",
            confirm_approval: 'Confirmar aprovació',
            approve_message: 'Aprovar aquesta presentació de paquet?',
            submission_label: 'Presentació',
            all_pack_types: 'Tots els tipus de paquet',
            passed: 'aprovat',
            failed_label: 'error',
            market_code_required: 'El codi de mercat és obligatori',
            version_required: 'La versió és obligatòria',
            pack_type_regulatory: 'Regulatori',
            framework_code: 'Codi del marc',
            jurisdiction_code: 'Codi de jurisdicció',
            pack_source: 'Font',
            pack_source_manual: 'Manual',
            pack_version: 'Versió del paquet',
            install_regulatory_pack: 'Instal·lar paquet regulatori',
            regulatory_pack_desc: 'Puja un paquet de marc regulatori (.zip)',
            framework_required: 'El codi del marc normatiu és obligatori',
            jurisdiction_required: 'El codi de jurisdicció és obligatori',
        },
        catalog_detail: {
            packs_installed_of: 'de {total} paquets instal·lats',
            install_all: 'Instal·lar tot',
            installing: 'Instal·lant...',
            removing: 'Eliminant...',
            install_all_named: 'Install all packs in {name}',
            installing_named: 'Installing... {name}',
            uninstall_named: 'Uninstall {name}',
            removing_named: 'Removing... {name}',
            manage_no_permission: 'Installing or uninstalling a market pack needs permission to manage market packs.',
            available: 'Available',
            cluster_installed_count: '({installed}/{total} installed)',
            frameworks_count_one: '1 framework',
            frameworks_count: '{count} frameworks',
            languages_count_one: '1 language',
            languages_count: '{count} languages',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            artifacts_count_one: '1 artifact',
            artifacts_count: '{count} artifacts',
            market_packs_count_one: '1 market pack',
            market_packs_count: '{count} market packs',
            install_success: 'Installed {market}: {frameworks}, {items}.',
            cluster_install_success: 'Cluster installed: {frameworks}, {forms}.',
            uninstall_success: 'Uninstalled {market}: removed {frameworks} and {artifacts}.',
            uninstall_confirm: 'Uninstall {market}? This removes all frameworks, artifacts and language packs for this market.',
            dependents_exist: 'Cannot uninstall {market}: other installed packs depend on it.',
            dependencies_required: '{market} needs {dependencies} installed first.',
            dependencies_required_unnamed: '{market} needs another market pack installed first.',
            requires: 'Requereix',
            tab_catalog: 'Catàleg',
            tab_installed: 'Instal·lat',
            details: 'Detalls',
        },
        // Sprint 63.33.0 (#594) — standards packs installed on their own.
        standalone_packs: {
            title: 'Standards',
            description: 'International standards are installed on their own: no market pack includes them. Installing one adds its frameworks, requirements and forms for your organisation.',
            install: 'Install',
            install_named: 'Install {name}',
            installing: 'Installing…',
            installing_named: 'Installing… {name}',
            installed: 'Installed',
            installed_other_route: 'Installed by another route',
            installed_other_route_hint: 'Its frameworks are present for your organisation, but this standard did not install them: another pack or a record created by hand did. There is nothing for it to remove here.',
            confirm_title: 'Install {name}?',
            confirm_message: 'This installs {name} for your organisation, with the frameworks, requirements and forms it carries.',
            confirm_label: 'Install',
            install_success: '{name} installed: {frameworks}, {forms}.',
            install_failed: 'The standard could not be installed.',
            install_no_permission: 'Installing a standard needs permission to change regulatory frameworks.',
            remove: 'Remove',
            remove_named: 'Remove {name}',
            removing: 'Removing…',
            removing_named: 'Removing… {name}',
            remove_confirm_title: 'Remove {name}?',
            remove_confirm_message: 'This removes {name} from your organisation, with the frameworks, requirements, deadlines and forms it installed, and every Module Mapping recorded against its frameworks, including mappings your organisation created or edited. Installing it again does not restore those mappings. Forms your organisation has filled in are kept. A removal cannot be undone.',
            remove_confirm_label: 'Remove',
            remove_success: '{name} removed: {frameworks}, {forms}.',
            remove_failed: 'The standard could not be removed.',
            remove_no_permission: 'Removing a standard needs permission to change regulatory frameworks.',
            refusal_already_installed: 'This standard is already installed for your organisation.',
            refusal_not_installable_alone: 'This pack is not installed on its own here: it is installed with its market, or from the sector overlay catalogue.',
            refusal_not_found: 'This standard is no longer in the catalogue. Reload the page.',
            refusal_not_installed: 'This standard is not installed for your organisation, so there is nothing to remove.',
            refusal_installed_by_another_route: 'This standard did not install its frameworks for your organisation, so it has nothing to remove. Remove them where they were installed.',
            refusal_forbidden: 'You do not have permission to change regulatory frameworks.',
            refusal_custody: 'This standard installs restricted registers; installing it requires the regulatory-artifact custody permission.',
        },
        translations_detail: {
            source_locale: "Idioma d'origen",
            target_locale: 'Idioma de destinació',
            locales_must_differ: "Els idiomes d'origen i de destinació han de ser diferents.",
            all_keys_translated: 'Totes les claus traduïdes',
            keys_missing: 'Falten {count} clau(s).',
            show_missing_only: 'Mostrar només les que falten',
            namespace: 'Espai de noms',
            source: 'Origen',
            target: 'Destinació',
        },
        frameworks_detail: {
            // fix-sprint-20260923-2136 — the detail page's own states, before the
            // framework record has been read (title, missing code, not found, failed).
            page_title_fallback: 'Framework',
            page_unavailable_heading: 'Framework unavailable',
            page_missing_code: 'No framework code was given in the address.',
            page_not_found: 'Framework {code} not found.',
            page_not_found_generic: 'Framework not found.',
            page_load_failed: 'The framework could not be loaded.',
            tab_frameworks: 'Marcs',
            tab_watchlist: 'Llista de seguiment',
            authority: 'Autoritat',
            requirements_count: 'Requisits',
            all_types: 'Tots els tipus',
            collapse_all: 'Replegar tot',
            expand_all: 'Expandir tot',
            all_sectors: 'All sectors',
            filter_by_sector: 'Filter by sector',
            search_frameworks: 'Search by name or code...',
            sef_url_label: 'Framework URL',
            sef_url_copy: 'Copy',
            sef_url_copy_label: 'Copy framework URL',
            sef_url_copied: 'Framework URL copied',
            sef_url_copy_failed: 'Could not copy the framework URL',
            filtered_to: 'Filtrat a',
            clear_filter: 'Esborrar filtre',
            no_requirements: "No s'han trobat requisits per a aquest marc.",
            requirements_label: 'Requisits',
            status_updated: 'Estat actualitzat correctament',
            overview_tab: 'Visió general',
            forms_tab: 'Formularis',
            procedures_tab: 'Procediments',
            fines_risks_tab: 'Multes i riscos',
            deadlines_tab: 'Terminis',
            guidance_tab: 'Orientació',
            guidance_empty: 'No hi ha contingut orientatiu per a aquest marc.',
            guidance_loading: "S'està carregant la guia...",
            guidance_section: "Secció d'orientació",
            guidance_view: 'Veure orientació',
            guidance_map_to_workflow: 'Mapa al flux de treball',
            guidance_link_evidence: "Evidència d'enllaç",
            guidance_filter_type: 'Tipus',
            guidance_filter_category: 'Categoria',
            guidance_filter_module: 'Mòdul',
            guidance_filter_status: 'Estat',
            guidance_filter_search: 'Guia de cerca',
            guidance_filter_all: 'Tots',
            guidance_table_reference: 'Referència',
            guidance_table_name: 'Nom de la guia',
            guidance_table_type: 'Tipus',
            guidance_table_category: 'Categoria',
            guidance_table_effective_date: "Data d'entrada en vigor",
            guidance_table_modules: 'Mòduls Relacionats',
            guidance_table_actions: 'Accions',
            guidance_reference_only_banner: "Només referència: s'aplica al sector {{sector}} però no a la vostra organització.",
            guidance_open_official_source: 'Obre la font oficial',
            guidance_no_detail: 'No hi ha detalls addicionals disponibles per a aquesta orientació.',
            enum_guidance_type_cross_framework_note: 'Cross-Framework Note',
            enum_guidance_type_draft_status_note: 'Draft Status Note',
            enum_guidance_type_framework_hierarchy_note: 'Framework Hierarchy Note',
            enum_guidance_type_framework_overlay_note: 'Framework Overlay Note',
            enum_guidance_type_implementing_decree_summary: 'Implementing Decree Summary',
            enum_guidance_type_practical_unofficial_practice: 'Practical (Unofficial) Practice',
            enum_guidance_category_auction: 'Auction',
            enum_guidance_category_complaint_dispute: 'Complaint & Dispute',
            enum_guidance_category_draft_governance: 'Draft Governance',
            enum_guidance_category_email_zip_submission: 'Email/ZIP Submission',
            enum_guidance_category_exchange_classification: 'Exchange Classification',
            enum_guidance_category_framework_hierarchy: 'Framework Hierarchy',
            enum_guidance_category_high_risk_ai: 'High-Risk AI',
            enum_guidance_category_lifecycle_controls: 'Lifecycle Controls',
            enum_guidance_category_listing_review: 'Listing Review',
            enum_guidance_category_national_database_access: 'National Database Access',
            enum_guidance_category_outsourced_processing: 'Outsourced Processing',
            enum_guidance_category_participant_registration: 'Participant Registration',
            enum_guidance_category_personal_data_linkage: 'Personal Data Linkage',
            enum_guidance_category_personnel_conditions: 'Personnel Conditions',
            enum_guidance_category_pricing_and_public_asset: 'Pricing & Public Asset',
            enum_guidance_category_procedural_overlay: 'Procedural Overlay',
            enum_guidance_category_public_sector_ai: 'Public-Sector AI',
            enum_guidance_category_requirement_detail: 'Requirement Detail',
            enum_guidance_category_responsibilities: 'Responsibilities',
            enum_guidance_category_risk_and_remediation: 'Risk & Remediation',
            enum_guidance_category_sandbox: 'Sandbox',
            enum_guidance_category_scope: 'Scope',
            enum_guidance_category_security_drills_incidents: 'Security Drills & Incidents',
            enum_guidance_category_submission_channel: 'Submission Channel',
            enum_guidance_category_transaction_evidence: 'Transaction Evidence',
            enum_guidance_category_transparency_labeling: 'Transparency Labeling',
            enum_module_evidence_management: 'Evidence Management',
            enum_module_security: 'Security',
            form_category_access: 'Access',
            form_category_application: 'Application',
            form_category_authority_response_record: 'Authority Response Record',
            form_category_business_plan: 'Business Plan',
            form_category_capital_adequacy: 'Capital Adequacy',
            form_category_capital_adequacy_report: 'Capital Adequacy Report',
            form_category_certificate: 'Certificate',
            form_category_classification: 'Classification',
            form_category_compliance: 'Compliance',
            form_category_cross_border_transfer: 'Cross-Border Transfer',
            form_category_decision: 'Decision',
            form_category_email_submission_record: 'Email Submission Record',
            form_category_evidence: 'Evidence',
            form_category_evidence_pack: 'Evidence Pack',
            form_category_evidence_pack_manifest: 'Evidence Pack Manifest',
            form_category_forms: 'Forms',
            form_category_information_system: 'Information System',
            form_category_internal_audit: 'Internal Audit',
            form_category_internal_audit_report: 'Internal Audit Report',
            form_category_internal_control: 'Internal Control',
            form_category_internal_control_report: 'Internal Control Report',
            form_category_model_risk: 'Model Risk',
            form_category_notification: 'Notification',
            form_category_official_certificate: 'Official Certificate',
            form_category_official_reference: 'Official Reference',
            form_category_policy: 'Policy',
            form_category_record: 'Record',
            form_category_remediation: 'Remediation',
            form_category_report: 'Report',
            form_category_reporting: 'Reporting',
            form_category_risk: 'Risk',
            form_category_risk_data: 'Risk Data',
            form_category_risk_management_report: 'Risk Management Report',
            form_category_sandbox: 'Sandbox',
            form_category_security_plan: 'Security Plan',
            form_category_technical_plan: 'Technical Plan',
            form_category_training: 'Training',
            unit_absolute_date: 'Absolute date',
            unit_annual: 'Annual',
            unit_before_transfer: 'Before transfer',
            unit_before_use: 'Before use',
            unit_calendar_days_after_event: 'Calendar days after event',
            unit_calendar_days_before_event: 'Calendar days before event',
            unit_date: 'Date',
            unit_days: 'Days',
            unit_event: 'Event',
            unit_hours_from_authentication: 'Hours from authentication',
            unit_month: 'Month',
            unit_months: 'Months',
            unit_months_from_future_effective_date: 'Months from future effective date',
            unit_on_request: 'On request',
            unit_quarterly: 'Quarterly',
            unit_upon_request: 'Upon request',
            unit_weeks: 'Weeks',
            unit_without_undue_delay: 'Without undue delay',
            unit_working_days_before_event: 'Working days before event',
            unit_year: 'Year',
            unit_year_or_material_change: 'Year or material change',
            unit_years: 'Years',
            unit_years_minimum: 'Years (minimum)',
            unit_day: 'Day',
            unit_week: 'Week',
            sla_type_phased_installation_obligation: 'Phased installation obligation',
            sla_type_transitional_window_end: 'Transitional period ends',
            sla_type_data_retention_minimum: 'Data retention, minimum',
            sla_type_authority_appraisal_result_d356_as_issued: 'Authority’s appraisal result (text as issued)',
            sla_type_authority_appraisal_result_amended: 'Authority’s appraisal result (amended text)',
            sla_type_authority_appraisal_result_nq22: 'Authority’s appraisal result (transitional text)',
            sla_type_transitional_end_date: 'Automatic-debit transition ends',
            sla_type_pre_launch_report_submission: 'Pre-launch report to the State Bank',
            sla_type_testing_term_ceiling: 'Maximum testing term',
            sla_type_end_of_purpose_disposition: 'Return, deletion or destruction when the purpose ends',
            sla_type_publicity_on_regulator_requirement: "Notice and publicity on the regulator's requirement",
            sla_type_information_on_regulator_request: "Information provided on the regulator's request",
            sla_type_depositor_mismatch_handling_period: "Handling period for a depositor's mismatch report",
            sla_type_depositor_lookup_start: 'Depositor lookup start',
            sla_type_periodic_security_assessment: 'Annual security assessment',
            sla_type_periodic_software_list_review: 'Approved software list review',
            sla_type_periodic_risk_review: 'Risk situation review',
            sla_type_periodic_continuity_drill: 'Continuity drill',
            sla_type_periodic_staff_training: 'Staff security training',
            sla_type_record_retention_minimum: 'Online log retention, minimum',
            sla_type_record_backup_minimum: 'Log backup retention, minimum',
            sla_type_periodic_vulnerability_scan: 'Internet-facing vulnerability scan',
            sla_type_remediation_patch_window_critical_internet_facing: 'Critical vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_critical_other: 'Critical vulnerability patch, other components',
            sla_type_remediation_patch_window_high_internet_facing: 'High-rated vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_high_other: 'High-rated vulnerability patch, other components',
            sla_type_periodic_security_review: 'Mobile app security review',
            sla_type_credential_max_validity: 'PIN and password, maximum validity',
            sla_type_credential_default_validity: 'Credential, default validity',
            sla_type_otp_max_validity_sms: 'SMS OTP, maximum validity',
            sla_type_otp_max_validity_voice: 'Voice OTP, maximum validity',
            sla_type_otp_max_validity_email: 'Email OTP, maximum validity',
            sla_type_otp_max_validity_matrix_card: 'OTP from an OTP matrix card, maximum validity',
            sla_type_otp_max_validity_soft: 'Soft OTP code, maximum validity',
            sla_type_otp_max_validity_token: 'Token OTP, maximum validity',
            sla_type_card_max_usable_life: 'OTP matrix card, maximum usable life',
            sla_type_confirmation_request_validity: 'Two-channel confirmation request, validity',
            sla_type_biometric_match_time_limit_device: 'Device biometric match, time limit',
            sla_type_biometric_match_time_limit_stored_by_organisation: 'Biometric match against data held by the organisation, time limit',
            sla_type_ai_risk_classification_before_use: 'AI risk classification (before use)',
            sla_type_annual_risk_assessment_report: 'Annual risk assessment report',
            sla_type_annual_training_readiness: 'Annual training readiness',
            sla_type_art_30_records_availability_to_dpa: 'Art. 30 records availability to DPA',
            sla_type_audit_window_type_i: 'Audit window (Type I)',
            sla_type_audit_window_type_ii_min: 'Audit window (Type II, minimum)',
            sla_type_audit_window_type_ii_typical: 'Audit window (Type II, typical)',
            sla_type_authentication_result_retention_readiness: 'Authentication result retention readiness',
            sla_type_breach_communication_to_data_subjects: 'Breach communication to data subjects',
            sla_type_breach_notification_to_supervisory_authority: 'Breach notification to supervisory authority',
            sla_type_classification_lifecycle_review: 'Classification lifecycle review',
            sla_type_consultation_end: 'Consultation end',
            sla_type_annual_activity_report_proposed: 'Annual activity report (proposed)',
            sla_type_approved_list_publication_proposed: 'Approved list publication (proposed)',
            sla_type_course_result_report_proposed: 'Course result report (proposed)',
            sla_type_foreign_certificate_application_proposed: 'Foreign certificate recognition application (proposed)',
            sla_type_network_registration_review_proposed: 'Training network registration review (proposed)',
            sla_type_recognised_body_change_notice_proposed: 'Recognised body change notice (proposed)',
            sla_type_agency_label_publication_proposed: 'Agency publication of the label and DPID (proposed)',
            sla_type_label_dpid_update_proposed: 'Label update with the DPID (proposed)',
            sla_type_inspection_annual_report_proposed: 'Annual inspection report to the Ministry (proposed)',
            sla_type_content_takedown_response: 'Content takedown response',
            sla_type_cookie_consent_reconfirmation: 'Cookie consent reconfirmation',
            sla_type_corrective_action_default: 'Corrective action (default)',
            sla_type_cross_border_core_authority_review_complex: 'Cross-border core data authority review (complex)',
            sla_type_cross_border_core_authority_review_standard: 'Cross-border core data authority review (standard)',
            sla_type_cross_border_exception_postfiling: 'Cross-border exception (post-filing)',
            sla_type_cross_border_important_prefiling: 'Cross-border important data (pre-filing)',
            sla_type_ctia_dossier_filing: 'CTIA dossier filing',
            sla_type_cross_border_dossier_applicant_completion: 'Applicant completes the cross-border transfer impact dossier',
            sla_type_database_update: 'Database update',
            sla_type_decision_effective_date: 'Decision effective date',
            sla_type_decree_effective_date: 'Decree effective date',
            sla_type_circular_effective_date: 'Circular effective date',
            sla_type_system_upgrade_completion: 'System upgrade completion',
            sla_type_incident_recovery_resumption: 'Resumption after an incident is fixed',
            sla_type_report_correction_resubmission: 'Report correction and resubmission',
            sla_type_regulator_review_period: 'Authority review period',
            sla_type_submission_window_lock: 'Submission window lock',
            sla_type_transitional_reporting_regime: 'Transitional reporting arrangement',
            sla_type_staged_commencement_date: 'Staged commencement date',
            sla_type_decree_issued: 'Decree issued',
            sla_type_direct_marketing_opt_out_honor: 'Direct marketing opt-out honoring',
            sla_type_document_review_annual: 'Document review (annual)',
            sla_type_dpia_prior_consultation_dpa_response: 'DPIA prior consultation — DPA response',
            sla_type_dsar_refusal_notification: 'DSAR refusal notification',
            sla_type_dsar_response: 'DSAR response',
            sla_type_dsar_response_extension: 'DSAR response extension',
            sla_type_emergency_drill_cadence: 'Emergency drill cadence',
            sla_type_entrusted_processing_record_retention: 'Entrusted processing record retention',
            sla_type_event_review: 'Event review',
            // Sprint 62.49.1 (DB-10) — the consumer-rights pack's acknowledgement-of-receipt row.
            sla_type_consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
            sla_type_food_traceability_records_request: 'Food traceability records request (24 hours)',
            sla_type_food_traceability_system_update: 'Food traceability system update',
            sla_type_evidence_currency_limit: 'Personal record statement currency (no more than 6 months before filing)',
            sla_type_framework_effective_date: 'Framework effective date',
            sla_type_high_risk_conformity_assessment_before_use: 'High-risk conformity assessment (before use)',
            sla_type_ict_systems_yearly_testing: 'ICT systems yearly testing',
            sla_type_important_core_transfer_approval_readiness: 'Important/core data transfer approval readiness',
            sla_type_incident_early_warning: 'Incident early warning',
            sla_type_incident_final_report: 'Incident final report',
            sla_type_incident_initial_notification: 'Incident initial notification',
            sla_type_cybersecurity_law_art45_1_assigned_level_protection_transition: 'Information systems with an assigned security level — protection conditions transition',
            sla_type_incident_initial_notification_outer_limit: 'Incident initial notification — outer limit from awareness',
            sla_type_incident_intermediate_report: 'Incident intermediate report',
            sla_type_incident_notification: 'Incident notification',
            sla_type_information_sharing_arrangement_notification: 'Information-sharing arrangement notification',
            sla_type_internal_audit_annual: 'Internal audit (annual)',
            sla_type_internal_post_consultation_review: 'Internal post-consultation review',
            sla_type_internal_post_session_review: 'Internal post-session review',
            sla_type_internal_review: 'Internal review',
            sla_type_law_effective_date: 'Law effective date',
            sla_type_lawful_deletion_request: 'Lawful deletion request',
            sla_type_management_body_training_cycle: 'Management body training cycle',
            sla_type_management_review_annual: 'Management review (annual)',
            sla_type_national_assembly_session: 'National Assembly session',
            sla_type_national_assembly_plenary_submission: 'Government submission heard by the National Assembly',
            sla_type_national_synthetic_database_response: 'National synthetic database response',
            sla_type_personnel_reassessment_readiness: 'Personnel reassessment readiness',
            sla_type_platform_registration_amendment: 'Platform registration amendment',
            sla_type_processing_log_retention: 'Processing log retention',
            sla_type_recertification_annual: 'Recertification (annual)',
            sla_type_recertification_cycle: 'Recertification cycle',
            sla_type_reclassification_authority_notification: 'Reclassification authority notification',
            sla_type_recurring_review: 'Recurring review',
            sla_type_reference_issued: 'Reference issued',
            sla_type_remediation_default: 'Remediation (default)',
            sla_type_risk_management_review_cycle: 'Risk management review cycle',
            sla_type_risk_management_self_assessment_cycle: 'Risk management self-assessment cycle',
            sla_type_roadmap_issued: 'Roadmap issued',
            sla_type_ropa_review_cycle: 'RoPA review cycle',
            sla_type_sandbox_certificate_review_ministry: 'Sandbox certificate review (ministry)',
            sla_type_sandbox_certificate_review_multi_authority: 'Sandbox certificate review (multi-authority)',
            sla_type_sandbox_certificate_review_provincial: 'Sandbox certificate review (provincial)',
            sla_type_sandbox_completion_report: 'Sandbox completion report',
            sla_type_sandbox_completion_certificate_review: 'Sandbox completion certificate review',
            sla_type_sandbox_dossier_validity_check: 'Sandbox dossier validity check',
            sla_type_sandbox_extension_application: 'Sandbox extension application',
            sla_type_sandbox_extension_decision: 'Sandbox extension decision',
            sla_type_sandbox_transitional_extension_request: 'Transitional operation extension request',
            sla_type_sandbox_transitional_extension_decision: 'Transitional operation extension decision',
            sla_type_sandbox_periodic_report_level_1_2: 'Sandbox periodic report (level 1–2)',
            sla_type_sandbox_periodic_report_level_3: 'Sandbox periodic report (level 3)',
            sla_type_sandbox_serious_incident_report: 'Sandbox serious incident report',
            sla_type_self_sovereign_log_sync_readiness: 'Self-sovereign log sync readiness',
            sla_type_serious_incident_final_report: 'Serious incident final report',
            sla_type_serious_incident_preliminary_other: 'Serious incident preliminary report (other)',
            sla_type_serious_incident_preliminary_severe: 'Serious incident preliminary report (severe)',
            sla_type_state_agency_ai_impact_assessment_before_use: 'State-agency AI impact assessment (before use)',
            sla_type_supply_chain_reassessment_cycle: 'Supply-chain reassessment cycle',
            sla_type_surveillance_audit_annual: 'Surveillance audit (annual)',
            sla_type_telecom_breach_authority_notification: 'Telecom breach — authority notification',
            sla_type_telecom_breach_authority_supplement: 'Telecom breach — authority supplement',
            sla_type_telecom_breach_subject_notification: 'Telecom breach — subject notification',
            sla_type_third_party_register_annual_reporting: 'Third-party register annual reporting',
            sla_type_tlpt_cycle: 'TLPT cycle',
            sla_type_traceability_system_connection_start: 'Traceability system connection start',
            sla_type_traffic_data_erasure_review: 'Traffic data erasure review',
            sla_type_transition_existing_systems: 'Transition — existing systems',
            sla_type_transition_new_systems: 'Transition — new systems',
            sla_type_transition_readiness: 'Transition readiness',
            guidance_related_link: 'Orientació relacionada',
            deadlines_empty: "No s'han trobat terminis reguladors per a aquest marc.",
            deadlines_sla_type: 'Tipus de SLA',
            deadlines_value: 'Termini',
            deadlines_unit: 'Unitat',
            deadlines_extension: 'Extensió',
            deadlines_last_day: 'Last day: {date}',
            deadlines_date_basis_computed: 'computed from the {period} period under the counting rule the legal reference names; not printed in the text',
            deadlines_date_basis_printed: 'printed in the signed text',
            deadlines_date_basis_next_working_day: 'an end on a non-working day moves to the next working day',
            deadlines_reference: 'Referència legal',
            framework_stack: 'Estructura del marc',
            layer_parent_law: 'Llei principal',
            layer_implementing_decree: "Decret d'implementació",
            layer_enforcement_decree: 'Decret sancionador',
            layer_admin_procedures: 'Procediments administratius',
            // fix-sprint-20260912-1003 — Framework Stack layer groups and row states.
            layer_prime_minister_decision: 'Prime Minister Decision',
            layer_official_roadmap: 'Reference',
            layer_child_instrument: 'Instruments subordinats',
            stack_status_in_force: 'In force',
            stack_status_in_force_reference: 'In force (reference)',
            stack_status_in_force_until: 'In force to {date}',
            stack_status_from_date: 'From {date}',
            stack_status_draft: 'Draft',
            stack_status_promulgated: 'Promulgated {date}',
            stack_status_superseded: 'Superseded {date}',
            stack_status_reference: 'Reference',
            stack_status_unknown: 'Status not recognised',
            stack_show_lineage: 'Show lineage',
            stack_became: 'enacted as this instrument on {date}',
            stack_replaced_by: 'replaced by this instrument on {date}',
            stack_became_law: 'enacted as this law on {date}',
            stack_replaced_by_law: 'replaced by this law on {date}',
            stack_has_lineage_promulgated: 'enacted from a draft',
            stack_has_lineage_superseded: 'replaced an earlier instrument',
            stack_no_framework_page: 'no framework page',
            status_active: 'Actiu',
            status_draft: 'Esborrany',
            draft_enforcement_badge: 'Esborrany — encara no promulgat',
            effective_date_draft: 'Esborrany — encara no vigent',
            effective_date: "Data d'entrada en vigor",
            forms_table_id: 'ID del formulari',
            forms_table_name: 'Nom',
            forms_table_source: 'Font',
            forms_table_category: 'Categoria',
            forms_table_type: 'Tipus',
            fill_form: 'Emplenar formulari',
            view_reference: 'Veure Referència',
            forms_empty: "No s'han trobat formularis per a aquest marc.",
            forms_loading: 'Carregant formularis...',
            filter_by_source_layer: "Filtra per capa d'origen",
            all_layers: 'Totes les capes',
            procedures_id: 'ID procediment',
            procedures_name: 'Procediment',
            procedures_trigger: 'Activador',
            procedures_steps: 'Passos',
            procedures_outputs: 'Resultats',
            procedures_related_forms: 'Formularis relacionats',
            procedures_submission_channels: "Canals d'enviament",
            procedures_empty: "No s'han trobat procediments per a aquest marc.",
            fines_area: "Àrea d'infracció",
            fines_articles: 'Articles',
            fines_individual: 'Multa (individual)',
            fines_organization: 'Multa (organització)',
            fines_revenue: 'Escalació per ingressos',
            fines_deadlines: 'Terminis',
            fines_remediation: 'Mesures correctores',
            fines_related: 'Procediments relacionats',
            fines_empty: 'No hi ha dades de sancions disponibles per a aquest marc.',
            // fix-sprint D1 F22 — `fetchWatchlistFrameworks`'s catch set an empty
            // watchlist with no flag, so a failed read printed "No draft or upcoming
            // frameworks" — a POSITIVE finding about the regulatory horizon, and the
            // most consequential thing that tab can say wrongly.
            watchlist_load_failed: 'The draft and upcoming frameworks could not be read. Reload to try again.',
            watchlist_retry_subject: 'the regulatory watchlist',
            /*
              fix-sprint D1 F9 — the six regulatory-content tabs on the framework
              detail view swallowed every read failure into an empty array and then
              STATED absence: "No requirements found for this framework.", "No forms
              found…", "No procedures found…", "No guidance content…". On a
              regulatory surface "this framework declares no requirements" is a claim
              an operator acts on, and a failed read is not that claim. Each tab now
              states the failure instead, beside the reload that clears it.
            */
            content_reload: 'Reload',
            content_reloading: 'Reloading…',
            requirements_load_failed: 'The requirements for this framework could not be read. Reload to try again.',
            forms_load_failed: 'The forms for this framework could not be read. Reload to try again.',
            procedures_load_failed: 'The procedures for this framework could not be read. Reload to try again.',
            deadlines_load_failed: 'The deadlines for this framework could not be read. Reload to try again.',
            fines_load_failed: 'The enforcement data for this framework could not be read. Reload to try again.',
            guidance_load_failed: 'The guidance for this framework could not be read. Reload to try again.',
            content_partial_failed: 'Part of this content could not be read, so what is shown may be incomplete. Reload to try again.',
            requirements_retry_subject: 'the requirements for this framework',
            forms_retry_subject: 'the forms for this framework',
            procedures_retry_subject: 'the procedures for this framework',
            deadlines_retry_subject: 'the deadlines for this framework',
            fines_retry_subject: 'the enforcement data for this framework',
            guidance_retry_subject: 'the guidance for this framework',
            fines_risk_type: 'Tipus de risc',
            fines_risk_triggers: 'Què el desencadena',
            fines_risk_only_note: 'Aquestes entrades descriuen risc de compliment i operatiu, no sancions econòmiques. Quan apliquen multes, es defineixen en el marc principal.',
            fines_enforcement_pending: "Marc sancionador encara no publicat. Les dades de sancions s'afegiran quan es promulgui l'instrument d'aplicació.",
            view_details: 'Veure detalls',
            back_to_list: 'Tornar a la llista',
            description: 'Descripció',
            jurisdiction: 'Jurisdicció',
            lineage: 'Llinatge',
            replaces: 'Substitueix',
            replaced_by: 'Substituït per',
            requirements: 'Requisits',
            tab_navigation: 'Pestanyes de detall del marc',
            forms_table_actions: 'Accions',
            create_form: 'Crear formulari',
            mark_promulgated: 'Marcar com a promulgat',
            status_promulgated: 'Promulgat',
            promulgated_success: 'Estat de la capa actualitzat a promulgat',
            transition_review: 'Revisió de transició',
            transition_review_desc: "Aquest marc té una revisió de transició pendent per a l'adopció de la llei successora.",
            transition_review_by: 'Data límit de revisió',
            transition_review_status: 'Estat de la revisió',
            transition_review_notes: 'Notes',
            transition_review_pending: 'Pendent',
            transition_review_in_progress: 'En curs',
            transition_review_completed: 'Completada',
            transition_review_successor: 'Marc successor',
            transition_review_lead_time: 'Temps de preparació',
            transition_review_lead_time_days: "{days} dies abans de la data d'entrada en vigor",
            transition_review_overdue: 'overdue',
            transition_review_forms_heading: 'Revisions de transició de formularis',
            transition_review_forms_desc: 'Reviseu cada plantilla operativa per verificar la seva rellevància sota el marc successor.',
            transition_review_outcome: 'Resultat',
            transition_review_outcome_carry_forward: 'Mantenir',
            transition_review_outcome_carry_forward_revised: 'Mantenir (revisat)',
            transition_review_outcome_retire: 'Retirar',
            transition_review_outcome_split: 'Dividir',
            transition_review_outcome_awaiting_decree: 'Esperant decret',
            transition_review_no_forms: 'No hi ha revisions de transició de formularis configurades.',
            transition_review_update_success: 'Revisió de transició actualitzada correctament',
            transition_review_operational_note: "Aquestes són plantilles operatives de suport als processos, no recomptes de formularis legals. L'atribució legal roman amb el marc antic fins que la llei successora entri en vigor.",
            rights_tab: 'Drets',
            rights_empty: "No s'han definit drets de subjecte de dades per a aquest marc.",
            rights_code: 'Codi',
            rights_name: "D'acord",
            rights_legal_basis: 'Base legal',
            rights_elaborating_articles: "Elaboració d'articles",
            rights_description: 'Descripció',
            requirements_reference: 'Referència',
            requirements_mandatory: 'Obligatori',
            // Sprint 62.58.0 (AC-B1, AC-B3) — whom each requirement addresses, and the filter over the declared classes.
            requirements_addressed_to: 'Addressed to',
            requirements_addressee_filter_hint: 'Shows the requirements addressed to any class you choose. Nothing is chosen for you.',
            requirements_addressee_count: 'Showing {shown} of {total} requirements',
            requirements_addressee_clear: 'Show all requirements',
            requirements_basis_note_show: 'Read the whole note',
            requirements_basis_note_hide: 'Show less',
            requirements_yes: 'Yes',
            requirements_no: 'No',
            requirements_source: 'Font',
            category_legal_basis: 'Base legal',
            category_data_subject_rights: 'Drets del subjecte de dades',
            category_notification: 'Notificació',
            category_assessment: 'Avaluació',
            category_governance: 'Governança',
            category_record_keeping: 'Manteniment de registres',
            category_cross_border: 'Transfronterer',
            category_technical_measures: 'Mesures tècniques',
            category_incident_reporting: "Informe d'incidències",
            category_risk_management: 'Gestió de riscos',
            category_supply_chain: 'Cadena de subministrament',
            category_certification: 'Certificació',
            child_frameworks: '{count} marc secundari',
            child_frameworks_plural: '{count} marcs secundaris',
            replaces_prefix: 'Substitueix:',
            replaced_by_prefix: 'Substituït per:',
            responsible_prefix: 'Responsable:',
            watchlist_empty_hint: 'Els marcs amb l\'estat "esborrany" o "pròxim" apareixeran aquí per al seguiment.',
            framework_count: '{count} marc',
            framework_count_plural: '{count} marcs',
            hiding_legacy: '(amagant {count} llegat)',
            label_VN_PDPD_DECREE_13: 'PDPD Decret 13/2023',
            label_VN_PDPL_LAW_2025: 'PDPL Llei 91/2025',
            label_VN_PDPL_DECREE_356_2025: 'Decret 356/2025/ND-CP',
            label_VN_PDPL_ADMIN_PROCEDURES_778_2026: 'QĐ 778/QĐ-BCA-A05',
            label_VN_PDPL_ENFORCEMENT_DRAFT_2026: "PDPL Esborrany d'execució 2026",
            label_VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: 'NQ22 Temporary Overlay 2026',
            label_VN_DATA_LAW: 'Llei de dades',
            label_VN_DATA_LAW_DECREE_165_2025: 'Dades Decret 165/2025',
            label_VN_DATA_EXCHANGES_DRAFT_2026: 'Esborrany de borses de dades 2026',
            label_VN_TELECOM_LAW_2023: 'Llei de telecomunicacions 2023',
            label_VN_TELECOM_DECREE_163_2024: 'Decret de telecomunicacions 163/2024',
            label_VN_CYBERSEC_LAW: 'Llei de ciberseguretat (24/2018)',
            label_VN_CYBERSEC_DECREE_53: 'Decret de cibersec 53/2022',
            label_VN_CYBERSEC_LAW_2025: 'Llei 116/2025 de ciberseguretat',
            label_VN_ECOMMERCE_LAW_2025: 'Llei 122/2025 de comerç electrònic',
            label_VN_AI_LAW_2026: 'AI Llei de 2026',
            label_VN_AI_DECREE_142_2026: 'AI Decree 142/2026/NĐ-CP',
            issuer_company: 'Companyia',
            issuer_authority_a05_mps: 'MPS/A05',
            issuer_authority_mic: 'MIC',
            issuer_authority_mps_ndc: 'MPS/NDC',
            issuer_other_authority: 'Autoritat',
            sla_type_rights_request_ack: 'Reconeixement de la sol·licitud de drets',
            'sla_type_Access control review': "Revisió del control d'accés",
            'sla_type_Annual governance attestation': 'Acreditació anual de govern',
            'sla_type_Audit review cycle': "Cicle de revisió d'auditoria",
            'sla_type_Authority request response': "Resposta a la sol·licitud de l'autoritat",
            'sla_type_Change notification processing': 'Processament de notificacions de canvi',
            'sla_type_Complaint acknowledgment': 'Reconeixement de la queixa',
            'sla_type_Complaint resolution': 'Resolució de queixes',
            'sla_type_Confidentiality policy review': 'Revisió de la política de confidencialitat',
            'sla_type_Cybersecurity incident full report': "Informe complet d'incidents de ciberseguretat",
            'sla_type_Cybersecurity incident initial report': "Informe inicial d'incidents de ciberseguretat",
            'sla_type_Data inventory review': "Revisió de l'inventari de dades",
            'sla_type_Disclosure log review cycle': 'Cicle de revisió del registre de divulgació',
            'sla_type_Incident classification decision': "Decisió de classificació d'incidències",
            'sla_type_Information system classification review': "Revisió de classificació del sistema d'informació",
            'sla_type_Notification processing (sales website/app)': 'Processament de notificacions (web/aplicació de vendes)',
            'sla_type_Pen test cycle': 'Cicle de prova del llapis',
            'sla_type_Registration processing (service platform)': 'Tramitació de registre (plataforma de serveis)',
            'sla_type_Risk assessment cycle': "Cicle d'avaluació de riscos",
            'sla_type_Takedown decision (after review)': 'Decisió de retirada (després de la revisió)',
            'sla_type_Termination notice (public)': 'Avís de terminació (públic)',
            sla_type_ai_impact_assessment_completion: "AI finalització de l'avaluació d'impacte",
            sla_type_ai_sandbox_application_review: "AI revisió de l'aplicació sandbox",
            sla_type_ai_serious_incident_report: "AI informe d'incident greu",
            sla_type_ai_system_registration: 'AI registre del sistema',
            sla_type_authority_request_response: "Resposta a la sol·licitud de l'autoritat",
            sla_type_certificate_issuance_review: "Revisió de l'emissió del certificat",
            sla_type_complaint_handling_approach_notice: "Avís d'aproximació a la gestió de queixes",
            sla_type_complaint_incomplete_info_request: "Queixa sol·licitud d'informació incompleta",
            sla_type_data_governance_attestation: 'Acreditació de governança de dades',
            sla_type_dsr_acknowledgment: 'Reconeixement DSR',
            sla_type_dsr_cl2_withdraw_restrict_object: 'DSR (Cl.2) — Retirar / restringir / objecte',
            sla_type_dsr_cl2_withdraw_restrict_object_third_party: 'DSR (Cl.2) — Tercer',
            sla_type_dsr_cl3_access_correct_provide: 'DSR (Cl.3) — Accedir / corregir / proporcionar',
            sla_type_dsr_cl3_access_correct_provide_third_party: 'DSR (Cl.3) — Tercer',
            sla_type_dsr_cl4_deletion: 'DSR (Cl.4) — Supressió',
            sla_type_dsr_cl4_deletion_third_party: 'DSR (Cl.4) — Supressió, tercer',
            sla_type_dsr_cl3_access_third_party: 'DSR (Cl.3) — Access, third party',
            sla_type_chemical_incident_system_declaration: 'Chemical incident — declaration to the System',
            sla_type_code_termination_explanation_window: 'Code termination — window to explain',
            sla_type_code_restoration_by_managing_body: 'Code restoration by the managing body',
            sla_type_traceability_data_update_frequency: 'Traceability data — update frequency',
            sla_type_authority_decision_deadline: 'Authority decision — due date',
            sla_type_authority_list_update_cycle: 'Authority list — update cycle',
            sla_type_dsr_cl4_provide_restrict_third_party: 'DSR (Cl.4) — Restriction, third party',
            sla_type_dsr_cl5_protective_measures: 'DSR (Cl.5) — Mesures de protecció',
            sla_type_telecom_authority_request_response: "Resposta a la sol·licitud de l'autoritat de telecomunicacions",
            sla_type_rights_request_completion: 'Finalització de la sol·licitud de drets',
            sla_type_incident_authority_notification: "Notificació a l'autoritat d'incidència",
            sla_type_incident_subject_notification: 'Notificació del subjecte de la incidència',
            sla_type_dpia_completion: 'DPIA finalització',
            sla_type_data_retention_review: 'Revisió de la retenció de dades',
            sla_type_authority_filing_review: "Revisió de l'expedient de l'autoritat",
            sla_type_authority_supplement_response: "Resposta al suplement de l'autoritat",
            sla_type_authority_resubmission: "Tornada a presentar l'autoritat",
            sla_type_authority_filing_supplement_response: "Resposta al suplement de presentació de l'autoritat",
            sla_type_authority_filing_resubmission: "L'autoritat presenta una nova presentació",
            sla_type_authority_delivery_window: 'Authority delivery window',
            sla_type_authority_examination_window: 'Authority examination window',
            sla_type_authority_notice_trigger: 'Authority notice trigger',
            sla_type_authority_processing_window: 'Authority processing window',
            sla_type_before_event: 'Before the event',
            sla_type_instalment_maximum_period: 'Instalment maximum period',
            sla_type_lead_time_before_event: 'Lead time before the event',
            sla_type_reporting_cycle: 'Reporting cycle',
            sla_type_reporting_period: 'Reporting period',
            sla_type_statutory_filing_window: 'Statutory filing window',
            sla_type_statutory_payment_deadline: 'Statutory payment deadline',
            sla_type_taxpayer_filing_window: 'Taxpayer filing window',
            sla_type_taxpayer_notification_window: 'Taxpayer notification window',
            sla_type_taxpayer_response_window: 'Taxpayer response window',
            sla_type_taxpayer_supplementation_window: 'Taxpayer supplementation window',
            sla_type_transition_deadline: 'Transition deadline',
            sla_type_provision_end_date: 'Provision ceases to apply',
            sla_type_authority_notification_window: 'Authority notification window',
            sla_type_enforcement_trigger: 'Enforcement applies from',
            sla_type_authority_advance_notice: 'Authority advance notice',
            sla_type_refund_limitation_period: 'Refund limitation period',
            sla_type_reinspection_limitation_period: 'Re-inspection limitation period',
            sla_type_violation_classification_threshold: 'Violation classification threshold',
            sla_type_write_off_eligibility_threshold: 'Write-off eligibility threshold',
            // Sprint 62.57.0 Slice E — the certificate register's period rows (stream P's sla_types).
            sla_type_certificate_application_evaluation: 'Evaluation of the certificate application',
            sla_type_certificate_deficiency_notice: 'Notice that the certificate application must be supplemented',
            sla_type_certificate_replacement_decision: 'Decision on re-issue or exchange of the certificate',
            sla_type_certificate_revoked_return: 'Return of a revoked certificate',
            unit_working_days: 'dies laborables',
            unit_calendar_days: 'dies naturals',
            unit_hours: 'hores',
            form_category_dpa: 'DPA',
            form_category_dsr: 'DSR',
            form_category_consent: 'Consentiment',
            form_category_privacy: 'Privadesa',
            form_category_breach: 'Incompliment',
            form_category_dpia: 'DPIA',
            form_category_register: "Registra't",
            form_category_monitoring: 'Seguiment',
            form_category_vendor: 'Venedor',
            form_category_org: 'Organitzacional',
            form_category_sector: 'Sector',
            form_category_data_mapping: 'Mapatge de dades',
            form_category_governance: 'Governança',
            form_category_incident: 'Incident',
            form_category_authority_submission: "Presentació de l'autoritat",
            form_category_notice: 'Avís',
            form_category_assessment_report: "Informe d'avaluació",
            form_category_request: 'Sol·licitud',
            form_category_dossier: 'Dossier',
            form_category_filing: 'Arxivament',
            module_consent: 'Consentiment',
            module_rights_request: 'Sol·licitud de drets',
            module_classification: 'Classificació',
            module_dpia: 'DPIA',
            module_governance: 'Governança',
            module_monitoring: 'Seguiment',
            module_register: "Registra't",
            module_risk: 'Risc',
            module_data_mapping: 'Mapatge de dades',
            module_incident: 'Incident',
            module_assessment: 'Avaluació',
            module_vendor: 'Venedor',
            module_policy: 'Política',
            module_audit: 'Auditoria',
            module_training: 'Formació',
            module_discovery: 'Descobriment',
            // Sprint 62.58.0 (walk O-2) — requirement applicable_module values with no label
            module_sector_licensing: 'Sector Licensing',
            module_electronic_transactions: 'Electronic Transactions',
            module_consumer_protection: 'Consumer Protection',
            module_ecommerce_governance: 'E-Commerce Governance',
            module_identity_verification: 'Identity Verification',
            module_program_governance: 'Program Governance',
            module_compliance_operations: 'Compliance Operations',
            module_vendor_governance: 'Vendor Governance',
            module_cybersecurity: 'Cybersecurity',
            module_authority_direction: 'Authority Direction',
            module_incident_response: 'Incident Response',
            module_data_governance: 'Data Governance',
            module_incident_operations: 'Incident Operations',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_customer_protection: 'Customer Protection',
            module_regulatory_change: 'Regulatory Change',
            module_third_party_risk: 'Third-Party Risk',
            module_legal_register: 'Legal Register',
            module_change_control: 'Change Control',
            module_records: 'Records',
            enum_guidance_type_calculation_guidance: 'Guia de càlcul',
            enum_guidance_type_implementation_guidance: "Guia d'implementació",
            enum_guidance_type_interpretive_note: 'Nota interpretativa',
            enum_guidance_type_legal_guidance: 'Orientació Jurídica',
            enum_guidance_type_procedural_guidance: 'Orientació de procediment',
            enum_guidance_type_sector_guidance: 'Orientació sectorial',
            enum_guidance_type_technical_methodology: 'Metodologia tècnica',
            enum_guidance_category_annual_reporting: 'Informe anual',
            enum_guidance_category_authority_cooperation: "Cooperació de l'autoritat",
            enum_guidance_category_authority_coordination: "Coordinació d'autoritats",
            enum_guidance_category_business_continuity: 'Continuïtat del negoci',
            enum_guidance_category_capital_adequacy: 'Suficiència de capital',
            enum_guidance_category_complaint_handling: 'Tramitació de queixes',
            enum_guidance_category_consent: 'Consentiment',
            enum_guidance_category_consent_management: 'Gestió del consentiment',
            enum_guidance_category_consumer_protection: 'Protecció del consumidor',
            enum_guidance_category_control_environment: 'Entorn de control',
            enum_guidance_category_cross_border: 'Transfronterer',
            enum_guidance_category_cross_border_transfers: 'Transferències transfrontereres',
            enum_guidance_category_data_classification: 'Classificació de dades',
            enum_guidance_category_data_localization: 'Localització de dades',
            enum_guidance_category_data_protection_impact_assessment: "Avaluació d'impacte de protecció de dades",
            enum_guidance_category_direct_marketing: 'Màrqueting directe',
            enum_guidance_category_dpia: 'DPIA',
            enum_guidance_category_governance: 'Governança',
            enum_guidance_category_ict_risk_management: 'Gestió del Risc TIC',
            enum_guidance_category_incident_management: "Gestió d'incidències",
            enum_guidance_category_internal_audit: 'Auditoria Interna',
            enum_guidance_category_isms_documentation: 'Documentació ISMS',
            enum_guidance_category_lawful_basis: 'Base legal',
            enum_guidance_category_licensing: 'Llicència',
            enum_guidance_category_local_presence: 'Presència local',
            enum_guidance_category_logging_and_retention: 'Registre i retenció',
            enum_guidance_category_logical_and_physical_access: 'Accés lògic i físic',
            enum_guidance_category_operational_resilience_testing: 'Prova de resiliència operativa',
            enum_guidance_category_outsourcing: 'Externalització',
            enum_guidance_category_platform_registration: 'Registre de la plataforma',
            enum_guidance_category_quality_of_service: 'Qualitat de Servei',
            enum_guidance_category_risk_classification: 'Classificació del risc',
            enum_guidance_category_risk_management: 'Gestió de riscos',
            enum_guidance_category_scope_definition: "Definició d'abast",
            enum_guidance_category_sectoral_localisation: 'Localització sectorial',
            enum_guidance_category_subscriber_protection: 'Protecció de subscriptors',
            enum_guidance_category_supply_chain: 'Cadena de subministrament',
            enum_guidance_category_system_classification: 'Classificació del sistema',
            enum_guidance_category_system_operations: 'Operacions del sistema',
            enum_guidance_category_telecommunications_data: 'Dades de telecomunicacions',
            enum_guidance_category_third_party_risk: 'Risc de tercers',
            enum_guidance_category_transitional_provisions: 'Disposicions transitòries',
            enum_guidance_category_vulnerability_management: 'Gestió de vulnerabilitats',
            enum_guidance_status_active: 'Actius',
            enum_guidance_status_draft: 'Esborrany',
            // Sprint 62.58.0 (walk D-1) — every guidance status a pack declares
            enum_guidance_status_deprecated: 'Deprecated',
            enum_guidance_status_superseded: 'Superseded',
            enum_guidance_status_upcoming: 'Upcoming',
            enum_guidance_status_legacy: 'Legacy',
            enum_guidance_status_temporary_active: 'Temporarily in effect',
            enum_guidance_status_active_reference: 'In force, reference only',
            enum_module_access_management: "Gestió d'accés",
            enum_module_ai_governance: 'AI Govern',
            enum_module_asset_management: "Gestió d'actius",
            enum_module_audit_trail: "Pista d'auditoria",
            enum_module_breach_notification: "Notificació d'incompliment",
            enum_module_capital_adequacy: 'Suficiència de capital',
            enum_module_classification: 'Classificació',
            enum_module_consent: 'Consentiment',
            enum_module_consent_management: 'Gestió del consentiment',
            enum_module_cross_border_transfers: 'Transferències transfrontereres',
            enum_module_data_localization: 'Localització de dades',
            enum_module_data_mapping: 'Mapatge de dades',
            enum_module_data_protection: 'Protecció de dades',
            enum_module_data_subject_rights: 'Drets del subjecte de dades',
            enum_module_dpia: 'DPIA',
            enum_module_encryption: 'Xifratge',
            enum_module_governance: 'Governança',
            enum_module_incident: 'Incident',
            enum_module_incident_management: "Gestió d'incidències",
            enum_module_internal_audit: 'Auditoria Interna',
            enum_module_isms_documentation: 'Documentació ISMS',
            enum_module_isms_governance: 'Governació ISMS',
            enum_module_logging_and_monitoring: 'Registre i seguiment',
            enum_module_monitoring: 'Seguiment',
            enum_module_policy_management: 'Gestió de polítiques',
            enum_module_risk: 'Risc',
            enum_module_risk_management: 'Gestió de riscos',
            enum_module_training: 'Formació',
            enum_module_vendor: 'Venedor',
            enum_module_vendor_management: 'Gestió de venedors',
            enum_module_vendor_risk: 'Risc del venedor',
            enum_module_vulnerability_management: 'Gestió de vulnerabilitats',
            // Sprint 62.58.0 (walk O-6) — English copy; translated in Phase 25 (R-57).
            enum_guidance_type_annex_reference: 'Annex Reference',
            enum_guidance_type_boundary_guidance: 'Boundary Guidance',
            enum_guidance_type_calculation_methodology: 'Calculation Methodology',
            enum_guidance_type_classification_guidance: 'Classification Guidance',
            enum_guidance_type_cross_framework_mapping: 'Cross-Framework Mapping',
            enum_guidance_type_enforcement_mechanics: 'Enforcement Mechanics',
            enum_guidance_type_follow_on_instrument_watchlist: 'Follow-On Instrument Watchlist',
            enum_guidance_type_framework_overview: 'Framework Overview',
            enum_guidance_type_framework_relationship: 'Framework Relationship',
            enum_guidance_type_framework_relationship_note: 'Framework Relationship Note',
            enum_guidance_type_handling_guidance: 'Handling Guidance',
            enum_guidance_type_implementation_note: 'Implementation Note',
            enum_guidance_type_implementation_timeline: 'Implementation Timeline',
            enum_guidance_type_obligation_map: 'Obligation Map',
            enum_guidance_type_official_reference_artifact: 'Official Reference Artifact',
            enum_guidance_type_operational_boundary: 'Operational Boundary',
            enum_guidance_type_operational_guidance: 'Operational Guidance',
            enum_guidance_type_platform_boundary: 'Platform Boundary',
            enum_guidance_type_readiness_mapping: 'Readiness Mapping',
            enum_guidance_type_reference_note: 'Reference Note',
            enum_guidance_type_regulatory_overview: 'Regulatory Overview',
            enum_guidance_type_requirement_detail: 'Requirement Detail',
            enum_guidance_type_scope_and_applicability: 'Scope and Applicability',
            enum_guidance_type_scope_guidance: 'Scope Guidance',
            enum_module_compliance: 'Compliance',
            enum_guidance_type_source_limitation: 'Source Limitation',
            enum_guidance_type_source_limits: 'Source Limits',
            enum_guidance_type_source_note: 'Source Note',
            enum_guidance_type_technical_reference: 'Technical Reference',
            enum_guidance_type_timeline: 'Timeline',
            enum_guidance_type_transition_guidance: 'Transition Guidance',
            enum_guidance_type_transition_note: 'Transition Note',
            enum_guidance_category_access: 'Access',
            enum_guidance_category_account_eligibility: 'Account Eligibility',
            enum_guidance_category_account_linkage: 'Account Linkage',
            enum_guidance_category_ai_functions: 'AI Functions',
            enum_guidance_category_amendments: 'Amendments',
            enum_guidance_category_annex: 'Annex',
            enum_guidance_category_assessment: 'Assessment',
            enum_guidance_category_audit: 'Audit',
            enum_guidance_category_authority: 'Authority',
            enum_guidance_category_authority_submission: 'Authority Submission',
            enum_guidance_category_boundary: 'Boundary',
            enum_guidance_category_certification: 'Certification',
            enum_guidance_category_claims: 'Platform Claims',
            enum_guidance_category_classification: 'Classification',
            enum_guidance_category_confidentiality: 'Confidentiality',
            enum_guidance_category_connection_procedure: 'Connection Procedure',
            enum_guidance_category_coordination: 'Coordination',
            enum_guidance_category_data_disclosure: 'Data Disclosure',
            enum_guidance_category_data_lifecycle: 'Data Lifecycle',
            enum_guidance_category_data_protection_and_security: 'Data Protection and Security',
            enum_guidance_category_database_regime: 'Database Regime',
            enum_guidance_category_deadlines: 'Deadlines',
            enum_guidance_category_dispute_support: 'Dispute Support',
            enum_guidance_category_document_reuse: 'Document Reuse',
            enum_guidance_category_duty_allocation: 'Duty Allocation',
            enum_guidance_category_effective_date: 'Effective Date',
            enum_guidance_category_electronic_enforcement: 'Electronic Enforcement',
            enum_guidance_category_electronic_submission: 'Electronic Submission',
            enum_guidance_category_enforcement: 'Enforcement',
            enum_guidance_category_enforcement_authority: 'Enforcement Authority',
            enum_guidance_category_enforcement_measures: 'Enforcement Measures',
            enum_guidance_category_evidence: 'Evidence',
            enum_guidance_category_exploitation_duties: 'Exploitation Duties',
            enum_guidance_category_forms_and_transition: 'Forms and Transition',
            enum_guidance_category_framework_map: 'Framework Map',
            enum_guidance_category_framework_mapping: 'Framework Mapping',
            enum_guidance_category_framework_relationships: 'Framework Relationships',
            enum_guidance_category_handling_measures: 'Handling Measures',
            enum_guidance_category_identification: 'Identification',
            enum_guidance_category_identification_code: 'Identification Code',
            enum_guidance_category_implementation: 'Implementation',
            enum_guidance_category_incident: 'Incident',
            enum_guidance_category_legal_basis: 'Legal Basis',
            enum_guidance_category_lifecycle: 'Lifecycle',
            enum_guidance_category_listing: 'Listing',
            enum_guidance_category_mapping: 'Mapping',
            enum_guidance_category_marketing_communications_boundary: 'Marketing Communications Boundary',
            enum_guidance_category_methodology: 'Methodology',
            enum_guidance_category_monitoring: 'Monitoring',
            enum_guidance_category_obligations: 'Obligations',
            enum_guidance_category_penalties: 'Penalties',
            enum_guidance_category_personal_data: 'Personal Data',
            enum_guidance_category_personal_data_protection: 'Personal Data Protection',
            enum_guidance_category_phase_in_timeline: 'Phase-In Timeline',
            enum_guidance_category_platform_boundary: 'Platform Boundary',
            enum_guidance_category_platform_duties: 'Digital Platform Duties',
            enum_guidance_category_platform_operation: 'Digital Platform Operation',
            enum_guidance_category_pricing: 'Pricing',
            enum_guidance_category_privacy: 'Privacy',
            enum_guidance_category_procedures: 'Procedures',
            enum_guidance_category_product_scope: 'Product Scope',
            enum_guidance_category_provenance: 'Provenance',
            enum_guidance_category_public_sector: 'Public Sector',
            enum_guidance_category_readiness: 'Readiness',
            enum_guidance_category_record_keeping: 'Record Keeping',
            enum_guidance_category_reference_data: 'Reference Data',
            enum_guidance_category_regulatory_watch: 'Regulatory Watch',
            enum_guidance_category_related_instruments: 'Related Instruments',
            enum_guidance_category_remediation: 'Remediation',
            enum_guidance_category_reporting: 'Reporting',
            enum_guidance_category_sanctions: 'Sanctions',
            enum_guidance_category_source: 'Source',
            enum_guidance_category_status: 'Status',
            enum_guidance_category_system_connection: 'System Connection',
            enum_guidance_category_technical_measures: 'Technical Measures',
            enum_guidance_category_testing: 'Testing',
            enum_guidance_category_timeline: 'Timeline',
            enum_guidance_category_transition: 'Transition',
            enum_guidance_category_transitional: 'Transitional Rules',
            enum_module_access: 'Access',
            enum_module_access_review: 'Access Reviews',
            enum_module_ai_advisor: 'AI Advisor',
            enum_module_assessment: 'Assessment',
            enum_module_audit: 'Audit',
            enum_module_authority_operations: 'Authority Operations',
            enum_module_authority_direction: 'Authority Direction',
            enum_module_continuity: 'Business Continuity',
            enum_module_deidentification: 'De-identification',
            enum_module_deletion_orchestration: 'Deletion Orchestration',
            enum_module_identity_verification: 'Identity Verification',
            enum_module_legal_matters: 'Legal Matters',
            enum_module_program_governance: 'Program Governance',
            enum_module_rights_request: 'Rights Request',
            enum_module_sector_licensing: 'Sector Licensing',
            enum_module_technical_measures: 'Technical Measures',
            category_access: 'Accés',
            category_availability: 'Disponibilitat',
            category_change_management: 'Gestió del canvi',
            category_classification: 'Classificació',
            category_communication_and_information: 'Comunicació i informació',
            category_compliance: 'Compliment',
            category_confidentiality: 'Confidencialitat',
            category_control_activities: 'Activitats de control',
            category_control_environment: 'Entorn de control',
            category_incident: 'Incident',
            category_internal_audit: 'Auditoria Interna',
            category_internal_control: 'Control intern',
            category_logical_and_physical_access: 'Accés lògic i físic',
            category_mis_data: "Dades del sistema d'informació de gestió",
            category_monitoring: 'Seguiment',
            category_monitoring_activities: 'Activitats de seguiment',
            category_outsourcing: 'Externalització',
            category_privacy: 'Privadesa',
            category_processing_integrity: 'Integritat del processament',
            category_reporting: 'Informes',
            category_risk_assessment: 'Avaluació de riscos',
            category_risk_mitigation: 'Mitigació de riscos',
            category_sector_applicability: 'Aplicabilitat sectorial',
            category_system_operations: 'Operacions del sistema',
            category_training: 'Formació',
            category_vendor: 'Venedor',
            guidance_related_forms_advisory: 'Alguns formularis tenen una guia relacionada: visualitzeu-lo a',
            guidance_related_procedures_advisory: 'Alguns procediments tenen una guia relacionada - veure a la',
            guidance_related_requirements_advisory: "Alguns requisits tenen una guia relacionada: veure'l a",
        },
        channels: {
            title: 'Canals de presentació i terminis',
            channels: 'Canals de presentació',
            add_channel: 'Afegir canal',
            channel_type: 'Tipus',
            name: 'Nom',
            instructions: 'Instruccions',
            active: 'Actiu',
            no_channels: 'Cap canal de presentació configurat',
            portal: 'Portal',
            in_person: 'En persona',
            postal: 'Postal',
            email: 'Correu electrònic',
            add: 'Afegir',
            deadlines: 'Terminis reguladors',
            add_deadline: 'Afegir termini',
            label: 'Etiqueta',
            days: 'Dies',
            day_type: 'Tipus de dia',
            calendar_days: 'Dies naturals',
            working_days: 'Dies laborables',
            no_deadlines: 'No hi ha terminis configurats',
            description: 'Descripció',
            failed_load: 'Error en load channel data',
            channel_added: 'Canal afegit',
            channel_added_announce: 'Canal de presentació afegit',
            failed_add_channel: 'Error en add channel',
            deadline_added: 'Termini afegit',
            deadline_added_announce: 'Termini regulador afegit',
            failed_add_deadline: 'Error en add deadline',
            channel_code: 'Declared channel',
            channel_code_none: 'Not bound to a declared channel',
            channel_code_hint: 'Which channel the instrument provides. Only a channel bound here can be used on a filing.',
            framework_declares_no_channels: 'This framework declares no submission channels',
            framework_not_installed: 'This framework is not installed for your organisation',
        },
        module_links: {
            title: 'Vinculació del marc',
            modules: 'Mòduls vinculats',
            add_module: 'Afegir mòdul',
            module_code: 'Codi del mòdul',
            required: 'Obligatori',
            link: 'Vincular',
            no_modules: 'Cap mòdul vinculat',
            notes: 'Notes',
            personas: 'Persones vinculades',
            add_persona: 'Afegir persona',
            persona_id: 'ID de persona',
            role_description: 'Descripció del rol',
            no_personas: 'Cap persona vinculada',
            failed_load: 'Error en load framework links',
            module_linked: 'Mòdul vinculat',
            module_linked_announce: 'Mòdul vinculat correctament',
            failed_link_module: 'Error en link module',
            persona_linked: 'Persona vinculada',
            persona_linked_announce: 'Persona vinculada correctament',
            failed_link_persona: 'Error en link persona',
        },
        regulatory_updates: {
            statuses: {
                pending: 'Pendent',
                assessed: 'Avaluat',
                applied: 'Aplicat',
                dismissed: 'Descartat',
            },
            title: 'Actualitzacions reguladores i tauler de marcs',
            page_description: 'Cobertura d’un cop d’ull per a cada marc normatiu instal·lat, més el registre de canvis dins del producte amb les actualitzacions reguladores: esmenes, dates d’entrada en vigor i avaluacions d’impacte.',
            coverage_dashboard: 'Tauler de cobertura del marc',
            no_frameworks: "No s'han trobat marcs",
            framework: 'Marc',
            modules: 'Mòduls',
            templates: 'Plantilles',
            forms: 'Formularis',
            channels: 'Canals',
            deadlines: 'Terminis',
            pending: 'Actualitzacions pendents',
            updates: 'Actualitzacions reguladores',
            create_update: 'Crear actualització',
            framework_id: 'ID del marc',
            update_title: 'Títol',
            description: 'Descripció',
            effective_date: "Data d'entrada en vigor",
            amendment_ref: "Referència de l'esmena",
            create: 'Crear',
            no_updates: 'Sense actualitzacions reguladores',
            status: 'Estat',
            impact: 'Impacte',
            change_summary: 'Resum de canvis',
            assess: 'Avaluar',
            failed_load: 'Error en load data',
            update_created: 'Actualització creada',
            update_created_announce: 'Actualització reguladora creada',
            failed_create: 'Error en create update',
            update_assessed: 'Actualització avaluada',
            failed_assess: 'Error en assess update',
            col_pack_version: 'Pack Version',
            amendment_ref_inline: 'Amendment reference: {reference}',
            default_assess_note: 'Impact assessed — review complete',
        },
        framework_relationships: {
            panel_title: 'Marcs relacionats',
            panel_intro: 'Com es relaciona aquest marc amb els altres. Una capa opcional pot aplicar-se sota diversos marcs superiors alhora; cadascun apareix aquí exactament una vegada.',
            outgoing_heading: 'Aquest marc s’aplica a',
            permalink_aria: 'Link to {name} in this list',
            incoming_heading: 'S’aplica sota aquest marc',
            outgoing_caption: 'Marcs als quals apunta aquest marc',
            incoming_caption: 'Marcs que depenen d’aquest marc',
            col_framework: 'Marc',
            col_relationship: 'Relació',
            col_scope: 'Origen',
            scope_global: 'Del contingut del paquet',
            scope_org: 'Específic de l’organització',
            load_failed: 'No s’han pogut carregar els marcs relacionats',
            relationship_kinds: {
                parent: 'Marc superior',
                applies_to: 'S’aplica a',
                cross_reference: 'Referència creuada',
                supersedes: 'Substitueix',
            },
        },
        sector_overlay: {
            sector_labels: {
                banking: 'Banca',
                insurance: 'Assegurances',
                healthcare: 'Sanitat',
                education: 'Education',
                ict_critical: 'TIC crítiques',
                telecom: 'Telecomunicacions',
                energy: 'Energia',
                transport: 'Transport',
                digital_services: 'Serveis digitals',
                digital_technology_industry: 'Indústria de la tecnologia digital',
                manufacturing: 'Manufacturing',
                public_administration: 'Administració pública',
                payments: 'Pagaments',
                publishing: 'Edició',
                crypto_assets: 'Criptoactius',
                other: 'Altres',
            },
            scope_labels: {
                cross_sector: 'Cross-sector',
                security_overlay: 'Security overlay',
                security_control_overlay: 'Security control overlay',
                activity_scope: 'Activity scope',
                process_scope: 'Process scope',
                consumer_facing_process_scope: 'Consumer-facing processes',
                platform_and_einvoice_process_scope: 'Platform and e-invoice processes',
                regulatory_overlay: 'Regulatory overlay',
            },
            sector_label: 'Sector',
            direct_scope_label: 'Àmbit legal directe',
            chip_direct: 'Aplicable directament',
            chip_reference: 'Només referència',
            banner_direct_copy: 'Aplicable directament a la vostra organització. Els informes i terminis sectorials estan actius.',
            banner_reference_copy: 'Només referència — la vostra organització no està classificada en aquest sector.',
            edit_mode_button: 'Edita el mode',
            edit_form_title: 'Canvia el mode d’instal·lació',
            mode_direct_label: 'Directe — legalment aplicable',
            mode_direct_help: 'Programa els terminis sectorials i els ginys del tauler.',
            mode_reference_label: 'Només referència',
            mode_reference_help: 'Es pot consultar però sense terminis ni ginys.',
            reason_note_label: 'Motiu (opcional)',
            reason_note_placeholder: 'p. ex. adquisició d’una filial bancària',
            cancel: 'Cancel·lar',
            confirm: 'Confirmar',
            confirming: 'Desant…',
            save: 'Desar',
            saving: 'Desant…',
            toast_mode_changed: 'S’ha actualitzat el mode de la superposició sectorial.',
            toast_installed: 'Superposició sectorial instal·lada.',
            toast_sectors_saved: 'S’ha desat la identificació sectorial.',
            install_modal_title: 'Instal·lar superposició sectorial',
            install_modal_intro: 'Les superposicions sectorials són paquets normatius opcionals limitats a un sol sector.',
            install_modal_footer: 'Podeu canviar el mode d’instal·lació més endavant des de la pàgina de detall del marc.',
            install_button: 'Instal·lar',
            installed_label: 'Instal·lada',
            uninstall_button: 'Desinstal·la',
            uninstall_confirm: 'Confirmeu la desinstal·lació',
            uninstall_cancel: 'Cancel·la',
            suggested_label: 'Suggerida',
            catalog_page_title: 'Superposicions sectorials',
            catalog_page_intro: 'Paquets normatius opcionals limitats a un sol sector industrial.',
            catalog_empty_title: 'No hi ha superposicions sectorials disponibles',
            catalog_empty_description: 'Els paquets de superposició sectorial publicats a la vostra plataforma apareixeran aquí.',
            authority_label: 'Autoritat',
            org_sectors_title: 'Sectors industrials',
            org_sectors_intro: 'Seleccioneu els sectors industrials en què opera la vostra organització.',
            widget_title: 'Informes de superposició sectorial pendents',
            widget_aria_label: 'Informes de superposició sectorial pendents',
            widget_empty: 'No hi ha terminis pròxims de superposicions sectorials.',
            audit_event_mode_changed: 'S’ha canviat el mode d’instal·lació de la superposició sectorial',
            audit_event_installed: 'Superposició sectorial instal·lada',
            view_details_button: 'Veure detalls →',
            banner_aria_label: 'Estat de la superposició sectorial',
            toast_load_failed: 'No s’han pogut carregar les superposicions sectorials',
            toast_install_failed: 'No s’ha pogut instal·lar la superposició sectorial',
            toast_uninstall_confirm_required: 'Escriviu «{packCode}» per confirmar la desinstal·lació.',
            toast_uninstalled: 'S’ha desinstal·lat {packCode}.',
            toast_uninstall_failed: 'No s’ha pogut desinstal·lar la superposició sectorial',
            uninstall_warning_destructive_label: 'Acció destructiva.',
            uninstall_warning_body: 'La desinstal·lació suprimirà el registre del marc i els terminis normatius persistents associats a {frameworkCode} d’aquesta organització.',
            uninstall_warning_type_to_confirm: 'Escriviu {packCode} per confirmar.',
            uninstall_in_progress: 'Desinstal·lant…',
            widget_view_all: 'Veure totes les superposicions sectorials →',
            widget_due_in_days: 'Venç en {days} d',
            widget_overdue_days: 'Vençut fa {days} d',
            widget_due_today: 'Venç avui',
            widget_due_unknown: '—',
            // Catalog overview, filters and feature-views modal
            catalog_summary_total: 'Overlays',
            catalog_summary_installed: 'Installed',
            catalog_summary_direct: 'Direct',
            catalog_summary_reference: 'Reference only',
            catalog_summary_available: 'Not installed',
            filter_toolbar_label: 'Filter the overlay catalog',
            filter_search_label: 'Search overlays',
            filter_search_placeholder: 'Search by name, code or authority',
            filter_sector_all: 'All sectors and scopes',
            filter_status_all: 'All statuses',
            filter_status_installed: 'Installed',
            filter_status_not_installed: 'Not installed',
            filter_mode_label: 'Install mode',
            filter_mode_all: 'All modes',
            filter_clear: 'Clear filters',
            filter_results: 'Showing {shown} of {total} overlays',
            filter_no_match_title: 'No overlay matches these filters',
            filter_no_match_description: 'Change or clear the filters to see more of the catalog.',
            col_overlay: 'Overlay',
            col_scope: 'Sector or scope',
            col_mode: 'Mode',
            col_authority: 'Authority',
            col_dates: 'Dates',
            col_version: 'Version',
            col_status: 'Status',
            col_views: 'Feature views',
            col_actions: 'Actions',
            date_effective: 'Effective',
            date_issued: 'Issued',
            date_released: 'Released',
            desc_expand: 'Show description',
            desc_collapse: 'Hide description',
            status_not_installed: 'Not installed',
            views_none: 'None',
            views_none_title: 'This overlay declares nothing that a dedicated screen reads.',
            views_button: '{enabled} of {total} enabled',
            views_button_aria: 'Show the feature views that {name} brings into use ({enabled} of {total} enabled)',
            views_modal_title: 'Feature views of {name}',
            views_modal_intro: 'The registers and pages that read what the pack of this overlay declares. A view is enabled once the overlay is installed as Direct and the module it belongs to is switched on.',
            views_col_view: 'View',
            views_col_menu: 'Where in the menu',
            views_col_state: 'State',
            views_open: 'Open',
            views_state_enabled: 'Enabled',
            views_state_reference: 'Reference only — shows nothing until installed as Direct',
            views_state_not_installed: 'Not enabled — overlay not installed',
            views_state_module_off: 'Not enabled — module switched off',
            views_no_access: 'Your role cannot open this view',
            views_module_state_unconfirmed: 'Module settings could not be read, so a switched-off module is not shown here.',
            views_unclaimed_heading: 'Declared without a dedicated screen',
            views_unclaimed_description: 'The pack also declares these record kinds, but no dedicated screen reads them for this overlay: {kinds}.',
            declared_kind_labels: {
                biometric_technical_regulation_commencement: 'Start of the voice and iris biometric technical regulations',
                chemical_traceability_code_termination: 'Chemical traceability code termination',
                chemical_traceability_commencement: 'Start of chemical traceability',
                chemical_traceability_incident_declaration: 'Chemical traceability incident declaration',
                consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
                cybersecurity_fee_schedule_commencement: 'Start of the cybersecurity fee schedule',
                food_database_traceability_commencement: 'Start of food database traceability',
                food_traceability_connection_start: 'Start of the food traceability connection',
                food_traceability_records_request: 'Food traceability records request',
                food_traceability_system_update: 'Food traceability system update',
            },
            views_declared_only: 'No dedicated screen',
            views_declared_only_aria: 'Show what {name} declares without a dedicated screen',
            views_basis_records: 'Reads records the pack declares',
            views_basis_regime: 'Reads a regime the pack declares',
            views_basis_fine_areas: 'Uses fine areas the pack declares',
            views_open_framework: 'Open the framework page',
            views_independent_note: 'Screens that work the same with or without an overlay, such as Legal Holds, are not listed.',
            menu_payments_evidence: 'Payments & eKYC Evidence',
            menu_trust_evidence: 'Trust & Identity Evidence',
            menu_platform_evidence: 'Platform Evidence',
            menu_public_data: 'Public Data Exchange',
            menu_product_compliance: 'Product Compliance & Traceability',
            menu_producer_substance: 'Producer & Substance Obligations',
            menu_trade_customs: 'Trade & Customs Evidence',
            menu_parcel_delivery: 'Parcel & Delivery Evidence',
            view_labels: {
                controlled_testing_cases: 'Controlled-Testing Cases',
                credit_information_supply: 'Credit-Information Supply Register',
                authority_directions: 'Authority Directions',
                regulated_accounts: 'Regulated Accounts',
                sector_licences: 'Sector Licences',
                licence_applications: 'Licence Applications and Fees',
                fee_lines_by_period: 'Fee Lines by Period',
                data_service_providers: 'Data-Service Provider Records',
                pcf_shared_information: 'Shared Supervisory Information',
                pcf_branch_publicity: 'Branch Publicity Evidence',
                pcf_branch_information_requests: 'Branch Information Requests',
                pcf_depositor_enquiries: 'Depositor Enquiries',
                pcf_supervision_system_records: 'Supervision System Records',
                data_exploitation_routes: 'Exploitation Routes',
                data_exploitation_requests: 'Requests and Grants',
                data_exploitation_testing: 'Controlled Testing Records',
                data_exploitation_notices: 'Incident Notices',
                data_exploitation_dispositions: 'End-of-Purpose Disposition',
                data_exploitation_incubator: 'Incubator Records',
                data_exploitation_foreign_cooperation: 'Foreign Cooperation Assessments',
                cybersecurity_assurance_index_periods: 'Assessment Periods',
                cybersecurity_assurance_index_statistics: 'Direction and Management Statistics',
                cybersecurity_assurance_index_supplier_requests: 'Supplier Evidence Requests',
                food_traceability_establishment: 'Food Establishment',
                food_traceability_lot_results: 'Lot Test Results',
                food_traceability_inspections: 'Food Inspections',
                food_traceability_warnings: 'Warnings',
                food_traceability_notices: 'Notices and Quantities',
                food_traceability_decisions: 'Violation Decisions',
                food_traceability_corrections: 'Corrections and Supplier Corrections',
                food_traceability_complaints: 'Complaints',
                food_traceability_routes: 'Connection Routes',
                biometric_capture_components: 'Biometric Capture Components',
                biometric_chain_of_custody: 'Biometric Chain-of-Custody Records',
                biometric_readiness: 'Biometric Readiness Reviews',
                biometric_supervision_records: 'Biometric Supervision and Inspection Records',
                product_classifications: 'Product Classifications',
                conformity_certificates: 'Conformity Certificates',
                student_records: 'Student Records',
                publications: 'Publications',
                publication_assessments: 'Publication Assessments',
                regulatory_placeholders: 'Regulatory Placeholders',
                supervisory_reporting: 'Supervisory Reporting',
                aml_programme: 'AML Programme',
                aml_cases: 'Suspicious-Activity Cases',
                identity_exchanges: 'Cross-Border Identity Exchanges',
                marketing_approvals: 'Marketing Approvals',
                investor_verifications: 'Investor Verifications',
                account_data_disclosures: 'Account Data Disclosures',
                regulatory_disclosures: 'Regulatory Disclosures',
                legal_representatives: 'Legal Representatives',
                penalty_readiness: 'Penalty Readiness',
                exposure_inputs: 'Exposure Inputs',
                enforcement_register: 'Enforcement Register',
                enforcement_cases: 'Enforcement Cases',
                identity_register: 'Identity Register',
                trust_providers: 'Trust Providers',
                electronic_submissions: 'Electronic Submissions',
                electronic_agreements: 'Electronic Agreements',
                payment_services: 'Payment Services',
                ekyc_evidence: 'eKYC Evidence',
                exception_review: 'Exception Review',
                retention_reconciliation: 'Retention Reconciliation',
                seller_register: 'Seller Register',
                transaction_reconciliation: 'Transaction Reconciliation',
                authority_correspondence: 'Authority Correspondence',
                dataset_catalog: 'Dataset Catalog',
                sharing_register: 'Sharing Register',
                open_data_releases: 'Open Data Releases',
                product_register: 'Product Register',
                batch_register: 'Batch & Serial Register',
                trace_timeline: 'Trace Timeline',
                conformity_evidence: 'Conformity Evidence',
                epr_reconciliation: 'EPR Reconciliation',
                chemical_register: 'Chemical Register',
                chemical_traceability_declarations: 'Chemical Traceability Declarations',
                customs_evidence: 'Customs Evidence',
                customs_tax_dossiers: 'Tax Dossiers and Received Documents',
                customs_tax_payments: 'Tax Payments and Due Dates',
                customs_tax_duty_free_lists: 'Duty-Free Lists and Usage Notifications',
                customs_tax_examinations: 'Tax Examinations',
                customs_tax_transition: 'Transition and Applicability',
                parcel_claims: 'Claims',
                postal_authorisations: 'Postal Authorisations',
            },
        },
        phased_obligations: {
            panel_title: 'Obligacions esglaonades',
            panel_aria_label: 'Obligacions esglaonades',
            panel_intro: 'Algunes clàusules d’aquest marc s’activen més tard que el marc mateix.',
            phased_effective_date_label: 'Entrada en vigor esglaonada:',
            status_pending: 'Pendent',
            status_active: 'Activa',
            status_early: 'Activa (implementació anticipada)',
            notice_window_label: 'Finestra de notificació a l’autoritat:',
            days_label: 'dies',
            recipient_label: 'Destinatari:',
            early_implement_button: 'Marca com a implementada anticipadament',
            early_implement_closed: 'Early implementation can be recorded only before the phased effective date, {date}.',
            effective_at_before_hint: 'Choose a date before the phased effective date, {date}.',
            effective_at_too_late: 'The date must be before the phased effective date, {date}.',
            early_implement_modal_title: 'Marca l’obligació esglaonada com a implementada anticipadament',
            early_implement_modal_intro: 'Recording an early implementation stores the date your organisation began applying this obligation. This panel then shows the obligation as active from that date, and shows the date the notice is due, counted from the recorded date. The platform does not send the notice and keeps no record of it being sent.',
            effective_at_label: 'En vigor des de',
            reason_label: 'Motiu (opcional)',
            reason_placeholder: 'p. ex. el consell ha aprovat l’adopció anticipada el 2026-09-01',
            confirm: 'Confirmar',
            toast_early_implemented: 'Implementació anticipada registrada.',
            early_read_failed: 'The early implementations recorded for this framework could not be read. Until they are, the status of each obligation is not shown and none can be marked early-implemented.',
            early_read_subject: 'the early implementations',
            audit_event_early_implemented: 'Obligació esglaonada marcada com a implementada anticipadament',
            widget_rolling_window_label: 'Activacions esglaonades dins dels 90 dies',
            notice_due_label: 'Notice due by the end of',
            notice_due_time_suffix: '(organisation time)',
            notice_rolled_note: 'The last day fell on a weekend or holiday, so it moved to the next working day.',
            notice_platform_sends_nothing: 'The platform does not send the notice and keeps no record of it being sent.',
            notice_basis: {
                day_after_is_day_one_last_day_rolls_past_non_working_day: 'Computed: the day after implementation is day 1, the last day moves past a weekend or holiday.',
                day_after_is_day_one: 'Computed: the day after implementation is day 1.',
            },
            notice_gap: {
                no_notice_clock_declared: 'No notice due date: no installed record states the notice period.',
                notice_clock_ambiguous: 'No notice due date: more than one installed record states a notice period, and none says which one applies.',
                notice_clock_not_computable: 'No notice due date: the platform does not compute the period the installed record states.',
                implemented_on_unreadable: 'No notice due date: the recorded date could not be read.',
                notice_clock_not_in_force: 'No notice due date: the framework is not yet in force on the day the notice would fall due.',
                framework_installed_for_reference: 'No notice due date: this framework is installed for reference, so its clocks are not run.',
                notice_clock_activation_unknown: 'No notice due date: whether the clock binds is not known, because the framework record states no date or could not be read.',
                implemented_on_not_before_phased_date: 'No notice due date: the recorded date is on or after the phased effective date, so the obligation applies from then anyway and was not implemented early.',
            },
        },
        deadline_activation: {
            token_live: 'In force',
            token_upcoming: 'Not yet in force',
            in_force_from: 'In force from {date}',
            start_date_not_stated: 'Start date not stated',
            token_unknown: 'Timing unknown',
            unknown_sentence: 'The framework record this deadline belongs to could not be read, so whether it applies yet is not known.',
            retry: 'Retry',
            retry_busy: 'Retrying…',
            // Gate E12.5 pass 1 (P2-3) — the framework record was read and states no date.
            token_phase_unresolved: 'Phase start not dated',
            phase_unresolved_sentence: 'This deadline applies from a phase of its framework, and the framework does not date that phase, so it is not treated as in force.',
            token_not_stated: 'No effective date stated',
            not_stated_sentence: 'The framework states no date from which this deadline applies, so it is not treated as in force.',
            retry_subject: 'the regulatory deadlines',
            column_heading: 'When it applies',
            widget_overlays_failed: 'The installed sector overlays could not be read.',
            widget_reads_failed: 'Deadlines for {count} sector overlay(s) could not be read.',
            widget_retry_subject: 'the sector overlay deadlines',
            settings_sources_label: 'Deadlines behind this suggestion',
            settings_upcoming_not_counted: 'Not used for the suggestion until it is in force.',
            settings_unknown_not_counted: 'Not used for the suggestion.',
            settings_not_calendar_days_not_counted: 'Not used for the suggestion: its period is not counted in calendar days.',
            settings_retry_subject: 'the framework deadlines',
            channels_retry_subject: 'the submission channels and deadlines',
            token_not_owed: 'Not owed',
            not_owed_framework_status: 'The framework is not in force.',
            not_owed_row_suspended: 'This duty is suspended.',
            not_owed_superseded_by: 'Superseded by {framework}.',
            not_owed_superseded_unnamed: 'Superseded by another framework.',
            not_owed_row_legacy: 'Legacy — no longer in force.',
            not_owed_unrecognised: 'The record states that this duty is not owed.',
            settings_not_owed_not_counted: 'Not used for the suggestion: this duty is not owed.',
        },
        // Sprint 62.58.0 (Slice C) — English for key parity until Phase 25 (R-57).
        framework_briefings: {
            summary_export: {
                title: 'Framework summary: {name}',
                section_status: 'Status and dates',
                col_field: 'Field',
                col_value: 'Value',
                field_code: 'Framework code',
                field_status: 'Status',
                field_issued: 'Issued',
                field_effective: 'In force from',
                draft_not_in_force: 'Draft — not yet in force',
                not_stated: 'Not stated',
                section_parent: 'Parent framework',
                parent_line: "This framework is read with its parent framework, {name} ({code}), status: {status}. The dated rows below are the parent framework's, not this framework's.",
                parent_not_installed: 'The parent framework {code} is not installed in this organization, so its dated rows are not shown.',
                no_parent: 'This framework names no parent framework.',
                parent_no_rows: 'No dated rows of the parent framework are in use in this organization.',
                col_belongs_to: 'Belongs to',
                section_guidance: 'Guidance',
                no_guidance: 'No guidance notes are installed for this framework.',
                col_title: 'Title',
                col_summary: 'Summary',
                section_requirements: 'Requirements',
                no_requirements: 'No requirements are installed for this framework.',
                col_requirement_id: 'ID',
                col_reference: 'Reference',
                col_addressed_to: 'Addressed to',
                col_mandatory: 'Mandatory',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_no_draft: 'No — proposed in a draft',
                mandatory_no_legacy: 'No — the framework is legacy',
                mandatory_no_suspended: "No — the framework's application is suspended",
                section_deadlines: 'Deadlines',
                no_deadlines: 'No deadline rows are installed for this framework.',
                col_deadline: 'Deadline',
                col_date_or_period: 'Date or period',
                col_note: 'Note',
                stated_date: '{date} (as stated)',
                authority_clock: "The authority's period, not a duty of the organization.",
                section_pack: 'Pack',
                pack_line: 'Installed from pack {code}, version {version}.',
                pack_not_recorded: 'The pack this framework was installed from is not recorded.',
                generated_line: "Generated on {date} from this organization's installed framework record. This summary is for reading only; ComplianceOne submits nothing to any authority.",
            },
            record: {
                tab: 'Briefings',
                heading: 'Briefings and consultation comments',
                intro: 'Record the briefings your organization held about this framework and the consultation comments it sent. An entry is kept as recorded; a correction is a new entry that names the one it corrects.',
                boundary: 'A consultation comment records what your organization sent, to whom and when. ComplianceOne submits nothing to any authority.',
                kind_briefing: 'Briefing',
                kind_consultation_comment: 'Consultation comment',
                channel_meeting: 'Meeting',
                channel_email: 'Email',
                channel_document: 'Document',
                channel_webinar: 'Webinar',
                channel_other: 'Other',
                col_kind: 'Kind',
                col_audience: 'Audience',
                col_held_on: 'Date',
                col_channel: 'Channel',
                col_approver: 'Approver',
                col_approval: 'Approval',
                col_pack_version: 'Pack version',
                approval_none: 'No approver named',
                approval_pending: 'Awaiting approval',
                approval_approved: 'Approved on {date}',
                record: 'Record an entry',
                recording: 'Recording…',
                approve: 'Approve',
                approving: 'Approving…',
                approve_aria: 'Approve the entry of {date} for {audience}',
                field_kind: 'Kind',
                field_audience: 'Audience',
                field_audience_hint_comment: 'For a consultation comment: to whom your organization sent it.',
                field_held_on: 'Date held or sent',
                field_held_on_hint: 'A calendar date, not after today.',
                field_channel: 'Channel',
                field_channel_note: 'Channel details',
                field_channel_note_required: 'Describe the channel when it is Other.',
                field_reference: 'Reference',
                field_reference_hint: 'The reference of what was sent, such as a letter number or a portal receipt.',
                field_summary: 'Summary',
                field_approver: 'Approver',
                field_correction_of: 'Corrects entry',
                field_correction_note: 'Correction note',
                empty: 'No briefings or consultation comments are recorded for this framework.',
                load_failed: 'The briefings and consultation comments could not be loaded.',
                record_failed: 'The entry could not be recorded.',
                approve_failed: 'The entry could not be approved.',
                recorded_toast: 'Entry recorded.',
                approved_toast: 'Entry approved.',
                pack_changed: 'Recorded against pack version {version}; this framework has changed since — see Regulatory Updates',
                corrected_by: 'Corrected by a later entry',
                correction_label: 'Correction',
                evidence_count: 'Documents: {count}',
                attach_summary_offer: 'Attach this summary to an entry',
                kind_placeholder: 'Choose a kind',
                channel_placeholder: 'Choose a channel',
                approver_placeholder: 'No approver',
                correction_none: 'Not a correction',
                correction_option: '{kind} of {date} for {audience}',
                correction_picker_failed: 'The earlier entries could not be read, so a correction cannot be chosen now.',
                error_required: 'This field is required.',
                error_held_on_future: 'The date cannot be after today.',
                error_correction_note_required: 'Explain what this entry corrects.',
                error_approver_not_found: 'The approver is not a user of this organization.',
                error_not_approver: 'Only the named approver can approve this entry.',
                error_already_approved: 'This entry is already approved.',
                error_self_approval: 'The approver must be someone other than the person recording the entry.',
                form_intro: 'Record what was held or sent. Documents are added after the entry is recorded, from Documents on its row.',
                summary_count: '{count} of {max} characters',
                loading: 'Loading the entries…',
                retry_subject: 'the entries',
                reload: 'Reload',
                reloading: 'Reloading…',
                pagination: 'Pages of entries',
                col_summary: 'Summary',
                col_documents: 'Documents',
                documents_open: 'Documents',
                documents_open_aria: 'Documents of the entry of {date} for {audience}',
                reference_line: 'Reference: {reference}',
                channel_note_line: 'Channel details: {note}',
                correction_note_line: 'Correction note: {note}',
                corrects_line: 'Corrects the entry of {date}',
                corrects_unknown: 'Corrects an earlier entry',
                pack_changed_open_comparison: 'See what changed',
                pack_changed_open_updates: 'Open Regulatory Updates',
                replaced_since: 'Recorded against this text; it has since been replaced by another framework —',
                pack_version_not_recorded: 'Not recorded',
                recorded_while: 'Framework status when recorded: {status}',
                framework_status_suspended: 'Suspended',
                recorded_line: 'Recorded by {person} on {date}',
                files_heading: 'Documents',
                files_intro: 'The documents behind this entry, such as the slides, the letter sent or the downloaded framework summary.',
                files_slot: 'Documents of this entry',
                files_none: 'No document is attached to this entry.',
                file_hash: 'SHA-256: {hash}',
                file_not_held: 'Document no longer held',
                file_download: 'Download',
                file_remove: 'Remove',
                file_upload: 'Add a document',
                file_bound: 'Document added.',
                file_removed: 'Document removed.',
                file_bind_failed: 'The document could not be added.',
                file_remove_failed: 'The document could not be removed.',
                file_not_owned: 'The entry this document belongs to was not found.',
                file_remove_confirm: 'Remove this document from the entry and delete it?',
                files_approved_kept: 'This entry has been approved, so its documents are kept as approved and cannot be removed. To correct it, record a correction entry.',
                documents_load_failed: 'The documents could not be loaded.',
                export_label: 'Export summary',
                export_done: 'Framework summary exported.',
                export_failed: 'The framework summary could not be exported.',
                attach_summary_hint: 'To keep this summary with an entry, open the Briefings tab, choose Documents on the entry and add the downloaded file.',
                directory_unavailable: 'The list of people could not be read, so an approver cannot be chosen now.',
                approver_retry_subject: 'the list of people',
            },
        },
        // Sprint 62.58.0 (Slice F) — the regulatory-update comparison and its review.
        update_comparison: {
            page: {
                module_guide: "What changed between the text installed before and the text installed now, requirement by requirement, with your organization's reading of it and the records filled under the earlier text.",
                title: 'What changed: {name}',
                title_fallback: 'What changed',
                description: 'The requirements of the text installed before, beside the text installed now. This comparison is information: it does not change what your organization owes.',
                back: 'Back to Regulatory Updates',
                loading: 'Loading the comparison…',
                load_failed: 'The comparison could not be loaded.',
                not_found: 'This regulatory update was not found.',
                retry_subject: 'the comparison',
                reload: 'Reload',
                reloading: 'Reloading…',
                no_comparison: 'This regulatory update holds no requirement comparison. An update recorded by hand, or one installed before comparisons were kept, has none.',
                status_heading: 'Status change',
                status_change: '{from} → {to}',
                replaced_by: 'Replaced by {name}',
                status_not_recorded: 'Not recorded',
                basis_computed: 'Compared requirement by requirement between the two installed versions of this framework.',
                basis_declared: 'Compared using the correspondence the replacing framework declares.',
                derived_on_read: 'These rows were worked out when this page was opened, from the frameworks installed now.',
                from_heading: 'Earlier text',
                to_heading: 'Text installed now',
                side_status: 'Status: {status}',
                side_pack_version: 'Pack version: {version}',
                counts_heading: 'Summary of changes',
                count_line: '{label}: {count}',
                change_changed: 'Changed',
                change_removed: 'Removed',
                change_new: 'New',
                change_unchanged: 'Unchanged',
                rows_heading: 'Requirements',
                filter_label: 'Show',
                filter_all: 'All requirements',
                showing: 'Showing {shown} of {total}',
                col_requirement: 'Requirement',
                col_change: 'Change',
                col_from_title: 'Title in the earlier text',
                col_to_title: 'Title in the text installed now',
                col_mandatory: 'Mandatory',
                col_note: 'Note',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_change: '{from} → {to}',
                not_in_text: 'Not in this text',
                also_corresponds: 'Also corresponds to: {ids}',
                rows_empty: 'No requirement matches this filter.',
                rows_pagination: 'Pages of requirements',
                superseded_texts_not_installed: 'No comparison: one of the two texts is no longer installed in your organization, or the replacing framework no longer declares that it replaces the earlier text. This regulatory update is kept as it was recorded.',
                withheld_other_pack: 'No comparison: the previous record was installed from another pack ({code}).',
                withheld_other_pack_unknown: 'No comparison: the previous record was not installed from this pack.',
            },
            review: {
                heading: "Your organization's reading",
                intro: "Record how each requirement that changed, was removed or is new affects your organization, and what you concluded. A reading is your organization's own record: it does not change what the framework requires, its status or any deadline.",
                record: 'Record comparison review',
                recording: 'Recording…',
                none: 'No comparison review is recorded yet.',
                current_heading: 'Current review',
                recorded_line: 'Recorded by {person} on {date}',
                conclusion: 'Conclusion',
                next_steps: 'Next steps',
                no_next_steps: 'No next steps recorded.',
                readings_heading: 'Readings',
                col_reading: 'Reading',
                reading_affects_us: 'Affects us',
                reading_does_not_affect_us: 'Does not affect us',
                reading_to_assess: 'To assess',
                no_rows_read: 'This review gives no reading for a single requirement.',
                history_heading: 'Earlier reviews',
                history_pagination: 'Pages of earlier reviews',
                col_recorded: 'Recorded',
                col_recorded_by: 'Recorded by',
                col_conclusion: 'Conclusion',
                col_readings: 'Readings',
                readings_summary: '{affects} affect us, {not_affect} do not, {to_assess} to assess',
                modal_intro: 'Give a reading for every requirement that changed, was removed or is new, then state your conclusion.',
                row_legend: '{id}: {title}',
                field_reading: 'Reading',
                field_reading_placeholder: 'Choose a reading',
                field_note: 'Note',
                field_conclusion: 'Conclusion',
                field_next_steps: 'Next steps',
                text_count: '{count} of {max} characters',
                error_reading_required: 'Choose a reading for this requirement.',
                error_conclusion_required: 'State your conclusion.',
                record_failed: 'The comparison review could not be recorded.',
                recorded_toast: 'Comparison review recorded.',
                no_comparison_error: 'This update holds no comparison to review.',
                rows_loading: 'Loading the requirements to read…',
                rows_load_failed: 'The requirements to read could not be loaded.',
                no_rows_to_read: 'Every requirement is unchanged, so only a conclusion is needed.',
            },
            reread: {
                heading: 'Records filled under the replaced text',
                intro: 'These records were filled while the earlier text applied. They are kept as they are: nothing in them is changed, scored or reopened. Re-read each one against the final text.',
                forms_heading: 'Form records',
                col_record: 'Record',
                col_form: 'Form',
                col_status: 'Status',
                col_updated: 'Last updated',
                forms_none: 'No form record was filled under the replaced text.',
                forms_withheld: 'Form records are listed only to people who may read form records.',
                untitled: 'Untitled record',
                forms_pagination: 'Pages of form records',
                questionnaires_heading: 'Vendor questionnaires',
                notice: 'Filled under {from}, which has been replaced by {to} — re-read it against the final text',
                notice_link: 'See what changed',
                notice_link_aria: 'See what changed between {from} and {to}',
                notice_same_text: 'Filled under pack version {version} of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_unversioned: 'Filled under an earlier pack version of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_link_aria: 'See what changed in {name}',
            },
            recheck: {
                will_draft: 'The pack publisher will recheck this draft on {date}.',
                will_legacy: 'The pack publisher will recheck this legacy framework on {date}.',
                will_suspended: 'The pack publisher will recheck this framework, whose application is suspended, on {date}.',
                was_due_draft: 'The pack publisher was due to recheck this draft on {date}; this record has not yet been updated.',
                was_due_legacy: 'The pack publisher was due to recheck this legacy framework on {date}; this record has not yet been updated.',
                was_due_suspended: 'The pack publisher was due to recheck this framework, whose application is suspended, on {date}; this record has not yet been updated.',
                records_line: "This framework's record changes only when a new pack version is installed; your own records are never changed.",
            },
            list: {
                col_changed: 'Changed requirements',
                no_comparison_short: 'No comparison',
                pagination: 'Pages of regulatory updates',
                view_comparison: 'View comparison',
                view_comparison_aria: 'View comparison for {title}',
                reviews_count: 'Reviews: {count}',
            },
        },
        // Sprint 62.49.1 (DB-2) — Framework Detail's Suspension section.
        framework_suspension: {
            heading: 'Suspension',
            intro: 'As the record states it: when the suspension of this framework’s application began, each instrument that suspended it, and what ends it.',
            start_label: 'Began',
            start_value: 'Suspended from {date}',
            basis_label: 'Basis for that day',
            steps_caption: 'Instruments that suspended this framework, in the order they took effect',
            col_instrument: 'Instrument',
            col_issued: 'Issued',
            col_effective: 'Took effect',
            col_until: 'Suspends until',
            col_replaces: 'Replaces',
            replaces_none: 'None',
            until_date: 'Until {date}',
            source_line: 'Read from: {source}',
            end_label: 'Ends',
            end_date: 'On {date}',
            end_event: 'When this happens: {event}',
            event_not_occurred: 'Not yet occurred',
            event_occurred_on: 'Occurred on {date}',
        },
        market_pack_installed: 'Market pack installed',
        missing_market_code: 'Cannot uninstall: missing market code',
        pack_installed_announce: 'Pack installed successfully',
        install_zip_refused_title: 'Uploaded pack files are not installed',
        install_zip_refused_body: 'An uploaded pack .zip must be signed, and no pack signing key is configured for this installation, so every uploaded zip is refused. Packs that ship with the release are installed from Localization (Market Packs, Language Packs and the Overlay Catalog), and each one is checked against the release before it installs.',
        pack_uninstalled: 'Language pack uninstalled',
    },
    partner_marketplace: {
        /**
         * fix-sprint-20260828-2118 F20 — promoted out of
         * `pages/admin/partner-marketplace/strings.ts`.
         *
         * That file is an inline `{ en, vi }` pair resolved by `extractLocale`, so
         * the 14 non-EN/VI locales received the English sentence at all 25 of this
         * module's call sites — on pages whose every other string comes from `t.*`.
         * The wording is 62.5.0's, unchanged.
         */
        selector_truncated: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
        nav: {
            dashboard: 'Tauler de control',
            partner_orgs: 'Organitzacions associades',
            service_tiers: 'Nivells de servei',
            deliverable_templates: 'Plantilles de lliurables',
            deliverables: 'Lliurables',
            brand_profiles: 'Perfils de marca',
            support_tickets: 'Tiquets de suport',
            workflow_templates: 'Plantilles de flux de treball',
            workflows: 'Fluxos de treball',
            escalation: 'Escalament',
            reports: 'Informes',
            audit: "Pista d'auditoria",
        },
        dashboard: {
            title: 'Tauler de socis',
            loading: 'Carregant dashboard data...',
            total_managed_tenants: "Total d'inquilins gestionats",
            avg_health_score: 'Avg Salut Puntuació',
            programme_health_score: 'Partner Programme Health',
            health_band_green: 'Healthy',
            health_band_amber: 'Needs attention',
            health_band_red: 'Critical',
            health_coverage_note: 'Scored from your own partner operations: open tickets, resolution times against target, deliverable timeliness and escalations. It does not cover the compliance posture or data freshness of the tenants you manage.',
            open_tickets: 'Tiquets oberts',
            upcoming_deliverables: 'Lliurables propers',
            escalated_tickets: 'Tiquets escalats',
            managed_tenants: 'Inquilins gestionats',
            partner_tier: 'Soci Nivell',
            managed_tenants_count: "Recompte d'inquilins gestionats",
            onboarding_date: "Data d'incorporació",
            view_details: 'Veure detalls',
            no_partner_orgs: "No s'han trobat partner organizations",
        },
        partner_orgs: {
            title: 'Organitzacions associades',
            new: 'Nou Partner Org',
            contact_name: 'Contacte Nom',
            contact_email: 'Contacte Correu electrònic',
            partner_tier: 'Soci Nivell',
            managed_orgs: 'Organitzacions gestionades',
            onboarding_date: "Data d'incorporació",
            no_orgs: "No s'han trobat partner organizations",
            partner_type: 'Partner Type',
            type_consulting: 'Consulting Firm',
            type_technology: 'Technology Provider',
            type_legal: 'Legal Firm',
            type_audit: 'Audit & Assurance',
            type_managed_services: 'Managed Services Provider',
            type_reseller: 'Reseller',
            contact_phone: 'Contacte Telèfon',
            metadata_json: 'Metadades (JSON)',
            change_status: 'Canviar estat',
            new_status: 'Nou estat',
            invalid_json_metadata: 'JSON invàlid al camp de metadades',
            suspended: 'Suspès',
            offboarded: 'Desvinculat',
            all_tiers: 'Tots els nivells',
            silver: 'Plata',
            gold: 'Or',
            platinum: 'Platí',
            status_offboarded: 'Desvinculat',
            status_suspended: 'Suspès',
            tier_gold: 'Or',
            tier_platinum: 'Platí',
            tier_silver: 'Plata',
            org_status_updated: 'Organization status updated',
        },
        service_tiers: {
            title: 'Nivells de servei',
            new: 'Nou nivell',
            tier_name: 'Nivell Nom',
            monthly_fee: 'Quota mensual',
            features: 'Funcionalitats',
            no_tiers: "No s'han trobat service tiers",
            tier_code: 'Codi de nivell',
            response_time_hrs: 'Resposta Hora (hrs)',
            resolution_time_hrs: 'Temps de resolució (hores)',
            review_frequency: 'Revisar Freqüència',
            sla_response_time: 'SLA Resposta Hora (hours)',
            sla_resolution_time: 'SLA Resolution Hora (hours)',
            module_profile: 'Included Modules',
            module_profile_hint: 'The modules this tier covers. Descriptive only — it records what the tier is sold as, and does not grant or restrict access.',
            module_profile_selected: '{count} of {total} selected',
            module_profile_select_all: 'Select all',
            module_profile_clear_all: 'Clear all',
            module_profile_unknown: 'Unrecognised — kept from an earlier version of this tier',
            comparing: 'Comparant...',
            compare: 'Comparar',
            comparison: 'Comparació de nivells de servei',
            attribute: 'Atribut',
            deprecate: 'Deprecar',
            deprecated: 'Obsolet',
            select_compare: 'Seleccioneu at least 2 tiers to compare',
            comparison_title: 'Comparació de nivells de servei',
            status_deprecated: 'Obsolet',
            activate: 'Activar',
            resolution_time: 'Temps de resolució',
            response_time: 'Resposta Hora',
            select_at_least_two: 'Seleccioneu at least 2 tiers',
        },
        deliverable_templates: {
            title: 'Plantilles de lliurables',
            new: 'Nou plantilla',
            template_name: 'Plantilla Nom',
            deliverable_type: 'Tipus de lliurable',
            no_templates: "No s'han trobat deliverable templates",
            frequency: 'Freqüència',
            est_effort_hrs: 'Esforç est. (h)',
            estimated_effort: 'Esforç estimat (hores)',
            associated_module: 'Mòdul associat',
            linked_tiers_count: 'Recompte de nivells vinculats',
            evidence_requirements: "Requisits d'evidència (separats per comes)",
            tier_ids: 'IDs de nivells (separats per comes)',
            module_none: 'No module',
            module_unrecognised: '{value} — not a platform module; kept as stored',
            frequency_one_time: 'Una vegada',
            frequency_monthly: 'Mensual',
            frequency_quarterly: 'Trimestral',
            frequency_annually: 'Anualment',
            type_report: 'Informe',
            type_review: 'Revisar',
            type_assessment: 'Avaluació',
            type_meeting: 'Reunió',
            type_configuration: 'Configuració',
            freq_one_time: 'Una vegada',
            freq_monthly: 'Mensual',
            freq_quarterly: 'Trimestral',
            freq_annually: 'Anualment',
            status_deprecated: 'Obsolet',
        },
        deliverables: {
            title: 'Lliurables',
            new: 'Nou Deliverable',
            partner: 'Soci',
            deliverable_type: 'Tipus de lliurable',
            due_date: 'Data de venciment',
            no_deliverables: "No s'han trobat deliverables",
            assigned_to: 'Assignat A',
            completion_date: 'Data de finalització',
            update_status: 'Actualitzar Estat',
            hide_details: 'Amagar Detalls',
            view_details: 'Veure detalls',
            evidence_ids: "IDs d'evidències",
            evidence_expected: 'Evidence expected',
            evidence_expected_none: 'No evidence requirements are recorded on this deliverable.',
            client_approval: 'Aprovació del client',
            bulk_update: 'Actualització massiva',
            bulk_update_status: 'Bulk Actualitzar Estat',
            generate_schedule: 'Generar Programar',
            generate_deliverable_schedule: 'Generar Deliverable Programar',
            unassigned: 'Unassigned',
            assigned_to_user: 'Assigned to {name}.',
            assignee_cleared: 'Deliverable is now unassigned.',
            edit_notes: 'Edit notes',
            edit_evidence: 'Edit evidence',
            evidence_select_label: 'Select evidence items',
            evidence_none_available: 'No evidence items are available. Add them in the evidence repository first.',
            approval_note_label: 'Note (optional)',
            approval_status_approved: 'Approved',
            approval_status_rejected: 'Rejected',
            approval_by_at: 'by {name} on {date}',
            approval_recorded: 'Client approval recorded.',
            approval_rejection_recorded: 'Client rejection recorded.',
            notes_saved: 'Notes saved.',
            evidence_saved: 'Evidence updated.',
            generated_count: 'Generated {count} deliverables.',
            generated_none: 'No deliverables were generated. No active deliverable template is linked to this tier — create one under Deliverable Templates and link it to this tier code first.',
            organization_id: 'Organització ID',
            service_tier_id: 'ID del nivell de servei',
            filter_assigned_to: 'Filtrar per Assignat A',
            select_status: 'Seleccioneu status...',
            new_status: 'Nou estat',
            upcoming: 'Proper',
            overdue: 'Vençut',
            status_deferred: 'Ajornat',
            status_overdue: 'Vençut',
            status_upcoming: 'Proper',
        },
        brand_profiles: {
            title: 'Perfils de marca',
            new: 'Nou Brand Profile',
            partner: 'Soci',
            brand_name: 'Nom de marca',
            primary_color: 'Color primari',
            logo_url: 'URL del logotip',
            no_profiles: "No s'han trobat brand profiles",
            secondary_color: 'Color secundari',
            header_text: 'Text de la capçalera',
            footer_text: 'Text del peu de pàgina',
            contact_email: 'Contacte Correu electrònic',
            contact_phone: 'Contacte Telèfon',
            contact_website: 'Lloc web de contacte',
            contact_address: 'Contacte Adreça',
            is_default: 'És Predeterminat',
            set_default: 'Establir predeterminat',
            preview: 'Previsualització',
            report_header_preview: "Previsualització de la capçalera de l'informe",
            report_footer_preview: "Previsualització del peu de l'informe",
            preview_description: "Aquesta és una previsualització de la capçalera de l'informe amb els colors de marca seleccionats.",
            contact_website_short: 'Lloc web',
            logo_preview: 'Previsualització del logotip',
        },
        support_tickets: {
            title: 'Tiquets de suport',
            new: 'Nou Ticket',
            partner: 'Soci',
            subject: 'Titular',
            assigned_to: 'Assignat A',
            no_tickets: "No s'han trobat support tickets",
            category: 'Categoria',
            priority: 'Prioritat',
            escalation_tier: 'Escalament Nivell',
            sla_status: 'SLA Estat',
            update_status: 'Actualitzar Estat',
            escalate: 'Escalar',
            add_note: 'Afegir Note',
            escalate_ticket: 'Escalar tiquet',
            escalate_to_tier: 'Escalar A Nivell',
            notes_timeline: 'Cronologia de notes',
            no_notes: 'Sense notes encara.',
            attachments: 'Adjunts',
            no_attachments: 'Sense adjunts.',
            client_org_id: 'ID Org. del client',
            content: 'Contingut',
            note_type: 'Tipus',
            view_detail: 'Veure Detail',
            hide: 'Amagar',
            new_status: 'Nou estat',
            all_categories: 'Totes les categories',
            all_priorities: 'Totes les prioritats',
            all_tiers: 'Tots els nivells',
            ticket_not_found: 'Tiquet no trobat',
            ticket_id: 'ID del tiquet',
            client_org: 'Org. del client',
            deescalate: 'Desescalar',
            deescalate_ticket: 'Desescalar el tiquet',
            deescalate_to_tier: 'De-escalate A Nivell',
            add_attachment: 'Afegir adjunt',
            file: 'Fitxer',
            linked_entity: 'Entitat vinculada',
            sla_target: 'Destinació',
            breached: 'Violat',
            remaining_hours: 'restants',
            na: 'N/A',
            tickets_found: 'tiquets trobats',
            reason: 'Motiu',
            target: 'Destinació',
            upload: 'Pujar',
        },
        workflow_templates: {
            title: 'Plantilles de flux de treball',
            new: 'Nou plantilla',
            template_name: 'Plantilla Nom',
            steps: 'Passos',
            no_templates: "No s'han trobat workflow templates",
            category: 'Categoria',
            view_steps: 'Veure Steps',
            hide_steps: 'Amagar passos',
            step: 'Pas',
            step_title: 'Títol',
            step_description: 'Descripció',
            assignee_role: 'Assignat Rol',
            sla_hours: 'Hores SLA',
            evidence_required: 'Evidència Obligatori',
            guidance_notes: "Notes d'orientació",
            add_step: 'Afegir Step',
            no_steps: 'Sense passos definits.',
            no_steps_defined: 'Sense passos definits.',
            templates_found: 'plantilles trobades',
            all_categories: 'Totes les categories',
        },
        workflows: {
            client_org: 'Org. del client',
            title: 'Instàncies de flux de treball',
            new: 'Nou flux de treball',
            template: 'Plantilla',
            partner: 'Soci',
            current_step: 'Pas actual',
            progress: 'Progrés',
            no_workflows: "No s'han trobat workflow instances",
            instances_found: 'instàncies trobades',
        },
        escalation: {
            title: 'Escalament Tauler de control',
            ticket: 'Tiquet',
            partner: 'Soci',
            escalated_at: 'Escalat el',
            reason: 'Motiu',
            no_escalations: "No s'han trobat escalations",
            time_in_tier: 'Hora en Nivell',
            auto_evaluate: 'Avaluació auto.',
            priority_critical: 'Crític',
            priority_high: 'Alt',
            priority_medium: 'Mitjà',
            priority_low: 'Low',
            deescalated_from_dashboard: 'Desescalat des del tauler',
            sla_breached: 'SLA incomplert',
            sla_remaining: 'SLA restant: {hours}h',
            sr_total_escalated: 'Total escalated',
            sr_by_tier: 'By tier',
            sr_by_priority: 'By priority',
        },
        reports: {
            client_org: 'Org. del client',
            title: 'Informes',
            partner: 'Soci',
            report_type: 'Informe Tipus',
            generated_at: 'Generat el',
            no_reports: "No s'han trobat reports",
            generate: 'Generar Informe',
        },
        audit: {
            title: 'Soci Auditoria Trail',
            event_type: 'Esdeveniment Tipus',
            entity_type: "Tipus d'entitat",
            entity: 'Entitat',
            entity_id: "ID de l'entitat",
            actor: 'Actor',
            timestamp: 'Marca de temps',
            no_events: "No s'han trobat audit events",
            events_found: 'esdeveniments trobats',
            client_org: 'Organització client',
            all_actors: 'Tots els actors',
            event_type_search: "Cerca tipus d'esdeveniment",
            event_type_created: 'Creat',
            event_type_updated: 'Actualitzat',
            event_type_deleted: "S'ha suprimit",
            event_type_set_default: 'Set as Default',
            event_type_status_changed: 'Status Changed',
            event_type_status_updated: 'Status Updated',
            event_type_generated: 'Generated',
            event_type_export_generated: 'Export Generated',
            event_type_escalated: 'Escalated',
            event_type_auto_escalated: 'Auto-Escalated',
            event_type_deescalated: 'De-escalated',
            event_type_managed_org_added: 'Managed Client Added',
            event_type_managed_org_removed: 'Managed Client Removed',
            event_type_versioned: 'New Version',
            event_type_attachment_added: 'Attachment Added',
            event_type_note_added: 'Note Added',
            event_type_step_advanced: 'Step Advanced',
            event_type_cancelled: 'Cancelled',
            entity_type_partner_org: 'Org. soci',
            entity_type_service_tier: 'Nivell de servei',
            entity_type_deliverable_template: 'Plantilla lliurable',
            entity_type_deliverable_instance: 'Instància lliurable',
            entity_type_brand_profile: 'Perfil de marca',
            entity_type_support_ticket: 'Bitllet de suport',
            entity_type_workflow_template: 'Plantilla de flux de treball',
            entity_type_workflow_instance: 'Instància de flux de treball',
            entity_type_generated_report: 'Informe generat',
            entity_type_audit_export: "Exportació d'auditoria",
        },
        no_permission_title: 'Permisos insuficients',
        no_permission_message: "No teniu permís per accedir a l'espai de treball del soci.",
        status_active: 'Actius',
        status_suspended: 'Suspès',
        status_offboarded: 'Desabordat',
        status_completed: 'Completat',
        status_cancelled: 'Cancel·lat',
        status_deprecated: 'Obsolet',
        status_new: 'Nou',
        status_triaged: 'Triatge',
        status_in_progress: 'En curs',
        status_waiting_on_client: 'Esperant al client',
        status_waiting_on_vendor: 'Esperant al venedor',
        status_escalated: 'Escalada',
        status_resolved: 'Solucionat',
        status_closed: 'Tancat',
        status_pending: 'Pendent',
        status_skipped: 'Saltat',
        client_org_column: 'Client org',
        client_org_unresolved: 'Client org not recorded',
        category_onboarding: 'Incorporació',
        category_incident_escalation: "Escalada d'incidències",
        category_config_change: 'Canvi de configuració',
        category_periodic_review: 'Revisió periòdica',
        category_offboarding: 'Offboarding',
        category_custom: 'Personalitzat',
        category_configuration: 'Configuració',
        category_incident: 'Incident',
        category_compliance_question: 'Pregunta de compliment',
        category_feature_request: 'Sol·licitud de funció',
        category_bug_report: "Informe d'errors",
        priority_critical: 'Crític',
        priority_high: 'Alt',
        priority_medium: 'Mitjana',
        priority_low: 'Baixa',
        tier_silver: 'Plata',
        tier_gold: 'Or',
        tier_platinum: 'Platí',
        report_type_compliance_summary: 'Resum de compliment',
        report_type_risk_overview: 'Visió general del risc',
        report_type_incident_report: "Informe d'incidències",
        report_type_consent_status: 'Estat del consentiment',
        report_type_program_maturity: 'Maduresa del programa',
        report_type_executive_pack: 'Pack executiu',
        note_type_internal: 'Interna',
        note_type_client_visible: 'Client visible',
        status_updated: "L'estat s'ha actualitzat correctament",
        deliverable_status_updated: "L'estat del lliurament s'ha actualitzat correctament",
        ticket_status_updated: "L'estat del bitllet s'ha actualitzat correctament",
        ticket_escalated: "El bitllet s'ha escalat correctament",
        note_added: "La nota s'ha afegit correctament",
        attachment_uploaded: "El fitxer adjunt s'ha penjat correctament",
        ticket_deescalated: "El bitllet s'ha desescalat correctament",
        auto_evaluation_completed: "S'ha completat l'autoavaluació",
        template_created: "La plantilla de flux de treball s'ha creat correctament",
        template_updated: "La plantilla de flux de treball s'ha actualitzat correctament",
        brand_set_default: "El perfil de marca s'ha definit com a predeterminat correctament",
        brand_created: 'Perfil de marca creat amb èxit',
        brand_updated: "El perfil de la marca s'ha actualitzat correctament",
        tier_created: "El nivell de servei s'ha creat correctament",
        tier_updated: "El nivell de servei s'ha actualitzat correctament",
        tier_deprecated: "El nivell de servei s'ha obsolet correctament",
        tier_activated: "El nivell de servei s'ha activat correctament",
        period: 'Període',
        format: 'Format',
        period_start: 'Inici del període',
        period_end: 'Final del període',
        reports_count: '{count} informes',
        reports_count_one: '{count} informe',
        metric: 'mètrica',
        value: 'Valor',
        showing_chart_view: "S'està mostrant la vista del gràfic",
        showing_table_view: "S'està mostrant la vista de la taula de dades",
        evidence_ids_placeholder: 'p. ex. ev-001, ev-002',
        confirm_cancel_workflow: 'Esteu segur que voleu cancel·lar aquest flux de treball? Aquesta acció no es pot desfer.',
        export_csv: 'Exporta CSV',
        compliance_qualifications_title: 'Qualificacions del servei de compliment',
        compliance_qualifications_desc: "The partner tier (Silver, Gold or Platinum) is a label on the partner record. The platform reads it in one place: a support ticket opened for an organisation the partner manages takes its resolution-time target from the partner's active service tier with the same code, or 24 hours when there is none. The tier grants no access and does not qualify a partner for any kind of engagement.",
        regulatory_output_title: 'Alineació de la sortida normativa',
        regulatory_output_desc: "Cada tipus de plantilla s'associa a una categoria de sortida normativa: els informes produeixen documentació de compliment, les revisions validen l'eficàcia del control, les avaluacions cobreixen avaluacions de risc (DPIA, anàlisi de mancances), sessions de govern de documents de reunions. Els requisits d'evidència defineixen els artefactes que cada lliurament ha de fer referència per estar preparat per a l'auditoria.",
        sla_compliance_title: "SLA Seguiment per a l'entrega de compliment",
        sla_compliance_desc: "Els SLA de temps de resposta i resolució són vinculants contractualment per a la prestació de serveis de compliment. La freqüència de revisió determina la freqüència amb què s'avaluen els lliuraments de compliment del partner. Els incompliments de l'SLA en les entrades crítiques per al compliment desencadenen una escalada automàtica i es registren a la pista d'auditoria de partners per obtenir proves normatives.",
        regulatory_engagement_title: 'Patrons de participació normativa',
        regulatory_engagement_desc: "Les categories de flux de treball s'assignen als tipus d'interacció normativa: la incorporació cobreix la configuració inicial del compliment i l'anàlisi de les llacunes, la revisió periòdica gestiona les avaluacions de compliment recurrents, l'escalada d'incidents gestiona la resposta a l'incompliment de la normativa, l'exclusió garanteix que es compleixin les obligacions de retenció de dades. Cada pas pot requerir la recollida d'evidències per a la integritat de la pista d'auditoria.",
        compliance_escalation_title: "Compliment-Procediments d'escalada crítics",
        compliance_escalation_desc: "L1 gestiona problemes operatius estàndard. L'L2 cobreix les incompliments que afecten el compliment (darrers terminis reglamentaris, llacunes de control). L'L3 es reserva per a escenaris d'incompliment de la normativa que requereixen atenció immediata de l'executiu. L'incompliment de SLA als bitllets de L2/L3 genera entrades de pista d'auditoria automàticament. Totes les accions d'escalada i desescalada es registren permanentment.",
        audit_regulatory_title: "Ruta d'evidència normativa",
        audit_regulatory_desc: "Aquesta pista d'auditoria proporciona registres evidents de manipulació per al compliment de la normativa. Cada acció del soci (crear, actualitzar, suprimir, escalar, canviar d'estat) es registra amb la identitat de l'actor, la marca de temps i la diferència de càrrega útil completa. Utilitzeu filtres de tipus d'entitat per aïllar proves per domini de compliment. Exporteu resultats filtrats per produir documentació preparada per a auditories per a les inspeccions reguladores.",
        evidence_trail_title: "Ruta d'evidència entre mòduls",
        evidence_trail_desc: "Cada pas del flux de treball pot recopilar identificadors d'evidència que s'enllacen als lliuraments, esdeveniments d'auditoria i artefactes de compliment entre mòduls. Avançar un pas amb identificadors d'evidència crea una cadena verificable que connecta els fluxos de treball dels socis amb la documentació normativa. Els fluxos de treball completats amb cadenes d'evidències completes serveixen com a prova de lliurament del compromís de compliment.",
        evidence_ids_label: 'ID de prova',
    },
    org_groups: {
        title: "Grups d'organitzacions",
        new: 'Afegir grup',
        group_name: 'Grup Nom',
        member_count: 'Recompte de membres',
        root_org: 'Organització arrel',
        no_groups: "No s'han trobat org groups",
        add_organization: 'Afegir Organització',
        select_organization: 'Seleccioneu una organització...',
        no_available_orgs: 'No hi ha organitzacions disponibles per afegir.',
        no_members: 'No hi ha organitzacions membres en aquest grup.',
        member_added: 'Organització afegida al grup.',
        member_removed: 'Organització eliminada del grup.',
        already_in_group: 'Aquesta organització ja pertany a un grup.',
        org_not_found: "No s'ha trobat l'organització.",
        add_member_error: "No s'ha pogut afegir l'organització al grup.",
        remove_member_error: "No s'ha pogut eliminar l'organització del grup.",
        confirm_remove_member: 'Esteu segur que voleu eliminar "{name}" d\'aquest grup?',
        load_error: "No s'han pogut carregar els detalls del grup d'organitzacions.",
        page_title_list: 'Grups organitzatius',
        page_title_detail: 'Detalls del grup organitzatiu',
        page_title_create: 'Crear un grup organitzatiu',
        page_title_settings: 'Configuració del grup organitzatiu',
        page_title_users: "Grup d'usuaris",
        page_title_reports: 'Informes de grup',
        page_title_dashboard: 'Tauler de control del grup',
        page_title_audit: 'Auditoria de grup',
        page_title_org_audit: 'Auditoria del grup organitzatiu',
        page_title_shared_vendors: 'Venedors compartits',
        page_title_shared_frameworks: 'Marcs compartits',
        page_title_shared_forms: 'Plantilles de formularis compartides',
        page_title_merged_vendors: 'Venedors fusionats',
        page_title_settings_inheritance: 'Herència de configuració',
        announce_loaded_groups: "S'han carregat {count} grups organitzatius",
        announce_loaded_group: "S'ha carregat el grup {name}",
        announce_loaded_users: "S'han carregat {count} usuaris del grup",
        announce_loaded_reports: "S'han carregat {count} informes",
        announce_loaded_vendors: "S'han carregat {count} proveïdors compartits",
        announce_loaded_frameworks: "S'han carregat {count} marcs compartits",
        announce_loaded_templates: "S'han carregat {count} plantilles de formulari compartit",
        announce_loaded_audit: "S'han carregat {count} esdeveniments d'auditoria",
        announce_loaded_merged: "S'han carregat {shared} venedors locals i {local} compartits",
        announce_dashboard_loaded: "S'ha carregat el tauler de control.",
        announce_dashboard_refreshed: "S'ha actualitzat el tauler.",
        announce_dashboard_refresh_failed: "No s'ha pogut actualitzar el tauler.",
        announce_refreshing: "S'està actualitzant el tauler de control...",
        announce_settings_loaded: "S'ha carregat l'herència de la configuració.",
        announce_settings_saved: "La configuració s'ha desat correctament.",
        announce_saving_settings: "S'està desant la configuració...",
        announce_error_saving: "S'ha produït un error en desar la configuració.",
        announce_group_dissolved: 'Grup dissolt.',
        announce_creating_group: "S'està creant un grup organitzatiu...",
        announce_group_created: 'Grup organitzatiu creat correctament.',
        announce_error_creating: "S'ha produït un error en crear el grup organitzatiu.",
        announce_vendor_published: "El venedor s'ha publicat correctament.",
        announce_vendor_retired: 'El venedor es va retirar.',
        announce_framework_published: 'Framework publicat amb èxit.',
        announce_framework_retired: 'Marc retirat.',
        announce_template_published: "La plantilla de formulari s'ha publicat correctament.",
        announce_template_retired: 'Plantilla de formulari retirada.',
        announce_role_assigned: 'Funció assignada correctament.',
        announce_error_assign_role: "S'ha produït un error en assignar la funció.",
        announce_role_revoked: "El paper s'ha revocat correctament.",
        announce_report_generating: "S'està generant un informe...",
        announce_report_requested: "S'ha sol·licitat la generació d'informes.",
        announce_error_report: "S'ha produït un error en sol·licitar l'informe.",
        announce_report_deleted: "S'ha suprimit l'informe.",
        announce_search_applied: 'Cerca aplicada.',
        announce_search_cleared: "S'ha esborrat la cerca.",
        announce_tab_selected: '{tab} pestanya seleccionada',
        announce_error_add_member: "S'ha produït un error en afegir un membre al grup.",
        announce_category_reverted: '{category} ha tornat a ser heretat dels pares.',
        announce_category_overridden: "{category} s'ha definit com a substitució local.",
        announce_override_saved: '{category} anul·lació desada.',
        announce_export_started: "S'està exportant l'auditoria com a {format}...",
        announce_export_completed: 'Exporta com a {format} completat.',
        announce_export_failed: "Ha fallat l'exportació.",
        announce_filter_org: 'Filtrat per organització {name}',
        announce_filter_org_cleared: "S'ha esborrat el filtre d'organització",
        announce_filter_event: "Filtrat per tipus d'esdeveniment {type}",
        announce_filter_cleared: "S'ha esborrat el filtre",
        announce_download: "S'està baixant {name} com a {format}",
        error_load_groups: "No s'han pogut carregar els grups organitzatius. Si us plau, torna-ho a provar.",
        error_load_users: "No s'han pogut carregar els usuaris del grup.",
        error_load_reports: "No s'han pogut carregar els informes del grup.",
        error_load_dashboard: "No s'ha pogut carregar el tauler de control del grup.",
        error_load_settings: "No s'ha pogut carregar la configuració del grup.",
        error_load_vendors: "No s'han pogut carregar els proveïdors combinats.",
        error_save_settings: "No s'ha pogut desar la configuració.",
        error_dissolve_group: "No s'ha pogut dissoldre el grup.",
        error_assign_role: "No s'ha pogut assignar la funció.",
        error_create_group: "No s'ha pogut crear el grup organitzatiu. Si us plau, torna-ho a provar.",
        error_request_report: "No s'ha pogut sol·licitar l'informe.",
        error_group_name_required: 'El nom del grup és obligatori.',
        success_settings_saved: "La configuració s'ha desat correctament.",
        confirm_retire_vendor: 'Esteu segur que voleu retirar el proveïdor "{name}"?',
        confirm_retire_framework: 'Esteu segur que voleu retirar el marc "{name}"?',
        confirm_retire_template: 'Esteu segur que voleu retirar la plantilla de formulari "{name}"?',
        confirm_dissolve_group: 'Esteu segur que voleu dissoldre "{name}"? Aquesta acció no es pot desfer.',
        confirm_revoke_role: 'Esteu segur que voleu revocar la funció "{role}" de "{name}"?',
        confirm_delete_report: 'Esteu segur que voleu suprimir l\'informe "{name}"?',
        dissolve_group: 'Dissol el grup',
        dissolve_warning: 'Això dissolrà definitivament el grup organitzatiu. Totes les organitzacions membres seran eliminades.',
        count_groups: '{count} grup',
        count_groups_plural: '{count} grups',
        count_users: '{count} usuari',
        count_users_plural: '{count} usuaris amb rols de grup',
        count_events: '{count} esdeveniment',
        count_events_plural: '{count} esdeveniments',
        count_reports: '{count} informe',
        count_reports_plural: '{count} informes',
        count_vendors: '{count} proveïdor compartit per tot el grup',
        count_vendors_plural: '{count} proveïdors compartits per tot el grup',
        count_frameworks: '{count} marc compartit a tot el grup',
        count_frameworks_plural: '{count} marcs compartits a tot el grup',
        count_templates: '{count} plantilla compartida a tot el grup',
        count_templates_plural: '{count} plantilles compartides a tot el grup',
        count_shared_vendors: '{count} venedors compartits',
        count_local_vendors: '{count} venedors locals',
        merged_combined_view: 'Visualització combinada de {shared} proveïdors locals i compartits de {local}',
        dashboard: {
            title: 'Tauler del grup',
            loading: 'Carregant dashboard data...',
            total_groups: 'Total Grups',
            total_members: 'Total de membres',
            active_groups: 'Actiu Grups',
            overall_compliance_score: 'Puntuació de compliment general',
            group_risk_level: 'Nivell de risc del grup',
            sla_compliance: 'SLA Compliment',
            compliance_posture: 'Postura de compliment',
            risk_heatmap: 'Mapa de calor de risc',
            sla_performance: 'SLA Rendiment',
            recent_incidents: 'Incidents recents',
            recent_activity: 'Activitat recent',
            refresh_label: 'Actualitza les dades del tauler',
            score_good: 'Bé',
            score_needs_attention: 'Necessita atenció',
            score_critical: 'Crític',
            trend_up: "Tendència a l'alça",
            trend_down: 'Tendència a la baixa',
            trend_stable: 'Estable',
            risk_low: 'Low',
            risk_medium: 'Mitjana',
            risk_high: 'Alt',
            risk_critical: 'Crític',
            risk_unknown: 'Desconegut',
            col_organization: 'Organització',
            col_score: 'Puntuació',
            col_trend: 'Tendència',
            col_open_issues: 'Problemes oberts',
            col_critical_issues: 'Assumptes crítics',
            col_health: 'Salut',
            col_vendor_risk: 'Risc del venedor',
            col_data_risk: 'Risc de dades',
            col_compliance_risk: 'Risc de compliment',
            col_total_cases: 'Total de casos',
            col_on_time: 'A temps',
            col_breached: 'Incompliment',
            col_sla_percent: 'SLA%',
            col_avg_days: 'Mitjana dies',
            sr_vendor_risk: 'Puntuació de risc del proveïdor: {score}',
            sr_data_risk: 'Puntuació de risc de les dades: {score}',
            sr_compliance_risk: 'Puntuació de risc de compliment: {score}',
            sr_sla_breaches: 'Avís: {count} SLA infraccions',
            sr_severity: 'Gravetat: {severity}',
        },
        detail: {
            title: 'Detall del grup',
            members: 'Membres',
            settings: 'Configuració',
            audit: 'Auditoria',
            sr_loading: "S'estan carregant els detalls del grup organitzatiu",
            sr_loading_hierarchy: 'Jerarquia de càrrega',
            tab_tasks: 'Shared tasks',
            tasks_intro: "The tasks of every organization in this group, read across the group because task sharing is switched on in this group's settings.",
            tasks_total: '{count} tasks across the group',
            tasks_overdue: '{count} overdue',
            tasks_by_status: 'By status',
            tasks_by_org: 'By organization',
            tasks_latest: 'Latest tasks',
            tasks_showing: 'Showing the {shown} most recent of {total}.',
            tasks_empty: 'No organization in this group has any tasks.',
            col_title: 'Title',
            col_org: 'Organization',
            col_status: 'Status',
            col_priority: 'Priority',
            col_due: 'Due',
            tasks_load_failed: "The group's shared tasks could not be read, so nothing is shown for them.",
            tasks_subject: 'shared tasks',
            sr_loading_tasks: 'Loading shared tasks',
            member_org: 'organització membre',
            root_org_label: 'organització arrel',
            hierarchy: 'Hierarchy',
        },
        users: {
            title: 'Usuaris del grup',
            add_user: 'Afegir usuari',
            no_users: 'Sense usuaris en aquest grup.',
            role_group_admin: 'Administrador del grup',
            role_group_reader: 'Lector grupal',
            sr_loading: "S'estan carregant els usuaris del grup",
        },
        reports: {
            title: 'Informes del grup',
            no_reports: "No s'han trobat reports",
            generate_report: 'Generar nou informe',
            report_type_compliance: 'Resum de compliment',
            report_type_risk: 'Visió general del risc',
            report_type_sla: 'SLA Rendiment',
            report_type_incident: "Informe d'incidències",
            report_type_executive: 'Breu executiu',
            report_type_board: 'Informe de la Junta',
            status_queued: 'En cua',
            status_generating: 'Generant',
            status_completed: 'Completat',
            status_failed: 'Ha fallat',
            period_start: 'Inici del període',
            period_end: 'Final del període',
            sr_loading: "S'estan carregant informes de grup",
            /*
             * The two row actions' accessible names, interpolated rather than built
             * as English template literals.
             *
             * They shipped as `aria-label={`Delete report ${…}`}` and
             * `aria-label={`Download ${…} as ${…}`}` — an `aria-label` is
             * user-visible text, so 15 non-English locales heard these in English.
             * The same defect was fixed by hand one page over in the same commit
             * that touched these two lines and left them.
             */
            download_report_aria: 'Download {title} as {format}',
            delete_report_aria: 'Delete report {title}',
        },
        shared_vendors: {
            title: 'Proveïdors compartits',
            no_vendors: "No s'han trobat shared vendors",
            merged_vendors: 'Proveïdors fusionats',
            publish_form_label: 'Publicar el proveïdor compartit',
            sr_loading: "S'estan carregant proveïdors compartits",
            sr_loading_merged: "S'estan carregant els venedors combinats",
            local_vendors: 'Venedors locals',
            source_shared: 'Compartit des del grup',
            source_local: 'Venedor local',
            category_technology: 'Tecnologia',
            category_consulting: 'Consultoria',
            category_outsourcing: 'Externalització',
            category_cloud: 'Núvol',
            category_data_processing: 'Tractament de dades',
            category_other: 'Altres',
            risk_tier_low: 'Low',
            risk_tier_medium: 'Mitjana',
            risk_tier_high: 'Alt',
            risk_tier_critical: 'Crític',
            compliance_compliant: 'Complint',
            compliance_non_compliant: 'Incompliment',
            compliance_pending: 'Pendent',
            compliance_under_review: 'En revisió',
            status_active: 'Actius',
            status_retired: 'Jubilat',
        },
        shared_frameworks: {
            // Fix sprint F28 (WCAG 3.3.2) — the framework-code field was labelled
            // `common.type` ("Type"), so its accessible name said nothing about what
            // it takes. With no installed framework the placeholder resolves to '',
            // leaving a required field with neither a meaningful name nor a hint.
            framework_code_label: 'Codi del marc',
            title: 'Marcs compartits',
            no_frameworks: "No s'han trobat shared frameworks",
            publish_form_label: 'Publicar un marc compartit',
            sr_loading: 'Carregant marcs compartits',
            status_active: 'Actius',
            status_retired: 'Jubilat',
        },
        shared_forms: {
            title: 'Plantilles de formularis compartides',
            no_forms: "No s'han trobat shared form templates",
            publish_form_label: 'Publicar una plantilla de formulari compartida',
            sr_loading: "S'estan carregant plantilles de formularis compartides",
            status_active: 'Actius',
            status_retired: 'Jubilat',
        },
        audit: {
            title: 'Auditoria del grup',
            consolidated_title: "Pista d'auditoria consolidada",
            consolidated_subtitle: "Esdeveniments d'auditoria entre organitzacions per a aquest grup",
            audit_log_title: 'Grup Auditoria Registre',
            event_type: 'Esdeveniment Tipus',
            actor: 'Actor',
            timestamp: 'Marca de temps',
            source_org: "Org. d'origen",
            org: 'Org',
            summary: 'Resum',
            details: 'Detalls',
            no_events: "No s'han trobat audit events",
            all_events: 'Tots Esdeveniments',
            all_orgs: 'Totes les org.',
            organization: 'Organització',
            from: 'De',
            to: 'To',
            search_events: 'Cercar esdeveniments...',
            clear: 'Esborrar',
            events_24h: 'Esdeveniments (24h)',
            events_7d: 'Esdeveniments (7d)',
            active_orgs: 'Org. actives',
            exporting: 'Exportant...',
            export_csv: 'Exportar CSV',
            export_json: 'Exportar JSON',
            back_to_group: 'Enrere a Grup',
            back_to_detail: 'Tornar al detall',
            sr_loading: "Carregant esdeveniments d'auditoria",
            sr_loading_summary: "S'està carregant el resum de l'auditoria",
            disabled_by_settings: 'The consolidated audit is switched off in the settings of this group. The root organization of the group can switch it back on under Settings.',
        },
        settings: {
            title: 'Grup Configuració',
            shared_tasks: 'Share task lists, statistics and calendars across the member organizations of this group',
            inheritance: 'Herència de configuració',
            inheritance_subtitle: 'Configura quines categories de configuració hereten del grup versus utilitza substitucions locals',
            sla_settings: 'SLA Configuració',
            calendar_settings: 'Calendari Configuració',
            notification_settings: 'Notificació Configuració',
            intake_settings: "Configuració d'entrada",
            cmp_integration: 'Integració CMP',
            overridden: 'Substituït',
            inheriting: 'Heretant',
            hide_defaults: 'Amagar valors predeterminats',
            show_group_defaults: 'Mostrar Grup Defaults',
            group_defaults: 'Valors predeterminats del grup',
            inherit_from_group: 'Inherit de Grup',
            use_local_override: 'Usar substitució local',
            local_override_json: 'Substitució local (JSON)',
            save_override: 'Desar substitució',
            saving: 'Desant...',
            sr_loading: "S'està carregant l'herència de la configuració",
            sr_loading_settings: "S'està carregant la configuració del grup",
            sr_enabled: 'Habilitat',
            sr_disabled: 'Inhabilitat',
            sr_override_active: 'Anul·lació local activa',
            sr_inherited: 'Heretat dels pares',
            danger_zone: 'Zona de perill',
            sharing_options: 'Opcions per compartir',
            advanced_options: 'Opcions avançades',
            max_member_orgs: 'Maximum member organizations',
        },
        status_active: 'Actius',
        status_dissolved: 'Dissolt',
        status_pending: 'Pendent',
        status_suspended: 'Suspès',
    },
    integrations: {
        nav: {
            webhooks: 'Webhooks',
            domains: 'Dominis',
            export_schedules: "Programació d'exportacions",
            email_log: 'Registre de correus',
        },
    },
    platform: {
        abandoned_uploads: {
            title: 'Abandoned uploads',
            description: 'Files uploaded on a form nobody saved, past the grace window. Reading this changes nothing.',
            grace_window: 'Grace window',
            hours: '{n} hours',
            as_of: 'As of',
            candidates: 'Uploads no record claimed',
            retained_held: 'Kept — legal hold',
            file: 'File',
            uploaded_for: 'Uploaded for',
            uploaded_by: 'Uploaded by',
            uploaded_at: 'Uploaded',
            past_window_by: 'Past the window by',
            hours_short: '{n} h',
            run_sweep: 'Delete abandoned uploads',
            running: 'Deleting...',
            sweep_success: '{swept} upload(s) deleted, {held} kept under a legal hold.',
            sweep_failed: 'The sweep could not be run. Please try again.',
            load_failed: 'The abandoned-upload register could not be read. Please try again.',
            reload: 'Reload',
            empty: 'No upload is past the grace window unclaimed.',
            restore_note: 'A deleted upload is recoverable: it is soft-deleted and can be restored from this page.',
        },
        nav: {
            users: 'Usuaris',
            roles: 'Rols',
            profile: 'El meu perfil',
        },
        security: {
            mfa: 'Configuració MFA',
            sso: 'Configuració SSO',
            api_keys: 'Claus API',
            service_accounts: 'Comptes de servei',
            ip_allowlist: "Llista d'IP permeses",
            delegations: 'Delegacions',
            break_glass: "Accés d'emergència",
            session_settings: 'Configuració de sessions',
            active_sessions: 'Sessions actives',
            access_reviews: "Revisions d'accés",
            my_reviews: 'Les meves revisions',
            role_analytics: 'Analítica de rols',
            attestation_schedules: "Programació d'atestacions",
            sod_rules: 'Regles SoD',
            sod_violations: 'Violacions SoD',
        },
        module_config: {
            title: 'Configuració de mòduls',
            subtitle: "Habiliteu o deshabiliteu mòduls de funcionalitat per a la vostra organització. Els mòduls deshabilitats s'amaguen de la navegació.",
            enabled: 'Habilitat',
            disabled: 'Deshabilitat',
            save: 'Desa la configuració',
            saving: 'Desant...',
            saved: 'Configuració desada correctament',
            nav_label: 'Configuració de mòduls',
            settings: 'Configuració',
            webhooks: 'Webhooks',
            domains: 'Dominis',
            export_schedules: "Programació d'exportacions",
            email_log: 'Registre de correus',
            announce_loaded: 'Module configuration loaded',
        },
        files_nav: {
            section: 'Fitxers',
            configuration: 'Configuració',
            browser: 'Navegador de fitxers',
            dashboard: 'Tauler de fitxers',
        },
        immutable_audit_trails: {
            nav_group_label: 'Auditoria i prova',
            nav_settings: 'Configuració',
            nav_status: 'Estat de la prova',
            nav_integrity_center: "Centre d'integritat",
            nav_usage: 'Ús i crèdits',
            settings_title: "Pistes d'auditoria immutables — Configuració",
            settings_subtitle: "Configuració per organització del complement de Pistes d'auditoria immutables.",
            settings_banner: "El complement de Pistes d'auditoria immutables no està habilitat per a la vostra organització. Habiliteu-lo aquí per començar a ancorar proves dels esdeveniments d'auditoria de Nivell A i Nivell B.",
            status_title: "Pistes d'auditoria immutables — Estat de la prova",
            status_subtitle: 'Relacions de prova per esdeveniment a través dels mòduls.',
            status_banner: "El complement de Pistes d'auditoria immutables no està habilitat per a la vostra organització. Aneu a Configuració per habilitar-lo.",
            integrity_center_title: "Pistes d'auditoria immutables — Centre d'integritat",
            integrity_center_subtitle: "Verifiqueu proves i exporteu Paquets d'integritat.",
            integrity_center_banner: "El complement de Pistes d'auditoria immutables no està habilitat per a la vostra organització. Aneu a Configuració per habilitar-lo abans d'executar la verificació o exportar Paquets d'integritat.",
            usage_title: "Pistes d'auditoria immutables — Ús i crèdits",
            usage_subtitle: "Saldo de crèdits, compres de paquets i historial d'ús.",
            usage_banner: "Habiliteu Pistes d'auditoria immutables a Configuració per començar a consumir crèdits de prova.",
            module_tips_placeholder: "Hi ha consells i orientacions per a aquesta superfície al centre d'ajuda.",
            column_label: 'Prova',
            no_proof_aria: 'No hi ha cap prova registrada',
            proof_status_receipt_label: 'rebut {id}',
            in_progress_hint: "En curs: s'actualitza automàticament",
            status_pills: {
                pending: 'Pendent',
                submitted: 'Enviada',
                anchored: 'Ancorada',
                confirmed: 'Confirmada',
                retrying: 'Reintentant',
                paused_cap: 'En pausa (límit)',
                failed: 'Ha fallat',
                failed_auth: "Ha fallat l'autenticació",
            },
            status_tooltips: {
                pending: 'Pendent: a la cua per al servei de proves. Passarà a Enviat en breu; no cal cap acció.',
                submitted: "Enviat: el vostre esdeveniment s'ha enviat al servei de proves. Per al mode per lots, està esperant per ser segellat i ancorat a la cadena. Actualització automàtica; no cal cap acció.",
                anchored: "Ancorat: el lot s'ha escrit a la cadena i està esperant la confirmació final. Actualització automàtica; no cal cap acció.",
                confirmed: 'Confirmat: la prova està ancorada a la cadena i es pot verificar.',
                retrying: "S'està tornant a provar: s'ha produït un error transitori. El conciliador torna a provar automàticament; no cal cap acció.",
                paused_cap: "En pausa: s'ha assolit el vostre límit de crèdit de prova mensual. Bescanvieu més crèdits o espereu fins al proper mes de facturació.",
                failed: "Ha fallat: l'ancoratge no ha tingut èxit. El conciliador tornarà a provar automàticament; contacteu amb l'assistència si persisteix.",
                failed_auth: "L'autenticació ha fallat: el servei de proves ha rebutjat les nostres credencials. Comproveu la pàgina de configuració de pistes d'auditoria immutables.",
            },
            mode_pills: {
                direct: 'Directa',
                batch: 'Per lots',
            },
            drawer: {
                title: 'Detalls de la prova',
                close: 'Tanca',
                section_proof: 'Prova',
                section_batch: 'Pertinença al lot',
                status: 'Estat',
                proof_reference: 'Referència de prova',
                receipt_id: 'ID del rebut',
                taxonomy_class: 'Classe de taxonomia',
                units_consumed: 'Unitats consumides',
                created_at: 'Creada',
                verification_ref: 'Referència de verificació de la cadena',
                chain: 'Cadena',
                tx_hash: 'Hash de la transacció',
                block_ref: 'Referència del bloc',
                batch_reference: 'Referència del lot',
                leaf_index: 'Índex de fulla',
                local_event_hash: "Hash local de l'esdeveniment",
                show_inclusion: "Mostra el camí d'inclusió",
                hide_inclusion: "Amaga el camí d'inclusió",
                privacy_note: 'La càrrega està hashejada; cap dada personal no surt de ComplianceOne per a aquest esdeveniment.',
                verify_now: 'Verifica ara',
                verify_pending: 'Verificació en curs…',
                verify_verified: 'Verificada — comprovacions locals i de la cadena correctes.',
                verify_issue_prefix: 'Problema de verificació: ',
                verify_failed: 'La crida de verificació ha fallat.',
                verify_result_prefix: 'Veredicte de verificació: ',
                verify_pending_note: "Verificació pendent: aquesta prova encara està pendent d'ancoratge a la cadena. Torna-ho a provar després que el lot estigui segellat.",
                awaiting_anchor_note: "Aquesta prova forma part d'un lot obert. La verificació està disponible un cop el lot està segellat i ancorat a la cadena, normalment dins de la finestra del lot configurada per l'organització. La insígnia d'estat s'actualitzarà automàticament.",
                terminal_error_note: "Aquesta prova no va poder ancorar. El conciliador d'estat de prova tornarà a intentar-ho automàticament; la verificació manual encara no té res a comprovar.",
            },
            integrity_center: {
                section_run: 'Executa la verificació',
                section_progress: "Progrés de l'execució",
                section_issues: 'Problemes',
                period_start: 'Inici del període',
                period_end: 'Fi del període',
                module_filter: 'Mòduls (separats per comes, opcional)',
                modules_placeholder: 'rights-requests, incidents',
                run_button: 'Executa la verificació',
                export_button: "Exporta el Paquet d'integritat",
                export_success: "Paquet d'integritat exportat.",
                export_failed: "L'exportació ha fallat.",
                empty_disabled: "Habiliteu Pistes d'auditoria immutables a Configuració per executar passades de verificació.",
                error_period_required: "Trieu una data d'inici i una de final.",
                error_start_run: "No s'ha pogut iniciar l'execució.",
                processed: 'Processats: {processed} de {total}',
                col_verdict: 'Veredicte',
                col_count: 'Recompte',
                col_proof_link: 'Enllaç de prova',
                col_module: 'Mòdul',
                col_event_type: "Tipus d'esdeveniment",
                col_expected: 'Hash esperat',
                col_recomputed: 'Hash recalculat',
                col_raised_at: 'Generat el',
                any_verdict: 'Qualsevol veredicte',
                no_issues: "No s'ha trobat cap problema.",
            },
            settings_page: {
                load_error: "No s'ha pogut carregar la configuració.",
                save: 'Desa',
                save_success: 'Configuració desada.',
                save_error: "No s'ha pogut desar.",
                discard: 'Descarta els canvis',
                read_only_banner: 'Teniu accés només de lectura. Contacteu un administrador de la plataforma per fer canvis.',
                section_status: 'Estat i habilitació',
                enabled_label: 'Complement habilitat',
                enabled_help: "Quan està habilitat, cada esdeveniment d'auditoria de Nivell A i Nivell B es hashejat i s'ancora.",
                section_mode: 'Mode de prova per defecte',
                mode_direct: 'Directe (un ancoratge per esdeveniment)',
                mode_batch: 'Per lots (ancoratge d’arrel Merkle per grup)',
                mode_hybrid: 'Híbrid (per defecte del registre per classe)',
                batch_window_label: 'Finestra del lot (minuts, 1–60)',
                section_taxonomy: "Taxonomia d'esdeveniments protegits",
                taxonomy_help: "Substitucions per classe d'esdeveniment. Nivell A → mode directe, Nivell B → mode per lots, Desactivat → no es prova.",
                taxonomy_search: 'Cerca substitucions',
                taxonomy_reset: 'Restableix als valors per defecte',
                taxonomy_event_class: "Classe d'esdeveniment",
                taxonomy_override: 'Substitució',
                taxonomy_actions: 'Accions',
                taxonomy_clear: 'Esborra',
                taxonomy_no_overrides: "Sense substitucions — s'utilitzen els valors per defecte del registre.",
                taxonomy_add_class: 'event_class.codi',
                taxonomy_add: 'Afegeix substitució',
                section_caps: 'Límits',
                cap_label: 'Límit mensual (unitats; en blanc = il·limitat)',
                warn_label: "Llindar d'avís (unitats; ha de ser inferior al límit)",
                auto_pause_label: 'Posa en pausa noves proves automàticament en arribar al límit',
                section_credentials: 'Credencials',
                credential_id: 'ID de credencial',
                credential_rotated_at: 'Última rotació',
                rotate_button: 'Rota la credencial',
                rotate_success: 'Credencial rotada. La credencial antiga és vàlida durant {n} minuts més.',
                rotate_failed: 'La rotació de la credencial ha fallat.',
                rotate_modal_title: 'Voleu rotar la credencial AesirX?',
                rotate_modal_body: 'La rotació emet una nova credencial. La credencial antiga continua sent acceptada per AesirX durant un període de gràcia de 60 minuts perquè els treballs en curs puguin acabar les seves tasques actuals. Després de 60 minuts, la credencial antiga es revoca.',
                rotate_modal_confirm: 'Rota ara',
                tier_a_off_modal_title: "Voleu desactivar la prova per a una classe d'esdeveniment?",
                tier_a_off_modal_body: "Establir una classe d'esdeveniment a «Desactivat» atura l'ancoratge d'aquests esdeveniments. L'arquitectura §6 explica la implicació regulatòria: els esdeveniments de Nivell A són rellevants per al regulador; desactivar-los pot trencar la defensabilitat de la pista d'auditoria.",
                tier_a_off_modal_confirm: 'Ho entenc — aplica la substitució',
                error_warn_lt_cap: "El llindar d'avís ha de ser inferior al límit mensual.",
                error_window_range: 'La finestra del lot ha de ser entre 1 i 60 minuts.',
                error_run_in_progress: 'Hi ha una execució de verificació en curs; ara no es pot deshabilitar el complement.',
            },
            status_page: {
                filter_status: 'Estat',
                filter_mode: 'Mode de prova',
                filter_module: 'Mòdul',
                filter_source_collection: "Col·lecció d'origen",
                filter_from_date: 'Des de',
                filter_to_date: 'Fins a',
                filter_search: 'Cerca',
                any_status: 'Qualsevol estat',
                any_mode: 'Qualsevol mode',
                col_module: 'Mòdul',
                col_event_type: "Tipus d'esdeveniment",
                col_proof_status: 'Estat',
                col_proof_mode: 'Mode',
                col_created_at: 'Creada',
                col_units: 'Unitats',
                empty_disabled: "El complement de Pistes d'auditoria immutables no està habilitat per a la vostra organització. Aneu a Configuració per habilitar-lo.",
                empty_enabled_no_rows: 'Cap enllaç de prova coincideix amb els filtres actuals.',
                export_csv: 'Exporta CSV',
                export_xlsx: 'Exporta XLSX',
                export_success: 'Exportació completada',
                export_failed: "L'exportació ha fallat",
            },
            usage_page: {
                section_alerts: 'Alertes',
                alert_dismiss: 'Descartar',
                alert_dismiss_aria: "Ignora l'alerta {id}",
                alert_dismissed: 'Alerta desestimada',
                alert_dismiss_failed: "No s'ha pogut descartar l'alerta",
                section_balance: 'Saldo de crèdits',
                balance_label: 'Crèdits disponibles',
                balance_hint: "Unitats restants (1 unitat = prova d'un esdeveniment protegit).",
                section_current_month: 'Mes actual',
                consumed_label: 'Consumits',
                granted_label: 'Concedits',
                refunded_label: 'Reemborsats',
                net_label: 'Net',
                section_history: "Historial d'ús",
                history_kind: 'Tipus',
                history_units: 'Unitats',
                history_balance_after: 'Saldo posterior',
                history_notes: 'Notes',
                history_at: 'Quan',
                cap_warning_banner: "Heu superat el llindar d'avís per a aquest mes de facturació. Considereu canviar crèdits addicionals.",
                cap_paused_banner: "S'ha arribat al límit mensual. Les enviaments de proves estan en pausa fins al mes vinent o fins que s'augmenti el límit.",
                section_redeem: 'Canvia paquet de crèdits',
                redeem_purchase_id: 'Número de factura',
                redeem_package: 'Paquet',
                redeem_button: 'Canvia',
                redeem_pending: "S'està bescanviant…",
                redeem_success: 'Crèdits canviats',
                redeem_failed: 'El canvi ha fallat',
                redeem_option_template: '{name} — {units} {units_label} — {price}',
                redeem_option_units: 'unitats',
                alert_dismissing: "S'està descartant...",
                alert_kind: {
                    cap_reached: "S'ha arribat al límit mensual",
                    low_balance: 'Balanç baix',
                    auth_failure: "Error d'autenticació",
                    merkle_mismatch: 'Desajust de Merkle',
                    batch_terminal_failure: 'Falla del terminal del lot',
                    direct_terminal_failure: "Error del terminal d'enviament directe",
                },
                usage_kind: {
                    consume: 'Consumir',
                    refund: 'Devolució',
                    grant: 'Grant',
                    adjustment: 'Ajust',
                    reservation: 'Reserva',
                    credit_grant: 'Credit granted',
                    consumption: 'Proof consumed',
                    refund_failed_proof: 'Refund (failed proof)',
                },
                package_starter: 'Arrancador',
                package_growth: 'Creixement',
                package_enterprise: 'Empresa',
                // Sprint 48.5.x — self-service "Buy More" billing (Flow A + B).
                buy_more: {
                    buy_button: 'Compra més crèdits',
                    purchase_title: 'Compra més crèdits',
                    purchase_subtitle: 'Trieu un paquet. Se us dirigirà al pagament segur de Stripe i la vostra factura arribarà per correu electrònic.',
                    checkout_button: 'Continua al pagament',
                    checkout_pending: 'Obrint el pagament…',
                    cancel_button: 'Cancel·la',
                    purchase_error: "No s'ha pogut iniciar el pagament. Torneu-ho a provar d'aquí a poc.",
                    redeem_subtitle: "Ja heu pagat? Seleccioneu el paquet que heu comprat i introduïu l'identificador de factura del vostre correu electrònic per afegir els crèdits.",
                    error_not_found: "No s'ha trobat cap factura amb aquest número. Comproveu el número de factura i torneu-ho a provar.",
                    error_not_paid: 'Aquesta factura encara no està marcada com a pagada.',
                    error_org_mismatch: "Aquesta factura no s'ha emès a la vostra organització.",
                    error_package_mismatch: 'Aquesta factura no coincideix amb el paquet seleccionat.',
                    error_billing_unavailable: "No s'ha pogut contactar amb el servei de facturació. Torneu-ho a provar d'aquí a poc.",
                },
                cap_over_banner: 'The monthly cap has been reached. Proofs keep being submitted because pausing at the cap is switched off.',
                resume_paused_button: 'Resume paused proofs',
                resume_paused_hint: 'Proofs held back when the monthly cap was reached are released by themselves when the cap is raised or removed, or when pausing at the cap is switched off. Use this once a new month has begun.',
                resume_paused_success: '{count} paused proofs released.',
                resume_paused_none: 'No paused proofs to release.',
                resume_paused_failed: 'The paused proofs could not be released.',
            },
        },
        files_config: {
            title: 'Configuració de fitxers',
            subtitle: "Configureu els límits de pujada de fitxers, les polítiques de retenció i les substitucions per tipus de propietari per a la superfície d'adjunts de la plataforma.",
            section_global: 'Valors globals per defecte',
            section_global_help: "Aquests límits s'apliquen a cada fitxer pujat pels usuaris de la vostra organització, llevat que una substitució per tipus de propietari els canviï.",
            section_overrides: 'Substitucions per tipus de propietari',
            section_overrides_help: 'Substituïu els límits globals per a un tipus de propietari específic. Els valors per tipus de propietari sempre prevalen sobre la configuració global.',
            max_file_size_label: 'Mida màxima de fitxer',
            max_file_size_hint: 'Per pujada individual (MiB)',
            max_total_attachments_label: "Nombre màxim total d'adjunts",
            max_total_attachments_hint: 'A tota la organització',
            max_total_size_label: 'Emmagatzematge total màxim',
            max_total_size_hint: 'A tota la organització (MiB)',
            allowed_mime_types_label: 'Tipus de fitxer permesos',
            allowed_mime_types_placeholder: 'Selecciona tipus MIME',
            retention_days_label: 'Període de retenció (dies)',
            retention_days_hint: "Quant de temps s'han de conservar els fitxers abans que es puguin eliminar. Deixeu-ho en blanc per no aplicar cap política de retenció.",
            legal_hold_label: 'Retenció legal',
            legal_hold_hint: "Quan està habilitada, l'eliminació definitiva d'un fitxer d'aquest tipus de propietari requereix el permís de substitució de retenció legal i el consentiment explícit de l'operador.",
            save: 'Desa els canvis',
            saving: 'Desant…',
            reset: 'Restableix',
            save_success: 'Configuració desada',
            empty_state_title: 'Utilitzant els valors per defecte de la plataforma',
            empty_state_body: "La vostra organització està funcionant actualment amb la política de fitxers per defecte de la plataforma. Editeu qualsevol valor a sota per crear una substitució a nivell d'organització.",
            read_only_banner: 'Teniu accés només de lectura a la configuració de fitxers. Contacteu un administrador de la plataforma per fer canvis.',
            owner_type_default_badge: 'Per defecte',
            owner_type_overridden_badge: 'Substituït',
            owner_type_label: 'Tipus de propietari',
            owner_type_collapse: 'Replega',
            owner_type_expand: 'Desplega',
            override_save: 'Desa la substitució',
            override_remove: 'Elimina la substitució',
            mime_labels: {
                'application/pdf': 'Document PDF',
                'image/png': 'Imatge PNG',
                'image/jpeg': 'Imatge JPEG',
                'image/jpg': 'Imatge JPG',
                'application/msword': 'Document Word (heretat)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Document Word',
                'application/vnd.ms-excel': 'Full de càlcul Excel (heretat)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Full de càlcul Excel',
                'text/csv': 'Fitxer CSV',
                'text/plain': 'Text pla',
                'message/rfc822': 'Missatge de correu electrònic',
            },
            global_legal_hold_label: 'Organization-wide legal hold',
            global_legal_hold_hint: 'When enabled, NO file across any owner type can be hard-deleted unless the operator has the legal_hold_override permission and explicitly opts in.',
            global_retention_days_label: 'Default retention period (days)',
            global_retention_days_hint: 'How long files must be kept before they become eligible for deletion. Leave blank for no retention policy. Per-owner-type overrides below can tighten.',
            global_abandoned_grace_label: 'Abandoned upload grace window (hours)',
            global_abandoned_grace_hint: 'How long a file that no saved record has claimed is left alone before the abandoned-upload sweep soft-deletes it. At least 24 hours, so a form that is still open is never affected.',
            global_abandoned_grace_invalid: 'Enter a whole number of hours from {min} to {max}.',
        },
        files_browser: {
            title: 'Navegador de fitxers',
            subtitle: "Cerqueu, filtreu i gestioneu cada fitxer adjunt pujat a tota l'organització.",
            filter_owner_type: 'Tipus de propietari',
            filter_uploader: 'Pujat per',
            filter_from: 'Pujat després de',
            filter_to: 'Pujat abans de',
            filter_size_min: 'Mida mínima (MiB)',
            filter_size_max: 'Mida màxima (MiB)',
            filter_mime: 'Tipus de fitxer',
            filter_deleted_state: "Estat d'eliminació",
            filter_deleted_any: 'Tots els fitxers',
            filter_deleted_only: 'Només eliminats',
            filter_deleted_exclude: 'Exclou els eliminats',
            filter_orphaned: 'Només orfes',
            filter_search_placeholder: 'Cerca per nom de fitxer…',
            filter_apply: 'Aplica filtres',
            filter_clear: 'Esborra filtres',
            col_filename: 'Nom de fitxer',
            col_owner_type: 'Tipus de propietari',
            col_owner_entity: 'Propietari',
            col_uploader: 'Pujat per',
            col_uploaded_at: 'Pujat',
            col_size: 'Mida',
            col_mime: 'Tipus',
            col_state: 'Estat',
            col_actions: 'Accions',
            state_active: 'Actiu',
            state_deleted: 'Eliminat',
            state_orphaned: 'Orfe',
            action_view_detail: 'Mostra detalls',
            action_download: 'Baixa',
            action_ai_edits: 'Edicions amb IA',
            action_soft_delete: 'Eliminació suau',
            action_restore: 'Restaura',
            action_hard_delete: 'Eliminació definitiva',
            bulk_soft_delete: 'Eliminació suau de la selecció',
            bulk_export_csv: 'Exporta CSV',
            bulk_select_all: 'Selecciona-ho tot',
            reason_label: 'Motiu',
            reason_min_10_chars: 'El motiu és obligatori (mínim 10 caràcters)',
            reason_min_20_chars: 'El motiu és obligatori (mínim 20 caràcters)',
            legal_hold_override_label: 'Substitueix la retenció legal',
            legal_hold_override_warning: "Aquest fitxer està subjecte a retenció legal. La seva eliminació definitiva quedarà registrada a la pista d'auditoria amb el vostre motiu i la marca de substitució.",
            empty_state_title: 'Cap fitxer coincideix amb els filtres',
            empty_state_body: 'Ajusteu els filtres per veure resultats, o esborreu-los per veure tots els fitxers.',
            pagination_prev: 'Anterior',
            pagination_next: 'Següent',
            pagination_page: 'Pàgina {page} de {total}',
            pagination_size_label: 'Per pàgina',
            pagination_total: 'Total: {count}',
            toast_soft_deleted: 'Fitxer eliminat suaument',
            toast_restored: 'Fitxer restaurat',
            toast_hard_deleted: 'Fitxer eliminat definitivament',
            toast_bulk_deleted: '{count} fitxer(s) eliminat(s)',
            toast_error: "L'operació ha fallat",
            confirm_soft_delete_title: 'Eliminació suau de fitxer',
            confirm_restore_title: 'Restaura fitxer',
            confirm_hard_delete_title: 'Elimina fitxer definitivament',
            confirm_bulk_soft_delete_title: 'Eliminació suau dels fitxers seleccionats',
            confirm_submit: 'Confirma',
            confirm_cancel: 'Cancel·la',
            col_view_entity: 'View',
        },
        files_detail: {
            modal_title: 'Detalls del fitxer',
            close: 'Tanca',
            meta_filename: 'Nom de fitxer',
            meta_size: 'Mida',
            meta_mime: 'Tipus',
            meta_owner_type: 'Tipus de propietari',
            meta_owner_entity: 'Entitat propietària',
            meta_uploader: 'Pujat per',
            meta_uploaded_at: 'Pujat',
            meta_storage_path: "Camí d'emmagatzematge",
            meta_deleted_state: "Estat d'eliminació",
            audit_section_title: "Pista d'auditoria",
            audit_load_more: 'Carrega més esdeveniments',
            audit_empty: "No hi ha esdeveniments d'auditoria registrats per a aquest fitxer.",
            audit_config_changed: 'Configuració modificada',
            audit_soft_deleted: 'Eliminat suaument',
            audit_restored: 'Restaurat',
            audit_hard_deleted: 'Eliminat definitivament',
            audit_downloaded: 'Baixat',
            audit_quota_breached: 'Intent de superar la quota',
            audit_owner_type_download_denied: 'Recuperació denegada',
            /*
             * The audit row as ONE composed string, not three JSX fragments glued
             * with English.
             *
             * The row used to render `{' by '}` and `{' on '}` as literals, so every
             * locale read "Endgültig gelöscht by Anna Schmidt on 11. Apr 2026" — the
             * event label and the actor came from `t` and the user map, the
             * connective words did not. A locale that orders these differently
             * cannot be rendered correctly by concatenation at all, which is why
             * this is a template rather than two `by`/`on` keys.
             *
             * `{actor}` is a link, so the page splits this on the placeholders and
             * substitutes nodes rather than calling `.replace`.
             */
            audit_row: '{event} per {actor} el {at}',
            action_download: 'Baixa',
            action_soft_delete: 'Eliminació suau',
            action_restore: 'Restaura',
            action_hard_delete: 'Eliminació definitiva',
            effective_settings_title: 'Política efectiva',
            effective_max_file_size: 'Mida màxima de fitxer',
            effective_retention_days: 'Retenció',
            effective_legal_hold: 'Retenció legal',
            effective_legal_hold_yes: 'Activa',
            effective_legal_hold_no: 'Cap',
        },
        files_dashboard: {
            title: 'Tauler de fitxers',
            subtitle: "Ús de l'emmagatzematge, creixement i fitxers orfes a tota l'organització.",
            kpi_total_files: 'Total de fitxers',
            kpi_total_size: 'Emmagatzematge total',
            kpi_near_quota: 'Ús de la quota',
            kpi_orphaned: 'Fitxers orfes',
            chart_by_owner_type: 'Fitxers per tipus de propietari',
            chart_growth_30d: 'Creixement a 30 dies',
            toggle_count: 'Per recompte',
            toggle_size: 'Per mida',
            orphaned_panel_title: 'Fitxers orfes (20 primers)',
            orphaned_view_all: 'Mostra tots els fitxers orfes',
            orphaned_empty: "No s'ha trobat cap fitxer orfe.",
            growth_empty: 'Cap activitat de pujada en els últims 30 dies.',
            retention_n_days: '{days} dia(es)',
            retention_none: 'Sense política',
        },
    },
    category_labels: {
        'user-guide': "Guia d'usuari",
        'admin-guide': "Guia d'administració",
        'getting-started': 'Primers passos',
        faq: 'Preguntes freqüents',
    },
    tag_labels: {
        faq: 'FAQ',
        dsar: 'DSAR',
        dsr: 'DSR',
        cmp: 'CMP',
        hmac: 'HMAC',
        sso: 'SSO',
        mfa: 'MFA',
        rbac: 'RBAC',
        api: 'API',
        'api-keys': 'API Claus',
        webhook: 'Webhook',
        webhooks: 'Webhooks',
        admin: 'Admin',
        aesirx: 'AesirX',
        purposes: 'Propòsits',
        preferences: 'Preferències',
        onboarding: 'Incorporació',
        'first-steps': 'Primers passos',
        login: 'Inicieu sessió',
        navigation: 'Navegació',
        users: 'Usuaris',
        user: 'Usuari',
        roles: 'Rols',
        role: 'Rol',
        'access-control': "Control d'accés",
        'access-management': "Gestió d'accés",
        permissions: 'Permisos',
        permission: 'Permís',
        consent: 'Consentiment',
        'consent-management': 'Gestió del consentiment',
        privacy: 'Privadesa',
        pdpl: 'PDPL',
        gdpr: 'GDPR',
        iso27001: 'ISO 27001',
        audit: 'Auditoria',
        auditing: 'Auditoria',
        breach: 'Incompliment',
        incident: 'Incident',
        incidents: 'Incidències',
        notification: 'Notificació',
        notifications: 'Notificacions',
        reporting: 'Informes',
        risk: 'Risc',
        vendor: 'Venedor',
        vendors: 'Venedors',
        'vendor-risk': 'Risc del venedor',
        assessment: 'Avaluació',
        assessments: 'Avaluacions',
        dpia: 'DPIA',
        'cross-border': 'Transfronterer',
        transfer: 'Transferència',
        transfers: 'Trasllats',
        deletion: 'Supressió',
        retention: 'Retenció',
        'data-mapping': 'Mapatge de dades',
        discovery: 'Descobriment',
        classification: 'Classificació',
        workflow: 'Flux de treball',
        workflows: 'Fluxos de treball',
        template: 'Plantilla',
        templates: 'Plantilles',
        catalog: 'Catàleg',
        monitoring: 'Seguiment',
        tasks: 'Tasques',
        task: 'Tasca',
        'task-management': 'Gestió de tasques',
        organizations: 'Organitzacions',
        org: 'Organització',
        governance: 'Governança',
        compliance: 'Compliment',
        forms: 'Formes',
        form: 'Formulari',
        connectors: 'Connectors',
        connector: 'Connector',
        integration: 'Integració',
        integrations: 'Integracions',
        security: 'Seguretat',
        encryption: 'Xifratge',
        backup: 'Còpia de seguretat',
        recovery: 'Recuperació',
        maintenance: 'Manteniment',
        settings: 'Configuració',
        configuration: 'Configuració',
        sla: 'SLA',
        escalation: 'Escalada',
        remediation: 'Remediació',
        evidence: 'Evidència',
        'case-management': 'Gestió de casos',
        profile: 'Perfil',
        delegation: 'Delegació',
        'service-accounts': 'Comptes de servei',
        license: 'llicència',
        'license-management': 'Gestió de llicències',
        plan: 'Pla',
        plans: 'Plans',
        dashboard: 'Tauler de control',
        'web-consent': 'Consentiment web',
        'partner-marketplace': 'Mercat de socis',
        partner: 'Soci',
        partners: 'Socis',
        deliverables: 'Entregables',
        'service-tiers': 'Nivells de servei',
        'support-tickets': 'Entrades de suport',
        'audit-trail': "Pista d'auditoria",
        'first-time-setup': 'Configuració per primera vegada',
        setup: 'Configuració',
        quickstart: 'Inici ràpid',
        tour: 'Tour',
        tours: 'Tours',
        'sample-data': 'Dades de mostra',
        'data-subject': 'Assumpte de les dades',
        'data-subjects': 'Assumptes de dades',
        pseudonymous: 'Pseudònim',
        identified: 'Identificat',
        'help-center': "Centre d'ajuda",
        article: 'Article',
        articles: 'Articles',
        search: 'Cerca',
        'platform-settings': 'Configuració de la plataforma',
        'platform-config': 'Configuració de la plataforma',
        'module-config': 'Configuració del mòdul',
        'module-configuration': 'Configuració del mòdul',
        'rights-requests': 'Sol·licituds de drets',
        'access-accountability': 'Accés a Responsabilitat',
        'data-discovery': 'Descobriment de dades',
        'incident-ops': "Operacions d'incidència",
        'deletion-orchestration': 'Orquestració de supressió',
        'compliance-forms': 'Formularis de compliment',
        'program-governance': 'Govern del programa',
        localization: 'Localització',
        'privacy-scanner': 'Escàner de privadesa',
        'consent-properties': 'Propietats del consentiment',
        acceptances: 'Acceptacions',
        access: 'Accés',
        'access-review': "Revisió d'accés",
        'access-reviews': 'Accedir a les ressenyes',
        accountability: 'Responsabilitat',
        acknowledgment: 'Reconeixement',
        'action-plans': "Plans d'acció",
        activation: 'Activació',
        activations: 'Activacions',
        'active-response': 'Resposta activa',
        activity: 'Activitat',
        adequacy: 'Adequació',
        'admin-guide': "Guia d'administració",
        aggregation: 'Agregació',
        agreements: 'Acords',
        ai: 'AI',
        alerts: 'Alertes',
        analytics: 'Analítica',
        anchoring: 'Ancoratge',
        approval: 'Aprovació',
        approvals: 'Aprovacions',
        apps: 'Aplicacions',
        assets: 'Actius',
        assignments: 'Encàrrecs',
        attestation: 'Atestació',
        'audit-log': "Registre d'auditoria",
        'audit-pack': "Paquet d'auditoria",
        authentication: 'Autenticació',
        authority: 'Autoritat',
        'auto-play': 'Reproducció automàtica',
        automated: 'Automatitzat',
        automation: 'Automatització',
        baseline: 'Línia de base',
        batches: 'Lots',
        bidirectional: 'Bidireccional',
        bilingual: 'Bilingüe',
        bindings: 'Enquadernacions',
        blockchain: 'Blockchain',
        board: 'Junta',
        brand: 'Marca',
        brds: 'BRDs',
        'break-glass': 'Rompe-Vidre',
        builder: 'Constructor',
        bulk: 'A granel',
        'bulk-sync': 'Sincronització massiva',
        bundles: 'Paquets',
        calendar: 'Calendari',
        campaigns: 'Campanyes',
        cases: 'Casos',
        categories: 'Categories',
        category: 'Categoria',
        certification: 'Certificació',
        chain: 'Cadena',
        change: 'Canviar',
        'change-awareness': 'Consciència del canvi',
        'change-log': 'Registre de canvis',
        'change-management': 'Gestió del canvi',
        'change-requests': 'Sol·licituds de canvi',
        'cmp-validation': 'CMP Validació',
        collection: 'Col·lecció',
        comparison: 'Comparació',
        completeness: 'Completitud',
        completion: 'Finalització',
        'compliance-posture': 'Postura de compliment',
        'compliance-proof': 'Prova de compliment',
        'compliance-reviews': 'Revisions de compliment',
        config: 'Config',
        confirmation: 'Confirmació',
        conflicts: 'Conflictes',
        consolidation: 'Consolidació',
        continuous: 'Contínua',
        contracts: 'Contractes',
        controls: 'Controls',
        cookies: 'Galetes',
        cooperation: 'Cooperació',
        coordination: 'Coordinació',
        'corporate-governance': 'Govern Corporatiu',
        'corrective-actions': 'Accions correctores',
        coverage: 'Cobertura',
        'credential-vault': 'Volta de credencials',
        credentials: 'Credencials',
        cycles: 'Cicles',
        dashboards: 'Taulers de control',
        'data-assets': 'Actius de dades',
        'data-flows': 'Fluxos de dades',
        'data-sensitivity': 'Sensibilitat de les dades',
        'data-steward': 'Administrador de dades',
        deadlines: 'Terminis',
        'decree-356': 'Decret 356',
        dedup: 'Desduplicació',
        definitions: 'Definicions',
        deliverable: 'Entregable',
        departments: 'Departaments',
        'dependency-order': 'Ordre de dependència',
        deployment: 'Desplegament',
        detection: 'Detecció',
        diff: 'Dif',
        'digital-properties': 'Propietats digitals',
        dimensions: 'Dimensions',
        disable: 'Desactivar',
        'disaster-recovery': 'Recuperació de desastres',
        disclosure: 'Divulgació',
        documentation: 'Documentació',
        documents: 'Documents',
        domains: 'Dominis',
        dossier: 'Dossier',
        dpa: 'DPA',
        drift: 'Deriva',
        'drift-detection': 'Detecció de deriva',
        'due-diligence': 'Due Diligence',
        email: 'Email',
        emergency: 'Emergència',
        enable: 'Activa',
        'enterprise-map': 'Mapa empresarial',
        erasure: 'Esborrat',
        evaluation: 'Avaluació',
        'event-bus': "Bus d'esdeveniments",
        exceptions: 'Excepcions',
        execution: 'Execució',
        executive: 'Executiu',
        exercises: 'Exercicis',
        export: 'Exporta',
        exports: 'Exportacions',
        filing: 'Arxivament',
        filter: 'Filtre',
        filtering: 'Filtrat',
        findings: 'Descobriments',
        flows: 'Fluxos',
        'follow-up': 'Seguiment',
        forensic: 'Forense',
        forensics: 'Forense',
        'form-filler': 'Emplenament de formularis',
        framework: 'Marc',
        frameworks: 'Marcs',
        'gap-analysis': 'Anàlisi de buits',
        'gap-report': 'Informe Gap',
        gaps: 'Buits',
        gates: 'Portes',
        'getting-started': 'Primers passos',
        'golden-templates': 'Plantilles daurades',
        grc: 'GRC',
        'group-management': 'Gestió de grups',
        groups: 'Grups',
        'guided-tours': 'Visites guiades',
        guides: 'Guies',
        handling: 'Manipulació',
        'hands-on': 'Pràctics',
        health: 'Salut',
        help: 'Ajuda',
        hierarchy: 'Jerarquia',
        history: 'Història',
        i18n: 'i18n',
        iam: 'IAM',
        'identity-provider': "Proveïdor d'identitat",
        implementation: 'Implementació',
        import: 'Importar',
        improvement: 'Millora',
        'incident-response': 'Resposta a incidents',
        inconsistencies: 'Incoherències',
        instances: 'Instàncies',
        intake: 'Ingesta',
        'intake-key': "Clau d'admissió",
        interactive: 'Interactiu',
        international: 'Internacional',
        inventory: 'Inventari',
        investigation: 'Investigació',
        investigations: 'Investigacions',
        'ip-allowlist': "Llista d'IP permeses",
        issues: 'Problemes',
        justification: 'Justificació',
        justifications: 'Justificacions',
        kanban: 'Kanban',
        keyboard: 'Teclat',
        keys: 'Les claus',
        'knowledge-base': 'Base de coneixement',
        koncernstyring: 'Govern Corporatiu',
        kpi: 'KPI',
        labeling: 'Etiquetatge',
        language: 'Llengua',
        'language-packs': "Paquets d'idiomes",
        languages: 'Idiomes',
        lawfulness: 'Legalitat',
        layout: 'Disseny',
        'legal-basis': 'Base legal',
        'lessons-learned': 'Lliçons apreses',
        levels: 'Nivells',
        library: 'Biblioteca',
        lifecycle: 'Cicle de vida',
        lineage: 'Llinatge',
        links: 'Enllaços',
        listings: 'Llistats',
        live: 'Viu',
        locale: 'Localització',
        log: 'Log',
        logging: 'Enregistrament',
        'managed-service': 'Servei Gestionat',
        mapping: 'Cartografia',
        mappings: 'Mapes',
        'market-packs': 'Paquets de mercat',
        marketplace: 'Mercat',
        maturity: 'Maduresa',
        metrics: 'Mètriques',
        models: 'Models',
        module: 'Mòdul',
        modules: 'Mòduls',
        mps: 'MPS',
        'multi-factor': 'Multifactor',
        'multi-market': 'Multimercat',
        'multi-org': 'Multi-Org',
        'multi-property': 'Multipropietat',
        'my-tasks': 'Les meves tasques',
        obligations: 'Obligacions',
        offboarding: 'Offboarding',
        oidc: 'OIDC',
        orchestration: 'Orquestració',
        'org-groups': 'Grups organitzatius',
        organization: 'Organització',
        overdue: 'Endarrerit',
        overview: 'Visió general',
        'owner-review': 'Revisió del propietari',
        package: 'paquet',
        packages: 'Paquets',
        packs: 'Paquets',
        pagination: 'Paginació',
        'parent-child': 'Pares i fills',
        patterns: 'Patrons',
        performance: 'Rendiment',
        periodic: 'Periòdic',
        periods: 'Períodes',
        personal: 'Personal',
        'personal-data': 'Dades personals',
        personas: 'Personas',
        planning: 'Planificació',
        platform: 'Plataforma',
        playbooks: 'Llibres de jugades',
        poc: 'PoC',
        'point-in-time': 'Punt en el temps',
        policies: 'Polítiques',
        portfolio: 'Cartera',
        'post-incident': 'Post-incident',
        'power-user': 'Usuari poderós',
        practice: 'Pràctica',
        pricing: 'Preus',
        'privacy-impact': 'Impacte de privadesa',
        procedures: 'Procediments',
        processes: 'Processos',
        processing: 'Tramitació',
        'processing-activities': 'Activitats de processament',
        procurement: 'Adquisició',
        productivity: 'Productivitat',
        profiles: 'Perfils',
        programs: 'Programes',
        progress: 'Progrés',
        proof: 'Prova',
        propagation: 'Propagació',
        properties: 'Propietats',
        'pub-sub': 'Pub-Sub',
        'public-form': 'Forma pública',
        quality: 'Qualitat',
        questionnaires: 'Qüestionaris',
        'quick-links': 'Enllaços ràpids',
        'quick-start': 'Inici ràpid',
        'quick-starts': 'Inicis ràpids',
        raci: 'RACI',
        readiness: 'Preparació',
        'real-time': 'En temps real',
        reassessments: 'Reavaluacions',
        reconciliation: 'Reconciliació',
        record: 'Registre',
        records: 'Registres',
        recurring: 'Recurrent',
        register: "Registra't",
        registry: 'Registre',
        regulations: 'Normativa',
        regulator: 'Regulador',
        regulatory: 'Regulatòria',
        'regulatory-frameworks': 'Marcs normatius',
        remap: 'Remapejar',
        reminders: 'Recordatoris',
        reports: 'Informes',
        requests: 'Sol·licituds',
        requirements: 'Requisits',
        response: 'Resposta',
        'response-plans': 'Plans de resposta',
        'response-team': 'Equip de resposta',
        'response-templates': 'Plantilles de resposta',
        'response-time': 'Temps de resposta',
        responses: 'Respostes',
        responsibility: 'Responsabilitat',
        results: 'Resultats',
        review: 'Revisió',
        reviews: 'Ressenyes',
        'right-to-delete': 'Dret a suprimir',
        rights: 'Drets',
        'risk-tiers': 'Nivells de risc',
        rollouts: 'Desplegaments',
        rollup: 'Enrotllament',
        ropa: 'ROPA',
        routing: 'Encaminament',
        rpo: 'RPO',
        rto: 'RTO',
        rules: 'Normes',
        runs: 'Corre',
        saml: 'SAML',
        scan: 'Escaneja',
        scanning: 'Escaneig',
        scc: 'SCC',
        schedule: 'Horari',
        scheduled: 'Programat',
        schedules: 'Horaris',
        scheduling: 'Programació',
        scores: 'Puntuacions',
        scoring: 'Puntuació',
        scripts: 'Guions',
        'security-reviews': 'Revisions de seguretat',
        'sensitive-data': 'Dades sensibles',
        sensitivity: 'Sensibilitat',
        'separation-of-duties': 'Separació de funcions',
        service: 'Servei',
        sessions: 'Sessions',
        'setup-wizard': 'Assistent de configuració',
        severity: 'Severitat',
        'shared-governance': 'Govern Compartit',
        'shared-settings': 'Configuració compartida',
        shortcuts: 'Dreceres',
        sidebar: 'Barra lateral',
        signals: 'Senyals',
        'single-sign-on': 'Inici de sessió únic',
        snapshots: 'Imatges instantànies',
        sod: 'SoD',
        sorting: 'Classificació',
        sources: 'Fonts',
        staging: 'Escenificació',
        standards: 'Normes',
        status: 'Estat',
        structure: 'Estructura',
        'sub-processor': 'Subprocessador',
        'sub-processors': 'Subprocessadors',
        submissions: 'Presentacions',
        subprocessors: 'Subprocessadors',
        subscription: 'Subscripció',
        suggestions: 'Suggeriments',
        summaries: 'Resums',
        supplements: 'Suplements',
        support: 'Suport',
        sync: 'Sincronització',
        system: 'Sistema',
        systems: 'Sistemes',
        tables: 'Taules',
        tagging: 'Etiquetatge',
        'tamper-proof': 'A prova de manipulació',
        taxonomies: 'Taxonomies',
        taxonomy: 'Taxonomia',
        tenants: 'Llogaters',
        termination: 'Terminació',
        'third-party': 'Tercers',
        'ticket-sync': 'Sincronització de bitllets',
        tickets: 'Entrades',
        tiers: 'Grades',
        tokens: 'Fitxes',
        totp: 'TOTP',
        traceability: 'Traçabilitat',
        tracking: 'Seguiment',
        trail: 'Sender',
        training: 'Formació',
        translation: 'Traducció',
        translations: 'Traduccions',
        transparency: 'Transparència',
        trends: 'Tendències',
        unified: 'Unificat',
        updates: 'Actualitzacions',
        validation: 'Validació',
        vault: 'Volta',
        'vendor-requests': 'Sol·licituds de venedors',
        verification: 'Verificació',
        versioning: 'Versioning',
        versions: 'Versions',
        vietnam: 'Vietnam',
        violations: 'Infraccions',
        visualization: 'Visualització',
        waivers: 'Renúncies',
        'web-records': 'Registres web',
        website: 'Lloc web',
        websites: 'Llocs web',
        weights: 'Peses',
        'white-label': 'Marca Blanca',
        withdrawal: 'Retirada',
    },
    module_labels: {
        // Sprint 62.57.0 Slice E — the audit module of the certificate register.
        service_certificates: 'Processing Service Certificate',
        // Sprint 63.25.0 (spec G2) — the source modules that had no label.
        compliance_rules: 'Compliance Rules',
        access_review: 'Access Reviews',
        consolidation: 'Consolidation',
        case_audit: 'Module Audit Trail',
        rights_requests: 'Sol·licituds de drets',
        data_mapping: "Registre d'activitats de tractament",
        governance: 'Governança',
        consent: 'Governança del consentiment',
        consent_properties: 'Propietats del consentiment',
        assessments: 'AIPD i avaluacions',
        classification: 'Classificació de dades',
        monitoring: 'Programes de supervisió',
        program: 'Programa de supervisió',
        incidents: 'Resposta a incidents',
        connectors: 'Connectors',
        vendor_risk: 'Risc de proveïdors',
        deletion_orchestration: "Orquestració d'eliminació",
        discovery: 'Descobriment de dades',
        incident_ops: "Operacions d'incidents",
        program_governance: 'Governança de programes',
        access_accountability: "Responsabilitat d'accés",
        localization: 'Localització',
        sector_overlays: 'Sector Overlays',
        partner_workspace: 'Espai de treball de socis',
        privacy_scanner: 'Escàner de privacitat',
        onboarding: 'Incorporació',
        compliance_forms: 'Formularis de Compliment',
        audit_trail: "Pista d'Auditoria",
        data_subjects: 'Interessats',
        organizations: 'Organitzacions',
        task_management: 'Gestió de tasques',
        deletion_orch: "Orquestració d'eliminació",
        forms: 'Formularis de compliment',
        partner_marketplace: 'Mercat de socis',
        dashboard: 'Tauler de control',
        platform_settings: 'Configuració de la plataforma',
        help_center: "Centre d'ajuda",
    },
    module_descriptions: {
        rights_requests: "Gestioneu les sol·licituds de drets dels interessats, incloent-hi l'accés, la supressió i la portabilitat. Feu seguiment del cicle de vida de les sol·licituds, assigneu responsables i compliu els terminis normatius.",
        data_mapping: 'Mapeeu els fluxos de dades a la vostra organització. Feu seguiment de les activitats de tractament, categories de dades, finalitats, polítiques de retenció i integracions de sistemes.',
        governance: "Supervisió i l'evidència que la sustenta. Gestioneu el registre de proveïdors i el seu cicle de vida, aproveu les decisions que aquest mòdul controla, vigileu les desviacions de compliment i mantingueu els registres de confiança i identitat, d'artefactes reguladors, de pagaments i eKYC, i d'evidència de la plataforma.",
        consent: "Gestioneu la recollida, l'emmagatzematge i la retirada del consentiment. Feu seguiment dels registres de consentiment, finalitats i integració amb plataformes de gestió del consentiment.",
        consent_properties: 'Configureu les propietats de consentiment, grups i plantilles mestres. Definiu configuracions de consentiment reutilitzables per a un desplegament consistent.',
        assessments: "Realitzeu Avaluacions d'Impacte en la Protecció de Dades (AIPD) i avaluacions de risc de privacitat. Avalueu les activitats de tractament per detectar riscos de compliment.",
        classification: 'Classifiqueu els actius de dades per nivell de sensibilitat. Apliqueu etiquetes taxonòmiques, feu seguiment dels registres del cicle de vida i identifiqueu llacunes de classificació.',
        monitoring: 'Configureu programes de supervisió per fer seguiment de mètriques de compliment, indicadors clau de risc i eficàcia dels controls al llarg del temps.',
        incidents: "Reporteu i gestioneu incidents de privacitat i violacions de dades. Feu seguiment dels fluxos d'investigació, notificació i remediació.",
        connectors: 'Configureu connectors de fonts de dades i integracions. Connecteu sistemes externs per a la descoberta automatitzada de dades i la supervisió.',
        vendor_risk: 'Avalueu i superviseu els riscos de proveïdors de tercers. Puntueu proveïdors, feu seguiment de la mitigació de riscos i gestioneu configuracions de risc de proveïdors.',
        deletion_orchestration: 'Orquestreu la supressió de dades a tots els sistemes. Feu seguiment de les sol·licituds de supressió, verifiqueu les cadenes de proves i assegureu la supressió completa de les dades.',
        discovery: 'Descobriu dades personals als sistemes connectats. Escanegeu fonts de dades per construir un inventari de dades complet.',
        incident_ops: "Gestió operativa d'incidents amb manuals de procediments, fluxos d'escalament i seguiment de SLA per a la resolució d'incidents.",
        program_governance: "Feu seguiment de la maduresa general del programa de privacitat, puntuacions de compliment, llacunes d'evidència i informes executius a tota la vostra organització.",
        access_accountability: "Investigueu i auditeu els patrons d'accés dels usuaris. Feu seguiment de les configuracions d'accés, superviseu l'activitat sospitosa i apliqueu la rendició de comptes.",
        localization: "Instal·leu i gestioneu paquets de localització de mercat. Afegiu marcs regulatoris, paquets d'idioma i contingut de compliment específic per regió.",
        sector_overlays: 'Install and manage optional sector overlays, and open the sector registers an activated overlay brings into use, organised by sector. Available in the Enterprise package.',
        partner_workspace: 'Col·laboreu amb socis externs. Compartiu dades de compliment, feu seguiment de les obligacions dels socis i gestioneu les relacions amb socis.',
        privacy_scanner: "Escanegeu llocs web per detectar problemes de compliment de privacitat. Superviseu l'ús de galetes, scripts de rastreig i la presència de polítiques de privacitat en temps real.",
        onboarding: "Guieu les noves organitzacions a través de la configuració de la plataforma. Assistent d'incorporació pas a pas per a la configuració inicial i la importació de dades.",
        compliance_forms: "Creeu i gestioneu formularis de compliment a partir d'un catàleg de plantilles. Empleneu formularis regulatoris, feu seguiment de les trameses i genereu informes.",
        audit_trail: "Visualitzeu una pista d'auditoria completa de totes les accions del sistema. Feu seguiment de qui va fer què, quan i on a tots els mòduls.",
        organizations: "Gestioneu organitzacions i configuracions multi-inquilí. Configureu perfils d'organització, jerarquies i relacions entre organitzacions.",
    },
    privacy_scanner: {
        anomaly_explain: {
            explain_label: 'Explica les anomalies',
            explaining: 'Explicant…',
            results_heading: "Explicacions d'anomalies ({severity})",
            tokens_label: '≈ {count} fitxes',
            items_label: '{count} elements',
            remediation_label: 'Passos de reparació',
            items_detail_label: 'Elements afectats',
            accept_label: "Accepta l'explicació",
            reject_label: "Rebutja l'explicació",
            error_generic: "No s'han pogut explicar les anomalies. Torneu-ho a provar o comproveu que l'escaneig s'hagi completat correctament.",
        },
        cookie_insight: {
            insight_label: 'Estadístiques de AI de galetes i seguiment',
            producing: 'Produint informació...',
            results_heading: 'Estadístiques de galetes i seguiment ({severity})',
            tokens_label: '≈ {count} fitxes',
            items_label: '{count} elements',
            classification_label: 'Classificació',
            severity_label: 'Severitat',
            banner_copy_label: 'Còpia de banner suggerida',
            retention_label: 'Consell de retenció',
            citations_label: 'Citacions',
            kind_cookie: 'Galeta',
            kind_tracker: 'Seguidor',
            class_necessary: 'Necessari',
            class_analytics: 'Analítica',
            class_marketing: 'Màrqueting',
            class_social: 'Social',
            accept_label: 'Accepta la visió',
            reject_label: 'Rebutja la visió',
            no_items: "No s'han detectat galetes ni rastrejadors en aquesta exploració.",
            error_generic: "No s'ha pogut produir la informació de les galetes. Torneu-ho a provar o comproveu que l'escaneig s'hagi completat correctament.",
        },
        monitoring: {
            title: 'Supervisió de privacitat',
            new_scan: 'Nou escaneig',
            scan_url: 'URL a escanejar',
            scan_frequency: "Freqüència d'escaneig",
            last_scan: 'Últim escaneig',
            score: 'Puntuació de privacitat',
            issues: 'Problemes trobats',
            history: "Historial d'escaneigs",
            settings: 'Configuració de supervisió',
        },
        real_time: {
            title: 'Escàner en temps real',
            scan_now: 'Escanejar ara',
            scanning: 'Escanejant...',
            results: "Resultats de l'escaneig",
            no_results: "Encara no hi ha resultats d'escaneig.",
            cookies: 'Galetes',
            trackers: 'Rastrejadors',
            consent_banners: 'Bàners de consentiment',
            third_party: 'Scripts de tercers',
            archive: 'Arxiu de resultats',
            settings: "Configuració de l'escàner",
        },
        dashboard: {
            page_title: 'Tauler de control de privadesa',
            privacy_monitoring: 'Monitorització de la privadesa',
            plan_label: '{package} pla',
            expires_label: 'caduca el {date}',
            settings: 'Configuració',
            refresh: 'Actualitza',
            refreshing_dashboard: 'Tauler de control actualitzat',
            refresh_aria: 'Actualitzar el tauler de control',
            not_configured_title: 'Monitorització de privadesa no configurada',
            not_configured_desc: 'Connecteu el vostre AesirX servei de monitorització de privadesa per començar a fer un seguiment del compliment del lloc web.',
            configure_monitoring: 'Configura el monitoratge',
            license_expired_title: 'Llicència caducada',
            license_expired_desc: 'La vostra AesirX llicència de monitorització de privadesa ha caducat. Si us plau, renoveu-lo per evitar la interrupció del servei.',
            compliance_score: 'Puntuació de compliment',
            cookies_detected: "S'han detectat galetes",
            trackers_label: 'Seguidors',
            open_issues: 'Problemes oberts',
            score_good: 'Bé',
            score_needs_improvement: 'Necessita millora',
            score_critical: 'Crític',
            action_required: 'Acció necessària',
            cookies_sr: 'galetes detectades',
            trackers_sr: 'rastrejadors trobats',
            open_issues_sr: 'qüestions obertes',
            action_required_sr: 'acció requerida',
            metric_label: 'mètrica',
            value_label: 'Valor',
            status_label: 'Estat',
            aria_metrics: 'Mètriques de control de privadesa',
            plan_limit_warning: 'Escanejant URL: {current}/{max}: heu assolit el límit del vostre pla.',
            manage_domains: 'Gestioneu dominis o actualitzeu el vostre pla.',
            monitored_domains: 'Dominis monitoritzats',
            domain_added: "S'ha afegit el domini {domain}.",
            domain_removed: "S'ha eliminat el domini {domain}.",
            failed_add_domain: "No s'ha pogut afegir el domini",
            failed_remove_domain: "No s'ha pogut eliminar el domini",
            failed_load: "No s'ha pogut carregar el tauler de control",
            new_domain_aria: 'Nou domini URL',
            add_domain: 'Afegeix domini',
            adding_domain: "S'està afegint…",
            no_domains: "Encara no s'ha configurat cap domini supervisat.",
            aria_domains_table: 'Dominis monitoritzats',
            col_domain: 'Domini',
            col_compliance_score: 'Puntuació de compliment',
            col_status: 'Estat',
            col_cookies: 'Galetes',
            col_trackers: 'Seguidors',
            col_issues: 'Problemes',
            col_last_scan: 'Últim escaneig',
            col_action: 'Acció',
            compliance_status_sr: 'estat de compliment',
            showing_chart: "S'està mostrant la vista del gràfic",
            showing_table: "S'està mostrant la vista de la taula de dades",
            loading_sr: "S'està carregant el tauler de control",
            col_framework_tags: 'Etiquetes de marc',
            col_last_monitored: 'Darrer seguiment',
            framework_tags_sr: 'marcs normatius aplicables',
            last_monitored_sr: 'marca de temps de seguiment continu',
            audit_trail_heading: "Pista d'auditoria",
            audit_trail_info: "Cada exploració de monitorització té una marca de temps i es registra. Els canvis de compliment a nivell de domini produeixen un registre d'auditoria immutable que es pot exportar per a una revisió normativa.",
            cybersecurity_law_info_heading: 'Detecció de lleis de ciberseguretat',
            cybersecurity_law_info: "Els dominis supervisats es comproven automàticament amb els requisits de la llei de ciberseguretat (p. ex., la Llei de ciberseguretat del Vietnam, la Directiva NIS2). Les etiquetes marc indiquen quines normatives s'apliquen. Els problemes es classifiquen segons el marc regulador per a la correcció específica.",
        },
        scan: {
            page_title: 'Escaneig en temps real',
            title: 'Escàner de privadesa en temps real',
            settings: 'Configuració',
            results_archive: 'Arxiu de resultats',
            launch_scan: 'Inicieu Scan',
            target_url: 'Objectiu URL',
            protocol_aria: 'Protocol',
            url_required: 'URL és obligatori',
            url_whitespace: 'Si us plau, traieu els espais en blanc del URL',
            url_invalid: 'Introduïu un domini vàlid (per exemple, example.com)',
            add_subpages: 'Afegeix subpàgines',
            add_subpages_aria: "Afegeix subpàgines, s'han afegit {count}.",
            start_scan: "Inicieu l'escaneig",
            launching: "S'està llançant…",
            scan_launched: "L'escaneig s'ha iniciat correctament",
            scan_failed: "No s'ha pogut iniciar l'exploració",
            scan_progress: "Progrés de l'escaneig",
            scanning_in_progress: 'Escaneig en curs...',
            scan_id: "Identificació d'escaneig",
            status_label: 'Estat',
            status_unknown: 'desconegut',
            progress_label: 'Progrés',
            pages_label: 'Pàgines',
            refresh_status: "Actualitzar l'estat",
            new_scan: 'Escaneig nou',
            scan_completed: 'Escaneig completat, resultats disponibles',
            failed_check_status: "No s'ha pogut comprovar l'estat de l'escaneig",
            scan_results: "Resultats de l'escaneig",
            main_page: 'Pàgina principal',
            page_n: 'Pàgina {n}',
            result_pages_nav: 'Pàgines de resultats',
            page_url: 'Pàgina URL',
            risk_score: 'Puntuació de risc',
            risk_score_sr: 'puntuació de risc',
            cookies_label: 'Galetes',
            cookies_sr: 'galetes detectades',
            scripts_label: 'Guions',
            scripts_sr: 'scripts detectats',
            issues_label: 'Problemes',
            issues_sr: 'problemes trobats',
            issues_found: 'Problemes trobats',
            aria_issues_table: "S'han trobat problemes durant l'escaneig",
            col_severity: 'Severitat',
            col_category: 'Categoria',
            col_title: 'Títol',
            col_remediation: 'Remediació',
            severity_sr: 'gravetat',
            scan_result_metrics: 'Escaneja mètriques de resultats',
            view_full_detail: 'Veure detall complet',
            col_consent_category: 'Categoria de consentiment',
            consent_category_sr: 'categorització del consentiment',
            compliance_evidence_heading: 'Evidència de compliment',
            compliance_evidence_info: "Les troballes de l'escaneig s'enllaçen amb els casos de compliment automàticament. Cada problema es pot localitzar a una categoria de consentiment (essencial, funcional, d'anàlisi, màrqueting) i produeix proves preparades per a l'auditoria per a la revisió normativa.",
            rule_config_heading: 'Configuració de regles',
            rule_config_info: "Les regles d'escaneig es poden configurar segons el marc normatiu. Les comprovacions de galetes, la detecció d'scripts i l'anàlisi de proveïdors es poden activar o desactivar mitjançant Configuració. Es poden definir regles personalitzades per adaptar-se als requisits de compliment de la vostra organització.",
            case_linkage_heading: 'Enllaç de casos de compliment',
            case_linkage_info: "Els problemes trobats durant les exploracions en temps real es poden enllaçar amb casos de compliment per fer un seguiment i correcció. Navegueu a la pàgina Detalls del resultat de l'escaneig per veure les associacions de casos.",
            subpages_modal_title: 'Afegeix subpàgines',
            subpages_modal_desc: 'Afegiu fins a 5 URL de subpàgina per escanejar juntament amb el domini principal.',
            subpage_n: 'Subpàgina {n}',
        },
        result_detail: {
            page_title: "Detall del resultat de l'escaneig",
            title: "Resultat de l'escaneig",
            back_to_archive: "Torna a l'arxiu",
            failed_load: "No s'ha pogut carregar el resultat de l'anàlisi",
            loading_sr: "S'està carregant el resultat de l'escaneig...",
            scan_id: "Identificació d'escaneig",
            target_url: 'Objectiu URL',
            status_label: 'Estat',
            status_sr: 'estat',
            started: 'Començat',
            completed: 'Completat',
            pages_scanned: 'Pàgines escanejades',
            https_label: 'HTTPS',
            highest_risk_score: 'Puntuació de risc més alta',
            most_cookies: 'La majoria de galetes',
            most_vendors: 'La majoria de venedors de tercers',
            most_issues: 'La majoria de problemes',
            across_pages: 'a través de pàgines',
            highest_values_aria: 'Valors més alts a totes les pàgines',
            scanned_pages_nav: 'Pàgines escanejades',
            main_page: 'Pàgina principal',
            page_n: 'Pàgina {n}',
            page_url: 'Pàgina URL',
            risk_score: 'Puntuació de risc',
            overall_risk_sr: 'puntuació global de risc',
            cookies_label: 'Galetes',
            cookies_sr: 'galetes detectades',
            vendors_label: 'Proveïdors de tercers',
            vendors_sr: 'proveïdors de tercers detectats',
            issues_label: 'Problemes',
            issues_sr: 'problemes trobats',
            risk_breakdown_aria: 'Desglossament de la puntuació de risc',
            issues_found: 'Problemes trobats',
            aria_issues_table: "S'han trobat problemes durant l'escaneig",
            col_severity: 'Severitat',
            col_category: 'Categoria',
            col_title: 'Títol',
            col_remediation: 'Remediació',
            severity_sr: 'gravetat',
            cookies_heading: 'Galetes ({count})',
            aria_cookies_table: "S'han detectat cookies",
            col_name: 'Nom',
            col_domain: 'Domini',
            col_path: 'Camí',
            col_secure: 'Segur',
            col_httponly: 'Només Http',
            col_samesite: 'SameSite',
            vendors_heading: 'Proveïdors de tercers ({count})',
            aria_vendors_table: "S'han detectat proveïdors de tercers",
            col_hostname: "Nom d'amfitrió",
            scripts_heading: 'Scripts/Beacons ({count})',
            aria_scripts_table: "S'han detectat scripts i balises",
            col_url: 'URL',
            col_type: 'Tipus',
            links_heading: 'Enllaços',
            first_party: 'Primera part',
            third_party: 'De tercers',
            col_risk_category: 'Categoria de risc',
            col_regulatory_framework: 'Marc normatiu',
            col_actionable_detail: 'Detall accionable',
            risk_category_sr: 'categoria de risc regulador',
            risk_category_mapping_heading: 'Cartografia de categories de risc',
            risk_category_mapping_info: "Cada troballa d'escaneig s'assigna a una categoria de risc reglamentària (recollida de dades, transferència transfronterera, gestió del consentiment, retenció de dades, controls de seguretat). Aquest mapatge permet una correcció dirigida prioritzada per l'impacte regulatori.",
            compliance_case_heading: 'Associació de casos de compliment',
            compliance_case_info: "Els resultats de l'escaneig estan relacionats amb casos de compliment per al seguiment d'extrem a extrem. Els problemes es poden escalar directament dels resultats de l'escaneig al flux de treball del cas de compliment.",
        },
        results_archive: {
            page_title: "Arxiu de resultats de l'escaneig",
            title: "Arxiu de resultats de l'escaneig",
            back_to_scanner: "Torna a l'escàner",
            failed_load: "No s'han pogut carregar els resultats de l'anàlisi",
            results_loaded: "S'han carregat els resultats de l'escaneig, {count} registres",
            total_label: 'Total: {count}',
            aria_table: "Arxiu de resultats de l'escaneig",
            col_scan_date: "Data d'escaneig",
            col_target_url: 'Objectiu URL',
            col_duration: 'Durada',
            col_issues_found: 'Problemes trobats',
            col_risk_score: 'Puntuació de risc',
            col_status: 'Estat',
            col_actions: 'Accions',
            scan_status_sr: "estat d'escaneig",
            view_result: 'Veure',
            no_results: "No s'han trobat resultats d'escaneig.",
            loading_sr: "S'estan carregant els resultats de l'escaneig",
            pagination_aria: "Paginació dels resultats de l'escaneig",
            col_risk_category: 'Categoria de risc',
            risk_category_sr: 'categoria de risc regulador',
            archive_retention_heading: "Retenció d'arxius",
            archive_retention_info: "Els resultats de l'escaneig es conserven durant el temps especificat a la política de retenció de dades de la vostra organització. Els resultats arxivats inclouen un context d'exploració complet (etiquetes de marc, categories de risc, orientació de correcció) i es poden exportar com a CSV per a proves normatives.",
            export_context_info: 'Els fitxers CSV exportats inclouen context normatiu: categoria de risc, marcs aplicables i detalls de correcció accionables per a cada troballa.',
        },
        scanner_settings: {
            page_title: "Configuració de l'escàner",
            title: "Configuració de l'escàner",
            back_to_scanner: "Torna a l'escàner",
            failed_load: "No s'ha pogut carregar la configuració de l'escàner",
            endpoint_url: 'Punt final URL',
            save_success: "La configuració de l'escàner s'ha desat correctament.",
            save_failed: "No s'ha pogut desar la configuració de l'escàner",
            test_connection: 'Prova de connexió',
            testing: 'Prova...',
            saving: "S'està desant...",
            loading_sr: "S'està carregant la configuració de l'escàner",
            endpoint_fixed_hint: 'The scanner address is fixed by the platform and cannot be changed here. Test Connection checks the AesirX credentials saved under Monitoring Settings.',
        },
        monitoring_settings: {
            page_title: 'Configuració de monitorització de privadesa',
            title: 'Configuració de monitoratge',
            back_to_dashboard: 'Tornar al tauler',
            failed_load: "No s'ha pogut carregar la configuració de supervisió",
            credentials_heading: 'AesirX Credencials',
            connected_as: 'Connectat com a {username}',
            credentials_desc: 'Introduïu les credencials del vostre compte AesirX per connectar la Monitorització de privadesa. Trobeu el vostre ID de membre a la pàgina de perfil del vostre compte AesirX.',
            client_id: 'ID de client',
            client_secret: 'Secret del client',
            username: "AesirX Nom d'usuari (correu electrònic)",
            password: 'AesirX Contrasenya',
            placeholder_keep: '(deixa en blanc per continuar existint)',
            credential_form_aria: 'AesirX formulari de credencials',
            test_connection: 'Prova de connexió',
            testing: "S'està provant…",
            test_aria: 'Prova la connexió AesirX.',
            testing_aria: 'Prova de connexió',
            save_configuration: 'Desa la configuració',
            saving: "S'està desant…",
            save_aria: 'Desa les credencials',
            saving_aria: 'Guardant credencials',
            credentials_saved: 'AesirX credencials desades correctament.',
            credentials_saving: "AesirX credencials desades. S'està tornant a carregar la configuració...",
            credentials_save_failed: "No s'han pogut desar les credencials",
            client_id_required: "Es requereix l'identificador de client i el nom d'usuari.",
            all_fields_required: 'Tots els camps de credencials són obligatoris.',
            email_heading: 'Notificacions per correu electrònic',
            receive_emails: "Rebre correus electrònics d'informes d'escaneig",
            email_toggle_aria: "Commuta els correus electrònics d'informes d'escaneig",
            only_on_change: "Només correu electrònic quan canvia l'estat del risc",
            only_on_change_aria: "Commuta el correu electrònic només amb el canvi d'estat de risc",
            emails_enabled: "Escaneja els correus electrònics d'informes activat",
            emails_disabled: "Escaneja els correus electrònics d'informes desactivats",
            only_change_on: 'Només enviant correus electrònics sobre canvis de risc',
            only_change_off: 'Enviament de correu electrònic a cada escaneig',
            email_failed: "No s'ha pogut actualitzar la configuració del correu electrònic",
            email_settings_aria: 'Configuració de notificacions per correu electrònic',
            loading_sr: "S'està carregant la configuració de monitorització",
        },
        entity_types: {
            user: 'Usuari',
            case: 'Cas',
            incident: 'Incident',
            assessment: 'Avaluació',
            vendor: 'Venedor',
            consent: 'Consentiment',
            policy: 'Política',
            data_map: 'Mapa de dades',
        },
        actions: {
            create: 'Crear',
            update: 'Actualització',
            delete: 'Suprimeix',
            view: 'Veure',
            export: 'Exporta',
            status_change: "Canvi d'estat",
        },
    },
    forms: {
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        title: 'Form',
        dashboard: {
            title: 'Tauler de formularis',
            total_templates: 'Total de plantilles',
            installed: 'Instal·lades',
            available: 'Disponibles',
            by_market: 'Per mercat',
            back_dashboard: 'Enrere a Tauler de control',
            browse_templates: 'Explorar plantilles',
            catalog_coverage: 'Cobertura del catàleg',
            quick_start: 'Inici ràpid',
            recent_instances: 'Instàncies recents',
            total_instances: "Total d'instàncies",
            fetch_failed: 'Error en fetch dashboard data. Si us plau, torneu-ho a intentar.',
            no_instances: "No s'han trobat instances",
            form_metrics: 'Resum de mètriques de formularis',
            templates_total: 'plantilles en total',
            instances_total: 'instàncies en total',
            installed_of_available: '{installed} de {available} instal·lat',
            templates_in_category: 'plantilles a {category}',
            uncategorized: 'Uncategorized',
            other_framework: 'Other',
            category_templates_aria: '{category} templates',
            no_templates: 'No form templates available.',
        },
        templates: {
            title: 'Plantilles de formularis',
            new: 'Nova plantilla',
            edit: 'Editar plantilla',
            name: 'Nom de la plantilla',
            form_type: 'Tipus de formulari',
            market: 'Mercat',
            language: 'Idioma',
            schema: 'Esquema del formulari',
            preview: 'Previsualització',
            duplicate: 'Duplicar plantilla',
            grid_view: 'Vista de quadrícula',
            list_view: 'Llista Veure',
            fetch_failed: 'Error en fetch templates. Si us plau, torneu-ho a intentar.',
            filter_templates: 'Filtrar plantilles',
            version_history: 'Historial de versions',
            no_version_history: 'Sense historial de versions — aquesta és la versió inicial.',
            form_id: 'ID del formulari',
            source_instrument: "Instrument d'origen",
            issuer: 'Emissor',
            all_frameworks: 'Tots els marcs',
            view_mode: 'Mode de visualització',
            classification: 'Classificació',
        },
        catalog: {
            title: 'Catàleg de formularis',
            install: 'Instal·lar',
            uninstall: 'Desinstal·lar',
            open_form: 'Open form',
            install_all: 'Instal·lar tot',
            installed_tab: 'Instal·lats',
            catalog_tab: 'Catàleg',
            search_placeholder: 'Cercar plantilles...',
            no_templates: "No s'han trobat plantilles.",
            template_count: '{count} plantilles',
            all_markets: 'Tots els mercats',
            sections: 'Seccions',
            all_categories: 'Totes les categories',
            category: 'Categoria',
            category_install_failed: 'Error en install category templates. Si us plau, torneu-ho a intentar.',
            category_install_success: 'Plantilles de categoria instal·lades.',
            fetch_failed: 'Error en fetch templates. Si us plau, torneu-ho a intentar.',
            fetch_installed_failed: 'Error en fetch installed templates. Si us plau, torneu-ho a intentar.',
            fields: 'Camps',
            install_failed: 'Error en install template. Si us plau, torneu-ho a intentar.',
            install_success: 'Plantilla installed amb èxit.',
            installing: 'Instal·lant...',
            removing: 'Eliminant...',
            uninstall_confirm: 'Esteu segur que voleu desinstal·lar aquesta plantilla?',
            uninstall_failed: 'Error en uninstall template. Si us plau, torneu-ho a intentar.',
            uninstall_success: 'Plantilla uninstalled amb èxit.',
            installation_summary: "Resum d'instal·lació",
            filter_catalog: 'Filtrar catàleg',
            all_frameworks: 'Tots els marcs',
            templates_installed_count: '{count} plantilles instal·lades correctament',
            all_already_installed: "Totes les plantilles d'aquesta categoria ja estan instal·lades",
            category_install_partial: '{installed} of {requested} template(s) installed. {refused} restricted register(s) were not installed — adding those requires the regulatory-artifact custody permission.',
            category_install_none: 'No templates were installed. {refused} restricted register(s) in this category require the regulatory-artifact custody permission.',
            form_id: 'ID del formulari',
            issuer: 'Emissor',
            catalog_tabs: 'Pestanyes del catàleg de formularis',
        },
        filler: {
            back_templates: 'Enrere a Plantilles',
            no_sections: "No s'han trobat sections",
            draft_created: 'Esborrany creat.',
            draft_saved: 'Esborrany desat.',
            fetch_template_failed: 'Error en fetch template. Si us plau, torneu-ho a intentar.',
            save_draft: 'Desar Esborrany',
            save_draft_failed: 'Error en save draft. Si us plau, torneu-ho a intentar.',
            saving: 'Desant...',
            select_option: 'Seleccioneu option',
            submit: 'Enviar',
            submit_failed: 'Error en submit. Si us plau, torneu-ho a intentar.',
            submitting: 'Enviant...',
            title: 'Assistent de formulari',
            form_id_label: 'ID del formulari',
            schema_label: 'Esquema',
            form_number_label: 'Form',
            framework_label: 'Marc',
            issuer_label: 'Emissor',
            source_label: 'Origen',
            market_label: 'Mercat',
            section_of: 'Secció {current} de {total}',
            form_navigation: 'Navegació del formulari',
            form_progress: 'Progrés del formulari: Secció {current} de {total}',
            removed_item: "S'ha eliminat l'element {index} de {field}",
            added_item: "S'ha afegit un nou element a {field}",
            go_to_previous: 'Anar a la secció anterior, secció {current} de {total}',
            go_to_next: 'Anar a la secció següent, secció {current} de {total}',
            risk_matrix_placeholder: '1–5',
        },
        filing: {
            title: 'Estat de presentació',
            advance: 'Avançar estat de presentació...',
            not_started: 'No iniciat',
            drafting: 'Redacció',
            internal_review: 'Revisió interna',
            approved_for_submission: 'Aprovat per a presentació',
            submitted_to_authority: "Presentat a l'autoritat",
            supplement_requested: 'Complement sol·licitat',
            supplement_in_progress: 'Complement en curs',
            resubmitted: 'Reenviat',
            accepted: 'Acceptat',
            rejected: 'Rebutjat',
            closed: 'Tancat',
            // Sprint 63.37.0 (Stream J) — the filing milestones the instance page lists.
            milestone_submitted_at: 'Submitted',
            milestone_supplement_requested_at: 'Supplement requested',
            milestone_supplement_submitted_at: 'Supplement submitted',
            milestone_accepted_at: 'Accepted',
            milestone_rejected_at: 'Rejected',
            milestone_closed_at: 'Closed',
        },
        instances: {
            approved_by: 'Aprovat Per',
            form_answers: 'Respostes del formulari',
            column_header: 'Form',
            no_answers: "No s'han trobat answers",
            view_outputs: 'Veure Outputs',
            approve: 'Aprovar',
            approve_failed: 'Error en approve. Si us plau, torneu-ho a intentar.',
            approving: 'Aprovant...',
            detail_title: 'Detall de la instància',
            fetch_failed: 'Error en fetch instances. Si us plau, torneu-ho a intentar.',
            generate_failed: 'Error en generate output. Si us plau, torneu-ho a intentar.',
            generate_output: 'Generar sortida',
            generating: 'Generant...',
            submitted_at: 'Enviat el',
            instance_metadata: 'Metadades de la instància del formulari',
            filing_status_steps: "Passos d'estat d'arxiu",
            current_status: 'estat actual',
            filing_status_update_failed: 'Error en actualitzar estat de la presentació',
            // Sprint 63.37.0 (I1) — the export buttons and the sample notice of an instance.
            form_number: 'Form number',
            export_pdf: 'Export PDF',
            export_docx: 'Export Word (DOCX)',
            exporting: 'Exporting {format}…',
            export_done: '{format} exported. The download has started.',
            export_failed: 'The {format} could not be exported.',
            export_not_approved: 'This form cannot be exported yet. Approve it first; a form inside a record is exported once it is completed or finalized.',
            sample_notice: 'This form is sample data. Every page of its exports carries the stamp "{stamp}".',
            // Sprint 63.37.0 (Stream J) — the finalize action and the form actions a parent record's row offers.
            finalize: 'Finalize',
            finalized: 'Form finalized.',
            finalize_failed: 'The form could not be finalized.',
            edit_form: 'Edit',
            view_form: 'View',
            open_instance: 'Open {form}',
            // Sprint 63.40.0 (decision 8) — the page description and the notification dossier.
            detail_description: 'The answers of one form, its exports and, where the form opens one, the notification dossier prepared from it.',
            notice_dossier: {
                title: 'Notification dossier',
                description: 'The platform prepares this dossier from the completed form. It does not file it: your organisation files it with the receiving authority.',
                prepare: 'Prepare the dossier for {form}',
                preparing: 'Preparing the dossier for {form}…',
                prepared: 'The dossier for {form} is prepared.',
                prepare_failed: 'The dossier could not be prepared.',
                prepare_after_final: 'Complete and approve the form to prepare its dossier.',
                load_failed: 'The dossier could not be loaded.',
                reload: 'Load the dossier again',
                loading: 'Loading the dossier…',
                reference: 'Dossier reference',
                receiving_authority: 'Receiving authority',
                receiving_authority_hint: 'As the installed form states it.',
                addressee_label: 'Name of the receiving body',
                addressee_hint: 'The installed form names a class of body, not one body. Enter the body you address this dossier to. Where the completed form already names it on its own addressee line, the dossier starts with that name. A name you enter or clear here is kept and does not change the form. You can leave it empty until it is decided.',
                addressee_recorded: 'Recorded name',
                not_recorded: 'Not recorded',
                statements_title: 'Your statements',
                statements_description: 'Only you, as the applicant, can make these statements. The platform records your answer and does not decide it.',
                statement_basis: 'Basis: {basis}',
                stated_yes: 'This applies',
                stated_no: 'This does not apply',
                stated_unanswered: 'Not answered',
                authority_step_title: "The receiving body's step",
                authority_step_note: "This step is the receiving body's. No deadline is set against your organisation.",
                save: 'Save the dossier',
                saving: 'Saving the dossier…',
                saved: 'The dossier is saved.',
                save_failed: 'The dossier could not be saved.',
                export_title: 'Export the dossier',
                export_hint: "The submission slip prints the receiving body's name as saved on the dossier. Until a name is saved, its addressee line is left blank.",
                export_open: 'Preview the submission slip',
            },
        },
        outputs: {
            back_instance: 'Tornar a la instància',
            no_outputs_msg: "Encara no s'han generat resultats.",
            no_outputs_title: 'Sense resultats',
            output_name: 'Nom de sortida',
            download: 'Baixar',
            download_failed: 'Error en download. Si us plau, torneu-ho a intentar.',
            fetch_failed: 'Error en fetch outputs. Si us plau, torneu-ho a intentar.',
            format: 'Format',
            generated_at: 'Generat el',
            generated_by: 'Generat per',
            size: 'Mida',
            title: 'Resultats del formulari',
            print: 'Imprimir',
            print_outputs: 'Imprimir sortides del formulari',
            printing: 'Imprimint sortides del formulari',
            pdf_preview: 'Vista prèvia PDF',
            downloading: 'Descarregant {name}',
            preview_in_new_window: 'Previsualitzar {name} ({format}) en una nova finestra',
            // Sprint 62.45.0 AC9 — column headers of the form print export (PDF table).
            print_col_field: 'Field',
            print_col_value: 'Value',
            // Sprint 62.45.0 AC9 — metadata lines of the form print export.
            print_meta_instance: 'Instance ID: {id}',
            print_meta_status: 'Status: {status}',
            print_meta_generated: 'Generated: {timestamp}',
            // Sprint 63.37.0 (H2) — the header lines of a printed official form.
            official_header_form_number: 'Form number',
            official_header_instrument: 'Source instrument',
            official_header_issuer: 'Issued by',
            official_header_authority: 'Authority',
            // Sprint 62.58.0 (AC-A4) — the draft-instrument notice an output prints before its title.
            draft_notice_frame: 'Prepared under a draft instrument that is not in force. Nothing in this document is an obligation.',
            draft_notice_framework: '{name} — pack version {version}',
            draft_notice_regenerate: 'Generated before the draft notice was added — generate it again.',
            // Sprint 62.58.0 gate E12.5 pass 2 (P3-5) — an output whose printed notices no longer match the frameworks' state.
            draft_notice_status_changed: "The framework's status has changed since this was generated — generate it again.",
        },
        // Sprint 62.58.0 (AC-A3) — what an instance surface of a draft instrument's template adds.
        draft_notice: {
            instance_note: 'Filling in this form does not make anything in it an obligation.',
        },
        cybersec_domains: {
            scope: 'Àmbit i aplicabilitat',
            risk: 'Avaluació de riscos i mapa de controls',
            policy: 'Polítiques de seguretat organitzativa',
            localization: 'Localització de dades i presència local',
            vendor: 'Tercers i cadena de subministrament',
            authority: "Gestió de sol·licituds d'autoritats",
            incident: 'Resposta a incidents i evidències',
            training: 'Formació i preparació',
        },
        market_finalization_domains: {
            data_law: 'Llei de Dades — Governança i Controls Operatius',
            pdpl_2025: 'PDPL 2025 — Governança de Privacitat i Drets dels Subjectes de Dades',
            decree_356: 'Decret 356/2025 — Controls de Proveïdors i Transfrontereres',
            telecom: 'Llei de Telecomunicacions — Confidencialitat i Sol·licituds de les Autoritats',
        },
        nav: {
            filing_case_links: "Enllaços d'expedient",
            section_routing: 'Encaminament de seccions',
            submission_packages: 'Paquets de presentació',
        },
        filing_case_links: {
            title: "Enllaços d'expedient",
            link_form: "Vincular formulari a l'expedient",
            filing_case_type: "Tipus d'expedient",
            form_instance: 'Instància de formulari',
            select_form_instance: 'Seleccioneu una instància de formulari...',
            select_filing_case: 'Seleccioneu un expedient...',
            no_links_found: "No s'han trobat enllaços d'expedient.",
            linked: 'Vinculat',
            filing_case_id: "ID del cas d'arxiu",
            link_created: "Enllaç de cas d'arxiu creat",
            link_deleted: "Enllaç d'expedient eliminat",
            aria_delete_link: 'Eliminar enllaç {name}',
            case_type_dpia_dossier: 'Dossier EIPD',
            case_type_cross_border_filing: 'Arxiu transfronterer',
            case_type_breach_notification: 'Notificació de violació',
            no_instances_guidance: 'Encara no hi ha instàncies de formulari. Creeu-ne una des de Plantilles de formulari.',
            no_filings_guidance: "Encara no hi ha expedients. Creeu-ne un des d'Avaluacions.",
        },
        section_routing: {
            title: 'Encaminament de seccions',
            assign_section: 'Assignar secció',
            department: 'Departament',
            select_department: 'Seleccioneu un departament...',
            assignee: 'Assignat a',
            select_assignee: 'Seleccioneu un assignat...',
            section: 'Secció',
            select_section: 'Seleccioneu una secció...',
            select_form_instance: 'Seleccioneu una instància de formulari...',
            no_assignments_found: "No s'han trobat assignacions de secció.",
            select_instance_prompt: 'Seleccioneu una instància de formulari per veure les assignacions de secció.',
            complete_section: 'Completar secció',
            status_pending: 'Pendent',
            status_in_progress: 'En curs',
            status_completed: 'Completat',
            status_reviewed: 'Revisat',
            form_instance_id: "ID d'instància del formulari",
            assignment_created: 'Assignació de secció creada',
            assignment_deleted: 'Assignació de secció eliminada',
            status_updated: 'Estat de la secció actualitzat',
            aria_delete_assignment: 'Eliminar assignació {name}',
            deadline: 'Data límit',
            deadline_help: 'Opcional. Les assignacions posteriors a aquesta data es mostren com a endarrerides.',
            overdue: 'Endarrerida',
            days_remaining: 'dies restants',
            approval_requested: 'Enviat per a revisió',
            form_approved: 'Formulari aprovat',
            form_rejected: 'Formulari rebutjat',
            no_departments_guidance: "No hi ha departaments configurats. Configureu-los a Configuració de l'organització.",
            no_sections_guidance: 'No hi ha seccions definides a la plantilla. Assegureu-vos que la plantilla tingui seccions.',
            select_instance_first: 'Seleccioneu primer una instància de formulari per carregar les seccions disponibles.',
        },
        submission_packages: {
            supporting_documents: 'Supporting Documents',
            supporting_documents_help: 'Files filed with this package. Each is stored against the package and listed on its record.',
            no_supporting_documents: 'No supporting documents attached yet.',
            attach_supporting_document: 'Attach a supporting document',
            supporting_document_attached: 'Supporting document attached',
            title: 'Paquets de presentació',
            assemble_package: 'Muntar paquet de presentació',
            dossier: 'Expedient',
            select_dossier: 'Seleccioneu un expedient...',
            package_label: 'Etiqueta del paquet',
            cover_letter: 'Carta de presentació',
            checklist: 'Llista de verificació',
            evidence_index: "Índex d'evidència",
            completeness: 'Completesa',
            incomplete_warning: 'No es pot muntar: el formulari té seccions incompletes.',
            no_packages_found: "No s'han trobat paquets de presentació.",
            version: 'Versió',
            package_assembled: 'Paquet de presentació reunit',
            package_deleted: 'Paquet de presentació eliminat',
            aria_delete_package: 'Eliminar paquet {name}',
            status_draft: 'Esborrany',
            status_complete: 'Complet',
            status_submitted: 'Enviat',
            status_accepted: 'Acceptat',
            status_rejected: 'Rebutjat',
            package_updated: 'Paquet de presentació actualitzat',
            document_generated: 'Document generat',
            documents: 'Documents',
            generate: 'Generar',
            preview: 'Previsualitzar',
            download: 'Descarregar',
            download_all: 'Descarregar tot',
            no_dossiers_guidance: "Encara no hi ha dossiers. Creeu-ne un des d'Avaluacions.",
            package_plan_help: 'Inclou al pla del paquet (generació automàtica de documents disponible):',
            cover_letter_help: "Carta de presentació oficial adreçada a l'autoritat receptora",
            checklist_help: 'Llista de verificació de compliment que verifica tots els requisits de presentació',
            evidence_index_help: 'Índex de documents probatoris referenciats en la presentació',
            completeness_skipped: 'Verificació de completitud omesa — no hi ha cap instància de formulari vinculada a aquest dossier.',
            completeness_unavailable: "Verificació de completitud no disponible — es procedeix amb l'elaboració del paquet.",
            duplicate_package_error: 'Ja existeix un paquet de presentació per a aquest dossier.',
            doc_cover_letter: 'Carta de presentació',
            doc_checklist: 'Llista de verificació',
            doc_evidence_index: "Índex d'evidències",
            export_csv: 'Exportar CSV',
            export_success: "Enllaços dels casos d'arxiu exportats amb èxit",
            no_assignments_guidance: "Encara no hi ha assignacions de seccions. Assigneu seccions a departaments i membres de l'equip per començar l'encaminament.",
            completeness_hint: 'Completeness is the share of the six dossier fields a package cannot be filed without that the dossier holds: dossier type, framework, authority, submission method, applicant organisation and applicant contact.',
            completeness_not_measured: 'Not measured: assembled before completeness was computed',
            missing_fields_label: 'Missing from the dossier',
            dossier_field_dossier_type: 'Dossier type',
            dossier_field_regulatory_framework_code: 'Framework',
            dossier_field_authority: 'Authority',
            dossier_field_submission_method: 'Submission method',
            dossier_field_applicant_org_name: 'Applicant organisation',
            dossier_field_applicant_contact: 'Applicant contact',
        },
        // Sprint 63.37.0 (D4) — the one official-standing badge (`OfficialFormBadge`).
        official_standing: {
            official: 'Official form',
            internal: 'Internal form',
        },
        template_picker: {
            title: 'Seleccionar una plantilla',
            hint: 'Trieu una plantilla de formulari normatiu o comenceu amb un formulari en blanc.',
            market_filter: 'Filtrar per mercat',
            framework_filter: 'Filtrar per marc',
            all_markets: 'Tots els mercats',
            all_frameworks: 'Tots els marcs',
            search_placeholder: 'Cercar plantilles...',
            blank_form: 'Formulari en blanc / genèric',
            blank_form_desc: 'Crear sense plantilla — omplir els camps manualment.',
            no_templates: "No s'han trobat plantilles per a aquest filtre.",
            templates_unreadable: 'Not every template for this form could be loaded, so the list below may be empty or incomplete. That does not mean there are none.',
            templates_read_subject: 'the form templates',
            filters_unreadable: 'The market and framework filters are not shown: their choices could not be loaded.',
            select: 'Seleccionar',
            link_error: "Ha fallat l'enllaç de la plantilla — podeu enllaçar una plantilla més tard des de la vista d'Edició.",
            internal_badge: 'Plantilla interna',
            legacy_badge: 'Obsoleta',
            draft_badge: 'Esborrany',
            upcoming_badge: 'Properament',
            hidden_badge: 'Amagada',
            show_hidden_toggle: 'Mostrar plantilles amagades / obsoletes',
            show_hidden_toggle_hint: "Vista d'administrador: inclou plantilles marcades com a internal_only, hidden, legacy o draft.",
        },
        cover_letter_modal: {
            title: 'Carta de presentació: vista prèvia i edició',
            iframe_title: 'Vista prèvia de la carta de presentació',
            stage_label: 'Etapa',
            stage_initial: 'Presentació inicial',
            stage_supplement: 'Resposta de suplement',
            stage_resubmission: 'Nova presentació',
            subject_label: 'Assumpte',
            body_label: 'Paràgraf principal',
            notes_label: 'Notes addicionals',
            refresh_preview: 'Actualitzar vista prèvia',
            confirm: 'Confirmar i desar',
            regenerate: 'Regenerar',
            snapshot_saved: 'Carta de presentació desada',
        },
        related_panel: {
            title: 'Formularis normatius relacionats',
            open_form: 'Emplenar formulari',
            no_forms: "No s'han trobat formularis relacionats.",
            hint_vn: "Formularis normatius específics del VN disponibles — seleccioneu un formulari per obrir l'assistent de compliment.",
            internal_badge: 'Intern',
            official_badge: 'Oficial',
            loading: 'Carregant formularis relacionats...',
            error: "No s'han pogut carregar els formularis relacionats.",
            form_id: 'ID del formulari',
            form_name: 'Nom del formulari',
            framework: 'Marc normatiu',
        },
        view_form: 'Veure formulari',
        edit_form: 'Editar formulari',
        save_draft: 'Desa l’esborrany',
        module_settings: 'Configuració del mòdul',
        validation_errors: 'Corregiu els errors de validació',
        custom_name_help: 'Opcional — per defecte, el nom de la plantilla si es deixa en blanc',
        select_placeholder: 'Selecciona...',
        // Gate E12.5 pass 3 (QA nit) — the stored-text fallback of a date-and-time field.
        datetime_stored_text_hint: 'Recorded before this field took a date and time; kept as written.',
        previous_section: 'Anterior',
        next_section: 'Següent',
        loading: 'Carregant...',
        array_add: 'Afegir',
        array_remove: 'Eliminar',
        array_item_label: '{label} element {index}',
        array_add_item: 'Afegir element a {label}',
        array_remove_item: 'Eliminar element {index} de {label}',
        legacy_banner: 'Aquest registre es va crear abans dels formularis unificats. Algunes dades poden diferir entre l’entitat i la instància del formulari.',
        field_required: '{label} és obligatori',
        section_announcement: 'Secció {current} de {total}: {title}',
        sections_nav_label: 'Seccions de formulari',
        no_sections: 'No hi ha cap secció de plantilla disponible.',
        section_label_tpl: 'Secció {index}: {title}',
        field_shown_announcement: '{field} ara es mostra.',
        field_required_announcement: '{field} ara és obligatori.',
        submit_blocked_hint_one: '1 camp obligatori encara necessita una resposta.',
        submit_blocked_hint_many: '{count} camps obligatoris encara necessiten una resposta.',
        module_settings_auto_applied: "La configuració específica del mòdul s'aplica automàticament.",
        operation_failed: "L'operació ha fallat",
        failed_to_load_form: "No s'ha pogut carregar el formulari",
        failed_to_save_form: "No s'ha pogut desar el formulari",
        failed_to_submit_form: "No s'ha pogut enviar el formulari",
        failed_to_load_template: "No s'ha pogut carregar la plantilla",
        failed_to_save_draft: "No s'ha pogut desar l'esborrany",
        failed_to_create: "No s'ha pogut crear",
        no_content_generated: 'No content generated',
    },
    pack_catalog: {
        title: 'Catàleg de paquets',
        install: 'Instal·lar paquet',
        uninstall: 'Desinstal·lar paquet',
        install_batch: 'Instal·lar conjunt',
        installed_tab: 'Instal·lats',
        catalog_tab: 'Catàleg',
        pack_details: 'Detalls del paquet',
        frameworks: 'Marcs reguladors',
        requirements: 'Requisits',
        artifacts: 'Artefactes',
        language_pack: "Paquet d'idioma",
        dependencies: 'Dependències',
        no_packs: "No s'han trobat paquets.",
    },
    platform_pages: {
        users: {
            title: 'Usuaris',
            new: 'Nou usuari',
            invite: 'Convidar usuari',
            edit: 'Editar usuari',
            name: 'Nom',
            email: 'Correu electrònic',
            role: 'Rol',
            status: 'Estat',
            identity_type: "Tipus d'identitat",
            initial_password: 'Initial password',
            require_password_change_action: 'Require password change',
            require_password_change_confirm: '{name} will reach only the change-password step, from their next request, until they choose a new password. Continue?',
            require_password_change_done: 'The user must now change their password.',
            require_password_change_failed: 'The password change could not be required.',
            password_change_pending: 'Password change required',
            initial_password_note: 'Share it through a secure channel. While a password change is required, the user can do nothing but choose a new password.',
            last_login: 'Últim inici de sessió',
            failed_login_attempts: 'Failed Login Attempts',
            never_signed_in: 'Never',
            deactivate: 'Desactivar',
            reactivate: 'Reactivar',
            unlock: 'Desbloquejar',
            reset_password: 'Restablir contrasenya',
            remove: 'Eliminar',
            remove_confirm: "Voleu eliminar \"{name}\"? Aquesta acció és permanent i l'usuari perdrà tot l'accés. El registre es conserva a efectes d'auditoria.",
            user_details_loaded: 'User details loaded',
            password_required: 'Password is required',
            password_min_length: 'Password must be at least 12 characters',
            password_uppercase: 'Password must contain at least one uppercase letter',
            password_lowercase: 'Password must contain at least one lowercase letter',
            password_digit: 'Password must contain at least one digit',
            password_special: 'Password must contain at least one special character',
            deactivate_failed: 'Failed to deactivate user',
            reactivate_failed: 'Failed to reactivate user',
            remove_failed: 'Failed to remove user',
            // fix-sprint-20261017-1500 F4 — UserDetailPage's error toasts read these, not English literals.
            update_failed: 'Failed to update user',
            unlock_failed: 'Failed to unlock user',
            assign_role_failed: 'Failed to assign role',
            revoke_role_failed: 'Failed to revoke role',
            create_failed: 'Failed to create user',
            count_one: '{total} user',
            count_other: '{total} users',
            announce_loaded: 'Users loaded',
        },
        roles: {
            title: 'Rols',
            new: 'Nou rol',
            edit: 'Editar rol',
            name: 'Nom del rol',
            description: 'Descripció',
            permissions: 'Permisos',
            users_count: 'Usuaris',
            detail: 'Detall del rol',
            all_tiers: 'Tots els nivells',
            tier: 'Nivell',
            type_system: 'Sistema',
            type_custom: 'Personalitzat',
            role_name_placeholder: 'p. ex. responsable regional de compliment',
            permissions_heading: 'Permissions',
            permissions_selected: '({count} selected)',
            select_all_offered: 'Select all',
            clear_all: 'Clear all',
            grant_bounds_hint: 'You can grant only permissions you hold yourself. Permissions that no system role holds by design, such as AML case access, can be granted only by someone who holds the role-assignment permission in their own right.',
            permission_not_held_summary: 'You do not hold these permissions, so you cannot grant them:',
            permission_not_held_item: 'You do not hold this permission',
            system_role_fixed: 'A system role’s permissions are fixed. Create a custom role to grant other permissions.',
            permission_required: 'At least one permission is required.',
            system_role_no_delete: 'System roles cannot be deleted.',
            create_failed: 'Failed to create role',
            delete_failed: 'Failed to delete role',
            update_failed: 'Failed to update role',
            delete_blocked_active_users: 'This role is still assigned to {count} user(s). Move them to another role, then delete this one.',
            delete_blocked_sso_config: 'Single sign-on still uses this role ({names}), as the default role for new users or in its group mapping. Change that setting first, then delete this role.',
            announce_loaded: 'Roles loaded',
            announce_detail_loaded: 'Role details loaded',
        },
        profile: {
            title: 'El meu perfil',
            name: 'Nom',
            display_name: 'Nom de visualització',
            email: 'Correu electrònic',
            phone: 'Telèfon',
            department: 'Departament',
            job_title: 'Càrrec',
            language: 'Idioma',
            role: 'Rol',
            persona: 'Persona detectada',
            last_login: 'Últim inici de sessió',
            save: 'Desar els canvis',
            save_success: 'Perfil actualitzat correctament',
            module_tips_section: 'Consells de mòdul',
            tips_toggle: 'Mostra consells normatius als mòduls',
            dismissed_tips: 'Consells descartats',
            reset_dismissed: 'Restableix tots els consells descartats',
            notification_prefs: 'Preferències de notificació',
            notification_prefs_link: 'Gestionar preferències de notificació →',
            change_password: 'Canviar contrasenya',
            current_password: 'Contrasenya actual',
            new_password: 'Nova contrasenya',
            confirm_password: 'Confirmar contrasenya',
            accessibility: 'Accessibilitat',
            high_contrast: 'Mode dalt contrast',
            high_contrast_desc: 'Augmentar el contrast per a una millor llegibilitat. Compleix WCAG 2.1 AAA.',
            passwords_no_match: 'Les contrasenyes no coincideixen',
            password_changed_logout: 'Contrasenya canviada. Es tancarà la sessió.',
            failed_update: "No s'ha pogut actualitzar el perfil",
            failed_change_password: "No s'ha pogut canviar la contrasenya",
            none: 'Cap',
            never: 'Mai',
            password_requirements: 'Mínim 12 caràcters, majúscula, minúscula, dígit, caràcter especial',
            announce_updated: 'Profile updated',
            announce_loaded: 'Profile loaded',
        },
        security: {
            mfa_title: 'Configuració MFA',
            mfa_enable: 'Habilitar MFA',
            mfa_disable: 'Deshabilitar MFA',
            mfa_setup: "Escanegeu aquest codi QR amb la vostra aplicació d'autenticació",
            sso_title: 'Configuració SSO',
            sso_provider: 'Proveïdor',
            sso_client_id: 'ID de client',
            sso_redirect: 'URI de redirecció',
            sso_idp_section: 'Identity Provider',
            sso_idp_entity_id: 'Identity provider entity ID',
            sso_idp_sso_url: 'Identity provider sign-in URL',
            sso_idp_binding: 'Request binding',
            sso_binding_redirect: 'HTTP Redirect',
            sso_binding_post: 'HTTP POST',
            sso_allow_idp_initiated: 'Accept sign-ins started at the identity provider',
            sso_jit_provisioning: 'Create a user on first sign-in',
            sso_default_role: 'Role for users created on first sign-in',
            sso_default_role_none: 'No default — refuse the sign-in',
            sso_assertions_signed: 'Signed assertions are always required.',
            sso_acs_hint: 'Give your identity provider this assertion consumer URL:',
            // ── Sprint 63.6.1 — OpenID Connect ────────────────────────────
            sso_oidc_issuer: 'Issuer',
            sso_oidc_issuer_hint: 'The issuer identifier your provider publishes, exactly as it appears in its discovery document. The platform reads that document from this address and refuses a configuration whose issuer does not match.',
            sso_oidc_redirect_uri: 'Redirect URI to register',
            sso_oidc_redirect_uri_hint: 'Register this exact address with your provider. The platform decides it from the configuration, so it cannot be edited here.',
            sso_oidc_redirect_uri_pending: 'Available once the configuration is saved',
            sso_oidc_client_auth: 'Client authentication',
            sso_oidc_client_auth_basic: 'Client secret, in the Authorization header',
            sso_oidc_client_auth_post: 'Client secret, in the request body',
            sso_oidc_scopes: 'Scopes',
            sso_oidc_scopes_hint: 'Separate with spaces. Leave empty for openid, profile and email. The openid scope is always requested: without it the provider returns no identity token.',
            sso_oidc_scopes_placeholder: 'openid profile email',
            sso_oidc_identity_claim_note: 'Identity comes from the email claim, and the address must be one your provider reports as verified. No other claim is read, and no claim grants a permission.',
            sso_certificates: 'Signing certificates',
            sso_certificates_empty: 'No signing certificate. A sign-in cannot be verified until one is added.',
            sso_certificate_pem: 'Certificate (PEM)',
            sso_certificate_add: 'Add certificate',
            sso_certificate_expires: 'Expires',
            sso_certificate_remove: 'Remove certificate',
            sso_certificate_limit: 'Two certificates are held at most, so a rotation needs no downtime.',
            sso_verify: 'Verify configuration',
            sso_verify_hint: 'Runs a full sign-in against this configuration and reports every check. It creates no session and no user, so you stay signed in.',
            sso_verify_report: 'Verification report',
            sso_verify_passed: 'Every check passed.',
            sso_verify_failed: 'Some checks failed.',
            sso_check_passed: 'Passed',
            sso_check_failed: 'Failed',
            sso_enforcement: 'SSO-only sign-in',
            sso_enforcement_off: 'Off — everyone can sign in with a password.',
            sso_enforcement_on: 'On — only exempt administrators can sign in with a password.',
            sso_enforcement_activate: 'Turn on SSO-only sign-in',
            sso_enforcement_deactivate: 'Turn off SSO-only sign-in',
            sso_enforcement_exempt: 'Administrators who keep password sign-in',
            sso_enforcement_before: 'Before you can turn this on:',
            sso_enforcement_need_verify: 'Verify this configuration and complete a sign-in through it, within the last 15 minutes.',
            sso_enforcement_need_exempt: 'Name at least one active administrator who has a password.',
            sso_enforcement_recovery: 'If the identity provider stops working, an exempt administrator can sign in with a password and turn this off; deactivating the configuration also restores password sign-in for everyone.',
            sso_mfa_heading: "Accept this provider's MFA",
            sso_mfa_intro: "By default the platform asks for its own MFA after every sign-in through this provider. Turn this on only if you trust this provider's MFA to stand in for the platform's.",
            sso_mfa_accept: "Accept this provider's MFA instead of the platform's",
            sso_mfa_state_off: "Off. The platform's own MFA applies to every sign-in through this provider.",
            sso_mfa_state_on: "On. A sign-in counts as MFA-verified only when the values the provider reports match the ones you list below. Every other sign-in is held at the platform's own MFA.",
            sso_mfa_no_meaning: "The platform attaches no meaning to any value. Only values you enter here, matched exactly and with the same capitalisation, count. A value you have not listed, a sign-in older than the maximum age and a missing value all fall back to the platform's own MFA.",
            sso_mfa_amr: 'Accepted authentication method values (amr)',
            sso_mfa_amr_hint: 'The exact amr values your provider reports for an MFA sign-in, separated by spaces or commas. OpenID Connect only.',
            sso_mfa_acr_oidc: 'Accepted authentication context values (acr)',
            sso_mfa_acr_saml: 'Accepted authentication context values (AuthnContextClassRef)',
            sso_mfa_acr_hint: 'The exact values your provider reports for an MFA sign-in, separated by spaces or commas.',
            sso_mfa_lists_rule: 'List at least one value, or nothing will count. When you list more than one kind of value, a sign-in must match every kind.',
            sso_mfa_empty_warning: 'Nothing is listed, so no sign-in through this provider will count as MFA-verified.',
            sso_mfa_max_age: 'Maximum authentication age (seconds)',
            sso_mfa_max_age_hint: 'A sign-in at the provider older than this does not count. Leave empty for no age check.',
            sso_mfa_max_age_invalid: 'Enter a whole number of seconds greater than zero, or leave the field empty.',
            sso_revoke_sessions: "End this provider's sessions",
            sso_revoke_confirm_title: "End this provider's sessions?",
            sso_revoke_confirm_message: 'Everyone currently signed in through {name} is signed out and must sign in again. Sessions that arrived by password or through another provider are not ended. Federated sessions created before sessions recorded which configuration they came from cannot be attributed to one provider, so they are ended as well.',
            sso_revoke_confirm_button: 'End sessions',
            sso_revoke_working: 'Ending sessions…',
            sso_revoke_result_heading: 'Sessions ended',
            sso_revoke_result_for: 'Provider: {name}',
            sso_revoke_count_total: 'Sessions ended',
            sso_revoke_count_legacy: 'Of these, sessions that could not be attributed to a configuration',
            sso_revoke_legacy_note: "These sessions pre-date the field that records which configuration a session came from, so they are ended whichever provider's sessions you end.",
            sso_revoke_announce: 'Sessions ended: {count}. Of these, {legacy} could not be attributed to a configuration because they pre-date the field that records it.',
            sso_revoke_failed: "Failed to end this provider's sessions.",
            sso_mfa_accepted_badge: 'Provider MFA accepted',
            api_keys_title: 'Claus API',
            api_keys_new: 'Nova clau API',
            api_keys_name: 'Nom de la clau',
            api_keys_last_used: 'Últim ús',
            api_keys_expires: 'Caduca',
            api_keys_revoke: 'Revocar',
            service_accounts_title: 'Comptes de servei',
            ip_allowlist_title: "Llista d'IP permeses",
            ip_add: 'Afegir adreça IP',
            ip_address: 'Adreça IP',
            ip_description: 'Descripció',
            delegations_title: 'Delegacions',
            delegations_new: 'Nova delegació',
            delegations_from: "De l'usuari",
            delegations_to: "A l'usuari",
            delegations_expires: 'Caduca',
            break_glass_title: "Accés d'emergència",
            break_glass_requester_only: 'You can request break-glass access here and follow your own requests below. Other people\u2019s requests, active sessions and history are shown to approvers.',
            break_glass_my_requests: 'Your requests',
            break_glass_request: 'Sol·licitar accés',
            break_glass_reason: "Motiu de l'accés",
            break_glass_duration: 'Durada',
            session_settings_title: 'Configuració de sessions',
            eviction_policy_section: "Política d'expulsió",
            session_timeout: 'Temps límit de sessió (minuts)',
            session_max_concurrent: 'Màxim de sessions simultànies',
            session_inactivity_warning: 'Inactivity Warning (minutes)',
            session_timeout_hint: 'Sessions will expire after this duration of total time. Valid range: 5-480 minutes.',
            session_max_concurrent_hint: 'Maximum number of active sessions per user. Valid range: 1-20.',
            session_inactivity_warning_hint: 'Show a warning after this many minutes of inactivity. Must be less than the session timeout.',
            session_timeout_range_error: 'Session timeout must be between 5 and 480 minutes.',
            session_max_concurrent_range_error: 'Max concurrent sessions must be between 1 and 20.',
            session_inactivity_warning_range_error: 'Inactivity warning must be between 1 minute and less than the session timeout.',
            session_settings_load_error: 'Failed to load session settings.',
            session_settings_save_error: 'Failed to save session settings.',
            eviction_policy_hint: 'What happens when a user exceeds the max concurrent sessions limit.',
            eviction_policy_reject: 'Reject New Login - Prevent new login until an existing session is terminated',
            eviction_policy_evict_oldest: 'Evict Oldest Session - Automatically terminate the oldest session to allow new login',
            active_sessions_title: 'Sessions actives',
            active_sessions_device: 'Dispositiu',
            active_sessions_ip: 'Adreça IP',
            active_sessions_started: 'Iniciada',
            active_sessions_revoke: 'Revocar sessió',
            active_sessions_confirm_terminate: 'Esteu segur que voleu finalitzar aquesta sessió?',
            active_sessions_confirm_terminate_current: 'Aquesta és la vostra sessió actual. Si cancel·leu la sessió, tanqueu la sessió immediatament. Continuar?',
            active_sessions_confirm_terminate_all: 'Esteu segur que voleu finalitzar totes les altres sessions? Només romandrà activa la vostra sessió actual.',
            active_sessions_terminated: 'La sessió ha finalitzat correctament.',
            active_sessions_all_terminated: "La resta de sessions s'han finalitzat.",
            active_sessions_terminate_all: 'Finalitzar totes les altres sessions',
            active_sessions_last_activity: 'Última Activitat',
            active_sessions_current: 'Sessió actual',
            active_sessions_revoke_selected: 'Revocar seleccionades',
            active_sessions_confirm_terminate_selected: 'Esteu segur que voleu acabar les sessions seleccionades?',
            active_sessions_selected_terminated: 'Sessions seleccionades acabades correctament.',
            ip_allowlist_desc: "Gestiona les adreces IP permeses per a l'accés de API.",
            ip_no_entries: "No s'ha configurat cap adreça IP.",
            session_settings_desc: "Configura el temps d'espera de la sessió i els límits de sessió simultània.",
            session_settings_save_policy: 'Desa la política de sessió',
            session_settings_saved: "La configuració de la sessió s'ha actualitzat correctament.",
            session_settings_idle_timeout: "Temps d'espera d'inactivitat (minuts)",
            session_settings_absolute_timeout: "Temps d'espera absolut (minuts)",
            session_settings_help_idle: "La sessió caducarà després d'aquest període d'inactivitat.",
            session_settings_help_absolute: "La sessió caducarà després d'aquesta durada total independentment de l'activitat.",
            session_settings_help_max: 'Nombre màxim de sessions simultànies per usuari.',
            sso_note: "Els canvis a la configuració de SSO poden requerir que els usuaris s'autentiquin de nou.",
            service_accounts_warning: 'Aquesta clau API només es mostrarà una vegada. Copieu-lo ara i deseu-lo de manera segura.',
            service_accounts_new: 'Nou compte de servei',
            service_accounts_name: 'Nom del compte',
            service_accounts_role: 'Rol',
            service_accounts_created: 'Creat',
            service_accounts_desc: 'Gestioneu els comptes de servei de màquina a màquina per a les integracions de API.',
            break_glass_warning: "L'accés al trencament de vidre s'ha auditat i registrat. Utilitzeu només per a emergències reals.",
            break_glass_min_chars: '{count} / {min} caràcters mínims',
            break_glass_permission_count: '{count} permisos seleccionats',
            break_glass_submitted: "S'ha enviat una sol·licitud de trencament de vidres. A l'espera d'aprovació.",
            break_glass_approved: "S'ha aprovat la sol·licitud de trencament de vidres.",
            break_glass_denied: "S'ha denegat la sol·licitud de trencament de vidres.",
            break_glass_confirm_activate: 'Approve this break-glass request? The approval is recorded with the permissions requested and the time it expires. It does not itself change what the requester can do.',
            break_glass_confirm_title: "Confirmeu l'activació",
            break_glass_pending: 'Sol·licituds pendents',
            break_glass_active_sessions: 'Approved Break-Glass Requests',
            break_glass_time_remaining: 'Temps restant',
            break_glass_expired: 'Caducat',
            break_glass_approved_denied_by: 'Aprovat/Denegat per',
            delegations_desc: 'Gestionar les delegacions de permisos temporals entre usuaris.',
            delegations_my: 'Les meves delegacions',
            delegations_to_me: 'Delegat a mi',
            delegations_scope_hint: 'An “all my permissions” delegation carries what you hold today. It does not change if your role changes later.',
            delegations_window_hint: 'The delegate holds these permissions from 00:00 on the start day until 23:59 on the end day, in your time zone, or until you revoke the delegation, checked on every request. Each use is recorded in the security audit trail.',
            delegations_start_in_past: 'The start cannot be before today.',
            delegations_end_before_start: 'The end must be after the start.',
            delegations_legacy_all: 'Grants nothing: made before delegations recorded what they carry. Revoke it and create a new one.',
            delegations_not_delegable_hint: 'Permissions held only through a named custom role or by no organisation, and single sign-on and group-user administration, cannot be delegated and are not listed.',
            delegations_reason: 'Reason',
            delegations_start: 'Start',
            delegations_end: 'End',
            delegations_scope_all: 'Tots els permisos',
            delegations_scope_specific: 'Permisos específics',
            delegations_created: 'Delegació creada amb èxit.',
            delegations_revoked: 'Delegació revocada.',
            delegations_confirm_revoke: 'Esteu segur que voleu revocar aquesta delegació?',
            delegations_confirm_revoke_title: 'Confirmeu la revocació',
            delegations_permission_count: '{count} permisos',
            delegations_no_longer_delegable: 'No longer delegable, so not granted: {permissions}',
            api_keys_status: 'Estat',
            api_keys_prefix: 'Prefix de clau',
            api_keys_permissions_count: 'Permisos',
            api_keys_ip_count: 'Restriccions IP',
            api_keys_created: 'Creat',
            api_keys_confirm_revoke_title: 'Confirmeu la revocació',
            api_keys_confirm_revoke_message: 'Esteu segur que voleu revocar la clau API "{name}"? Aquesta acció no es pot desfer.',
            api_keys_confirm_rotate_title: 'Confirmeu la rotació',
            api_keys_confirm_rotate_message: 'Rotate the API key "{name}"? A new key is issued now; the current key keeps working for 24 hours so you can replace it, then stops. If the key may be compromised, revoke it instead.',
            api_keys_created_success: "La clau API s'ha creat correctament.",
            api_keys_revoked_success: 'La clau API "{name}" s\'ha revocat.',
            api_keys_rotated_success: 'La tecla API "{name}" s\'ha girat. Copieu la nova clau a continuació.',
            api_keys_load_error: "No s'han pogut carregar les API claus.",
            api_keys_create_error: "No s'ha pogut crear la clau API.",
            api_keys_revoke_error: "No s'ha pogut revocar la clau API.",
            api_keys_rotate_error: "No s'ha pogut girar la tecla API.",
            api_keys_allowed_origins: 'Allowed origins',
            api_keys_allowed_origins_hint: 'One origin per line: scheme, host and optional port, e.g. https://app.example.com. When set, a request must carry an Origin header that matches one entry exactly, or it is refused. Leave empty for a server-to-server key.',
            api_keys_rate_limit: 'Requests per minute',
            api_keys_rate_limit_hint: 'Leave empty for no limit. Over the limit, a request is refused with 429 and a Retry-After header until the next minute starts.',
            api_keys_rate_limit_invalid: 'Requests per minute must be a whole number from 1 to 10,000.',
            api_keys_status_at_creation: 'Status at creation',
            api_keys_status_option_active: 'Active: the key works as soon as it is created',
            api_keys_status_option_inactive: 'Inactive: the key authenticates nothing until you activate it',
            api_keys_col_origins: 'Origins',
            api_keys_col_rate_limit: 'Limit per minute',
            api_keys_no_limit: 'No limit',
            api_keys_edit_title: 'Edit API key',
            api_keys_save: 'Save changes',
            api_keys_updated_success: 'API key "{name}" has been updated.',
            api_keys_update_error: 'Failed to update API key.',
            api_keys_edit_action: 'Edit key {name}',
            api_keys_activate_action: 'Activate key {name}',
            api_keys_pause_action: 'Pause key {name}',
            api_keys_rotate_action: 'Rotate key {name}',
            api_keys_revoke_action: 'Revoke key {name}',
            api_keys_copy_raw_key: 'Copy API key to clipboard',
            api_keys_activated_success: 'API key "{name}" is active.',
            api_keys_paused_success: 'API key "{name}" is paused. It authenticates nothing until it is activated.',
            api_keys_status_error: 'Failed to change the API key status.',
            api_keys_status_rotated: 'Rotated',
            api_keys_rotated_grace_until: 'Still works until {time}. Revoke it to stop it now.',
            service_accounts_load_error: 'Failed to load service accounts.',
            service_accounts_created_success: 'Service account created successfully.',
            service_accounts_create_error: 'Failed to create service account.',
            service_accounts_confirm_deactivate_title: 'Confirm Deactivation',
            service_accounts_confirm_deactivate_message: 'Are you sure you want to deactivate the service account "{name}"? All associated API keys will stop working.',
            service_accounts_deactivated_success: 'Service account "{name}" has been deactivated.',
            service_accounts_deactivate_error: 'Failed to deactivate service account.',
            service_accounts_deactivate_action: 'Deactivate service account {name}',
            service_accounts_table_label: 'Service accounts',
            service_accounts_name_placeholder: 'e.g., Data Sync Bot',
            service_accounts_purpose_placeholder: 'e.g., Automated data synchronization',
            permission_picker_selected_count: '{count} selected',
            permission_picker_result_count: '{count} of {total} permissions shown',
            permission_picker_selected_heading: 'Selected permissions',
            permission_picker_remove: 'Remove {permission}',
            break_glass_duration_15: '15 minuts',
            break_glass_duration_30: '30 minuts',
            break_glass_duration_60: '60 minuts (1h)',
            break_glass_duration_120: '120 minuts (2h)',
            break_glass_duration_240: '240 minuts (4h)',
            sod_rules_title: 'Normes de segregació de funcions',
            sod_violations_report: "Informe d'infraccions",
            sod_create_rule: 'Crea una regla',
            sod_col_name: 'Nom',
            sod_col_description: 'Descripció',
            sod_col_conflict_count: 'Recompte de conflictes',
            sod_col_severity: 'Severitat',
            sod_col_status: 'Estat',
            sod_col_actions: 'Accions',
            sod_no_rules: "No s'han trobat regles SoD",
            sod_severity_warning: 'Avís',
            sod_severity_blocking: 'Bloqueig',
            sod_active: 'Actius',
            sod_inactive: 'Inactiu',
            sod_edit: 'Edita',
            sod_exceptions: 'Excepcions',
            sod_deactivate: 'Desactivar',
            sod_confirm_deactivate_title: 'Confirmeu la desactivació',
            sod_confirm_deactivate_message: 'Vols desactivar la regla "{name}"? Ja no marcarà les infraccions.',
            sod_deactivate_error: "No s'ha pogut desactivar la regla",
            sod_rule_count: '{count} rules',
            active_sessions_loaded: 'Active sessions loaded',
            api_keys_loaded: 'API keys loaded',
            backup_codes_regenerated: 'Backup codes regenerated successfully.',
            break_glass_loaded: 'Break glass data loaded',
            delegations_loaded: 'Delegations loaded',
            ip_added: 'IP address added.',
            ip_allowlist_loaded: 'IP allowlist loaded',
            ip_removed: 'IP address removed.',
            mfa_disabled: 'MFA has been disabled.',
            mfa_status_loaded: 'MFA status loaded',
            service_accounts_loaded: 'Service accounts loaded',
            session_settings_loaded: 'Session settings loaded',
            // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
            mfa_org_enforcement_heading: 'Organisation enforcement',
            // fix-sprint-20261017-1500 F6 — a failed policy read is stated, with a retry.
            mfa_org_policy_read_failed: "The organisation's MFA policy could not be read, so its current level is not shown.",
            mfa_org_policy_retry_subject: "the organisation's MFA policy",
            mfa_org_enforcement_intro: 'Choose whether this organisation requires a second factor. The default is Optional, which is what the platform has always done: people who set up an authenticator are asked for a code, and people who do not are not. Raising the level never locks anyone out — a person who is required to have a second factor and does not yet have one is asked to set one up at their next sign-in, and can complete that themselves.',
            mfa_level_optional: 'Optional',
            mfa_level_optional_desc: 'Each person decides. Anyone who has set up an authenticator is asked for a code; anyone who has not signs in with their password alone.',
            mfa_level_admin_required: 'Required for administrators',
            mfa_level_admin_required_desc: "Anyone whose role can change this organisation's security settings must have a second factor. This is decided by what the role can do, not by what the role is called, so your own custom roles are covered too.",
            mfa_level_all_required: 'Required for everyone',
            mfa_level_all_required_desc: 'Everyone in this organisation must have a second factor.',
            mfa_enforcement_current: 'Current',
            mfa_enforcement_apply: 'Use this level',
            mfa_unenrolled_count: '{count} people would be asked to set up an authenticator at their next sign-in.',
            mfa_enforcement_confirm_title: 'Change the enforcement level?',
            mfa_enforcement_confirm_message: 'This sets the level to {level}. {count} people will be asked to set up an authenticator the next time they sign in. They can complete it themselves and nobody is locked out.',
            mfa_enforcement_saved: 'Enforcement level updated.',
            mfa_enforcement_save_failed: 'The enforcement level could not be changed.',
            mfa_enforcement_no_lockout: 'Raising the level cannot lock this organisation out. Someone who is required to have a second factor and does not yet have one still signs in; they are taken to a setup screen and can finish it without help.',
            mfa_admin_heading: 'Two-factor authentication',
            mfa_admin_enrolled: 'Set up',
            mfa_admin_not_enrolled: 'Not set up',
            mfa_admin_backup_codes_remaining: '{count} unused backup codes',
            mfa_admin_reason_label: 'Reason',
            mfa_admin_reason_hint: 'At least 20 characters. This is recorded in the audit trail and is the only lasting record of why.',
            mfa_admin_reset: 'Reset',
            mfa_admin_disable: 'Turn off',
            mfa_admin_revoke_codes: 'Revoke backup codes',
            mfa_admin_action_done: 'Done. This person has been notified.',
            mfa_admin_action_failed: 'That could not be completed.',
            // ── fix-sprint-20260921-2110: EN placeholders pending Phase 25 (R-57) ──
            mfa_page_intro: 'Set up and manage the second verification step for your own account. If your organisation requires one, the level it has chosen is shown below.',
            mfa_not_enrolled_intro: 'Protect your account with a second verification step.',
            mfa_scan_instruction: 'Scan the QR code with your authenticator app, then enter the 6-digit code it shows to confirm.',
            mfa_qr_alt: 'QR code holding the setup key for your authenticator app. If you cannot scan it, the same key is written out in the field below.',
            mfa_secret_manual_label: 'Or enter this setup key manually',
            mfa_confirm_code_label: 'Enter the 6-digit code from your authenticator app',
            mfa_enrolled_description: 'This account is protected by a second verification step.',
            mfa_enabled_since: 'In use since {date}.',
            mfa_status_load_failed: 'Your two-factor status could not be loaded.',
            mfa_enroll_failed: 'Setting up a second factor could not be started.',
            mfa_invalid_code: 'That code was not accepted. Check your authenticator app and enter the code it shows now.',
            mfa_regenerate_failed: 'New backup codes could not be generated.',
            mfa_disable_confirm_title: 'Turn off two-factor authentication?',
            mfa_disable_confirm_message: 'Your account will be protected by its password alone, which makes it easier for someone else to sign in as you. You can set up a second factor again at any time.',
            mfa_disable_failed: 'Two-factor authentication could not be turned off.',
            mfa_backup_codes_heading: 'Your backup codes',
            mfa_backup_codes_save_now: 'These codes are shown once and cannot be shown again. Save them somewhere safe now — each one signs you in if you lose your authenticator, and each works only once.',
            mfa_backup_codes_file_heading: 'AesirX ComplianceOne — MFA backup codes',
            mfa_backup_codes_file_notice: 'Store these codes in a safe place. Each code can only be used once.',
            mfa_backup_codes_file_generated: 'Generated: {date}',
            mfa_enforcement_apply_named: 'Use this level: {level}',
            mfa_admin_no_read_notice: 'You can see whether this person has a second factor and you can take it away. You cannot see what it is, and you cannot set one up on their behalf.',
            ip_enforcement_heading: 'Enforcement',
            ip_enforcement_dry_run: 'This list is not refusing anything. It is being checked against every signed-in request and the results are recorded, so you can see who would be refused before anyone is.',
            ip_enforcement_active: 'Enforcing since {date}.',
            ip_enforcement_would_block: '{count} recorded occasions in the last 30 days where a signed-in request came from an address this list does not cover.',
            ip_enforcement_would_block_zero: 'No signed-in request from an address outside this list has been recorded in the last 30 days. That can also mean nobody has signed in, so it is a reason to look rather than an all-clear.',
            ip_enforcement_confirm_button: 'Start enforcing',
            ip_enforcement_withdraw_button: 'Stop enforcing',
            ip_enforcement_confirm_title: 'Start refusing requests from outside this list?',
            ip_enforcement_confirm_message: 'From now on, a signed-in request from an address this list does not cover is refused.',
            ip_enforcement_confirmed: 'This list is now being enforced.',
            ip_enforcement_withdrawn: 'This list is no longer being enforced.',
            ip_enforcement_sessions_note: 'Turning this on does not sign anybody out. Each request is judged as it arrives, so a session from an address that is no longer covered simply stops working. To end sessions now, use Active Sessions.',
            ip_enforcement_edit_withdraws: 'Editing the ranges stops enforcement until you confirm again, because the recorded figures you confirmed against were about the old list.',
            ip_configure: 'Configure',
            ip_configure_title: 'Configure IP Allowlist',
            ip_configure_enabled_hint: 'When enabled, only requests from listed IP addresses will be allowed.',
            ip_enforcement_mode_label: 'Enforcement Mode',
            ip_enforcement_mode_all_access: 'All Access — restrict both UI and API',
            ip_enforcement_mode_api_keys_only: 'API Keys Only — only restrict API key access',
            ip_address_placeholder: '203.0.113.1 or 192.168.1.0/24',
            ip_description_placeholder: 'e.g., Office network',
            ip_entries_table_label: 'IP allowlist entries',
            ip_remove_action: 'Remove {cidr} from allowlist',
            ip_remove_confirm_title: 'Confirm Remove',
            ip_remove_confirm_message: 'Are you sure you want to remove {cidr} from the allowlist?',
            ip_load_failed: 'Failed to load IP allowlist settings.',
            ip_cidr_invalid: 'Invalid format. Enter a single IP (203.0.113.1) or CIDR range (192.168.1.0/24)',
            ip_cidr_duplicate: 'This CIDR is already in the allowlist.',
            ip_add_failed: 'Failed to add IP address.',
            ip_remove_failed: 'Failed to remove IP address.',
            ip_save_failed: 'Failed to save settings.',
            ip_enforcement_confirm_caller_covered: 'You are connecting from {ip}, and this list covers it, so you will not lose access to this screen.',
            ip_enforcement_confirm_caller_not_covered: 'You are connecting from {ip}, which this list does not cover. Confirming is refused rather than locking you out, so add this address to the list first.',
            ip_enforcement_confirm_caller_unknown: 'Whether this list covers the address you are connecting from could not be established on this screen. It is checked again when you confirm, and a confirmation that would lock you out is refused.',
            ip_enforcement_readiness_unreadable: 'The 30-day record of what this list would have refused could not be read, so this screen cannot show you the figure.',
            sso_metadata_url: 'Metadata / discovery URL',
            sso_metadata_url_hint: 'The OpenID Connect discovery or metadata document published by the identity provider. This is not the redirect URI: the redirect URI to register with the provider is shown above, is derived by the platform, and cannot be typed here.',
            sso_client_secret: 'Client Secret',
            sso_issuer_placeholder: 'https://login.example.com',
            sso_sp_entity_id_placeholder: 'https://grc.example.com/saml/metadata',
            sso_configs_table_label: 'SSO configurations',
            sso_configs_unreadable: 'The SSO configurations could not be read, so this screen cannot say whether this organisation has any.',
            sso_configs_retry: 'Reload',
            sso_configs_retry_busy: 'Reloading…',
            sso_configs_retry_subject: 'the SSO configurations',
            sso_load_failed: 'Failed to load SSO configurations.',
            sso_report_read_failed: 'Failed to read the verification report.',
            sso_save_failed: 'Failed to save SSO configuration.',
            sso_delete_failed: 'Failed to delete SSO configuration.',
            sso_certificate_add_failed: 'Failed to add the certificate.',
            sso_verify_start_failed: 'Failed to start the verification run.',
            sso_enforcement_on_failed: 'Failed to turn on SSO-only sign-in.',
            sso_enforcement_off_failed: 'Failed to turn off SSO-only sign-in.',
            sso_updated: 'SSO configuration updated successfully.',
            sso_created: 'SSO configuration created successfully.',
            sso_delete_confirm_title: 'Confirm Delete',
            sso_delete_confirm_message: 'Are you sure you want to delete the SSO configuration "{name}"?',
            sso_deleted: 'SSO configuration "{name}" deleted.',
            sso_delete_end_sessions: "Also end this provider's sessions",
            sso_delete_end_sessions_hint: 'Deleting a configuration stops new sign-ins through it but does not end the sessions it has already started. Leave this ticked to end them now. If you untick it, you can end them later under "Deleted configurations".',
            sso_delete_sessions_not_ended: 'The configuration was deleted, but its sessions could not be ended. End them under "Deleted configurations".',
            sso_deleted_heading: 'Deleted configurations',
            sso_deleted_intro: 'These configurations no longer accept sign-ins. Deleting a configuration does not end the sessions it started; end them here with "End this provider\'s sessions".',
            sso_deleted_table_label: 'Deleted SSO configurations',
            sso_deleted_unreadable: 'The deleted SSO configurations could not be read.',
            sso_deleted_retry_subject: 'the deleted SSO configurations',
            sso_configs_loaded: 'SSO configurations loaded',
            active_sessions_select_aria: 'Select session from {ip}',
            active_sessions_terminate_aria: 'Terminate session from {ip}',
            active_sessions_terminate_current_aria: 'Terminate session from {ip} (current session)',
            active_sessions_unknown_ip: 'unknown IP',
            active_sessions_select_all_aria: 'Select all sessions',
        },
        access_reviews: {
            title: "Revisions d'accés",
            campaigns: 'Campanyes de revisió',
            new_campaign: 'Nova campanya',
            campaign_name: 'Nom de la campanya',
            scope: 'Abast',
            reviewers: 'Revisors',
            due_date: 'Data límit',
            progress: 'Progrés',
            my_reviews: 'Les meves revisions',
            approve: 'Aprovar',
            revoke: 'Revocar',
            analytics: 'Analítica de rols',
            analytics_title: 'Analítica de rols',
            permission_usage: 'Ús de permisos',
            role_coverage: 'Cobertura de rols',
            campaign_detail: 'Detalls de la campanya',
            campaign_description: 'Descripció',
            campaign_status: 'Estat',
            campaign_deadline: 'Data límit',
            campaign_scope_type: "Tipus d'abast",
            campaign_completion: 'Finalització',
            campaign_total_items: "Total d'articles",
            campaign_pending_items: 'Pendent',
            campaign_confirmed_items: 'Confirmat',
            campaign_revoked_items: 'Revocat',
            campaign_escalated_items: 'Escalada',
            campaign_launch: 'Llançament de la campanya',
            campaign_cancel: 'Cancel·la la campanya',
            campaign_confirm_launch: 'Esteu segur que voleu llançar aquesta campanya?',
            campaign_confirm_cancel: 'Esteu segur que voleu cancel·lar aquesta campanya?',
            campaign_launched: "La campanya s'ha llançat amb èxit.",
            campaign_cancelled: 'Campanya cancel·lada.',
            review_items: "Revisió d'elements",
            review_item_user: 'Usuari',
            review_item_role: 'Rol',
            review_item_reviewer: 'Revisor',
            review_item_decision: 'Decisió',
            review_item_justify: 'Justificació',
            review_item_confirm: 'Confirmeu',
            review_item_revoke: 'Revocar',
            review_item_escalate: 'Escalar',
            all_decisions: 'Totes les decisions',
            bulk_action: 'Acció massiva',
            bulk_confirm: 'Confirmació massiva',
            bulk_revoke: 'Revocació massiva',
            selected_count: '{count} seleccionat',
            justify_required: 'Cal justificació',
            scope_all_users: 'Tots els Usuaris',
            scope_role_owner: 'Propietari del rol',
            scope_department: 'Departament',
            back_to_campaigns: 'Tornar a Campanyes',
            recommendations: 'Recomanacions',
            refreshing: 'Refrescant...',
            decide: 'Decideix',
            decision_by: 'Decisió de',
            loading_items: "S'estan carregant elements...",
            loading_review_items: "S'estan carregant els elements de ressenya...",
            campaign_not_found: "No s'ha trobat la campanya",
            loading_campaign: "S'estan carregant els detalls de la campanya...",
            campaign_completed: "La campanya s'ha completat correctament.",
            confirm_cancel_title: 'Confirmeu la cancel·lació',
            justify_bulk_confirm: 'Justificació de la confirmació massiva:',
            justify_bulk_revoke: 'Justificació de la revocació massiva:',
            items_selected: '{count} elements seleccionats',
            justify_bulk_required: 'Cal justificar les accions massives',
            create_campaign_title: "Creeu una campanya de revisió d'accés",
            name_required: 'El nom és obligatori',
            scope_type_required: "El tipus d'abast és obligatori",
            reviewer_strategy_required: 'Es requereix una estratègia de revisor',
            deadline_required: 'Cal una data límit',
            reviewer_id_required: "L'identificador de revisor és necessari per a una estratègia d'usuari específica",
            invalid_json: 'JSON no vàlid',
            scope_filter_label: "Filtre d'abast (JSON)",
            reviewer_strategy_label: 'Estratègia del revisor',
            reviewer_id_label: 'Identificador del revisor',
            deadline_label: 'Data límit',
            create_campaign_btn: 'Crea campanya',
            scope_type_label: "Tipus d'abast",
            scope_by_role: 'Per Rol',
            scope_by_department: 'Per Departament',
            scope_custom: 'Personalitzat',
            reviewer_manager: 'Gerent',
            reviewer_specific_user: 'Usuari específic',
            enter_json_filter: 'Introduïu el filtre JSON.',
            hint_label: 'Pista:',
            role_usage: 'Ús de rols',
            over_privileged_users: 'Usuaris amb excés de privilegis',
            role_name_col: 'Nom del rol',
            assigned_users: 'Usuaris assignats',
            active_users_30d: 'Usuaris actius (30 d)',
            total_perms: 'Total Perms',
            used_perms: 'Perms usats',
            unused_perms: 'Perms no utilitzats',
            no_data_available: 'No hi ha dades disponibles',
            roles_col: 'Rols',
            total_col: 'Total',
            used_col: 'Usat',
            unused_pct: '% no utilitzat',
            current_count: 'Recompte actual',
            recommended_count: 'Recompte recomanat',
            rationale: 'Justificació',
            loading_analytics: "S'estan carregant les analítiques...",
            loading_role_analytics: "S'estan carregant les dades d'anàlisi de rols...",
            computed_at: 'Calculat a:',
            form_errors: 'El formulari té {count} error(s). Si us plau, corregiu-los.',
            scope_filter_invalid_json: "El filtre d'abast conté JSON no vàlid",
            scope_hint_all_users: "No cal cap filtre: s'inclouran tots els usuaris.",
            review_type_label: 'Tipus de revisió',
            review_type_required: 'El tipus de revisió és obligatori',
            reviewer_ids_label: 'IDs dels revisors',
            reviewer_ids_hint: "Introduïu IDs d'usuari separats per comes",
            reviewer_ids_required: 'Cal almenys un ID de revisor',
            start_date_label: "Data d'inici",
            start_date_required: "La data d'inici és obligatòria",
            end_date_label: 'Data de fi',
            end_date_required: 'La data de fi és obligatòria',
            review_type_user_access: "Accés d'usuari",
            review_type_role_permissions: 'Permisos de rol',
            review_type_service_accounts: 'Comptes de servei',
            review_type_privileged_access: 'Accés privilegiat',
            review_type_application_access: 'Accés a aplicació',
            auto_revoke_label: 'Revocació automàtica en denegar',
            campaigns_title: 'Accediu a les campanyes de revisió',
            create_campaign_label: '+ Crea campanya',
            no_campaigns_found: "No s'han trobat campanyes",
            loading_campaigns: "S'estan carregant campanyes...",
            no_reviews_assigned: "No t'han assignat ressenyes",
            loading_reviews: 'Carregant comentaris...',
            review_decision_title: 'Revisió de la decisió',
            justification_label: 'Justificació',
            announce_loaded_review_items: "S'han carregat {count} elements de revisió",
            announce_loaded_campaigns: "S'han carregat {count} campanyes",
            announce_decision_submitted: 'La decisió "{decision}" s\'ha enviat a l\'element de revisió',
            announce_decision_submitted_for: 'S\'ha enviat la decisió "{decision}" per a {name}',
            announce_bulk_decision: '{decision} massiu aplicat a {count} elements',
        },
        attestation: {
            training_ids_label: 'Training Covered',
            training_ids_help: 'The training courses this attestation covers. The link is carried into each campaign the schedule generates.',
            training_ids_none: 'No training linked',
            training_ids_count: '{count} course(s)',
            training_ids_load_error: 'Training courses could not be read. The list may be incomplete.',
            title: "Programació d'atestacions",
            new: 'Nova programació',
            edit: 'Editar programació',
            create_title: "Crear programació d'atestació",
            edit_title: "Editar programació d'atestació",
            schedule_history: 'Historial de programació',
            next_run_date: 'Data de la propera execució',
            schedule_name: 'Nom de la programació',
            frequency: 'Freqüència',
            next_run: 'Pròxima execució',
            last_run: 'Última execució',
            history: "Historial d'atestacions",
            description: 'Descripció',
            scope: 'Àmbit',
            status: 'Estat',
            created_at: 'Creat',
            updated_at: 'Actualitzat',
            trigger_now: 'Activa ara',
            back_to_schedules: 'Tornar a Horaris',
            confirm_trigger: 'Trigger this schedule now?',
            confirm_delete: 'Delete this schedule?',
            triggered: "La programació s'ha activat correctament.",
            deleted: "S'ha suprimit l'agenda.",
            saved: "La programació s'ha desat correctament.",
            campaigns_generated: 'Campanyes generades',
            run_date: "Data d'execució",
            run_status: "Estat d'execució",
            create_schedule: 'Crear horari',
            view_history: "Visualitza l'historial",
            deactivate: 'Desactivar',
            overdue: 'OVERDUE',
            overdue_sr: "L'horari està vençut",
            confirm_trigger_title: "Confirmeu l'acció",
            confirm_trigger_message: 'Are you sure you want to trigger this schedule now?',
            confirm_deactivate_title: 'Confirmeu la desactivació',
            confirm_deactivate_message: 'Esteu segur que voleu desactivar aquesta programació?',
            triggered_success: "La programació s'ha activat correctament.",
            triggered_unmapped_reviewer: 'The cycle was started, but the reviewer rule "{strategy}" has no source in the platform, so the campaign names no reviewer: whoever launches it reviews its items.',
            trigger_scope_unresolvable: 'This schedule’s scope cannot be turned into a review campaign. Edit the schedule so its scope filter names at least one role, department or user, or choose a supported scope.',
            trigger_reviewer_not_found: 'The reviewer named on this schedule is no longer an active user of this organisation. Edit the schedule and choose another reviewer.',
            reviewer_not_found: 'The reviewer ID does not name an active user of this organisation',
            scope_unsupported: 'This scope type is not supported: a review campaign scopes by user, role and department only',
            deactivated_success: 'Horari desactivat.',
            name_label: 'Nom *',
            frequency_label: 'Freqüència *',
            scope_type_label: "Tipus d'abast *",
            scope_filter_label: "Filtre d'abast (JSON)",
            reviewer_strategy_label: 'Estratègia del revisor *',
            reviewer_id_label: 'Identificador del revisor *',
            frequency_quarterly: 'Trimestral',
            frequency_semi_annual: 'Semestral',
            frequency_annual: 'Anual',
            scope_all_users: 'Tots els Usuaris',
            scope_by_role: 'Per Rol',
            scope_by_department: 'Per Departament',
            scope_custom: 'Personalitzat',
            reviewer_manager: 'Gerent',
            reviewer_role_owner: 'Propietari del rol',
            reviewer_specific_user: 'Usuari específic',
            computed_next_run_date: 'Data de propera execució calculada',
            current_scheduled_date: 'Data programada actual',
            recomputed_frequency_changed: '(recalculat -- la freqüència ha canviat)',
            estimated_days_hint: "Estimació basada en {days} dies a partir d'avui",
            validation_name_required: 'El nom és obligatori',
            validation_frequency_required: 'La freqüència és necessària',
            validation_scope_type_required: "El tipus d'abast és obligatori",
            validation_reviewer_strategy_required: 'Es requereix una estratègia de revisor',
            validation_reviewer_id_required: "L'identificador de revisor és necessari per a una estratègia d'usuari específica",
            validation_invalid_json: 'JSON no vàlid',
            validation_scope_filter_invalid_json: "El filtre d'abast conté JSON no vàlid",
            form_errors: 'El formulari té {count} error(s). Si us plau, corregiu-los.',
            create_schedule_btn: 'Crear horari',
            schedule_history_title: 'Historial de programacions',
            campaign_name_col: 'Nom de la campanya',
            items_col: 'Elements',
            no_campaigns_yet: "Encara no s'ha generat cap campanya",
            loading_history: "S'està carregant l'historial...",
            loading_schedule: "S'està carregant la programació...",
            total_campaigns: 'Total de campanyes: {count}',
            results_per_page: 'Resultats per pàgina',
            page_size_option: '{size} / pàgina',
        },
        sod: {
            title: 'Regles de separació de funcions',
            new: 'Nova regla',
            edit: 'Editar regla',
            create_title: 'Crear regla SoD',
            edit_title: 'Editar regla SoD',
            rule_name: 'Nom de la regla',
            conflicting_permissions: 'Permisos en conflicte',
            permission_a: 'Permís A',
            permission_b: 'Permís B',
            violations: 'Violacions SoD',
            violation_user: 'Usuari',
            violation_rule: 'Regla violada',
            violation_date: 'Detectat el',
            exceptions: 'Excepcions SoD',
            exception_reason: "Motiu de l'excepció",
            exception_approver: 'Aprovat per',
            exception_status: "Estat d'excepció",
            has_exception: 'Té una excepció',
            no_exception: 'Cap excepció',
            grant_exception: "Subvenció d'excepció",
            granting: 'Atorgant...',
            grant_new_exception: 'Concediu una nova excepció',
            exceptions_for: 'Excepcions per a: {name}',
            back_to_rules: 'Tornar a Regles',
            no_violations: "No s'han trobat infraccions",
            no_exceptions_found: "No s'han trobat excepcions per a aquesta regla",
            all_exception_statuses: "Tots els estats d'excepció",
            user_id_label: "ID d'usuari",
            justification_label: 'Justificació',
            compensating_controls_label: 'Controls compensadors',
            expires_at_label: 'Caduca a les',
            granting_exception_for: "S'està concedint una excepció per a la regla: {name}",
            severity: 'Severitat',
            severity_warning: 'Avís',
            severity_blocking: 'Bloqueig',
            violation_count: '{count} infracció(s)',
            exception_count: '{count} excepció(es)',
            rule_description: 'Descripció',
            confirm_delete: 'Esteu segur que voleu suprimir aquesta regla?',
            deleted: "S'ha suprimit la regla.",
            framework_code: 'Marc normatiu',
            name_label: 'Nom *',
            severity_label: 'Gravetat *',
            conflict_pairs_legend: 'Parells de permisos de conflicte',
            conflict_pairs_hint: 'Cada parell defineix dos permisos que no hauria de tenir el mateix usuari.',
            placeholder_permission_a: '-- Permís A --',
            placeholder_permission_b: '-- Permís B --',
            permission_a_for_pair: 'Permís A per a la parella {index}',
            permission_b_for_pair: 'Permís B per a la parella {index}',
            conflict_pair_label: 'Parell de conflicte {index}',
            remove_conflict_pair: 'Elimina el parell de conflictes {index}',
            add_pair: '+ Afegeix parell',
            placeholder_name: "p. ex. Separació d'aprovació de pagament",
            create_rule: 'Crea una regla',
            loading_rule: "S'està carregant la regla SoD...",
            failed_load_rule: "No s'ha pogut carregar la regla.",
            failed_create_rule: "No s'ha pogut crear la regla SoD.",
            failed_update_rule: "No s'ha pogut actualitzar la regla SoD.",
            validation_name_required: 'El nom és obligatori.',
            validation_pair_required: 'Es requereix almenys un parell de permisos de conflicte.',
            validation_pair_both_required: "Parella {index}: els dos permisos s'han de seleccionar.",
            validation_pair_must_differ: 'Parella {index}: el permís A i el permís B han de ser diferents.',
            added_conflict_pair: "S'ha afegit el parell de conflictes {index}",
            removed_conflict_pair: "S'ha eliminat el parell de conflictes {index}",
            announce_loaded_violations: "S'han carregat {count} infraccions",
            announce_loaded_exceptions: "S'han carregat {count} excepcions",
            announce_loaded_rules: "S'han carregat {count} regles SoD",
            announce_loading_exceptions: "S'estan carregant les excepcions...",
            regulatory_info: {
                sod_context: 'Les regles SoD imposen la separacio regulatoria de rols exigida per la PDPL. Les violacions vinculades a un framework_code es poden incloure als paquets de proves (UC-VN-09).',
                violation_export_context: 'Les exportacions de violacions SoD inclouen framework_code, rols en conflicte, gravetat, creador i data de deteccio per als paquets de proves (P-VN-10).',
                exception_context: 'Les excepcions SoD produeixen evidencia: aprovador, justificacio, controls compensatoris i data d\\u0027expiracio. Les excepcions expirades romanen visibles (UC-VN-09 Pas 5).',
            },
        },
        regulatory_info: {
            attestation_context: 'Attestation schedules repeat an access review on a cycle (quarterly, semi-annual or annual). Each cycle creates a review campaign for all users, for named roles or departments, or for named users, and a named reviewer answers it. A reviewer rule the campaign model cannot express (manager or role owner) is shown when the cycle is started, and whoever launches the campaign then reviews its items. Schedule history lists the campaigns each schedule has generated.',
            api_keys_context: 'API key telemetry constitutes account evidence per VN_CYBERSEC_LAW_2025 Section B.5. Each key record includes last_used_at, ip_allowlist, permissions, status, and expires_at.',
            session_context: 'Session management enforces access controls per Telecom Law confidentiality requirements (Section B.4). Session events produce audit trail entries.',
            break_glass_context: 'Break-glass procedures produce regulatory justification evidence: requestor identity, justification, permissions, duration, approver identity, and approval/denial timestamps.',
            license_context: 'License settings gate modules by tier using PACKAGE_DEFINITIONS. Framework-specific modules require appropriate license tiers. Tier changes produce audit trail entries.',
            module_config_context: 'Module configuration supports framework-driven enablement. Disabling a module required by an active regulatory framework may break evidence completeness.',
            notification_context: 'Compliance-critical notifications (breach deadlines, attestation due dates) are marked with a compliance_critical flag and cannot be fully suppressed.',
            delegation_context: 'Delegation governance produces audit-grade evidence: delegator_id, delegate_id, permission scope, start/end dates, and status. Expired delegations remain visible.',
            access_control_context: 'Access control configuration meets Cybersecurity Law requirements (VN_CYBERSEC_LAW_2025): IP allowlist, MFA, service accounts, and SSO changes are audit-trailed.',
        },
    },
    cmp: {
        integration: {
            title: 'Integració CMP',
            status: 'Estat de la connexió',
            endpoint: "Punt d'accés CMP",
            last_sync: 'Última sincronització',
            configure: 'Configurar',
        },
        purpose_mapping: {
            title: 'Assignació de finalitats',
            cmp_purpose: 'Finalitat CMP',
            grc_purpose: 'Finalitat GRC',
            mapping_status: "Estat de l'assignació",
            auto_map: 'Assignació automàtica',
            new_mapping: 'Nova assignació',
        },
        webhook: {
            title: 'Configuració de webhooks',
            webhook_url: 'URL del webhook',
            secret: 'Secret del webhook',
            events: "Tipus d'esdeveniments",
            test_webhook: 'Provar webhook',
        },
        bulk_sync: {
            title: 'Sincronització massiva',
            sync_now: 'Sincronitzar ara',
            last_run: 'Última execució',
            records_synced: 'Registres sincronitzats',
            sync_history: 'Historial de sincronització',
            syncing: 'Sincronitzant...',
        },
        sync_settings: {
            sync_failures: 'Errors de sincronització',
            sync_secret_warning: 'Secret de sincronització (deseu-lo ara — no es mostrarà de nou):',
            push_description: "Configureu GRC Suite per enviar actualitzacions d'estat de casos a AesirX CMP.",
            technical_description: "Aquesta configuració controla com GRC Suite envia les actualitzacions d'estat del cas a AesirX CMP.",
            direction_label: 'Direcció',
            direction_value: 'GRC Suite → AesirX CMP (sortint)',
            auth_label: 'Autenticació',
            auth_value: 'Signatura HMAC-SHA256 a la capçalera X-GRC-Signature',
            retry_label: 'Reintentar Política',
            retry_value: '3 reintents amb retrocés exponencial (30s, 2min, 10min)',
            inbound_label: "Config. d'entrada",
            inbound_value: "El webhook d'entrada (CMP → GRC) està configurat a",
            cmp_webhook_settings: 'CMP Webhook Configuració',
            page_title: 'Configuració de Sincronització CMP',
            outbound_sync_configured: 'Sincronització de sortida configurada.',
            configure_sync_failed: 'Error en configurar la sincronització',
            configuration_updated: 'Configuració actualitzada.',
            update_configuration_failed: 'Error en actualitzar la configuració',
            connection_test_failed: 'Prova de connexió fallida',
            test_result_message: 'Prova enviada a {endpoint}. Estat: {status}',
            endpoint_placeholder: 'https://your-cmp-domain/api/grc-sync',
            events: {
                new_to_under_review: 'Nou → Under Review (assigned)',
                under_review_to_identity_verification: 'Under Revisar → Identitat Verificació',
                under_review_to_in_progress: 'Under Revisar → En curs',
                in_progress_to_completed: 'En curs → Completat',
                in_progress_to_denied: 'En curs → Denegat',
                any_to_closed: 'Qualsevol → Tancat',
            },
        },
    },
    settings_pages: {
        webhooks: {
            title: 'Webhooks',
            new: 'Nou webhook',
            url: 'URL',
            url_placeholder: 'https://example.com/webhook',
            events: 'Esdeveniments',
            secret: 'Secret',
            active: 'Actiu',
            test: 'Provar',
            create_failed: 'Error en create webhook. Si us plau, torneu-ho a intentar.',
            create_success: 'Webhook creat amb èxit',
            deactivate_failed: 'Error en deactivate webhook. Si us plau, torneu-ho a intentar.',
            deactivate_success: 'Webhook desactivat.',
            fetch_failed: 'Error en fetch webhooks. Si us plau, torneu-ho a intentar.',
            send_test_failed: 'Error en send test. Si us plau, torneu-ho a intentar.',
            test_failed: 'Webhook test ha fallat.',
            test_success: 'Prova del webhook amb èxit.',
            edit_title: 'Edita webhook',
            announce_loaded: "S'han carregat els webhooks",
            update_success: 'Webhook actualitzat correctament.',
            update_failed: 'Error en actualitzar el webhook.',
            activate_success: 'Webhook activat.',
            activate_failed: 'Error en activar el webhook.',
        },
        domains: {
            title: 'Dominis',
            new: 'Nou domini',
            domain_name: 'Nom del domini',
            verified: 'Verificat',
            verify: 'Verificar',
            add_failed: 'Error en add domain. Si us plau, torneu-ho a intentar.',
            add_success: 'Domini added amb èxit',
            domain_placeholder: 'p. ex. grc.example.com',
            fetch_failed: 'Error en fetch domains. Si us plau, torneu-ho a intentar.',
            remove_failed: 'Error en remove domain. Si us plau, torneu-ho a intentar.',
            remove_success: 'Domini removed amb èxit',
            txt_record: 'Registre TXT',
            verify_dns_failed: 'La verificació DNS ha fallat.',
            verify_failed: 'Error en verify domain. Si us plau, torneu-ho a intentar.',
            verify_success: 'Domini verificat amb èxit',
            announce_loaded: 'Domains loaded',
        },
        export_schedules: {
            title: "Programació d'exportacions",
            new: 'Nova programació',
            schedule_name: 'Nom',
            frequency: 'Freqüència',
            format: 'Format',
            last_run: 'Última execució',
            next_run: 'Pròxima execució',
            create_failed: 'Error en create schedule. Si us plau, torneu-ho a intentar.',
            create_success: 'Programar creat amb èxit',
            day_of_month: 'Dia del mes',
            day_of_week: 'Dia de la setmana',
            deactivate_failed: 'Error en deactivate schedule. Si us plau, torneu-ho a intentar.',
            deactivate_success: 'Programació desactivada.',
            fetch_failed: 'Error en fetch schedules. Si us plau, torneu-ho a intentar.',
            freq_daily: 'Diari',
            freq_monthly: 'Mensual',
            freq_weekly: 'Setmanal',
            recipients: 'Destinataris',
            time_utc: 'Hora (UTC)',
            trigger_failed: 'Error en trigger export. Si us plau, torneu-ho a intentar.',
            trigger_success: 'Exportar triggered amb èxit.',
            type_audit_trail: "Pista d'auditoria",
            type_compliance_report: 'Compliment Informe',
            type_custom: 'Personalitzat',
            type_incident_report: 'Incident Informe',
            type_ropa: 'ROPA',
            announce_loaded: 'Export schedules loaded',
            execution_note: "Each run produces the file as the schedule's run actor, shown under Runs as: the person who created the schedule, or who last changed what it produces, with that person's current permissions. Each run is recorded below. Recipients receive an e-mail with a link to this page; the file downloads under each reader's own permissions.",
            paused_reason_label: 'Paused by the platform',
            type_withdrawn: 'Withdrawn type',
            runs: 'Runs',
            runs_for: 'Runs for {name}',
            runs_empty: 'This schedule has not run yet.',
            runs_fetch_failed: 'Failed to load the runs.',
            close_runs: 'Close runs',
            run_status: 'Status',
            run_scheduled_for: 'Run for',
            run_trigger: 'Started by',
            run_attempts: 'Attempts',
            run_reason: 'Reason',
            run_file: 'File',
            run_status_running: 'Running',
            run_status_completed: 'Completed',
            run_status_failed: 'Failed',
            run_trigger_scheduled: 'Schedule',
            run_trigger_manual: 'Manual trigger',
            run_will_retry: 'It will be tried again.',
            download: 'Download',
            triggering: 'Exporting…',
            disabling: 'Disabling…',
            trigger_completed: 'Export completed. The file is in the run list.',
            trigger_run_failed: 'The export ran and failed: {reason}',
            failure_actor_inactive: 'The person this schedule runs as is no longer an active member, so the export cannot run as them.',
            failure_actor_lacks_permission: 'The person this schedule runs as no longer holds the permission this export needs.',
            failure_export_type_withdrawn: 'This export type was withdrawn, so the schedule cannot produce it. To export again, a new schedule with another export type is needed.',
            failure_export_too_large: 'The export selects more rows than one file can hold.',
            failure_generation_failed: 'The file could not be generated.',
            failure_interrupted: 'The run was interrupted before it finished.',
            failure_never_executed: 'Requested before scheduled exports were executed, so this run never ran and produced no file.',
            report: {
                incident_title: 'Incident report',
                compliance_title: 'Compliance report — framework coverage',
                window: 'Period: {start} to {end}',
                count: 'Rows: {count}',
                overall: 'Overall mapped coverage: {pct}',
                overall_none: 'Overall mapped coverage: not measurable (no installed framework declares requirements)',
                col_reference: 'Reference',
                col_title: 'Title',
                col_status: 'Status',
                col_severity: 'Severity',
                col_created: 'Created',
                col_closed: 'Closed',
                col_framework: 'Framework',
                col_requirements: 'Requirements',
                col_mapped: 'Mapped',
                col_coverage: 'Coverage',
                col_open_gaps: 'Open gaps',
            },
            email: {
                subject: 'Scheduled export ready: {name}',
                body: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file: {link}',
                body_html: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file:',
            },
            type_none_held: 'You hold none of the permissions an export type needs, so you cannot create a schedule: a schedule runs as the person who creates it.',
            type_offered_hint: 'Only the export types you may export yourself are offered: the schedule runs as you.',
            grant_ropa: 'the permission to export the record of processing activities as PDF',
            grant_audit_trail: 'the permission to export audit events',
            grant_compliance_report: 'the permission to read regulatory frameworks',
            grant_incident_report: 'the permission to run incident exports',
            refusal_permission_not_held: 'You do not hold {permission}, which this export needs, so you cannot make this schedule produce it.',
            refusal_permission_not_held_generic: 'You do not hold the permission this export needs.',
            refusal_schedule_inactive: 'This schedule is switched off, so it cannot be run. Enable it first.',
            refusal_export_type_withdrawn: "This schedule's export type was withdrawn, so it cannot be run or switched on. To keep exporting, create a schedule with another export type using New Schedule.",
            col_runs_as: 'Runs as',
            run_executed_as: 'Ran as',
            run_delivered_to: 'E-mail queued for',
            run_delivered_none: 'No e-mail queued',
            run_delivered_hint: 'An address under E-mail queued for means the e-mail was placed in the sending queue, not that it arrived. The Notification Admin Queue, open to administrators of notifications, shows whether each e-mail was sent.',
            enable: 'Enable',
            enabling: 'Enabling…',
            enable_success: 'Schedule enabled. Its next run is counted from now.',
            enable_failed: 'The schedule could not be enabled.',
            refusal_run_actor_cannot_run: 'This schedule runs as {name}, who cannot run it now: they are no longer an active member, or no longer hold the permission its export needs. It can be switched on once {name} can run it again: ask an administrator to restore their membership or permission. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_inactive: 'This schedule runs as {name}, who is no longer an active member of the organisation, so its runs would be refused and it cannot be switched on. It can be switched on once {name} is an active member again: ask an administrator to restore their membership. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_lacks_permission: 'This schedule runs as {name}, who no longer holds {permission}, which its export needs, so it cannot be switched on. It can be switched on once {name} holds it again: ask an administrator to give {name} that permission. To export now, create a schedule with New Schedule: it runs as you.',
            run_actor_unnamed: 'the person shown as Runs as',
            people_names_failed: 'The user directory could not be loaded, so a person may be shown by their account identifier instead of their name.',
        },
        email_log: {
            title: 'Registre de correus',
            recipient: 'Destinatari',
            subject: 'Assumpte',
            sent_at: 'Enviat el',
            status: 'Estat',
            resend: 'Reenviar',
            fetch_failed: 'Error en fetch email logs. Si us plau, torneu-ho a intentar.',
            announce_loaded: 'Email log loaded',
        },
    },
    organizations: {
        title: 'Organitzacions',
        create: 'Afegir organització',
        edit: 'Editar Organització',
        name: 'Nom',
        slug: 'Llimac',
        timezone: 'Zona horària',
        locale: 'Configuració regional predeterminada',
        parent: 'Organització matriu',
        group: 'Grup',
        org_group: "Grup d'Organitzacions",
        no_group: '(Cap - no pertany a cap grup)',
        created: 'Creat',
        no_parent: '(Cap - autònom)',
        no_orgs: "No s'han trobat organizations",
        load_error: 'Error en load organizations. Si us plau, torneu-ho a intentar.',
        save_error: 'Error en save organization. Si us plau, torneu-ho a intentar.',
        created_msg: 'Organització creat',
        updated: 'Organització actualitzat',
        deactivate: 'Desactivar',
        deactivated: 'Organització desactivada',
        confirm_deactivate: 'Esteu segur que voleu desactivar aquesta organització?',
        loaded_results: 'Organizations carregats, {count} resultats',
        form_loaded: 'Formulari de Organization carregat',
        locale_vi_vn: 'Tiếng Việt (vi-VN)',
        locale_en_us: 'Anglès (en-US)',
        // fix-sprint #431 — the organisation's country (ISO 3166-1 alpha-2).
        country: 'Country',
        country_not_recorded: 'Not recorded',
        country_hint: 'The country this organisation is established in. The cross-border transfer check on rights requests compares a data subject’s country with it; while it is not recorded, that check is not evaluated.',
        // Gate E12.5 remediation 2 (fix-sprint #431) — the row Edit reads the record first.
        edit_reading: 'Reading the organisation’s record…',
        edit_read_error: 'The organisation’s record could not be read, so its current values are not shown and it cannot be saved here.',
        no_head: 'No Head',
        all_frameworks: 'Tots els marcs',
        assigned_at: 'Assignat el',
    },
    intake_configs: {
        title: "Configuracions d'entrada públiques",
        create: 'Afegir Config',
        edit: "Editar config. d'entrada",
        label_col: 'Etiqueta',
        key: "Clau d'entrada",
        key_hint: "L'URL pública serà: /rights-request?key=",
        mapped_org: 'Organització de destinació',
        status: 'Estat',
        enabled: 'Activat',
        disabled: 'Desactivat',
        request_types: 'Activat Sol·licitud Types',
        language: 'Predeterminat Idioma',
        public_url: 'URL pública',
        no_configs: "No s'han trobat configs d'entrada. Creeu-ne una per activar formularis de sol·licitud de drets.",
        load_error: 'Error en load intake configs. Si us plau, torneu-ho a intentar.',
        save_error: 'Error en save. Si us plau, torneu-ho a intentar.',
        delete_error: 'Error en delete. Si us plau, torneu-ho a intentar.',
        created_msg: 'Configuració creada',
        updated: 'Configuració actualitzada',
        deleted: "Config. d'entrada eliminada",
        confirm_delete: "Eliminar aquesta configuració d'entrada?",
        select_types: 'Seleccioneu at least one request type',
        select_org: 'Seleccioneu target organization',
        routing_mode: "Mode d'Encaminament",
        single_org: 'Organització Única',
        org_group_mode: "Grup d'Organitzacions",
        org_group: "Grup d'Organitzacions",
        select_group: "Seleccioneu un grup d'organitzacions...",
        group_root_hint: "S'estableix automàticament a l'organització arrel del grup quan se selecciona el grup.",
        loaded_results: 'Intake configurations carregats, {count} resultats',
        form_loaded: 'Formulari de Intake configuration carregat',
    },
    frequency: {
        weekly: 'Setmanal',
        monthly: 'Mensual',
        quarterly: 'Trimestral',
        semi_annually: 'Semestral',
        annually: 'Anual',
        custom: 'Personalitzat',
    },
    /**
     * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01, the privacy office's two registers.
     *
     * One block with two children rather than two top-level blocks: the
     * publication vocabulary (`channels`, `publication_states`, the target
     * editor's labels) is shared by both registers, and a second spelling of it
     * would be free to drift from the first — the defect `PUBLICATION_CHANNELS`
     * is declared once in shared to avoid.
     *
     * R-57: EN and VI are authored. The other fourteen locales carry the EN
     * string for structural parity and are translated by the Phase 25 batch —
     * stated here rather than left to be inferred from the text.
     */
    privacy_office: {
        publication_states: {
            not_published: 'Not published',
            published: 'Published',
            withdrawn: 'Withdrawn',
        },
        channels: {
            public_website: 'Public website',
            mobile_app: 'Mobile app',
            intranet: 'Intranet',
            physical_notice: 'Physical notice',
            direct_notice: 'Direct notice to people',
            other: 'Other',
        },
        filter_publication_state: 'Publication',
        targets_label: 'Where it was published',
        targets_hint: 'At least one place a person can go and read it. A publication with no location is a claim nobody can check.',
        target_channel: 'Channel',
        target_locator: 'Address or location',
        target_locator_hint: 'A web address for a website, a screen name for an app, or a described location for a notice board.',
        target_locale: 'Language (optional)',
        target_add: 'Add another location',
        target_remove: 'Remove this location',
        field_published_on: 'Published on',
        field_published_on_hint: 'The date it actually became public, not the date you are entering it here.',
        appointments: {
            title: 'Data Protection Officer appointments',
            subtitle: 'Record who is appointed, the decision that appointed them, and where the appointment was made public.',
            module_guide: 'Record the appointment of your data protection officer as a register entry rather than a filled-in form: who is appointed, who decided it and when, when the appointment takes effect, and where you said so publicly. An appointment is saved as a draft and raises no duty until you bring it into effect — that transition is the one that says your organisation has a named officer in post. Only an appointment that is in effect can be published, and a publication must say where a person can go and read it.',
            nav: 'DPO appointments',
            record: 'Record appointment',
            table_label: 'Data protection officer appointments',
            sr_loading: 'Loading appointments',
            announce_loaded: '{count} appointments loaded',
            none: 'No appointments match these filters.',
            filters_label: 'Filter appointments',
            filter_status: 'Status',
            col_appointee: 'Appointed person',
            col_position: 'Position',
            col_decision: 'Appointing decision',
            col_takes_effect: 'Takes effect',
            col_status: 'Status',
            col_publication: 'Publication',
            takes_effect_unknown: 'Not stated',
            statuses: {
                draft: 'Draft',
                in_effect: 'In effect',
                ended: 'Ended',
                superseded: 'Superseded',
            },
            action_take_effect: 'Bring into effect',
            action_publish: 'Record publication',
            action_end: 'End appointment',
            modal_record_title: 'Record a DPO appointment',
            modal_record_hint: 'Record a decision your organisation has already taken. The appointment is saved as a draft and raises no duty until you bring it into effect.',
            field_full_name: 'Full name',
            field_position_title: 'Position title',
            field_contact_email: 'Contact email',
            field_contact_email_hint: 'Written as name@example.com. This is the address the public sees.',
            field_contact_phone: 'Contact phone (optional)',
            field_decided_by: 'Decided by',
            field_decided_by_hint: 'The body or person with the authority to appoint.',
            field_decided_on: 'Decision date',
            field_decision_reference: 'Your reference for the decision (optional)',
            field_takes_effect_on: 'Takes effect on (optional)',
            field_takes_effect_hint: 'Leave this empty if the start date is not settled. The duty is then raised undated with that reason, rather than dated from the day you typed it in.',
            field_ends_on: 'Ends on (optional)',
            modal_take_effect_title: 'Bring the appointment into effect',
            modal_take_effect_hint: 'From this point your organisation has a named officer in post. State the date it started if it is not already recorded.',
            field_correction_note: 'Note about the correction (optional)',
            modal_publish_title: 'Record where the appointment was published',
            modal_publish_hint: 'Only an appointment that is in effect can be published — a person is not named publicly as your officer before your organisation says they are one.',
            modal_end_title: 'End the appointment',
            modal_end_hint: 'An appointment that ran its course and one replaced by a newer appointment are different answers to "why is this person no longer the officer". Both are kept.',
            field_end_outcome: 'Outcome',
            field_end_reason: 'Reason',
            field_end_date: 'End date (optional)',
            toast_recorded: 'Appointment recorded.',
            toast_in_effect: 'Appointment is now in effect.',
            toast_published: 'Publication recorded.',
            toast_ended: 'Appointment ended.',
        },
        notices: {
            title: 'Privacy notices',
            subtitle: 'Draft a revision of a privacy notice, for the public or for named data subjects, have it approved, and record where it was published.',
            module_guide: 'Keep a register of the privacy notices your organisation gives, to the public or to named data subjects of its processing activities: which revision is live, who approved the text, and where a person can read it. Approval and publication are two separate acts, usually by two different people, so the register can show a notice that was approved and never actually published — the failure this record exists to make visible. A notice cannot go straight from draft to published, and a published notice that is taken down keeps its publication history with the reason it was withdrawn.',
            nav: 'Privacy notices',
            draft: 'New revision',
            table_label: 'Privacy notices',
            sr_loading: 'Loading privacy notices',
            announce_loaded: '{count} privacy notices loaded',
            none: 'No privacy notices match these filters.',
            filters_label: 'Filter privacy notices',
            filter_status: 'Status',
            col_title: 'Title',
            col_revision: 'Revision',
            col_locales: 'Languages',
            col_status: 'Status',
            col_approval: 'Approved by',
            col_publication: 'Publication',
            approval_none: 'Not approved',
            statuses: {
                draft: 'Draft',
                approved: 'Approved',
                published: 'Published',
                withdrawn: 'Withdrawn',
                superseded: 'Superseded',
            },
            action_approve: 'Approve',
            action_publish: 'Publish',
            action_withdraw: 'Withdraw',
            action_supersede: 'Supersede',
            modal_draft_title: 'New privacy notice revision',
            modal_draft_hint: 'The revision is the version number your published text carries. It is not a version this platform counts for you.',
            field_notice_title: 'Title',
            field_revision: 'Revision',
            field_content_url: 'Where the text lives',
            field_content_url_hint: 'The address of the notice text itself — the page, document or file a reader would open.',
            field_locales: 'Languages',
            field_locales_hint: 'One language code per line, or separated by commas.',
            field_supersedes: 'Replaces which notice (optional)',
            modal_approve_title: 'Approve the notice',
            modal_approve_hint: 'Approving records who signed the text off. It does not give the notice to its audience — publishing is a separate act.',
            field_approved_by: 'Approved by (optional)',
            field_approved_by_hint: 'Leave this empty to record yourself as the approver.',
            field_approval_reference: 'Your reference for the approval (optional)',
            modal_publish_title: 'Publish the notice',
            modal_publish_hint: 'Record that the approved text is now given to its audience, and where a person can read it.',
            publish_needs_approval: 'This notice carries no recorded approval, so it cannot be published. Record who approved the text first.',
            modal_withdraw_title: 'Withdraw the notice',
            modal_withdraw_hint: 'Withdrawing keeps the publication history: "we published this and took it down" is a different answer from "we never published this".',
            field_withdraw_reason: 'Reason for withdrawing',
            modal_supersede_title: 'Supersede the notice',
            modal_supersede_hint: 'Superseding closes this revision because a newer one has replaced it, and it is the last thing that can happen to it in this register.',
            field_supersede_reason: 'Reason for superseding',
            toast_drafted: 'Privacy notice revision created.',
            toast_approved: 'Privacy notice approved.',
            toast_published: 'Privacy notice published.',
            toast_withdrawn: 'Privacy notice withdrawn.',
            toast_superseded: 'Privacy notice superseded.',
        },
    },
    /* Sprint 65.18.0 — continuity (IRP-01). English text held for structural
       parity; real translation deferred to Phase 25 per R-57. */
    /* Sprint 65.19.0 -- legal matters (toolkit row LEG-01). */
    /* 2026-09-24 owner ruling — legacy instrument notice. English text held for
       structural parity; real translation deferred to Phase 25 per R-57. */
    framework_lifecycle_notice: {
        legacy_chip: 'Legacy — no longer in force',
        legacy_notice_label: 'Legacy instrument notice',
        legacy_body: 'This instrument has lapsed or been replaced. Results determined under it may still apply under transition rules. Check the current instrument before relying on it.',
        suspended_chip: 'Suspended — not an obligation',
        suspended_notice_label: 'Suspended instrument notice',
        suspended_body: 'The application of this instrument is suspended, so its duties are not owed while the suspension lasts. Check what ends the suspension before relying on anything recorded under it.',
    },
    framework_articles: {
        title: 'Articles',
        article: 'Article',
        heading: 'Heading',
        state: 'Platform coverage',
        totals_summary: '{total} articles in this instrument — {mapped} implemented by a requirement, {declared} recorded as imposing no duty, {unmapped} not yet mapped.',
        not_article_structured: 'This instrument is not divided into articles — it uses control identifiers — so there is no article map to show. That is correct for this framework and not a gap.',
        not_mapped_yet: 'This instrument is divided into articles and the installed pack does not yet carry them. Until it does, the platform cannot say which of its provisions are implemented.',
        structure_unverified: 'Nobody has read this instrument\u2019s official text yet, so the platform does not know how it divides its provisions and cannot say which of them are implemented. This is a statement about the platform, not about the instrument.',
        structure_not_declared: 'This record does not say how its instrument divides its provisions, so the platform does not know whether it has an article map. That is a gap in the installed pack, not a statement about the instrument.',
        structure_not_required: 'This record comes from a market pack, and a market pack is not required to declare how its instrument divides its provisions, so the platform has no article map to show for it. That is not a gap in the installed pack.',
        structure_origin_unknown: 'This record does not say how its instrument divides its provisions, and the platform cannot tell which kind of pack it was installed from, so it does not know whether an article map should exist.',
        coverage_line: 'Article coverage: {coverage}',
        coverage_levels: {
            mapped: 'Every article is accounted for',
            partial: 'Some articles are not yet mapped',
            not_assessed: 'Not assessed',
        },
        load_failed: 'The articles for this framework could not be read.',
        recorded_text: {
            column: 'Recorded text',
            read: 'Read',
            read_aria: 'Read the recorded text of Article {article}',
            dialog_title: 'Article {article} — recorded text',
            load_failed: 'The recorded text of this article could not be read.',
            load_more: 'Show more',
            source_line: 'Source: {title}',
            page_single: 'page {page} of the source',
            pages_range: 'pages {start}–{end} of the source',
            extraction: {
                ocr: 'read by OCR from the signed scan',
                pdf_text_layer: 'extracted from the PDF text layer',
                docx_text: 'extracted from the DOCX original',
                html_text: 'extracted from the official web page',
                unrecorded: 'extraction method not recorded',
            },
            missing: 'The recorded source does not carry this article\u2019s text, so none is shown. Only its heading is known.',
            partial: 'The source ends without a closing mark after this article, so the text may run on into material that follows it.',
            notice: 'This is the instrument\u2019s original Vietnamese text as recorded from its signed source. It may contain recognition errors, it does not certify current legal effect, and no translation is provided.',
        },
        source_tab: {
            title: 'Source',
            intro: 'The instrument\u2019s recorded text, read in full: chapters, articles, annexes and page marks as the signed source carries them.',
            source_select: 'Recorded source',
            page_mark: 'Page {page}',
            progress: 'Showing {shown} of {total} characters',
            load_more: 'Show more',
            load_all: 'Show the rest',
            load_failed: 'The recorded source text could not be read.',
            no_recorded_source: 'No recorded source text is held for this framework. Its requirements and articles are on the other tabs.',
            composite: 'This framework groups other instruments and has no text of its own. Read the source of each of them:',
            superseded_draft: 'This consultation draft was later promulgated, and its own text is not held. Read the instrument that replaced it:',
            open_in_source: 'In source',
            open_in_source_aria: 'Show Article {article} in the full source',
            article_not_in_source: 'The recorded source does not carry Article {article}\u2019s heading, so it cannot be shown in place.',
        },
        states: {
            mapped: 'Implemented',
            no_duty_declared: 'No duty on the controller',
            not_yet_mapped: 'Not yet mapped',
        },
    },
    credit_information: {
        title: 'Credit-Information Uses',
        scope_note: 'Law 91/2025/QH15 Điều 27(1)(b) prohibits using a data subject’s credit information for credit scoring, credit rating, credit-information assessment or creditworthiness assessment unless that data subject has consented. Decree 356/2025/NĐ-CP Điều 8 details the Article: consent must state the purposes — including those four where they are carried out — the collection sources and sharing parties, the retention period and how consent may be withdrawn; compliance is assessed once every 01 year; and disclosure or loss of sensitive data in this sector is notified to the authority and the data subject within 72 hours.',
        boundary_heading: 'Scope and boundary — from your installed frameworks',
        boundary_frame: 'These statements come from the frameworks installed for your organisation, shown as each framework states them. This register records that a use took place, for which purpose, and the consent it rested on. The platform computes no credit score, produces no rating, assesses no creditworthiness and makes no lending decision. A use no consent covers is refused here rather than recorded with a warning beside it.',
        boundary_none: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organisation, not your organisation’s scope.',
        boundary_load_error: 'The installed frameworks’ boundary statements could not be read, so none is shown. This does not mean that no framework declares one.',
        boundary_retry_subject: 'the boundary statements',
        purposes_load_failed: 'The purposes your installed frameworks declare could not be read, so a use cannot be recorded yet. This does not mean that none is declared.',
        purposes_retry_subject: 'the declared purposes',
        consent_check_timing: 'The consent is checked as it stands when you record the use, not as it stood at the time of use. A consent withdrawn, or given, between those two moments is judged as it is now.',
        kpi_label: 'Register summary',
        kpi_total: 'Total',
        kpi_total_hint: 'all uses recorded in this register',
        kpi_on_this_page: 'on this page',
        kpi_not_loaded: 'not loaded',
        uses: 'Recorded uses',
        record: 'Record a use',
        data_subject_ref: 'Data subject reference',
        information_description: 'What credit information was used',
        purpose: 'Purpose',
        used_at: 'Used at',
        consent_evidence_id: 'Consent evidence',
        consent_evidence_hint: 'The consent-evidence entry this use rests on. It is resolved and checked on the server — naming one that does not cover this purpose is refused.',
        consent_purpose_id: 'Which purpose on that consent',
        consent_purpose_hint: 'Which purpose named on that consent covers this use. Your organisation decides which of its own purposes corresponds; the platform checks that the one you name is genuinely on that consent.',
        consent_state: 'Consent',
        record_withdrawal: 'Record a withdrawal',
        withdrawn_at: 'Withdrawn on',
        no_purposes_installed: 'No framework installed for your organisation declares a credit-information purpose, so a use cannot be recorded against one here.',
        empty_uses: 'No credit-information use has been recorded yet.',
        load_failed: 'The credit-information register could not be read.',
        record_failed: 'The use could not be recorded.',
        recorded: 'Use recorded.',
        withdrawal_recorded: 'Withdrawal recorded.',
        withdrawal_failed: 'The withdrawal could not be recorded.',
        states: {
            consent_in_force: 'In force',
            consent_withdrawn: 'Withdrawn',
        },
    },
    legal_matters: {
        title: 'Legal Matters',
        scope_note: 'No installed instrument requires the controller to operate this register. Law 91/2025/QH15 Điều 4(1)(đ) declares that a data subject may complain and may denounce, and Decree 356/2025/NĐ-CP Điều 36(6) assigns the resolution of complaints and denunciations to the state authority — so a right is declared, and no duty to receive it falls on you. Running this register is a capability your organisation chooses, and any rhythm shown here is your own rather than a statutory deadline.',
        matters: 'Matters',
        receive: 'Record a matter',
        kind: 'Kind',
        origin: 'Rests on',
        right_code: 'Which right',
        raised_by: 'Raised by',
        subject: 'What it concerns',
        state_label: 'State',
        received_at: 'Received',
        acknowledged_at: 'Acknowledged',
        acknowledge: 'Record the acknowledgement',
        resolve: 'Record the conclusion',
        resolution_summary: 'What was concluded',
        concluded_at: 'Concluded on',
        not_acknowledged: 'Not yet acknowledged',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        intake_from: 'From rights request {reference}',
        right_not_recorded: 'The intake did not say which right it raises; a person records it.',
        record_right: 'Record the right',
        record_right_hint: 'Choose the right this matter rests on, from those the installed frameworks declare at the provision the intake was made under.',
        right_recorded: 'Right recorded',
        // fix-sprint-20261026-0900 Finding 6 — what a matter awaiting its right rests on,
        // the related-matters column, and the refusals of Record the right.
        rests_on_provision: 'At {basis} of {framework}',
        rests_on_provision_no_basis: 'Under {framework}; no provision is stated',
        related_matters: 'Related matters',
        no_related_matters: 'None',
        record_right_closed: 'This matter is closed, so the right it rests on can no longer be recorded.',
        record_right_not_at_provision: 'That right does not rest on the provision the intake was made under. Choose one of the rights offered.',
        record_right_not_declared: 'No installed framework declares that right. Choose another.',
        record_right_already_recorded: 'The right this matter rests on has already been recorded.',
        suggested_link: 'Possibly the same matter as “{subject}”: an earlier request gave the same email address, which is not verified.',
        suggested_link_open: 'Open that matter',
        suggested_link_accept: 'Same matter',
        suggested_link_dismiss: 'Not the same matter',
        suggested_link_accepted: 'A person recorded this as the same matter as “{subject}”.',
        suggested_link_dismissed: 'A person recorded this as not the same matter as “{subject}”.',
        suggested_link_decided: 'Decision recorded',
        linked_matter_not_found: 'The legal matter this link points to could not be read.',
        linked_matter_shown: 'The legal matter the link points to is shown first.',
        no_rights_installed: 'No framework installed for your organisation declares a right, so a matter cannot rest on one here.',
        rights_loading: 'Reading the rights your installed frameworks declare…',
        rights_load_failed: 'The rights your installed frameworks declare could not be read, so a matter cannot rest on one until they are.',
        rights_retry_subject: 'the declared rights',
        matters_retry_subject: 'the legal-matters register',
        loading: 'Loading the legal-matters register',
        right_placeholder: 'Choose the right',
        withdrawn_on_by: 'Withdrawn at {at}, recorded by {by}.',
        withdrawal_reason_not_recorded: 'No reason was recorded for this withdrawal.',
        timeline_acknowledged_before_received: 'The acknowledgement cannot be earlier than when the matter was received.',
        timeline_concluded_before_received: 'The conclusion cannot be earlier than when the matter was received.',
        timeline_concluded_before_acknowledged: 'The conclusion cannot be earlier than the acknowledgement.',
        kpi_row_label: 'Legal-matters summary',
        kpi_not_loaded: 'Not loaded',
        kpi_total: 'Matters',
        kpi_total_hint: 'in the register',
        kpi_on_this_page: 'on this page',
        withdraw: 'Record a withdrawal',
        withdrawal_reason: 'Why it was withdrawn',
        withdrawn: 'Withdrawal recorded.',
        empty_matters: 'No matter has been recorded yet.',
        load_failed: 'The legal-matters register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        acknowledged: 'Acknowledgement recorded.',
        resolved: 'Conclusion recorded.',
        kinds: {
            complaint: 'Complaint',
            denunciation: 'Denunciation',
            claim: 'Claim',
            litigation: 'Litigation',
            regulatory_penalty: 'Regulatory penalty',
            other: 'Other',
        },
        origins: {
            statutory_right: 'A right an installed framework declares',
            elective: 'A matter your organisation chose to track',
        },
        states: {
            received: 'Received',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            withdrawn: 'Withdrawn',
        },
        raised_by_options: {
            data_subject: 'Data subject',
            employee: 'Employee',
            regulator: 'Regulator',
            counterparty: 'Counterparty',
            other: 'Other',
        },
    },
    continuity: {
        title: 'Continuity Exercises',
        scope_note: 'No installed instrument requires continuity exercises. Law 91/2025/QH15 and Decree 356/2025/NĐ-CP were both read in full and neither imposes this control, so any rhythm shown here is your organisation’s own and never a statutory deadline. An exercise that raised improvement actions is not finished until each has been retested.',
        plans: 'Continuity plans',
        exercises: 'Exercises',
        plan_name: 'Plan name',
        plan_scope: 'Scope',
        plan_owner: 'Owner',
        scope_note_label: 'Say what it covers',
        create_plan: 'Add a plan',
        schedule: 'Schedule an exercise',
        kind: 'Kind',
        status_label: 'Status',
        outcome_label: 'Outcome',
        held_at: 'Held on',
        participants: 'Participants',
        open_loops: 'Improvement actions awaiting retest',
        no_open_loops: 'All retested',
        last_reviewed: 'Last reviewed',
        never_reviewed: 'Never reviewed',
        empty_plans: 'No continuity plan has been recorded yet.',
        empty_exercises: 'No exercise has been recorded yet.',
        complete: 'Complete the exercise',
        cancel: 'Cancel the exercise',
        cancel_reason: 'Why it was cancelled',
        completed: 'Exercise completed.',
        cancelled: 'Exercise cancelled.',
        retested: 'Retest recorded.',
        record_retest: 'Record a retest',
        retested_at: 'Retested on',
        retest_note: 'What the retest showed',
        participant_name: 'Who led it',
        participant_role: 'Their role',
        summary: 'What happened',
        improvement_action: 'Improvement action (optional)',
        action_owner: 'Action owner',
        load_failed: 'The continuity register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        retry_plans_subject: 'the continuity plans',
        retry_exercises_subject: 'the exercises',
        complete_for: 'Complete the exercise: {exercise}',
        cancel_for: 'Cancel the exercise: {exercise}',
        record_retest_for: 'Record a retest for {exercise}: {action}',
        completing_heading: 'Completing the exercise: {exercise}',
        cancelling_heading: 'Cancelling the exercise: {exercise}',
        retest_heading: 'Recording a retest for {exercise}: {action}',
        scopes: {
            systems: 'Systems',
            premises: 'Premises',
            people: 'People',
            suppliers: 'Suppliers',
            data_recovery: 'Data recovery',
            other: 'Other',
        },
        kinds: {
            tabletop: 'Tabletop',
            simulation: 'Simulation',
            live: 'Live',
            other: 'Other',
        },
        outcomes: {
            objectives_met: 'Objectives met',
            objectives_partially_met: 'Objectives partially met',
            objectives_not_met: 'Objectives not met',
            not_completed: 'Not completed',
        },
        statuses: {
            scheduled: 'Scheduled',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
    },
    /* Sprint 65.17.0 — de-identification runs (ANON-01). English text held for
     structural parity; real translation deferred to Phase 25 per R-57. */
    deidentification: {
        title: 'De-identification Runs',
        scope_note: 'Decree 356/2025/NĐ-CP Điều 10(6) requires that a data subject be able to rectify, anonymise and delete identification records in artificial-intelligence and metaverse systems. Outside that scope this is a control your organisation chooses to run. A run is not complete until somebody has judged what remains re-identifiable.',
        population: 'Population transformed',
        method: 'Method',
        method_note: 'Name the method',
        create: 'Record a run',
        created: 'De-identification run recorded.',
        empty: 'No de-identification run has been recorded yet.',
        status_label: 'Status',
        sample_label: 'Sample',
        residual_risk_label: 'Residual re-identification risk',
        load_failed: 'The de-identification runs could not be read.',
        create_failed: 'The run could not be recorded.',
        residual_risk_absent: 'Not yet judged',
        record_judgement: 'Record the sample and judgement',
        record_judgement_for: 'Record the sample and judgement for {population}',
        sample_outcome: 'Sample outcome',
        records_examined: 'Records examined',
        sample_finding: 'What the sample found',
        residual_risk_reasoning: 'Residual re-identification risk — your reasoning',
        residual_risk_hint: 'The platform derives no figure. What remains re-identifiable depends on data your organisation does not hold, so this is recorded as your judgement, against your account and the moment you save it.',
        updated: 'De-identification run updated.',
        update_failed: 'The run could not be updated.',
        approve_for: 'Approve the run for {population}',
        reject: 'Reject the run',
        reject_for: 'Reject the run for {population}',
        reject_heading_for: 'Rejecting the run for {population}',
        decision_note: 'Why the run was rejected',
        approved: 'De-identification run approved.',
        rejected: 'De-identification run rejected.',
        decide_failed: 'The decision could not be recorded.',
        total_runs: '{count} run(s) recorded',
        methods: {
            suppression: 'Suppression',
            generalisation: 'Generalisation',
            pseudonymisation: 'Pseudonymisation',
            aggregation: 'Aggregation',
            noise_addition: 'Noise addition',
            hashing_with_deleted_key: 'Hashing with the key deleted',
            other: 'Other',
        },
        sample_outcomes: {
            not_sampled: 'Not sampled',
            sample_clean: 'Sample found no identifiers',
            sample_found_identifiers: 'Sample found identifiers',
        },
        statuses: {
            draft: 'Draft',
            awaiting_approval: 'Awaiting approval',
            approved: 'Approved',
            rejected: 'Rejected',
        },
    },
    training: {
        title: 'Training & Awareness',
        subtitle: 'Assign training to named people and record each completion against the evidence that shows it happened.',
        module_guide: 'Define the training your organisation requires, assign it to named people with a deadline, and close each assignment against evidence — a completed form, an uploaded certificate or a recorded attestation. An assignment cannot be marked complete on its own; exempting someone is a decision recorded against the person who took it, with a reason. Completion rates here feed the training component of your audit-readiness score.',
        nav_assignments: 'Training & Awareness',
        new_course: 'New course',
        assign: 'Assign training',
        summary_open: 'Open',
        summary_completed: 'Completed',
        summary_overdue: 'Overdue',
        summary_exempted: 'Exempted',
        filters_label: 'Filter training assignments',
        filter_status: 'Status',
        filter_overdue_only: 'Overdue only',
        focused_heading: 'Assignment:',
        sr_loading: 'Loading training assignments',
        no_assignments: 'No training assignments match these filters.',
        table_label: 'Training assignments',
        col_course: 'Course',
        col_assignee: 'Assignee',
        col_due: 'Due',
        col_status: 'Status',
        col_evidence: 'Evidence',
        overdue_badge: 'Overdue',
        evidence_none: 'None recorded',
        exempted_by_label: 'Exempted by',
        cancelled_by_label: 'Withdrawn by',
        cancellation_reason_not_recorded: 'Withdrawn — no reason was stored',
        action_start: 'Start',
        action_complete: 'Record completion',
        action_exempt: 'Exempt',
        action_cancel: 'Withdraw',
        modal_course_title: 'New training course',
        field_course_title: 'Course title',
        field_course_description: 'Description',
        field_recurrence_months: 'Repeat every (months)',
        field_recurrence_hint: 'Leave empty for one-off training. This is an internal cadence, not a statutory deadline.',
        modal_assign_title: 'Assign training',
        field_course: 'Course',
        no_courses_hint: 'No active courses yet. Define a course before assigning training.',
        courses_heading: 'Courses',
        courses_table_label: 'Active training courses',
        no_courses: 'No active courses yet.',
        col_description: 'Description',
        col_recurrence: 'Repeats',
        recurrence_one_off: 'One-off',
        recurrence_every: 'Every {count} months',
        action_assign_course: 'Assign',
        field_assignees: 'People',
        field_assignees_hint: 'One user id per line, or separated by commas. Each person gets their own assignment and their own task.',
        field_due_at: 'Due date',
        modal_complete_title: 'Record completion',
        modal_complete_hint: 'A completion must cite the evidence that shows the training was done — a submitted form, an uploaded file or a recorded attestation. The assignment cannot be closed without it.',
        field_evidence_kind: 'Evidence type',
        field_evidence_reference: 'Evidence reference',
        modal_exempt_title: 'Exempt from training',
        modal_exempt_hint: 'An exemption is recorded against you, with the reason you give. It is never counted as a completion.',
        field_exemption_reason: 'Reason for the exemption',
        modal_cancel_title: 'Withdraw assignment',
        field_cancel_reason: 'Reason for withdrawing',
        toast_started: 'Training assignment started.',
        toast_course_created: 'Training course created.',
        toast_assigned: 'Training assigned.',
        toast_completed: 'Completion recorded.',
        toast_exempted: 'Exemption recorded.',
        toast_cancelled: 'Assignment withdrawn.',
        announce_loaded: '{count} training assignments loaded',
        statuses: {
            assigned: 'Assigned',
            in_progress: 'In progress',
            completed: 'Completed',
            exempted: 'Exempted',
            cancelled: 'Withdrawn',
        },
        evidence_kinds: {
            form_instance: 'Completed form',
            attachment: 'Uploaded file',
            attestation: 'Recorded attestation',
        },
    },
    task_management: {
        // Sprint 63.32.0 (#543) — the module a task came from, in the reader's language.
        source_modules: {
            rights_requests: 'Rights Requests',
            assessments: 'DPIA & Assessments',
            incidents: 'Incident Response',
            incident_ops: 'Incident Operations',
            monitoring: 'Monitoring Programs',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion Orchestration',
            access_review: 'Access Reviews',
            consent: 'Consent Governance',
            consent_properties: 'Consent Properties',
            forms: 'Compliance Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Data Classification',
            discovery: 'Data Discovery',
            data_mapping: 'Data Mapping',
            compliance_operations: 'Compliance Work Programme',
            training: 'Training',
            privacy_office: 'Privacy Office',
            authority_operations: 'Authority Operations',
            ai_governance: 'AI Governance',
            regulatory_classification: 'Regulatory Classification',
            org_settings: 'Organization Profile',
            customs_tax_administration: 'Customs Tax Administration',
            service_certificates: 'Processing Service Certificate',
            manual: 'Manual',
        },
        nav: {
            calendar: 'Calendari',
            all_tasks: 'Totes les tasques',
            my_tasks: 'Les meves tasques',
            overdue: 'Endarrerides',
            board: 'Tauler',
            workflows: 'Fluxos de treball',
        },
        list: {
            title: 'Totes les tasques',
            my_title: 'Les meves tasques',
            overdue_title: 'Tasques endarrerides',
            new_task: 'Crear tasca',
            no_tasks: "No s'han trobat tasques.",
            empty_state: "No s'han trobat tasques. Les tasques es generen a partir de fluxos de treball de mòduls com avaluacions, incidents i campanyes de revisió.",
            total: 'Total de tasques',
            search_placeholder: 'Cercar tasques...',
            bulk_actions: 'Accions massives',
            change_status: 'Canviar estat',
            change_priority: 'Canviar prioritat',
            assign: 'Assignar',
            sr_my_tasks_count: '{count} tasques assignades a tu',
            sr_days_overdue: '{count} dies de retard',
            sla_label: 'SLA',
        },
        detail: {
            workflow_step: 'Workflow step',
            title: 'Detall de la tasca',
            description: 'Descripció',
            workflow_progress: 'Progrés del flux de treball',
            checklist: 'Llista de verificació',
            dependencies: 'Dependències',
            comments: 'Comentaris',
            activity_timeline: "Cronologia d'activitat",
            assignees: 'Assignats',
            add_assignee: 'Afegir assignat',
            remove_assignee: 'Eliminar assignat',
            due_date: 'Data de venciment',
            start_date: "Data d'inici",
            sla_status: 'Estat SLA',
            sla_on_track: 'En termini',
            sla_at_risk: 'En risc',
            sla_breached: 'Incomplert',
            tags: 'Etiquetes',
            source_entity: "Entitat d'origen",
            view_source: 'Veure origen',
            add_dependency: 'Afegir dependència',
            remove_dependency: 'Eliminar dependència',
            write_comment: 'Escriure un comentari...',
            post_comment: 'Publicar',
            edit_comment: 'Editar',
            delete_comment: 'Eliminar',
            add_checklist_item: 'Afegir element',
            no_dependencies: 'Sense dependències',
            no_comments: 'Encara sense comentaris',
            blocks: 'Bloqueja',
            blocked_by: 'Bloquejat per',
            related: 'Relacionat',
            working_days_remain: 'dies laborables restants',
            created_by: 'Creat per',
            completed_at: 'Completat el',
            remove_assignee_aria: 'Remove assignee: {name}',
            // Sprint 63.32.0 (#543) — the seeded role names an assignment can carry.
            assignee_role_labels: {
                platform_admin: 'Platform Admin',
                compliance_owner: 'Compliance Owner',
                case_manager: 'Case Manager',
                auditor: 'Auditor',
                approver: 'Approver',
                partner_external_counsel: 'External Counsel (Partner)',
                processor_liaison: 'Processor Liaison',
            },
            remove_dependency_aria: 'Remove dependency: {name}',
            no_assignees: 'Sense assignats',
            add_assignee_placeholder: "Introduïu l'ID d'usuari...",
            edited: 'editat',
            timeline: "Cronologia d'activitat",
            no_timeline: 'Encara sense activitat',
            timeline_reason: 'Reason',
            no_workflow: 'Sense flux de treball assignat',
            add_dependency_placeholder: "Introduïu l'ID de tasca...",
            sr_status_updated: 'Estat actualitzat a {status}',
            sr_priority_updated: "S'ha actualitzat la prioritat a {priority}",
            // Sprint 63.30.0 (#590, #574) — due date edit and Escalate.
            save_due_date: 'Save due date',
            due_date_saved: 'Due date set to {date}',
            due_date_save_failed: 'The due date could not be saved.',
            escalate: 'Escalate',
            escalate_title: 'Escalate this task',
            escalate_description: 'Escalating moves the task to Escalated. Your reason is recorded with the status change and shown beside it in the Activity Timeline.',
            escalate_reason: 'Reason for escalating',
            escalate_reason_required: 'Enter a reason for escalating.',
            escalating: 'Escalating...',
            sr_escalated: 'Task escalated',
            escalate_failed: 'The task could not be escalated.',
            convert_to_workflow: 'Executar com a flux de treball',
            convert_modal_title: "Converteix la tasca a l'execució del flux de treball",
            convert_workflow_label: 'Flux de treball',
            convert_workflow_placeholder: 'Trieu un flux de treball...',
            convert_attachments_label: 'Adjunts',
            convert_attachments_empty: 'No hi ha fitxers adjunts en aquesta tasca.',
            convert_persona_label: 'Anul·lació de persona (opcional)',
            convert_submit: "Inicieu l'execució del flux de treball",
            convert_starting: "S'està iniciant l'execució del flux de treball...",
            convert_started: "S'ha iniciat l'execució del flux de treball: s'obre l'execució...",
            convert_disabled_in_flight: "Ja s'està executant un flux de treball d'aquesta tasca.",
            linked_workflow_chip: 'De {workflow_title}',
            linked_chat_chip: 'Des del xat AI.',
            linked_more: '+{count} més',
        },
        calendar: {
            title: 'Calendari de tasques',
            page_title: 'Calendari',
            create_task: 'Crear tasca',
            month: 'Mes',
            week: 'Setmana',
            day: 'Dia',
            today: 'Avui',
            more_tasks: '+{count} més',
            no_tasks_day: 'Cap tasca aquest dia',
            all_day: 'Tot el dia',
            quick_create_tooltip: 'Crear tasca per a aquest dia',
            view_changed: 'La vista del calendari ha canviat a {view}',
            tasks_in_view: '{count} tasques a la vista',
            task_in_view: '{count} tasca a la vista',
            filter_my_tasks: 'Les meves tasques',
            filter_overdue: 'Endarrerides',
            filter_high_priority: 'Prioritat alta',
            filter_all: 'Totes',
        },
        filters: {
            assignee: 'Assignat',
            me: 'Jo',
            role: 'Rol',
            status: 'Estat',
            priority: 'Prioritat',
            task_type: 'Tipus de tasca',
            source_module: "Mòdul d'origen",
            organization: 'Organització',
            sla_breached_only: 'Mostrar només SLA incomplert',
            clear_filters: 'Esborrar filtres',
            apply_filters: 'Aplicar',
            source_label: 'Font',
            source_manual: 'Manual',
            source_module_kind: 'Generat per mòduls',
            source_chat: 'Des del xat AI.',
            source_workflow_run: 'Des de AI Execució del flux de treball',
        },
        status: {
            pending: 'Pendent',
            in_progress: 'En curs',
            blocked: 'Bloquejat',
            completed: 'Completat',
            overdue: 'Endarrerit',
            escalated: 'Escalat',
            cancelled: 'Cancel·lat',
        },
        priority: {
            critical: 'Crític',
            high: 'Alt',
            medium: 'Mitjà',
            low: 'Baix',
        },
        task_types: {
            manual_task: 'Tasca manual',
            dsr_case: 'Cas DSR',
            dsr_review: 'Revisió DSR',
            dsr_escalation: 'Escalada DSR',
            dpia_review: 'Revisió AIPD',
            dpia_approval: 'Aprovació AIPD',
            transfer_review: 'Revisió de transferència',
            incident_task: "Tasca d'incident",
            incident_notification: "Notificació d'incident",
            playbook_step: 'Pas del manual',
            regulator_deadline: 'Termini del regulador',
            monitoring_task: 'Tasca de supervisió',
            kpi_alert: 'Alerta KPI',
            vendor_review: 'Revisió de proveïdor',
            vendor_questionnaire: 'Qüestionari de proveïdor',
            governance_review: 'Revisió de governança',
            drift_alert: 'Alerta de desviació',
            governance_approval: 'Aprovació de governança',
            deletion_task: "Tasca d'eliminació",
            deletion_verification: "Verificació d'eliminació",
            access_review: "Revisió d'accés",
            sod_alert: 'Alerta SoD',
            consent_workflow: 'Flux de consentiment',
            consent_alert: 'Alerta de consentiment',
            property_review: 'Revisió de propietat',
            inconsistency_alert: "Alerta d'inconsistència",
            form_review: 'Revisió de formulari',
            dossier_deadline: 'Termini de dossier',
            submission_task: "Tasca d'enviament",
            translation_review: 'Revisió de traducció',
            governance_task: 'Tasca de governança',
            reporting_deadline: "Termini d'informe",
            scanner_alert: "Alerta d'escàner",
            classification_review: 'Revisió de classificació',
            lifecycle_task: 'Tasca de cicle de vida',
            discovery_review: 'Revisió de descobriment',
            forseti_task: 'Forseti Task',
        },
        training_assignment: 'Assignació de formació',
        task_type_groups: {
            training: 'Formació',
            manual: 'Manual',
            dsr: 'DSR / Sol·licituds de drets',
            assessments: 'Avaluacions',
            incidents: 'Incidències',
            incident_ops: "Operacions d'incidents",
            monitoring: 'Supervisió',
            vendor_risk: 'Risc de proveïdors',
            governance: 'Governança',
            deletion: 'Eliminació',
            access_review: "Revisió d'accés",
            consent: 'Consentiment',
            consent_properties: 'Propietats del consentiment',
            forms: 'Formularis',
            localization: 'Localització',
            program_governance: 'Governança de programes',
            privacy_scanner: 'Escàner de privacitat',
            classification: 'Classificació',
            discovery: 'Descobriment',
            forseti: 'Forseti',
        },
        create: {
            title: 'Crear tasca',
            task_title: 'Títol',
            task_description: 'Descripció',
            task_type: 'Tipus de tasca',
            task_type_label: 'Tipus de tasca',
            task_type_placeholder: 'Seleccionar un tipus de tasca',
            priority: 'Prioritat',
            assignees: 'Assignats',
            assignee_search_placeholder: 'Cercar usuaris...',
            assignee_search_no_results: 'No users found.',
            assignee_search_loading: 'Loading…',
            tags_placeholder: 'tag1, tag2',
            remove_assignee: 'Treure {name}',
            due_date: 'Data de venciment',
            start_date: "Data d'inici",
            checklist: 'Llista de verificació',
            tags: 'Etiquetes',
            add_checklist_item: 'Afegir element de llista',
            validation: {
                title_required: 'El títol és obligatori',
                title_max_length: 'El títol ha de tenir 500 caràcters o menys',
                description_max_length: 'La descripció ha de tenir 5000 caràcters o menys',
                due_date_before_start: "La data de venciment ha de ser posterior a la data d'inici",
                max_reminders: 'Màxim 5 recordatoris per tasca',
            },
            reminders: {
                title: 'Recordatoris',
                add: 'Afegir recordatori',
                at_due_date: 'A la data de venciment',
                minutes_before: '{count} minuts abans',
                hours_before: '{count} hores abans',
                days_before: '{count} dies abans',
                weeks_before: '{count} setmanes abans',
                custom: 'Personalitzat',
                channel_platform: 'Plataforma',
                channel_email: 'Correu electrònic',
                channel_both: 'Ambdós',
                offset_label: 'Recordatori {n}: moment',
                channel_label: 'Recordatori {n}: canal',
                remove_label: 'Eliminar el recordatori {n}',
            },
            workflow: {
                label: 'Flux de treball',
                none: "Cap (gestió manual d'estat)",
                steps_count: '{count} passos',
                preview_label: 'Passos del flux de treball',
            },
        },
        errors: {
            fetch_failed: 'Error en carregar les tasques.',
            save_failed: 'Error en desar la tasca.',
            delete_failed: 'Error en eliminar la tasca.',
            status_change_failed: "Error en canviar l'estat de la tasca.",
            assign_failed: 'Error en assignar la tasca.',
            comment_failed: 'Error en desar el comentari.',
            checklist_failed: 'Error en actualitzar la llista de verificació.',
            dependency_failed: 'Error en actualitzar la dependència.',
            workflow_failed: 'Error en desar el flux de treball.',
            workflow_activate_failed: 'Error en activar el flux de treball.',
            workflow_delete_failed: 'Error en eliminar el flux de treball.',
            template_install_failed: 'Error en instal·lar la plantilla.',
            search_failed: 'Error en cercar tasques.',
            convert_failed: "No s'ha pogut iniciar l'execució del flux de treball.",
            convert_quota_exhausted: 'Cub de fitxes baix. Recarregar abans de tornar a executar.',
        },
        dependency_types: {
            blocks: 'Bloqueja',
            blocked_by: 'Bloquejat per',
            related: 'Relacionat',
        },
        workflow: {
            current_step: 'Pas actual',
            transition: 'Transició',
            history: 'Historial de passos',
            no_workflow: 'Sense flux de treball assignat',
            detail_title: 'Detalls del flux de treball',
            list_title: 'Definicions de fluxos de treball',
            create_workflow: 'Crear flux de treball',
            browse_templates: 'Explorar plantilles',
            template_catalog_title: 'Catàleg de plantilles de flux de treball',
            name: 'Nom',
            description: 'Descripció',
            task_types: 'Tipus de tasques',
            steps: 'Passos',
            version: 'Versió',
            created: 'Creat',
            actions: 'Accions',
            status_draft: 'Esborrany',
            status_active: 'Actiu',
            status_archived: 'Arxivat',
            activate: 'Activar',
            archive: 'Arxivar',
            save: 'Desar',
            edit: 'Editar',
            delete: 'Eliminar',
            clone: 'Clonar',
            install: 'Instal·lar',
            installed: 'Instal·lat',
            system: 'Sistema',
            general_settings: 'Configuració general',
            default_assignment: 'Assignació per defecte',
            default_sla: 'SLA per defecte',
            workflow_steps: 'Passos del flux de treball',
            add_step: 'Afegir pas',
            step_name: 'Nom del pas',
            step_status: "Valor d'estat",
            assignment_strategy: "Estratègia d'assignació",
            assignment_role: "Rol d'assignació",
            sla_hours: 'Hores SLA',
            sla_calendar_type: 'Tipus de calendari',
            working_days: 'Dies laborables',
            calendar_days: 'Dies naturals',
            is_terminal: 'Pas terminal',
            required_fields: 'Camps obligatoris',
            checklist_template: 'Plantilla de llista de verificació',
            transitions: 'Transicions',
            add_transition: 'Afegir transició',
            from_step: 'Des de',
            to_step: 'Fins a',
            permission: 'Permís',
            sla_action: 'Acció SLA',
            notify: 'Notificar',
            escalation_rules: "Regles d'escalada",
            add_rule: 'Afegir regla',
            trigger: 'Disparador',
            threshold: 'Llindar',
            action: 'Acció',
            target_role: 'Rol objectiu',
            trigger_sla_threshold: 'Llindar SLA',
            trigger_sla_breach: 'Incompliment SLA',
            trigger_blocked_duration: 'Durada de bloqueig',
            trigger_manual: 'Manual',
            action_notify: 'Notificar',
            action_escalate: 'Escalar nivell',
            action_reassign: 'Reassignar',
            sla_start: 'Iniciar',
            sla_pause: 'Pausar',
            sla_resume: 'Reprendre',
            sla_stop: 'Aturar',
            strategy_manual: 'Manual',
            strategy_role_based: 'Basat en rol',
            strategy_round_robin: 'Round Robin',
            strategy_least_loaded: 'Menys carregat',
            strategy_creator: 'Creador',
            confirm_delete: 'Esteu segur que voleu eliminar aquest flux de treball?',
            confirm_archive: 'Esteu segur que voleu arxivar aquest flux de treball?',
            cannot_delete_active: 'Els fluxos de treball actius no es poden eliminar. Arxiveu-los primer.',
            validation_errors: 'Errors de validació del flux de treball',
            step_count: '{count} passos',
            no_workflows: "No s'han trobat fluxos de treball.",
            no_templates: 'No hi ha plantilles disponibles.',
            sr_templates_count: '{count} plantilles disponibles',
            template_installed: 'Plantilla instal·lada correctament.',
            activate_success: 'Flux de treball activat correctament.',
            archive_success: 'Flux de treball arxivat correctament.',
            save_success: 'Flux de treball desat correctament.',
            delete_success: 'Flux de treball eliminat correctament.',
            applicable_task_types: 'Tipus de tasca aplicables',
            steps_column: 'Passos',
            status_value_hint: "Clau d'estat del flux de treball utilitzada per les transicions. Lletres minúscules, números i guions baixos.",
            from_status: "Estat d'origen",
            to_status: 'Estat de destinació',
            required_permissions: 'Permisos requerits',
            transition_required_fields: 'Camps requerits (transició)',
            auto_assign: 'Assignació automàtica',
            auto_assign_role: "Rol d'assignació automàtica",
            threshold_percent: 'Llindar (%)',
            blocked_hours: 'Hores bloquejades',
            notify_roles: 'Notificar rols',
            escalate_to_role: 'Escalar al rol',
            notification_template: 'Plantilla de notificació',
            action_escalate_tier: 'Escalar nivell',
            create_modal_title: 'Crear flux de treball',
            create_modal_description: "Defineix passos, transicions i regles d'escalada. El flux de treball es crea com a esborrany.",
            create_modal_footer_note: "Creat com a esborrany. Activa'l quan el graf es validi.",
            validation_name_required: 'El nom del flux de treball és obligatori.',
            validation_task_types_required: 'Selecciona almenys un tipus de tasca aplicable.',
            validation_min_one_step: 'Afegeix almenys un pas.',
            validation_min_one_transition: 'Afegeix almenys una transició.',
            validation_step_name_required: 'El nom del pas és obligatori.',
            validation_status_value_required: "El valor de l'estat és obligatori.",
            validation_status_value_duplicate: "El valor de l'estat ha de ser únic entre els passos.",
            validation_transition_endpoints_required: "Selecciona tant un estat d'origen com un estat de destinació.",
            validation_transition_unknown_status: 'Aquesta transició fa referència a un estat que cap pas defineix.',
            validation_sla_positive: "Les hores d'SLA han de ser superiors a zero. Deixa-ho buit per no tenir SLA.",
            validation_threshold_percent_range: 'El llindar ha de ser entre 0 i 100.',
            validation_blocked_hours_positive: 'Les hores bloquejades han de ser superiors a zero.',
            warning_no_terminal_step: "Cap pas terminal. L'activació fallarà fins que un pas es marqui com a terminal.",
            warning_step_no_outgoing: 'Passos no terminals sense transicions de sortida: {statuses}',
            warning_unreachable_steps: 'Passos no accessibles des del primer pas: {statuses}',
            orphaned_transitions_warning: '{count} transicions fan referència a un pas eliminat.',
            remove_orphaned_transitions: 'Elimina les transicions òrfenes',
            transitions_renamed_announcement: "{count} transicions actualitzades al nou valor d'estat.",
            add_checklist_item: 'Afegeix element de llista de verificació',
            checklist_item_label: 'Element de la llista de verificació',
        },
        columns: {
            priority: 'Prioritat',
            title: 'Títol',
            status: 'Estat',
            type: 'Tipus',
            assignees: 'Assignat(s)',
            due_date: 'Data de venciment',
            source: 'Origen',
            actions: 'Accions',
        },
        groups: {
            overdue: 'Endarrerit',
            due_today: 'Venç avui',
            due_this_week: 'Venç aquesta setmana',
            due_later: 'Venç més tard',
            no_due_date: 'Sense data de venciment',
            closed: 'Completades o cancel·lades',
        },
        board: {
            title: 'Tauler de tasques',
            drag_hint: "Arrossegueu tasques entre columnes per canviar l'estat",
            empty_column: 'Sense tasques',
            keyboard_hint: 'Teclat: fletxes per navegar, espai per agafar una tasca, fletxes esquerra/dreta per seleccionar columna, Enter per deixar, Escape per cancel·lar.',
            sr_moving: 'Movent {title}. Sobre {column}. Premeu Enter per deixar, Escape per cancel·lar.',
            sr_tasks_shown: '{count} tasques mostrades',
            sr_picked_up: "S'ha agafat {title}",
            sr_returned: '{title} ha tornat a {column}',
            sr_dropped: "S'ha deixat {title} a {column}, posició {position} de {total}",
            sr_move_cancelled: 'Moviment cancel·lat',
            sr_over_column: 'Sobre {column}',
            sr_moved_to: '{title} mogut a {column}',
            invalid_transition: 'No es pot moure la tasca de {from} a {to}.',
            terminal_status: 'Aquesta tasca esta en un estat terminal i no es pot moure.',
            invalid_drop_target: 'Destinacio no valida',
        },
        team: {
            title: "Càrrega de treball de l'equip",
            assignee: 'Assignat',
            active: 'Actiu',
            overdue: 'Endarrerit',
            blocked: 'Bloquejat',
            completed_7d: 'Completat (7d)',
            load: 'Càrrega',
            load_high: 'Alta',
            load_medium: 'Mitjana',
            load_normal: 'Normal',
        },
        graph: {
            title: 'Gràfic de dependència',
            list_view: 'Vista de llista',
            graph_view: 'Vista gràfica',
            zoom_in: 'Apropa',
            zoom_out: 'Allunya el zoom',
            reset_zoom: 'Restableix',
            blocks_label: 'blocks',
            related_label: 'related',
            no_dependencies: 'Sense dependències per visualitzar.',
        },
        add_dependency_modal: {
            title: 'Afegeix dependència',
            search_placeholder: 'Cerca tasques per títol o identificador...',
            no_results: "No s'han trobat tasques.",
            this_blocks: 'Aquesta tasca bloqueja la selecció',
            this_blocked_by: 'Aquesta tasca està bloquejada per seleccionada',
            related: 'Relacionats',
            max_dependencies: 'Màxim 10 dependències per tasca.',
            circular_error: 'No es pot afegir: crearia una dependència circular.',
        },
        conflict_detection: {
            title: 'Detecció de conflictes',
            unresolved: 'sense resoldre',
            no_conflicts: "No s'han detectat conflictes",
            section: 'Secció',
            field: 'Camp',
            dept_a: 'Departament A',
            value_a: 'Valor A',
            dept_b: 'Departament B',
            value_b: 'Valor B',
            severity: 'Gravetat',
            status: 'Estat',
            resolved: 'Resolt',
            open: 'Obert',
            resolve: 'Resoldre',
            load_failed: 'Error en load conflicts',
            resolve_success: 'Conflicte resolt correctament',
            resolve_failed: 'Error en resolve conflict',
            severity_critical: 'Crític',
            severity_warning: 'Avís',
            severity_info: 'Informació',
        },
        consolidation: {
            title: 'Progrés de consolidació',
            add_child: 'Afegir tasca filla',
            child_task_id: 'ID de la tasca filla',
            department_id: 'ID del departament',
            link: 'Vincular',
            overall_completion: 'Compleció general',
            departments_complete: 'Departaments completats',
            overdue: 'Vençut',
            ready: 'Preparat',
            not_ready: 'No preparat',
            master_status: 'Estat principal',
            department_breakdown: 'Desglossament per departaments',
            department: 'Departament',
            status: 'Estat',
            completion: 'Compleció',
            due_date: 'Data de venciment',
            overdue_flag: 'Vençut',
            child_tasks: 'Tasques filles',
            task: 'Tasca',
            load_failed: 'Error en load consolidation data',
            child_linked: 'Tasca filla vinculada correctament',
            link_failed: 'Error en link child task',
            confirm_unlink: 'Esteu segur que voleu desvincular aquesta tasca filla?',
            child_unlinked: 'Tasca filla desvinculada',
            unlink_failed: 'No s’ha pogut desvincular la tasca filla',
            unlink: 'Desvincular',
            status_completed: 'Completat',
            status_in_progress: 'En curs',
            status_pending: 'Pendent',
            select_child_task: 'Seleccionar subtasca',
            select_department: 'Seleccionar departament',
        },
        section_assignments: {
            title: 'Assignacions de secció',
            assign_section: 'Assignar secció',
            section_ref: 'Ref. de secció',
            section_label: 'Etiqueta de secció',
            department_id: 'ID del departament',
            department: 'Departament',
            assignee_id: "ID de l'assignat",
            assignee: 'Cesionari',
            mandatory: 'Obligatori',
            assign: 'Assignar',
            no_assignments: 'Sense assignacions de secció',
            status: 'Estat',
            completion: 'Compleció',
            mark_complete: 'Completar',
            load_failed: 'Error en load section assignments',
            assign_success: 'Secció assignada correctament',
            assign_failed: 'Error en assign section',
            status_updated: 'Estat actualitzat',
            status_update_failed: 'Error en actualitzar estat',
            section_ref_placeholder: 'p. ex. 3.2',
            status_completed: 'Completat',
            status_in_progress: 'En curs',
            status_pending: 'Pendent',
            select_department: 'Seleccioneu el departament',
            select_assignee: 'Seleccioneu el cedit',
        },
        overdue_columns: {
            days_overdue: 'Dies de retard',
        },
        regulatory_info: {
            deadline_context: 'En crear tasques DPIA, les dates de venciment han de coincidir amb els terminis de presentacio PDPL: entrada departamental = 10 dies laborables. Els terminis de ciberseguretat i telecomunicacions tambe apliquen.',
            overdue_deadline_context: 'Terminis reguladors de dades de paquet: entrada departamental PDPL = 10 dies laborables, resposta complementaria = 15 dies laborables, reenviament = 10 dies laborables. Les tasques amb SLA incomplert es distingeixen visualment.',
            section_assignment_context: 'Flux DPIA multidepartamental (PDPL-P1): el DPO assigna seccions del dossier a departaments. Termini d entrada departamental: 10 dies laborables.',
            template_catalog_context: 'Les plantilles del sistema cobreixen processos especifics del marc: DPIA PDPL, certificacio de Llei de Dades, resposta a incidents de ciberseguretat, sol-licituds d autoritat de telecomunicacions i registre de sistemes d IA. Les plantilles del sistema no son editables.',
            workflow_instantiation_context: 'Les plantilles instal-lades es poden instanciar en fluxos de treball actius. Les tasques es creen automaticament amb dates de venciment calculades.',
        },
        overdue: {
            days_overdue: 'dies vençuts',
        },
    },
    notifications: {
        bell: {
            title: 'Notificacions',
            unread_count: 'unread',
        },
        drawer: {
            title: 'Notificacions',
            mark_all_read: 'Marca-ho tot com a llegit',
            no_notifications: 'No hi ha notificacions',
            filter: {
                all: 'All',
                unread: 'Sense llegir',
                tasks: 'Tasques',
                approvals: 'Aprovacions',
                alerts: 'Alertes',
            },
        },
        preferences: {
            title: 'Preferències de notificació',
            description: 'Configureu com rebeu les notificacions',
            category: {
                label: 'Categoria',
                task_assigned: 'Tasca assignada',
                task_sla_approaching: "SLA S'apropa",
                task_overdue: 'Tasca vençuda',
                task_completed: 'Tasca completada',
                task_escalated: 'Tasca escalada',
                workflow_transition: 'Transició de flux de treball',
                approval_requested: 'Aprovació sol·licitada',
                comment_added: 'Comentari afegit',
                weekly_digest: 'Resum setmanal',
                sla_breach_report: "SLA Informe d'incompliment",
                system_alert: 'Alerta del sistema',
                // English until Phase 25 (R-57).
                rights_request_received: 'New Rights Request',
                // English until Phase 25 (R-57).
                acknowledgment_reminder: 'Acknowledgment Reminder',
                approval_expired: 'Approval Request Expired',
                scheduled_export_delivered: 'Scheduled Export Delivered',
                cross_border_transfer_added: 'Cross-Border Transfer Added',
            },
            channel: {
                platform: 'Plataforma',
                email: 'Email',
            },
            quiet_hours: {
                title: 'Hores tranquil·les',
                start: 'Iniciar',
                end: 'End',
                timezone: 'Zona horària',
            },
            digest: {
                title: 'Freqüència de digestió',
                daily: 'Diàriament',
                weekly: 'Setmanalment',
                none: 'Cap',
            },
            watched: {
                title: 'Entitats vigilades',
                empty: 'No hi ha entitats observades',
            },
            save: 'Desa les preferències',
            saving: "S'està desant...",
            saved_success: "Les preferències s'han desat correctament",
            load_error: "No s'han pogut carregar les preferències desades. Mostrant els valors predeterminats: els canvis es desaran quan feu clic a Desa.",
            enabled: 'Habilitat',
            disabled: 'Inhabilitat',
            unwatch: 'Deixa de mirar',
            announce_loaded: 'Notification preferences loaded',
            save_error: 'Could not save your notification preferences. Your changes are still on the page; try saving again.',
        },
        admin_queue: {
            title: "Cua d'administració de notificacions",
            description: "Supervisar l'estat de lliurament de correus i reintentar missatges fallits",
            load_error: "No s'han pogut carregar les dades de la cua de correu",
            retry_error: "No s'ha pogut reintentar el correu",
            filter_status: 'Filtrar per estat',
            all_statuses: 'Tots',
            refresh: 'Actualitzar',
            status_queued: 'En cua',
            status_sent: 'Enviat',
            status_failed: 'Fallit',
            status_bounced: 'Retornat',
            status_sending: 'Sending',
            col_recipient: 'Destinatari',
            col_category: 'Categoria',
            col_template: 'Plantilla',
            col_status: 'Estat',
            col_retries: 'Reintents',
            col_created: 'Creat',
            col_sent: 'Enviat el',
            col_actions: 'Accions',
            no_items: "No s'han trobat elements a la cua de correu",
            retry: 'Reintentar',
            retrying: 'Reintentant...',
            prev: 'Anterior',
            next: 'Següent',
            access_denied_title: "Accés d'administrador requerit",
            access_denied: 'Aquesta pàgina només està disponible per a administradors. Contacteu amb el vostre administrador si necessiteu accés.',
            announce_queue_loaded: "S'ha carregat la cua de correu electrònic, {count} elements",
            announce_processed: "S'han processat {count} correus electrònics",
            process_now: 'Processa ara',
            processing_ellipsis: "S'està processant...",
        },
    },
    ui_assist: {
        toolbar: {
            title: "Assistència d'IU",
            activate: 'Activar',
            deactivate: 'Desactivar',
            connected: 'Connectat',
            disconnected: 'Desconnectat',
            disconnected_hint: 'Desconnectat: executeu `npm run relay` per connectar-vos a Claude CLI',
            open_panel: 'Obriu el tauler UI Assist',
            history: 'Història',
            settings: 'Configuració',
        },
        prompt: {
            placeholder: 'Descriu el canvi que vols fer...',
            send: 'Enviar',
            cancel: 'Cancel·la',
            char_count: '{count} / {max}',
        },
        session: {
            streaming: "S'està processant...",
            complete: 'Completa',
            error: 'Error',
            abort: 'Avortar',
            abort_confirm: 'Esteu segur que voleu avortar?',
            aborted_hint: "La sessió va ser interrompuda — pots continuar des d'aquí.",
            undo: 'Desfer',
            redo: 'Refer',
            retry: 'Torna-ho a provar',
            dismiss: 'Descartar',
        },
        label: {
            component: 'Component',
            element: 'Element',
            file: 'Fitxer',
        },
        response: {
            you: 'You',
            claude: 'Claude',
            empty: 'Encara no hi ha missatges. Seleccioneu elements i envieu una sol·licitud per començar.',
        },
        panel: {
            tab_conversation: 'Xatejar',
            tab_tasks: 'Tasques',
            tab_sprint: 'Sprint',
            tab_sessions: 'Sessions',
            tab_lobby: 'Vestíbul',
            follow_up: 'Seguiment',
            followup_placeholder: 'Fes una pregunta de seguiment...',
            interrupt_placeholder: 'Escriu per interrompre i redirigir…',
            send_interrupt: 'Envia i Interromp',
            will_interrupt: 'Interromprà la resposta actual i enviarà això en el seu lloc',
            expand: 'Ampliar a pantalla completa',
            collapse: 'Reduir al panell lateral',
            no_sessions: 'No hi ha sessions anteriors. Seleccioneu elements i envieu una instrucció per començar.',
            session_active: 'Activa',
            session_ended: 'Finalitzada',
            session_messages: '{count} missatges',
            switch_session: 'Canviar a aquesta sessió',
            load_session: "Carregueu l'historial de sessions",
            session_restored: 'Sessió restaurada de la història',
            archive_session: "Sessió d'arxiu",
            archive_confirm: "Arxiu aquesta sessió? El fitxer de sessió s'eliminarà.",
            new_session: 'Nova sessió',
            new_session_desc: "Utilitzeu Activa per seleccionar un element d'IU i iniciar una sessió nova",
            start_new_session: 'Comença una nova sessió',
            no_active_session_chat: "Inicieu una sessió nova o utilitzeu Activa per seleccionar un element d'IU a la pàgina per començar.",
            no_active_session_tasks: 'Inicieu una sessió nova o seleccioneu una sessió existent per crear tasques.',
            no_active_session_sprint: 'Inicia una sessió nova o selecciona una sessió existent per planificar un sprint.',
            previous_sessions: 'Sessions prèvies',
        },
        tasks: {
            none_found: "No s'han trobat tasques a la resposta. Demaneu a Claude que suggereixi tasques.",
            selected: 'selected',
            create_selected: 'Crear tasques',
            execute_tasks: 'Executar tasques',
            creating: "S'està creant…",
            created: 'tasques creades',
            create_error: "No s'ha pogut crear la tasca",
            plan_sprint: 'Planifica Sprint',
            priority_critical_desc: 'Bloquejant — corregir immediatament',
            priority_high_desc: 'Important — resoldre en aquest sprint',
            priority_medium_desc: 'Normal — planificar per al proper sprint',
            priority_low_desc: 'Desitjable — candidat per al backlog',
            add_custom: 'Afegir tasca',
            add_title_placeholder: 'Títol de la tasca…',
            add_desc_placeholder: 'Descripció (opcional)',
            add_save: 'Afegir',
            add_cancel: 'Cancel·lar',
        },
        sprint: {
            title: 'Planificador de Sprint',
            assigned: 'assigned',
            new_placeholder: 'Nom de Sprint (p. ex., Sprint 18.1.0)',
            add: 'Add',
            backlog: 'Endarreriment',
            move_to: 'Mou a...',
            empty: 'Creeu un grup de sprint i assigneu-li tasques.',
            create_all: 'Crear totes les tasques',
            remove: 'Eliminar',
            drop_here: "Assigna tasques de l'endarreriment a continuació",
            add_task: "Afegeix una tasca de l'endarreriment...",
        },
        feature_disabled: 'UI Assist no està activat',
        permission_denied: 'No teniu permís per utilitzar UI Assist',
    },
    license: {
        title: 'Paquet i llicència',
        current_license: 'Llicència actual',
        license_key: 'Clau de llicència',
        activate: 'Activa la llicència',
        deactivate: 'Desactiva la llicència',
        validate_now: 'Valida ara',
        status: 'Estat de la llicència',
        active: 'Actius',
        expired: 'Caducat',
        suspended: 'Suspès',
        invalid: 'No vàlid',
        pending: 'Pendent',
        no_license: 'Sense llicència',
        expires_at: 'Caduca',
        last_validated: 'Última validació',
        domain: 'Domini amb llicència',
        activation_success: "La llicència s'ha activat correctament.",
        activation_failed: "L'activació de la llicència ha fallat.",
        domain_mismatch: 'El domini de llicència no coincideix amb el domini actual.',
        deactivation_success: 'Llicència desactivada.',
        validation_success: 'Llicència validada correctament.',
        validation_failed: 'La validació de la llicència ha fallat.',
        enter_key: 'Introduïu la vostra clau de llicència',
        confirm_deactivate: "Esteu segur que voleu desactivar aquesta llicència? Les restriccions del mòdul s'aplicaran en funció del nivell predeterminat.",
        announce_loaded: 'License settings loaded',
        verification_stale: 'This licence has not been verified with the licence service for more than {days} days, so it reads as unverified. It reads as valid again once a revalidation succeeds.',
        scope_installation: 'The licence and its package apply to the whole installation: every organisation on it. Only a platform administrator can activate, deactivate or change them.',
    },
    package_mgmt: {
        title: 'paquet',
        module_column: 'Mòdul',
        current_tier: 'Paquet actual',
        light: 'Llum',
        standard: 'Estàndard',
        enterprise_unlimited: 'Empresa il·limitada',
        change_package: 'Canvia de paquet',
        upgrade: 'Actualitza',
        downgrade: 'Baixa la categoria',
        target_tier: 'Paquet objectiu',
        new_license_key: 'Clau de llicència per al paquet Target',
        confirm_change: 'Confirmeu el canvi de paquet',
        change_success: 'El paquet ha canviat correctament.',
        change_failed: 'El canvi de paquet ha fallat.',
        modules_included: 'Mòduls inclosos',
        modules_restricted: 'No disponible al paquet actual',
        upgrade_to_enable: 'Actualitza per habilitar aquest mòdul.',
        module_availability: 'Disponibilitat del mòdul',
        comparison: 'Comparació de paquets',
        power_users: 'Usuaris poderosos',
        unlimited: 'Il·limitat',
        impact_warning: 'La baixada desactivarà els mòduls següents:',
        invalid_license: 'La clau de llicència proporcionada no és vàlida per a aquest paquet.',
        tier_mismatch: 'La clau de llicència és per a un nivell de paquet diferent.',
    },
    board_report: {
        title: 'Informe de Compliment per al Consell',
        subtitle: 'Resum executiu de compliment en tots els mòduls',
        date_from: 'Data des de',
        date_to: 'Data fins a',
        language: "Idioma de l'informe",
        generate: 'Generar informe',
        generating: 'Generant informe...',
        generate_requires_export: 'Generating a report needs the permission to export governance reports. Ask an administrator to grant it.',
        download_pdf: 'Descarregar PDF',
        close: 'Tancar informe',
        open_board_report: 'Generar informe del consell',
        close_board_report: 'Tancar informe del consell',
        report_period: "Període de l'informe",
        generated_at: 'Generat el',
        generated_by: 'Generat per',
        organization: 'Organització',
        section_rights_requests: 'Sol·licituds de drets',
        section_vendors: 'Proveïdors',
        section_dpias: 'AIPD',
        section_consent: 'Polítiques de consentiment',
        section_data_mapping: 'Mapeig de dades',
        section_incidents: 'Incidències',
        section_classification: 'Classificació de dades',
        section_governance: 'Governança',
        open: 'Obert',
        closed: 'Tancat',
        sla_compliance: 'Compliment SLA',
        total: 'Total',
        expired_dpa: 'DPA vençut',
        high_risk: 'Alt risc',
        completed: 'Completat',
        overdue: 'Vençut',
        policies_active: 'Polítiques actives',
        coverage: 'Cobertura',
        systems: 'Sistemes',
        completeness: 'Completesa',
        avg_resolution_days: 'Mitjana resolució (dies)',
        labeled: 'Etiquetat',
        unlabeled_systems: 'Sistemes sense etiquetar',
        compliance_score: 'Puntuació de compliment',
        drift_events_open: 'Desviacions obertes',
        no_data: 'No hi ha dades disponibles per al període seleccionat.',
        error_date_range: "Seleccioneu la data d'inici i la data de fi.",
        table_category: 'Categoria',
        table_metric: 'Mètrica',
        table_value: 'Valor',
        showing_chart_view: 'Mostrant vista de gràfic',
        showing_table_view: 'Mostrant vista de taula de dades',
        error_generate: "No s'ha pogut generar l'informe del consell.",
        playbook_evidence_title: "Evidència d'execució de llibres de jugades",
        playbook_evidence_description: "Activitat d'execució per llibre de jugades i fitxers d'evidència adjunts per al període del report.",
        playbook_name: 'Llibre de jugades',
        playbook_execution_count: 'Execucions',
        playbook_steps_total: 'Passos',
        playbook_steps_required: 'Evidència requerida',
        playbook_steps_attached: 'Evidència adjunta',
        playbook_coverage_pct: 'Cobertura',
        playbook_evidence_files: "Fitxers d'evidència",
        playbook_no_evidence: 'No hi ha execucions de llibres de jugades en aquest període.',
        playbook_download_file: 'Descarregar fitxer',
    },
    framework_module_links: {
        title: 'Vinculació de Marc Regulatori',
        modules: 'Mòduls Vinculats',
        add_module: 'Afegir Mòdul',
        module_code: 'Codi de Mòdul',
        required: 'Obligatori',
        notes: 'Notes',
        link: 'Vincular',
        no_modules: 'No hi ha mòduls vinculats',
        personas: 'Persones Vinculades',
        add_persona: 'Afegir Persona',
        persona_id: 'ID de Persona',
        role_description: 'Descripció del Rol',
        no_personas: 'No hi ha persones vinculades',
    },
    framework_channels: {
        title: 'Canals de Presentació & Terminis',
        channels: 'Canals de Presentació',
        add_channel: 'Afegir Canal',
        channel_type: 'Tipus',
        name: 'Nom',
        instructions: 'Instruccions',
        active: 'Actiu',
        no_channels: 'No hi ha canals de presentació configurats',
        portal: 'Portal',
        in_person: 'En Persona',
        postal: 'Postal',
        email: 'Correu electrònic',
        add: 'Afegir',
        deadlines: 'Terminis Regulatoris',
        add_deadline: 'Afegir Termini',
        label: 'Etiqueta',
        days: 'Dies',
        day_type: 'Tipus de Dia',
        calendar_days: 'Dies Naturals',
        working_days: 'Dies Hàbils',
        description: 'Descripció',
        no_deadlines: 'No hi ha terminis configurats',
    },
    regulatory_updates: {
        title: 'Actualitzacions Regulatòries & Tauler de Marc',
        coverage_dashboard: 'Tauler de Cobertura de Marc',
        no_frameworks: "No s'han trobat marcs",
        framework: 'Marc Regulatori',
        status: 'Estat',
        modules: 'Mòduls',
        templates: 'Plantilles',
        forms: 'Formularis',
        channels: 'Canals',
        deadlines: 'Terminis',
        pending: 'Actualitzacions Pendents',
        updates: 'Actualitzacions Regulatòries',
        create_update: 'Crear Actualització',
        framework_id: 'ID de Marc',
        update_title: 'Títol',
        description: 'Descripció',
        effective_date: 'Data de Vigència',
        create: 'Crear',
        no_updates: 'No hi ha actualitzacions regulatòries',
        impact: 'Impacte',
        assess: 'Avaluar',
    },
    module_tips: caModuleTips,
    framework_rights: {
        be_informed: 'Dret a ser informat',
        consent: 'Dret al consentiment',
        refuse_consent: 'Dret a refusar el consentiment',
        withdraw_consent: 'Dret a retirar el consentiment',
        access: "Dret d'accés",
        rectification: 'Dret de rectificació',
        data_provision: 'Dret a la provisió de dades',
        deletion: 'Dret de supressió',
        restriction_of_processing: 'Dret a la limitació del tractament',
        object_to_processing: "Dret d'oposició al tractament",
        complaint: 'Dret a presentar una reclamació',
        denunciation: 'Dret de denúncia',
        lawsuit: 'Dret a exercir accions legals',
        claim_damages: 'Dret a reclamar danys i perjudicis',
        request_protection_measures: 'Dret a sol·licitar mesures de protecció',
    },
    framework_content: {
        EU_EPRIVACY_DIRECTIVE: {
            display_name: 'Directiva 2002/58/CE (Directiva sobre la privadesa electrònica)',
            description: "Directiva matriu per a les transposicions nacionals de la Directiva ePrivacy. Cobreix les galetes, la privadesa de les comunicacions electròniques i el màrqueting directe. Cada estat membre l'ha de transposar al seu dret intern.",
        },
        EU_GDPR: {
            display_name: 'Reglament General de Protecció de Dades (UE) 2016/679',
            description: "El Reglament General de Protecció de Dades (RGPD) és un reglament de la Unió Europea sobre la protecció de dades i la privadesa. S'aplica directament a tots els estats membres de la UE i de l'EEE.",
        },
        EU_NIS2_DIRECTIVE: {
            display_name: 'Directiva (UE) 2022/2555 (NIS2)',
            description: "Directiva matriu per a les transposicions nacionals de la NIS2. Cobreix la gestió del risc de ciberseguretat, la notificació d'incidents i la seguretat de la cadena de subministrament per a les entitats essencials i importants.",
        },
        VN_AI_LAW_2026: {
            display_name: "Llei d'IA del Vietnam (134/2025/QH15) — Vigent des de l'1 de març de 2026",
            description: "Llei d'IA del Vietnam (Luật Trí tuệ nhân tạo 134/2025/QH15) que regula el desenvolupament, el desplegament i l'ús de sistemes d'intel·ligència artificial. Estableix la classificació de riscos, els deures de transparència i etiquetatge, els requisits de notificació d'incidents, les obligacions d'avaluació d'impacte i un mecanisme de proves controlades (sandbox) per a les activitats d'IA al Vietnam.",
        },
        VN_AI_DECREE_142_2026: {
            display_name: 'Decree 142/2026/NĐ-CP — AI Law Implementation',
            description: 'Decree 142/2026/NĐ-CP (Nghị định số 142/2026/NĐ-CP) detailing certain articles and implementing measures of the Vietnam AI Law (Law 134/2025/QH15). Issued 2026-04-30, effective 2026-05-01. Lead authority: Ministry of Science and Technology. Operationalises AI risk classification, classification dossier, reclassification review, high-risk conformity assessment, high-risk risk-management system, transparency / explanation / technical marking / visible labeling, serious AI incident reporting (72h / 5wd / 15d), state-agency AI impact assessment (Mẫu AI02), and the controlled testing / AI sandbox lifecycle (Mẫu AI03a-AI09b). Submission via the AI one-stop e-portal or MoST transitional channel. No statutory fines.',
        },
        VN_CYBERSEC_LAW: {
            display_name: 'Llei de Ciberseguretat del Vietnam (era 24/2018)',
            description: "Llei de Ciberseguretat del Vietnam (Luật An ninh mạng, Llei núm. 24/2018/QH14) que estableix els requisits de ciberseguretat, localització de dades i notificació d'incidents. Vigent fins a ser substituïda per la Llei 116/2025/QH15 amb efectes a partir de l'1 de juliol de 2026.",
        },
        VN_CYBERSEC_DECREE_53: {
            display_name: 'Decret 53 — Aplicació de la Llei de Ciberseguretat',
            description: "Decret 53/2022/NĐ-CP que desenvolupa la Llei de Ciberseguretat (Luật An ninh mạng), cobreix la determinació de l'àmbit d'aplicació, els supòsits de localització de dades, els requisits de presència local i els fluxos de cooperació amb les autoritats.",
        },
        VN_CYBERSEC_LAW_2025: {
            display_name: 'Llei de Ciberseguretat (116/2025/QH15)',
            description: "Llei de Ciberseguretat consolidada del Vietnam (Luật An ninh mạng 116/2025/QH15), que substitueix la Llei 24/2018/QH14, vigent des de l'1 de juliol de 2026. Consolida l'àmbit del Decret 53/2022/NĐ-CP (vegeu VN_CYBERSEC_DECREE_53).",
        },
        VN_DATA_LAW: {
            display_name: 'Llei de Dades del Vietnam',
            description: "Llei de Dades del Vietnam (Luật Dữ liệu, Llei 60/2024/QH15) que estableix requisits integrals de governança de dades, incloent-hi l'inventari de dades, la classificació, la governança de l'intercanvi i la governança de transferències transfrontereres de dades. Es solapa amb VN_PDPL_LAW_2025 (aspectes de dades personals) i VN_CYBERSEC_LAW_2025 (localització/classificació de dades).",
        },
        VN_DATA_LAW_DECREE_165_2025: {
            display_name: 'Decret 165/2025/NĐ-CP (Aplicació de la Llei de Dades)',
            description: "Decret 165/2025/NĐ-CP que desenvolupa la Llei de Dades del Vietnam (Luật Dữ liệu 60/2024/QH15). Proporciona els formularis oficials per a l'avaluació d'impacte de transferències transfrontereres de dades, l'informe anual d'avaluació de riscos i l'accés a la base de dades nacional. Defineix 11 formularis oficials Mẫu số.",
        },
        VN_DATA_EXCHANGES_DRAFT_2026: {
            display_name: 'Projecte de Decret sobre Operacions de Borses de Dades 2026',
            description: "Projecte de decret sobre l'operació de plataformes d'intercanvi de dades (Dự thảo Nghị định quy định về hoạt động của sàn dữ liệu). Regula la classificació de plataformes, l'elegibilitat dels participants, els controls de catalogació de productes i serveis de dades, les proves en sandbox, el flux de transaccions, la contractació electrònica, els certificats de drets de propietat sobre les dades i la gestió de queixes/disputes. Actualment en consulta pública; encara no promulgat. Cita la Llei de Dades 60/2024/QH15, la Llei de Protecció de Dades Personals 91/2025/QH15, la Llei de Ciberseguretat 116/2025/QH15 i la Llei de Transaccions Electròniques 20/2023/QH15.",
        },
        VN_ECOMMERCE_LAW_2025: {
            display_name: 'Llei de Comerç Electrònic (122/2025/QH15)',
            description: 'Llei de Comerç Electrònic del Vietnam (Luật Thương mại điện tử 122/2025/QH15) que estableix un règim nacional unificat per al comerç electrònic, abastant operacions de plataformes nacionals i transfrontereres, governança i responsabilitat de plataformes, protecció del consumidor, controls sobre venedors i afiliats, gestió de queixes, moderació de continguts i execució de retirades, i la preparació dels procediments administratius per al sistema estatal de gestió de comerç electrònic.',
        },
        VN_PDPD_DECREE_13: {
            display_name: 'Decret 13/2023/NĐ-CP',
            description: "Decret 13/2023/NĐ-CP, l'instrument operatiu per als deures del PDPL incloent-hi la presentació d'expedients, els artefactes de governança, la mecànica transfronterera i els procediments administratius del MoPS (Bộ Công an). Va deixar de tenir efectes l'1 de gener de 2026 en virtut de l'article 42.2 del Decret 356/2025/NĐ-CP; la Llei 91/2025/QH15 regula ara la protecció de dades personals.",
        },
        VN_PDPL_LAW_2025: {
            display_name: 'Llei de Protecció de Dades Personals (91/2025/QH15)',
            description: "Llei de Protecció de Dades Personals del Vietnam (Luật Bảo vệ dữ liệu cá nhân 91/2025/QH15) que estableix un marc integral de protecció de dades personals, vigent des de l'1 de gener de 2026. Cobreix els temes de compliment A–J: bases lícites de tractament, consentiment per a dades sensibles, drets de l'interessat (accés/rectificació/supressió/restricció/oposició/portabilitat), notificació de violacions a l'autoritat i a les persones afectades, DPIA per a tractaments d'alt risc, governança de transferències transfrontereres, designació del DPO, registres d'activitats de tractament i obligacions contractuals dels encarregats. Obligacions de presentació davant l'autoritat a través del MoPS (Bộ Công an) (VN_PDPL_ADMIN_PROCEDURES_778_2026). Referències creuades: VN_PDPL_DECREE_356_2025 (decret de desenvolupament), VN_CYBERSEC_LAW_2025 (solapaments en ciberseguretat), VN_DATA_LAW (governança de dades).",
        },
        VN_PDPL_DECREE_356_2025: {
            display_name: 'Decret 356/2025/NĐ-CP (Aplicació del PDPL)',
            description: "Decret 356/2025/NĐ-CP que desenvolupa la Llei de Protecció de Dades Personals (Llei 91/2025/QH15). Cobreix la presentació d'expedients, els artefactes de governança, la mecànica transfronterera i la certificació de proveïdors de serveis. Inventari de formularis annexos: Phụ lục I–VIII (avaluació d'impacte, transferència transfronterera, certificació). Procediments administratius del MoPS via VN_PDPL_ADMIN_PROCEDURES_778_2026 (Quyết định 778/QĐ-BCA-A05). Flux de certificació: sol·licitud → emissió → sol·licitud de canvi → reemissió → revocació. Referències creuades: VN_PDPL_LAW_2025 (llei matriu), VN_CYBERSEC_LAW_2025 (localització de dades), VN_DATA_LAW (solapaments amb la governança de dades).",
        },
        VN_PDPL_ADMIN_PROCEDURES_778_2026: {
            display_name: 'Procediments Administratius A05 del MPS (Quyết định 778/QĐ-BCA-A05)',
            description: "Quyết định 778/QĐ-BCA-A05 (febrer de 2026) — Procediments administratius per a la protecció de dades personals sota A05 (MPS, Bộ Công an). Defineix els procediments de presentació, tramitació i emissió de resultats d'expedients, incloent-hi els formularis DLCN-HC i els formularis estatutaris Mẫu số.",
        },
        VN_PDPL_ENFORCEMENT_DRAFT_2026: {
            display_name: 'Projecte de Decret Sancionador 2026',
            description: 'Projecte de decret sancionador i de règim sancionador per a les infraccions de la Llei de Protecció de Dades Personals (Llei 91/2025/QH15). Defineix els trams de sancions, els factors agreujants/atenuants i els procediments sancionadors. Actualment en estat de projecte, pendent de promulgació.',
        },
        VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: {
            display_name: 'Government Resolution NQ22/2026 — MoPS Administrative Simplification (Temporary Overlay)',
            description: 'Temporary procedural overlay (effective 2026-04-29, expiring 2027-03-01) introducing three official channels (National Public Service Portal, direct submission to MoPS, postal submission to MoPS) for PDPL Decree 356/2025 and Decision 778/2026 dossier submissions. Email is NOT a NQ22 official channel — a separate unofficial email-ZIP submission guidance (VN_PDPL_GUIDANCE_EMAIL_ZIP_DOSSIER_SUBMISSION_2026) is shipped with requires_legal_review: true. Procedural overlay only — does NOT alter substantive obligations, fine ranges, or the binding force of underlying frameworks. Review task scheduled 2027-02-01.',
        },
        VN_TELECOM_LAW_2023: {
            display_name: 'Llei de Telecomunicacions del Vietnam 2023',
            description: "Llei de Telecomunicacions del Vietnam 2023 (Luật Viễn thông), una capa sectorial per a empreses de telecomunicacions/ISP/plataformes amb obligacions de confidencialitat, control de divulgació i gestió de sol·licituds d'autoritats. Referències creuades: VN_CYBERSEC_LAW_2025 (seguretat dels sistemes d'informació), VN_PDPL_LAW_2025 (tractament de dades d'usuaris), VN_DATA_LAW (governança de dades).",
        },
        VN_TELECOM_DECREE_163_2024: {
            display_name: 'Decret 163/2024/NĐ-CP (Aplicació de la Llei de Telecomunicacions)',
            description: 'Decret 163/2024/NĐ-CP que desenvolupa la Llei de Telecomunicacions (Luật Viễn thông 24/2023/QH15). Proporciona els formularis oficials per a la notificació de serveis de telecomunicacions, llicències, registre i plans de desenvolupament de centres de dades. Defineix 7 formularis oficials Mẫu số.',
        },
    },
    framework_requirements: {
        vn_dp_draft_f: {
            title: 'Queixes, disputes i gestió del risc',
            description: "Els operadors de plataforma han de proporcionar la recepció de queixes, un flux de gestió de disputes amb preservació d'evidències, controls d'identificació de riscos i d'alerta primerenca, i una revisió periòdica dels riscos de privadesa, ciberseguretat i gestió d'accessos.",
        },
        vn_dp_draft_e: {
            title: 'Flux de transacció i certificat de drets de propietat sobre les dades',
            description: "Les transaccions requereixen el registre del compte, l'aprovació del llistat, la contractació electrònica, la confirmació de pagament, el registre de lliurament i l'emissió d'un certificat de drets de propietat sobre les dades quan procedeixi. L'historial de transaccions i la traçabilitat s'han de preservar en un registre de transaccions.",
        },
        vn_dp_draft_d: {
            title: "Controls del sandbox i de l'entorn de proves",
            description: "Els entorns de prova han d'estar aïllats, amb accés restringit, monitoratge de seguretat i revisats abans d'extreure cap sortida. No es pot extreure cap dada d'origen de l'entorn de proves, i el tancament/neteja de les sessions de prova s'ha de registrar.",
        },
        vn_dp_draft_c: {
            title: 'Controls del llistat de productes i serveis de dades',
            description: "Els llistats han d'estar acompanyats d'evidència d'origen lícit i de drets de propietat/ús, registres de validació tècnica, declaracions de qualitat i registres de revisió i aprovació del llistat per part de la plataforma. Les dades prohibides s'han de filtrar abans de la publicació.",
        },
        vn_dp_draft_b: {
            title: 'Elegibilitat i incorporació dels participants',
            description: "Els operadors de plataforma han de verificar l'estatus jurídic, la capacitat jurídica i els requisits de negoci condicionat per a venedors i compradors, registrar comptes de transacció i de pagament, i mantenir un registre de participants.",
        },
        vn_dp_draft_a: {
            title: 'Classificació i model operatiu de la plataforma de dades',
            description: "Els operadors de plataforma han d'establir una classificació del tipus de plataforma (plataforma nacional de dades vs. altres plataformes de dades), definir l'elegibilitat i les responsabilitats de l'operador, i documentar les tarifes, els preus, les normes d'operació i la governança tècnica.",
        },
        d165_r5: {
            title: 'Accés a la informació de la base de dades nacional',
            description: "Les sol·licituds d'extracció/ús d'informació de la base de dades digital nacional utilitzen els formularis prescrits (Mẫu số 07a/07b).",
        },
        d165_r4: {
            title: "Informe anual d'avaluació de riscos",
            description: "Els informes anuals d'avaluació de riscos s'han de presentar mitjançant els formularis prescrits (Mẫu số 05a/05b, 06).",
        },
        d165_r3: {
            title: 'Notificació de finalització de transferència transfronterera',
            description: "La finalització d'una transferència transfronterera s'ha de notificar mitjançant els formularis prescrits (Mẫu số 04a/04b).",
        },
        d165_r2: {
            title: "Notificació del resultat de l'avaluació",
            description: "L'autoritat emet les notificacions de resultats d'avaluació mitjançant el formulari prescrit (Mẫu số 03).",
        },
        d165_r1: {
            title: "Presentació de l'avaluació d'impacte de transferència transfronterera de dades",
            description: "Les organitzacions han de presentar els expedients d'avaluació d'impacte de transferència/tractament transfronterer de dades mitjançant els formularis prescrits (Mẫu số 01a/01b, 02).",
        },
        a964190d_8d1d_4284_bc7d_4023e2866751: {
            title: 'Consentiment per a galetes i tecnologies similars',
            description: "L'emmagatzematge o l'accés a la informació en un equip terminal només es permet amb el consentiment informat previ, excepte en el cas de l'emmagatzematge tècnicament necessari.",
        },
        '8437d96d_cf01_4f87_89f6_4faaf8eefdf6': {
            title: 'Confidencialitat de les comunicacions',
            description: 'Els estats membres garantiran la confidencialitat de les comunicacions i de les dades de trànsit associades realitzades a través de xarxes públiques de comunicacions i serveis de comunicacions electròniques disponibles per al públic.',
        },
        c5420073_50c6_4981_816a_398460e5f546: {
            title: 'Consentiment per a màrqueting directe (opt-in)',
            description: "L'ús de sistemes de comunicacions electròniques per al màrqueting directe requereix el consentiment previ de la persona abonada.",
        },
        c8bf67d2_874b_4647_92d7_39aaa3230819: {
            title: 'Soft opt-in per a clients existents',
            description: "El màrqueting directe a clients existents per a productes o serveis similars es permet sense consentiment sempre que s'ofereixi una opció d'oposició (opt-out).",
        },
        b546df40_9217_4204_b71c_5af5022a0ff9: {
            title: 'Notificació de violacions per a proveïdors de telecomunicacions',
            description: "Els proveïdors de serveis de comunicacions electròniques disponibles per al públic han de notificar a l'autoritat nacional competent les violacions de dades personals.",
        },
        '5bd66ae4_dee3_4611_9104_71742df55484': {
            title: "Dret d'oposició al màrqueting directe",
            description: 'Les persones abonades han de poder oposar-se a la recepció de comunicacions de màrqueting directe en qualsevol moment i de manera gratuïta.',
        },
        a10af31f_ba28_494e_ab50_f8679e86a0b0: {
            title: 'Conservació i supressió de les dades de trànsit',
            description: "Les dades de trànsit relatives a persones abonades i usuàries s'han de suprimir o anonimitzar quan ja no siguin necessàries per a la transmissió, excepte per a la facturació o els pagaments per interconnexió.",
        },
        '8577e60f_66a8_4651_8336_d561db9929ad': {
            title: 'Condicions per al tractament de dades de localització',
            description: 'Les dades de localització diferents de les dades de trànsit només es poden tractar quan estiguin anonimitzades o amb el consentiment de la persona usuària, en la mesura i durant el temps necessaris.',
        },
        '7f3abb41_8713_4276_93b5_6902f419a016': {
            title: 'Licitud del tractament — consentiment',
            description: "El tractament només serà lícit si l'interessat ha donat el seu consentiment al tractament de les seves dades personals per a una o diverses finalitats específiques.",
        },
        '1c2a945a_8eac_428d_8105_e80e95288ea4': {
            title: 'Licitud del tractament — contracte',
            description: "El tractament és necessari per a l'execució d'un contracte en què l'interessat és part.",
        },
        '2b50c0fb_ee96_412e_a092_c3c1f3f817e8': {
            title: 'Licitud del tractament — obligació legal',
            description: "El tractament és necessari per al compliment d'una obligació legal aplicable al responsable del tractament.",
        },
        '702ea760_9e5c_4c32_babc_0b5094ca9735': {
            title: 'Licitud del tractament — interessos vitals',
            description: "El tractament és necessari per protegir els interessos vitals de l'interessat o d'una altra persona física.",
        },
        '8b55ce88_2d5e_467b_a55c_8c5b0efab67e': {
            title: 'Licitud del tractament — interès públic',
            description: "El tractament és necessari per al compliment d'una missió realitzada en interès públic o en l'exercici de poders públics.",
        },
        '8a72dd16_ee3c_4947_b5cd_9484719adad8': {
            title: 'Licitud del tractament — interès legítim',
            description: "El tractament és necessari per a la satisfacció d'interessos legítims perseguits pel responsable del tractament o per un tercer.",
        },
        afefc965_8f83_4d75_a9fc_92d4d4f549d6: {
            title: 'Tractament de categories especials (dades sensibles)',
            description: "Es prohibeix el tractament de categories especials de dades personals (origen racial, opinions polítiques, salut, etc.) llevat que es compleixin les condicions específiques de l'art. 9.2.",
        },
        '10a2d63f_744d_4222_91f6_86a5a4cdaefd': {
            title: 'Condicions per al consentiment',
            description: "El responsable del tractament ha de poder demostrar que s'ha donat el consentiment. El consentiment ha de ser lliure, específic, informat i inequívoc. La retirada del consentiment ha de ser tan fàcil com prestar-lo.",
        },
        '27697f99_efef_49e6_b8e7_c2dc550d1791': {
            title: 'Consentiment del menor — serveis digitals',
            description: "El tractament de dades personals d'un menor de 16 anys (o de l'edat inferior fixada per l'estat membre, mai inferior a 13) requereix el consentiment dels titulars de la pàtria potestat per als serveis de la societat de la informació.",
        },
        '53883480_508e_4f8e_984f_9354c18de285': {
            title: 'Informació i comunicació transparents',
            description: 'El responsable del tractament ha de facilitar la informació relativa al tractament de manera concisa, transparent, intel·ligible i de fàcil accés.',
        },
        b5eb22b7_78f2_4c55_b342_827d5f8ac90c: {
            title: 'Informació en la recollida directa',
            description: "Quan les dades personals s'obtinguin de l'interessat, el responsable del tractament ha de facilitar la informació prescrita en el moment de la recollida.",
        },
        e55f7dbf_cd2a_4713_887e_8c64744e2d95: {
            title: 'Informació en la recollida indirecta',
            description: "Quan les dades personals no s'hagin obtingut de l'interessat, el responsable del tractament ha de facilitar la informació prescrita en un termini raonable.",
        },
        '6bc5ec07_0589_4593_882a_13f41c31e1f6': {
            title: "Dret d'accés",
            description: "L'interessat té dret a obtenir la confirmació del tractament i a accedir a les seves dades personals i a la informació complementària.",
        },
        '0e011e25_dcea_4a1e_ae55_583e683cea44': {
            title: 'Dret de rectificació',
            description: "L'interessat té dret a obtenir, sense dilació indeguda, la rectificació de les dades personals inexactes.",
        },
        e8ec5a55_f657_4a99_9b92_3e9861067d28: {
            title: "Dret de supressió (dret a l'oblit)",
            description: "L'interessat té dret a obtenir, sense dilació indeguda, la supressió de les dades personals quan concorrin els motius establerts.",
        },
        '5b0c92fd_cd08_47be_bfd2_0540ad73f84f': {
            title: 'Dret a la limitació del tractament',
            description: "L'interessat té dret a obtenir la limitació del tractament quan es compleixin les condicions establertes.",
        },
        '3b829862_39f4_41be_8f2b_d469469a801b': {
            title: 'Obligació de notificació — rectificació/supressió/limitació',
            description: "El responsable del tractament ha de comunicar qualsevol rectificació, supressió o limitació a cada destinatari a qui s'hagin comunicat les dades.",
        },
        '9cf34be6_121b_4fdf_ad7c_c4773696522e': {
            title: 'Dret a la portabilitat de les dades',
            description: "L'interessat té dret a rebre les seves dades personals en un format estructurat, d'ús comú i llegible per màquina.",
        },
        becae433_90b9_4cc0_bd33_df50976c1561: {
            title: "Dret d'oposició",
            description: "L'interessat té dret a oposar-se al tractament basat en interès públic o en interessos legítims, incloent-hi l'elaboració de perfils.",
        },
        '4ef387f1_439c_4579_82a3_01565e6dc05d': {
            title: "Decisions individuals automatitzades, inclosa l'elaboració de perfils",
            description: "L'interessat té dret a no ser objecte d'una decisió basada únicament en el tractament automatitzat que produeixi efectes jurídics o l'afecti significativament de manera similar.",
        },
        '667ae5eb_a39a_4809_a032_923cc804eaed': {
            title: "Notificació de violacions a l'autoritat de control (72 h)",
            description: "En cas de violació de dades personals, el responsable del tractament ha de notificar-ho a l'autoritat de control en un termini de 72 hores des que en tingui constància, llevat que sigui improbable que comporti un risc.",
        },
        '40e4c2ad_bc85_436c_814f_265f02c8c3b1': {
            title: 'Notificació de violacions als interessats (alt risc)',
            description: "Quan la violació pugui comportar un risc alt per als drets i les llibertats, el responsable del tractament ha de comunicar-ho a l'interessat sense dilació indeguda.",
        },
        '9dbb47bd_172f_4335_b7f3_4558476c4192': {
            title: 'Resposta a les sol·licituds dels interessats (30 dies)',
            description: "El responsable del tractament ha de respondre a les sol·licituds de l'interessat sense dilació indeguda i, en qualsevol cas, en el termini d'un mes des de la recepció, prorrogable fins a dos mesos més en sol·licituds complexes.",
        },
        '175d16b7_a0fc_44ff_bb9e_5ccb5d3de5a9': {
            title: "Avaluació d'impacte relativa a la protecció de dades (AIPD)",
            description: "Quan el tractament pugui comportar un risc alt per als drets i les llibertats, el responsable del tractament ha de realitzar una AIPD abans d'iniciar-lo.",
        },
        e54a7b09_33dd_4cda_8e21_d10a51e5e925: {
            title: "Consulta prèvia a l'autoritat de control",
            description: "El responsable del tractament ha de consultar l'autoritat de control quan una AIPD indiqui que el tractament comportaria un alt risc en absència de mesures mitigadores.",
        },
        '047b7838_bae5_4861_9c38_c99fedec9c96': {
            title: 'Llista de tractaments amb AIPD obligatòria',
            description: "L'AIPD és obligatòria per a l'avaluació o l'elaboració sistemàtica de perfils, el tractament a gran escala de categories especials i l'observació sistemàtica d'espais d'accés públic.",
        },
        aa08dc83_7c1a_441d_a8e7_960f65c8de87: {
            title: "Registre d'activitats de tractament (responsable)",
            description: 'Cada responsable del tractament ha de mantenir un registre de les activitats de tractament efectuades sota la seva responsabilitat, incloent-hi les finalitats, categories, destinataris, transferències i terminis de conservació.',
        },
        b7eb6296_5962_449c_b267_d322d7919122: {
            title: "Registre d'activitats de tractament (encarregat)",
            description: "Cada encarregat del tractament ha de mantenir un registre de totes les categories d'activitats de tractament dutes a terme per compte d'un responsable.",
        },
        '0d57e6d5_bbf5_4ea6_9a89_0f57bed61b5b': {
            title: 'Seguretat del tractament',
            description: "El responsable i l'encarregat del tractament han d'aplicar mesures tècniques i organitzatives apropiades per garantir un nivell de seguretat adequat al risc.",
        },
        ad85edfc_3c92_4ce4_a414_3ba6186cae98: {
            title: 'Protecció de dades des del disseny i per defecte',
            description: "El responsable del tractament ha d'aplicar les mesures adequades per garantir que només es tractin les dades personals necessàries per a cada finalitat específica (minimització de dades).",
        },
        '933b438d_53b8_4b6a_b1db_8e7f1b429ede': {
            title: 'Designació del delegat de protecció de dades',
            description: "El responsable i l'encarregat del tractament han de designar un delegat de protecció de dades (DPD) quan el tractament el dugui a terme una autoritat pública, impliqui una observació habitual i sistemàtica o un tractament a gran escala de categories especials.",
        },
        '28810a5d_d818_411a_9e8c_792d1b2c9b2f': {
            title: 'Principi general de les transferències',
            description: 'Qualsevol transferència de dades personals a un tercer país només es pot dur a terme si es compleixen les condicions del capítol V.',
        },
        '60add64b_bb0c_44e7_baaf_666af65b2d0f': {
            title: "Transferències basades en una decisió d'adequació",
            description: 'Es pot efectuar una transferència quan la Comissió hagi decidit que el tercer país garanteix un nivell de protecció adequat.',
        },
        cde8c7cd_3727_40ce_a321_e6b72b335179: {
            title: 'Transferències subjectes a garanties adequades',
            description: "En absència d'una decisió d'adequació, les transferències es poden efectuar quan s'aportin garanties adequades, com ara clàusules contractuals tipus (CCT) o normes corporatives vinculants (BCR).",
        },
        '0e8edaa1_2156_44d9_95f2_1b284f581658': {
            title: 'Clàusules contractuals tipus',
            description: 'Les clàusules contractuals tipus adoptades per la Comissió o per una autoritat de control proporcionen garanties adequades per a les transferències internacionals.',
        },
        '608a8de7_6c75_4f88_bd4c_1cc308780ab9': {
            title: 'Normes corporatives vinculants',
            description: "Les normes corporatives vinculants aprovades per una autoritat de control competent proporcionen garanties adequades per a les transferències internacionals dins d'un grup empresarial.",
        },
        '420ced80_0d3b_4a28_adad_14ba54df3e40': {
            title: 'Excepcions per a situacions específiques',
            description: "En absència d'una decisió d'adequació o de garanties adequades, les transferències es poden basar en el consentiment explícit, la necessitat contractual, l'interès públic, l'exercici o defensa de reclamacions, els interessos vitals o un registre públic.",
        },
        c86fbc0d_7188_486c_8ea5_96b3fe957c6f: {
            title: "Responsabilitat i formació de l'òrgan de direcció",
            description: "Els òrgans de direcció de les entitats essencials i importants han d'aprovar les mesures de gestió del risc de ciberseguretat i rebre formació periòdica.",
        },
        ffb95a09_993c_4db0_a4bb_c48000e43b33: {
            title: 'Mesures de gestió del risc de ciberseguretat',
            description: 'Les entitats essencials i importants han de prendre mesures tècniques, operatives i organitzatives adequades i proporcionades per gestionar els riscos de ciberseguretat.',
        },
        '6a7c8c8f_4b81_4b47_adb7_c6abd12acd4b': {
            title: "Polítiques i procediments de gestió d'incidents",
            description: "Les entitats han d'implementar polítiques de gestió d'incidents que incloguin procediments de detecció, anàlisi, contenció i resposta.",
        },
        de019eab_9992_4361_be7c_3e592f209d76: {
            title: 'Continuïtat del negoci i gestió de crisis',
            description: "Les entitats han d'implementar mesures de continuïtat del negoci, incloent-hi la gestió de còpies de seguretat, la recuperació davant desastres i la gestió de crisis.",
        },
        '6b5f482f_301c_4f69_bd91_f9ae1b21de6c': {
            title: 'Seguretat de la cadena de subministrament',
            description: "Les entitats han d'abordar els aspectes de seguretat de les relacions amb proveïdors i prestadors de serveis directes, incloent-hi avaluacions de riscos de la cadena de subministrament.",
        },
        c8e7738b_358c_4c79_9fc2_a564f15a6e37: {
            title: "Seguretat en l'adquisició, desenvolupament i manteniment de xarxes i sistemes d'informació",
            description: "Les entitats han d'implementar la seguretat en l'adquisició, desenvolupament i manteniment de les xarxes i sistemes d'informació, incloent-hi la gestió de vulnerabilitats.",
        },
        '72520ed1_51ff_4983_a785_9be1f72cf7dd': {
            title: 'Gestió i divulgació de vulnerabilitats',
            description: "Les entitats han d'implementar polítiques i procediments per a la divulgació coordinada i la gestió de vulnerabilitats.",
        },
        f009e65d_d55e_4c98_9532_63c150694698: {
            title: "Avaluació de l'eficàcia de les mesures de gestió del risc",
            description: "Les entitats han d'implementar polítiques i procediments per avaluar l'eficàcia de les mesures de gestió del risc de ciberseguretat.",
        },
        '00e88824_675d_4b37_960c_757b3176c079': {
            title: 'Polítiques de criptografia i xifratge',
            description: "Les entitats han d'implementar polítiques i procediments relatius a l'ús de la criptografia i, quan correspongui, del xifratge.",
        },
        de29d287_81f1_46b1_b327_9cbf06ddad3d: {
            title: "Seguretat de personal, control d'accés i gestió d'actius",
            description: "Les entitats han d'implementar mesures de seguretat de personal, polítiques de control d'accés i gestió d'actius.",
        },
        '750f4459_9ba4_415e_bc47_5b0ec27d35a2': {
            title: 'Autenticació multifactor',
            description: "Les entitats han d'utilitzar autenticació multifactor o solucions d'autenticació contínua, comunicacions de veu/vídeo/text segures i sistemes de comunicacions d'emergència segurs.",
        },
        '359474f9_1820_4e3e_9031_a593b66ccde4': {
            title: 'Alerta primerenca al CSIRT (24 h)',
            description: "Les entitats han de presentar una alerta primerenca al CSIRT competent (equip de resposta a incidents de seguretat informàtica) en un termini de 24 hores des que tinguin coneixement d'un incident significatiu.",
        },
        '66acd92c_3091_4148_b935_68b0d24308e7': {
            title: "Notificació d'incident al CSIRT (72 h)",
            description: "Les entitats han de presentar una notificació d'incident al CSIRT competent en un termini de 72 hores des que tinguin coneixement d'un incident significatiu.",
        },
        '68269688_06b2_43ea_8e3e_a84532086557': {
            title: 'Informe final al CSIRT (1 mes)',
            description: "Les entitats han de presentar un informe final al CSIRT competent com a màxim un mes després de la notificació de l'incident.",
        },
        c8512bde_b064_4926_b333_06b9c95cecd7: {
            title: "Determinació de l'aplicabilitat per a activitats d'IA",
            description: "Determinar si l'organització porta a terme activitats d'IA subjectes a la Llei d'IA del Vietnam i documentar la decisió d'aplicabilitat.",
        },
        '3a87ad63_179a_45ba_8743_98d865df80ab': {
            title: "Classificació del risc dels sistemes d'IA per nivell de risc",
            description: "Classificar cada sistema d'IA per nivell de risc (mínim, limitat, alt, inacceptable) utilitzant la metodologia guiada de classificació.",
        },
        '0f4475d3_77a6_4b41_bfe2_d4b299b1bd45': {
            title: "Manteniment de l'entrada al registre de sistemes d'IA",
            description: "Mantenir un registre de sistemes d'IA amb el nom del sistema, propietari, finalitat, context de desplegament, nivell de risc i implicació de proveïdors.",
        },
        '2a73ce94_906a_4c95_8a45_a5bdf2a8118c': {
            title: "Expedient del sistema d'IA amb llista de verificació d'artefactes i evidències",
            description: "Construir i mantenir un expedient del sistema amb la llista d'artefactes obligatòria, biblioteca d'evidències i historial de versions.",
        },
        e92cfb42_d91b_444f_adbc_c03903844eac: {
            title: "Obligacions de transparència per als operadors de sistemes d'IA",
            description: "Implementar mesures de transparència per als sistemes d'IA, incloent-hi la notificació als usuaris, la divulgació de la implicació de la IA i la documentació del sistema.",
        },
        '45eb43d7_9b63_4166_aa0d_fa6aeeaa70ba': {
            title: "Etiquetatge, marca d'aigua i signatura de metadades de la sortida d'IA",
            description: "Implementar l'etiquetatge i la marca d'aigua per al contingut generat per IA, incloent-hi la signatura de metadades quan sigui aplicable.",
        },
        '9566c94d_0dd3_419c_869f_6707d2a972f9': {
            title: 'Requisits de divulgació per al contingut generat per IA',
            description: 'Comunicar als usuaris quan el contingut és generat per IA i proporcionar mecanismes perquè els usuaris puguin identificar la implicació de la IA.',
        },
        '21d39ea5_df78_45c6_8710_b203db229e25': {
            title: "Deures d'explicabilitat i transparència orientada a l'usuari",
            description: "Proporcionar sortides explicables per a les decisions d'IA que afecten persones i mantenir documentació de transparència orientada a l'usuari.",
        },
        f523fbc2_156d_466d_ad50_6a0c51b3ac9d: {
            title: "Obligacions de supervisió humana per a sistemes d'IA d'alt risc",
            description: "Garantir que hi hagi mecanismes de supervisió humana per als sistemes d'IA d'alt risc, incloent-hi capacitats d'escalada i d'anul·lació.",
        },
        bd180a89_4976_4790_9e50_f1c5c97dc576: {
            title: "Monitoratge i detecció d'incidents d'IA",
            description: "Implementar capacitats de monitoratge per detectar incidents, malfuncionaments i resultats adversos dels sistemes d'IA.",
        },
        e7c1c60b_856c_4d3d_b6c9_092cde9b56bd: {
            title: "Notificació d'incidents greus d'IA a l'autoritat",
            description: "Notificar els incidents greus d'IA a l'autoritat competent dins del termini prescrit (72 hores).",
        },
        '97f15adc_558a_49f2_bb06_86597cb9579b': {
            title: "Compliment dels terminis de notificació d'incidents d'IA",
            description: "Complir els terminis de notificació per a incidents d'IA, incloent-hi la notificació inicial, les actualitzacions i els informes de tancament.",
        },
        '96c33d7e_fe52_4730_adac_70c8c477c88e': {
            title: "Avaluació d'impacte d'IA per a sistemes d'alt risc",
            description: "Realitzar avaluacions d'impacte per a sistemes d'IA d'alt risc cobrint riscos, mitigacions, supervisió humana i plans de monitoratge.",
        },
        '0f3b72c0_1030_44ff_ba53_596f45601cd7': {
            title: "Determinació d'elegibilitat per a proves controlades / sandbox",
            description: "Determinar l'elegibilitat per a la participació en proves controlades (sandbox) i documentar l'avaluació d'elegibilitat.",
        },
        '4a6aed20_f655_4182_a760_aef1f329d5df': {
            title: "Presentació de sol·licituds de sandbox i seguiment de l'aprovació",
            description: "Presentar sol·licituds de sandbox a l'autoritat competent i fer el seguiment de l'estat d'aprovació al llarg del cicle de vida de la sol·licitud.",
        },
        '8376988d_cd32_491e_a5b8_3340d3973358': {
            title: 'Punts de control de monitoratge i finalització/sortida del sandbox',
            description: 'Realitzar punts de control de monitoratge periòdics durant la participació al sandbox i preparar informes de finalització/sortida.',
        },
        '7cb2b544_0512_4702_a092_9f1c10872376': {
            title: "Documentació d'exempcions/excepcions d'etiquetatge amb justificació",
            description: "Documentar qualsevol exempció o excepció d'etiquetatge amb la justificació i les evidències de suport.",
        },
        dccd7fbf_872a_4716_87a4_b9c5ce8e2387: {
            title: 'Localització de dades per a usuaris vietnamites',
            description: "Les dades dels usuaris vietnamites en els serveis prestats al Vietnam s'han d'emmagatzemar dins del territori del Vietnam.",
        },
        '9eb86a03_e165_4963_8813_2272b638d529': {
            title: "Notificació d'incidents de ciberseguretat",
            description: "Els incidents de ciberseguretat s'han de notificar a les autoritats competents de manera oportuna.",
        },
        '736ac7de_7a6a_4bfd_b2be_c594e290038a': {
            title: "Classificació dels sistemes d'informació",
            description: "Els sistemes d'informació s'han de classificar per nivell d'importància i s'han d'aplicar mesures de seguretat adequades.",
        },
        ffb807cf_f121_4b52_8e50_e091c33180c9: {
            title: 'Avaluació de riscos de ciberseguretat',
            description: "S'han de dur a terme avaluacions periòdiques de riscos de ciberseguretat per als sistemes d'informació.",
        },
        a4de518b_a5f1_481f_9b08_f8d2bc9db7e5: {
            title: "Determinació d'aplicabilitat (arbre de decisió)",
            description: "Les organitzacions han de determinar si estan dins de l'àmbit del Decret 53 utilitzant l'arbre de decisió prescrit.",
        },
        '01f99e9a_5e30_4d14_aae5_a2ce322a700b': {
            title: 'Avaluació i preparació de la localització de dades',
            description: "Les organitzacions han d'avaluar els requisits de localització de dades i demostrar la seva preparació.",
        },
        cf24f011_dc23_4d84_aef5_994dde440571: {
            title: "Llista de verificació de preparació d'emmagatzematge i retenció",
            description: "S'ha de completar una llista de verificació de preparació d'emmagatzematge i retenció per al compliment de la localització de dades.",
        },
        b3884cb8_c785_4258_9517_9e393be4f4e1: {
            title: 'Avaluació del requisit de presència local',
            description: "Les organitzacions han d'avaluar si s'apliquen els requisits de presència local i planificar en conseqüència.",
        },
        c1dc645b_cb1a_42a6_8ca5_9b15cfa84156: {
            title: "Recepció i verificació de sol·licituds de l'autoritat",
            description: "Les sol·licituds de l'autoritat s'han de rebre, verificar i processar segons els procediments prescrits.",
        },
        d4571651_f39d_4c51_8f2d_7248c37f7b14: {
            title: "Resposta a sol·licituds de l'autoritat dins del termini",
            description: "Les respostes a les sol·licituds de l'autoritat s'han de proporcionar dins del termini prescrit.",
        },
        '66c1df70_3456_40e9_b645_6c6bd3be702e': {
            title: "Registre de gestió de sol·licituds de l'autoritat",
            description: "S'ha de mantenir un registre de totes les activitats de gestió de sol·licituds de l'autoritat.",
        },
        '04bbba2a_9804_4199_a517_054b6f4689fb': {
            title: "Determinació d'aplicabilitat i àmbit (consolidat)",
            description: "Les organitzacions han de determinar si estan dins de l'àmbit de la llei consolidada de ciberseguretat, abastant tant l'antic àmbit de la Llei 24/2018 com el del Decret 53/2022.",
        },
        '89a21eaf_cf09_4fec_bbb4_a1dadd652f6e': {
            title: "Classificació dels sistemes d'informació per nivell d'importància",
            description: "Els sistemes d'informació s'han de classificar per nivell d'importància (general, important, crític) amb les obligacions de seguretat corresponents.",
        },
        '83ad981e_23ff_4a92_bebd_cd24487e912f': {
            title: 'Estructura de governança de ciberseguretat i personal responsable',
            description: "Les organitzacions han d'establir estructures de governança de ciberseguretat i designar personal responsable.",
        },
        '98d9a523_fefb_400b_bb7f_ebaf609a6ed4': {
            title: 'Avaluació periòdica del risc de ciberseguretat',
            description: "S'han de dur a terme avaluacions periòdiques de riscos de ciberseguretat per a tots els sistemes d'informació classificats.",
        },
        '5236539f_4c1a_4eff_b2eb_fc11eb4eab23': {
            title: "Mesures de seguretat per classificació del sistema d'informació",
            description: "S'han d'aplicar mesures tècniques de seguretat adequades a cada nivell de classificació del sistema d'informació.",
        },
        b69a8be2_ae0c_408c_b8f1_73b573eca876: {
            title: "Control d'accessos i gestió d'identitats",
            description: "S'han d'implementar mesures de control d'accessos i gestió d'identitats per als sistemes d'informació.",
        },
        ac774c26_feb7_4123_b799_c54f77bd87b5: {
            title: "Registre d'esdeveniments, monitoratge i pista d'auditoria",
            description: "Els sistemes d'informació han de mantenir capacitats de registre d'esdeveniments, monitoratge i pista d'auditoria.",
        },
        a86cb01d_239b_44e2_9e09_e6973bcf9658: {
            title: 'Gestió de vulnerabilitats i pegats',
            description: "S'han d'establir i mantenir processos de gestió de vulnerabilitats i aplicació de pegats.",
        },
        '826a789f_0c03_4918_81a1_6aa1cc2c205c': {
            title: 'Localització de dades dels usuaris vietnamites',
            description: "Les dades dels usuaris vietnamites en els serveis prestats al Vietnam s'han d'emmagatzemar dins del territori del Vietnam.",
        },
        '8bee2b51_ace8_4118_89c8_010b96640016': {
            title: 'Requisits de presència local per a proveïdors de serveis estrangers',
            description: 'Els proveïdors de serveis estrangers que operen al Vietnam han de mantenir presència local segons el que es prescriu.',
        },
        '74d89250_b45f_41b6_bfe3_f915071580de': {
            title: "Documentació de l'arquitectura d'emmagatzematge local i del flux transfronterer",
            description: "L'arquitectura d'emmagatzematge local s'ha de documentar i els fluxos transfronterers de dades s'han de mapar i controlar.",
        },
        '04dec6f8_3280_494c_8506_292804b16d4d': {
            title: 'Detecció i resposta a incidents de ciberseguretat',
            description: 'Les organitzacions han de disposar de capacitats per detectar i respondre a incidents de ciberseguretat.',
        },
        faeda71b_5050_497d_b08b_e3f3f5313ad4: {
            title: "Notificació obligatòria d'incidents a l'autoritat competent",
            description: "Els incidents de ciberseguretat qualificats s'han de notificar al MPS/MIC dins dels terminis prescrits.",
        },
        d732b5a2_2b2c_48a0_955d_97a39daf132d: {
            title: "Compliment del termini de notificació d'incidents",
            description: "Les notificacions d'incidents han de complir els requisits de termini prescrits (informe inicial dins de 24 h, informe complet dins de 72 h).",
        },
        '8e2dffb0_3481_44f0_baf2_130ddd8cfe12': {
            title: "Gestió de sol·licituds de l'autoritat i cooperació",
            description: "Les sol·licituds de l'autoritat s'han de gestionar amb diligència i s'ha de proporcionar cooperació segons els procediments prescrits.",
        },
        e4c5b870_1d61_48fe_a6c2_1b9f5a458ea3: {
            title: "Registre de casos de sol·licitud de l'autoritat i registre de divulgacions",
            description: "Totes les sol·licituds de l'autoritat i les divulgacions s'han de registrar en un registre de casos amb pista d'auditoria completa.",
        },
        '8eff8b17_65bc_4f94_8ece_c509a9fd9abb': {
            title: 'Avaluació de seguretat de proveïdors i tercers',
            description: "Els proveïdors de tercers amb accés als sistemes d'informació han de ser sotmesos a una avaluació de seguretat.",
        },
        '69891e1b_97f6_464c_9cce_b1276794fe4a': {
            title: "Controls d'accés a dades per part de proveïdors i notificació d'incidents",
            description: "L'accés a dades per part de proveïdors s'ha de documentar i s'han d'establir obligacions contractuals de notificació d'incidents.",
        },
        d89b6bc0_7a9e_4816_b31e_7c0abc02e90c: {
            title: 'Formació i exercicis de conscienciació en seguretat',
            description: "S'han de dur a terme regularment formació en conscienciació de ciberseguretat i exercicis de simulació (tabletop).",
        },
        f9c5d3cc_5bb2_4b30_a5fb_ada34e407981: {
            title: "Test d'intrusió i auditoria de seguretat periòdics",
            description: "S'han de realitzar tests d'intrusió i auditories de seguretat periòdics sobre els sistemes d'informació classificats.",
        },
        '88dd9877_d2e2_4e41_a248_ae39b1ff478f': {
            title: "Mantenir l'inventari de dades digitals",
            description: 'Les organitzacions han de mantenir un inventari complet de dades digitals que cataloga tots els actius de dades.',
        },
        '18e4bfd1_42cf_4d87_870f_5a34242962ef': {
            title: 'Classificació de dades per sensibilitat i importància',
            description: "Les dades s'han de classificar segons els nivells de sensibilitat i importància definits per la Llei de Dades (Luật Dữ liệu).",
        },
        b89981ac_7559_46f9_a74c_a143976dde4e: {
            title: 'Definir regles de gestió per nivell de classificació',
            description: "S'han d'establir regles de gestió per a cada nivell de classificació de dades.",
        },
        '3bf8019d_b72c_41d7_82cf_fc367efd9526': {
            title: "Seguiment de l'intercanvi i divulgació de dades amb aprovacions",
            description: "Totes les activitats d'intercanvi i divulgació de dades s'han de seguir amb les aprovacions adequades.",
        },
        '8623eca2_4cf9_41bd_bfbd_2300c06ca1f3': {
            title: 'Governança de la transferència transfronterera de dades',
            description: 'Les transferències transfrontereres de dades han de complir els requisits de governança previstos a la Llei de Dades (Luật Dữ liệu).',
        },
        '006b8aa6_9379_4eff_b8ab_935f9a087601': {
            title: 'Mantenir polítiques i responsabilitats de governança',
            description: 'Les organitzacions han de mantenir polítiques de governança de dades i assignar responsabilitats de governança.',
        },
        f12fc090_5c3e_4fa8_9813_74bea0359c5c: {
            title: 'Atestat anual de governança',
            description: "S'ha de completar un atestat anual de governança per confirmar el compliment continu.",
        },
        f6c3060a_5e52_4f97_b1af_c6a5f00bfaee: {
            title: 'Avaluació del risc de dades',
            description: "S'ha de dur a terme una avaluació del risc de dades per identificar i mitigar els riscos de governança de dades.",
        },
        '6f228998_1c61_49cf_a585_3d3e9baff55a': {
            title: "Responsabilitat i obligacions de governança de l'operador de plataforma",
            description: "Establir estructures de responsabilitat de l'operador de plataforma incloent-hi polítiques de governança, responsabilitats de monitoratge i obligacions d'execució.",
        },
        eee1d423_3783_4530_bc64_eef6fd9d02dc: {
            title: 'Publicació de les normes de la plataforma i transparència de la informació',
            description: 'Publicar les normes de la plataforma, les condicions de comerç i les divulgacions operatives de manera transparent i accessible.',
        },
        '50b57433_7a51_4e92_9c84_c748d6865b70': {
            title: 'Notificació de lloc web/aplicació de venda de comerç electrònic al MOIT',
            description: 'Presentar la notificació al MOIT (Bộ Công Thương) per a llocs web i aplicacions de venda de comerç electrònic via Online.gov.vn o canals designats.',
        },
        '92854abe_6654_4155_8495_f3419a8a8da0': {
            title: 'Registre de plataforma de servei/marketplace de comerç electrònic al MOIT',
            description: "Registrar les plataformes de serveis i marketplaces de comerç electrònic al MOIT (Bộ Công Thương) incloent-hi la presentació d'expedients i el seguiment de l'aprovació.",
        },
        b8bff437_805e_4a4a_a62e_89cb90b89d3f: {
            title: 'Manteniment del registre de venedors (nacionals i estrangers)',
            description: 'Mantenir un registre de tots els venedors a la plataforma incloent-hi venedors nacionals i estrangers, amb incorporació, verificació i seguiment del cicle de vida.',
        },
        '75778639_5235_4d13_a728_fa772c27fc0c': {
            title: 'Registre de venedors en livestream i controls de compliment',
            description: "Mantenir un registre de venedors en livestream amb verificació d'identitat, seguiment d'activitats i controls de compliment.",
        },
        '405f2932_742a_4bcb_9784_1a3d81f2e714': {
            title: "Registre d'afiliats i socis de màrqueting",
            description: "Mantenir un registre dels afiliats i socis de referència amb l'estat del contracte, les directrius de contingut i els compromisos de compliment.",
        },
        cce5a4eb_a576_459c_95b5_bdd0e8ed30a8: {
            title: 'Llistat de productes i execució sobre béns prohibits/condicionats',
            description: "Implementar controls de llistat de productes incloent-hi el cribratge, monitoratge i accions d'execució sobre béns prohibits i condicionats.",
        },
        '9fc01e19_18dd_4d60_b95a_c1f4bcdbb6ae': {
            title: "Registre de queixes de consumidors amb recepció, accions i pista d'auditoria",
            description: "Mantenir un registre de queixes amb registres de recepció, accions d'investigació, resultats de resolució i pista d'auditoria completa.",
        },
        '8a21764e_e5f4_4934_9913_c235f4c462ca': {
            title: 'Procediment de gestió de queixes i terminis de resolució',
            description: "Establir procediments de gestió de queixes amb terminis definits d'acusament de recepció, investigació i resolució.",
        },
        '8406373c_ec31_4bf8_810f_72d9d73b3de8': {
            title: 'Moderació de continguts, monitoratge i execució de retirades',
            description: 'Implementar procediments de moderació de continguts, monitoratge i execució de retirades per a contingut il·legal, prohibit o infractor.',
        },
        '6f32c315_323b_4ce6_a4bb_3262a5b56afe': {
            title: "Registre d'accions de retirada i execució amb preservació d'evidències",
            description: "Mantenir un registre d'accions de retirada i execució amb registres de decisions, preservació d'evidències i notificacions als venedors.",
        },
        c0bbbfbf_29bd_4813_89f1_956eab9f1a23: {
            title: 'Registre de polítiques i divulgacions de la plataforma (versions, dates de publicació, acceptació)',
            description: "Mantenir un registre de les polítiques i divulgacions de la plataforma amb historial de versions, dates de publicació i evidències d'acceptació.",
        },
        c9fcbe90_622d_43ca_9ec6_db26c0eda490: {
            title: "Deures d'informació al consumidor — informació precisa del producte i antiengany",
            description: 'Garantir informació precisa i clara dels productes i serveis i implementar mesures per evitar pràctiques enganyoses a la plataforma.',
        },
        '8f49603b_454c_4470_ab07_2f845a447595': {
            title: 'Condicions operatives per a plataformes estrangeres i registre/notificació',
            description: 'Complir les condicions operatives per a plataformes estrangeres que serveixen usuaris vietnamites incloent-hi els requisits de registre o notificació.',
        },
        e0dc85cc_5d2a_4142_b1a6_cc2fde891f7b: {
            title: 'Representació local o autorització per a plataformes estrangeres',
            description: 'Designar un representant local o entitat autoritzada al Vietnam per a plataformes estrangeres tal com exigeix la llei.',
        },
        '5719f47c_f298_429e_90c3_df6ceed856e1': {
            title: 'Coordinació de gestió de venedors i queixes transfrontereres',
            description: "Coordinar la gestió de venedors transfronterers i la resolució de queixes incloent-hi l'escalada i la gestió d'evidències.",
        },
        c0b3db29_9161_4cef_a889_94e3b85d78fe: {
            title: 'Interacció amb el sistema estatal de gestió del comerç electrònic i preparació per al reporting',
            description: 'Preparar la interacció amb el sistema estatal de gestió del comerç electrònic incloent-hi el mapatge de camps de dades i la capacitat de reporting.',
        },
        '145a7016_720a_4a06_badd_00145842821e': {
            title: "Notificació de canvis materials posteriors a l'aprovació",
            description: "Presentar notificacions per a canvis materials en els detalls, polítiques o operacions de la plataforma després de l'aprovació inicial.",
        },
        '4bc0ee2a_610d_490d_8122_d6d1afadaa22': {
            title: 'Notificació de cessament i desactivació',
            description: "Presentar notificacions de cessament o desactivació quan es deixin d'operar serveis de comerç electrònic incloent-hi l'avís públic i la gestió de la protecció del consumidor.",
        },
        be95bc85_2b25_4de7_a3e7_154b780289e4: {
            title: 'Elegibilitat i manteniment del programa de segell de confiança i de qualificació de credibilitat',
            description: "Avaluar l'elegibilitat per als programes de segell de confiança i qualificació de credibilitat, i mantenir les evidències de compliment per a la participació continuada.",
        },
        f639afe2_18f2_4c0a_82d9_626308994267: {
            title: 'Consentiment per al tractament de dades personals',
            description: "El tractament de dades personals requereix el consentiment de l'interessat tret que s'apliqui una exempció.",
        },
        '5dd8e1b9_ac2d_48af_8c62_538937f0a4f7': {
            title: 'Sensitive personal data: tell the data subject the data is sensitive',
            description: 'For processing sensitive personal data, the data subject must be told that the data to be processed is sensitive personal data (Art. 11(8)). Art. 28 adds the measures for protecting it: applying the measures of Art. 26(2) and Art. 27; designating a unit with the function of protecting personal data and staff in charge of it, and exchanging their details with the specialised personal data protection agency; and notifying the data subject that their sensitive personal data is being processed, except in the cases of Art. 13(4), Art. 17 and Art. 18.',
        },
        '5a958c76_6943_4449_a15c_ddb503ce526f': {
            title: "Dret d'accés",
            description: "Els interessats tenen el dret d'accedir a les seves dades personals en poder del responsable del tractament.",
        },
        '1ae0a58b_65a5_4a32_9b2c_0baed7799a32': {
            title: 'Dret de rectificació',
            description: 'Els interessats tenen el dret de sol·licitar la rectificació de dades personals inexactes.',
        },
        '25c61e57_273f_48cb_a318_d6315ad744f1': {
            title: 'Dret de supressió',
            description: 'Els interessats tenen el dret de sol·licitar la supressió de les seves dades personals.',
        },
        '0eafe047_ed8b_4a9a_bda9_b4ef2ae353c2': {
            title: 'Dret a restringir el tractament',
            description: 'Els interessats tenen el dret de sol·licitar la restricció del tractament de les seves dades personals.',
        },
        '77f9677a_f436_43ec_a947_3bdc44fc192a': {
            title: "Dret d'oposició",
            description: "Els interessats tenen el dret d'oposar-se al tractament de les seves dades personals.",
        },
        c8b19e70_782c_49c9_8a71_14bef4147d6b: {
            title: 'Dret a retirar el consentiment',
            description: 'Els interessats tenen el dret de retirar en qualsevol moment el consentiment prèviament prestat.',
        },
        '7f472a1c_b37f_498d_adc7_df8d8eafbae9': {
            title: "Notificació de violacions a l'autoritat (72 h)",
            description: "El responsable del tractament ha de notificar al Ministeri de Seguretat Pública (Bộ Công an) en un termini de 72 hores des del descobriment d'una violació de dades personals.",
        },
        '98bed7ab_e814_43f5_8e70_e95c413266a1': {
            title: 'Notificació de violacions als interessats',
            description: 'El responsable del tractament ha de notificar als interessats afectats una violació de dades personals sense dilació indeguda.',
        },
        '404a3576_dd84_4bac_acf4_29719615c45e': {
            title: "DPIA per a tractaments d'alt risc",
            description: "S'ha de dur a terme una avaluació d'impacte sobre la protecció de dades (DPIA) per a les activitats de tractament que probablement comportin un alt risc.",
        },
        '29d9ed94_9360_457c_9aca_37aefac66969': {
            title: 'Avaluació de transferència transfronterera',
            description: "S'ha de dur a terme una avaluació d'impacte abans de transferir dades personals fora del Vietnam.",
        },
        '9620ff08_b83c_4e1f_9350_eba835b145fe': {
            title: "Registre d'activitats de tractament",
            description: "Els responsables del tractament han de mantenir un Registre d'Activitats de Tractament (RoPA) que documenti totes les activitats de tractament.",
        },
        '96ee0ff3_3ead_431e_aa57_08ccff4821fc': {
            title: 'Registres dels acords de tractament de dades',
            description: "S'han de mantenir registres dels acords de tractament de dades amb encarregats i subencarregats.",
        },
        '434dc319_1922_49e4_b757_37622135c4b2': {
            title: 'Garanties per a transferències transfrontereres',
            description: 'Les transferències de dades personals fora del Vietnam requereixen garanties adequades i avaluacions documentades.',
        },
        '3372c651_d5a8_4cae_ae57_2fc684ad0768': {
            title: 'Acusament de recepció en 2 dies hàbils',
            description: "Les sol·licituds dels interessats s'han d'acusar de recepció en un termini de 2 dies hàbils des de la recepció.",
        },
        b15fa62d_3f52_4e42_8442_f4969e08883a: {
            title: 'Resolució en 15 dies hàbils',
            description: "Les sol·licituds dels interessats s'han de resoldre en un termini de 15 dies hàbils, amb una possible pròrroga de 5 dies hàbils.",
        },
        '5d81233c_f671_4827_b874_827762edfc20': {
            title: "Presentació de l'expedient d'avaluació d'impacte al MoPS",
            description: "Les organitzacions que duen a terme tractaments d'alt risc han de presentar un expedient d'avaluació d'impacte al Ministeri de Seguretat Pública (Bộ Công an) utilitzant el procediment administratiu prescrit.",
        },
        '83bf5bf8_edbd_4f0a_9f8e_5f47db01324d': {
            title: "Presentació de l'expedient de transferència transfronterera al MoPS",
            description: "Les organitzacions que transfereixen dades personals fora del Vietnam han de presentar un expedient d'avaluació d'impacte de la transferència transfronterera al Ministeri de Seguretat Pública (Bộ Công an).",
        },
        '9454e465_e618_4b6f_80b3_d79fdd9cc5cb': {
            title: 'Certificació del proveïdor de servei de tractament',
            description: "Les organitzacions que ofereixen el tractament de dades personals com a servei han d'obtenir un certificat de servei de tractament del Ministeri de Seguretat Pública (Bộ Công an).",
        },
        b88aec34_046a_4e0a_9c77_ddad27ea4bc4: {
            title: "Expedient d'avaluació d'impacte de tractament quan es compleixin els supòsits",
            description: "S'ha de presentar un expedient d'avaluació d'impacte de tractament quan es compleixin els supòsits prescrits.",
        },
        '9d34a206_b85e_4649_a23b_e2c6f1398402': {
            title: "Expedient d'impacte de transferència transfronterera",
            description: "S'ha de preparar i presentar un expedient d'impacte de transferència transfronterera per a les transferències internacionals de dades.",
        },
        '210d3ec3_e6f2_424b_ae25_cad76bc13320': {
            title: 'Designació del delegat de protecció de dades',
            description: "S'ha de designar un delegat de protecció de dades segons el que exigeix el Decret 13.",
        },
        '776183b2_2797_40e8_8d81_c4f512c566d2': {
            title: 'Artefactes de governança (polítiques, rols, procediments)',
            description: "S'han de mantenir els artefactes de governança incloent-hi polítiques, rols i procediments.",
        },
        '4f1385f1_6dfc_4368_b4af_1040cd3f77b7': {
            title: "Completesa del paquet d'evidències i pista d'auditoria immutable",
            description: "Els paquets d'evidències han de ser complets i s'ha de mantenir una pista d'auditoria immutable.",
        },
        f6826e26_8991_4a0f_9117_481490230bdd: {
            title: 'Suport al flux de treball del cas de presentació',
            description: "S'han de suportar els fluxos de treball de casos de presentació per a expedients d'avaluació d'impacte i transferències transfrontereres.",
        },
        '8a28ee33_305a_491f_a93f_90e59f8da91b': {
            title: 'Requisits contractuals per a encarregats',
            description: "S'han d'establir i monitorar els requisits contractuals per a encarregats del tractament de dades.",
        },
        '4fd36e83_8286_40e0_b9b2_a4bc5e8377b3': {
            title: 'Revisió anual de compliment',
            description: "S'ha de dur a terme una revisió anual de compliment per verificar l'adherència continuada.",
        },
        '8b6b6c13_0d32_4439_819f_4bd8cff02217': {
            title: "Registres de presentacions a l'autoritat",
            description: "S'han de mantenir els registres de totes les presentacions a l'autoritat.",
        },
        e7ce6c23_03dc_42dd_bccd_fa571c3afee3: {
            title: 'Bases lícites per al tractament de dades personals',
            description: "El tractament de dades personals s'ha de basar en les bases lícites definides pel PDPL.",
        },
        c919c66c_6a74_4403_bb97_04b773d270bf: {
            title: 'Sensitive personal data: identify it, protect it, and say so when seeking consent',
            description: "Law 91/2025/QH15 defines sensitive personal data in Article 2(3) as personal data tied to an individual's privacy which, when infringed, directly affects the lawful rights and interests of agencies, organisations and individuals, on a list the Government issues; Decree 356/2025/NĐ-CP Article 4(1) is that list. The Law attaches no consent rule to sensitive personal data as a class; sector articles carry their own. Article 26(1)(a) requires the data subject's consent in collecting and processing personal data for health information and in insurance business, except in the Article 19(1) cases. Article 27(1)(b) forbids using a data subject's credit information for credit scoring, credit rating, credit information assessment or creditworthiness assessment without their consent. Article 31(3)(a) forbids location tracking by radio-frequency identification and other technologies, except with the data subject's consent, at the request of a competent authority as provided by law, or where the law provides otherwise. Among what the instruments attach to sensitive data: in processing it, an agency or organisation must set rules limiting access rights, a processing procedure and security measures (Decree 356 Article 4(2)); when consent to process it is sought, the data subject must be told that the data to be processed is sensitive personal data (Decree 356 Article 6(4)); a transfer of it must carry physical security for storage and transmission devices, encryption, anonymisation and other security measures (Decree 356 Article 7(2)); an organisation or individual in finance, banking or credit information must fully comply with the rules on protecting sensitive personal data (Law 91 Article 27(1)(a)), and where a data subject's sensitive data in that sector is disclosed or lost, whoever directly collected it must notify the specialised personal data protection agency and the data subject within 72 hours of discovering it (Decree 356 Article 8(3)); and a small business, start-up, household business or micro-enterprise that directly processes sensitive personal data does not have the option or exemption Law 91 Article 38(2)–(3) and Decree 356 Article 41 give from Articles 21 and 22 and Article 33(2) of the Law. Record which of the organisation's personal data falls on that list.",
        },
        '419cc7c3_a257_4293_a054_304dc2fcb8df': {
            title: "Drets de l'interessat (accés, rectificació, supressió, restricció, oposició, portabilitat)",
            description: 'Els interessats tenen drets integrals incloent-hi accés, rectificació, supressió, restricció, oposició i portabilitat.',
        },
        '638e6e25_3a74_4864_8bff_8e21ab82860b': {
            title: "Notificació de violacions a l'autoritat",
            description: "Els responsables del tractament han de notificar a l'autoritat competent les violacions de dades personals dins del termini prescrit.",
        },
        '6e75c111_66b1_4726_8c27_6537846edb30': {
            title: 'Notificació de violacions als interessats',
            description: 'Els responsables del tractament han de notificar als interessats afectats les violacions de dades personals.',
        },
        f686123d_3bdd_4a61_a53c_efe5b52d9058: {
            title: "Avaluació d'impacte sobre la protecció de dades",
            description: "S'ha de dur a terme una avaluació d'impacte sobre la protecció de dades per a les activitats de tractament d'alt risc.",
        },
        '554ea5b6_092d_4295_a57a_1bd9b2eef291': {
            title: 'Governança de transferències transfrontereres',
            description: 'Les transferències transfrontereres de dades personals han de complir els requisits de governança.',
        },
        cf9a5b29_46fd_4072_b877_13b8a69df82c: {
            title: 'Designació del delegat de protecció de dades',
            description: "Les organitzacions han de designar un delegat de protecció de dades tal com s'exigeixi.",
        },
        f5a4965d_eb0c_47df_9933_f185ae46463b: {
            title: "Registres d'activitats de tractament",
            description: 'Les organitzacions han de mantenir els registres de totes les activitats de tractament.',
        },
        ce8986ff_684d_41ec_8147_b3f2a1c5206c: {
            title: 'Obligacions contractuals dels encarregats',
            description: "S'han d'establir obligacions contractuals amb els encarregats del tractament de dades.",
        },
        '7a3d7554_7b6f_49d7_8864_4a5cd71fd6bd': {
            title: "Presentació de l'expedient d'avaluació d'impacte",
            description: "Els expedients d'avaluació d'impacte s'han de presentar a l'autoritat competent tal com es prescriu.",
        },
        '65dba62f_0788_44c1_89e3_09d802e28eb9': {
            title: "Expedient d'impacte de transferència transfronterera",
            description: "S'ha de preparar i presentar un expedient d'impacte de transferència transfronterera per a les transferències internacionals de dades.",
        },
        '0eccb520_b1fa_4fa3_a3ab_2d30823dd00e': {
            title: 'Nomenament i notificació del DPO',
            description: "S'ha de nomenar un delegat de protecció de dades i notificar-ho a l'autoritat.",
        },
        '43ead19e_982a_4615_bafd_e6a3a4e80b3a': {
            title: 'Polítiques i procediments de governança',
            description: "S'han d'establir i mantenir polítiques i procediments de governança.",
        },
        '9ebc7a42_e1f1_49c4_ac90_22a691fd2219': {
            title: "Paquet d'evidències i pista d'auditoria immutable",
            description: "Els paquets d'evidències han de ser complets i s'ha de mantenir una pista d'auditoria immutable.",
        },
        '5b0ba1c0_7b86_4c80_b9f9_26e4ff9652c2': {
            title: 'Cicle de vida del cas de presentació',
            description: "Els cicles de vida dels casos de presentació s'han de gestionar a través de les etapes prescrites.",
        },
        '83f2f346_47bb_4377_a711_3cef26742f39': {
            title: 'Requisits contractuals per a encarregats',
            description: "S'han d'establir i monitorar els requisits contractuals per a encarregats del tractament de dades.",
        },
        cb972e06_f773_492e_8ad5_f65d1e999b79: {
            title: 'Certificació del proveïdor de serveis de tractament',
            description: "Les organitzacions que ofereixen serveis de tractament de dades han d'obtenir la certificació.",
        },
        '4ba36ab5_39f0_4d79_927e_c39587bed226': {
            title: "Registres de presentacions a l'autoritat",
            description: "S'han de mantenir els registres de totes les presentacions a l'autoritat.",
        },
        f7a62c88_3cda_4031_bc52_1598661a5128: {
            title: 'Revisió anual de compliment',
            description: "S'ha de dur a terme una revisió anual de compliment per verificar l'adherència continuada.",
        },
        '3e97f4d4_60ad_46a8_ad2b_615d4ca04aee': {
            title: 'Mesures tècniques de seguretat de dades',
            description: "S'han d'implementar i mantenir mesures tècniques per a la seguretat de les dades.",
        },
        '45f4e745_a923_4879_94ce_5a2b41b7e886': {
            title: 'Requisits dels formularis oficials Mẫu số del Decret 356',
            description: "Els 13 formularis oficials Mẫu số de procediment administratiu (Mẫu số 01a–10) prescrits pel Decret 356/2025/NĐ-CP s'han d'utilitzar per a les presentacions corresponents al Ministeri de Seguretat Pública (Bộ Công an). Els elements Phụ lục I–VIII són mapatges interns d'estructura de dades, no formularis regulatoris oficials.",
        },
        ff452604_7461_4c12_aeab_cb7dd1fb5ba3: {
            title: "Presentació de l'expedient DPIA a A05",
            description: 'Les organitzacions han de presentar els expedients DPIA a A05/MPS (Bộ Công an) per a activitats de tractament que impliquin dades personals sensibles o tractaments a gran escala.',
        },
        '003600ae_7a0f_4034_b206_72f59409b8f3': {
            title: "Presentació de l'expedient de transferència transfronterera",
            description: "Les organitzacions han de presentar els expedients de transferència transfronterera de dades per a la revisió i aprovació d'A05/MPS (Bộ Công an).",
        },
        '4632b20c_293a_4c7e_968e_4ce178c3b7d8': {
            title: 'Sol·licitud de certificat de servei de tractament',
            description: 'Els proveïdors de serveis de tractament de dades han de sol·licitar certificats a A05/MPS (Bộ Công an).',
        },
        be96add4_ebdb_4b6c_9426_4dc87b7cd2ce: {
            title: 'Notificació del tractament de dades personals',
            description: 'Els responsables han de notificar a A05/MPS (Bộ Công an) les activitats de tractament de dades personals utilitzant els formularis prescrits (Mẫu số 01a/01b).',
        },
        '39f50471_27cc_46bd_acff_9aa2211d43ab': {
            title: 'Informe anual de situació',
            description: 'Els responsables han de presentar informes anuals de situació del tractament de dades personals (Mẫu số 09).',
        },
        '3b45c6fa_d66a_448a_9ff1_597caa055e09': {
            title: "Confidencialitat de la informació de l'usuari per defecte",
            description: "La informació de l'usuari ha de ser tractada com a confidencial per defecte.",
        },
        '2994510e_b1f8_4cdd_947c_d9542746433b': {
            title: 'Controls mínims de protecció organitzatius i tècnics',
            description: "S'han d'implementar controls mínims de protecció organitzatius i tècnics.",
        },
        '8643160f_9a10_4d93_baf5_082c6a7c768a': {
            title: 'Divulgació només amb base adequada',
            description: "La divulgació d'informació de l'usuari només es permet amb una base jurídica adequada.",
        },
        c1fb7d85_fdc0_402e_ab0c_ff134bd72dfa: {
            title: 'Registre obligatori de divulgacions i auditabilitat',
            description: "Totes les divulgacions s'han de registrar i ser auditables.",
        },
        efa36bc0_abae_417a_b676_9509af37f89d: {
            title: "Flux d'aprovació intern per a divulgacions",
            description: "S'ha d'establir un flux d'aprovació intern per a totes les divulgacions.",
        },
        d086fdcd_9a23_4500_893c_db8ac62bd787: {
            title: "Gestió de la resposta a sol·licituds de l'autoritat",
            description: "Les sol·licituds de l'autoritat s'han de gestionar i respondre segons els procediments prescrits.",
        },
    },
    framework_rights_descriptions: {
        be_informed: "L'interessat té el dret a ser informat sobre les activitats de tractament de dades personals.",
        consent: "L'interessat té el dret a consentir el tractament de les seves dades personals.",
        refuse_consent: "L'interessat té el dret a no consentir el tractament de les seves dades personals.",
        withdraw_consent: "L'interessat té el dret a sol·licitar la retirada del consentiment prèviament prestat.",
        access: "L'interessat té el dret a consultar i accedir a les seves dades personals en poder del responsable del tractament.",
        rectification: "L'interessat té el dret a corregir o sol·licitar la correcció de dades personals inexactes.",
        data_provision: "L'interessat té el dret a sol·licitar el lliurament de les seves dades personals (yêu cầu cung cấp dữ liệu cá nhân).",
        deletion: "L'interessat té el dret a sol·licitar la supressió o destrucció de les dades personals, amb subjecció a les excepcions de la llei.",
        restriction_of_processing: "L'interessat té el dret a sol·licitar la restricció del tractament de les seves dades personals.",
        object_to_processing: "L'interessat té el dret a presentar una sol·licitud d'oposició al tractament de les seves dades personals.",
        complaint: "L'interessat té el dret a presentar una queixa (khiếu nại) en relació amb el tractament de dades personals.",
        denunciation: "L'interessat té el dret a formular una denúncia (tố cáo) sobre infraccions de la protecció de dades personals.",
        lawsuit: "L'interessat té el dret a interposar accions legals (khởi kiện) per infraccions dels seus drets de protecció de dades.",
        claim_damages: "L'interessat té el dret a reclamar la indemnització pels danys causats per infraccions de la protecció de dades personals.",
        request_protection_measures: "L'interessat té el dret a sol·licitar a les autoritats competents o a les organitzacions/persones rellevants que apliquin mesures per protegir les seves dades personals.",
    },
    framework_procedures: {
        VN_DP_DRAFT_P7: {
            name: 'Gestió i monitoratge del risc (esborrany)',
            steps: {
                '1': 'Identificar les categories de risc',
                '2': "Mantenir controls d'alerta primerenca",
                '3': 'Revisar els riscos de recollida, emmagatzematge, tractament, intercanvi i transacció',
                '4': "Registrar els riscos de privadesa, ciberseguretat i gestió d'accessos",
                '5': 'Activar accions de remediació',
                '6': "Preservar l'historial de revisions",
            },
        },
        VN_DP_DRAFT_P6: {
            name: 'Queixes i disputes (esborrany)',
            steps: {
                '1': 'Rebre la sol·licitud de queixa / disputa',
                '2': 'Comprovar la completesa de la informació requerida; sol·licitar suplements en 24 hores si és incompleta',
                '3': "En els 3 dies hàbils següents a la recepció de la sol·licitud completa, notificar a les parts l'enfocament de gestió i el termini previst",
                '4': 'Sol·licitar respostes i documents de suport',
                '5': 'Avaluar els fets de la disputa i la responsabilitat de la plataforma',
                '6': 'Registrar el resultat de la resolució',
                '7': 'Preservar les evidències i tancar el cas',
            },
        },
        VN_DP_DRAFT_P5: {
            name: 'Lliurament i certificat de drets de propietat sobre les dades (esborrany)',
            steps: {
                '1': 'Lliurar el producte/servei segons el mètode contractual',
                '2': "Registrar l'esdeveniment i el moment del lliurament",
                '3': 'Generar el certificat de drets de propietat sobre les dades',
                '4': 'Registrar la finalització de la transacció',
                '5': 'Permetre la consulta / verificació posterior quan calgui',
            },
        },
        VN_DP_DRAFT_P4: {
            name: 'Inici de transacció i contractació (esborrany)',
            steps: {
                '1': "Revisar el llistat i el resultat de l'avaluació",
                '2': 'Iniciar el flux de compra, negociació o subhasta',
                '3': 'Generar el contracte electrònic',
                '4': "Obtenir l'acceptació de les dues parts",
                '5': 'Registrar la finalització del pagament',
                '6': 'Autoritzar el lliurament',
            },
        },
        VN_DP_DRAFT_P3: {
            name: 'Accés a sandbox / entorn de proves (esborrany)',
            steps: {
                '1': 'Presentar la sol·licitud de sandbox',
                '2': "Definir l'abast, les dades permeses, el període de temps i els permisos de tractament",
                '3': "Provisionar l'entorn tècnic aïllat",
                '4': 'Monitorar la seguretat i els accessos durant les proves',
                '5': "Revisar la sortida abans de l'extracció",
                '6': "Aprovar l'extracció només quan estigui permès",
                '7': "Tancar la sessió de proves i netejar l'entorn",
            },
        },
        VN_DP_DRAFT_P2: {
            name: 'Llistat de productes / serveis de dades (esborrany)',
            steps: {
                '1': 'Crear la sol·licitud de llistat',
                '2': "Capturar l'evidència d'origen i de drets de propietat / ús",
                '3': "Registrar les característiques tècniques, format, detalls de l'API i restriccions",
                '4': 'Registrar les declaracions de qualitat',
                '5': "Presentar l'expedient de llistat per a la revisió de la plataforma",
                '6': 'La plataforma realitza les comprovacions de completesa, legalitat i tècniques',
                '7': 'Aprovar o rebutjar el llistat',
                '8': "Si s'aprova, publicar el llistat amb el resultat de l'avaluació adjunt",
            },
        },
        VN_DP_DRAFT_P1: {
            name: 'Incorporació de participants (esborrany)',
            steps: {
                '1': 'Determinar el tipus de participant: venedor o comprador',
                '2': "Verificar l'estatus jurídic i la capacitat jurídica",
                '3': "Verificar les condicions addicionals si s'apliquen línies de negoci condicionades",
                '4': 'Registrar el compte de transacció',
                '5': 'Vincular el compte de pagament',
                '6': 'Aprovar la participació a la plataforma',
                '7': "Emmagatzemar el registre del participant i la pista d'auditoria",
            },
        },
        AI_A1: {
            name: "Registre / Notificació del sistema d'IA",
            steps: {
                '1': "Comprovació d'aplicabilitat i classificació del sistema d'IA",
                '2': "Construir l'expedient de registre/notificació",
                '3': 'Aprovació interna',
                '4': 'Presentar pel canal corresponent',
                '5': "Bucle d'esmena — si es retorna, actualitzar i tornar a presentar",
                '6': 'Tancar i emmagatzemar la confirmació',
            },
        },
        AI_A2: {
            name: "Flux de classificació del risc d'IA",
            steps: {
                '1': "Crear l'entrada al registre de sistemes d'IA",
                '2': "Executar l'assistent de classificació",
                '3': 'Adjuntar evidències',
                '4': 'Aprovar la classificació',
                '5': "Vincular els resultats a l'expedient del sistema",
            },
        },
        AI_A3: {
            name: "Compliment d'etiquetatge i divulgació",
            steps: {
                '1': "Identificar les obligacions d'etiquetatge per sistema i cas d'ús",
                '2': "Implementar els avisos d'etiquetatge",
                '3': 'Capturar evidències — captures de pantalla, text de la interfície, metadades tècniques',
                '4': 'Gestionar les exempcions i registrar la justificació',
                '5': 'Aprovar i versionar',
            },
        },
        AI_A4: {
            name: "Notificació d'incidents greus",
            steps: {
                '1': "Crear el registre d'incident d'IA",
                '2': 'Classificar la gravetat',
                '3': "Executar accions de contenció i captura d'evidències",
                '4': "Generar l'exportació de la plantilla d'informe d'incident greu",
                '5': "Presentar a l'autoritat pel canal corresponent",
                '6': "Fer el seguiment de les respostes de l'autoritat i el tancament",
            },
        },
        AI_A5: {
            name: "Avaluació d'impacte de l'IA",
            steps: {
                '1': "Crear el registre de l'avaluació",
                '2': 'Emplenar les seccions requerides',
                '3': 'Adjuntar evidències — fluxos de dades, documentació del model, controls',
                '4': 'Aprovació interna',
                '5': "Exportar l'informe i emmagatzemar-lo a l'expedient",
            },
        },
        AI_A6: {
            name: 'Participació en proves controlades / sandbox',
            steps: {
                '1': "Completar el registre d'elegibilitat i preparació",
                '2': 'Preparar la sol·licitud i exportar-la',
                '3': 'Presentar pel canal corresponent',
                '4': "Fer el seguiment de l'aprovació",
                '5': 'Executar els punts de control i registres de monitoratge',
                '6': "Completar l'informe de finalització i el paquet de tancament",
            },
        },
        CYBERSEC_C1: {
            name: "Avaluació de seguretat de sistemes d'informació crítics",
            steps: {
                '1': "Classificar els sistemes d'informació segons els requisits nacionals de ciberseguretat",
                '2': "Realitzar l'avaluació de seguretat i la valoració del risc per als sistemes classificats",
                '3': 'Implementar les mesures de seguretat requerides i els controls de referència',
                '4': 'Documentar les evidències de compliment i programar revisions periòdiques',
            },
        },
        CYBERSEC_C2: {
            name: "Notificació d'incidents de ciberseguretat",
            steps: {
                '1': "Detectar, classificar i avaluar l'incident de ciberseguretat",
                '2': "Executar accions de resposta immediata i preservació d'evidències",
                '3': "Informar l'autoritat i participar en la coordinació nacional de resposta a incidents",
                '4': 'Realitzar la revisió postincident i documentar les lliçons apreses',
            },
        },
        CYBERSEC_C3: {
            name: 'Verificació del compliment de la localització de dades',
            steps: {
                '1': "Avaluar si s'apliquen els supòsits de localització de dades segons el tipus de servei i les categories de dades",
                '2': "Verificar que l'arquitectura d'emmagatzematge local i els controls de flux transfronterer estiguin implementats",
                '3': 'Avaluar els requisits de presència local (sucursal / oficina de representació) quan sigui aplicable',
                '4': "Documentar les evidències de compliment i conservar-les per a la revisió de l'autoritat",
            },
        },
        CYBERSEC_C4: {
            name: "Resposta a sol·licituds de cooperació de l'autoritat",
            steps: {
                '1': "Rebre i verificar la legitimitat i l'abast de la sol·licitud de l'autoritat",
                '2': 'Recopilar i preparar la informació sol·licitada o executar les accions requerides',
                '3': "Presentar la resposta a l'autoritat dins del termini exigit",
                '4': 'Registrar la divulgació i tancar el cas',
            },
        },
        CYBERSEC_C5: {
            name: 'Verificació de la qualificació del personal de ciberseguretat',
            steps: {
                '1': "Avaluar la postura de seguretat del proveïdor utilitzant el marc d'avaluació de ciberseguretat",
                '2': "Verificar l'abast d'accés del proveïdor a les dades i registrar-lo al registre d'accessos del proveïdor",
                '3': "Revisar la clàusula de notificació d'incidents del proveïdor i els requisits contractuals de ciberseguretat",
                '4': 'Programar revisions periòdiques de seguretat del proveïdor i monitorar el compliment',
            },
        },
        DATA_D1: {
            name: "Configuració de l'inventari i la classificació de dades",
            steps: {
                '1': "Catalogar tots els actius de dades amb els seus tipus i fonts al registre d'inventari de dades",
                '2': 'Classificar cada actiu per nivell de sensibilitat',
                '3': 'Definir regles de gestió per a cada nivell de classificació',
                '4': "Capturar evidències — captures de pantalla, diagrames d'arquitectura, controls d'accés",
                '5': 'Programar revisions periòdiques',
            },
        },
        DATA_D2: {
            name: "Governança de l'intercanvi de dades",
            steps: {
                '1': "Avaluar els nous acords d'intercanvi mitjançant l'avaluació del risc d'intercanvi de dades",
                '2': "Completar el flux d'aprovació intern",
                '3': "Registrar l'esdeveniment d'intercanvi al registre d'intercanvi/divulgació de dades",
                '4': "Capturar evidències — registres d'aprovació, documents contractuals",
                '5': "Revisió periòdica dels acords d'intercanvi",
            },
        },
        DATA_D3: {
            name: 'Avaluació de transferències transfrontereres de dades',
            steps: {
                '1': "Identificar les transferències transfrontereres a l'inventari de dades",
                '2': "Realitzar l'avaluació del risc de la transferència",
                '3': 'Aplicar garanties i mesures de governança',
                '4': "Documentar a la secció transfronterera de l'atestat de governança",
                '5': "Exportar el paquet d'avaluació per a auditoria",
            },
        },
        DATA_D4: {
            name: 'Atestat anual de governança',
            steps: {
                '1': 'Revisar la matriu RACI de governança',
                '2': "Verificar l'estat de les polítiques (governança, classificació, intercanvi, retenció)",
                '3': "Completar el paquet d'atestat",
                '4': "Obtenir l'aprovació interna",
                '5': "Exportar i arxivar l'atestat",
            },
        },
        DATA_D5: {
            name: "Gestió d'incidents de dades",
            steps: {
                '1': "Classificar l'incident utilitzant el registre de decisions",
                '2': "Executar la resposta a l'incident segons el manual",
                '3': 'Capturar evidències — registres, captures de pantalla, comunicacions',
                '4': 'Realitzar la revisió postincident',
                '5': "Si cal notificar a l'autoritat: crear el cas de presentació i presentar-lo al MIC",
                '6': 'Tancar i arxivar',
            },
        },
        ECOM_P1: {
            name: 'Notificació (Lloc web/aplicació de venda)',
            steps: {
                '1': 'Determinar la classificació: lloc/aplicació de venda vs. plataforma de marketplace/serveis',
                '2': "Crear el registre intern de l'expedient amb la identitat de la companyia, la informació del lloc/aplicació i les divulgacions requerides",
                '3': "Generar el paquet d'evidències — captures de pantalla de divulgacions, URL de polítiques, historial de versions",
                '4': 'Presentar via Online.gov.vn — pujar documents, registrar la marca de temps i la confirmació',
                '5': 'Fer el seguiment del processament — si es retorna per esmena, capturar les raons i tornar a presentar',
                '6': "Tancar — emmagatzemar la confirmació, vincular les evidències i la pista d'auditoria",
            },
        },
        ECOM_P2: {
            name: 'Registre (Plataformes de servei / Marketplaces)',
            steps: {
                '1': 'Determinar el model de plataforma i les obligacions — marketplace vs. mixt vs. comerç social',
                '2': "Construir l'expedient de registre — formulari de sol·licitud, esquema de prestació de serveis, condicions, normes de la plataforma",
                '3': 'Aprovacions internes — assignar el responsable jurídic/de compliment, aprovar per a la presentació',
                '4': "Presentar via Online.gov.vn — pujar l'expedient, registrar la marca de temps",
                '5': 'Bucle de processament — si es retorna per esmena, actualitzar i tornar a presentar',
                '6': 'Aprovació i operacionalització — emmagatzemar la confirmació, mantenir els registres de compliment continuat',
            },
        },
        ECOM_P3: {
            name: "Notificació d'actualització/canvi",
            steps: {
                '1': "Identificar el motiu del canvi — canvi material en els elements de l'expedient",
                '2': "Generar el paquet de canvi — resum del canvi, documents actualitzats, actualitzacions d'evidències",
                '3': "Presentar l'actualització i registrar l'estat de la presentació",
                '4': 'Fer el seguiment del processament i tancar',
            },
        },
        ECOM_P4: {
            name: 'Cessament / Desactivació',
            steps: {
                '1': "Preparar el paquet de cessament — data de cessament, motiu, pla de gestió de la protecció del consumidor, prova d'avís públic",
                '2': 'Presentar el cessament — registrar la marca de temps i la confirmació',
                '3': "Tancar — emmagatzemar l'estat final i les evidències",
            },
        },
        ECOM_P5: {
            name: 'Programa de segell de confiança / qualificació de credibilitat',
            steps: {
                '1': "Revisió d'elegibilitat i preparació",
                '2': "Preparar l'expedient utilitzant el formulari oficial",
                '3': 'Presentar i fer el seguiment',
                '4': 'Mantenir les evidències de compliment continuat',
            },
        },
        PDPL_P1: {
            name: "Presentació de l'expedient DPIA al MPS",
            steps: {
                '1': 'Completar la DPIA utilitzant la via dels formularis del Decret 356 (Mẫu số 02a/02b)',
                '2': 'Reunir els documents de portada i les evidències de suport',
                '5': "Conservar l'expedient i l'evidència de presentació per a la pista d'auditoria",
            },
        },
        PDPL_P2: {
            name: 'Sol·licitud de transferència transfronterera',
            steps: {
                '1': "Completar l'expedient de transferència transfronterera utilitzant els formularis del Decret 356 (Mẫu số 01a/01b, Mẫu số 09)",
            },
        },
        PDPL_P3: {
            name: 'Sol·licitud del certificat de servei de tractament de dades',
            steps: {
                '1': "Preparar l'expedient de sol·licitud de certificació segons els requisits del Decret 356",
                '2': 'Presentar la sol·licitud al MPS amb els documents de suport requerits',
                '3': 'Respondre als comentaris de revisió i sol·licituds de suplement del MPS',
            },
        },
        PDPL_P4: {
            name: "Tramitació de sol·licituds de drets de l'interessat",
            steps: {
                '1': "Rebre i registrar la sol·licitud; verificar la identitat de l'interessat en 2 dies hàbils",
                '2': 'Avaluar el tipus de sol·licitud i determinar el termini aplicable (10/15/20 dies segons el tipus)',
                '3': 'Executar les accions de compliment a través dels sistemes i departaments rellevants',
                '4': "Preparar i enviar la resposta a l'interessat amb el resultat i la justificació",
                '5': 'Tancar el cas i conservar les evidències per a auditoria; documentar qualsevol justificació de denegació',
            },
        },
        PDPL_P5: {
            name: 'Notificació de violacions al MPS',
            steps: {
                '1': "Detectar i avaluar la violació; determinar si s'aplica l'obligació de notificació i resoldre el termini de notificació aplicable a partir dels registres de terminis instal·lats",
                '2': 'Preparar el paquet de notificació segons els requisits del PDPL',
                '3': 'Presentar la notificació al MPS dins del termini de notificació resolt a partir dels registres de terminis instal·lats, comptat des del descobriment',
                '4': 'Respondre a les sol·licituds de seguiment del MPS; presentar suplements i evidències de coordinació',
            },
        },
        TELECOM_T1: {
            name: "Gestió de sol·licituds de l'autoritat",
            steps: {
                '1': "Rebre i registrar la sol·licitud de l'autoritat al registre d'entrada",
                '2': "Verificar la identitat de l'autoritat, la base jurídica i l'abast de la sol·licitud",
                '3': "Realitzar la revisió jurídica interna i obtenir l'aprovació de la direcció",
                '4': 'Preparar el paquet de resposta amb les evidències reunides',
                '5': 'Redactar i aprovar la carta de resposta',
                '6': 'Crear el cas de presentació i enviar la resposta pel canal corresponent',
                '7': "Registrar la divulgació i fer el seguiment de l'acusament de recepció de l'autoritat",
                '8': "Tancar el cas i conservar les evidències per a la pista d'auditoria",
            },
        },
        TELECOM_T2: {
            name: 'Gestió de divulgacions',
            steps: {
                '1': "Rebre la sol·licitud de divulgació (consentiment de l'usuari, obligació legal o sol·licitud de l'autoritat)",
                '2': "Completar el flux d'aprovació de la divulgació",
                '3': 'Executar la divulgació i registrar-la al registre',
                '4': "Capturar evidències — registres d'aprovació, registres de comunicacions",
                '5': 'Revisió periòdica del registre de divulgacions',
            },
        },
        TELECOM_T3: {
            name: "Configuració de la política de confidencialitat i del control d'accés",
            steps: {
                '1': 'Redactar i aprovar la política de confidencialitat',
                '2': "Redactar i aprovar la política de control d'accés i de registre",
                '3': "Documentar les activitats de tractament al registre de tractament d'informació de l'usuari",
                '4': "Capturar evidències — documents de política, evidències d'implementació",
                '5': 'Programar revisions periòdiques',
            },
        },
        TELECOM_T4: {
            name: "Avaluació d'incidents de telecomunicacions",
            steps: {
                '1': "Classificar i avaluar l'incident",
                '2': 'Determinar els requisits de notificació',
                '3': "Si es requereix notificació a l'autoritat: crear el cas de presentació",
                '4': 'Capturar i preservar evidències',
                '5': 'Resolució i tancament',
            },
        },
        TELECOM_T5: {
            name: 'Gestió de la retenció i la supressió',
            steps: {
                '1': 'Definir el calendari de retenció i supressió per categoria de dades',
                '2': 'Implementar els procediments de supressió',
                '3': 'Capturar evidències — registres de supressió',
                '4': 'Revisió periòdica dels calendaris',
            },
        },
    },
    framework_fines: {
        VN_CYBERSEC_LAW: {},
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-A': 'Registre / Notificació / Preparació administrativa',
            'ECOM-FINE-B': 'Queixes i protecció del consumidor',
            'ECOM-FINE-C': 'Monitoratge i execució de retirades',
            'ECOM-FINE-D': 'Operacions transfrontereres',
            'ECOM-FINE-E': 'Normes de la plataforma i transparència',
        },
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-A': "Incompliments en la gestió de sol·licituds de l'autoritat",
            'TELECOM-FINE-B': 'Confidencialitat i divulgació no autoritzada',
            'TELECOM-FINE-C': "Incompliments en el control d'accés i el registre",
            'TELECOM-FINE-D': 'Infraccions en retenció i supressió',
            'TELECOM-FINE-E': "Incompliments en l'avaluació d'incidents de telecomunicacions",
        },
    },
    framework_fines_detail: {
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-E': {
                fine_individual: 'Fins a 100 milions de VND',
                fine_org: 'Fins a 200 milions de VND',
                deadlines: 'Immediat des de la detecció',
                remediation: [
                    'Mandatory incident classification',
                    'Mandatory severity and decision records',
                    'Mandatory evidence preservation for confidentiality incidents',
                    'Mandatory post-incident closure',
                ],
            },
            'TELECOM-FINE-D': {
                fine_individual: 'Fins a 100 milions de VND',
                fine_org: 'Fins a 200 milions de VND',
                deadlines: 'Obligació continuada',
                remediation: [
                    'Mandatory retention schedule implementation',
                    'Mandatory deletion evidence preservation',
                    'Mandatory retention exception decision records',
                ],
            },
            'TELECOM-FINE-C': {
                fine_individual: 'Fins a 100 milions de VND',
                fine_org: 'Fins a 200 milions de VND',
                deadlines: 'Obligació continuada',
                remediation: [
                    'Mandatory access-control policy implementation',
                    'Mandatory logging of access events',
                    'Mandatory review of access rights',
                ],
            },
            'TELECOM-FINE-B': {
                fine_individual: 'Fins a 100 milions de VND',
                fine_org: 'Fins a 200 milions de VND',
                deadlines: 'Obligació continuada',
                remediation: [
                    'Mandatory confidentiality policy implementation',
                    'Mandatory disclosure approval workflow',
                    'Mandatory disclosure trail maintenance',
                    'Mandatory user information access controls',
                ],
            },
            'TELECOM-FINE-A': {
                fine_individual: 'Fins a 100 milions de VND',
                fine_org: 'Fins a 200 milions de VND',
                deadlines: "Segons s'especifiqui a la sol·licitud de l'autoritat",
                remediation: [
                    'Mandatory intake and verification of authority request',
                    'Mandatory response within prescribed timeline',
                    'Mandatory disclosure log entry',
                    'Mandatory evidence preservation',
                ],
            },
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-E': {
                fine_individual: 'Fins a 80 milions de VND',
                fine_org: 'Fins a 160 milions de VND',
                deadlines: 'Obligació continuada',
                remediation: [
                    'Mandatory platform rules publication and versioning',
                    'Mandatory user-facing disclosures',
                    'Mandatory seller/affiliate/livestream obligations evidenced',
                    'Mandatory control history and approval chain',
                ],
            },
            'ECOM-FINE-D': {
                fine_individual: 'Fins a 80 milions de VND',
                fine_org: 'Fins a 160 milions de VND',
                deadlines: 'Obligació continuada',
                remediation: [
                    'Mandatory cross-border obligations assessment',
                    'Mandatory authorized representative readiness',
                    'Mandatory cross-border operations register',
                    'Mandatory evidence of compliance action',
                ],
            },
            'ECOM-FINE-C': {
                fine_individual: 'Fins a 80 milions de VND',
                fine_org: 'Fins a 160 milions de VND',
                deadlines: 'Obligació continuada',
                remediation: [
                    'Mandatory review of prohibited or unlawful listings',
                    'Mandatory takedown decision records',
                    'Mandatory evidence preservation',
                    'Mandatory seller notification and closure trail',
                ],
            },
            'ECOM-FINE-B': {
                fine_individual: 'Fins a 80 milions de VND',
                fine_org: 'Fins a 160 milions de VND',
                deadlines: "Segons s'especifiqui per les obligacions de gestió de queixes",
                remediation: [
                    'Mandatory complaint intake procedure',
                    'Mandatory resolution record',
                    'Mandatory complaint audit trail',
                    'Mandatory escalation for cross-border seller issues',
                ],
            },
            'ECOM-FINE-A': {
                fine_individual: 'Fins a 80 milions de VND',
                fine_org: 'Fins a 160 milions de VND',
                deadlines: 'Abans de començar les operacions de comerç electrònic',
                remediation: [
                    'Mandatory platform classification',
                    'Mandatory notification or registration completion',
                    'Mandatory evidence of portal submission',
                    'Mandatory update/termination tracking',
                ],
            },
        },
        VN_CYBERSEC_LAW: {},
    },
    framework_procedures_detail: {
        VN_DP_DRAFT_P7: {
            trigger: 'Operació continuada de la plataforma',
            outputs: [
                'Risk assessment record',
                'Early warning log',
                'Remediation record',
                'Review trail',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Operador de la plataforma / Risc',
                '2': 'Operador de la plataforma / Seguretat',
                '3': 'Operador de la plataforma / Risc',
                '4': 'Operador de la plataforma / Seguretat',
                '5': 'Operador de la plataforma',
                '6': 'Compliment',
            },
            step_outputs: {
                '1': ['Risk assessment record'],
                '2': ['Early warning log'],
                '3': ['Risk review findings'],
                '4': ['Risk register entries'],
                '5': ['Remediation record'],
                '6': ['Review trail'],
            },
        },
        VN_DP_DRAFT_P6: {
            trigger: 'Un participant presenta una sol·licitud de queixa o disputa',
            outputs: [
                'Complaint intake record',
                'Dispute resolution record',
                'Evidence pack',
                'Case closure record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Operador de la plataforma',
                '2': 'Operador de la plataforma',
                '3': 'Operador de la plataforma',
                '4': 'Operador de la plataforma',
                '5': 'Operador de la plataforma / Jurídic',
                '6': 'Operador de la plataforma',
                '7': 'Compliment',
            },
            step_outputs: {
                '1': ['Complaint intake record'],
                '2': ['Completeness check', 'Supplement request (if any)'],
                '3': ['Handling approach notice'],
                '4': ['Evidence pack'],
                '5': ['Assessment findings'],
                '6': ['Dispute resolution record'],
                '7': ['Case closure record', 'Archived evidence pack'],
            },
        },
        VN_DP_DRAFT_P5: {
            trigger: 'El lliurament es completa després que es compleixin les condicions contractuals i de pagament',
            outputs: [
                'Delivery record',
                'Certificate of property rights over data',
                'Transaction register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Venedor / Operador de la plataforma',
                '2': 'Operador de la plataforma',
                '3': 'Operador de la plataforma',
                '4': 'Operador de la plataforma',
                '5': 'Operador de la plataforma',
            },
            step_outputs: {
                '1': ['Delivery record'],
                '2': ['Delivery event log entry'],
                '3': ['Property rights certificate'],
                '4': ['Transaction register entry'],
                '5': ['Verification access record'],
            },
        },
        VN_DP_DRAFT_P4: {
            trigger: 'Un comprador inicia una transacció per a dades, productes o serveis',
            outputs: [
                'Transaction initiation record',
                'Electronic contract',
                'Payment confirmation',
                'Delivery authorization record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Comprador',
                '2': 'Comprador / Operador de la plataforma',
                '3': 'Operador de la plataforma',
                '4': 'Comprador / Venedor',
                '5': 'Operador de la plataforma / Pagament',
                '6': 'Operador de la plataforma',
            },
            step_outputs: {
                '1': ['Listing review decision'],
                '2': ['Transaction initiation record'],
                '3': ['Electronic contract record'],
                '4': ['Contract acceptance record'],
                '5': ['Payment confirmation'],
                '6': ['Delivery authorization record'],
            },
        },
        VN_DP_DRAFT_P3: {
            trigger: "Un participant vol avaluar productes/serveis de dades en l'entorn aïllat",
            outputs: [
                'Sandbox request',
                'Sandbox scope record',
                'Output review record',
                'Sandbox termination record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Participant',
                '2': 'Operador de la plataforma / Participant',
                '3': 'Operador de la plataforma / TI',
                '4': 'Operador de la plataforma / Seguretat',
                '5': 'Operador de la plataforma',
                '6': 'Operador de la plataforma',
                '7': 'Operador de la plataforma / TI',
            },
            step_outputs: {
                '1': ['Sandbox request'],
                '2': ['Sandbox scope record'],
                '3': ['Provisioned environment'],
                '4': ['Security monitoring log'],
                '5': ['Output review record'],
                '6': ['Extraction approval decision'],
                '7': ['Sandbox termination record'],
            },
        },
        VN_DP_DRAFT_P2: {
            trigger: 'Un venedor vol llistar dades, producte o servei de dades per a la negociació',
            outputs: [
                'Listing application',
                'Lawful-source verification record',
                'Technical validation record',
                'Quality disclosure record',
                'Listing approval record',
                'Listing register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Venedor',
                '2': 'Venedor / Jurídic',
                '3': 'Venedor / TI',
                '4': 'Venedor',
                '5': 'Venedor',
                '6': 'Operador de la plataforma',
                '7': 'Operador de la plataforma',
                '8': 'Operador de la plataforma',
            },
            step_outputs: {
                '1': ['Listing application'],
                '2': ['Lawful-source verification record'],
                '3': ['Technical validation record'],
                '4': ['Quality disclosure record'],
                '5': ['Submitted listing dossier'],
                '6': ['Review findings'],
                '7': ['Listing approval record'],
                '8': ['Published listing', 'Listing register entry'],
            },
        },
        VN_DP_DRAFT_P1: {
            trigger: "Un venedor o comprador s'incorpora a la plataforma de dades",
            outputs: [
                'Seller / buyer onboarding record',
                'Participant register entry',
                'Account setup evidence',
                'Approval record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Operador de la plataforma',
                '2': 'Operador de la plataforma / Jurídic',
                '3': 'Operador de la plataforma / Jurídic',
                '4': 'Operador de la plataforma',
                '5': 'Operador de la plataforma',
                '6': 'Operador de la plataforma',
                '7': 'Compliment',
            },
            step_outputs: {
                '1': ['Participant type decision'],
                '2': ['Legal capacity verification record'],
                '3': ['Conditional-business check record'],
                '4': ['Transaction account registration record'],
                '5': ['Payment account link record'],
                '6': ['Participation approval record'],
                '7': ['Participant register entry', 'Audit trail'],
            },
        },
        PDPL_P1: {
            trigger: "Inici d'un tractament de dades personals que requereix avaluació d'impacte",
            outputs: ['DPIA dossier', 'Submission confirmation', 'Supplement package'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Equip de Privadesa',
                '2': 'Equip de Privadesa',
                '3': 'Jurídic / Compliment',
                '4': 'DPO / Equip de Privadesa',
                '5': 'Equip de Compliment',
            },
            step_outputs: {
                '1': ['Completed DPIA dossier'],
                '2': ['Dossier package', 'Cover letter'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Updated dossier'],
                '5': ['Archived dossier', 'Audit evidence pack'],
            },
        },
        PDPL_P2: {
            trigger: 'Intenció de transferir dades personals fora del Vietnam',
            outputs: ['Transfer dossier', 'Submission confirmation', 'Supplement response'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Equip de Privadesa',
                '2': 'Jurídic / Compliment',
                '3': 'DPO / Equip de Privadesa',
            },
            step_outputs: {
                '1': ['Transfer dossier package'],
                '2': ['Submission confirmation', 'Tracking reference'],
                '3': ['Supplement response', 'Updated dossier'],
            },
        },
        PDPL_P3: {
            trigger: 'Organització que presta serveis de tractament de dades amb requisit de certificació',
            outputs: ['Certification dossier', 'Certificate', 'Re-issue and exchange record'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Jurídic / Compliment',
                '2': 'Jurídic / Compliment',
                '3': 'DPO / Equip de Privadesa',
                '4': 'Equip de Compliment',
            },
            step_outputs: {
                '1': ['Certification application dossier'],
                '2': ['Submission confirmation'],
                '3': ['Supplement response'],
                '4': ['Certificate record', 'Re-issue and exchange record'],
            },
        },
        PDPL_P4: {
            trigger: "Recepció d'una sol·licitud de drets de l'interessat",
            outputs: [
                'DSR intake record',
                'Identity verification',
                'Response letter',
                'Case closure report',
            ],
            channels: ['Online portal', 'Email', 'In-person'],
            responsible: {
                '1': 'Equip de Privadesa / Atenció al client',
                '2': 'DPO / Equip de Privadesa',
                '3': 'Responsables de departament / TI',
                '4': 'DPO / Equip de Privadesa',
                '5': 'Equip de Compliment',
            },
            step_outputs: {
                '1': ['DSR intake record', 'Identity verification record'],
                '2': ['Request assessment', 'Deadline assignment'],
                '3': ['Fulfillment evidence', 'Department confirmations'],
                '4': ['Response letter'],
                '5': ['Case closure report', 'Audit evidence pack'],
            },
        },
        PDPL_P5: {
            trigger: "Descobriment d'una violació o infracció de protecció de dades personals",
            outputs: [
                'Breach assessment record',
                'Notification package',
                'Submission confirmation',
                'Supplement package',
            ],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Equip de Resposta a Incidents / DPO',
                '2': 'DPO / Equip de Privadesa',
                '3': 'Jurídic / Compliment',
                '4': 'DPO / Equip de Resposta a Incidents',
            },
            step_outputs: {
                '1': ['Breach assessment record', 'Notification decision record'],
                '2': ['Breach notification package'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Coordination evidence'],
            },
        },
    },
    framework_guidance_references: {
        'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era': 'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era',
        'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.': 'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.',
        'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.',
        'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.',
        'Government/MOIT public reporting.': 'Government/MOIT public reporting.',
        'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.': 'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.',
        'MPS consultation dossier — draft information-systems protection decree.': 'MPS consultation dossier — draft information-systems protection decree.',
        'MPS consultation — draft general implementing decree.': 'MPS consultation — draft general implementing decree.',
        'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).': 'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).',
        'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).': 'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).',
    },
    framework_deadline_references: {
        'ComplianceOne internal review': 'ComplianceOne internal review',
        'ComplianceOne internal review date': 'ComplianceOne internal review date',
        'Draft personnel provisions': 'Draft personnel provisions',
        'Draft specialist provisions': 'Draft specialist provisions',
        'Internal child-instrument review.': 'Internal child-instrument review.',
        'Internal platform review.': 'Internal platform review.',
        'Internal recommendation based on draft lifecycle governance': 'Internal recommendation based on draft lifecycle governance',
        'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).': 'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).',
        'Internal recommended digital-transformation control review.': 'Internal recommended digital-transformation control review.',
        'Internal recommended review of digital-system minimum requirements (annual or on material change).': 'Internal recommended review of digital-system minimum requirements (annual or on material change).',
        'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).': 'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).',
        'National digital technology industry database — quarterly update (where direct scope is confirmed).': 'National digital technology industry database — quarterly update (where direct scope is confirmed).',
        'Official MPS consultation page': 'Official MPS consultation page',
        'Platform operational control — not a statutory deadline': 'Platform operational control — not a statutory deadline',
        'PDPL Art. 9': 'PDPL Art. 9',
        'PDPL Art. 17': 'PDPL Art. 17',
        'PDPL Art. 23': 'PDPL Art. 23',
        'Decree 13 Art. 24': 'Decret 13 Art. 24',
        'MoPS Admin Procedures': 'Procediments administratius del MoPS (Bộ Công an)',
        'Annual cycle': 'Cicle anual',
        'Decree 13/2023/NĐ-CP Art. 24': 'Decret 13/2023/NĐ-CP Art. 24',
        'Decree 356/2025/NĐ-CP Art. 25(4)': 'Decret 356/2025/NĐ-CP Art. 25(4)',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2': 'Decret 356/2025/NĐ-CP Art. 5 Cl. 2',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5': 'Decret 356/2025/NĐ-CP Art. 5 Cl. 2–5',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 3': 'Decret 356/2025/NĐ-CP Art. 5 Cl. 3',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 4': 'Decret 356/2025/NĐ-CP Art. 5 Cl. 4',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 5': 'Decret 356/2025/NĐ-CP Art. 5 Cl. 5',
        'Decree 356/2025/NĐ-CP Art. 8(3)': 'Decret 356/2025/NĐ-CP Art. 8(3)',
        'Draft Decree on Data Platforms 2026': 'Projecte de Decret sobre Plataformes de Dades 2026',
        'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025': 'Llei 122/2025/QH15 Cap. III; NĐ 98/2020; NĐ 24/2025',
        'Law 122/2025/QH15 Ch. IV (Consumer Protection)': 'Llei 122/2025/QH15 Cap. IV (Protecció del consumidor)',
        'Law 122/2025/QH15 — Online.gov.vn operational procedure': 'Llei 122/2025/QH15 — Procediment operatiu Online.gov.vn',
        'Law 134/2025/QH15 Article 12': 'Llei 134/2025/QH15 Article 12',
        'Law 134/2025/QH15 Article 21': 'Llei 134/2025/QH15 Article 21',
        'Law 134/2025/QH15 Article 27': 'Llei 134/2025/QH15 Article 27',
        'Law 134/2025/QH15 Articles 9–10': 'Llei 134/2025/QH15 Articles 9–10',
        'Law 24/2023/QH15 — article mapping pending': "Llei 24/2023/QH15 — mapatge d'articles pendent",
        'Law 60/2024/QH15 (internal control target)': 'Llei 60/2024/QH15 (objectiu de control intern)',
        'Law 91/2025/QH15 Art. 21': 'Llei 91/2025/QH15 Art. 21',
        'Law 91/2025/QH15 Art. 23': 'Llei 91/2025/QH15 Art. 23',
        'Policy-defined': 'Definit per política interna',
        Quarterly: 'Trimestral',
        'QĐ 778/QĐ-BCA-A05 administrative procedure': 'Procediment administratiu QĐ 778/QĐ-BCA-A05',
        'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)': "objectiu de control intern (Llei 116/2025/QH15 Art. 24(2)(b), 41(5) — calendari exacte pendent de l'instrument d'aplicació)",
        'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)': "objectiu de control intern (Llei 116/2025/QH15 Art. 41(3) — calendari exacte pendent de l'instrument d'aplicació)",
        'source verification pending': 'verificació de la font pendent',
        'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)': "Circular 83/2025/TT-NHNN — Reporting d'auditoria interna ad hoc al SBV (Capa 5)",
        'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)': 'Circular 83/2025/TT-NHNN — Informe ICAAP anual (Phụ lục III) (Capa 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)': 'Circular 83/2025/TT-NHNN — Phụ lục I anual (Capa 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)': 'Circular 83/2025/TT-NHNN — Phụ lục IV anual (Capa 5)',
        'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)': 'Circular 83/2025/TT-NHNN — Informe anual de gestió del risc (Phụ lục II) (Capa 5)',
        'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)': "Circular 83/2025/TT-NHNN — Responsabilitat de gestió d'actius (Capa 2, tema C)",
        "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)": "Circular 83/2025/TT-NHNN — Supervisió del Consell d'Administració / Consell de Membres (Capa 2, tema A)",
        'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Comitès del Consell (Capa 2, tema A)',
        'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Decisions i actes del Consell (Capa 2, tema A)',
        'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Procediments de control de sucursals (Capa 2, tema C)',
        'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Avaluació de compliment de les normes internes (Capa 2, tema D)',
        'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Funció de compliment (Capa 2, tema D)',
        'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Monitoratge de la remediació de compliment (Capa 2, tema D)',
        'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risc de concentració (Capa 3, tema E)',
        'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)': "Circular 83/2025/TT-NHNN — Conflicte d'interessos (Capa 2, tema C)",
        'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)': 'Circular 83/2025/TT-NHNN — Cultura de control (Capa 2, tema A/B)',
        'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risc de crèdit (Capa 3, tema E)',
        'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Autoritat delegada (Capa 2, tema C)',
        'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)': "Circular 83/2025/TT-NHNN — Notificació d'implementació anticipada (Capa 5)",
        'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Responsabilitat del director general (Capa 2, tema A)',
        'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — ICAAP (Capa 3, tema E)',
        'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)': "Circular 83/2025/TT-NHNN — Risc de tipus d'interès en la cartera bancària (Capa 3, tema E)",
        'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)': "Circular 83/2025/TT-NHNN — Carta d'auditoria interna (Capa 2, tema H)",
        'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)': "Circular 83/2025/TT-NHNN — Encàrrecs d'auditoria interna (Tema H)",
        'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)': "Circular 83/2025/TT-NHNN — Comunicació de troballes d'auditoria interna (Capa 2, tema H)",
        'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)': "Circular 83/2025/TT-NHNN — Troballes d'auditoria interna i accions correctives (Tema H)",
        'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)': "Circular 83/2025/TT-NHNN — Seguiment de l'auditoria interna (Tema H)",
        'Circular 83/2025/TT-NHNN — Internal Audit Governance': "Circular 83/2025/TT-NHNN — Governança de l'auditoria interna",
        'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)': "Circular 83/2025/TT-NHNN — Independència de l'auditoria interna (Capa 2, tema H)",
        'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)': "Circular 83/2025/TT-NHNN — Pla d'auditoria interna (Capa 2/Tema H)",
        'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)': "Circular 83/2025/TT-NHNN — Reporting d'auditoria interna al SBV (Capa 2, tema H)",
        'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)': 'Circular 83/2025/TT-NHNN — Governança del sistema de control intern (Capa 2)',
        'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)': "Circular 83/2025/TT-NHNN — Procediment d'informació interna (Capa 2, tema C)",
        'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)': "Circular 83/2025/TT-NHNN — Registre d'informes interns (Capa 4, tema F)",
        'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Monitoratge de canvis legals (Capa 2, tema D)',
        'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risc legal i reputacional (Capa 3, tema E)',
        'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Escalada de superació de límits (Capa 3, tema E)',
        'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risc de liquiditat (Capa 3, tema E)',
        'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Revisió anual del MIS (Capa 4, tema F)',
        'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Còpia de seguretat i continuïtat del MIS (Capa 4, tema F)',
        'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)': "Circular 83/2025/TT-NHNN — Mecanismes d'escalada del MIS (Capa 4, tema F)",
        'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Fiabilitat i confidencialitat del MIS (Capa 4, tema F)',
        'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Fonts de dades de risc del MIS (Capa 4, tema F)',
        'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)': "Circular 83/2025/TT-NHNN — Sistema d'informació de gestió (Capa 4)",
        'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risc de mercat (Capa 3, tema E)',
        'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)': 'Circular 83/2025/TT-NHNN — Registre de riscos materials (Capa 3)',
        'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risc de model (Capa 3, tema E)',
        'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Revisió de nous productes (Capa 3, tema E)',
        'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risc operacional (Capa 3, tema E)',
        'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)': "Circular 83/2025/TT-NHNN — BCP d'externalització (Capa 2, tema G)",
        'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)': "Circular 83/2025/TT-NHNN — Contractes d'externalització (Capa 2, tema G)",
        'Circular 83/2025/TT-NHNN — Outsourcing Controls': "Circular 83/2025/TT-NHNN — Controls d'externalització",
        'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)': "Circular 83/2025/TT-NHNN — Diligència deguda d'externalització (Capa 2, tema G)",
        'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)': "Circular 83/2025/TT-NHNN — Proveïdor de contingència d'externalització (Capa 2, tema G)",
        'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)': "Circular 83/2025/TT-NHNN — Materialitat d'externalització (Capa 2, tema G)",
        'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)': "Circular 83/2025/TT-NHNN — Monitoratge d'externalització (Capa 2, tema G)",
        'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)': 'Circular 83/2025/TT-NHNN — Referències tècniques Phụ lục V/VI (Capa 5)',
        'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Registre de polítiques (Capa 2, tema C)',
        'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Ètica professional (Capa 2, tema C)',
        'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)': 'Circular 83/2025/TT-NHNN — Apetit de risc (Capa 3)',
        'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Coordinació del Comitè de Risc (Capa 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Governança de dades de risc (Capa 3, tema E/F) — esglaonat fins al 01-01-2028',
        'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Revisió de límits de risc (Capa 3, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Marc de límits de risc (Capa 3, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Funció de gestió del risc (Capa 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Metodologia i models de risc (Capa 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Reporting del risc (Capa 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Cadència del reporting del risc (Capa 4, tema F)',
        'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)': 'Circular 83/2025/TT-NHNN — Presentació de remediació al SBV (Capa 5)',
        'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)': 'Circular 83/2025/TT-NHNN — Canals de presentació al SBV (Capa 5)',
        'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)': 'Circular 83/2025/TT-NHNN — Aplicabilitat sectorial (Capa 1)',
        'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Segregació de funcions (Capa 2, tema C)',
        'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)': "Circular 83/2025/TT-NHNN — Supervisió de l'alta direcció (Capa 2, tema A)",
        'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risc estratègic (Capa 3, tema E)',
        'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01': "Circular 83/2025/TT-NHNN — Proves d'estrès (Capa 3, tema E) — esglaonat fins al 01-01-2028",
        'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Consell de Supervisió (Capa 2, tema A)',
        'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Registre de troballes de supervisió (Capa 4, tema F)',
        'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)': 'Circular 83/2025/TT-NHNN — Rols i escalada de les Tres Línies (Capa 2, tema B)',
        'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)': 'Circular 83/2025/TT-NHNN — Tres Línies de Defensa (Capa 2)',
        'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)': "Circular 83/2025/TT-NHNN — Escalada d'infraccions (Capa 2, tema D)",
        // Sprint 62.49.1 (E12.5 pass 1, P3) — consumer-rights and food-traceability references.
        'Law 19/2023/QH15 — Điều 31 khoản 2': 'Law 19/2023/QH15 — Điều 31 khoản 2',
        'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.': 'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.',
        'Circular 11/2026/TT-BCT — Điều 6 khoản 2': 'Circular 11/2026/TT-BCT — Điều 6 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 12 khoản 2': 'Circular 11/2026/TT-BCT — Điều 12 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 1': 'Circular 11/2026/TT-BCT — Điều 15 khoản 1',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 2': 'Circular 11/2026/TT-BCT — Điều 15 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 2': 'Circular 63/2026/TT-BCT — Điều 10 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 3': 'Circular 63/2026/TT-BCT — Điều 10 khoản 3',
        'Circular 63/2026/TT-BCT — Điều 19 khoản 1': 'Circular 63/2026/TT-BCT — Điều 19 khoản 1',
    },
    framework_authorities: {
        'Quốc hội (National Assembly)': 'Quốc hội (Assemblea Nacional)',
        'Ministry of Public Security / A05 Department': 'Ministeri de Seguretat Pública (Bộ Công an) / Departament A05',
        'Ministry of Public Security': 'Ministeri de Seguretat Pública (Bộ Công an)',
        'Ministry of Information and Communications': "Ministeri d'Informació i Comunicacions (Bộ Thông tin và Truyền thông)",
        'Government of Vietnam': 'Govern del Vietnam',
        'European Data Protection Board (EDPB)': 'Comitè Europeu de Protecció de Dades (EDPB)',
        'ENISA / National CSIRTs': 'ENISA / CSIRT nacionals',
        'State Bank of Vietnam': 'Banc Estatal del Vietnam (Ngân hàng Nhà nước)',
    },
    tour_titles: {
        access_accountability: 'Accés a la visita de responsabilitat',
        assessments: "Visita d'avaluació",
        audit_trail: "Visita d'auditoria",
        classification: 'Tour de classificació',
        compliance_forms: 'Tour de formularis de compliment',
        connectors: 'Tour de connectors',
        consent_properties: 'Tour de propietats de consentiment',
        consent: 'Tour de consentiment',
        dashboard: 'Visita guiada al tauler',
        data_mapping: 'Tour de mapeig de dades',
        data_subjects: 'Recorregut d’interessats',
        deletion_orchestration: 'Deletion Orchestration Tour',
        discovery: 'Tour de descoberta',
        governance: 'Tour de govern',
        help_center: "Visita guiada al Centre d'ajuda",
        incident_ops: "Tour d'operacions d'incidències",
        incidents: "Tour d'incidències",
        localization: 'Tour de localització',
        monitoring: 'Visita de seguiment',
        onboarding: "Tour d'incorporació",
        organizations: "Tour d'Organitzacions",
        partner_workspace: "Visita a l'espai de treball dels socis",
        platform_settings: 'Visita guiada a la configuració de la plataforma',
        privacy_scanner: "Tour de l'escàner de privadesa",
        program_governance: 'Tour de govern del programa',
        rights_requests: 'Tour de sol·licituds de drets',
        task_management: 'Tour de gestió de tasques',
        vendor_risk: 'Tour de Risc del Proveïdor',
    },
    tour_descriptions: {
        access_accountability: "Com a responsable d'auditoria interna (P-VN-10), revisa les proves d'accés, l'ús de permisos d'auditoria i investiga patrons anòmals per demostrar la integritat i la confidencialitat de l'GDPR de l'article 5(1)(f).",
        assessments: 'Com a empresa DPO (P-VN-01), duu a terme DPIA seguint el Decret 356 Mau so 02a, identifiqueu les llacunes de compliment i avalueu els riscos de transferència transfronterera sota PDPL.',
        audit_trail: "Com a responsable d'auditoria interna (P-VN-10), consulteu proves entre mòduls, munteu paquets d'auditoria i exporteu registres d'evidència de manipulació per a enviaments regulatoris.",
        classification: 'Com a administrador de dades (P-VN-05), defineix taxonomies de classificació de dades, assigna etiquetes de sensibilitat, aplica polítiques de retenció i resol les llacunes de cobertura necessàries per al compliment de PDPL.',
        compliance_forms: 'Com a empresa DPO (P-VN-01), busqueu, completeu i envieu formularis reguladors oficials vietnamites, inclosos les plantilles Phu luc I, Phu luc II i Mau so per a la presentació del Comitè PDP.',
        connectors: 'Com a responsable de sistemes informàtics (P-VN-05), captureu els requisits empresarials per a les integracions, mapeu els connectors als fluxos de dades de compliment i verifiqueu la preparació del POC abans de posar-se en marxa.',
        consent_properties: 'Com a DPO Enterprise (P-VN-01), configureu el desplegament de CMP: registre de propietat digital, plantilles daurades per a bàners de consentiment PDPL coherents i matrius vinculants que vinculen propietats amb activitats de processament.',
        consent: 'Com a empresa DPO (P-VN-01), gestioneu el cicle de vida de la política de consentiment: definiu els propòsits de recollida, feu complir els drets de retirada de PDPL i integreu-vos amb la vostra plataforma de gestió de consentiment.',
        dashboard: "Com a DPO de l'empresa (P-VN-01), orienteu-vos al voltant del centre de comandaments de la postura de compliment que mostra els terminis de PDPL, l'incompliment dels SLA i els KPI de preparació per a l'auditoria.",
        data_mapping: "Com a administrador de dades (P-VN-05), crea el teu inventari ROPA, mapeja els fluxos de dades transfronterers i manté el registre d'activitats de processament requerit per PDPL.",
        data_subjects: 'Com a delegat de protecció de dades empresarial o responsable de compliment del sector educatiu, gestioneu perfils unificats d’interessats que vinculen registres de consentiment, sol·licituds de drets i activitat cronològica a tota la plataforma.',
        deletion_orchestration: "Com a DPO (P-VN-01), verifiqueu la supressió de dades d'extrem a extrem en sistemes interns i proveïdors de tercers, amb cadenes de proves criptogràfiques per al compliment del dret d'esborrat de l'article 17 de GDPR.",
        discovery: "Com a administrador de dades (P-VN-05), executeu exploracions automatitzades per descobrir dades personals a través dels sistemes connectats, revisar les troballes per fases i introduïu els resultats al mòdul Classificació per a l'etiquetatge PDPL.",
        governance: 'Com a Gestor de riscos de proveïdors (P-VN-08), manteniu el registre de proveïdors, feu un seguiment de les cadenes de subprocessadors, avalueu els riscos de transferència transfronterera i feu complir els manuals de govern sota PDPL.',
        help_center: 'Per a totes les persones: DPO, auditors, responsables de TI i gestors de compliment, trobeu respostes, guies pràctiques i documentació específica del mòdul per donar suport als vostres fluxos de treball diaris de compliment.',
        incident_ops: "Com a comandant d'incident (P-VN-09), activeu els llibres de resposta, feu un seguiment del compliment de SLA amb la finestra de notificació de PDPL de 72 hores, gestioneu les escalades i coordineu la resposta del proveïdor.",
        incidents: "Com a comandant d'incident (P-VN-09), detecteu i informeu les infraccions de dades amb obligacions de notificació PDPL de 72 hores al Comitè PDP, trieu per gravetat i creeu dossiers d'incidències de grau d'auditoria.",
        localization: 'Com a empresa DPO (P-VN-01), gestioneu el cicle de vida del marc regulador: instal·leu paquets de mercat vietnamita, activeu els marcs PDPL i el Decret 13, assigneu els requisits als mòduls de la plataforma i gestioneu les traduccions.',
        monitoring: "Com a DPO de l'empresa (P-VN-01), establiu programes de control de compliment continus, feu un seguiment dels canvis normatius, mesureu els KPI i gestioneu els casos d'interacció regulatori sota PDPL.",
        onboarding: "Com a entrenador de compliment (P-VN-02), impartiu formació mitjançant visites guiades, carregueu dades de mostra realistes per a exercicis pràctics i executeu l'assistent de configuració per a escenaris d'inici ràpid.",
        organizations: 'Com a DPO (P-VN-01), configureu estructures de compliment de diverses organitzacions, establiu jerarquies de departaments i encamineu les sol·licituds dels subjectes de dades al vostre grup corporatiu.',
        partner_workspace: 'Com a gestor de comptes de socis, integreu les organitzacions associades, configureu els nivells de servei, feu un seguiment dels lliuraments de compliment i gestioneu el mercat de socis.',
        platform_settings: 'Com a administrador de la plataforma, gestioneu tota la configuració operativa: identitat, seguretat, integracions i accés al mòdul que sustenten el vostre programa de compliment.',
        privacy_scanner: 'Com a analista de privadesa (P-VN-06), realitzeu exploracions de control contínues per detectar infraccions del consentiment de galetes, rastrejadors no autoritzats i llacunes de les polítiques, generant proves de la privadesa electrònica i del compliment de GDPR.',
        program_governance: "Navegueu entre models de maduresa, avaluacions, buits d'evidència, resums executius i molt més. Cada submòdul contribueix a una visió holística de la salut interna del vostre programa.",
        rights_requests: "Com a empresa DPO (P-VN-01), gestiona les sol·licituds de drets dels subjectes de dades sota PDPL amb terminis legals de 15 dies, verificació d'identitat i pistes de resolució d'auditoria.",
        task_management: 'Dirigiu les tasques de compliment entre persones, feu un seguiment dels terminis de SLA i automatitzeu els fluxos de treball que mantinguin el vostre equip responsable.',
        vendor_risk: 'Com a gestor de riscos de proveïdors (P-VN-08), puntueu el risc de tercers, feu complir les revisions de la porta i desencadeneu reavaluacions, a diferència del Govern del programa, que fa un seguiment de la vostra maduresa interna.',
    },
    consolidation: {
        title: 'Casos de consolidacio',
        loading_cases: 'Loading consolidation cases',
        loading_case_detail: 'Loading case detail',
        new_case: 'Crear un cas de consolidació',
        case_count: 'case',
        created: 'Es va crear un cas de consolidació',
        create: 'Crea un cas',
        creating: "S'està creant...",
        cancel: 'Cancel·la',
        no_cases: "No s'han trobat casos de consolidació.",
        all_statuses: 'Tots els estats',
        all_purposes: 'Tots els Propòsits',
        filter_status: 'Filtra per estat',
        filter_purpose: 'Filtra per finalitat',
        col_title: 'Títol',
        col_purpose: 'Propòsit',
        col_status: 'Estat',
        col_progress: 'Progrés',
        col_deadline: 'Data límit',
        col_department: 'Departament',
        col_version: 'Versió',
        col_evidence: 'Evidència',
        sections_accepted: 'accepted',
        prev: 'Anterior',
        next: 'A continuació',
        page: 'Pàgina',
        prev_page: 'Pàgina anterior',
        next_page: 'Pàgina següent',
        field_title: 'Títol',
        field_purpose: 'Propòsit',
        field_framework: 'Codi Marc',
        field_deadline: 'Data límit',
        field_reviewer: "Identificador d'usuari del revisor",
        title_placeholder: 'Introduïu el títol del cas',
        framework_placeholder: 'p. ex., PDPD-2023',
        reviewer_placeholder: "Introduïu l'identificador d'usuari del revisor",
        // fix-sprint-20260821-2140 F6 — structural parity only. R-57 defers real
        // translation of the 14 non-EN/VI locales to Phase 25 (sprint-57.7.0); these
        // land EN-identical and are counted there rather than repaired here.
        field_summary: 'Summary',
        field_details: 'Details',
        field_risk_assessment: 'Risk Assessment',
        field_mitigation_measures: 'Mitigation Measures',
        purposes: {
            dpia_filing: 'DPIA Arxiu',
            cross_border_filing: 'Arxiu transfronterer',
            annual_governance_attestation: 'Declaració anual de govern',
            breach_package: "Paquet d'incompliment",
            audit_inspection_pack: "Paquet d'inspecció d'auditoria",
        },
        statuses: {
            draft: 'Esborrany',
            collecting: 'Col·leccionant',
            consolidating: 'Consolidant',
            review: 'Revisió',
            corrections: 'Correccions',
            pending_approval: "Pendent d'aprovació",
            approved: 'Aprovat',
            completed: 'Completat',
            closed: 'Tancat',
            rejected: 'Rebutjat',
        },
        section_statuses: {
            pending: 'Pendent',
            in_progress: 'En curs',
            submitted: 'Presentat',
            accepted: 'Acceptat',
            needs_correction: 'Necessita correcció',
            corrected: 'Corregit',
        },
        flag_types: {
            missing_section: 'Falta la secció',
            conflicting_entry: 'Entrada conflictiva',
            stale_entry: 'Entrada ranci',
            missing_evidence: 'Evidència que falten',
            incomplete_data: 'Dades incompletes',
            duplicate_entry: 'Entrada duplicada',
        },
        flag_severities: {
            critical: 'Crític',
            warning: 'Avís',
            info: 'Informació',
        },
        sections_title: 'Seccions',
        no_sections: "Encara no s'ha assignat cap secció.",
        flags_title: 'Banderes',
        no_flags: "No s'han detectat banderes.",
        open: 'open',
        approval_chain: "Cadena d'homologació",
        pending: 'Pendent',
        audit_trail: "Pista d'auditoria",
        no_audit: "No hi ha esdeveniments d'auditoria.",
        auto_assign: 'Assignació automàtica de seccions',
        start_collection: 'Inicia la col·lecció',
        review_case: 'Revisió del cas',
        generate_export: 'Generar exportació',
        review_title: 'Revisió de consolidació',
        advance_approval: "Avançament a l'aprovació",
        back_to_case: 'Torna a Case',
        accept: 'Acceptar',
        return_section: 'Tornar',
        return_comment: 'Torna el comentari...',
        resolve: 'Resol',
        dismiss: 'Descartar',
        resolution_notes: 'Notes de resolució...',
        notes: 'Notes',
        section_accepted: 'Secció acceptada',
        section_returned: 'Secció retornada per correcció',
        advanced_to_approval: 'Cas avançat a aprovació pendent',
        section_data: 'Dades de la secció',
        save_progress: 'Desa el progrés',
        submit_section: "Secció d'enviament",
        submitting: "S'està enviant...",
        resubmit: 'Torna a enviar',
        progress_saved: "S'ha guardat el progrés",
        section_submitted: 'Secció presentada',
        correction_required: 'Correcció necessària',
        evidence_title: 'Evidència',
        no_evidence: "No s'adjunta cap prova.",
        add_evidence: 'Afegeix proves',
        evidence_added: 'Evidència afegida',
        evidence_files: 'expedients de proves',
        uploaded_by: 'carregat per',
        comments_title: 'Comentaris',
        no_comments: 'Encara no hi ha comentaris.',
        comment_placeholder: 'Afegeix un comentari...',
        add_comment: 'Afegeix un comentari',
        comment_added: 'Comentari afegit',
        view_data: 'Visualitza les dades enviades',
        export_title: 'Dossier Exportació',
        export_generated: "Paquet d'exportació generat",
        regenerate: 'Regenerar',
        regenerating: 'Regenerant...',
        generated_at: 'Generat a',
        artifacts_title: 'Exporta artefactes',
        artifact_type: 'Artefacte',
        file_name: 'Nom del fitxer',
        file_size: 'Mida',
        generated: 'Generat',
        download: 'Descarregar',
        artifacts: {
            master_report: 'Memòria Mestra',
            evidence_bundle: 'Paquet de proves (ZIP)',
            contributor_lineage: 'Llinatge de col·laboradors',
            conflict_resolution: 'Resolució de conflictes',
            approval_history: "Historial d'aprovacions",
        },
        official_forms: 'Formularis oficials',
        forms_description: 'A partir de les dades consolidades es poden generar els següents formularis oficials. Els formularis oficials estan separats dels documents de treball interns.',
        announce_loaded_cases: "S'han carregat {count} casos de consolidació",
        announce_case_detail_loaded: "S'ha carregat el detall del cas",
        announce_case_transitioned: 'Transició de minúscules a {status}',
        announce_flag_resolved: 'Bandera resolta',
        announce_flag_dismissed: 'Bandera descartada',
        case_detail_loaded: "S'ha carregat el detall del cas",
        sections_auto_assigned: 'Seccions assignades automàticament des de la jerarquia del departament',
        errors: {
            load_failed: "No s'han pogut carregar les dades. Si us plau, torna-ho a provar.",
            create_failed: "No s'ha pogut crear cas. Si us plau, torna-ho a provar.",
            required_fields: 'Ompliu tots els camps obligatoris.',
            not_found: "No s'ha trobat.",
            comment_required: 'Cal comentar quan es retorna una secció.',
            notes_required: 'Les notes són necessàries per resoldre o descartar una bandera.',
        },
    },
    rights_requests: {
        import: {
            title: 'Importar casos',
            nav_title: 'Importació massiva',
            description: 'Pugeu un fitxer CSV per importar massivament casos de sol·licitud de drets.',
            select_file: 'Seleccionar fitxer CSV',
            file_help: 'CSV amb capçaleres que coincideixin amb els camps del cas.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            // fix-sprint #431 — was hard-coded English on the Import page.
            required_columns: 'Required columns:',
            file_too_large: 'Fitxer massa gran. La mida màxima és de 2MB.',
            preview: 'Vista prèvia',
            columns_detected: 'Columnes detectades: {count}',
            column_mapping: 'Assignació de columnes',
            import_button: 'Importar',
            import_complete: 'Importació completada',
            import_error: 'La importació ha fallat',
            result: 'Resultat de la importació',
            total: 'Total',
            imported: 'Importat',
            skipped: 'Omès',
            failed: 'Fallat',
            errors: 'Errors',
            error_row: 'Fila',
            error_field: 'Camp',
            error_message: 'Missatge',
            history: "Historial d'importació",
            no_history: "Sense historial d'importació.",
            history_file: 'Nom del fitxer',
            history_date: 'Data',
        },
        batch: {
            nav_title: 'Procés per lots',
            page_title: 'Procés per lots DSR',
            page_subtitle: "Carregueu un CSV de DSR pendents. Forseti redacta una resposta de reconeixement per sol·licitud i posa a la cua una tasca de verificació per fila. Les DPO comentaris + les accepten abans d'enviar-les.",
            upload_heading: 'Carrega',
            csv_hint: 'Columnes obligatòries: request_id, requester_email, request_type. Opcional: data límit (ISO-8601), notes.',
            rows_heading: "S'han analitzat {count} files",
            submit_label: 'Executar el lot',
            submitting: 'Redacció…',
            col_request_id: 'Identificació de la sol·licitud',
            col_email: 'Correu electrònic del sol·licitant',
            col_type: 'Tipus',
            col_status: 'Estat',
            col_actions: 'Accions',
            status_queued: 'En cua',
            status_drafted: 'Redacció',
            status_failed: 'Ha fallat',
            status_accepted: 'Acceptat',
            status_rejected: 'Rebutjat',
            status_parsing_error: "Error d'anàlisi",
            templates: {
                access: 'Acknowledging your data subject access request. We will respond by {deadline} with a copy of the personal data we process about you, the purposes and the recipients, per [{frameworks}]. Please reply with any clarifying detail; otherwise we will proceed with full disclosure.',
                deletion: 'Acknowledging your erasure request under [{frameworks}]. We will identify the records in scope, apply any retention that still applies (a legal hold, a contractual obligation) and confirm completion by {deadline}.',
                correction: 'Acknowledging your correction request under [{frameworks}]. Please reply with the field or fields you want corrected and the new values; we will route the request to the relevant business unit and confirm by {deadline}.',
                objection: 'Acknowledging your objection to processing under [{frameworks}]. We will pause the relevant processing while we review your reasons and respond by {deadline}.',
                portability: 'Acknowledging your data portability request under [{frameworks}]. We will export your data in scope in a structured, commonly used, machine-readable format and confirm by {deadline}.',
                restriction: 'Acknowledging your restriction request under [{frameworks}]. We will mark the relevant records as restricted and respond by {deadline}.',
                consent_withdrawal: 'Acknowledging your withdrawal of consent under [{frameworks}]. Future processing that relies solely on the withdrawn consent will stop; processing on another basis (a legal obligation, a contract) continues unless you object to it separately. We will confirm by {deadline}.',
                protective_measures: 'Acknowledging your request for personal data protection measures and solutions under [{frameworks}]. We will assess and implement the applicable safeguards and confirm by {deadline}.',
                processing_information: 'Acknowledging your request to be informed about the processing of your personal data under [{frameworks}]. We will tell you what personal data we process about you, why, and who receives it, and respond by {deadline}.',
                consent_decision: 'Acknowledging your decision on consent to the processing of your personal data under [{frameworks}]. We will record it and apply it to the processing that relies on your consent, and confirm by {deadline}.',
                deadline_fallback: 'the deadline set by the law that applies to your request',
            },
            errors: {
                csv_empty: 'The CSV file is empty.',
                csv_missing_header: 'The CSV file is missing the required column: {header}.',
                row_missing_field: 'A required field is missing (request_id, requester_email or request_type).',
                row_unknown_type: 'Unknown request type: {type}.',
                read_failed: 'The CSV file could not be read.',
                run_failed: 'The batch could not be run.',
                decision_failed: 'The decision could not be saved.',
            },
            synthesis_statuses: {
                model: 'Drafted by the AI model',
                template_parse_failed: 'Standard template: the AI model returned no usable text',
                template_provider_failed: 'Standard template: the AI model call failed',
                template_not_configured: 'Standard template: no AI model is configured',
                template_by_design: 'Standard template: this workflow does not use the AI model for this field',
            },
            csv_picker_label: 'CSV file',
            draft_source: 'Draft source',
            draft_language: 'Draft language: {locale}',
            accept: 'Acceptar',
            reject: 'Rebutja',
            permission_denied: 'You need both the case import and Forseti chat permissions to run a batch.',
            col_draft: 'Drafted response',
            view_draft: 'View drafted response',
            frameworks_cited: 'Frameworks cited',
        },
    },
    assessment_triggers: {
        title: "Activadors d'avaluació",
        subtitle: "Configura la creació d'avaluacions automatitzades i les notificacions DPO per als fluxos de treball de privadesa.",
        auto_create_dpia_label: "Cal crear DPIA automàticament quan es processi l'activitat marcada com a DPIA",
        auto_notify_new_purpose_label: 'Notifica automàticament a DPO quan es crei un propòsit nou amb categories de dades sensibles',
        auto_notify_cross_border_label: "Notifica automàticament a DPO quan s'afegeixi una transferència transfronterera a l'activitat de processament",
        default_dpo_label: 'DPO predeterminat',
        default_dpo_placeholder: 'Seleccioneu DPO predeterminat...',
        notification_channels_label: 'Canals de notificació',
        channel_in_app: "A l'aplicació",
        channel_email: 'Email',
        save_success: "S'ha desat la configuració de l'activador de l'avaluació.",
        save_error: "No s'ha pogut desar la configuració de l'activador de l'avaluació.",
        fetch_error: "No s'ha pogut carregar la configuració de l'activador de l'avaluació.",
        fetch_users_error: "No s'han pogut carregar els usuaris.",
        announce_loaded: "S'ha carregat la configuració de l'activador de l'avaluació",
        dpia_status: 'DPIA Estat',
        dpia_linked: 'DPIA Enllaçat',
        dpia_required_no_dpia: "DPIA Obligatori — No s'ha creat",
        create_dpia: 'Crea DPIA',
        create_dpia_success: "DPIA creat i enllaçat a l'activitat.",
        create_dpia_error: "No s'ha pogut crear DPIA per a l'activitat.",
        view_dpia: 'Mostra DPIA',
        dpo_warning_title: 'The default DPO is not an active user',
        dpo_warning_body: 'The default DPO is no longer an active user of this organisation. New DPIAs are created with no reviewer named, so any user who can approve DPIAs may decide them. Choose an active user as the default DPO.',
        auto_notify_cross_border_help: 'When a processing activity is saved with a cross-border destination it did not list before, the default DPO is notified on the channels below. With no default DPO, the Compliance Owners are notified.',
        auto_notify_processor_change_label: 'Raise a DPIA reassessment when a processor changes',
        auto_notify_processor_change_help: "A change to a vendor, a sub-processor or a processing activity's processor details raises one reassessment task, with a checklist of the evidence to re-check, for each DPIA that names that vendor or activity. A DPIA whose previous reassessment task is still open does not get a second one.",
        notification_channels_help: "Where the cross-border notification is sent. Each recipient's own notification preferences still apply.",
    },
    data_subjects: {
        title: 'Perfils de Persones Interessades',
        nav_title: 'Persones Interessades',
        nav: {
            profiles: 'Perfils',
        },
        create: 'Crear Perfil',
        edit: 'Editar Perfil',
        delete: 'Eliminar Perfil',
        delete_confirm: 'Esteu segur que voleu suprimir aquest perfil de subjecte de dades?',
        display_name: 'Nom de visualització',
        email: 'Email',
        phone: 'Telèfon',
        employee_id: "Identificador de l'empleat",
        student_id: "DNI d'estudiant",
        notes: 'Notes',
        external_refs: 'Referències externes',
        cases_count: 'Casos',
        workflows_count: 'Registres de consentiment',
        last_activity: 'Última Activitat',
        timeline: 'Cronologia',
        link_case: 'Link Case',
        link_workflow: 'Link Workflow',
        no_profiles: "No s'han trobat perfils de subjectes de dades.",
        search_placeholder: 'Cerca per nom, correu electrònic, telèfon o identificador...',
        create_success: "S'ha creat el perfil de l'interessat.",
        create_error: "No s'ha pogut crear el perfil del subjecte de dades.",
        update_success: "S'ha actualitzat el perfil de l'interessat.",
        update_error: "No s'ha pogut actualitzar el perfil del subjecte de dades.",
        delete_success: "S'ha suprimit el perfil de l'interessat.",
        delete_error: "No s'ha pogut suprimir el perfil del subjecte de dades.",
        link_case_success: 'Cas enllaçat al perfil.',
        link_case_error: "No s'ha pogut enllaçar el cas al perfil.",
        link_workflow_success: 'Flux de treball enllaçat al perfil.',
        link_workflow_error: "No s'ha pogut enllaçar el flux de treball al perfil.",
        load_error: "No s'han pogut carregar els perfils dels subjectes de dades.",
        detail_load_error: "No s'ha pogut carregar el perfil de l'assumpte de dades.",
        timeline_load_error: "No s'ha pogut carregar la cronologia.",
        tab_cases: 'Casos DSR',
        tab_consent: 'Registres de consentiment',
        tab_timeline: 'Cronologia',
        tab_consent_workflows: 'Fluxos de treball de consentiment',
        tab_web_consent: 'CMP Consentiment',
        web_consent_empty: "No s'han trobat CMP registres de consentiment per a aquest tema.",
        web_consent_pagination: 'CMP consent record pages',
        web_consent_actions: {
            grant: 'Granted',
            revoke: 'Revoked',
            update: 'Updated',
            reject_all: 'Rejected all',
            accept_all: 'Accepted all',
        },
        web_consent_load_error: "No s'han pogut carregar els CMP registres de consentiment.",
        col_policy_version: 'Versió de la política',
        col_versions: 'Versions',
        col_consent_version: 'Consentiment',
        col_cookie_declaration_version: 'Declaració de cookies',
        col_purposes: 'Propòsits',
        purpose_granted: "D'acord",
        purpose_denied: 'Negat',
        granted_domains: "D'acord",
        denied_domains: 'Negat',
        col_action: 'Acció',
        col_timestamp: 'Marca de temps',
        col_domain: 'Domini',
        no_cases: 'No hi ha casos vinculats.',
        no_workflows: 'No hi ha fluxos de treball de consentiment enllaçats.',
        no_timeline: 'No hi ha esdeveniments de cronologia.',
        case_id: 'ID del cas',
        case_status: 'Estat',
        case_type: 'Tipus de sol·licitud',
        case_created: 'Creat',
        workflow_id: 'ID de flux de treball',
        workflow_type: 'Tipus',
        workflow_status: 'Estat',
        workflow_created: 'Creat',
        timeline_date: 'Data',
        timeline_event: 'Esdeveniment',
        timeline_details: 'Detalls',
        timeline_time: 'Temps',
        timeline_type: 'Tipus',
        timeline_summary: 'Resum',
        type_case: 'Cas DSR',
        type_consent_workflow: 'Flux de treball de consentiment',
        profile_info: 'Informació del perfil',
        announce_loaded: "S'han carregat els perfils dels subjectes de dades",
        announce_detail_loaded: "S'ha carregat el perfil del subjecte de dades",
        select_case: '— Seleccioneu un cas —',
        select_workflow: '— Seleccioneu un flux de treball —',
        no_cases_hint: "Vinculeu casos per veure'ls aquí.",
        no_workflows_hint: "Vinculeu fluxos de consentiment per veure'ls aquí.",
        case_options_read_subject: 'the cases offered for linking',
        workflow_options_read_subject: 'the consent workflows offered for linking',
        external_identifiers: 'External identifiers',
        external_identifiers_desc: 'The identifiers the installed record kinds declare for a data subject. An erasure request is checked against the records issued under them.',
        external_identifiers_none_declared: 'No installed record kind declares an external identifier for a data subject.',
        external_identifiers_load_error: 'The external identifier fields could not be read.',
        external_identifiers_edit_unavailable: 'The external identifier fields could not be read, so they are not changed by this save.',
        external_identifier_hint: 'Leave blank to remove it.',
        external_ref_conflicts_heading: 'Identifier conflicts',
        external_ref_conflict: 'On {date}, a {kind} record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_unlabelled: 'On {date}, an issued record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_open_record: 'Open the issued record',
        profile_stale: 'This profile was changed after you opened it, so your change was not saved. The profile has been reloaded; make your change again.',
        identifier_produced_by_record: 'The {label} was recorded by an issued record, so it cannot be removed or changed here.',
    },
    flows: {
        map: {
            description_prefix: '{systems} sistemes · {flows} fluxos · {crossBorder} transfronterer · {highRisk} risc alt/crític',
            switch_to_list: 'Canvia a la vista de llista',
            switch_to_list_aria: 'Canvia a la vista de taula',
            svg_aria: 'Mapa de flux de dades: {systems} sistemes, {flows} fluxos, {crossBorder} transfronterer',
            selected_system: 'Sistema seleccionat',
            drill_hint: 'Obriu la pestanya llista per editar els fluxos enllaçats, els DPIA i les assignacions de proveïdors per a aquest sistema.',
        },
    },
    packages: {
        light: {
            name: 'Llum',
            description: 'Eines essencials de compliment de privacitat per a equips petits',
        },
        standard: {
            name: 'Estàndard',
            description: 'Plataforma de compliment completa per a organitzacions en creixement',
        },
        enterprise_unlimited: {
            name: 'Empresa il·limitada',
            description: 'Compliment de nivell empresarial amb suport multi-organització i serveis dedicats',
        },
    },
    forseti: caForseti,
    pdplEvidencePacks: {
        title: 'Paquets de proves PDPL',
        subtitle: 'Tramesa controlada pel client a MoPS A05',
        nav: { emailProviders: 'Proveïdors de correu' },
        providers: {
            pageTitle: 'Proveïdors de correu',
            pageSubtitle: 'Configureu el proveïdor de correu sortint de la vostra organització per a la tramesa de paquets de proves PDPL a MoPS A05.',
            architectureRuleHeading: "Regla d'arquitectura (bloquejada)",
            architectureRuleBody: 'AesirX no és el remitent dels correus de paquet de proves. Cada tramesa utilitza un proveïdor que és propietat vostra i sota el vostre control. AesirX mai envia en nom vostre.',
            empty: 'Encara no hi ha proveïdors configurats.',
            createCta: 'Crear proveïdor',
            statusPendingConsent: 'Pendent de consentiment',
            statusPendingConnectionCheck: 'Pendent de prova de connexió',
            statusActive: 'Actiu',
            statusConnectionCheckFailed: 'Prova de connexió fallida',
            statusDisabled: 'Desactivat',
            acceptConsentCta: 'Acceptar consentiment',
            runConnectionCheckCta: 'Executar prova de connexió',
            disableCta: 'Desactivar',
            disableConfirm: 'Desactivar aquest proveïdor atura les trameses directes fins que es configuri un nou proveïdor i es torni a confirmar el consentiment. Voleu continuar?',
            columnDisplayName: 'Nom',
            columnFromAddress: 'Adreça del remitent',
            columnStatus: 'Estat',
            columnLastCheck: 'Última prova',
            columnActions: 'Accions',
            createModalTitle: 'Nou proveïdor SMTP',
            createDisplayNameLabel: 'Nom visible',
            createFromAddressLabel: 'Adreça del remitent',
            createReplyToLabel: 'Adreça de resposta (opcional)',
            createSmtpHostLabel: 'Servidor SMTP',
            createSmtpPortLabel: 'Port SMTP',
            createSmtpUsernameLabel: 'Usuari SMTP',
            createSmtpPasswordLabel: 'Contrasenya SMTP',
            createSmtpEncryptionLabel: 'Xifratge',
            createSmtpEncryptionStarttls: 'STARTTLS',
            createSmtpEncryptionTls: 'TLS (implícit)',
            createSmtpEncryptionNone: 'Cap',
            createOauthM365Tab: 'Microsoft 365',
            createOauthGoogleTab: 'Google Workspace',
            createOauthDisabledBadge: 'Properament',
            createOauthDisabledHint: 'Els proveïdors OAuth encara no estan habilitats. Utilitzeu SMTP de moment.',
            createSubmitCta: 'Crear el proveïdor',
            createCancelCta: 'Cancel·lar',
            createErrorInvalidFromAddress: "L'adreça del remitent ha de ser una bústia vàlida.",
            createErrorDuplicateFromAddress: 'Ja hi ha un altre proveïdor configurat per a aquesta adreça del remitent.',
            createErrorGeneric: "No s'ha pogut crear el proveïdor. Reviseu el formulari i torneu-ho a provar.",
            consentModalTitle: "Confirmar regla d'arquitectura",
            consentLocaleToggleEn: 'Anglès',
            consentLocaleToggleVi: 'Vietnamita',
            consentAcknowledgeCta: 'Confirmo — accepta el consentiment',
            consentCancelCta: 'Cancel·lar',
            consentErrorMismatch: "El text del consentiment s'ha modificat i ja no coincideix amb l'avís canònic. Torneu a obrir el diàleg i envieu-ho de nou.",
            testModalTitle: 'Prova de connexió',
            testRunningCta: 'Provant la connexió…',
            testRunCta: 'Executar prova de connexió',
            testRetryCta: 'Reintentar la prova',
            testSuccessHeading: 'Connexió correcta',
            testSuccessBody: 'Autenticat contra {host}:{port} en {latencyMs} ms. El proveïdor és a punt per a trameses directes.',
            testFailureHeading: 'Connexió fallida',
            testFailureBody: "La prova no s'ha pogut completar. Reviseu el motiu a continuació i torneu-ho a provar després de corregir-ho.",
            testFailureReasonLabel: "Motiu de l'error",
            testCloseCta: 'Tancar',
            detailDrawerTitle: 'Detalls del proveïdor',
            detailDrawerHistoryHeading: 'Historial de proves de connexió',
            detailDrawerConsentHeading: 'Confirmació del consentiment',
            detailDrawerNoHistory: "Encara no s'han registrat proves de connexió.",
            detailDrawerNoConsent: "El consentiment encara no s'ha confirmat.",
            outcomeSuccess: 'Èxit',
            outcomeFailure: 'Error',
        },
        builder: {
            pageTitle: 'Munta el paquet de proves',
            pageSubtitle: 'Pugeu els 6 fitxers de parts, trieu el tipus de paquet i munteu el ZIP determinista de 8 parts per a la presentació.',
            packTypeLabel: 'Tipus de paquet',
            packTypeDpia: 'Processing impact-assessment pack (notice 02a/02b; report 10)',
            packTypeOtia: 'Cross-border impact-assessment pack (notice 01a/01b; report 09)',
            part01CoverLetter: 'Part 01 — Carta de presentació (PDF)',
            part02AuthorityForm: "Part 02 — Formulari de l'autoritat (PDF)",
            part03Supporting: 'Part 03 — Proves justificatives (≥1)',
            part04Safeguards: 'Part 04 — Salvaguardes',
            part04SafeguardsRequiredOtia: '(obligatori per a OTIA)',
            part05PriorCorrespondence: 'Part 05 — Correspondència prèvia (opcional)',
            part06Signature: 'Part 06 — Proves de signatura i segell (≥1)',
            uploadPickFile: 'Tria fitxer',
            uploadPickFiles: 'Tria fitxers',
            uploadRemoveFile: 'Treu',
            requiredMissing: 'Manca part obligatòria',
            assembleCta: 'Munta paquet',
            assembling: 'Muntant…',
            assembleSuccessHeading: 'Paquet muntat',
            assembleSuccessSummary: '{fileCount} fitxers · {bytes} bytes · SHA-256 {shaPrefix}…',
            composeNextCta: 'Redacta esborrany de correu',
            warningsHeading: 'Avisos de validació',
            assembleError: "No s'ha pogut muntar el paquet. Reviseu les entrades i torneu-ho a provar.",
            forsetiAssistHeading: 'Assistència IA Forseti (opcional)',
            forsetiAssistHint: 'Executeu el PDPL Evidence Pack Assembly Advisor per ressaltar les parts que falten, redactar la prosa de la carta de presentació i assenyalar les llacunes de salvaguarda. AesirX no és el remitent — la IA només assessora; vós aproveu cada artefacte.',
            forsetiAssistCta: 'Obre la biblioteca de fluxos Forseti',
            forsetiAiRunIdLabel: "ID d'execució IA Forseti vinculada (enganxa per registrar l'assistència IA)",
        },
        draft: {
            pageTitle: 'Esborrany de correu',
            pageSubtitle: "Redacteu i editeu l'esborrany de correu. L'enviament directe arribarà en una versió posterior.",
            composeCta: 'Redacta esborrany',
            composing: 'Redactant…',
            noDraftYet: 'Encara no hi ha esborrany. Redacteu-ne un per començar.',
            localeToggleLabel: 'Idioma',
            localeEn: 'Anglès',
            localeVi: 'Vietnamita',
            subjectLabel: 'Assumpte',
            bodyLabel: 'Cos',
            saveCta: 'Desa canvis',
            saving: 'Desant…',
            saveError: "No s'ha pogut desar l'esborrany. Torneu-ho a provar.",
            manifestSummaryLabel: 'Resum del manifest',
            coverLetterLabel: 'Carta de presentació (PDF)',
            coverLetterDownloadCta: 'Baixa el PDF',
            editHistoryHeading: "Historial d'edicions",
            editHistoryEmpty: 'Encara no hi ha edicions.',
            editHistoryEntry: 'v{version} · {fields} · {when} · {who}',
            unofficialChannelHeading: 'NQ22 avís de canal no oficial',
            uploadSentProofCta: "Puja prova d'enviament manual",
            versionLabel: 'Versió',
            statusDraft: 'Esborrany',
            statusEdited: 'Editat',
            statusFinalized: 'Finalitzat',
        },
        sentProof: {
            modalTitle: "Puja prova d'enviament manual",
            architectureLockNote: "AesirX no és el remitent. Aquesta prova registra l'enviament manual que vau fer a través del vostre propi client de correu.",
            fileLabel: 'Fitxer de prova (.eml, .png, .jpg)',
            envelopeFromLabel: 'De',
            envelopeToLabel: 'A',
            envelopeSubjectLabel: 'Assumpte (en enviar)',
            envelopeSentAtLabel: 'Enviat a',
            envelopeAttachmentShaLabel: 'SHA-256 del fitxer adjunt (en enviar)',
            uploadCta: 'Puja prova',
            uploading: 'Pujant…',
            uploadSuccess: "Prova d'enviament pujada.",
            uploadErrorInvalidMime: 'Tipus de fitxer no permès. Useu .eml, .png, .jpg o .jpeg.',
            uploadErrorGeneric: "No s'ha pogut pujar la prova. Torneu-ho a provar.",
            existingProofsHeading: "Proves d'enviament registrades",
            existingProofsEmpty: "Encara no hi ha proves d'enviament registrades.",
            proofEntry: '{when} · {filename}',
        },
        packDetail: {
            pageTitle: 'Paquet de proves',
            notFoundMessage: "Paquet no trobat: pot haver-se eliminat o l'enllaç és incorrecte.",
            manifestHeading: 'Manifest',
            dossierLabel: 'Expedient',
            packShaLabel: 'SHA-256 del paquet',
            fileCountLabel: 'Fitxers',
            bytesLabel: 'Mida',
            composeDraftCta: 'Redacta esborrany',
            sendComingSoonCta: 'Enviament directe: properament',
            sendComingSoonHint: "El flux d'enviament directe està en desenvolupament actiu. Useu 'Puja prova d'enviament manual' a la pàgina de l'esborrany fins que es publiqui.",
            sendCta: 'Envia mitjançant el proveïdor configurat',
            sendDisabledNoProviderHint: 'Configureu primer un proveïdor de correu controlat pel client. AesirX mai no envia en nom vostre.',
            timelineHeading: "Historial d'enviaments",
        },
        // Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + timeline + authority response + hard-copy follow-up.
        send: {
            // Send dialog
            dialogTitle: 'Envia el paquet de proves',
            providerPickerLabel: 'Proveïdor de correu configurat',
            providerPickerHint: 'AesirX mai no envia en nom vostre. Trieu un dels vostres proveïdors configurats a continuació.',
            providerPickerEmpty: "No hi ha cap proveïdor actiu configurat. Configureu-ne un a la pàgina de Proveïdors de correu abans d'enviar.",
            providerPickerEmptyCta: 'Obre Proveïdors de correu',
            draftPickerLabel: 'Esborrany de correu',
            draftPickerOption: '{locale} · v{version}',
            draftPickerEmpty: "Encara no s'ha redactat cap esborrany per a aquest paquet. Redacteu-ne un primer a la pàgina de l'esborrany.",
            recipientToLabel: 'Per a',
            recipientToHint: 'Una o més adreces de correu electrònic, separades per comes.',
            recipientCcLabel: 'Cc (separats per comes)',
            recipientCcHint: "Opcional. Llista d'adreces de correu electrònic separades per comes.",
            recipientBccLabel: 'Cco (separats per comes)',
            recipientBccHint: "Opcional. Llista d'adreces de correu electrònic separades per comes.",
            consentReaffirmHeading: 'Reafirmeu el consentiment del proveïdor',
            submitCta: 'Envia ara',
            sending: 'Enviant…',
            cancelCta: 'Cancel·la',
            sendSuccess: "Enviament registrat. El paquet s'ha transmès mitjançant el vostre proveïdor configurat.",
            sendErrorGeneric: "No s'ha pogut enviar el paquet. Reviseu l'error següent i torneu-ho a provar.",
            sendErrorAlreadyFinalized: "Aquest esborrany ja s'ha utilitzat per a un enviament correcte. Redacteu un esborrany nou abans de tornar a enviar-lo.",
            sendErrorInvalidRecipients: 'Una o més adreces de destinació no són bústies de correu vàlides.',
            // Timeline
            timelineEmpty: "Encara no hi ha enviaments registrats. Useu 'Envia mitjançant el proveïdor configurat' a dalt per transmetre a través del vostre proveïdor SMTP / Microsoft 365 / Google Workspace controlat pel client.",
            timelineProviderLabel: 'Enviat mitjançant el vostre proveïdor configurat',
            timelineSentAtLabel: 'Enviat',
            timelineRecipientLabel: 'Destinatari',
            timelineOutcomeSent: 'Enviat',
            timelineOutcomeFailed: 'Ha fallat',
            timelineViewDetailsCta: 'Mostra els detalls',
            // Authority-response chips
            authorityChipNone: "S'espera resposta",
            authorityChipAcknowledgement: 'Acusament de rebuda',
            authorityChipAcceptance: 'Acceptació',
            authorityChipRejection: 'Rebuig',
            authorityChipSupplementRequest: 'Sol·licitud de complement',
            authorityChipNoResponse: 'Sense resposta',
            // Hard-copy chips
            hardCopyChipNone: 'Sense seguiment en paper',
            hardCopyChipPending: 'Enviat — lliurament pendent',
            hardCopyChipDelivered: 'Lliurat',
            // Drawer
            drawerTitle: "Registre d'enviament",
            drawerEnvelopeHeading: 'Sobre',
            drawerEnvelopeFromLabel: 'De',
            drawerEnvelopeToLabel: 'Per a',
            drawerEnvelopeCcLabel: 'Cc',
            drawerEnvelopeBccLabel: 'Cco',
            drawerEnvelopeSubjectLabel: 'Assumpte',
            drawerEnvelopeMessageIdLabel: 'ID del missatge',
            drawerOutcomeHeading: 'Resultat',
            drawerSnapshotHeading: 'Captura de la cadena de custòdia',
            drawerSnapshotPackShaLabel: "SHA-256 del paquet en l'enviament",
            drawerSnapshotDraftVersionLabel: "Versió de l'esborrany en l'enviament",
            drawerSnapshotAttachmentLabel: 'ZIP transmès',
            drawerSnapshotAttachmentDownloadCta: 'Baixa el ZIP',
            drawerAuthorityResponseHeading: "Resposta de l'autoritat",
            drawerHardCopyHeading: 'Seguiment en paper',
            drawerSentByLabel: 'Enviat per',
            drawerSendIdLabel: "Identificador del registre d'enviament",
            drawerSmtpResponseLabel: 'Resposta SMTP',
            drawerFailureReasonLabel: 'Motiu de la fallada',
            drawerLatencyLabel: 'Latència',
            drawerLatencyValue: '{ms} ms',
            drawerAcceptedLabel: 'Acceptat',
            drawerRejectedLabel: 'Rebutjat',
            drawerCloseCta: 'Tanca',
            // Authority-response panel
            authorityRecordCta: 'Registra la resposta',
            authorityReclassifyCta: 'Reclassifica',
            authorityClassificationLabel: 'Classificació',
            authorityReceivedAtLabel: 'Rebuda el',
            authorityFreeTextLabel: 'Notes (opcional)',
            authorityEvidenceLabel: 'Fitxer de prova (.eml / .pdf / .png / .jpg — opcional)',
            authorityReasonLabel: 'Motiu de la reclassificació',
            authorityHistoryHeading: 'Historial de reclassificacions',
            authorityHistoryEmpty: 'No hi ha reclassificacions registrades.',
            authorityHistoryEntry: '{when} · {previous} → {next} · {who}{reason}',
            authoritySubmitCta: 'Desa la resposta',
            authoritySubmitting: 'Desant…',
            authorityErrorInvalidMime: 'El tipus de fitxer de prova no està permès. Useu .eml, .pdf, .png o .jpg.',
            authorityErrorGeneric: "No s'ha pogut desar la resposta. Torneu-ho a provar.",
            authorityRecordedAtLabel: 'Registrada el',
            authorityRecordedByLabel: 'Registrada per',
            authorityEvidenceFilenameLabel: 'Prova',
            authorityEvidenceDownloadCta: "Baixa l'evidència",
            // Hard-copy panel
            hardCopyRecordCta: 'Registra el seguiment en paper',
            hardCopyConfirmCta: 'Confirma el lliurament',
            hardCopyEditNotesCta: 'Edita les notes de lliurament',
            hardCopyPostedAtLabel: 'Enviat el',
            hardCopyCourierLabel: 'Missatger',
            hardCopyTrackingLabel: 'Número de seguiment',
            hardCopyRecipientLabel: "Destinatari a l'etiqueta",
            hardCopyReceiptLabel: 'Prova de recepció (.pdf / .png / .jpg — opcional)',
            hardCopyReceiptDownloadCta: 'Baixa el comprovant',
            hardCopyDeliveredAtLabel: 'Lliurat el',
            hardCopyDeliveryNotesLabel: 'Notes de lliurament (opcional)',
            hardCopySubmitCta: 'Desa el seguiment',
            hardCopySubmitting: 'Desant…',
            hardCopyErrorInvalidMime: 'El tipus de fitxer de rebut no està permès. Useu .pdf, .png o .jpg.',
            hardCopyErrorGeneric: "No s'ha pogut desar el seguiment. Torneu-ho a provar.",
        },
    },
    // Sprint 62.53.0 — organisation-profile statements: the recorded attributes'
    // names and the applicability review a statement of "unknown" raises.
    org_profile_statements: {
        attribute_names: {
            data_exploitation_routes: 'Data exploitation routes',
            regulated_entity_types: 'Regulated entity types',
            personal_data_processing_services: 'Personal data processing services your organization provides to others as a business',
        },
        review_task: {
            title: 'Applicability review: {attribute}',
            description: 'Your organization stated that it does not know its answer for “{attribute}”. The requirements that depend on it await an applicability review and are not treated as out of scope. Record the answer on the Organization profile page.',
        },
        nav_label: 'Organization profile',
        page: {
            title: 'Organization profile',
            description: 'What your organization states about itself: the industry sectors it operates in, the data exploitation routes it takes part in and the regulated-entity role it holds. Your installed frameworks read these statements to decide which of their requirements apply to you. A requirement that depends on an answer you have not given, or have stated as unknown, awaits an applicability review and is never treated as out of scope.',
            sectors_heading: 'Industry sectors',
        },
        section: {
            // Sprint 62.57.0 Slice E (§12.6.1) — the installed declarations' item text under each value.
            value_sources_label: 'In the installed framework',
            state_legend: 'Your answer',
            state_selected: 'Selected',
            state_selected_help: 'One or more of the options below apply to your organization. Tick each one that does.',
            state_none: 'None of these',
            state_none_help: 'None of the options apply. Requirements that depend only on them read as out of scope.',
            state_unknown: 'Unknown — review needed',
            state_unknown_help: 'You do not know yet. An applicability review is added to your task list, and the requirements that depend on this answer are never treated as out of scope.',
            values_legend: 'Options that apply',
            values_disabled_hint: 'Choose “Selected” to pick the options that apply.',
            values_required: 'Choose at least one option, or choose another answer.',
            used_by_label: 'Used by',
            used_by_none: 'No installed framework names this option.',
            used_by_item: '{framework} ({count} requirements)',
            used_by_item_one: '{framework} (1 requirement)',
            used_by_item_reference: '{framework} ({count} requirements, installed for reference only)',
            used_by_item_reference_one: '{framework} (1 requirement, installed for reference only)',
            note_label: 'Note',
            note_hint: 'Optional. The basis for this answer, in your own words (up to {max} characters).',
            evidence_heading: 'Evidence',
            evidence_hint: 'Files that support this answer. A file named by a saved statement is kept for as long as the statement history exists.',
            evidence_none: 'No evidence attached.',
            evidence_not_held: 'File no longer held',
            evidence_upload_label: 'Attach evidence',
            evidence_unbind: 'Remove from statement',
            evidence_unbind_label: 'Remove {filename} from this statement',
            evidence_unbind_not_held_label: 'Remove the file that is no longer held from this statement',
            evidence_limit: 'A statement can name at most {max} files.',
            current_none: 'No statement recorded yet. Requirements that depend on this answer await an applicability review.',
            current_revision: 'Current statement: revision {revision}, recorded {date}.',
            save: 'Save statement',
            saving: 'Saving…',
            reset: 'Reset',
            saved: 'Statement saved.',
            save_failed: 'The statement could not be saved.',
            conflict: 'Someone else changed this statement while you were editing it (you read revision {expected}; it is now revision {current}). Nothing was saved, and your changes are still on screen.',
            reload_current: 'Reload the current statement',
            evidence_not_held_error: 'Some evidence files named in this statement are no longer held ({count}). Remove them from the statement and save again.',
            load_failed: 'This statement could not be read.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the statement for {attribute}',
            read_only: 'You can view this statement but not change it.',
            history_show: 'Show statement history ({count})',
            history_hide: 'Hide statement history',
            history_caption: 'Statement history, newest first',
            history_revision: 'Revision',
            history_date: 'Recorded',
            history_answer: 'Answer',
            history_values: 'Options',
            history_note: 'Note',
            history_evidence: 'Evidence files',
            history_none: '—',
        },
        attribute_descriptions: {
            data_exploitation_routes: 'The routes by which your organization takes part in the exploitation of data held or offered by the State. Requirements that depend on a route apply only when you select it.',
            regulated_entity_types: 'The regulated-entity role your organization holds in the credit-cooperative and deposit-protection system, if any. Requirements addressed to a role apply only when you select it.',
            personal_data_processing_services: 'The personal data processing services your organization provides, or intends to provide, to other organizations as a business. Select only what your organization does: nothing is filled in from your other records. Requirements that depend on a service apply only when you select it.',
        },
        value_labels: {
            data_exploitation_routes: {
                public_data_access_request: 'Requesting access to State-held data',
                state_data_centre_exploitation: 'Exploiting data at a State data centre',
                state_data_incubator: 'Taking part in a State data incubator',
                data_controlled_testing: 'Controlled testing of a data solution',
                state_data_centre_data_sharing: 'Sharing data with a State data centre',
                state_data_centre_investment: 'Investing in or sponsoring a State data centre',
                foreign_party_data_cooperation: 'Data cooperation with a foreign party',
                state_data_incentive_application: 'Applying for a State data incentive',
            },
            regulated_entity_types: {
                credit_cooperative: 'People’s credit fund',
                cooperative_central_bank: 'Cooperative bank',
                deposit_insurer: 'Deposit insurer',
                microfinance_institution: 'Microfinance institution',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Providing and operating automated systems or software that process personal data on behalf of a controller',
                creditworthiness_scoring: 'Scoring or rating creditworthiness from personal data',
                online_collection_and_processing: 'Collecting and processing personal data online for others',
                health_service_data_processing: 'Processing personal data in health services',
                education_monitoring_data_processing: 'Processing personal data to monitor learners in education services',
                personal_data_analytics_and_mining: 'Analysing or mining personal data',
                personal_data_encryption: 'Encrypting personal data for others',
                automated_processing_emerging_technologies: 'Automated processing of personal data with emerging technologies',
                location_data_platform: 'Operating a platform that processes location data',
            },
        },
        value_help: {
            data_exploitation_routes: {
                public_data_access_request: 'Your organization requests access to, or the exploitation of, data held by State bodies through a formal request.',
                state_data_centre_exploitation: 'Your organization exploits data that a State data creation and exploitation centre makes available.',
                state_data_incubator: 'Your organization takes part in a data incubation programme the State runs.',
                data_controlled_testing: 'Your organization tests a data product, service or solution under a controlled-testing mechanism.',
                state_data_centre_data_sharing: 'Your organization supplies or shares data with a State data centre, without exploiting the centre’s data.',
                state_data_centre_investment: 'Your organization invests in, contributes assets to or sponsors a State data centre, without exploiting the centre’s data.',
                foreign_party_data_cooperation: 'Your organization cooperates with a foreign organization or individual in these data activities.',
                state_data_incentive_application: 'Your organization applies for, or receives, a State incentive or support for data activities. Applying is not exploiting data.',
            },
            regulated_entity_types: {
                credit_cooperative: 'Your organization is a credit institution organised as a cooperative that takes deposits from and lends to its members.',
                cooperative_central_bank: 'Your organization is the cooperative bank of the credit-cooperative system.',
                deposit_insurer: 'Your organization insures the deposits held at credit institutions.',
                microfinance_institution: 'Your organization is a microfinance institution.',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Your organization provides or operates a system or software that processes personal data automatically for a controller.',
                creditworthiness_scoring: 'Your organization scores or rates the creditworthiness of people for others, using their personal data.',
                online_collection_and_processing: 'Your organization collects and processes personal data online as a service to other organizations.',
                health_service_data_processing: 'Your organization processes personal data as part of the health services it provides to others.',
                education_monitoring_data_processing: 'Your organization processes personal data to monitor learners as part of the education services it provides to others.',
                personal_data_analytics_and_mining: 'Your organization analyses or mines personal data as a service to other organizations.',
                personal_data_encryption: 'Your organization encrypts personal data as a service to other organizations.',
                automated_processing_emerging_technologies: 'Your organization processes personal data automatically using emerging technologies, as a service to others.',
                location_data_platform: 'Your organization operates a platform that processes location data for others.',
            },
        },
        review: {
            title: 'Applicability review',
            description: 'Requirements of your installed frameworks that wait on an answer on this page. Each one applies, or is out of scope, once the answer is recorded; until then it is not treated as out of scope.',
            state_not_stated: 'No statement recorded',
            state_selected: 'Selected',
            state_none: 'None of these',
            state_unknown: 'Unknown — review needed',
            group_state: 'Current answer: {state}',
            group_count: '{count} requirements await this answer.',
            group_count_one: '1 requirement awaits this answer.',
            group_empty: 'No requirement of an installed framework awaits this answer.',
            group_empty_reference: 'No requirement of a directly applicable framework awaits this answer.',
            reference_intro: 'These frameworks are installed for reference only, so their requirements are not applied to your organization. If they were installed as directly applicable, these requirements would await this answer:',
            reference_item: '{framework} ({count} requirements)',
            reference_item_one: '{framework} (1 requirement)',
            column_framework: 'Framework',
            column_requirement: 'Requirement',
            load_failed: 'The applicability review could not be read.',
            retry_subject: 'the applicability review',
        },
        outcome: {
            // Sprint 62.57.0 Slice E (D48) — an unstated condition read as out of scope.
            out_of_scope_not_stated: 'Applies only where your organization has recorded it (none recorded)',
            column: 'Applies to your organization',
            applicable: 'Applies',
            out_of_scope: 'Out of scope',
            not_applicable: 'Reference only — not assigned to any organization',
            installed_for_reference: 'Installed for reference only — not applied to your organization',
            not_entitled: 'Not in your package — not applied to your organization',
            not_declared: 'Not declared',
            profile_incomplete: 'Awaiting your organization profile',
            profile_incomplete_named: 'Awaiting your organization profile: {attributes}',
            attributes: {
                sector: 'industry sectors',
                data_exploitation_routes: 'data exploitation routes',
                regulated_entity_types: 'regulated entity types',
                personal_data_processing_services: 'personal data processing services provided to others',
            },
        },
    },
    // Sprint 63.37.0 (E0, E5) — English until the Phase 25 locale pass (R-57).
    org_identity: {
        nav_label: 'Organisation identity',
        module_guide: 'Organisation identity: the legal name, offices, registration, tax code, contacts and legal representative that official forms and cover letters are filled from.',
        title: 'Organisation identity',
        description: "Record your organisation's legal name, offices, registration, tax code, contacts and legal representative once. Official forms are filled from this record when you choose Fill from record, and cover letters read it too.",
        section_names: 'Names and offices',
        section_registration: 'Registration',
        section_contact: 'Contact details',
        section_representative: 'Legal representative',
        field_legal_name: 'Legal name',
        field_legal_name_help: 'The name exactly as it appears on the registration certificate.',
        field_trading_name: 'Trading name',
        field_trading_name_help: 'A short or trading name, if the organisation uses one.',
        field_head_office_address: 'Head office address',
        field_transaction_office_address: 'Transaction office address',
        field_transaction_office_address_help: 'Leave blank if it is the same as the head office.',
        field_registration_number: 'Registration Number',
        field_registration_instrument: 'Registration document',
        field_registration_instrument_help: 'The document that records the registration, for example a business registration certificate or an establishment decision.',
        field_registration_issuer: 'Issued by',
        field_registration_issue_date: 'Date of issue',
        field_registration_issue_place: 'Place of issue',
        field_tax_code: 'Tax Code',
        field_tax_code_help: 'The tax code is recorded separately from the registration number, even where the two are the same.',
        field_phone: 'Phone',
        field_email: 'Email',
        field_website: 'Website',
        field_website_help: 'Starting with https:// or http://.',
        field_legal_representative_name: 'Full name',
        field_legal_representative_title: 'Position',
        save: 'Save identity',
        saving: 'Saving…',
        saved: 'Organisation identity saved.',
        save_failed: 'The organisation identity could not be saved.',
        fix_fields: 'Some fields need correcting before the identity can be saved.',
        load_failed: 'The organisation identity could not be read.',
        retry: 'Retry',
        conflict_message: 'Someone else saved the organisation identity after you opened this page. Your changes were not saved. Reload the saved identity, then make your changes again.',
        conflict_reload: 'Reload the saved identity',
        conflict_reloaded: 'The saved identity is shown. Make your changes again, then save.',
        loading: 'Loading the organisation identity',
        read_only: 'You can view the organisation identity. Saving it needs the settings write permission.',
        restricted_title: 'You do not have access to the organisation identity',
        restricted_message: 'Ask an administrator for the settings read permission.',
        last_saved: 'Last saved {date}',
        never_saved: 'Nothing has been recorded yet. Fields left blank are left blank on forms too.',
        error_too_long: 'This value is too long.',
        error_invalid_date: 'Enter a real date.',
        error_invalid_email: 'Enter an email address, for example name@example.com.',
        error_invalid_url: 'Enter a web address starting with https:// or http://.',
        error_invalid_phone: 'Use digits, spaces and + ( ) . - only.',
        error_invalid: 'This value is not accepted.',
    },
    form_prefill: {
        draft_undeclared: "The form does not say where {count} of the draft's fields go, so they were not drafted.",
        fill_from_record: 'Fill from record',
        fill_from_named_record: 'Fill from {record}',
        filling: 'Filling…',
        result: 'Filled {filled} fields; {differs} differ from the record.',
        failed: 'The form could not be filled from the record.',
        from_source: 'From {source}',
        not_recorded_in_source: 'Not recorded in {source}',
        family_organisation: 'the organisation identity',
        family_dpo: 'the DPO appointment',
        family_dpia: 'the DPIA',
        family_transfer: 'the transfer assessment',
        family_incident: 'the incident',
        family_dossier: 'the dossier filing',
        record_generic: 'the record',
        hint: 'Fills empty fields only. A value you entered is never replaced.',
    },
    // Owner rulings 2026-10-26 — idea #717 and OQ-86-6. English until the
    // Phase 25 locale work (R-57); structural parity with en.ts.
    customs_tax_exports: {
        titles: {
            dossiers: 'Customs tax dossiers and documents received',
            payments: 'Customs tax payments',
            duty_free_lists: 'Duty-free lists',
            examinations: 'Customs tax examinations',
            debt_instruments: 'Customs tax debt instruments',
            transition_statement: 'Transition statement',
        },
        sections: {
            dossiers: 'Dossiers',
            documents_received: 'Documents received',
            figures_side_by_side: 'Stated figures side by side',
            deductions: 'Deductions as customs recorded them',
            usage_notifications: 'Usage notifications recorded',
            steps: 'Steps',
            office_examinations: 'At the customs office',
            determinations: 'Determinations recorded',
            refund_records: 'Refund records',
            enforcement_notices: 'Enforcement notices received',
            instalment_payments: 'Payments recorded against instalment notices (each as its document states it; no total)',
            noted_alternatives: 'Noted alternative readings of the usage schedule',
        },
        columns: {
            paper_form_number: 'Paper form number',
            electronic_indicator_set: 'Electronic indicator set',
            matched_by_title: 'Matched by title',
            issued_by_authority: 'Issued by the authority',
            dossier: 'Dossier',
            procedure: 'Procedure',
            register_role: 'Register role',
            customs_records: 'Customs records',
            channel: 'Channel',
            signed_off_on: 'Signed off on',
            lodged_on: 'Lodged on',
            confirmation_answer: 'Confirmation answer',
            install_mode_when_made: 'Install mode when made',
            review_approval: 'Review approval',
            document: 'Document',
            kind: 'Kind',
            number_as_printed: 'Number as printed',
            received_on: 'Received on',
            stated_figures: 'Stated figures',
            stated_deadlines: 'Stated deadlines',
            reply: 'Reply',
            payment: 'Payment',
            paid_on: 'Paid on',
            stated_amount: 'Stated amount',
            payer_class: 'Payer class',
            payment_date_basis: 'Payment-date basis',
            document_number: 'Document number',
            pays: 'Pays',
            receipt: 'Receipt',
            received_document: 'Received document',
            figure: 'Figure',
            payment_figure_as_stated: 'Payment figure (as stated)',
            document_figure_as_stated: 'Document figure (as stated)',
            comparison: 'Comparison',
            closed: 'Closed',
            list: 'List',
            reference: 'Reference',
            acknowledged_on: 'Acknowledged on',
            imports_ended_on: 'Imports ended on',
            expires_on: 'Expires on',
            deduction: 'Deduction',
            customs_record: 'Customs record',
            goods_as_stated: 'Goods as stated',
            unit_as_stated: 'Unit as stated',
            cleared_as_stated: 'Cleared (as stated)',
            remaining_as_stated: 'Remaining (as stated)',
            notification: 'Notification',
            notice_kind: 'Notice kind',
            financial_year_ended_on: 'Financial year ended on',
            notified_on: 'Notified on',
            examination: 'Examination',
            purpose: 'Purpose',
            decision_signed_on: 'Decision signed on',
            decision_sent_on: 'Decision sent on',
            announced_on: 'Announced on',
            re_examination_of: 'Re-examination of',
            step: 'Step',
            occurred_on: 'Occurred on',
            refused_to_sign: 'Refused to sign',
            record: 'Record',
            authority_request: 'Authority request',
            explanation_sent_on: 'Explanation sent on',
            outcome_on: 'Outcome on',
            instrument: 'Instrument',
            role: 'Role',
            effective_on: 'Effective on',
            instalment_band: 'Instalment band',
            approved_on: 'Approved on',
            stated_debt_figure: 'Stated debt figure',
            guarantee_valid_until: 'Guarantee valid until',
            record_type: 'Record type',
            governing_date: 'Governing date',
            outcome: 'Outcome',
            reading: 'Reading',
            open: 'Open',
            determination: 'Determination',
            determined_on: 'Determined on',
            refund_record: 'Refund record',
            dossier_register_role: 'Dossier register role',
            classification: 'Classification',
            decision_document: 'Decision document',
            offset_notice_document: 'Offset notice received',
            post_refund_examination: 'Post-refund examination',
            stated_case: 'Case as the decision states it',
            stated_case_source: 'Case stated in',
            stated_case_citation: 'Provision cited for the case',
            received_as: 'Received as',
            document_date: 'Document date',
            amount_stated_for_notice: 'Amount stated for this notice',
            financial_year_reported: 'Financial year reported (ending)',
            noted_alternative_year: 'Noted alternative financial year (ending)',
            note: 'Note',
            reading_kind: 'Kind of reading',
            classification_case: 'Case the classification notice states',
            acceptance_notice: "Acceptance notice (the authority's clock)",
            handling_time: "Handling time (the authority's clock)",
            stated_case_choices: 'Installed cases matched (as stated)',
            stated_due_on: 'Due date as stated',
            period_ceiling: 'Longest instalment period',
            stated_plan_end: 'Latest end the Law allows',
            enforcement_from: "Enforcement threshold (the authority's clock)",
        },
        cells: {
            review_approved: 'Approved by {name} on {date}',
            review_approved_earlier_version: 'Approved by {name} on {date}, against an earlier version of the record',
            register_role_refund: 'Refund',
            register_role_overpaid_refund: 'Refund of an overpaid amount',
            noted_not_due: 'Noted reading, not a due date',
            received_as_third_party: 'Received as a third party',
            received_as_enforced_taxpayer: 'The taxpayer under enforcement',
            clock_authority_ends: "The authority's clock ends {date} ({clock})",
            clock_authority_ends_before_excluded: "The authority's clock ends {date} before the time not counted is added ({clock})",
            clock_authority_no_date: "The authority's clock; no date, because the event it runs from is not recorded ({clock})",
            clock_period_ends: 'The period ends {date} ({clock})',
            clock_closed: 'Closed on {date}',
            clock_not_applicable: 'Not applicable to this record ({clock})',
            clock_not_live: 'Not in force yet ({clock})',
            clock_classification_not_recorded: 'No date: the classification the authority notified is not recorded',
            clock_not_declared: 'No installed clock declares it',
            clock_instrument_not_installed: 'Stated in an instrument that is not installed ({instruments}); no date',
            clock_no_date: 'No date ({clock})',
            plan_end_by_ceiling: "{date}, the end of the longest period; a shorter schedule in the written acceptance is the plan's own",
            plan_end_by_guarantee: "{date}, the end of the guarantee's validity, which comes first; a shorter schedule in the written acceptance is the plan's own",
            clock_not_counted_before_commencement: 'Not counted by the platform: debts outstanding at the end of {outstanding} follow the Law ({reference}), but no point is shown for a due date before {activation}',
            clock_not_counted_before_commencement_unnamed: 'Not counted by the platform: no point is shown for a due date before {activation}',
            meta_boundary: 'Boundary ({framework}): {summary}',
            meta_boundary_none: 'Boundary: no installed framework declares a boundary statement for these registers.',
            meta_amounts_as_stated: 'Every amount is a figure a person entered from a document; nothing here is computed.',
            meta_statement_for: 'Statement for: {date}',
            meta_nothing_remains: 'Nothing remains open under the earlier rules.',
            meta_records_remain: 'Records remain open under the earlier rules.',
            meta_transition_unavailable: 'The transition reading is not available on this installation.',
            role_notified_debt: 'Tax debt as notified',
            role_freeze: 'Freeze',
            role_freeze_end: 'End of a freeze',
            role_write_off: 'Write-off',
            role_restoration: 'Restoration',
            role_instalment_request: 'Instalment request',
            role_instalment_notice: 'Instalment notice',
            received_document_label: '{kind} {number}, dated {date}',
            received_document_label_no_number: '{kind}, received {date}',
            case_no_longer_installed: 'A case from an instrument that is no longer installed',
        },
    },
    cross_border_filing_fence: {
        register_customs_tax_dossiers: 'Tax Dossiers and Received Documents',
        refused: 'This form is recorded in {register}, not as a cross-border filing. Record the dossier there.',
        open_register: 'Open {register}',
        option_owned_elsewhere: '{form} — recorded in {register}',
        picker_hint: 'Some installed forms are recorded in {register} and cannot be chosen here.',
        advisory_title: 'This form is now recorded in {register}',
        advisory_body: 'This filing was recorded here before forms like it moved to {register}. It stays readable here and has not been changed. Record new dossiers on this form in {register}.',
        list_marker: 'Now recorded in {register}',
    },
    // Sprint 63.43.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    sector_overlays_module: {
        nav: {
            overview: 'Overview',
        },
        overview: {
            title: 'Sector Overlays',
            subtitle: 'What your active sector overlays bring into use, grouped by sector.',
            loading: 'Loading sector overlays…',
            load_failed: 'Sector overlays could not be loaded.',
            not_entitled_title: 'Sector Overlays is not in your package',
            not_entitled_body: "Sector Overlays is an Enterprise module. Your organization's package does not include it, so no sector overlay is shown here.",
            empty_title: 'No sector overlay is active',
            empty_body: 'Install an overlay in the Overlay Catalog and choose Direct to bring its views into use here. An overlay installed for reference only adds no views.',
            open_catalog: 'Open the Overlay Catalog',
            organization_profile: 'Organization profile',
            organization_profile_hint: "Your organization's sectors decide which overlays apply to you directly.",
            provided_by: 'Provided by',
            views_heading: 'Views in this sector',
            shared_views_heading: 'Shared views this sector uses',
            shared_view_location: 'In {location}',
            open_view_aria: '{view} — {location}',
            no_visible_views: "None of this sector's views is open to your role.",
            sector_count_one: '1 overlay active',
            sector_count: '{count} overlays active',
        },
    },
    // Sprint 62.57.0 Slice E (§12.6) — the processing-service certificate register.
    service_certificates: enServiceCertificates,
    evidence_reports: {
        intendedFilingReadinessHint: 'A prospective filing date is required for statutory readiness for a new dossier. You may leave it blank to save a draft or gap report. An existing dossier filing can supply the basis.',
        captureUnconfirmed: 'Capture may still be running. Retry this same capture to check or recover it; a completed version is reused.',
        retryCapture: 'Check or retry this capture',
        sampleEvidenceLimitation: 'Required content may be complete, but admitted evidence is synthetic sample data and cannot establish readiness for statutory filing. Internal or training exports remain available.',
        referenceRendition: 'Reference rendition',
        languagePreservationHint: 'Language selection changes headings and labels. Saved answers and evidence retain their authored language. This output does not translate or certify the supplied text.',
        title: 'Prepare an evidence report',
        intro: 'Define the question and scope, review actual records, and freeze an attributable report. Blank templates do not demonstrate completed work.',
        legacy: 'Legacy catalog export',
        legacyHint: 'Catalog exports contain framework references. They are not reviewed evidence reports.',
        name: 'Report title',
        framework: 'Installed framework',
        purpose: 'Purpose',
        internal_readiness: 'Internal readiness review',
        authority_request: 'Response to an authority request',
        processing_assessment: 'Processing assessment dossier',
        transfer_assessment: 'Transfer assessment dossier',
        dossier_update: 'Dossier update',
        assessmentKind: 'Assessment type',
        dpia: 'Processing assessment',
        otia: 'Transfer assessment',
        applicant: 'Applicant',
        organization: 'Organization',
        individual: 'Individual',
        role: 'Organization role',
        controller: 'Controller',
        controller_processor: 'Controller and processor',
        processor: 'Processor',
        other: 'Other',
        coverage: 'Coverage',
        bounded: 'Selected activity / bounded scope',
        organization_complete: 'Complete organization and processing lifetime',
        coverageStatement: 'Scope and coverage statement',
        coverageHint: 'State what this report covers and excludes. A selected activity does not establish organization-wide statutory coverage.',
        recipient: 'Intended recipient',
        questions: 'Request questions (one per line)',
        language: 'Report language',
        en: 'English',
        vi: 'Vietnamese',
        asOf: 'Evidence as of',
        timezone: 'IANA timezone',
        periodStart: 'Period start (optional)',
        periodEnd: 'Period end (optional)',
        intendedFiling: 'Intended filing date',
        filing: 'Existing dossier filing',
        sources: 'Select actual source records',
        sourceKind: 'Source type',
        sourceSearch: 'Search authorized source records',
        sourceHint: 'Only existing records you may read are offered. Select none for an honest gap report. Search or change page to reach further records.',
        search: 'Search',
        next: 'Next page',
        previous: 'Previous page',
        remove: 'Remove',
        review: 'Review evidence and requirements',
        preview: 'Refresh review and preview',
        freeze: 'Freeze report and generate files',
        busy: 'Working…',
        failed: 'The request failed. Your entries have been retained.',
        required: 'Complete the required fields before continuing.',
        emptySources: 'No source records selected. Missing content remains explicit.',
        candidate: 'Source evidence',
        noCandidates: 'No accessible evidence supplied. This report contains gaps, not proof of completed work.',
        openSource: 'Open source record',
        inspect: 'Inspect captured content',
        sourceRevision: 'Source revision',
        state: 'Source state',
        include: 'Include',
        exclude: 'Exclude',
        decision: 'Evidence decision',
        reason: 'Reason for decision',
        requirementLinks: 'Requirements supported',
        requirements: 'Requirement assessment',
        applicability: 'Applicability',
        applicable: 'Applicable',
        not_applicable: 'Not applicable',
        undetermined: 'Undetermined',
        adequacy: 'Content adequacy',
        unreviewed: 'Unreviewed',
        adequate: 'Adequate',
        partial: 'Partial',
        unsupported: 'Unsupported',
        missing: 'Missing content',
        present: 'Content present',
        supported: 'Supported requirements',
        blockers: 'Mandatory blockers',
        ready: 'Ready for selected purpose',
        notReady: 'Not ready for selected purpose',
        statutoryReady: 'Statutory content readiness',
        readinessHint: 'Content readiness, internal approval, dispatch, official receipt and authority outcome are separate decisions. An adverse finding can be part of a complete assessment.',
        diagnostics: 'Profile and source limitations',
        supplement: 'Supplemental assessment content',
        conclusions: 'Assessment conclusions and residual risks',
        limitations: 'Limitations and unresolved questions',
        disclosure: 'Release decision',
        full: 'Release full reviewed content',
        redacted_release: 'Release selected evidence only',
        releaseReason: 'Reason and recipient disclosure review',
        releaseEvidence: 'Evidence permitted in the released copy',
        gapTask: 'Assign gap action',
        taskTitle: 'Action title',
        assignee: 'Responsible person',
        dueDate: 'Due date',
        createTask: 'Create task',
        taskCreated: 'Gap action created',
        openTask: 'Open task',
        taskHint: 'Completing a task does not establish adequate evidence. Return and refresh after the source record has been corrected.',
        previewTitle: 'Readable report preview',
        version: 'Version',
        snapshot: 'Frozen report',
        capturedAt: 'Captured at',
        capturedBy: 'Captured by',
        reviewedBy: 'Reviewed by',
        manifestHash: 'Snapshot manifest hash',
        files: 'Report and evidence files',
        download: 'Download',
        noFiles: 'Files are not available for this version.',
        newVersion: 'Revise as a new version',
        frozenHint: 'This is the frozen version. Changes require a new preview and version; existing approval applies only to its exact version.',
        pending: 'Files pending',
        finalized: 'Files finalized',
        failedState: 'File generation failed',
        sourceContent: 'Captured source content',
        attachments: 'Source attachments',
        close: 'Close',
        saveReview: 'Apply review to preview',
        profile: 'Assessment profile',
        noProfile: 'No supported assessment profile is installed for this selection.',
        returnReview: 'Return to review',
        create: 'Create report',
        cancel: 'Cancel',
        dpiaSource: 'Processing assessments',
        transfer_assessmentSource: 'Transfer assessments',
        processing_activitySource: 'Processing activities',
        systemSource: 'Systems',
        vendorSource: 'Vendors',
        consentSource: 'Consent records',
        rights_requestSource: 'Rights requests',
        incidentSource: 'Incidents',
        form_instanceSource: 'Completed / draft form instances',
        dossierFormsSource: 'Informes i notificacions completats de l’expedient',
        attachmentSource: 'Attachments',
        evidenceSource: 'Evidence records',
        selected: 'Selected source records',
        sourceUnavailable: 'Source unavailable or permission restricted',
        supplementHint: 'Provide the missing substantive assessment content. Empty content remains a gap.',
        choose: 'Choose…',
        page: 'Page',
        exemption: 'Assert an exemption',
        exemptionReason: 'Exemption grounds and supporting evidence',
        approvalStatus: 'Internal approval / dispatch status',
        scope: 'Report scope',
        sourceOutcomes: 'Source retrieval outcomes',
        openReportForm: 'Complete the assessment report form',
        openNoticeForm: 'Complete the applicant notice',
        formHint: 'Complete and finalize these installed forms, then select their saved instances as source records. Opening a blank form does not supply evidence.',
        reportTitle: 'Evidence report',
        final: 'Final saved content',
        draft: 'Draft content',
        historical: 'Historical content',
        unverified: 'Unverified source',
        sourceStatusOther: 'Other status',
        captureCurrent: 'Capture current saved content',
        captureHint: 'New or corrected records saved after this instant are outside this report. Keep this time for a historical report, or update it deliberately to include current saved content.',
        sourceFacts: 'Recorded facts',
        provenance: 'Source provenance',
        approveReportConfirm: 'Approve version {version} for release to the report’s intended recipient? This records your approval; it does not record statutory filing, official receipt or an authority decision.',
        consent_evidenceSource: 'Consent receipts and proofs',
        documentRoles: 'Supporting document components',
        historicalUse: 'Use historical evidence for this reporting period',
        historicalHint: 'Historical evidence can support a bounded internal review or authority response only when dated admitted supporting files establish validity throughout the selected period. It does not establish current statutory readiness.',
        historicalReason: 'Reason for using historical evidence',
        validFrom: 'Valid from (ISO date and time with offset)',
        validUntil: 'Valid until (ISO date and time with offset)',
        historicalProof: 'Dated supporting files admitted to this report',
        savedContentErrors: 'Saved form content requires correction',
        historicalCoverageError: 'The stated validity range must cover the entire reporting period.',
        linkTask: 'Link an existing task',
    },
};
//# sourceMappingURL=ca.js.map